import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Capacitor } from "@capacitor/core";
import SprintWidget from "./plugins/SprintWidgetPlugin";
import { createPortal } from "react-dom";
import type { GEntry, GSprint } from "./types/entries";
import { SettingsHeaderTabs, SettingsPage } from "./settings/SettingsPage";
import type { Section as SettingsSection } from "./settings/SettingsPage";
import { LoginModal } from "./supabase/LoginModal";
import { AppShell } from "./components/layout/AppShell";
import { DailyTargetsCard } from "./components/DailyTargetsCard";
import { LandingPage } from "./components/LandingPage";
import { resetPassword, signIn, signUp } from "./supabase/auth";
import { useSupabaseSync } from "./supabase/useSupabaseSync";
import {
  type OuraConnection,
  disconnectOura,
  getOuraConnection,
  startOuraConnect,
  syncOuraNow,
  updateOuraSettings,
} from "./supabase/oura";
import { pushProfile } from "./supabase/sync";
import { runOuraSanityTests } from "./domain/oura-sanity";
import { buildDemoProfiles, type DemoProfileId } from "./demoProfiles";
import {
  addDaysYmdSafe,
  clampYmdToBounds,
  daysBetweenClamped,
  isValidYmd,
  MAX_DAILY_POINTS,
  MAX_FUTURE_DAYS,
  MAX_SPRINT_SPAN_DAYS,
  MAX_YMD,
  MIN_YMD,
  safeParseYmd,
} from "./dateGuardrails";
import { MAX_SPRINT_NAME_CHARS, sanitizeSprintName } from "./sprintName";
import { APP_DOMAIN, APP_NAME, APP_TAGLINE } from "./brand";
import { brand } from "./theme/brand";
import { actionColors, phaseColors, statusColors } from "./themeColorTokens";
import { addDaysYMD, formatMD, formatMDY, parseISODateYMD, todayISO, isYmd } from "./lib/date";
import { clamp, comma, fmt2, parseSignedDraftNumber, safeNumber } from "./lib/number";
import { isNativeIOS } from "./lib/platform";
import { calcAge, calcAgeAtDate, calcBMR, calcBMRAtDate, calcTDEEFromActive, calcTDEEFromFactor, getNetCalories } from "./domain/physiology";
import { computeTargetDateFromRate, computeSprintPlanMetrics, deriveSprintGoalType, getLoggedWeightForDate, getRatePresetOptions, normalizeSprintWeightsForGoalType, SPRINT_PACE_LIMITS, type SprintGoalType, validateSprintGuardrails } from "./domain/sprint";
import { reconcileEntriesDerivedMetrics } from "./domain/entries";
import { useHealthKit } from "./hooks/useHealthKit";
import { OnboardingFlowV2 } from "./onboarding";
import type { AuthChoice, OnboardingResult } from "./onboarding";
import { signInWithGoogle as v2SignInWithGoogle, signInWithApple as v2SignInWithApple } from "./supabase/socialAuth";
import { signInWithMagicLink as v2SignInWithMagicLink } from "./supabase/auth";
import { mergeHealthKitWeights, mergeHealthKitActiveCalories } from "./healthkit/mergeHealthKitData";
import type { ActivityBurnSource } from "./types/entries";
import {
  fitLabelToWidth,
  SPRINT_CHIP_AVG_CHAR_PX,
  SPRINT_CHIP_HORIZONTAL_PADDING,
  SPRINT_CHIP_MAX_WIDTH_PX,
  SPRINT_CHIP_MIN_LABEL_TEXT_PX,
  SPRINT_CHIP_TEXT_MARGIN,
} from "./helpers/sprintChipLabel";
import { filterRedundantPaceWarnings, getPaceBandDisplayLabel } from "./helpers/paceLabels";
import {
  Bar,
  BarChart,
  Brush,
  CartesianGrid,
  Cell,
  ComposedChart,
  Customized,
  Legend,
  Line,
  LineChart,
  ReferenceArea,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

const navIconBase: React.CSSProperties = { width: 20, height: 20, stroke: "currentColor", strokeWidth: 2, fill: "none", strokeLinecap: "round", strokeLinejoin: "round" };
const HomeIcon = () => <svg viewBox="0 0 24 24" style={navIconBase}><path d="M3 10.5 12 3l9 7.5" /><path d="M5 9.5V21h14V9.5" /><path d="M9.5 21v-6h5v6" /></svg>;
const FlagIcon = () => <svg viewBox="0 0 24 24" style={navIconBase}><path d="M5 3v18" /><path d="M5 4h12l-2.5 4L17 12H5" /></svg>;
const MapIcon = () => <svg viewBox="0 0 24 24" style={navIconBase}><path d="m3 6 6-2 6 2 6-2v14l-6 2-6-2-6 2z" /><path d="M9 4v14" /><path d="M15 6v14" /></svg>;
const ChartIcon = () => <svg viewBox="0 0 24 24" style={navIconBase}><path d="M3 3v18h18" /><path d="m7 15 4-4 3 3 5-7" /></svg>;
const HistoryIcon = () => <svg viewBox="0 0 24 24" style={navIconBase}><path d="M3 12a9 9 0 1 0 3-6.7" /><path d="M3 3v6h6" /><path d="M12 7v5l3 2" /></svg>;
const ChevronIcon = ({ direction = "down", size = 14 }: { direction?: "down" | "up" | "right"; size?: number }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} style={{ display: "inline-block", verticalAlign: "middle" }}>
    {direction === "up" ? <path d="m6 15 6-6 6 6" stroke="currentColor" strokeWidth="2.2" fill="none" strokeLinecap="round" strokeLinejoin="round" /> : null}
    {direction === "down" ? <path d="m6 9 6 6 6-6" stroke="currentColor" strokeWidth="2.2" fill="none" strokeLinecap="round" strokeLinejoin="round" /> : null}
    {direction === "right" ? <path d="m9 6 6 6-6 6" stroke="currentColor" strokeWidth="2.2" fill="none" strokeLinecap="round" strokeLinejoin="round" /> : null}
  </svg>
);
const UploadIcon = ({ size = 16 }: { size?: number }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} style={{ display: "inline-block", verticalAlign: "middle" }} fill="none">
    <path d="M12 15V5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="m8 9 4-4 4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M20 16.5v1A2.5 2.5 0 0 1 17.5 20h-11A2.5 2.5 0 0 1 4 17.5v-1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);
const DownloadIcon = ({ size = 16 }: { size?: number }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} style={{ display: "inline-block", verticalAlign: "middle" }} fill="none">
    <path d="M12 5v10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="m8 11 4 4 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M20 16.5v1A2.5 2.5 0 0 1 17.5 20h-11A2.5 2.5 0 0 1 4 17.5v-1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);
const FileIcon = ({ size = 16 }: { size?: number }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} style={{ display: "inline-block", verticalAlign: "middle" }} fill="none">
    <path d="M14 2H7a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M14 2v5h5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// ---------------------------
// WeightPlanner Pro (single-file)
// ---------------------------

const STORAGE_KEYS = {
  entries: "weight-tracker-entries",
  vitals: "weight-tracker-vitals",
  forecast: "weight-tracker-forecast",
  sprints: "weight-tracker-sprints",
  roadmap: "weight-tracker-roadmap",
  roadmapUpdatedAt: "weight-tracker-roadmap-updated-at",
  deletedEntries: "weight-tracker-deleted-entries",
  hasSynced: "weight-tracker-has-synced",
  healthKitEnabled: "healthkit-enabled",
  healthKitEnabledUpdatedAt: "healthkit-enabled-updated-at",
};

// Survives clearAnonymousLocalState() so an onboarding plan can cross the
// OAuth/magic-link sign-in boundary. See completeOnboardingFromV2 and the
// hydration effect near the post-sign-in routing block.
const ONBOARDING_HANDOFF_KEY = "weight-tracker-onboarding-handoff";
const ONBOARDING_HANDOFF_TTL_MS = 15 * 60 * 1000;

// Loose shape — the rest of WeightTracker stores entries/vitals/sprints/forecast
// as untyped objects (persistAll accepts them as-is), so keep the same looseness
// here rather than fighting the existing inference.
type OnboardingHandoff = {
  entries: any;
  vitals: any;
  sprints: any;
  forecast: any;
  authMethod: "apple" | "google" | "magic";
  email?: string;
  createdAt: number;
};

const COLORS = {
  bg1: brand.background.primary,
  bg2: brand.background.elevated,
  card: "rgba(255,255,255,0.05)",
  cardBorder: brand.border.subtle,
  blue: brand.accent.primary,
  lightBlue: brand.chart.trend,
  green: brand.state.success,
  orange: brand.state.warning,
  red: brand.state.danger,
  pink: "#ec4899",
  purple: "#8b5cf6",
};

const SPRINT_PALETTE = ["#60a5fa", "#2563eb", "#22d3ee", "#0ea5e9", "#34d399", "#10b981", "#84cc16", "#f59e0b", "#f97316", "#f43f5e", "#ec4899", "#a78bfa", "#8b5cf6", "#6366f1", "#14b8a6", "#eab308"];

function createAdaptiveDateTickFormatter(totalDays: number) {
  const mode = totalDays >= 365 ? "monthYear" : totalDays >= 120 ? "monthDayYearHints" : "monthDay";

  return (ymd: string) => {
    const dt = parseISODateYMD(ymd);
    if (!dt) return ymd;

    const month = dt.getMonth() + 1;
    const day = dt.getDate();
    const yearShort = String(dt.getFullYear()).slice(-2);

    if (mode === "monthYear") return `${month}/${yearShort}`;
    if (mode === "monthDayYearHints") return month === 1 ? `${month}/${day}/${yearShort}` : `${month}/${day}`;
    return `${month}/${day}`;
  };
}

function buildAdaptiveDateTicks(dates: string[], maxTickCount: number) {
  if (!dates.length) return [];
  if (dates.length <= 2 || maxTickCount <= 1) return [dates[0], dates[dates.length - 1]].filter(Boolean);
  if (dates.length <= maxTickCount) return dates;

  const tickSet = new Set<number>([0, dates.length - 1]);
  const stride = Math.max(1, Math.ceil((dates.length - 1) / Math.max(1, maxTickCount - 1)));

  for (let index = stride; index < dates.length - 1; index += stride) tickSet.add(index);

  const sorted = [...tickSet].sort((a, b) => a - b);
  if (sorted.length >= 2) {
    const last = sorted[sorted.length - 1];
    const previous = sorted[sorted.length - 2];
    if (last - previous < Math.max(2, Math.ceil(stride / 2))) tickSet.delete(previous);
  }

  return [...tickSet].sort((a, b) => a - b).map((index) => dates[index]);
}

function getTrackerDateTickCount(totalDays: number, density: "mobile" | "desktop") {
  if (density === "mobile") {
    // Max safe ≈10 ticks in ~310px plot at -32°, 10px font (~31px footprint per label)
    if (totalDays > 730) return 9;
    if (totalDays > 365) return 9;
    if (totalDays > 180) return 9;  // was 6
    if (totalDays > 120) return 9;  // was 7
    if (totalDays > 84)  return 9;  // was 8
    if (totalDays > 56)  return 10; // was 9
    if (totalDays > 35)  return 10; // was 10
    if (totalDays > 21)  return 10; // was 11
    return 10;                       // was 12
  }

  // Desktop: max safe ≈22 ticks in ~800px+ plot at -45°, 12px font (~32px footprint per label)
  if (totalDays > 730) return 22;
  if (totalDays > 365) return 20;
  if (totalDays > 180) return 18;  // was 10
  if (totalDays > 120) return 16;  // was 12
  if (totalDays > 84)  return 18;  // was 15
  if (totalDays > 56)  return 16;  // was 14
  if (totalDays > 35)  return 14;  // was 12
  return 12;                        // was 10
}

// ---------------------------
// Roadmap types + constants
// ---------------------------

type RoadmapPhaseType = "cut" | "bulk" | "maintain";

type RoadmapPhase = {
  id: string;
  orderIndex: number;
  type: RoadmapPhaseType;
  startDate: string;   // YYYY-MM-DD (locked except phase 0)
  endDate: string;     // YYYY-MM-DD
  startWeight: number; // locked except phase 0
  endWeight: number;
};

type Roadmap = {
  id: string;
  name: string;
  phases: RoadmapPhase[];
  createdAt: string;
  phaseSprintLinks?: Record<string, string>; // phaseId → sprintId
};

type TrackerCalorieMetric = "netCalories" | "caloriesIn" | "activeCalories" | "tdee";
type ChartMetric = "weight" | TrackerCalorieMetric;

type RoadmapPhaseBuilderDraft = {
  type: RoadmapPhaseType;
  startDate: string;
  endDate: string;
  startWeight: string;
  endWeight: string;
  aggressiveness: WizardAggressiveness;
  planMethod: "byDate" | "byPace";
  orderIndex: number;
  startValuesInherited: boolean;
  inheritedFromPhaseNumber: number | null;
};

const ROADMAP_PHASE_COLORS: Record<RoadmapPhaseType, string> = {
  cut: phaseColors.cut,
  bulk: phaseColors.bulk,
  maintain: phaseColors.maintain,
};

const ROADMAP_PHASE_LABELS: Record<RoadmapPhaseType, string> = {
  cut: "Cut",
  bulk: "Bulk",
  maintain: "Maintain",
};

const CHART_METRIC_OPTIONS: { key: ChartMetric; label: string }[] = [
  { key: "weight", label: "Weight" },
  { key: "netCalories", label: "Net" },
  { key: "caloriesIn", label: "Calories In" },
  { key: "activeCalories", label: "Active" },
  { key: "tdee", label: "TDEE" },
];

const TRACKER_CALORIE_METRIC_OPTIONS: { key: TrackerCalorieMetric; label: string }[] = CHART_METRIC_OPTIONS.filter((option): option is { key: TrackerCalorieMetric; label: string } => option.key !== "weight");

const CHART_METRIC_META: Record<ChartMetric, { label: string; title: string; unit: string; type: "weight" | "cal" }> = {
  weight: { label: "Weight", title: "Weight (lbs)", unit: "lbs", type: "weight" },
  netCalories: { label: "Net", title: "Net calories (cal/day)", unit: "cal/day", type: "cal" },
  caloriesIn: { label: "Calories In", title: "Calories in (cal/day)", unit: "cal/day", type: "cal" },
  tdee: { label: "TDEE", title: "TDEE (cal/day)", unit: "cal/day", type: "cal" },
  activeCalories: { label: "Active", title: "Active calories (cal/day)", unit: "cal/day", type: "cal" },
};

function getCalorieWarningFloor(gender: string | null | undefined) {
  return String(gender ?? "").toUpperCase() === "F" ? CAL_IN_MIN_WARN_F : CAL_IN_MIN_WARN_M;
}

function resolveBoundedPlanCalories({ requiredNet, tdee, gender }: { requiredNet: number | null; tdee: number | null; gender: string | null | undefined }) {
  const floor = getCalorieWarningFloor(gender);
  if (requiredNet === null || tdee === null) return { intake: null, net: requiredNet, belowFloor: false, floor };
  const rawIntake = Math.max(0, Math.round(requiredNet + tdee));
  const belowFloor = rawIntake < floor;
  return { intake: rawIntake, net: requiredNet, belowFloor, floor };
}

// "very-aggressive" and "lean" only apply to cut and bulk respectively.
// "maintain" ignores aggressiveness (rate is always 0).
type WizardAggressiveness = "conservative" | "lean" | "moderate" | "aggressive" | "very-aggressive";

type WizardPhaseRow = {
  type: RoadmapPhaseType;
  startDate: string;
  startWeight: string;
  endDate: string;
  targetWeight: string;
  aggressiveness: WizardAggressiveness;
  lastEdited: "endDate" | "targetWeight";
  planMethod: "byDate" | "byPace";
};

type WizardPattern = "bulk-cut" | "bulk-maintain-cut" | "cut-bulk" | "cut-maintain-bulk" | "single";

// Rates in lb/week (negative = loss for cut).
// Aligned with sprint builder's getRatePresetOptions.
const AGGRESSIVENESS_RATES: Record<WizardAggressiveness, Record<RoadmapPhaseType, number>> = {
  conservative:     { cut: -0.5,  bulk: 0.25, maintain: 0 },
  lean:             { cut: -0.5,  bulk: 0.25, maintain: 0 }, // bulk label; same rate as conservative
  moderate:         { cut: -1.0,  bulk: 0.5,  maintain: 0 },
  aggressive:       { cut: -1.5,  bulk: 1.0,  maintain: 0 },
  "very-aggressive":{ cut: -2.0,  bulk: 1.0,  maintain: 0 }, // cut only
};

// Per-type pace option lists — matches sprint builder labels exactly
const CUT_PACE_OPTIONS: { key: WizardAggressiveness; label: string }[] = [
  { key: "conservative",      label: "Lean"   },
  { key: "moderate",          label: "Moderate"       },
  { key: "aggressive",        label: "Aggressive"     },
  { key: "very-aggressive",   label: "Above recommended"},
];
const BULK_PACE_OPTIONS: { key: WizardAggressiveness; label: string }[] = [
  { key: "lean",              label: "Lean"           },
  { key: "moderate",          label: "Moderate"       },
  { key: "aggressive",        label: "Aggressive"     },
];

function inferRoadmapPhaseType(startWeight: number, endWeight: number): RoadmapPhaseType {
  const goalType = deriveSprintGoalType(startWeight, endWeight);
  return goalType === "cut" ? "cut" : goalType === "bulk" ? "bulk" : "maintain";
}

function getDefaultTargetWeightForPhaseType(type: RoadmapPhaseType, startWeight: number): number {
  if (!Number.isFinite(startWeight)) return startWeight;
  const probeTarget = type === "cut" ? startWeight - 1 : type === "bulk" ? startWeight + 1 : startWeight;
  const moderateRate = getRatePresetOptions({ startWeight, targetWeight: probeTarget }).find((option) => option.label === "Moderate")?.rate
    ?? (type === "cut" ? 1 : type === "bulk" ? 0.5 : 0);
  const defaultDeltaLbs = Math.max(0, Math.round(moderateRate * 8 * 10) / 10);
  const target = normalizeSprintWeightsForGoalType({ goalType: type, startWeight, targetWeight: null, defaultDeltaLbs });
  return Math.round((target ?? startWeight) * 10) / 10;
}

function getPaceOptionsForType(type: RoadmapPhaseType) {
  if (type === "cut")      return CUT_PACE_OPTIONS;
  if (type === "bulk")     return BULK_PACE_OPTIONS;
  return []; // maintain has no pace selector
}

function defaultPaceForType(type: RoadmapPhaseType): WizardAggressiveness {
  if (type === "cut")  return "moderate";
  if (type === "bulk") return "lean";
  return "moderate"; // maintain — ignored
}

const _WPUp   = <svg aria-hidden viewBox="0 0 7 8" width="8" height="9"><polygon points="3.5,0 7,8 0,8" fill="#34d399"/></svg>;
const _WPDown = <svg aria-hidden viewBox="0 0 7 8" width="8" height="9"><polygon points="0,0 7,0 3.5,8" fill="#f87171"/></svg>;
const _WPFlat = <svg aria-hidden viewBox="0 0 7 3" width="8" height="5"><rect x="0" y="1" width="7" height="1.5" rx="0.75" fill="rgba(255,255,255,0.38)"/></svg>;
const WIZARD_PATTERN_OPTIONS: { key: WizardPattern; label: string; desc: string; icon: React.ReactNode }[] = [
  { key: "bulk-cut",          label: "Bulk \u2192 Cut",              desc: "Build mass, then lean out",            icon: <span style={{display:"inline-flex",alignItems:"center",gap:1}}>{_WPUp}{_WPDown}</span> },
  { key: "bulk-maintain-cut", label: "Bulk \u2192 Maintain \u2192 Cut",   desc: "Build, stabilize, then shred",         icon: <span style={{display:"inline-flex",alignItems:"center",gap:1}}>{_WPUp}{_WPFlat}{_WPDown}</span> },
  { key: "cut-bulk",          label: "Cut \u2192 Bulk",              desc: "Lean down first, then build",          icon: <span style={{display:"inline-flex",alignItems:"center",gap:1}}>{_WPDown}{_WPUp}</span> },
  { key: "cut-maintain-bulk", label: "Cut \u2192 Maintain \u2192 Bulk",   desc: "Lean down, stabilize, then build",     icon: <span style={{display:"inline-flex",alignItems:"center",gap:1}}>{_WPDown}{_WPFlat}{_WPUp}</span> },
  { key: "single",            label: "Single Phase",             desc: "One focused cut, bulk, or maintain",   icon: <svg aria-hidden viewBox="0 0 10 10" width="12" height="12" fill="none" stroke="rgba(255,255,255,0.7)" strokeWidth="1.5"><circle cx="5" cy="5" r="4"/><circle cx="5" cy="5" r="1.5" fill="rgba(255,255,255,0.7)" stroke="none"/></svg> },
];

const DEFAULT_VITALS = {
  height: 70.5,
  gender: "M",
  birthday: "1986-05-15",
  activityLevel: "light",
  activityFactor: 1.3,
  displayName: "",
  avatarKey: "",
};

const ACTIVITY_LEVELS = [
  { value: "sedentary", label: "Sedentary", factor: 1.2 },
  { value: "light", label: "Light", factor: 1.3 },
  { value: "moderate", label: "Moderate", factor: 1.5 },
  { value: "very-active", label: "Very Active", factor: 1.7 },
  { value: "athlete", label: "Athlete", factor: 1.9 },
];

const ACTIVITY_DESCS: Record<string, string> = {
  sedentary:    "Mostly seated during the day.",
  light:        "On your feet part of the day.",
  moderate:     "Regular physical movement most days.",
  "very-active":"Heavy physical work or frequent training.",
  athlete:      "High-volume training or physically demanding routine.",
};

function _OBActIcon({ n }: { n: number }): React.ReactElement {
  return (
    <span style={{ display: "inline-flex", gap: "1px", alignItems: "flex-end" }}>
      {[1, 2, 3, 4, 5].map((i) => (
        <span key={i} style={{ width: 2, height: 2 + i * 2, background: i <= n ? "rgba(255,255,255,0.82)" : "rgba(255,255,255,0.18)", borderRadius: 1, display: "block" }} />
      ))}
    </span>
  );
}
const ONBOARDING_ACTIVITY_ROWS: Array<{ value: string; icon: React.ReactNode; label: string; desc: string }> = [
  { value: "sedentary",   icon: <_OBActIcon n={1} />, label: "Sedentary",   desc: "Mostly seated during the day." },
  { value: "light",       icon: <_OBActIcon n={2} />, label: "Light",       desc: "On your feet part of the day." },
  { value: "moderate",    icon: <_OBActIcon n={3} />, label: "Moderate",    desc: "Regular movement most days." },
  { value: "very-active", icon: <_OBActIcon n={4} />, label: "Very active", desc: "Frequent training or physical work." },
  { value: "athlete",     icon: <_OBActIcon n={5} />, label: "Athlete",     desc: "High-volume training." },
];

const DEFAULT_FORECAST = {
  targetWeight: 165,
  targetDate: null,
  assumedNetCalories: -886,
  activityFactor: 1.3,
  inputMode: "net", // net | intake
  sprintStartDate: null,
  activeSprintId: null,
  planHistory: [],
  sprintStatusDisplayMode: "avg", // avg | today
};

function normalizeSprintStatusDisplayMode(value: unknown): "avg" | "today" {
  return value === "today" ? "today" : "avg";
}

function normalizeForecastPlanHistory(rows) {
  if (!Array.isArray(rows)) return [];
  return rows
    .map((row) => {
      const effectiveDate = toYmdLoose(row?.effectiveDate);
      const targetNet = safeNumber(row?.targetNet);
      if (!isYmd(effectiveDate) || targetNet === null) return null;
      const sprintIdRaw = row?.sprintId;
      const sprintId = sprintIdRaw === null || sprintIdRaw === undefined || String(sprintIdRaw).trim() === "" ? null : String(sprintIdRaw);
      return {
        sprintId,
        effectiveDate,
        targetNet,
        createdAt: row?.createdAt ? String(row.createdAt) : new Date().toISOString(),
      };
    })
    .filter(Boolean)
    .sort((a, b) => {
      if (a.sprintId !== b.sprintId) return String(a.sprintId ?? "") < String(b.sprintId ?? "") ? -1 : 1;
      if (a.effectiveDate === b.effectiveDate) return a.createdAt < b.createdAt ? -1 : 1;
      return a.effectiveDate < b.effectiveDate ? -1 : 1;
    });
}

const TRACKER_SERIES_VISIBILITY_KEY = "weight-tracker-progress-series-visible";
const LANDING_DISMISSED_KEY = "wt_landing_dismissed";
const CAL_IN_MIN_WARN_M = 1500;   // NIH recommended min — males
const CAL_IN_MIN_WARN_F = 1200;   // NIH recommended min — females
const CAL_IN_MAX_WARN = 6000;
const TDEE_MIN_WARN = 1200;
const TDEE_MAX_WARN = 5500;
const HEIGHT_MIN_IN = 36;          // 3 ft
const HEIGHT_MAX_IN = 96;          // 8 ft
const AGE_MIN = 13;
const AGE_MAX = 100;
const WEIGHT_MIN_WARN = 70;
const WEIGHT_MAX_WARN = 600;
const WEIGHT_DELTA_WARN_LBS = 8;
const REPLAN_STATUS_THRESHOLD_LBS = 0.5;
const REPLAN_MAX_ABS_NET = 1500;
const STALE_WEIGHIN_DAYS = 14;
const REPLAN_UP_TO_DATE_NET_TOLERANCE = 50;
const TRACKER_PROGRESS_SERIES_STYLES = {
  actual: { color: COLORS.blue, strokeWidth: 2, dot: { r: 2, strokeWidth: 0 }, strokeOpacity: 0.55, connectNulls: false },
  movingAvg: { color: COLORS.lightBlue, strokeWidth: 4, dot: false, connectNulls: true },
  expected: { color: COLORS.purple, strokeWidth: 2.5, dot: false, strokeDasharray: "5 5", strokeOpacity: 0.9, connectNulls: true },
  projected: { color: COLORS.orange, strokeWidth: 2, dot: false, strokeDasharray: "1 6", connectNulls: false },
  originalPlan: { color: COLORS.pink, strokeWidth: 2, dot: false, strokeDasharray: "2 4", strokeOpacity: 0.9, connectNulls: false },
};


// ---------------------------
// Robust storage
// ---------------------------

function createMemoryStorage() {
  const mem = new Map();
  return {
    async get(key) {
      return { value: mem.has(key) ? mem.get(key) : null };
    },
    async set(key, value) {
      mem.set(key, value);
      return true;
    },
    async delete(key) {
      mem.delete(key);
      return true;
    },
    kind: "memory",
  };
}

function canUseLocalStorage() {
  try {
    if (typeof window === "undefined" || !window.localStorage) return false;
    const k = "__wtp_test__";
    window.localStorage.setItem(k, "1");
    window.localStorage.removeItem(k);
    return true;
  } catch {
    return false;
  }
}

function createLocalStorageAdapter() {
  return {
    async get(key) {
      try {
        const v = window.localStorage.getItem(key);
        return { value: v === null ? null : v };
      } catch {
        return { value: null };
      }
    },
    async set(key, value) {
      window.localStorage.setItem(key, value);
      return true;
    },
    async delete(key) {
      window.localStorage.removeItem(key);
      return true;
    },
    kind: "localStorage",
  };
}

function createWindowStorageAdapter(winStorage) {
  return {
    async get(key) {
      const res = await winStorage.get(key);
      if (res && typeof res === "object" && "value" in res) return { value: res.value };
      return { value: res ?? null };
    },
    async set(key, value) {
      return winStorage.set(key, value);
    },
    async delete(key) {
      if (typeof winStorage.delete === "function") return winStorage.delete(key);
      return winStorage.set(key, null);
    },
    kind: "window.storage",
  };
}

function getStorage() {
  if (typeof window !== "undefined") {
    const ws = window.storage;
    if (ws && typeof ws.get === "function" && typeof ws.set === "function") return createWindowStorageAdapter(ws);
    if (canUseLocalStorage()) return createLocalStorageAdapter();
  }
  return createMemoryStorage();
}

const storage = getStorage();

async function writeOnboardingHandoff(blob: OnboardingHandoff): Promise<void> {
  try {
    await storage.set(ONBOARDING_HANDOFF_KEY, JSON.stringify(blob));
  } catch (e) {
    console.warn("[onboarding-handoff] write failed", e);
  }
}

async function readOnboardingHandoff(): Promise<OnboardingHandoff | null> {
  try {
    const res = await storage.get(ONBOARDING_HANDOFF_KEY);
    const raw = res?.value;
    if (!raw || typeof raw !== "string") return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    if (typeof parsed.createdAt !== "number") return null;
    if (parsed.authMethod !== "apple" && parsed.authMethod !== "google" && parsed.authMethod !== "magic") return null;
    return parsed as OnboardingHandoff;
  } catch {
    return null;
  }
}

async function clearOnboardingHandoff(): Promise<void> {
  try {
    await storage.delete(ONBOARDING_HANDOFF_KEY);
  } catch {
    // ignore
  }
}

// ---------------------------
// Date utils
// ---------------------------

function getLatestLoggedWeight(entries) {
  if (!Array.isArray(entries) || !entries.length) return null;
  return entries.reduce((latest, entry) => {
    if (!entry || !Number.isFinite(entry.weight) || !isYmd(entry.date)) return latest;
    if (!latest || entry.date > latest.date) return { weight: Number(entry.weight), date: entry.date };
    return latest;
  }, null);
}

// ---------------------------
// Plan-by-Rate helpers
// ---------------------------

function daysBetweenInclusiveStartExclusiveEnd(startYmd, endYmd) {
  const s = parseISODateYMD(startYmd);
  const e = parseISODateYMD(endYmd);
  if (!s || !e) return null;
  return Math.ceil((e.getTime() - s.getTime()) / (1000 * 60 * 60 * 24));
}

function formatSyncError(err) {
  const raw = err instanceof Error ? err.message : String(err ?? "Sync failed");
  const lower = raw.toLowerCase();
  if (lower.includes("failed to fetch") || lower.includes("networkerror")) {
    return "Failed to fetch (check Apps Script deployment access + URL)";
  }
  return raw;
}

function toYmdLoose(v) {
  if (!v) return "";
  const raw = String(v);
  const ymd = raw.includes("T") ? raw.slice(0, 10) : raw.slice(0, 10);
  if (!isYmd(ymd)) return "";
  return clampYmdToBounds(ymd, MIN_YMD, MAX_YMD);
}

function hashString(value) {
  let hash = 0;
  const text = String(value ?? "");
  for (let i = 0; i < text.length; i += 1) {
    hash = (hash << 5) - hash + text.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash);
}

function hexToRgba(hex, alpha = 0.12) {
  if (typeof hex !== "string") return `rgba(59,130,246,${alpha})`;
  const normalized = hex.trim().replace("#", "");
  const full = normalized.length === 3 ? normalized.split("").map((ch) => `${ch}${ch}`).join("") : normalized;
  if (!/^[0-9a-fA-F]{6}$/.test(full)) return `rgba(59,130,246,${alpha})`;
  const r = parseInt(full.slice(0, 2), 16);
  const g = parseInt(full.slice(2, 4), 16);
  const b = parseInt(full.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

function pickNextSprintColor(existingSprints = [], preferred = null) {
  if (preferred && SPRINT_PALETTE.includes(preferred)) return preferred;

  const usage = new Map(SPRINT_PALETTE.map((color) => [color, 0]));
  existingSprints.forEach((s) => {
    if (SPRINT_PALETTE.includes(s?.color)) {
      usage.set(s.color, (usage.get(s.color) ?? 0) + 1);
    }
  });

  const minUsage = Math.min(...Array.from(usage.values()));
  return SPRINT_PALETTE.find((color) => (usage.get(color) ?? 0) === minUsage) ?? SPRINT_PALETTE[0];
}

// ---------------------------
// Physiology
// ---------------------------

function getBestKnownWeightForDate({
  entriesAsc,
  date,
  sprintStartWeight,
  vitalsCurrentWeight,
}: {
  entriesAsc: any[];
  date: string;
  sprintStartWeight: number | null;
  vitalsCurrentWeight?: number | null;
}) {
  const entryOnDate = entriesAsc.find((e) => e.date === date);
  if (entryOnDate && Number.isFinite(entryOnDate.weight)) return entryOnDate.weight;

  if (entriesAsc.length) {
    const timeline = buildDailyTimelineWithInferredRange(entriesAsc, entriesAsc[0].date, date, date);
    const point = timeline.find((d) => d.date === date);
    const inferred = point?.inferred ?? null;
    if (Number.isFinite(inferred)) return inferred;
  }

  for (let i = entriesAsc.length - 1; i >= 0; i -= 1) {
    const e = entriesAsc[i];
    if (e.date < date && Number.isFinite(e.weight)) return e.weight;
  }

  if (Number.isFinite(vitalsCurrentWeight)) return vitalsCurrentWeight;

  return Number.isFinite(sprintStartWeight) ? sprintStartWeight : null;
}

function resolveTdeeForDate({
  entry,
  date,
  bestKnownWeight,
  activityLevelFactor,
  vitals,
}: {
  entry: any;
  date: string;
  bestKnownWeight: number | null;
  activityLevelFactor: number;
  vitals: any;
}) {
  if (!entry) return { tdee: null, source: "unknown" as const };

  if (Number.isFinite(entry.totalBurned)) {
    return { tdee: entry.totalBurned, source: "loggedTotal" as const };
  }

  const bmr = Number.isFinite(bestKnownWeight)
    ? calcBMRAtDate({
      weightLbs: bestKnownWeight,
      heightIn: vitals.height,
      gender: vitals.gender,
      birthday: vitals.birthday,
      onDateYmd: date,
    })
    : null;

  if (Number.isFinite(entry.activeCalories) && bmr !== null) {
    return {
      tdee: calcTDEEFromActive({ bmr, activeCalories: entry.activeCalories }),
      source: "fromActive" as const,
    };
  }

  if (bmr !== null) {
    return {
      tdee: calcTDEEFromFactor({ bmr, factor: activityLevelFactor }),
      source: "fromActivityFactor" as const,
    };
  }

  return { tdee: null, source: "unknown" as const };
}

function formatTdeeSourceLabel(source) {
  if (source === "loggedTotal") return "Logged";
  if (source === "fromActive") return "From Active";
  if (source === "fromActivityFactor") return "Estimated";
  return "—";
}

function renderSourcedNumber(
  value: number | null,
  source: "oura" | "manual" | "inferred" | string | null | undefined,
): React.ReactNode {
  if (value === null) return "—";
  const isInferred = source === "inferred";
  const isOura = source === "oura";
  if (!isInferred && !isOura) {
    return comma(value);
  }
  return (
    <div style={{ display: "grid", lineHeight: 1.15 }}>
      <span style={isInferred ? { color: "rgba(255,255,255,0.72)" } : undefined}>
        {comma(value)}
      </span>
      <span style={{ fontSize: "0.68rem", color: isInferred ? "rgba(255,255,255,0.40)" : "rgba(255,255,255,0.50)" }}>
        {isOura ? "Oura" : "Estimated"}
      </span>
    </div>
  );
}

function renderSprintLogInlineValueWithUnit(value: string, unit: string) {
  return (
    <>
      <span>{value}</span>
      <span className="sprint-log-support-pill__unit">{unit}</span>
    </>
  );
}

// ---------------------------
// Roadmap helpers
// ---------------------------

function calcPhaseMetrics(phase: RoadmapPhase, tdee: number | null) {
  const startDt = safeParseYmd(phase.startDate);
  const endDt = safeParseYmd(phase.endDate);
  if (!startDt || !endDt) return null;
  const days = Math.round((endDt.getTime() - startDt.getTime()) / 86400000);
  if (days <= 0) return null;
  const weeks = days / 7;
  const deltaWeight = phase.endWeight - phase.startWeight;
  const dailyDeltaKcal = (deltaWeight * 3500) / days;
  const rateLbsPerWeek = deltaWeight / weeks;
  const targetNet = dailyDeltaKcal;
  const targetIntake = tdee !== null ? tdee + dailyDeltaKcal : null;
  return { days, weeks, deltaWeight, dailyDeltaKcal, rateLbsPerWeek, targetNet, targetIntake };
}

function propagatePhases(phases: RoadmapPhase[], fromIndex: number): RoadmapPhase[] {
  const out = [...phases];
  for (let i = fromIndex + 1; i < out.length; i++) {
    out[i] = { ...out[i], startDate: out[i - 1].endDate, startWeight: out[i - 1].endWeight };
  }
  return out;
}

// Shift all phases after fromIdx by deltaDayMs, preserving each phase's duration.
// Also relinks startWeight = prev.endWeight for shifted phases.
function pushPhasesAfterIndex(phases: RoadmapPhase[], fromIdx: number, deltaDayMs: number): RoadmapPhase[] {
  if (deltaDayMs === 0 || fromIdx >= phases.length - 1) return phases;
  const out = [...phases];
  for (let i = fromIdx + 1; i < out.length; i++) {
    const sTs = safeParseYmd(out[i].startDate, "0001-01-01", "9999-12-31")?.getTime();
    const eTs = safeParseYmd(out[i].endDate, "0001-01-01", "9999-12-31")?.getTime();
    out[i] = {
      ...out[i],
      startDate:   sTs != null ? tsToYmd(sTs + deltaDayMs) : out[i].startDate,
      endDate:     eTs != null ? tsToYmd(eTs + deltaDayMs) : out[i].endDate,
      startWeight: out[i - 1].endWeight,
    };
  }
  return out;
}

// ---------------------------
// Aggressiveness band config + helper
// ---------------------------

type RateBandSeverity = "conservative" | "moderate" | "aggressive" | "extreme";

const RATE_BAND_CONFIG: Record<RoadmapPhaseType, { label: string; minAbs: number; maxAbs: number; severity: RateBandSeverity; warn: boolean }[]> = {
  cut: [
    { label: "Lean cut",       minAbs: 0,    maxAbs: 0.75, severity: "conservative", warn: false },
    { label: "Moderate cut",   minAbs: 0.75, maxAbs: 1.25, severity: "moderate",     warn: false },
    { label: "Aggressive cut", minAbs: 1.25, maxAbs: 2.00, severity: "aggressive",   warn: false },
    { label: "Above recommended cut", minAbs: 2.00, maxAbs: Infinity, severity: "extreme",  warn: true  },
  ],
  bulk: [
    { label: "Lean bulk",      minAbs: 0,    maxAbs: 0.25, severity: "conservative", warn: false },
    { label: "Moderate bulk",  minAbs: 0.25, maxAbs: 0.50, severity: "moderate",     warn: false },
    { label: "Aggressive bulk",minAbs: 0.50, maxAbs: 1.00, severity: "aggressive",   warn: false },
    { label: "Above recommended bulk", minAbs: 1.00, maxAbs: Infinity, severity: "extreme",  warn: true  },
  ],
  maintain: [
    { label: "Maintain",     minAbs: 0,    maxAbs: 0.25, severity: "moderate",  warn: false },
    { label: "Off-maintain", minAbs: 0.25, maxAbs: Infinity, severity: "extreme", warn: true },
  ],
};

function getRateBand(phaseType: RoadmapPhaseType, lbsPerWeek: number): { label: string; severity: RateBandSeverity; warn: boolean } {
  const absRate = Math.abs(lbsPerWeek);
  const bands = RATE_BAND_CONFIG[phaseType];
  // All bands except the last use [minAbs, maxAbs] — inclusive lower and upper.
  // This keeps plan-summary tags aligned with exact "By pace" preset values
  // (e.g., bulk +0.25 => Lean, +0.50 => Moderate, +1.00 => Aggressive).
  for (let i = 0; i < bands.length - 1; i++) {
    const b = bands[i];
    if (absRate >= b.minAbs && absRate <= b.maxAbs) {
      return { label: b.label, severity: b.severity, warn: b.warn };
    }
  }
  // The extreme (last) band fires only when strictly above its threshold.
  // Exactly at the threshold belongs to the previous (non-extreme) band.
  const extreme = bands[bands.length - 1];
  if (absRate > extreme.minAbs) {
    return { label: extreme.label, severity: extreme.severity, warn: extreme.warn };
  }
  const prev = bands[bands.length - 2];
  return { label: prev.label, severity: prev.severity, warn: prev.warn };
}

function normalizeNearZero(value: number | null | undefined, epsilon = 0.0001): number | null {
  if (value === null || value === undefined || !Number.isFinite(Number(value))) return null;
  const n = Number(value);
  return Math.abs(n) < epsilon ? 0 : n;
}

function formatCalPerDay(value: number | null | undefined): string {
  const normalized = normalizeNearZero(value);
  if (normalized === null) return "—";
  return `${comma(Math.round(normalized))} cal/day`;
}

function formatSignedPace(value: number | null | undefined, digits = 1, unit = "lb/week") {
  const normalized = normalizeNearZero(value);
  if (normalized === null) return "—";
  if (normalized === 0) return `0.${"0".repeat(Math.max(0, digits - 1))}0 ${unit}`;
  const abs = Math.abs(normalized).toFixed(digits);
  return `${normalized > 0 ? "+" : "-"}${abs} ${unit}`;
}

// Convert timestamp to YYYY-MM-DD
function tsToYmd(ts: number): string {
  const d = new Date(ts);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

// Pure version of phase edit with propagation — no side effects
// When endDate changes: push later phases by the same delta (preserving their durations).
// Otherwise (type / weight / startDate only): use classic weight-only propagation.
function applyPhaseEditPure(
  roadmap: Roadmap,
  phaseId: string,
  patch: Partial<Pick<RoadmapPhase, "endDate" | "endWeight" | "startDate" | "startWeight" | "type">>
): Roadmap {
  const idx = roadmap.phases.findIndex((p) => p.id === phaseId);
  if (idx < 0) return roadmap;
  const phase = roadmap.phases[idx];

  let endDate   = patch.endDate   ?? phase.endDate;
  let endWeight = patch.endWeight ?? phase.endWeight;
  const startDate   = patch.startDate   ?? phase.startDate;
  const startWeight = patch.startWeight ?? phase.startWeight;
  const type        = patch.type        ?? phase.type;

  // Snap weight to nearest 1 lb
  endWeight = Math.round(endWeight);

  // Enforce minimum 1-day duration
  if (endDate <= startDate) {
    endDate = addDaysYMD(startDate, 1) ?? startDate;
  }

  const updatedPhase: RoadmapPhase = { ...phase, endDate, endWeight, startDate, startWeight, type };
  const updatedPhases = [...roadmap.phases];
  updatedPhases[idx] = updatedPhase;

  // Push later phases when endDate changed (preserves their durations)
  const oldEndTs = safeParseYmd(phase.endDate, "0001-01-01", "9999-12-31")?.getTime() ?? 0;
  const newEndTs = safeParseYmd(endDate, "0001-01-01", "9999-12-31")?.getTime() ?? 0;
  const deltaDayMs = newEndTs - oldEndTs;
  const finalPhases = deltaDayMs !== 0 && idx < roadmap.phases.length - 1
    ? pushPhasesAfterIndex(updatedPhases, idx, deltaDayMs)
    : propagatePhases(updatedPhases, idx);

  return { ...roadmap, phases: finalPhases };
}

// Compute phase type warning (soft — not blocking)
function getPhaseTypeWarning(phase: RoadmapPhase): string | null {
  if (phase.type === "cut" && phase.endWeight > phase.startWeight + 0.5) {
    return "Target weight is above start — this doesn't look like a cut.";
  }
  if (phase.type === "bulk" && phase.endWeight < phase.startWeight - 0.5) {
    return "Target weight is below start — this doesn't look like a bulk.";
  }
  if (phase.type === "maintain" && Math.abs(phase.endWeight - phase.startWeight) > 1) {
    return `Weight changes ${Math.abs(phase.endWeight - phase.startWeight).toFixed(1)} lb — this doesn't look like a maintain.`;
  }
  return null;
}

function getPhaseGuardrails(phase: RoadmapPhase, metrics: ReturnType<typeof calcPhaseMetrics>): string[] {
  if (!metrics) return [];
  const { rateLbsPerWeek } = metrics;
  const band = getRateBand(phase.type, rateLbsPerWeek);
  // Warnings fire only when band.warn is true — i.e. strictly beyond the extreme threshold.
  // Exactly at the threshold (2.0 cut / 1.0 bulk / 0.25 maintain) returns warn: false.
  if (!band.warn) return [];
  if (phase.type === "cut") {
    return ["Extreme cut: Trending faster than 2.0 lb/week. Consider extending the phase or increasing calories."];
  }
  if (phase.type === "bulk") {
    return ["Extreme bulk: Gaining faster than 1.0 lb/week. Consider slowing to reduce unnecessary fat gain."];
  }
  if (phase.type === "maintain") {
    return ["Not maintenance: Drifting more than 0.25 lb/week. Consider adjusting calories or extending the phase."];
  }
  return [];
}

function buildRoadmapChartData(phases: RoadmapPhase[]) {
  // Returns data points for each phase boundary for a continuous weight line
  const points: { date: string; weight: number; phaseIndex: number }[] = [];
  for (let i = 0; i < phases.length; i++) {
    const p = phases[i];
    if (i === 0) points.push({ date: p.startDate, weight: p.startWeight, phaseIndex: i });
    points.push({ date: p.endDate, weight: p.endWeight, phaseIndex: i });
  }
  return points;
}

// Given startWeight + endDate + aggressiveness → compute targetWeight
function computeTargetWeightFromRate(startWeight: number, startDate: string, endDate: string, type: RoadmapPhaseType, aggressiveness: WizardAggressiveness): number | null {
  if (type === "maintain") return startWeight; // maintain always nets zero
  const start = safeParseYmd(startDate);
  const end = safeParseYmd(endDate);
  if (!start || !end || endDate <= startDate) return null;
  const days = Math.round((end.getTime() - start.getTime()) / 86400000);
  const weeks = days / 7;
  const rate = AGGRESSIVENESS_RATES[aggressiveness][type];
  return Math.round((startWeight + rate * weeks) * 10) / 10;
}

// Given startWeight + targetWeight + aggressiveness → compute endDate
function computeEndDateFromRate(startDate: string, startWeight: number, targetWeight: number, type: RoadmapPhaseType, aggressiveness: WizardAggressiveness): string | null {
  if (type === "maintain") return addDaysYMD(startDate, 28); // maintain: default 4 weeks
  const rate = AGGRESSIVENESS_RATES[aggressiveness][type];
  if (rate === 0) return addDaysYMD(startDate, 28);
  const delta = targetWeight - startWeight;
  if (type === "cut" && delta > 0.1) return null;
  if (type === "bulk" && delta < -0.1) return null;
  const weeks = Math.abs(delta / Math.abs(rate));
  if (!Number.isFinite(weeks) || weeks < 0.5) return null;
  const days = Math.max(7, Math.round(weeks * 7));
  return addDaysYMD(startDate, days);
}

// Compute the actual lb/week implied by start+end weight and duration (no preset assumed)
function computeImpliedRate(startDate: string, startWeight: number, endDate: string, targetWeight: number): number | null {
  const start = safeParseYmd(startDate);
  const end = safeParseYmd(endDate);
  if (!start || !end || endDate <= startDate) return null;
  const days = (end.getTime() - start.getTime()) / 86400000;
  const weeks = days / 7;
  if (weeks < 0.5) return null;
  return Math.round(((targetWeight - startWeight) / weeks) * 100) / 100;
}

// Build default wizard phase rows for a given pattern and starting conditions
function initWizardRows(pattern: WizardPattern, startDate: string, startWeight: number): WizardPhaseRow[] {
  const PATTERN_SEGMENTS: Record<WizardPattern, { type: RoadmapPhaseType; defaultWeeks: number }[]> = {
    "bulk-cut":          [{ type: "bulk", defaultWeeks: 10 }, { type: "cut",      defaultWeeks: 10 }],
    "bulk-maintain-cut": [{ type: "bulk", defaultWeeks: 8  }, { type: "maintain", defaultWeeks:  4 }, { type: "cut",  defaultWeeks: 8 }],
    "cut-bulk":          [{ type: "cut",  defaultWeeks: 10 }, { type: "bulk",     defaultWeeks: 10 }],
    "cut-maintain-bulk": [{ type: "cut",  defaultWeeks: 8  }, { type: "maintain", defaultWeeks:  4 }, { type: "bulk", defaultWeeks: 8 }],
    "single":            [{ type: "cut",  defaultWeeks: 12 }],
  };
  const segs = PATTERN_SEGMENTS[pattern];
  const rows: WizardPhaseRow[] = [];
  let curDate = startDate;
  let curWeight = startWeight;

  for (const seg of segs) {
    const pace = defaultPaceForType(seg.type);
    const endDate = addDaysYMD(curDate, seg.defaultWeeks * 7) ?? curDate;
    // Maintain: endWeight = startWeight (net 0)
    const rate = seg.type === "maintain" ? 0 : AGGRESSIVENESS_RATES[pace][seg.type];
    const targetWeight = Math.round((curWeight + rate * seg.defaultWeeks) * 10) / 10;
    rows.push({
      type: seg.type,
      startDate: curDate,
      startWeight: String(Math.round(curWeight * 10) / 10),
      endDate,
      targetWeight: String(targetWeight),
      aggressiveness: pace,
      lastEdited: "endDate",
      planMethod: "byDate",
    });
    curDate = endDate;
    curWeight = targetWeight;
  }
  return rows;
}

// Propagate startDate/startWeight downstream through wizard rows after a row changes
function propagateWizardRows(rows: WizardPhaseRow[], fromIndex: number): WizardPhaseRow[] {
  const out = [...rows];
  for (let i = fromIndex + 1; i < out.length; i++) {
    const prevTargetWeight = safeNumber(out[i - 1].targetWeight) ?? safeNumber(out[i - 1].startWeight) ?? 0;
    const prevEndDate = out[i - 1].endDate;
    const prevStartDate = out[i - 1].startDate;
    // Recompute this row's start fields
    const newStartDate = prevEndDate || addDaysYMD(prevStartDate, 7) || prevStartDate;
    const newStartWeight = prevTargetWeight;
    // Keep existing aggressiveness, type — recompute the dependent field based on planMethod
    const row = out[i];
    const pm = row.planMethod ?? "byDate";
    let newEndDate = row.endDate;
    // Guard: if existing endDate is invalid relative to the propagated startDate, reset to ~3 months out
    if (!newEndDate || newEndDate <= newStartDate) {
      newEndDate = addDaysYMD(newStartDate, 91) ?? newStartDate;
    }
    let newTargetWeight = row.targetWeight;
    if (pm === "byPace" && safeNumber(row.targetWeight) !== null) {
      // byPace: targetWeight is the anchor, recompute endDate
      const ed = computeEndDateFromRate(newStartDate, newStartWeight, safeNumber(row.targetWeight)!, row.type, row.aggressiveness);
      if (ed) newEndDate = ed;
    } else if (newEndDate) {
      // byDate: endDate is the anchor, recompute targetWeight
      const tw = computeTargetWeightFromRate(newStartWeight, newStartDate, newEndDate, row.type, row.aggressiveness);
      if (tw !== null) newTargetWeight = String(tw);
    }
    out[i] = { ...row, startDate: newStartDate, startWeight: String(Math.round(newStartWeight * 10) / 10), endDate: newEndDate, targetWeight: newTargetWeight };
  }
  return out;
}

// Convert finalized wizard rows into RoadmapPhase[]
function wizardRowsToPhases(rows: WizardPhaseRow[]): RoadmapPhase[] {
  return rows.map((row, i) => {
    const startWeight = safeNumber(row.startWeight) ?? 0;
    const endWeight = safeNumber(row.targetWeight) ?? startWeight;
    return {
      id: genId("phase"),
      orderIndex: i,
      type: row.type,
      startDate: row.startDate,
      endDate: row.endDate,
      startWeight,
      endWeight,
    };
  });
}

// ---------------------------
// Data transforms
// ---------------------------

function recalcTheoretical(entriesSortedAsc) {
  // Global theoretical series across *all* data.
  // Note: sprint-scoped theoretical is computed separately so it can reset at sprint start.
  const out = entriesSortedAsc.map((e) => ({ ...e }));
  for (let i = 0; i < out.length; i += 1) {
    if (i === 0) {
      out[i].theoreticalWeight = out[i].weight;
      continue;
    }
    const prev = out[i - 1];
    const prevNet = prev.netCalories ?? 0;
    const delta = prevNet / 3500;
    out[i].theoreticalWeight = (prev.theoreticalWeight ?? prev.weight) + delta;
  }
  return out;
}

function calcSprintTheoreticalByDate(entriesAsc) {
  // Sprint-scoped theoretical series.
  // Resets on the sprint's first entry weight and then applies net calories day-to-day.
  // If netCalories is missing for a day, we treat it as 0 (flat) rather than breaking the series.
  const map = new Map();
  if (!entriesAsc || !entriesAsc.length) return map;

  let theoretical = entriesAsc[0].weight;
  map.set(entriesAsc[0].date, theoretical);

  for (let i = 1; i < entriesAsc.length; i += 1) {
    const prev = entriesAsc[i - 1];
    const prevNet = prev.netCalories ?? 0;
    theoretical = theoretical + prevNet / 3500;
    map.set(entriesAsc[i].date, theoretical);
  }
  return map;
}

function calcExpectedWeightByDate({ timelineDates, sprintStartEffective, startWeight, sprintEntriesAsc, todayYmd, hasCaloriesInLogs }) {
  const map = new Map();
  if (!timelineDates?.length || !sprintStartEffective || startWeight === null || startWeight === undefined || !todayYmd) return map;
  if (!hasCaloriesInLogs) {
    timelineDates.forEach((date) => map.set(date, null));
    return map;
  }

  const netByDate = new Map(sprintEntriesAsc.map((e) => [e.date, Number.isFinite(e.netCalories) ? e.netCalories : null]));

  let expected = startWeight;
  for (let i = 0; i < timelineDates.length; i += 1) {
    const date = timelineDates[i];
    if (date > todayYmd) {
      map.set(date, null);
      continue;
    }
    if (i > 0) {
      const prevDate = timelineDates[i - 1];
      const prevNet = netByDate.get(prevDate);
      const netCalories = prevNet ?? 0;
      expected += netCalories / 3500;
    }
    map.set(date, expected);
  }

  return map;
}

function dayCountInclusive(startYmd, endYmd) {
  if (!startYmd || !endYmd) return null;
  if (endYmd < startYmd) return 0;
  return daysBetweenInclusiveStartExclusiveEnd(startYmd, addDaysYMD(endYmd, 1));
}

function inDateRange(ymd, startYmd, endYmd) {
  if (!ymd) return false;
  if (startYmd && ymd < startYmd) return false;
  if (endYmd && ymd > endYmd) return false;
  return true;
}

function filterSeriesByDate(series, startYmd, endYmd) {
  if (!startYmd && !endYmd) return series;
  return series.filter((d) => inDateRange(d.date, startYmd, endYmd));
}

function buildDailyTimelineWithInferredRange(entriesAsc, startYmd, endYmd, carryForwardUntilYmd = null) {
  if (!entriesAsc.length) return [];

  const sortedEntries = entriesAsc.filter((e) => isYmd(e?.date)).sort((a, b) => (a.date < b.date ? -1 : 1));
  if (!sortedEntries.length) return [];

  const defaultStart = sortedEntries[0].date;
  const rawStart = isYmd(startYmd) ? startYmd : defaultStart;
  const rawEnd = isYmd(endYmd) ? endYmd : sortedEntries[sortedEntries.length - 1].date;

  const start = clampYmdToBounds(rawStart, MIN_YMD, MAX_YMD);
  const endCandidate = clampYmdToBounds(rawEnd, start, MAX_YMD);
  const maxSafeEnd = clampYmdToBounds(addDaysYMD(start, MAX_DAILY_POINTS - 1) ?? endCandidate, start, MAX_YMD);
  const end = endCandidate > maxSafeEnd ? maxSafeEnd : endCandidate;

  const days = daysBetweenClamped(start, end, MAX_DAILY_POINTS);
  if (days === null || end < start) return [];

  const byDate = new Map(sortedEntries.map((e) => [e.date, e]));
  const known = sortedEntries.map((e) => ({ date: e.date, weight: e.weight }));

  const timeline = [];
  let prevIdx = -1;
  let nextIdx = 0;

  for (let i = 0; i < days; i += 1) {
    const d = addDaysYMD(start, i);
    if (!d) continue;

    while (nextIdx < known.length && known[nextIdx].date < d) {
      prevIdx = nextIdx;
      nextIdx += 1;
    }

    const e = byDate.get(d);
    if (e) {
      timeline.push({ date: d, actual: e.weight, inferred: null });
      continue;
    }

    const prev = prevIdx >= 0 ? known[prevIdx] : null;
    const next = nextIdx < known.length ? known[nextIdx] : null;

    let inferred = null;
    if (prev && next) {
      const span = daysBetweenInclusiveStartExclusiveEnd(prev.date, next.date);
      const offset = daysBetweenInclusiveStartExclusiveEnd(prev.date, d);
      if (span && offset !== null) {
        const t = offset / span;
        inferred = prev.weight + (next.weight - prev.weight) * t;
      }
    } else if (prev && !next && carryForwardUntilYmd && d <= carryForwardUntilYmd) {
      inferred = prev.weight;
    }

    timeline.push({ date: d, actual: null, inferred });
  }

  return timeline;
}

function buildDailyTimelineWithInferred(entriesAsc, endYmd) {
  if (!entriesAsc.length) return [];
  return buildDailyTimelineWithInferredRange(entriesAsc, entriesAsc[0].date, endYmd);
}

function addDailyMovingAverage(dailyPoints, window = 7, maxDateKey = null, method = "endpointWeighted") {
  const out = dailyPoints.map((d) => ({ ...d }));
  const vals = out.map((d) => {
    const v = d.actual ?? d.inferred;
    return Number.isFinite(v) ? v : null;
  });

  const normalizedMethod = method === "sma" ? "sma" : "endpointWeighted";

  for (let i = 0; i < out.length; i += 1) {
    if (isYmd(maxDateKey) && out[i]?.date > maxDateKey) {
      out[i].movingAvg = null;
      continue;
    }

    const start = Math.max(0, i - (window - 1));
    let sum = 0;
    let weightSum = 0;
    let count = 0;
    let observed = 0;
    for (let j = start; j <= i; j += 1) {
      if (isYmd(maxDateKey) && out[j]?.date > maxDateKey) continue;
      const v = vals[j];
      if (v === null) continue;
      observed += 1;
      const weight = normalizedMethod === "endpointWeighted" ? observed : 1;
      sum += v * weight;
      weightSum += weight;
      count += 1;
    }
    if (count < 2) {
      out[i].movingAvg = null;
      continue;
    }

    out[i].movingAvg = normalizedMethod === "endpointWeighted"
      ? (weightSum > 0 ? sum / weightSum : null)
      : sum / count;
  }

  return out;
}

function getCurrentWeightAnchor({
  chartData,
  movingAvgDays,
  todayKey,
  sprintStartWeight,
  sprintStartDateKey,
}) {
  const safeChart = Array.isArray(chartData) ? chartData : [];
  const sorted = safeChart
    .filter((d) => isYmd(d?.date) && d.date <= todayKey)
    .sort((a, b) => (a.date < b.date ? -1 : 1));

  for (let i = sorted.length - 1; i >= 0; i -= 1) {
    const d = sorted[i];
    if (Number.isFinite(d.movingAvg)) {
      return {
        weight: d.movingAvg,
        dateKey: d.date,
        source: "movingAvg",
        meta: { windowDays: movingAvgDays, throughDateKey: d.date },
      };
    }
  }

  for (let i = sorted.length - 1; i >= 0; i -= 1) {
    const d = sorted[i];
    if (Number.isFinite(d.actual)) {
      return {
        weight: d.actual,
        dateKey: d.date,
        source: "lastLogged",
        meta: { throughDateKey: d.date },
      };
    }
  }

  for (let i = sorted.length - 1; i >= 0; i -= 1) {
    const d = sorted[i];
    if (Number.isFinite(d.inferred)) {
      return {
        weight: d.inferred,
        dateKey: d.date,
        source: "inferred",
        meta: { throughDateKey: d.date },
      };
    }
  }

  if (Number.isFinite(sprintStartWeight) && isYmd(sprintStartDateKey)) {
    return {
      weight: sprintStartWeight,
      dateKey: sprintStartDateKey,
      source: "sprintStart",
      meta: { throughDateKey: sprintStartDateKey },
    };
  }

  return null;
}

function countTimelineDays(points) {
  return points.reduce((acc, d) => {
    const v = d.actual ?? d.inferred;
    return Number.isFinite(v) ? acc + 1 : acc;
  }, 0);
}

function countActualEntryDays(points) {
  return points.reduce((acc, d) => (Number.isFinite(d.actual) ? acc + 1 : acc), 0);
}

function suggestedMovingAverageWindowFromData(timelineDayCount, actualEntryCount) {
  if (!timelineDayCount || timelineDayCount <= 0) return 7;

  let baseWindow = 7;
  if (timelineDayCount <= 14) baseWindow = 3;
  else if (timelineDayCount <= 31) baseWindow = 5;
  else if (timelineDayCount <= 62) baseWindow = 7;
  else if (timelineDayCount <= 124) baseWindow = 10;
  else if (timelineDayCount <= 186) baseWindow = 14;
  else if (timelineDayCount <= 365) baseWindow = 21;
  else baseWindow = 30;

  if (!actualEntryCount || actualEntryCount <= 2) return 3;
  if (actualEntryCount <= 5) return Math.min(baseWindow, 5);
  if (actualEntryCount <= 10) return Math.min(baseWindow, 7);

  const density = actualEntryCount / timelineDayCount;
  if (density < 0.15) return Math.min(baseWindow, 7);
  if (density < 0.3) return Math.min(baseWindow, 10);
  if (density < 0.5) return Math.min(baseWindow, 14);

  return baseWindow;
}

function calcRegressionSlopePerDay(points) {
  if (!Array.isArray(points) || points.length < 2) return null;
  const firstDate = points[0]?.date;
  if (!isYmd(firstDate)) return null;

  let sumX = 0;
  let sumY = 0;
  let sumXY = 0;
  let sumXX = 0;
  let n = 0;

  for (const p of points) {
    if (!isYmd(p?.date) || !Number.isFinite(p?.value)) continue;
    const x = daysBetweenInclusiveStartExclusiveEnd(firstDate, p.date);
    if (x === null) continue;
    const y = p.value;
    sumX += x;
    sumY += y;
    sumXY += x * y;
    sumXX += x * x;
    n += 1;
  }

  if (n < 2) return null;
  const denom = n * sumXX - sumX * sumX;
  if (Math.abs(denom) < 1e-9) return null;
  const slope = (n * sumXY - sumX * sumY) / denom;
  const intercept = (sumY - slope * sumX) / n;
  return { slopePerDay: slope, intercept, firstDate, count: n };
}

function calcForecastSeries({ lastDate, currentWeight, targetDate, effectiveNetCalories }) {
  if (!currentWeight || !lastDate || !targetDate || effectiveNetCalories === null) return [];
  const days = daysBetweenInclusiveStartExclusiveEnd(lastDate, targetDate);
  if (!days || days <= 0) return [];

  const start = parseISODateYMD(lastDate);
  let w = currentWeight;
  const dailyDelta = effectiveNetCalories / 3500;

  const series = [];
  for (let i = 1; i <= days; i += 1) {
    w += dailyDelta;
    const dt = new Date(start);
    dt.setDate(start.getDate() + i);
    const ymd = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
    series.push({ date: ymd, projected: w });
  }
  return series;
}

function netColor(net) {
  if (net === null) return "rgba(255,255,255,0.15)";
  if (net === 0) return "rgba(255,255,255,0.25)";

  const abs = Math.abs(net);
  const intensity = clamp((abs - 100) / (1500 - 100), 0, 1);

  if (net < 0) {
    const r = Math.round(46 * (1 - intensity) + 16 * intensity);
    const g = Math.round(220 * (1 - intensity) + 185 * intensity);
    const b = Math.round(120 * (1 - intensity) + 129 * intensity);
    return `rgb(${r},${g},${b})`;
  }

  const r = Math.round(234 * (1 - intensity) + 239 * intensity);
  const g = Math.round(179 * (1 - intensity) + 68 * intensity);
  const b = Math.round(8 * (1 - intensity) + 68 * intensity);
  return `rgb(${r},${g},${b})`;
}

function csvEscape(value) {
  const s = value === null || value === undefined ? "" : String(value);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function entriesToCsv(entriesAsc) {
  const headers = ["date", "weight", "caloriesIn", "totalBurned", "activeCalories", "netCalories", "bmr", "theoreticalWeight"];
  const rows = [...entriesAsc].sort((a, b) => (a.date < b.date ? -1 : 1));
  const lines = [headers.join(",")];
  for (const e of rows) lines.push(headers.map((h) => csvEscape(e[h])).join(","));
  return lines.join("\n");
}

// Minimal, clean template for CSV imports.
// Supported columns on import:
// - required: date, weight
// - optional: caloriesIn, totalBurned, activeCalories, netCalories
//
// Import rules:
// - If totalBurned is present, it's treated as truth (even if activeCalories is present).
// - If activeCalories is present and totalBurned is blank, we compute totalBurned = (BMR*1.2 + activeCalories)
//   using vitals and age *as of that entry date*, then store totalBurned in the DB.
// - netCalories: if provided, we keep it; else if caloriesIn and totalBurned are both known, we compute it.
const EXAMPLE_IMPORT_CSV = `date,weight,caloriesIn,activeCalories,totalBurned,netCalories
2026-02-12,187.2,1600,650,,
2026-02-13,187.0,1500,520,2750,
`;

function parseCsvRow(line) {
  // Minimal CSV parser w/ quotes support.
  const out = [];
  let cur = "";
  let inQuotes = false;
  for (let i = 0; i < line.length; i += 1) {
    const ch = line[i];
    if (inQuotes) {
      if (ch === '"') {
        if (line[i + 1] === '"') {
          cur += '"';
          i += 1;
        } else {
          inQuotes = false;
        }
      } else {
        cur += ch;
      }
    } else {
      if (ch === ",") {
        out.push(cur);
        cur = "";
      } else if (ch === '"') {
        inQuotes = true;
      } else {
        cur += ch;
      }
    }
  }
  out.push(cur);
  return out.map((s) => String(s).trim());
}

function normalizeHeader(h) {
  return String(h || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "")
    .replace(/_/g, "");
}

function importEntriesFromCsvText(csvText) {
  const lines = String(csvText || "")
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter((l) => l.length);
  if (!lines.length) return { rows: [], errors: ["Empty CSV"] };

  const headers = parseCsvRow(lines[0]).map(normalizeHeader);
  const idxDate = headers.indexOf("date");
  const idxWeight = headers.indexOf("weight");
  const idxCaloriesIn = headers.indexOf("caloriesin");

  // Allow a few variants
  const idxTotalBurned = (() => {
    const i = headers.indexOf("totalburned");
    if (i >= 0) return i;
    const j = headers.indexOf("tdee");
    if (j >= 0) return j;
    return -1;
  })();

  const idxActiveCalories = (() => {
    const i = headers.indexOf("activecalories");
    if (i >= 0) return i;
    const j = headers.indexOf("active");
    if (j >= 0) return j;
    return -1;
  })();

  const idxNetCalories = (() => {
    const i = headers.indexOf("netcalories");
    if (i >= 0) return i;
    const j = headers.indexOf("net");
    if (j >= 0) return j;
    return -1;
  })();

  // Accept 2-col CSV (Date,Weight) even without header match
  const assumeTwoCols = idxDate === -1 && idxWeight === -1 && headers.length >= 2;

  const rows = [];
  const errors = [];

  for (let i = 1; i < lines.length; i += 1) {
    const cols = parseCsvRow(lines[i]);

    const date = assumeTwoCols ? (cols[0] || "").trim() : (cols[idxDate] || "").trim();
    const wRaw = assumeTwoCols ? cols[1] : cols[idxWeight];

    if (!isYmd(date)) {
      errors.push(`Row ${i + 1}: invalid date "${date}"`);
      continue;
    }

    const weight = safeNumber(wRaw);
    if (weight === null) {
      // allow blank weights (skip)
      continue;
    }

    const caloriesIn = idxCaloriesIn >= 0 ? safeNumber(cols[idxCaloriesIn]) : null;
    const totalBurned = idxTotalBurned >= 0 ? safeNumber(cols[idxTotalBurned]) : null;
    const activeCalories = idxActiveCalories >= 0 ? safeNumber(cols[idxActiveCalories]) : null;
    const netCalories = idxNetCalories >= 0 ? safeNumber(cols[idxNetCalories]) : null;

    rows.push({ date, weight, caloriesIn, totalBurned, activeCalories, netCalories });
  }

  // de-dupe by date (last one wins)
  const byDate = new Map();
  for (const r of rows) byDate.set(r.date, r);
  const deduped = Array.from(byDate.values()).sort((a, b) => (a.date < b.date ? -1 : 1));

  return { rows: deduped, errors };
}

function effectiveSprintStart(firstEverYmd, sprintStartDate) {
  if (!firstEverYmd) return null;
  if (!sprintStartDate) return firstEverYmd;
  return sprintStartDate < firstEverYmd ? firstEverYmd : sprintStartDate;
}

function getSprintAnchorWeight(entriesAsc, sprintStartDate, sprintEndDate = null) {
  if (!Array.isArray(entriesAsc) || !isYmd(sprintStartDate)) {
    return { anchorWeight: null, anchorDate: null, anchorSource: "none" };
  }

  const inWindow = entriesAsc.filter((e) => {
    if (!e || !isYmd(e.date)) return false;
    if (e.date < sprintStartDate) return false;
    if (sprintEndDate && e.date > sprintEndDate) return false;
    return true;
  });

  const startDateEntry = inWindow.find((e) => e.date === sprintStartDate && safeNumber(e.weight) !== null);
  if (startDateEntry) {
    return { anchorWeight: startDateEntry.weight, anchorDate: startDateEntry.date, anchorSource: "start-date" };
  }

  const firstInSprint = inWindow.find((e) => safeNumber(e.weight) !== null);
  if (firstInSprint) {
    return { anchorWeight: firstInSprint.weight, anchorDate: firstInSprint.date, anchorSource: "first-in-sprint" };
  }

  return { anchorWeight: null, anchorDate: null, anchorSource: "none" };
}

function downloadTextFile(filename, text) {
  try {
    const blob = new Blob([text], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  } catch (e) {
    console.error("Download failed", e);
  }
}

const PROFILE_SCHEMA_VERSION = 2;

function normalizeRoadmapForProfile(rawRoadmap, validSprintIds: Set<string>, fallbackStartDate: string) {
  if (!rawRoadmap || typeof rawRoadmap !== "object") return null;

  const rawPhases = Array.isArray(rawRoadmap.phases) ? rawRoadmap.phases : [];
  if (!rawPhases.length) return null;

  const stagedPhases = rawPhases
    .map((phase, idx) => {
      if (!phase || typeof phase !== "object") return null;
      const type = phase.type === "cut" || phase.type === "bulk" || phase.type === "maintain" ? phase.type : null;
      if (!type) return null;
      return {
        id: String(phase.id ?? "").trim() || genId("phase"),
        orderIndex: safeNumber(phase.orderIndex) ?? idx,
        type,
        startDate: toYmdLoose(phase.startDate),
        endDate: toYmdLoose(phase.endDate),
        startWeight: safeNumber(phase.startWeight),
        endWeight: safeNumber(phase.endWeight),
      };
    })
    .filter(Boolean)
    .sort((a, b) => (a.orderIndex === b.orderIndex ? (a.id < b.id ? -1 : 1) : a.orderIndex - b.orderIndex));

  if (!stagedPhases.length) return null;

  const normalizedPhases: RoadmapPhase[] = [];
  for (let idx = 0; idx < stagedPhases.length; idx += 1) {
    const phase = stagedPhases[idx];
    if (idx === 0) {
      const startDate = isYmd(phase.startDate) ? phase.startDate : fallbackStartDate;
      const minEndDate = addDaysYMD(startDate, 1) ?? startDate;
      const endDate = isYmd(phase.endDate) && phase.endDate >= minEndDate ? phase.endDate : minEndDate;
      const startWeight = phase.startWeight ?? phase.endWeight ?? 0;
      const endWeight = phase.endWeight ?? startWeight;
      normalizedPhases.push({ ...phase, orderIndex: 0, startDate, endDate, startWeight, endWeight });
      continue;
    }

    const prev = normalizedPhases[idx - 1];
    const startDate = prev.endDate;
    const minEndDate = addDaysYMD(startDate, 1) ?? startDate;
    const endDate = isYmd(phase.endDate) && phase.endDate >= minEndDate ? phase.endDate : minEndDate;
    const startWeight = prev.endWeight;
    const endWeight = phase.endWeight ?? startWeight;
    normalizedPhases.push({ ...phase, orderIndex: idx, startDate, endDate, startWeight, endWeight });
  }

  const phaseIdSet = new Set(normalizedPhases.map((p) => p.id));
  const rawLinks = rawRoadmap.phaseSprintLinks && typeof rawRoadmap.phaseSprintLinks === "object" ? rawRoadmap.phaseSprintLinks : {};
  const phaseSprintLinks = Object.fromEntries(
    Object.entries(rawLinks)
      .filter(([phaseId, sprintId]) => phaseIdSet.has(phaseId) && typeof sprintId === "string" && validSprintIds.has(sprintId))
      .sort(([a], [b]) => (a < b ? -1 : 1))
  );

  return {
    id: String(rawRoadmap.id ?? "").trim() || genId("roadmap"),
    name: String(rawRoadmap.name ?? "Roadmap").trim() || "Roadmap",
    phases: normalizedPhases,
    createdAt: rawRoadmap.createdAt ? String(rawRoadmap.createdAt) : new Date().toISOString(),
    ...(Object.keys(phaseSprintLinks).length ? { phaseSprintLinks } : {}),
  };
}

function migrateProfilePayload(raw) {
  const sourceVersion = safeNumber(raw?.profileVersion ?? raw?.version) ?? 0;
  const base = { ...(raw || {}) };

  if (!Array.isArray(base.roadmaps)) {
    if (base.roadmap && typeof base.roadmap === "object") base.roadmaps = [base.roadmap];
    else base.roadmaps = [];
  }

  base.version = PROFILE_SCHEMA_VERSION;
  base.profileVersion = PROFILE_SCHEMA_VERSION;
  return { payload: base, sourceVersion };
}

function buildProfileExportPayload({ vitals, entries, sprints, forecast, roadmap }) {
  const validSprintIds = new Set((Array.isArray(sprints) ? sprints : []).map((s) => String(s?.id ?? "").trim()).filter(Boolean));
  const normalizedRoadmap = roadmap ? normalizeRoadmapForProfile(roadmap, validSprintIds, toYmdLoose(new Date()) || "1970-01-01") : null;
  return {
    version: PROFILE_SCHEMA_VERSION,
    profileVersion: PROFILE_SCHEMA_VERSION,
    exportedAt: new Date().toISOString(),
    vitals: {
      height: safeNumber(vitals?.height),
      gender: String(vitals?.gender ?? "").trim().toUpperCase(),
      birthday: toYmdLoose(vitals?.birthday),
      activityLevel: getActivityOptionByLevel(String(vitals?.activityLevel ?? "").trim())?.value ?? getActivityOptionByFactor(vitals?.activityFactor).value,
      activityFactor: safeNumber(vitals?.activityFactor),
      currentWeightLbs: safeNumber(vitals?.currentWeightLbs),
    },
    entries: Array.isArray(entries)
      ? entries.map((e) => ({
        date: toYmdLoose(e?.date),
        weight: safeNumber(e?.weight),
        caloriesIn: safeNumber(e?.caloriesIn),
        totalBurned: safeNumber(e?.totalBurned),
        activeCalories: safeNumber(e?.activeCalories),
        netCalories: safeNumber(e?.netCalories),
        bmr: safeNumber(e?.bmr),
        tdeeSource: typeof e?.tdeeSource === "string" ? e.tdeeSource : null,
        updatedAt: e?.updatedAt ? String(e.updatedAt) : null,
      }))
      : [],
    sprints: Array.isArray(sprints)
      ? sprints.map((s) => ({
        id: String(s?.id ?? "").trim(),
        name: sanitizeSprintName(s?.name),
        startDate: toYmdLoose(s?.startDate),
        endDate: s?.endDate ? toYmdLoose(s.endDate) : null,
        color: String(s?.color ?? SPRINT_PALETTE[0]),
        createdAt: s?.createdAt ? String(s.createdAt) : null,
        updatedAt: s?.updatedAt ? String(s.updatedAt) : null,
      }))
      : [],
    forecast: {
      activeSprintId: forecast?.activeSprintId ? String(forecast.activeSprintId) : null,
      sprintStartDate: forecast?.sprintStartDate ? toYmdLoose(forecast.sprintStartDate) : null,
      targetWeight: safeNumber(forecast?.targetWeight),
      targetDate: forecast?.targetDate ? toYmdLoose(forecast.targetDate) : null,
      assumedNetCalories: safeNumber(forecast?.assumedNetCalories),
      inputMode: forecast?.inputMode === "intake" ? "intake" : "net",
      planHistory: normalizeForecastPlanHistory(forecast?.planHistory),
      sprintStatusDisplayMode: normalizeSprintStatusDisplayMode(forecast?.sprintStatusDisplayMode),
    },
    roadmaps: normalizedRoadmap ? [normalizedRoadmap] : [],
  };
}

function importProfilePayload(raw) {
  if (!raw || typeof raw !== "object") {
    return { error: "Profile import failed: top-level JSON object is required." };
  }

  const { payload: migrated, sourceVersion } = migrateProfilePayload(raw);

  const nowIso = new Date().toISOString();
  const vitalsRaw = migrated.vitals;
  const entriesRaw = migrated.entries;
  const sprintsRaw = migrated.sprints;
  const forecastRaw = migrated.forecast;
  const roadmapsRaw = migrated.roadmaps;

  if (!vitalsRaw || typeof vitalsRaw !== "object") {
    return { error: "Profile import failed: missing vitals object." };
  }
  if (!Array.isArray(entriesRaw)) {
    return { error: "Profile import failed: entries must be an array." };
  }
  if (!Array.isArray(sprintsRaw)) {
    return { error: "Profile import failed: sprints must be an array." };
  }
  if (!forecastRaw || typeof forecastRaw !== "object") {
    return { error: "Profile import failed: missing forecast object." };
  }
  if (!Array.isArray(roadmapsRaw)) {
    return { error: "Profile import failed: roadmaps must be an array when provided." };
  }

  const birthday = toYmdLoose(vitalsRaw.birthday);
  if (!isYmd(birthday)) {
    return { error: "Profile import failed: vitals.birthday must use YYYY-MM-DD." };
  }
  const gender = String(vitalsRaw.gender ?? "").trim().toUpperCase();
  if (!(gender === "M" || gender === "F")) {
    return { error: "Profile import failed: vitals.gender must be M or F." };
  }

  const nextVitals = normalizeVitals({
    ...DEFAULT_VITALS,
    height: safeNumber(vitalsRaw.height) ?? DEFAULT_VITALS.height,
    gender,
    birthday,
    activityLevel: getActivityOptionByLevel(String(vitalsRaw.activityLevel ?? "").trim())?.value ?? getActivityOptionByFactor(vitalsRaw.activityFactor).value,
    activityFactor: safeNumber(vitalsRaw.activityFactor) ?? DEFAULT_VITALS.activityFactor,
    currentWeightLbs: safeNumber(vitalsRaw.currentWeightLbs),
  });

  const byDate = new Map();
  for (let i = 0; i < entriesRaw.length; i += 1) {
    const row = entriesRaw[i];
    if (!row || typeof row !== "object") {
      return { error: `Profile import failed: entries[${i}] must be an object.` };
    }
    const date = toYmdLoose(row.date);
    if (!isYmd(date)) {
      return { error: `Profile import failed: entries[${i}].date must use YYYY-MM-DD.` };
    }
    byDate.set(date, {
      date,
      weight: safeNumber(row.weight),
      caloriesIn: safeNumber(row.caloriesIn),
      totalBurned: safeNumber(row.totalBurned),
      activeCalories: safeNumber(row.activeCalories),
      netCalories: safeNumber(row.netCalories),
      bmr: safeNumber(row.bmr),
      theoreticalWeight: safeNumber(row.weight),
      updatedAt: row.updatedAt ? String(row.updatedAt) : nowIso,
    });
  }

  const sprintById = new Map();
  for (let i = 0; i < sprintsRaw.length; i += 1) {
    const sprint = sprintsRaw[i];
    if (!sprint || typeof sprint !== "object") {
      return { error: `Profile import failed: sprints[${i}] must be an object.` };
    }
    const id = String(sprint.id ?? "").trim();
    if (!id) {
      return { error: `Profile import failed: sprints[${i}].id is required.` };
    }
    const startDate = toYmdLoose(sprint.startDate);
    if (!isYmd(startDate)) {
      return { error: `Profile import failed: sprints[${i}].startDate must use YYYY-MM-DD.` };
    }
    const endDateRaw = sprint.endDate == null ? null : toYmdLoose(sprint.endDate);
    if (endDateRaw !== null && !isYmd(endDateRaw)) {
      return { error: `Profile import failed: sprints[${i}].endDate must use YYYY-MM-DD when provided.` };
    }
    const createdAt = sprint.createdAt ? toYmdLoose(sprint.createdAt) : startDate;
    const updatedAt = sprint.updatedAt ? String(sprint.updatedAt) : nowIso;
    sprintById.set(id, {
      id,
      name: sanitizeSprintName(sprint.name, "Sprint"),
      startDate,
      endDate: endDateRaw,
      color: SPRINT_PALETTE.includes(sprint.color) ? sprint.color : pickNextSprintColor(Array.from(sprintById.values())),
      createdAt: isYmd(createdAt) ? createdAt : startDate,
      updatedAt,
      forecastEndDate: sprint.forecastEndDate && isYmd(toYmdLoose(sprint.forecastEndDate)) ? toYmdLoose(sprint.forecastEndDate) : null,
    });
  }

  const nextSprints = Array.from(sprintById.values()).sort((a, b) => {
    if (a.startDate === b.startDate) return a.id < b.id ? -1 : 1;
    return a.startDate < b.startDate ? -1 : 1;
  });

  const nextForecast = {
    ...DEFAULT_FORECAST,
    activeSprintId: forecastRaw.activeSprintId ? String(forecastRaw.activeSprintId) : null,
    sprintStartDate: forecastRaw.sprintStartDate ? toYmdLoose(forecastRaw.sprintStartDate) : null,
    targetWeight: safeNumber(forecastRaw.targetWeight),
    targetDate: forecastRaw.targetDate ? toYmdLoose(forecastRaw.targetDate) : null,
    assumedNetCalories: safeNumber(forecastRaw.assumedNetCalories),
    inputMode: forecastRaw.inputMode === "intake" ? "intake" : "net",
    planHistory: normalizeForecastPlanHistory(forecastRaw.planHistory),
    sprintStatusDisplayMode: normalizeSprintStatusDisplayMode(forecastRaw.sprintStatusDisplayMode),
  };

  if (nextForecast.sprintStartDate && !isYmd(nextForecast.sprintStartDate)) {
    return { error: "Profile import failed: forecast.sprintStartDate must use YYYY-MM-DD when provided." };
  }
  if (nextForecast.targetDate && !isYmd(nextForecast.targetDate)) {
    return { error: "Profile import failed: forecast.targetDate must use YYYY-MM-DD when provided." };
  }
  if (nextForecast.activeSprintId && !nextSprints.some((s) => s.id === nextForecast.activeSprintId)) {
    return { error: "Profile import failed: forecast.activeSprintId does not match any sprint id." };
  }

  const nextEntries = recalcTheoretical(Array.from(byDate.values()).sort((a, b) => (a.date < b.date ? -1 : 1)));
  const validSprintIds = new Set(nextSprints.map((s) => s.id));
  const fallbackStartDate = nextEntries[nextEntries.length - 1]?.date ?? toYmdLoose(new Date()) ?? "1970-01-01";
  const normalizedRoadmaps = roadmapsRaw
    .map((rm) => normalizeRoadmapForProfile(rm, validSprintIds, fallbackStartDate))
    .filter(Boolean);
  const nextRoadmap = normalizedRoadmaps[0] ?? null;
  return { nextVitals, nextEntries, nextSprints, nextForecast, nextRoadmap, importedProfileVersion: sourceVersion };
}

function genId(prefix = "id") {
  return `${prefix}_${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

function truncateSprintName(name: string, maxLen = 18): string {
  return name.length > maxLen ? name.slice(0, maxLen - 1) + "…" : name;
}

// ---------------------------
// Self-tests (keep simple)
// ---------------------------

function runSelfTests() {
  console.assert(isYmd("2026-02-12"), "YMD validation failed");
  console.assert(!isYmd("2/12/2026"), "YMD validation should fail");


  const series = calcForecastSeries({ lastDate: "2026-02-12", currentWeight: 200, targetDate: "2026-02-15", effectiveNetCalories: -3500 });
  console.assert(series.length === 3, "Forecast days incorrect");
  console.assert(Math.abs(series[0].projected - 199) < 1e-6, "Forecast math incorrect");

  // CSV import (minimal)
  const imported = importEntriesFromCsvText(`date,weight\n2026-02-12,187.2\n2026-02-13,187.0`);
  console.assert(imported.rows.length === 2, "CSV import rows incorrect");
  console.assert(imported.rows[0].date === "2026-02-12", "CSV import date parse failed");

  // CSV import (activeCalories + blank totalBurned)
  const imported2 = importEntriesFromCsvText(`date,weight,caloriesIn,activeCalories\n2026-02-12,187.2,1600,500`);
  console.assert(imported2.rows.length === 1, "CSV import (active) should yield a row");
  console.assert(imported2.rows[0].activeCalories === 500, "CSV import activeCalories parse failed");

  // Roadmap normalization sanity
  const rmNormalized = normalizeRoadmapForProfile({
    id: "rm_1",
    name: "Test",
    phases: [
      { id: "p2", orderIndex: 3, type: "bulk", startDate: "2026-03-01", endDate: "2026-03-05", startWeight: 201, endWeight: 204 },
      { id: "p1", type: "cut", startDate: "2026-02-01", endDate: "2026-02-20", startWeight: 210, endWeight: 201 },
    ],
    phaseSprintLinks: { p2: "missing" },
  }, new Set(), "2026-02-01");
  console.assert(!!rmNormalized, "Roadmap normalization should return a roadmap");
  console.assert(rmNormalized?.phases?.[1]?.startDate === rmNormalized?.phases?.[0]?.endDate, "Roadmap phases should chain startDate");
  console.assert((rmNormalized?.phaseSprintLinks ? Object.keys(rmNormalized.phaseSprintLinks).length : 0) === 0, "Invalid sprint links should be dropped");

  // Age-at-date sanity
  console.assert(calcAgeAtDate("2000-02-10", "2026-02-09") === 25, "Age before birthday should be 25");
  console.assert(calcAgeAtDate("2000-02-10", "2026-02-10") === 26, "Age on birthday should be 26");

  // forecastEndDate behavior (pure helper)
  const helper = (sprint, todayStr, fallback) => {
    const candidate = sprint?.forecastEndDate ?? (sprint?.endDate !== null ? sprint?.endDate : null) ?? fallback ?? todayStr;
    return candidate ?? todayStr;
  };
  console.assert(helper({ endDate: null, forecastEndDate: "2026-05-12" }, "2026-02-12", "2026-05-11") === "2026-05-12", "forecastEndDate should win");
  console.assert(helper({ endDate: "2019-04-08", forecastEndDate: null }, "2026-02-12", "2026-05-11") === "2019-04-08", "Archived should use endDate");

  // Upsert behavior (pure simulation): allow updating calories/TDEE without re-entering weight
  const simulateUpsert = (existingAsc, patch) => {
    const nextMap = new Map(existingAsc.map((e) => [e.date, e]));
    const prev = nextMap.get(patch.date) ?? null;
    const weight = patch.weight ?? prev?.weight ?? null;
    nextMap.set(patch.date, { ...prev, ...patch, weight });
    return Array.from(nextMap.values()).sort((a, b) => (a.date < b.date ? -1 : 1));
  };
  const existing = [{ date: "2026-02-15", weight: 184.8, caloriesIn: null, totalBurned: null, activeCalories: null, netCalories: null, bmr: null, theoreticalWeight: 184.8 }];
  const updated = simulateUpsert(existing, { date: "2026-02-15", weight: null, caloriesIn: 1200, totalBurned: 3000 });
  console.assert(updated[0].weight === 184.8, "Update should preserve existing weight if omitted");
  console.assert(updated[0].caloriesIn === 1200, "Update should set caloriesIn");

  // Sprint-scoped theoretical should reset at the sprint start weight
  const sprint = [
    { date: "2026-02-12", weight: 187.2, netCalories: null },
    { date: "2026-02-13", weight: 187.0, netCalories: -1693 },
    { date: "2026-02-14", weight: 185.6, netCalories: -2030 },
  ];
  const theo = calcSprintTheoreticalByDate(sprint);
  console.assert(Math.abs(theo.get("2026-02-12") - 187.2) < 1e-9, "Sprint theoretical must start at sprint start weight");
  console.assert(Math.abs(theo.get("2026-02-13") - 187.2) < 1e-6, "Theoretical day2 should reflect prev day's net (null treated as 0)");
  console.assert(Math.abs(theo.get("2026-02-14") - (187.2 + -1693 / 3500)) < 1e-6, "Sprint theoretical should apply prev day's net");

  const anchorFallback = getSprintAnchorWeight(
    [
      { date: "2026-02-10", weight: 190.1 },
      { date: "2026-02-14", weight: 188.8 },
    ],
    "2026-02-12",
    null
  );
  console.assert(anchorFallback.anchorSource === "first-in-sprint", "Anchor should fall back to first in-sprint weigh-in");

  const anchorMissing = getSprintAnchorWeight([{ date: "2026-02-11", weight: 190.1 }], "2026-02-12", null);
  console.assert(anchorMissing.anchorSource === "none", "Anchor must not fall back to pre-sprint weigh-ins");

  const reconciled = reconcileEntriesDerivedMetrics(
    [{ date: "2026-02-24", weight: 181.8, caloriesIn: 1257, totalBurned: 3239, activeCalories: null, netCalories: null, bmr: null }],
    { height: 70, gender: "M", birthday: "1990-01-01", activityFactor: 1.3, currentWeightLbs: 181.8 },
  );
  console.assert(reconciled[0].netCalories === -1982, "Net calories should be backfilled when calories-in and TDEE exist");

  // Oura sanity check tests
  runOuraSanityTests();
}

// ---------------------------
// Styles
// ---------------------------

const pageWrap = {
  minHeight: "100vh",
  background: `linear-gradient(180deg, ${COLORS.bg1}, ${COLORS.bg2})`,
  color: "white",
  fontFamily: 'Inter, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Arial',
  padding: "2rem 1.25rem 4rem",
};

const containerStyle = {
  maxWidth: 1600,
  margin: "0 auto",
};

const cardStyle = {
  background: COLORS.card,
  border: `1px solid ${COLORS.cardBorder}`,
  borderRadius: 18,
  padding: "1.5rem",
  backdropFilter: "blur(10px)",
};

const modalBackdropStyle = {
  position: "fixed",
  inset: 0,
  background: "rgba(2,6,23,0.78)",
  zIndex: 10000,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 16,
};

const modalPanelStyle = {
  width: "min(720px, calc(100vw - 32px))",
  maxHeight: "calc(100vh - 32px)",
  overflowY: "hidden",
  overflowX: "hidden",
  borderRadius: 16,
  border: "1px solid rgba(148,163,184,0.35)",
  background: brand.background.card,
  color: "rgba(241,245,249,0.96)",
  boxShadow: "0 24px 64px rgba(2,6,23,0.6)",
  padding: "1rem",
};

const tooltipStyle = {
  background: "rgba(15, 23, 42, 0.9)",
  border: "1px solid rgba(255,255,255,0.15)",
  borderRadius: 12,
  color: "white",
};

const btnBase = {
  border: `1px solid ${COLORS.cardBorder}`,
  borderRadius: 12,
  padding: "0.6rem 1rem",
  cursor: "pointer",
  fontWeight: 700,
};

const settingsButtonStyle = {
  ...btnBase,
  background: "rgba(255,255,255,0.06)",
  color: "white",
  fontSize: "0.9rem",
  padding: "0.62rem 0.95rem",
  borderRadius: 12,
  display: "inline-flex",
  alignItems: "center",
  gap: "0.45rem",
};

const GOAL_FIELD_HEIGHT = 50;
const SPRINT_DATE_FIELD_HEIGHT = 44;
const SPRINT_PLAN_CONTROL_HEIGHT = 46;
const SPRINT_DATE_PRESET_ROW_HEIGHT = 28;
const ONBOARDING_SPRINT_HELPER_ROW_HEIGHT = 12;
const CREATE_SPRINT_LABEL_ROW_MIN_HEIGHT = 22;
const CREATE_SPRINT_HELPER_ROW_MIN_HEIGHT = 18;
const goalLabelRowStyle = {
  color: "rgba(255,255,255,0.75)",
  fontSize: "0.85rem",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "0.45rem",
  minHeight: 20,
};

const createSprintFieldWrapStyle = {
  display: "flex",
  flexDirection: "column",
  gap: 8,
  minWidth: 0,
};

const createSprintLabelRowStyle = {
  ...goalLabelRowStyle,
  minHeight: CREATE_SPRINT_LABEL_ROW_MIN_HEIGHT,
};

const createSprintHelperRowStyle = {
  fontSize: "0.75rem",
  color: "rgba(255,255,255,0.55)",
  minHeight: CREATE_SPRINT_HELPER_ROW_MIN_HEIGHT,
};

const infoIconButtonStyle = {
  border: "1px solid rgba(255,255,255,0.24)",
  background: "rgba(255,255,255,0.05)",
  color: "rgba(255,255,255,0.82)",
  width: 18,
  height: 18,
  borderRadius: 999,
  fontSize: "0.72rem",
  lineHeight: 1,
  cursor: "pointer",
  pointerEvents: "auto",
};

const infoTooltipStyle = {
  position: "absolute",
  top: 24,
  left: 0,
  zIndex: 40,
  maxWidth: 360,
  minWidth: 260,
  padding: "0.72rem 0.8rem",
  borderRadius: 12,
  border: "1px solid rgba(255,255,255,0.24)",
  background: "rgba(15,23,42,0.99)",
  color: "rgba(255,255,255,0.92)",
  fontSize: "0.84rem",
  lineHeight: 1.45,
  boxShadow: "0 10px 28px rgba(2,6,23,0.45)",
  pointerEvents: "auto",
};

const tableHeaderStyle = {
  textAlign: "left",
  padding: "0.9rem 0.75rem",
  color: "rgba(255,255,255,0.65)",
  fontSize: "0.75rem",
  letterSpacing: "0.12em",
  textTransform: "uppercase",
};

const tableCellStyle = {
  padding: "0.9rem 0.75rem",
  color: "rgba(255,255,255,0.92)",
  borderBottom: "1px solid rgba(255,255,255,0.06)",
  whiteSpace: "nowrap",
};

function Pill({ label, tone = "muted", size = "md" }) {
  const map = {
    muted: "rgba(255,255,255,0.12)",
    blue: "rgba(59,130,246,0.20)",
    green: statusColors.positiveSoftBg,
    red: "rgba(239,68,68,0.20)",
    purple: "rgba(139,92,246,0.20)",
    orange: statusColors.warningSoftBg,
    pink: "rgba(236,72,153,0.20)",
  };
  const isSmall = size === "sm";
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: isSmall ? "0.3rem" : "0.4rem",
        padding: isSmall ? "0.16rem 0.48rem" : "0.35rem 0.7rem",
        borderRadius: 999,
        background: map[tone] ?? map.muted,
        border: "1px solid rgba(255,255,255,0.12)",
        color: "rgba(255,255,255,0.92)",
        fontSize: isSmall ? "0.72rem" : "0.85rem",
        lineHeight: 1.15,
        fontWeight: 700,
      }}
    >
      {label}
    </span>
  );
}

function CompactSummaryChip({ label, tone = "muted", maxWidth = 190 }) {
  const map = {
    muted: "rgba(255,255,255,0.12)",
    blue: "rgba(59,130,246,0.20)",
    green: statusColors.positiveSoftBg,
    red: "rgba(239,68,68,0.20)",
  };

  return (
    <span
      title={label}
      style={{
        display: "inline-block",
        maxWidth,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        padding: "0.32rem 0.62rem",
        borderRadius: 999,
        background: map[tone] ?? map.muted,
        border: "1px solid rgba(255,255,255,0.12)",
        color: "rgba(255,255,255,0.92)",
        fontSize: "0.8rem",
        fontWeight: 700,
      }}
    >
      {label}
    </span>
  );
}

function FilteredLegend({ payload, excludeKeys = [], interactiveKeys = [], onItemClick = null }) {
  const items = (payload || []).filter((p) => !excludeKeys.includes(p.dataKey));
  return (
    <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap", justifyContent: "center", paddingTop: "0.5rem" }}>
      {items.map((item) => {
        const interactive = interactiveKeys.includes(item.dataKey) && typeof onItemClick === "function";
        return (
          <button
            type="button"
            key={item.dataKey}
            onClick={interactive ? () => onItemClick(item.dataKey) : undefined}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.4rem",
              color: "rgba(255,255,255,0.82)",
              fontSize: "0.85rem",
              background: interactive ? "rgba(59,130,246,0.14)" : "transparent",
              border: interactive ? "1px solid rgba(59,130,246,0.4)" : "none",
              borderRadius: interactive ? 999 : 0,
              padding: interactive ? "0.28rem 0.62rem" : 0,
              cursor: interactive ? "pointer" : "default",
              opacity: interactive ? 1 : 0.9,
            }}
            title={interactive ? "Click to cycle smoothing" : undefined}
          >
            <span style={{ width: 10, height: 10, borderRadius: 3, background: item.color }} />
            <span>{item.value}</span>
            {interactive ? <svg viewBox="0 0 8 5" width="8" height="5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{opacity:0.85,flexShrink:0}} aria-hidden="true"><polyline points="1,1 4,4 7,1"/></svg> : null}
          </button>
        );
      })}
    </div>
  );
}

function PinnedChip({ label, color }) {
  return (
    <span
      title="Always shown"
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "0.42rem",
        padding: "0.24rem 0.58rem",
        borderRadius: 999,
        background: "rgba(255,255,255,0.06)",
        border: "1px solid rgba(255,255,255,0.14)",
        color: "rgba(255,255,255,0.86)",
        fontSize: "0.78rem",
        fontWeight: 700,
        cursor: "default",
      }}
    >
      <span style={{ width: 8, height: 8, borderRadius: 999, background: color }} />
      {label}
    </span>
  );
}

function PlanMethodToggle({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const opts = [
    { v: "byDate", title: "By date" },
    { v: "byPace", title: "By pace" },
  ];
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.5rem" }}>
      {opts.map(({ v, title }) => (
        <button
          key={v}
          type="button"
          onClick={() => onChange(v)}
          style={{
            textAlign: "left",
            padding: "0.45rem 0.9rem",
            background: value === v ? "rgba(59,130,246,0.22)" : "rgba(255,255,255,0.05)",
            border: value === v ? "1px solid rgba(99,155,255,0.5)" : "1px solid rgba(255,255,255,0.14)",
            borderRadius: 12,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
          }}
        >
          <span style={{ color: "white", fontWeight: 700, fontSize: "0.88rem" }}>{title}</span>
        </button>
      ))}
    </div>
  );
}

function SprintGoalTypeToggle({ value, onChange }: { value: SprintGoalType | null; onChange: (v: SprintGoalType) => void }) {
  const opts: Array<{ v: SprintGoalType; label: string }> = [
    { v: "cut", label: "Cut" },
    { v: "maintain", label: "Maintain" },
    { v: "bulk", label: "Bulk" },
  ];
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: "0.45rem" }}>
      {opts.map((opt) => {
        const active = value === opt.v;
        return (
          <button
            key={opt.v}
            type="button"
            onClick={() => onChange(opt.v)}
            style={{
              ...btnBase,
              minHeight: 36,
              borderRadius: 12,
              border: active ? "1px solid rgba(99,155,255,0.58)" : "1px solid rgba(255,255,255,0.14)",
              background: active ? "linear-gradient(135deg, rgba(59,130,246,0.34), rgba(99,102,241,0.24))" : "rgba(255,255,255,0.06)",
              color: "white",
              fontWeight: 700,
            }}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

function RatePresetChips({ presets, selectedRate, onSelect, detailed = false, showNegative = false, compact = false }: { presets: any[]; selectedRate: any; onSelect: (rate: any) => void; detailed?: boolean; showNegative?: boolean; compact?: boolean }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: detailed ? `repeat(${Math.max(1, presets.length)}, minmax(0, 1fr))` : "unset", gap: "0.3rem", flexWrap: "nowrap", overflowX: detailed ? "visible" : "auto" }}>
      {presets.map((p) => {
        const signedRate = showNegative ? -Math.abs(Number(p.rate)) : Number(p.rate);
        const rateLabel = `${signedRate > 0 ? "+" : ""}${Number(signedRate).toString().replace(/\.0$/, "")} lb/wk`;
        return (
          <button
            key={p.label}
            type="button"
            disabled={p.disabled}
            onClick={() => { if (!p.disabled) onSelect(p.rate); }}
            style={{
              ...btnBase,
              display: "flex",
              flexDirection: detailed ? "column" : "row",
              justifyContent: "center",
              alignItems: "center",
              gap: detailed ? "0.1rem" : "0.25rem",
              padding: detailed ? (compact ? "0.32rem 0.3rem" : "0.45rem 0.55rem") : "0.28rem 0.65rem",
              fontSize: detailed ? (compact ? "0.7rem" : "0.77rem") : "0.8rem",
              fontWeight: 700,
              textAlign: "center",
              background: selectedRate === p.rate && !p.disabled
                ? `linear-gradient(135deg, ${COLORS.blue}, ${COLORS.lightBlue})`
                : "rgba(255,255,255,0.07)",
              color: p.disabled ? "rgba(255,255,255,0.35)" : "white",
              border: selectedRate === p.rate && !p.disabled
                ? `1px solid ${COLORS.lightBlue}`
                : "1px solid rgba(255,255,255,0.14)",
              cursor: p.disabled ? "not-allowed" : "pointer",
            }}
          >
            <span style={{ fontWeight: 800, overflowWrap: "break-word", wordBreak: "break-word" }}>{p.label}</span>
            {detailed ? <span style={{ fontSize: compact ? "0.6rem" : "0.65rem", color: p.disabled ? "rgba(255,255,255,0.3)" : "rgba(255,255,255,0.75)", fontWeight: 600 }}>{rateLabel}</span> : null}
          </button>
        );
      })}
    </div>
  );
}

function ToggleBadge({ label, color, active, onToggle }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      style={{
        ...btnBase,
        padding: "0.28rem 0.66rem",
        borderRadius: 999,
        fontSize: "0.8rem",
        cursor: "pointer",
        background: active ? `${color}33` : "transparent",
        border: `1px solid ${color}`,
        color: active ? "rgba(255,255,255,0.95)" : "rgba(255,255,255,0.62)",
      }}
      title="Toggle overlay"
    >
      <span style={{ width: 9, height: 9, borderRadius: 999, background: color, opacity: active ? 1 : 0.45 }} />
      {label}
    </button>
  );
}

function OverlayLegendRow({ items, visibility, onToggle, title = "Overlay toggles", compact = false }) {
  return (
    <div style={{ display: "grid", gap: compact ? "0.34rem" : "0.42rem", padding: compact ? "0" : "0.85rem", marginTop: "0" }}>
      <div style={{ fontSize: "0.76rem", letterSpacing: "0.08em", textTransform: "uppercase", color: "rgba(255,255,255,0.58)", fontWeight: 700 }}>{title}</div>
      <div style={{ display: "flex", gap: "0.55rem", flexWrap: "nowrap" }}>
        {items.map((item) => (
          <ToggleBadge key={item.key} label={item.label} color={item.color} active={!!visibility[item.key]} onToggle={() => onToggle(item.key)} />
        ))}
      </div>
    </div>
  );
}

function TriFieldRow({ leftLabel, leftField, rightLabel, rightField, chip, mobile = false }) {
  if (mobile) {
    return (
      <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr) minmax(72px, 82px)", gap: "0.24rem 0.5rem", alignItems: "center" }}>
        <div style={{ minHeight: "1.5rem", display: "flex", alignItems: "center", minWidth: 0, paddingRight: "0.24rem", overflow: "hidden", whiteSpace: "nowrap" }}>{leftLabel}</div>
        <div style={{ minHeight: "1.5rem", display: "flex", alignItems: "center", minWidth: 0, overflow: "hidden", whiteSpace: "nowrap" }}>{rightLabel}</div>
        <div style={{ minHeight: "1.5rem" }} />
        <div style={{ minWidth: 0 }}>{leftField}</div>
        <div style={{ minWidth: 0 }}>{rightField}</div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", minHeight: 40, minWidth: 0 }}>
          {chip}
        </div>
      </div>
    );
  }
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 86px", gap: "0.28rem 0.55rem", alignItems: "center" }}>
      <div style={{ minHeight: "1.5rem", display: "flex", alignItems: "center", overflow: "hidden", whiteSpace: "nowrap" }}>{leftLabel}</div>
      <div style={{ minHeight: "1.5rem", display: "flex", alignItems: "center", overflow: "hidden", whiteSpace: "nowrap" }}>{rightLabel}</div>
      <div style={{ minHeight: "1.5rem" }} />
      <div>{leftField}</div>
      <div>{rightField}</div>
      <div style={{ display: "flex", alignItems: "center", minHeight: 44 }}>{chip}</div>
    </div>
  );
}

function FieldLabel({
  text,
  showLock = false,
  lockTitle = "Locked",
  mobile = false,
  style,
}: {
  text: string;
  showLock?: boolean;
  lockTitle?: string;
  mobile?: boolean;
  style?: React.CSSProperties;
}) {
  return (
    <div style={{ ...style, marginBottom: 0, display: "flex", alignItems: "center", gap: mobile ? "0.22rem" : "0.35rem", minWidth: 0 }}>
      <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", minWidth: 0 }}>{text}</span>
      {showLock && (
        <span
          aria-label={lockTitle}
          title={lockTitle}
          style={{
            color: "rgba(255,255,255,0.4)",
            fontWeight: 700,
            fontSize: "0.72rem",
            lineHeight: 1,
            border: mobile ? "none" : "1px solid rgba(255,255,255,0.18)",
            borderRadius: 999,
            padding: mobile ? 0 : "0.05rem 0.3rem",
            flexShrink: 0,
            display: "inline-flex",
            alignItems: "center",
          }}
        >
          <svg viewBox="0 0 12 14" width="11" height="12" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="1" y="6" width="10" height="7" rx="1.5"/><path d="M3.5 6V4.5a2.5 2.5 0 0 1 5 0V6"/></svg>
        </span>
      )}
    </div>
  );
}

function MobileDateField({
  value,
  onCommit,
  disabled = false,
  readOnly = false,
  min,
  max,
  style,
  onCommitStart,
}: {
  value: string;
  onCommit: (ymd: string) => void;
  disabled?: boolean;
  readOnly?: boolean;
  min?: string;
  max?: string;
  style: React.CSSProperties;
  onCommitStart?: () => void;
}) {
  // Format YYYY-MM-DD → MM/DD/YYYY for consistent cross-browser display
  const display = value && value.length === 10
    ? `${value.slice(5, 7)}/${value.slice(8, 10)}/${value.slice(0, 4)}`
    : "";
  const stylePadding = (style && (style as any).padding) ?? "0.55rem 0.65rem";
  // Reserve room on the right for the calendar icon without changing other padding sides.
  const containerStyle: React.CSSProperties = { ...style, padding: stylePadding, paddingRight: 36 };
  const iconActive = !disabled && !readOnly;
  return (
    <div style={{ position: "relative", overflow: "hidden", display: "flex", alignItems: "center", ...containerStyle }}>
      <span style={{ pointerEvents: "none", opacity: display ? 1 : 0.4, zIndex: 1, position: "relative", flex: 1, minWidth: 0, overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>
        {display || "MM/DD/YYYY"}
      </span>
      <span
        aria-hidden="true"
        style={{
          position: "absolute",
          right: 6,
          top: "50%",
          transform: "translateY(-50%)",
          width: 24,
          height: 24,
          borderRadius: 8,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          background: iconActive ? "rgba(59,130,246,0.18)" : "rgba(255,255,255,0.06)",
          border: `1px solid ${iconActive ? "rgba(59,130,246,0.42)" : "rgba(255,255,255,0.12)"}`,
          color: iconActive ? "#93c5fd" : "rgba(255,255,255,0.45)",
          opacity: iconActive ? 1 : 0.5,
          pointerEvents: "none",
          zIndex: 1,
        }}
      >
        <svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <rect x="2" y="3.5" width="12" height="10.5" rx="1.5" />
          <path d="M2 6.5h12" />
          <path d="M5.5 2v2.5" />
          <path d="M10.5 2v2.5" />
        </svg>
      </span>
      <input
        type="date"
        value={value}
        disabled={disabled}
        readOnly={readOnly}
        min={min}
        max={max}
        onClick={(e) => {
          if (disabled || readOnly) return;
          const inputEl = e.currentTarget as HTMLInputElement & { showPicker?: () => void };
          inputEl.showPicker?.();
        }}
        onFocus={(e) => {
          if (disabled || readOnly) return;
          const inputEl = e.currentTarget as HTMLInputElement & { showPicker?: () => void };
          inputEl.showPicker?.();
        }}
        onChange={(e) => {
          const next = e.target.value;
          if (!isYmd(next)) return;
          onCommitStart?.();
          onCommit(next);
        }}
        style={{
          position: "absolute", inset: 0,
          zIndex: 2,
          width: "100%", height: "100%",
          opacity: 0.01,
          background: "transparent",
          color: "transparent",
          WebkitTextFillColor: "transparent",
          caretColor: "transparent",
          cursor: disabled || readOnly ? "default" : "pointer",
          WebkitAppearance: "none" as any,
        }}
      />
    </div>
  );
}



function SprintPlanSummary({ startWeight, targetWeight, startDate = "", targetDate = "", weeklyRate, requiredNet, tdee, gender, calorieView = "net", onCalorieViewChange = () => {}, compact = false }: { startWeight: number | null; targetWeight: number | null; startDate?: string; targetDate?: string; weeklyRate: number | null; requiredNet: number | null; tdee: number | null; gender: string | null | undefined; calorieView?: "net" | "intake"; onCalorieViewChange?: (mode: "net" | "intake") => void; compact?: boolean }) {
  const normalizedWeekly = normalizeNearZero(weeklyRate);
  const phaseType: RoadmapPhaseType = startWeight === null || targetWeight === null
    ? "maintain"
    : targetWeight < startWeight
      ? "cut"
      : targetWeight > startWeight
        ? "bulk"
        : "maintain";
  const signedWeekly = normalizedWeekly === null
    ? null
    : phaseType === "cut"
      ? -Math.abs(normalizedWeekly)
      : phaseType === "bulk"
        ? Math.abs(normalizedWeekly)
        : 0;
  const band = getRateBand(phaseType, Math.abs(normalizedWeekly ?? 0));
  const bandTone = band.warn
    ? { bg: "rgba(251,191,36,0.14)", border: "1px solid rgba(251,191,36,0.3)", color: "rgba(255,237,213,0.96)" }
    : band.severity === "aggressive"
      ? { bg: "rgba(251,191,36,0.14)", border: "1px solid rgba(251,191,36,0.32)", color: "#fde68a" }
      : { bg: "rgba(148,163,184,0.14)", border: "1px solid rgba(148,163,184,0.3)", color: "rgba(226,232,240,0.9)" };
  const metrics = computeSprintPlanMetrics({ startWeight, targetWeight, startDate, targetDate });
  const bounded = resolveBoundedPlanCalories({ requiredNet, tdee, gender });
  const durationLabel = formatSprintDurationLabel(metrics.durationDays);
  return (
    <div style={{ ...createSprintFieldWrapStyle, background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.12)", display: "grid", gap: "0.42rem", padding: "0.9rem" }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: "0.5rem", alignItems: "center", flexWrap: "wrap" }}>
        <div style={{ fontWeight: 800, color: "rgba(255,255,255,0.92)", marginBottom: "0.2rem" }}>Plan summary</div>
        <div style={{ display: "inline-flex", border: "1px solid rgba(255,255,255,0.14)", borderRadius: 999, overflow: "hidden" }}>
          {(["net", "intake"] as const).map((mode) => (
            <button key={mode} type="button" onClick={() => onCalorieViewChange(mode)} style={{ ...btnBase, border: "none", borderRadius: 0, minHeight: 24, padding: "0.16rem 0.52rem", fontSize: "0.72rem", background: calorieView === mode ? "rgba(59,130,246,0.3)" : "rgba(255,255,255,0.05)", color: "white" }}>{mode === "net" ? "Net" : "Intake"}</button>
          ))}
        </div>
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", gap: "0.7rem", flexWrap: "wrap", alignItems: "center" }}>
        <span>Pace</span>
        {signedWeekly === 0 ? <span style={{ color: "rgba(255,255,255,0.72)", fontWeight: 600 }}>Maintain</span> : (
          <span style={{ display: "inline-flex", alignItems: "center", gap: "0.4rem" }}>
            <span style={{ fontWeight: 700 }}>{formatSignedPace(signedWeekly, 1)}</span>
            <span style={{ borderRadius: 999, padding: "0.1rem 0.42rem", fontSize: "0.68rem", fontWeight: 700, background: bandTone.bg, border: bandTone.border, color: bandTone.color }}>{band.warn ? "Above recommended" : getPaceBandDisplayLabel(band.label)}</span>
          </span>
        )}
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", gap: "0.7rem", flexWrap: "wrap" }}>
        <span>{calorieView === "net" ? "Net calories" : "Intake calories"}</span>
        <span style={{ display: "inline-flex", alignItems: "center", gap: "0.35rem", fontWeight: 700 }}>
          <span>{formatCalPerDay(calorieView === "net" ? bounded.net : bounded.intake)}</span>
          {bounded.belowFloor ? (
            <span style={{ fontSize: "0.66rem", color: "rgba(253,224,71,0.95)", border: "1px solid rgba(251,191,36,0.45)", borderRadius: 999, padding: "0.08rem 0.38rem", letterSpacing: "0.02em", fontWeight: 700 }}>Low intake</span>
          ) : null}
        </span>
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", gap: "0.7rem", flexWrap: "wrap" }}><span>Duration</span><span style={{ fontWeight: 700 }}>{durationLabel}</span></div>
      {!compact && <div style={{ display: "flex", justifyContent: "space-between", gap: "0.7rem", flexWrap: "wrap" }}><span>Net weight change</span><span style={{ fontWeight: 700 }}>{metrics.deltaLbs !== null ? `${metrics.deltaLbs > 0 ? "+" : ""}${metrics.deltaLbs.toFixed(1)} lb` : "—"}</span></div>}
      {!compact && <div style={{ display: "flex", justifyContent: "space-between", gap: "0.7rem", flexWrap: "wrap", color: "rgba(255,255,255,0.58)" }}>
        <span>Estimated TDEE</span>
        <span>{formatCalPerDay(tdee)}</span>
      </div>}
    </div>
  );
}

function FieldMetricChip({ label }) {
  return (
    <span style={{
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      width: "100%",
      boxSizing: "border-box",
      padding: "0 0.45rem",
      minHeight: 38,
      borderRadius: 10,
      border: "1px solid",
      fontSize: "0.78rem",
      fontWeight: 800,
      fontVariantNumeric: "tabular-nums",
      letterSpacing: "0.01em",
      whiteSpace: "nowrap",
      background: "rgba(255,255,255,0.07)",
      borderColor: "rgba(255,255,255,0.14)",
      color: "rgba(255,255,255,0.86)",
    }}>{label}</span>
  );
}

function DeltaChip({ startWeight, endWeight }) {
  const sw = safeNumber(startWeight);
  const ew = safeNumber(endWeight);
  if (sw === null || ew === null) return <FieldMetricChip label="—" />;
  const delta = Math.round((ew - sw) * 10) / 10;
  const sign = delta > 0 ? "+" : "";
  const unit = Math.abs(delta) === 1 ? "lb" : "lbs";
  return <FieldMetricChip label={`${sign}${delta.toFixed(1)} ${unit}`} />;
}

function DurationChip({ startDate, endDate }) {
  const weeks = computeDurationWeeks(startDate, endDate);
  if (weeks === null) return <FieldMetricChip label="—" />;
  return <FieldMetricChip label={`${weeks.toFixed(1)}w`} />;
}


function WeightInputWithUnit({ value, onChange, disabled = false, readOnly = false, style = {}, step = undefined, placeholder = undefined }) {
  return (
    <div style={{ position: "relative" }}>
      <input
        type="text"
        inputMode="decimal"
        pattern="[0-9]*"
        value={value}
        onChange={onChange}
        disabled={disabled}
        readOnly={readOnly}
        step={step}
        placeholder={placeholder}
        style={{ ...style, paddingRight: "2.3rem", boxSizing: "border-box" }}
      />
      <span style={{ position: "absolute", right: "0.6rem", top: "50%", transform: "translateY(-50%)", color: "rgba(255,255,255,0.38)", fontSize: "0.78rem", pointerEvents: "none" }}>lbs</span>
    </div>
  );
}

function DurationPresetChips({ startDate, endDate, onSelect, disabled = false }) {
  const selectedPreset = getMatchingDurationPreset(startDate, endDate);
  return (
    <div style={{ display: "flex", gap: "0.25rem", flexWrap: "wrap", overflowX: "visible" }}>
      {DURATION_PRESETS.map((preset) => {
        const isSelected = selectedPreset === preset.key;
        return (
          <button
            key={preset.key}
            type="button"
            disabled={disabled}
            onClick={() => onSelect(addMonthsToYmd(startDate, preset.months))}
            style={{
              ...btnBase,
              minHeight: 24,
              padding: "0.12rem 0.45rem",
              fontSize: "0.7rem",
              lineHeight: 1.1,
              fontWeight: 700,
              background: isSelected ? `linear-gradient(135deg, ${COLORS.blue}, ${COLORS.lightBlue})` : "rgba(255,255,255,0.07)",
              border: isSelected ? `1px solid ${COLORS.lightBlue}` : "1px solid rgba(255,255,255,0.12)",
              color: disabled ? "rgba(255,255,255,0.4)" : "rgba(255,255,255,0.86)",
              opacity: disabled ? 0.6 : 1,
              flexShrink: 0,
              cursor: disabled ? "default" : "pointer",
            }}
          >
            {preset.key}
          </button>
        );
      })}
    </div>
  );
}

function StatCard({ label, value, sub, accent = COLORS.blue }) {
  return (
    <div
      style={{
        ...cardStyle,
        padding: "0.85rem 1rem",
        borderRadius: 14,
      }}
    >
      <div style={{ color: "rgba(255,255,255,0.65)", fontSize: "0.72rem", letterSpacing: "0.1em", textTransform: "uppercase" }}>{label}</div>
      <div style={{ marginTop: "0.4rem", fontSize: "1.35rem", fontWeight: 800, color: accent }}>{value ?? "—"}</div>
      {sub ? <div style={{ marginTop: "0.3rem", fontSize: "0.78rem", color: "rgba(255,255,255,0.58)" }}>{sub}</div> : null}
    </div>
  );
}

function ViewerStatChip({ label, value, sub = null, tone = "muted", subSize = "0.67rem" }: { label: string; value: string; sub?: React.ReactNode; tone?: "muted" | "blue" | "green" | "red" | "orange"; subSize?: string }) {
  const toneMap: Record<string, { border: string; bg: string; value: string }> = {
    green: { border: statusColors.positiveSoftBorder, bg: statusColors.positiveSoftBg, value: statusColors.positiveTextSoft },
    red: { border: "rgba(239,68,68,0.45)", bg: "rgba(239,68,68,0.12)", value: "#fee2e2" },
    orange: { border: statusColors.warningSoftBorder, bg: statusColors.warningSoftBg, value: statusColors.warningTextSoft },
    blue: { border: "rgba(59,130,246,0.45)", bg: "rgba(59,130,246,0.16)", value: "#dbeafe" },
    muted: { border: "rgba(255,255,255,0.16)", bg: "rgba(255,255,255,0.04)", value: "rgba(255,255,255,0.92)" },
  };

  return (
    <div style={{ border: `1px solid ${toneMap[tone]?.border ?? toneMap.muted.border}`, background: toneMap[tone]?.bg ?? toneMap.muted.bg, borderRadius: 12, padding: "0.5rem 0.64rem", minWidth: 0, maxWidth: "100%", overflowWrap: "anywhere" }}>
      <div style={{ fontSize: "0.6rem", letterSpacing: "0.09em", textTransform: "uppercase", color: "rgba(255,255,255,0.62)", fontWeight: 700 }}>{label}</div>
      <div style={{ marginTop: "0.15rem", fontSize: "0.96rem", fontWeight: 900, color: toneMap[tone]?.value ?? toneMap.muted.value }}>{value}</div>
      {sub ? <div style={{ marginTop: "0.14rem", fontSize: subSize, color: "rgba(255,255,255,0.46)", fontWeight: 600 }}>{sub}</div> : null}
    </div>
  );
}

function MinMaxStatChip({ title, min, max, unit, decimals = 0, showSign = true }: { title: string; min: number; max: number; unit: string; decimals?: number; showSign?: boolean }) {
  const fmt = (v: number) => `${showSign ? (v > 0 ? "+" : v < 0 ? "-" : "") : ""}${Math.abs(v).toFixed(decimals)}`;
  const rowStyle: React.CSSProperties = { display: "flex", alignItems: "baseline", gap: "0.35rem", overflow: "hidden" };
  const labelStyle: React.CSSProperties = { fontSize: "0.66rem", color: "rgba(255,255,255,0.52)", fontWeight: 700, flexShrink: 0, width: 28 };
  const valStyle: React.CSSProperties = { fontSize: "0.88rem", fontWeight: 900, color: "rgba(255,255,255,0.92)", whiteSpace: "nowrap" };
  const unitStyle: React.CSSProperties = { fontSize: "0.6rem", color: "rgba(255,255,255,0.46)", fontWeight: 600, whiteSpace: "nowrap" };
  return (
    <div style={{ border: "1px solid rgba(255,255,255,0.2)", background: "rgba(255,255,255,0.05)", borderRadius: 12, padding: "0.5rem 0.64rem", minWidth: 0, maxWidth: "100%", overflow: "hidden" }}>
      <div style={{ fontSize: "0.6rem", letterSpacing: "0.06em", textTransform: "uppercase", color: "rgba(255,255,255,0.62)", fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{title}</div>
      <div style={{ marginTop: "0.18rem", display: "grid", gap: "0.08rem" }}>
        <div style={rowStyle}>
          <span style={labelStyle}>Min</span>
          <span style={valStyle}>{fmt(min)}</span>
          <span style={unitStyle}>{unit}</span>
        </div>
        <div style={rowStyle}>
          <span style={labelStyle}>Max</span>
          <span style={valStyle}>{fmt(max)}</span>
          <span style={unitStyle}>{unit}</span>
        </div>
      </div>
    </div>
  );
}


function Input({ label, value, onChange, type = "text", placeholder = "", right = null, helper = null, disabled = false, readOnly = false, inputRef = null, inputId = undefined, onBlur = null, onKeyDown = null, maxLength = undefined, controlHeight = GOAL_FIELD_HEIGHT, min = undefined, labelRight = null, containerStyle = null, inputContainerStyle = null, inputStyle = null }) {
  return (
    <div style={{ display: "grid", gap: "0.45rem", minWidth: 0, ...containerStyle }}>
      {labelRight ? (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <label htmlFor={inputId} style={{ color: "rgba(255,255,255,0.75)", fontSize: "0.85rem" }}>{label}</label>
          {labelRight}
        </div>
      ) : (
        <label htmlFor={inputId} style={{ color: "rgba(255,255,255,0.75)", fontSize: "0.85rem" }}>{label}</label>
      )}
      <div style={{ display: "grid", gridTemplateColumns: right ? "1fr auto" : "1fr", gap: "0.6rem", minWidth: 0, ...inputContainerStyle }}>
        {type === "date" ? (
          <MobileDateField
            value={value}
            min={min}
            disabled={disabled}
            readOnly={readOnly}
            onCommit={(v) => onChange(v)}
            style={{
              width: "100%",
              maxWidth: "100%",
              padding: "0.75rem 0.85rem",
              borderRadius: 12,
              border: "1px solid rgba(255,255,255,0.14)",
              background: "rgba(255,255,255,0.04)",
              color: "white",
              outline: "none",
              boxSizing: "border-box",
              minHeight: controlHeight,
              opacity: disabled ? 0.6 : 1,
              ...inputStyle,
            }}
          />
        ) : (
          <input
            id={inputId}
            ref={inputRef}
            type={type}
            value={value}
            min={min}
            onChange={(e) => onChange(e.target.value)}
            onBlur={onBlur ? (e) => onBlur(e.target.value, e) : undefined}
            onKeyDown={onKeyDown ? (e) => onKeyDown(e.target.value, e) : undefined}
            placeholder={placeholder}
            maxLength={maxLength}
            disabled={disabled}
            readOnly={readOnly}
            style={{
              width: "100%",
              maxWidth: "100%",
              maxInlineSize: "100%",
              minWidth: 0,
              minInlineSize: 0,
              padding: "0.75rem 0.85rem",
              borderRadius: 12,
              border: "1px solid rgba(255,255,255,0.14)",
              background: "rgba(255,255,255,0.04)",
              color: "white",
              outline: "none",
              boxSizing: "border-box",
              overflow: "hidden",
              textOverflow: "ellipsis",
              minHeight: controlHeight,
              opacity: disabled ? 0.6 : 1,
              ...inputStyle,
            }}
          />
        )}
        {right}
      </div>
      {helper ? <div style={{ fontSize: "0.75rem", color: "rgba(255,255,255,0.55)" }}>{helper}</div> : null}
    </div>
  );
}

function SourceChip({ source, isToday }: { source: "oura" | "manual" | "inferred" | "waiting" | null; isToday?: boolean }) {
  if (!source) return null;
  const map: Record<string, { bg: string; color: string; label: string }> = {
    oura:    { bg: "rgba(99,102,241,0.22)",  color: "#c7d2fe",               label: isToday ? "Oura • Live" : "Oura" },
    manual:  { bg: "rgba(34,197,94,0.16)",   color: "#86efac",               label: "Manual" },
    inferred:{ bg: "rgba(245,158,11,0.18)",  color: "#fcd34d",               label: "Estimated" },
    waiting: { bg: "rgba(255,255,255,0.08)", color: "rgba(255,255,255,0.4)", label: "Oura • Waiting" },
  };
  const s = map[source] ?? map.waiting;
  return (
    <span style={{ fontSize: "0.72rem", fontWeight: 700, padding: "0.15rem 0.45rem",
      borderRadius: 999, background: s.bg, color: s.color,
      border: "1px solid rgba(255,255,255,0.1)", lineHeight: 1.3 }}>
      {s.label}
    </span>
  );
}

function InfoTooltipIcon({ ariaLabel, isOpen, onToggle, onOpen, onClose, children }) {
  const anchorRef = useRef<HTMLSpanElement | null>(null);
  const tipRef = useRef<HTMLDivElement | null>(null);
  const [tipPos, setTipPos] = useState<{ above: boolean; leftPx: number | null }>({ above: false, leftPx: null });

  useLayoutEffect(() => {
    if (!isOpen) return;
    const anchor = anchorRef.current;
    const tip = tipRef.current;
    if (!anchor || !tip) return;

    const anchorRect = anchor.getBoundingClientRect();
    const tipRect = tip.getBoundingClientRect();
    const viewportW = window.innerWidth;
    const viewportH = window.innerHeight;
    const spacing = 8;
    const margin = 8;

    const canFitBelow = (viewportH - anchorRect.bottom) >= (tipRect.height + spacing + margin);
    const above = !canFitBelow;

    let leftPx = (anchorRect.width - tipRect.width) / 2;
    let globalLeft = anchorRect.left + leftPx;
    if (globalLeft < margin) {
      leftPx += (margin - globalLeft);
      globalLeft = margin;
    }
    const rightOverflow = (globalLeft + tipRect.width) - (viewportW - margin);
    if (rightOverflow > 0) {
      leftPx -= rightOverflow;
    }

    setTipPos({ above, leftPx });
  }, [isOpen]);

  return (
    <span ref={anchorRef} style={{ position: "relative", display: "inline-flex", alignItems: "center" }}>
      <button
        type="button"
        aria-label={ariaLabel}
        onClick={onToggle}
        onMouseEnter={onOpen}
        onMouseLeave={onClose}
        onBlur={onClose}
        style={infoIconButtonStyle}
      >
        i
      </button>
      {isOpen && (
        <div
          ref={tipRef}
          role="tooltip"
          style={{
            ...infoTooltipStyle,
            maxWidth: Math.min(360, Math.max(260, (typeof window !== "undefined" ? window.innerWidth : 360) - 24)),
            left: tipPos.leftPx ?? "50%",
            transform: tipPos.leftPx === null ? "translateX(-50%)" : "none",
            top: tipPos.above ? "auto" : "calc(100% + 8px)",
            bottom: tipPos.above ? "calc(100% + 8px)" : "auto",
            zIndex: 22000,
            whiteSpace: "normal",
            wordBreak: "break-word",
          }}
        >
          {children}
        </div>
      )}
    </span>
  );
}

function TargetCalorieInputGroup({
  mode,
  currentSignedValue,
  netDraft,
  intakeDraft,
  onNetDraftChange,
  onIntakeDraftChange,
  onCommitNet,
  onCommitIntake,
  onSetNegative,
  onSetPositive,
  showSignControlsInNetOnly = false,
  controlHeight = GOAL_FIELD_HEIGHT,
}) {
  const isNet = mode === "net";
  const normalizedSigned = normalizeNearZero(safeNumber(currentSignedValue) ?? 0) ?? 0;
  const isNegative = normalizedSigned < 0;
  const isPositive = normalizedSigned > 0;
  const showSignControls = !showSignControlsInNetOnly || isNet;
  return (
    <div
      style={{
        display: "flex",
        alignItems: "stretch",
        width: "100%",
        minWidth: 0,
        minHeight: controlHeight,
        borderRadius: 12,
        border: "1px solid rgba(255,255,255,0.14)",
        background: "rgba(255,255,255,0.04)",
        overflow: "hidden",
      }}
    >
      <input
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        value={isNet ? netDraft : intakeDraft}
        onChange={(e) => {
          if (isNet) onNetDraftChange(e.target.value);
          else onIntakeDraftChange(e.target.value.replace(/-/g, ""));
        }}
        onBlur={isNet ? onCommitNet : onCommitIntake}
        style={{
          flex: 1,
          minWidth: 0,
          width: "100%",
          padding: "0.75rem 0.85rem",
          border: "none",
          borderRadius: 0,
          background: "transparent",
          color: "white",
          outline: "none",
        }}
      />
      {showSignControls && (
      <div
        style={{
          width: 56,
          minWidth: 56,
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          borderLeft: "1px solid rgba(255,255,255,0.14)",
          background: isNet ? "rgba(255,255,255,0.04)" : "rgba(255,255,255,0.02)",
          opacity: isNet ? 1 : 0.55,
        }}
      >
        <button
          type="button"
          disabled={!isNet}
          aria-label="Set net to deficit"
          onClick={onSetNegative}
          style={{
            border: "none",
            borderRadius: 0,
            background: isNet && isNegative ? "rgba(34,197,94,0.28)" : "transparent",
            color: isNet && isNegative ? "#dcfce7" : "rgba(255,255,255,0.8)",
            fontSize: "0.78rem",
            fontWeight: isNet && isNegative ? 900 : 700,
            cursor: isNet ? "pointer" : "default",
          }}
        >
          -
        </button>
        <button
          type="button"
          disabled={!isNet}
          aria-label="Set net to surplus"
          onClick={onSetPositive}
          style={{
            border: "none",
            borderRadius: 0,
            borderLeft: "1px solid rgba(255,255,255,0.12)",
            background: isNet && isPositive ? "rgba(239,68,68,0.26)" : "transparent",
            color: isNet && isPositive ? "#fee2e2" : "rgba(255,255,255,0.8)",
            fontSize: "0.78rem",
            fontWeight: isNet && isPositive ? 900 : 700,
            cursor: isNet ? "pointer" : "default",
          }}
        >
          +
        </button>
      </div>
      )}
    </div>
  );
}

function SprintColorDots({ value, onChange, label = "Color" }) {
  const [open, setOpen] = useState(false);
  const selected = SPRINT_PALETTE.includes(value) ? value : SPRINT_PALETTE[0];
  const pickerRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;

    const closeIfOutside = (event) => {
      if (!pickerRef.current?.contains(event.target)) {
        setOpen(false);
      }
    };

    document.addEventListener("pointerdown", closeIfOutside);
    return () => {
      document.removeEventListener("pointerdown", closeIfOutside);
    };
  }, [open]);

  return (
    <div style={{ display: "grid", gap: "0.45rem", position: "relative" }}>
      <div style={{ color: "rgba(255,255,255,0.75)", fontSize: "0.85rem" }}>{label}</div>
      <div style={{ position: "relative", width: "fit-content", minHeight: 44, display: "flex", alignItems: "center" }} ref={pickerRef}>
        <button
          type="button"
          onClick={() => setOpen((prev) => !prev)}
          style={{
            width: 52,
            height: 30,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderRadius: 9,
            border: "1px solid rgba(255,255,255,0.24)",
            background: "rgba(255,255,255,0.05)",
            cursor: "pointer",
            padding: "0 0.5rem",
          }}
          title="Choose sprint color"
        >
          <span
            aria-hidden
            style={{
              width: 13,
              height: 13,
              borderRadius: 999,
              background: selected,
              border: "1px solid rgba(255,255,255,0.6)",
              boxShadow: "0 0 0 1px rgba(15,23,42,0.35) inset",
            }}
          />
          <svg
            aria-hidden
            viewBox="0 0 8 5"
            width="8"
            height="5"
            fill="none"
            stroke="rgba(255,255,255,0.88)"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{
              transform: open ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform 120ms ease",
              pointerEvents: "none",
              flexShrink: 0,
            }}
          >
            <polyline points="1,1 4,4 7,1"/>
          </svg>
        </button>

        {open ? (
          <div
            onPointerDown={(e) => e.stopPropagation()}
            style={{
              position: "absolute",
              zIndex: 20,
              top: "calc(100% + 0.35rem)",
              right: 0,
              padding: "0.5rem",
              borderRadius: 10,
              border: "1px solid rgba(255,255,255,0.2)",
              background: "rgba(15,23,42,0.97)",
              display: "grid",
              gridTemplateColumns: "repeat(4, 28px)",
              gap: "8px",
            }}
          >
            {SPRINT_PALETTE.map((color) => {
              const active = color === selected;
              return (
                <button
                  type="button"
                  key={color}
                  onPointerDown={(e) => e.stopPropagation()}
                  onClick={() => {
                    onChange(color);
                    setOpen(false);
                  }}
                  title="Select color"
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: 999,
                    border: active ? "2px solid rgba(255,255,255,0.95)" : "1px solid rgba(255,255,255,0.35)",
                    background: color,
                    cursor: "pointer",
                    padding: 0,
                    outline: active ? "2px solid rgba(255,255,255,0.4)" : "none",
                    outlineOffset: 1,
                  }}
                />
              );
            })}
          </div>
        ) : null}
      </div>
    </div>
  );
}

// ---------------------------
// Height helpers
// ---------------------------

function fmtHeightFtIn(totalInches) {
  if (totalInches === null || totalInches === undefined || totalInches === "" || Number.isNaN(Number(totalInches))) return "—";
  const tot = Number(totalInches);
  if (tot <= 0) return "—";
  const ft = Math.floor(tot / 12);
  const ins = Math.round((tot % 12) * 10) / 10;
  return `${ft}'${ins}"`;
}

function getActivityOptionByLevel(level) {
  return ACTIVITY_LEVELS.find((option) => option.value === level) ?? null;
}

function getActivityOptionByFactor(factor) {
  const safeFactor = safeNumber(factor);
  if (safeFactor === null) return ACTIVITY_LEVELS[1];
  return ACTIVITY_LEVELS.reduce((closest, option) => (
    Math.abs(option.factor - safeFactor) < Math.abs(closest.factor - safeFactor) ? option : closest
  ), ACTIVITY_LEVELS[0]);
}

function normalizeVitals(rawVitals) {
  const merged = { ...DEFAULT_VITALS, ...(rawVitals || {}) };
  const levelOption = getActivityOptionByLevel(String(merged.activityLevel ?? "").trim())
    ?? getActivityOptionByFactor(merged.activityFactor);

  return {
    ...merged,
    activityLevel: levelOption.value,
    activityFactor: safeNumber(merged.activityFactor) ?? levelOption.factor,
  };
}

function addMonthsToYmd(ymd, months) {
  if (!isYmd(ymd)) return "";
  const d = new Date(ymd + "T00:00:00");
  d.setMonth(d.getMonth() + months);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

const DURATION_PRESETS = [
  { key: "1M", months: 1 },
  { key: "3M", months: 3 },
  { key: "6M", months: 6 },
  { key: "1Y", months: 12 },
];

function computeDurationWeeks(startYmd, endYmd) {
  const days = daysBetweenInclusiveStartExclusiveEnd(startYmd, endYmd);
  if (days === null || days <= 0) return null;
  return Math.round((days / 7) * 10) / 10;
}

function formatSprintDurationLabel(durationDays: number | null) {
  if (durationDays === null || durationDays <= 0) return "—";
  if (durationDays < 30) return `${durationDays} ${durationDays === 1 ? "day" : "days"}`;
  if (durationDays < 90) {
    const weeks = durationDays / 7;
    return `${weeks.toFixed(1)} ${Math.abs(weeks - 1) < 0.05 ? "week" : "weeks"}`;
  }
  const months = durationDays / 30.4375;
  return `${months.toFixed(1)} ${Math.abs(months - 1) < 0.05 ? "month" : "months"}`;
}

function getMatchingDurationPreset(startYmd, endYmd) {
  if (!isYmd(startYmd) || !isYmd(endYmd)) return null;
  const match = DURATION_PRESETS.find((preset) => addMonthsToYmd(startYmd, preset.months) === endYmd);
  return match?.key ?? null;
}

// ---------------------------
// Main
// ---------------------------

function ModalShell({
  title,
  titleAddon = null,
  ariaLabel,
  onClose,
  panelStyle = {},
  backdropStyle = {},
  children,
}: {
  title: string;
  titleAddon?: React.ReactNode;
  ariaLabel: string;
  onClose: () => void;
  panelStyle?: React.CSSProperties;
  backdropStyle?: React.CSSProperties;
  children: React.ReactNode;
}) {
  if (typeof document === "undefined") return null;
  return createPortal(
    <div style={{ ...modalBackdropStyle, ...backdropStyle }} onClick={onClose}>
      <div role="dialog" aria-modal="true" aria-label={ariaLabel} onClick={(e) => e.stopPropagation()} style={{ ...modalPanelStyle, ...panelStyle }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingBottom: "0.65rem" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", minWidth: 0 }}>
            <div style={{ fontWeight: 800, fontSize: "1rem", color: "rgba(255,255,255,0.95)", letterSpacing: "-0.01em", flexShrink: 0 }}>{title}</div>
            {titleAddon}
          </div>
          <button
            type="button"
            aria-label={`Close ${ariaLabel}`}
            style={{
              width: 30, height: 30, padding: 0,
              display: "flex", alignItems: "center", justifyContent: "center",
              border: "1px solid rgba(255,255,255,0.12)",
              borderRadius: 8,
              background: "rgba(255,255,255,0.05)",
              color: "rgba(255,255,255,0.7)",
              cursor: "pointer",
              fontSize: "0.92rem",
              flexShrink: 0,
            }}
            onClick={onClose}
          >
            <svg viewBox="0 0 10 10" width="10" height="10" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true"><line x1="1" y1="1" x2="9" y2="9"/><line x1="9" y1="1" x2="1" y2="9"/></svg>
          </button>
        </div>
        {children}
      </div>
    </div>,
    document.body
  );
}

export default function WeightTracker({
  userId = null,
  userEmail = null,
  verifiedEmailNotice = null,
  verifiedEmailPrefill = null,
  authSuccessNotice = null,
  redirectAuthMode = null,
  passwordRecoveryActive = false,
  onClearAuthSuccessNotice,
  onClearVerifiedEmailNotice,
  onPasswordRecoveryComplete,
  ouraConnectedNotice = false,
  ouraErrorNotice = null,
  onClearOuraNotices,
}: {
  userId?: string | null;
  userEmail?: string | null;
  verifiedEmailNotice?: string | null;
  verifiedEmailPrefill?: string | null;
  authSuccessNotice?: string | null;
  redirectAuthMode?: "signin" | "signup" | "updatePassword" | null;
  passwordRecoveryActive?: boolean;
  onClearAuthSuccessNotice?: () => void;
  onClearVerifiedEmailNotice?: () => void;
  onPasswordRecoveryComplete?: (email: string | null) => void;
  ouraConnectedNotice?: boolean;
  ouraErrorNotice?: string | null;
  onClearOuraNotices?: () => void;
}) {
  type DemoEntrySource = "onboarding" | "settings";
  type RealProfileSnapshot = {
    entries: any[];
    vitals: any;
    forecast: any;
    sprints: any[];
    roadmap: Roadmap | null;
    currentPage: string;
  };

  const todayYmd = useMemo(() => todayISO(), []);
  const didRunTests = useRef(false);
  useEffect(() => {
    if (!didRunTests.current) {
      didRunTests.current = true;
      try {
        runSelfTests();
      } catch {
        // ignore
      }
    }
  }, []);

  const [currentPage, setCurrentPage] = useState("tracker"); // tracker | trends | all | sprints | settings | viewer
  const [settingsInitialSection, setSettingsInitialSection] = useState<SettingsSection>("profile");
  const [settingsActiveSection, setSettingsActiveSection] = useState<SettingsSection>("profile");
  const [logbookOpenedFromSettings, setLogbookOpenedFromSettings] = useState(false);
  const [isDemoMode, setIsDemoMode] = useState(false);
  const [demoProfileId, setDemoProfileId] = useState<DemoProfileId>("cut_ahead");
  const [demoEntrySource, setDemoEntrySource] = useState<DemoEntrySource>("settings");

  // CSV import (weight-only or full export)
  const [csvImportMsg, setCsvImportMsg] = useState(null);
  const [settingsDataStatusMsg, setSettingsDataStatusMsg] = useState(null);
  const [demoNotice, setDemoNotice] = useState<string | null>(null);

  // Auto-clear settings status messages after 5 seconds so they don't persist across pages.
  useEffect(() => {
    if (!settingsDataStatusMsg) return;
    const t = setTimeout(() => setSettingsDataStatusMsg(null), 5000);
    return () => clearTimeout(t);
  }, [settingsDataStatusMsg]);
  const isShellPreview = useMemo(() => {
    if (typeof window === "undefined") return false;
    const previewMode = new URLSearchParams(window.location.search).get("preview");
    return previewMode === "dashboard" || previewMode === "main" || previewMode === "layout" || previewMode === "shell" || previewMode === "ui";
  }, []);

  const allowPreviewBootstrap = useMemo(() => {
    if (typeof window === "undefined") return false;
    const params = new URLSearchParams(window.location.search);
    // Prevent accidental demo bootstraps from bookmarked preview links in production.
    return import.meta.env.DEV
      || import.meta.env.VITE_ENABLE_PREVIEW_BOOTSTRAP === "true"
      || params.get("previewConfirm") === "1";
  }, []);

  const [showCsvImportModal, setShowCsvImportModal] = useState(false);
  const [showAllSprintHistory, setShowAllSprintHistory] = useState(false);
  const [mobileSprintHistoryVisibleCount, setMobileSprintHistoryVisibleCount] = useState(3);
  const [expandedSprintHistoryRowDate, setExpandedSprintHistoryRowDate] = useState<string | null>(null);
  const [mobileAllDataVisibleCount, setMobileAllDataVisibleCount] = useState(3);
  const [expandedAllDataRowDate, setExpandedAllDataRowDate] = useState<string | null>(null);
  const [restoreProfileFile, setRestoreProfileFile] = useState(null);
  const [restoreConfirmText, setRestoreConfirmText] = useState("");

  const [entries, setEntries] = useState([]); // asc
  const entriesAsc = entries;
  const [vitals, setVitals] = useState(() => normalizeVitals(DEFAULT_VITALS));
  const [forecast, setForecast] = useState(DEFAULT_FORECAST);
  const [sprints, setSprints] = useState([]);
  const [roadmap, setRoadmap] = useState<Roadmap | null>(null);
  const [roadmapUpdatedAt, setRoadmapUpdatedAt] = useState<string | null>(null);
  const [initialDataLoaded, setInitialDataLoaded] = useState(false);
  // Tombstones: map of entry date → ISO timestamp when deleted offline
  const [deletedEntryTombstones, setDeletedEntryTombstones] = useState<Record<string, string>>({});
  // Whether this device has ever successfully synced with the cloud
  const [hasSynced, setHasSynced] = useState(false);
  const realProfileSnapshotRef = useRef<RealProfileSnapshot | null>(null);
  // Keep a ref so drag handlers always see the latest roadmap without re-subscribing
  const roadmapRef = useRef<Roadmap | null>(null);
  roadmapRef.current = roadmap;
  const sprintSectionRef = useRef<HTMLDivElement | null>(null);
  const [sprintSectionNode, setSprintSectionNode] = useState<HTMLDivElement | null>(null);
  const setSprintSectionRefs = useCallback((node: HTMLDivElement | null) => {
    sprintSectionRef.current = node;
    setSprintSectionNode(node);
  }, []);

  // ── Mobile breakpoint (mirrors AppShell's 700 px breakpoint) ──────────────
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" && window.innerWidth <= 700
  );
  const [isDashboardMobile, setIsDashboardMobile] = useState(
    typeof window !== "undefined" && window.innerWidth < 768
  );
  const [isDashboardHeroStacked, setIsDashboardHeroStacked] = useState(false);
  const HERO_STACK_BREAKPOINT = 1120;
  const [isCompactMobile, setIsCompactMobile] = useState(
    typeof window !== "undefined" && window.innerWidth <= 640
  );
  const [sprintStatusExpanded, setSprintStatusExpanded] = useState(
    () => (typeof window === "undefined" ? true : window.innerWidth >= 768)
  );
  const [rebalanceExpanded, setRebalanceExpanded] = useState(
    () => (typeof window === "undefined" ? true : window.innerWidth >= 768)
  );
  const [progressControlsExpanded, setProgressControlsExpanded] = useState(
    () => (typeof window === "undefined" ? true : window.innerWidth >= 768)
  );
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 700);
      setIsDashboardMobile(window.innerWidth < 768);
      setIsCompactMobile(window.innerWidth <= 640);
    };
    window.addEventListener("resize", checkMobile, { passive: true });
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  useLayoutEffect(() => {
    if (isDashboardMobile) {
      setIsDashboardHeroStacked(true);
      return;
    }
    if (!sprintSectionNode) {
      setIsDashboardHeroStacked(false);
      return;
    }

    const applyWidth = (width: number) => {
      setIsDashboardHeroStacked(width <= HERO_STACK_BREAKPOINT);
    };
    const measureNow = () => applyWidth(sprintSectionNode.getBoundingClientRect().width);

    measureNow();
    let rafId: number | null = null;
    if (typeof window !== "undefined" && typeof window.requestAnimationFrame === "function") {
      rafId = window.requestAnimationFrame(measureNow);
    }

    const onResize = () => measureNow();
    window.addEventListener("resize", onResize, { passive: true });

    const observer = typeof ResizeObserver !== "undefined"
      ? new ResizeObserver((entries) => {
        const width = entries[0]?.contentRect.width ?? sprintSectionNode.getBoundingClientRect().width;
        applyWidth(width);
      })
      : null;
    observer?.observe(sprintSectionNode);

    return () => {
      if (rafId !== null && typeof window !== "undefined" && typeof window.cancelAnimationFrame === "function") {
        window.cancelAnimationFrame(rafId);
      }
      window.removeEventListener("resize", onResize);
      observer?.disconnect();
    };
  }, [HERO_STACK_BREAKPOINT, isDashboardMobile, sprintSectionNode]);

  // Roadmap UI state
  const [roadmapWizardOpen, setRoadmapWizardOpen] = useState(false);
  const [roadmapPhaseBuilderDraft, setRoadmapPhaseBuilderDraft] = useState<RoadmapPhaseBuilderDraft | null>(null);
  const [syncSprintConfirmOpen, setSyncSprintConfirmOpen] = useState(false);
  const [syncSprintConflicts, setSyncSprintConflicts] = useState<string[]>([]);
  const [syncedPhaseEditInfoOpen, setSyncedPhaseEditInfoOpen] = useState(false);
  const [rmWizardStep, setRmWizardStep] = useState(0);
  const [rmWizardPattern, setRmWizardPattern] = useState<WizardPattern>("cut-maintain-bulk");
  const [rmWizardPhaseRows, setRmWizardPhaseRows] = useState<WizardPhaseRow[]>([]);
  const [rmWizardPhaseIdx, setRmWizardPhaseIdx] = useState(0);
  const [editingRoadmapPhaseId, setEditingRoadmapPhaseId] = useState<string | null>(null);
  const [roadmapHeaderMenuOpen, setRoadmapHeaderMenuOpen] = useState(false);
  const [expandedRoadmapPhaseById, setExpandedRoadmapPhaseById] = useState<Record<string, boolean>>({});
  const [mobilePhaseMenuOpenId, setMobilePhaseMenuOpenId] = useState<string | null>(null);
  // Desktop roadmap pagination: one phase per page (mobile keeps full accordion)
  const [roadmapDesktopPage, setRoadmapDesktopPage] = useState(0);
  const roadmapHeaderMenuRef = useRef<HTMLDivElement | null>(null);
  const mobilePhaseMenuRef = useRef<HTMLDivElement | null>(null);

  // Chart edit mode state
  const [chartEditMode, setChartEditMode] = useState(false);
  // X-axis pan offset (ms) and user-initiated extend (ms) — reset when edit closes
  const [chartPanOffsetMs, setChartPanOffsetMs] = useState(0);
  const [chartXExtendMs, setChartXExtendMs] = useState(0);
  const panStartRef = useRef<{ clientX: number; baseOffset: number; msPerPx: number } | null>(null);
  type DragState = {
    phaseId: string;
    phaseIndex: number;
    // origin pixel position when drag started (relative to SVG plot area)
    originPx: { x: number; y: number };
    // domain values when drag started
    originEndDateTs: number;
    originEndWeight: number;
    // current (preview) domain values
    currentEndDateTs: number;
    currentEndWeight: number;
    // scale refs captured at drag start
    xScale: (v: number) => number;
    yScale: (v: number) => number;
    xScaleInvert: (px: number) => number;
    yScaleInvert: (px: number) => number;
    // shift-key state
    shiftLockAxis: "none" | "x" | "y";
  };
  const [dragState, setDragState] = useState<DragState | null>(null);
  const roadmapChartContainerRef = useRef<SVGSVGElement | null>(null);
  // Mirror dragState in a ref so event handlers can always read the latest value
  const dragStateRef = useRef<DragState | null>(null);
  dragStateRef.current = dragState;
  // Overlay position (client coords) for drag feedback panel
  const [dragOverlayPos, setDragOverlayPos] = useState<{ x: number; y: number } | null>(null);

  // Attach window-level drag event listeners when a drag is active
  const dragPhaseId = dragState?.phaseId ?? null;
  useEffect(() => {
    if (!dragPhaseId) return;

    const onMouseMove = (e: MouseEvent) => {
      const ds = dragStateRef.current;
      if (!ds) return;
      const svg = roadmapChartContainerRef.current;
      if (!svg) return;
      const svgRect = svg.getBoundingClientRect();
      const mouseX = e.clientX - svgRect.left;
      const mouseY = e.clientY - svgRect.top;

      let rawTs = ds.xScaleInvert(mouseX);
      let rawWeight = ds.yScaleInvert(mouseY);

      // Snap to day boundary
      const dayMs = 86400000;
      rawTs = Math.round(rawTs / dayMs) * dayMs;
      // Snap to 1 lb
      rawWeight = Math.round(rawWeight);
      // Clamp weight to sane bounds
      rawWeight = Math.max(50, Math.min(1000, rawWeight));
      // Enhanced snap-to-zero-delta for maintain phases (within ±0.75 lb of startWeight)
      const rmForSnap = roadmapRef.current;
      if (rmForSnap) {
        const snapPhase = rmForSnap.phases.find((p) => p.id === ds.phaseId);
        if (snapPhase?.type === "maintain" && Math.abs(rawWeight - snapPhase.startWeight) <= 0.75) {
          rawWeight = Math.round(snapPhase.startWeight);
        }
      }

      // Shift-key: lock axis
      let newTs = rawTs;
      let newWeight = rawWeight;
      if (e.shiftKey) {
        const dxPx = Math.abs(mouseX - ds.originPx.x);
        const dyPx = Math.abs(mouseY - ds.originPx.y);
        if (dxPx >= dyPx) {
          newWeight = ds.originEndWeight; // horizontal lock
        } else {
          newTs = ds.originEndDateTs; // vertical lock
        }
      }

      // Constraint: endDate >= startDate + 1 day (no crossing; later phases push with us)
      const rm = roadmapRef.current;
      if (rm) {
        const phIdx = rm.phases.findIndex((p) => p.id === ds.phaseId);
        const thisPhase = phIdx >= 0 ? rm.phases[phIdx] : null;
        if (thisPhase) {
          const minTs = (safeParseYmd(thisPhase.startDate, "0001-01-01", "9999-12-31")?.getTime() ?? 0) + dayMs;
          if (newTs < minTs) newTs = minTs;
        }
      }

      setDragState(prev => prev ? { ...prev, currentEndDateTs: newTs, currentEndWeight: newWeight } : null);
      setDragOverlayPos({ x: e.clientX + 16, y: e.clientY - 40 });
    };

    const onMouseUp = async () => {
      const ds = dragStateRef.current;
      setDragState(null);
      setDragOverlayPos(null);
      if (!ds) return;
      const endDate = tsToYmd(ds.currentEndDateTs);
      const endWeight = ds.currentEndWeight;
      await applyPhaseEdit(ds.phaseId, { endDate, endWeight });
    };

    window.addEventListener("pointermove", onMouseMove);
    window.addEventListener("pointerup", onMouseUp);
    return () => {
      window.removeEventListener("pointermove", onMouseMove);
      window.removeEventListener("pointerup", onMouseUp);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dragPhaseId]);

  // Exit edit mode on Escape key; also reset pan/extend state
  useEffect(() => {
    if (!chartEditMode) {
      setChartPanOffsetMs(0);
      setChartXExtendMs(0);
      panStartRef.current = null;
      return;
    }
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setChartEditMode(false);
        setDragState(null);
        setDragOverlayPos(null);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [chartEditMode]);

  // Prevent background scroll while edit overlay is open
  useEffect(() => {
    if (!chartEditMode) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [chartEditMode]);

  useEffect(() => {
    if (!editingRoadmapPhaseId) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setEditingRoadmapPhaseId(null);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [editingRoadmapPhaseId]);

  useEffect(() => {
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node | null;
      if (roadmapHeaderMenuRef.current && target && !roadmapHeaderMenuRef.current.contains(target)) {
        setRoadmapHeaderMenuOpen(false);
      }
      const el = event.target as HTMLElement | null;
      if (mobilePhaseMenuOpenId && !el?.closest("[data-phase-menu-root='true']")) {
        setMobilePhaseMenuOpenId(null);
      }
    };
    window.addEventListener("pointerdown", onPointerDown);
    return () => window.removeEventListener("pointerdown", onPointerDown);
  }, [mobilePhaseMenuOpenId]);

  useEffect(() => {
    if (chartEditMode) setEditingRoadmapPhaseId(null);
  }, [chartEditMode]);

  // Clamp desktop roadmap page when phases are added/removed
  useEffect(() => {
    const count = roadmap?.phases?.length ?? 0;
    if (count === 0) return;
    setRoadmapDesktopPage((p) => Math.min(p, count - 1));
  }, [roadmap?.phases?.length]);

  const [newSprintType, setNewSprintType] = useState("new"); // new | historical
  const [newSprintName, setNewSprintName] = useState("");
  const [newSprintStartDate, setNewSprintStartDate] = useState(todayYmd);
  const [newSprintEndDate, setNewSprintEndDate] = useState("");
  const [newSprintForecastEndDate, setNewSprintForecastEndDate] = useState("");
  const [newSprintStartingWeight, setNewSprintStartingWeight] = useState("");
  const [newSprintStartWeightSource, setNewSprintStartWeightSource] = useState<"fromLog" | "edited" | null>(null);
  const [newSprintColor, setNewSprintColor] = useState(SPRINT_PALETTE[0]);
  const [newSprintTargetWeight, setNewSprintTargetWeight] = useState(DEFAULT_FORECAST.targetWeight);
  const [newSprintTargetDate, setNewSprintTargetDate] = useState(DEFAULT_FORECAST.targetDate ?? "");
  const [newSprintInputMode, setNewSprintInputMode] = useState(DEFAULT_FORECAST.inputMode);
  const [newSprintAssumedNetCalories, setNewSprintAssumedNetCalories] = useState(DEFAULT_FORECAST.assumedNetCalories);
  const [newSprintNetDraft, setNewSprintNetDraft] = useState(String(DEFAULT_FORECAST.assumedNetCalories));
  const [newSprintIntakeDraft, setNewSprintIntakeDraft] = useState(String(Math.max(0, safeNumber(DEFAULT_FORECAST.assumedNetCalories) ?? 0)));
  const [editingSprintId, setEditingSprintId] = useState(null);
  const [editingSprintDraft, setEditingSprintDraft] = useState({ name: "", startDate: "", endDate: "", color: SPRINT_PALETTE[0] });

  // Plan-by-Rate state for Sprint Settings modal
  const [sprintPlanMethod, setSprintPlanMethod] = useState("byDate"); // "byDate" | "byPace"
  const [sprintPresetRate, setSprintPresetRate] = useState(null); // number | null
  const [sprintPresetClamped, setSprintPresetClamped] = useState(false);

  // Plan-by-Rate state for Create Sprint modal
  const [newSprintPlanMethod, setNewSprintPlanMethod] = useState("byDate");
  const [newSprintPresetRate, setNewSprintPresetRate] = useState(null);
  const [newSprintPresetClamped, setNewSprintPresetClamped] = useState(false);
  const [calFloorConfirmData, setCalFloorConfirmData] = useState<{
    rawIntake: number;
    floorIntake: number;
    safeAdjustedNet: number;
    lowestAllowableIntake: number | null;  // intake at 2lb/wk cap; null if pace already ≤ limit
    lowestAllowableNet: number | null;     // net cal/day at 2lb/wk cap
  } | null>(null);
  const [calFloorChoice, setCalFloorChoice] = useState<"recommended" | "fastest">("recommended");
  const [calFloorEditContext, setCalFloorEditContext] = useState<{ recommendedNet: number; recommendedTargetDate: string | null; fastestNet: number | null; fastestTargetDate: string | null } | null>(null);
  const [newSprintSummaryMode, setNewSprintSummaryMode] = useState<"net" | "intake">("net");
  const [newSprintGoalType, setNewSprintGoalType] = useState<SprintGoalType | null>(null);
  const [sprintSummaryMode, setSprintSummaryMode] = useState<"net" | "intake">("net");

  const [viewerSprintId, setViewerSprintId] = useState(null);

  // One-time migration: ensure the current active sprint (start 2026-02-12) has a forecastEndDate.
  const didMigrateActiveForecastEnd = useRef(false);

  // Daily entry
  const [tdeeInputMode, setTdeeInputMode] = useState("total"); // total | active
  const [currentEntry, setCurrentEntry] = useState({
    date: todayYmd,
    weight: "",
    caloriesIn: "",
    totalBurned: "",
    activeCalories: "",
    totalBurnedSource: null as "oura" | "manual" | "inferred" | null,
    activeCaloriesSource: null as "oura" | "manual" | "inferred" | null,
  });
  const [dailyEntryFocusedField, setDailyEntryFocusedField] = useState<"weight" | "caloriesIn" | "burnTotal" | "burnActive" | null>(null);

  // UI toggles
  const [activeModal, setActiveModal] = useState(null); // null | "vitals" | "sprint" | "newSprint"
  const [showMobileLogModal, setShowMobileLogModal] = useState(false);
  const [logSuccessToast, setLogSuccessToast] = useState<string | null>(null);
  const [saveButtonFlash, setSaveButtonFlash] = useState(false);
  const [sprintSaveToast, setSprintSaveToast] = useState<string | null>(null);
  const [roadmapDeleteConfirmOpen, setRoadmapDeleteConfirmOpen] = useState(false);
  const [roadmapPhaseDeleteConfirm, setRoadmapPhaseDeleteConfirm] = useState<{ phaseId: string; phaseName: string; isLastPhase: boolean } | null>(null);
  const [sprintDeleteConfirm, setSprintDeleteConfirm] = useState<{ sprintId: string; sprintName: string; isActive: boolean } | null>(null);
  const [showActivityFactorHelp, setShowActivityFactorHelp] = useState(false);
  const [showSprintNetHelp, setShowSprintNetHelp] = useState(false);
  const [sprintStatusDisplayMode, setSprintStatusDisplayMode] = useState<"avg" | "today">(normalizeSprintStatusDisplayMode(DEFAULT_FORECAST.sprintStatusDisplayMode));
  const [showNewSprintStartDateHelp, setShowNewSprintStartDateHelp] = useState(false);
  const [showNewSprintStartWeightHelp, setShowNewSprintStartWeightHelp] = useState(false);
  const [vitalsDraft, setVitalsDraft] = useState(() => normalizeVitals(DEFAULT_VITALS));
  const [vitalsEditorError, setVitalsEditorError] = useState<string | null>(null);
  const [pendingVitalsSave, setPendingVitalsSave] = useState<ReturnType<typeof normalizeVitals> | null>(null);
  const [showMetabolismWeightConfirm, setShowMetabolismWeightConfirm] = useState(false);

  // ---- Onboarding ----
  type ObPhase = "bodyStats" | "currentWeight" | "activity" | "import" | "goal";
  const [obPhase, setObPhase] = useState<ObPhase>("bodyStats");

  // Inline auth state for welcome screen
  const [obAuthMode, setObAuthMode] = useState<"none" | "signin" | "signup">("none");
  const [obEmail, setObEmail] = useState("");
  const [obPassword, setObPassword] = useState("");
  const [obAuthBusy, setObAuthBusy] = useState(false);
  const [obAuthBanner, setObAuthBanner] = useState<{ variant: "error" | "info" | "success"; title: string; message: string } | null>(null);
  const [obWelcomeNotice, setObWelcomeNotice] = useState<string | null>(verifiedEmailNotice);
  const obEmailInputRef = useRef<HTMLInputElement | null>(null);
  const obPasswordInputRef = useRef<HTMLInputElement | null>(null);
  const [obShowPassword, setObShowPassword] = useState(false);
  const [obResetOpen, setObResetOpen] = useState(false);
  const [obResetEmail, setObResetEmail] = useState("");
  const [obResetBusy, setObResetBusy] = useState(false);
  const isAppleTouchDevice = useMemo(() => {
    if (typeof navigator === "undefined") return false;
    const ua = navigator.userAgent ?? "";
    return /iPad|iPhone|iPod/.test(ua) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  }, []);

  const [obName, setObName] = useState("");
  const [obVitals, setObVitals] = useState({ ft: "", ins: "", birthday: "", gender: "M", activityFactor: 1.3, currentWeightLbs: "" });
  const [obSprint, setObSprint] = useState({
    name: "",
    startDate: todayYmd,
    startWeight: "",
    startWeightSource: "vitals", // vitals | custom
    targetWeight: "",
    targetDate: addMonthsToYmd(todayYmd, 3),
    activityFactor: 1.3,
    inputMode: "net", // net | intake
    color: SPRINT_PALETTE[0],
    netDraft: "",
    intakeDraft: "",
    assumedNetCalories: null,
    planMethod: "byDate", // "byDate" | "byPace"
    presetRate: null, // number | null
    presetClamped: false,
    goalType: null as SprintGoalType | null,
  });
  const [obErrors, setObErrors] = useState([]);
  const [obCsvRows, setObCsvRows] = useState(null); // parsed rows from importEntriesFromCsvText
  const [obCsvMsg, setObCsvMsg] = useState(null);
  const [obProfileMsg, setObProfileMsg] = useState(null);
  const [obTdeeHelp, setObTdeeHelp] = useState(false);
  const [obSignedInWithAccount, setObSignedInWithAccount] = useState(false);
  // Set to true when cloud pull completes and we detect an authenticated user with no data and no
  // vitals — covers the email-confirmation path where obSignedInWithAccount is never set.
  const [obShouldShowForAuthUser, setObShouldShowForAuthUser] = useState(false);
  const [obCloudNote, setObCloudNote] = useState<string | null>(null);
  const lastAuthRoutingUserIdRef = useRef<string | null>(null);
  const [showObPlanReveal, setShowObPlanReveal] = useState(false);
  const [showObSkipSprintInfo, setShowObSkipSprintInfo] = useState(false);

  const [forecastNetDraft, setForecastNetDraft] = useState(String(DEFAULT_FORECAST.assumedNetCalories));
  const [forecastIntakeDraft, setForecastIntakeDraft] = useState(String(Math.max(0, safeNumber(DEFAULT_FORECAST.assumedNetCalories) ?? 0)));
  const [sprintStartDraft, setSprintStartDraft] = useState("");

  const isValidAuthEmail = (email: string) => /^\S+@\S+\.\S+$/.test(email.trim());

  const getAuthErrorBanner = (message: string) => {
    const normalized = message.toLowerCase();
    if (normalized.includes("invalid email")) {
      return { variant: "error" as const, title: "Invalid email", message: "Please enter a valid email address." };
    }
    if (normalized.includes("invalid login credentials") || normalized.includes("invalid_credentials") || normalized.includes("incorrect")) {
      return { variant: "error" as const, title: "Sign in failed", message: "Incorrect password. Try again or reset it." };
    }
    return { variant: "error" as const, title: "Sign in failed", message: message || "Something went wrong." };
  };

  const handleObAuthSubmit = async () => {
    const trimmedEmail = obEmail.trim();
    if (!isValidAuthEmail(trimmedEmail)) {
      setObAuthBanner({ variant: "error", title: "Invalid email", message: "Please enter a valid email address." });
      return;
    }

    setObAuthBusy(true);
    setObAuthBanner(null);
    setObResetOpen(false);

    try {
      if (obAuthMode === "signup") {
        const { data, error: err } = await signUp(trimmedEmail, obPassword);

        const signUpErrorMessage = err?.message ?? "";
        const signUpUser = data?.user;
        const signUpSession = data?.session;
        const identities = Array.isArray((signUpUser as any)?.identities) ? (signUpUser as any).identities : null;
        const hasNoKnownIdentities = Array.isArray(identities) && identities.length === 0;
        const duplicateByErrorText = /already registered|already exists|email.*taken/i.test(signUpErrorMessage);
        const duplicateByResponseShape = !signUpSession && !!signUpUser && hasNoKnownIdentities;

        if (duplicateByErrorText || duplicateByResponseShape) {
          setObAuthMode("signin");
          setObPassword("");
          setObAuthBanner({
            variant: "info",
            title: "Account found",
            message: "This email already has an account. Please sign in to continue.",
          });
          window.setTimeout(() => obPasswordInputRef.current?.focus(), 0);
          return;
        }

        if (err) throw err;
        if (!data.session) {
          setObAuthMode("signin");
          setObPassword("");
          setObAuthBanner({
            variant: "success",
            title: "Check your email",
            message: `We sent a verification link to ${trimmedEmail}. Open it to activate your account, then come back and sign in.`,
          });
          return;
        }
        // If email confirmation is disabled and a session is returned,
        // mark as signed in and let the auth listener / cloud sync flow advance onboarding.
        setObSignedInWithAccount(true);
        return;
      }

      const { error: err } = await signIn(trimmedEmail, obPassword);
      if (err) throw err;
      // Sign in success: userId will update → cloud sync interstitial will take over
      setObSignedInWithAccount(true);
    } catch (err: any) {
      const msg: string = err?.message ?? "";
      if (obAuthMode === "signup" && /already registered|already exists|email.*taken/i.test(msg)) {
        setObAuthMode("signin");
        setObPassword("");
        setObAuthBanner({
          variant: "info",
          title: "Account found",
          message: "This email already has an account. Please sign in to continue.",
        });
        window.setTimeout(() => obPasswordInputRef.current?.focus(), 0);
      } else {
        setObAuthBanner(getAuthErrorBanner(msg));
      }
    } finally {
      setObAuthBusy(false);
    }
  };

  const handleObPasswordReset = async () => {
    const trimmedEmail = obResetEmail.trim();
    if (!isValidAuthEmail(trimmedEmail)) {
      setObAuthBanner({ variant: "error", title: "Invalid email", message: "Please enter a valid email address." });
      return;
    }

    setObResetBusy(true);
    setObAuthBanner(null);
    try {
      const { error } = await resetPassword(trimmedEmail);
      if (error) throw error;
      setObResetOpen(false);
      setObAuthMode("signin");
      setObEmail(trimmedEmail);
      setObPassword("");
      setObAuthBanner({
        variant: "success",
        title: "Reset email sent",
        message: "Check your inbox for a password reset link.",
      });
    } catch (err: any) {
      setObAuthBanner(getAuthErrorBanner(err?.message ?? ""));
    } finally {
      setObResetBusy(false);
    }
  };

  useEffect(() => {
    setObWelcomeNotice(verifiedEmailNotice);
  }, [verifiedEmailNotice]);



  useEffect(() => {
    if (obAuthMode !== "signin") return;
    if (obPassword) obPasswordInputRef.current?.focus();
    else obEmailInputRef.current?.focus();
  }, [obAuthMode]);

  useEffect(() => {
    if (!obWelcomeNotice || redirectAuthMode === "updatePassword") return;
    const t = window.setTimeout(() => {
      setObWelcomeNotice(null);
    }, 5000);
    return () => window.clearTimeout(t);
  }, [obWelcomeNotice, redirectAuthMode]);

  useEffect(() => {
    if (!userId || redirectAuthMode === "updatePassword") return;
    setObWelcomeNotice(null);
    onClearVerifiedEmailNotice?.();
  }, [userId, onClearVerifiedEmailNotice, redirectAuthMode]);

  // Handle Oura OAuth return: navigate to settings and refresh connection state
  useEffect(() => {
    if (!ouraConnectedNotice && !ouraErrorNotice) return;
    setCurrentPage("settings");
    if (ouraConnectedNotice && userId) {
      getOuraConnection().then(setOuraConnection).catch(() => {});
    }
    if (ouraErrorNotice) {
      setOuraRedirectError(ouraErrorNotice);
    }
    onClearOuraNotices?.();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ouraConnectedNotice, ouraErrorNotice]);

  useEffect(() => {
    if (forecast.inputMode === "net") {
      setForecastNetDraft(String(safeNumber(forecast.assumedNetCalories) ?? 0));
    }
  }, [forecast.assumedNetCalories, forecast.inputMode]);

  useEffect(() => {
    if (forecast.inputMode === "intake") {
      setForecastIntakeDraft(String(Math.max(0, safeNumber(forecast.assumedNetCalories) ?? 0)));
    }
  }, [forecast.assumedNetCalories, forecast.inputMode]);

  useEffect(() => {
    if (newSprintInputMode === "net") {
      setNewSprintNetDraft(String(safeNumber(newSprintAssumedNetCalories) ?? 0));
    }
  }, [newSprintAssumedNetCalories, newSprintInputMode]);

  useEffect(() => {
    if (newSprintInputMode === "intake") {
      setNewSprintIntakeDraft(String(Math.max(0, safeNumber(newSprintAssumedNetCalories) ?? 0)));
    }
  }, [newSprintAssumedNetCalories, newSprintInputMode]);

  useEffect(() => {
    setObSprint((s) => {
      if (s.startWeightSource === "custom") return s;
      return { ...s, startWeight: obVitals.currentWeightLbs, startWeightSource: "vitals" };
    });
  }, [obVitals.currentWeightLbs]);


  useEffect(() => {
    const hasActiveSprint = sprints.some((s) => s?.endDate === null);
    if (activeModal !== "sprint") return;
    if (hasActiveSprint) return;
    if (sprintStartDraft) return;
    setSprintStartDraft(todayYmd);
  }, [activeModal, sprints, sprintStartDraft, todayYmd]);

  const commitForecastNetDraft = () => {
    const parsed = parseSignedDraftNumber(forecastNetDraft);
    if (parsed === null) {
      setForecastNetDraft(String(safeNumber(forecast.assumedNetCalories) ?? 0));
      return;
    }
    setForecast((f) => ({ ...f, assumedNetCalories: parsed }));
  };

  const commitForecastIntakeDraft = () => {
    const parsed = parseSignedDraftNumber(forecastIntakeDraft.replace(/-/g, ""));
    if (parsed === null) {
      setForecastIntakeDraft(String(Math.max(0, safeNumber(forecast.assumedNetCalories) ?? 0)));
      return;
    }
    setForecast((f) => ({ ...f, assumedNetCalories: Math.max(0, parsed) }));
  };

  const commitNewSprintNetDraft = () => {
    const parsed = parseSignedDraftNumber(newSprintNetDraft);
    if (parsed === null) {
      setNewSprintNetDraft(String(safeNumber(newSprintAssumedNetCalories) ?? 0));
      return;
    }
    setNewSprintAssumedNetCalories(parsed);
  };

  const commitNewSprintIntakeDraft = () => {
    const parsed = parseSignedDraftNumber(newSprintIntakeDraft.replace(/-/g, ""));
    if (parsed === null) {
      setNewSprintIntakeDraft(String(Math.max(0, safeNumber(newSprintAssumedNetCalories) ?? 0)));
      return;
    }
    setNewSprintAssumedNetCalories(Math.max(0, parsed));
  };


  const commitSprintDraftNet = () => {
    setSprintDraft((prev) => {
      if (!prev) return prev;
      const parsed = parseSignedDraftNumber(prev.netDraft);
      if (parsed === null) {
        return { ...prev, netDraft: String(safeNumber(prev.assumedNetCalories) ?? 0) };
      }
      return { ...prev, assumedNetCalories: parsed, netDraft: String(parsed) };
    });
  };

  const commitSprintDraftIntake = () => {
    setSprintDraft((prev) => {
      if (!prev) return prev;
      const parsed = parseSignedDraftNumber(String(prev.intakeDraft ?? "").replace(/-/g, ""));
      if (parsed === null) {
        return { ...prev, intakeDraft: String(Math.max(0, safeNumber(prev.assumedNetCalories) ?? 0)) };
      }
      const next = Math.max(0, parsed);
      return { ...prev, assumedNetCalories: next, intakeDraft: String(next) };
    });
  };
  const [calcPlanNotice, setCalcPlanNotice] = useState(null);
  const [calcPlanError, setCalcPlanError] = useState(null);

  // ---- Plan-by-Rate effects ----

  // Create Sprint: recompute targetDate when rate preset or relevant fields change
  useEffect(() => {
    if (newSprintPlanMethod !== "byPace" || newSprintPresetRate === null) return;
    const result = computeTargetDateFromRate({
      startWeight: newSprintStartingWeight,
      targetWeight: newSprintTargetWeight,
      startDateYmd: newSprintStartDate,
      rateLbsPerWeekAbs: newSprintPresetRate,
    });
    if (!result || !result.targetDateYmd) return;
    setNewSprintPresetClamped(result.clamped);
    setNewSprintTargetDate(result.targetDateYmd);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newSprintPlanMethod, newSprintPresetRate, newSprintStartingWeight, newSprintTargetWeight, newSprintStartDate]);

  // Onboarding Sprint: recompute targetDate when rate preset or relevant fields change
  useEffect(() => {
    if (obSprint.planMethod !== "byPace" || obSprint.presetRate === null) return;
    const result = computeTargetDateFromRate({
      startWeight: obSprint.startWeight,
      targetWeight: obSprint.targetWeight,
      startDateYmd: obSprint.startDate,
      rateLbsPerWeekAbs: obSprint.presetRate,
    });
    if (!result || !result.targetDateYmd) return;
    setObSprint((s) => ({ ...s, targetDate: result.targetDateYmd, presetClamped: result.clamped }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [obSprint.planMethod, obSprint.presetRate, obSprint.startWeight, obSprint.targetWeight, obSprint.startDate]);

  const applyGoalTypeTargetDefault = (goalType: SprintGoalType | null, startWeight: number | null, targetWeight: number | null) => {
    if (!goalType) return targetWeight;
    return normalizeSprintWeightsForGoalType({ goalType, startWeight, targetWeight, defaultDeltaLbs: 10 });
  };

  useEffect(() => {
    if (!newSprintGoalType) return;
    const sw = safeNumber(newSprintStartingWeight);
    const tw = safeNumber(newSprintTargetWeight);
    const next = applyGoalTypeTargetDefault(newSprintGoalType, sw, tw);
    if (next !== null && next !== tw) setNewSprintTargetWeight(next);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newSprintGoalType]);

  useEffect(() => {
    const sw = safeNumber(obSprint.startWeight);
    const tw = safeNumber(obSprint.targetWeight);
    if (sw === null || tw === null) return;
    const derived = deriveSprintGoalType(sw, tw);
    if (derived !== obSprint.goalType) {
      setObSprint((s) => ({ ...s, goalType: derived }));
    }
  }, [obSprint.startWeight, obSprint.targetWeight, obSprint.goalType]);

  useEffect(() => {
    if (!obSprint.goalType) return;
    const sw = safeNumber(obSprint.startWeight);
    const tw = safeNumber(obSprint.targetWeight);
    const next = applyGoalTypeTargetDefault(obSprint.goalType, sw, tw);
    if (next !== null && next !== tw) {
      setObSprint((s) => ({ ...s, targetWeight: String(next) }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [obSprint.goalType]);

  const dailyEntryCardRef = useRef(null);
  const dailyEntryDateRef = useRef(null);
  const dailyEntryWeightRef = useRef(null);
  const pendingConfirmActionRef = useRef(null);
  const [isDailyEntryDatePickerOpen, setIsDailyEntryDatePickerOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmLines, setConfirmLines] = useState([]);
  const [recalcConfirm, setRecalcConfirm] = useState(null);
  const [dotTooltip, setDotTooltip] = useState<"planned" | "actual" | null>(null);

  // Archive controls
  const [archiveName, setArchiveName] = useState("");
  const [sprintDraft, setSprintDraft] = useState<any | null>(null);
  const [sprintDraftOriginal, setSprintDraftOriginal] = useState<any | null>(null);
  const [sprintStartWeightSource, setSprintStartWeightSource] = useState<"fromLog" | "edited" | null>(null);
  const [sprintUpdateConfirm, setSprintUpdateConfirm] = useState<any | null>(null);
  const [createSprintWarningConfirm, setCreateSprintWarningConfirm] = useState<any | null>(null);
  const [pendingSprintAction, setPendingSprintAction] = useState<any | null>(null);
  const [archiveConfirm, setArchiveConfirm] = useState<any | null>(null);
  const [archiveFirstPrompt, setArchiveFirstPrompt] = useState<any | null>(null);
  const didRepairActiveSprintsRef = useRef(false);
  const didNormalizeLegacySprintEndDatesRef = useRef(false);

  useEffect(() => {
    if (!sprintDraft) return;
    const sw = safeNumber(sprintDraft.startingWeight);
    const tw = safeNumber(sprintDraft.targetWeight);
    const derived = deriveSprintGoalType(sw, tw);
    if ((sprintDraft.goalType ?? "maintain") !== derived) {
      setSprintDraft((prev) => (prev ? { ...prev, goalType: derived } : prev));
    }
  }, [sprintDraft?.startingWeight, sprintDraft?.targetWeight, sprintDraft?.goalType]);

  // Trends page date range controls
  const [trendsStart, setChartsStart] = useState(null);
  const [trendsEnd, setChartsEnd] = useState(null);
  const [trendsStartDraft, setChartsStartDraft] = useState("");
  const [trendsEndDraft, setChartsEndDraft] = useState("");
  const [trendsRangeMessage, setChartsRangeMessage] = useState(null);
  const [showSprints, setShowSprints] = useState(true);
  const [trendsMetric, setChartsMetric] = useState<ChartMetric>("weight");
  const [viewerMetric, setViewerMetric] = useState<ChartMetric>("weight");
  const [trackerCaloriesMetric, setTrackerCaloriesMetric] = useState<"netCalories" | "caloriesIn" | "tdee" | "activeCalories">("netCalories");
  const [trendsBrushRange, setChartsBrushRange] = useState({ startIndex: 0, endIndex: 0 });

  useEffect(() => {
    if (currentPage !== "trends") setChartsMetric("weight");
    if (currentPage !== "viewer") setViewerMetric("weight");
  }, [currentPage]);

  useEffect(() => {
    if (!showCsvImportModal) return undefined;
    const onEscape = (event) => {
      if (event.key === "Escape") setShowCsvImportModal(false);
    };
    window.addEventListener("keydown", onEscape);
    return () => window.removeEventListener("keydown", onEscape);
  }, [showCsvImportModal]);

  useEffect(() => {
    if (!activeModal) return undefined;

    const onEscape = (event) => {
      if (event.key !== "Escape") return;
      setActiveModal(null);
      setEditingSprintId(null);
      setEditingSprintDraft({ name: "", startDate: "", endDate: "", color: SPRINT_PALETTE[0] });
      setShowActivityFactorHelp(false);
      setShowSprintNetHelp(false);
    };

    window.addEventListener("keydown", onEscape);
    return () => window.removeEventListener("keydown", onEscape);
  }, [activeModal]);

  useEffect(() => {
    if (!confirmOpen) return undefined;
    const onEscape = (event) => {
      if (event.key !== "Escape") return;
      setConfirmOpen(false);
      setConfirmLines([]);
      pendingConfirmActionRef.current = null;
    };
    window.addEventListener("keydown", onEscape);
    return () => window.removeEventListener("keydown", onEscape);
  }, [confirmOpen]);

  useEffect(() => {
    if (activeModal !== "goal" && showActivityFactorHelp) setShowActivityFactorHelp(false);
    if (activeModal !== "goal" && showSprintNetHelp) setShowSprintNetHelp(false);
  }, [activeModal, showActivityFactorHelp, showSprintNetHelp]);

  useEffect(() => {
    if (currentPage === "sprints") return;
    if (showNewSprintStartDateHelp) setShowNewSprintStartDateHelp(false);
    if (showNewSprintStartWeightHelp) setShowNewSprintStartWeightHelp(false);
  }, [currentPage, showNewSprintStartDateHelp, showNewSprintStartWeightHelp]);

  useEffect(() => {
    setActiveModal(null);
    setShowActivityFactorHelp(false);
    setShowSprintNetHelp(false);
  }, [currentPage]);
  const [chartSprintActionId, setChartSprintActionId] = useState(null);
  const [movingAverageMode, setMovingAverageMode] = useState("auto"); // off | auto | manual
  const [manualMovingAverageWindow, setManualMovingAverageWindow] = useState(7);
  const [trackerCalorieCompareMode, setTrackerCalorieCompareMode] = useState("net"); // net | intake
  const [trackerSeriesVisible, setTrackerSeriesVisible] = useState(() => {
    const defaults = { expected: true, projected: true, originalPlan: true };
    if (typeof window === "undefined") return defaults;
    try {
      const raw = window.localStorage.getItem(TRACKER_SERIES_VISIBILITY_KEY);
      if (!raw) return defaults;
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== "object") return defaults;
      return {
        expected: parsed.expected !== false,
        projected: parsed.projected !== false,
        originalPlan: parsed.originalPlan !== false,
      };
    } catch {
      return defaults;
    }
  });
  const [trackerViewMode, setTrackerViewMode] = useState("target"); // 3w | 6w | target
  const latestEntriesRef = useRef(entriesAsc);
  const latestSprintsRef = useRef(sprints);
  const latestForecastRef = useRef(forecast);
  const healthKitLocalUpdatedAtRef = useRef<number>(0);
  const pendingHealthKitCloudSyncRef = useRef(false);
  // Tracks whether local storage had any data when the app first booted.
  // Set once in the load effect before initialDataLoaded becomes true.
  const hadLocalDataOnBootRef = useRef(false);

  useEffect(() => {
    latestEntriesRef.current = entriesAsc;
    latestSprintsRef.current = sprints;
    latestForecastRef.current = forecast;
  }, [entriesAsc, sprints, forecast]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem(TRACKER_SERIES_VISIBILITY_KEY, JSON.stringify(trackerSeriesVisible));
    } catch {
      // noop
    }
  }, [trackerSeriesVisible]);

  const persistAll = async (nextEntries, nextVitals = vitals, nextForecast = forecast, nextSprints = sprints, _options = {}) => {
    if (isDemoMode) return;
    try {
      await storage.set(STORAGE_KEYS.entries, JSON.stringify(nextEntries));
      await storage.set(STORAGE_KEYS.vitals, JSON.stringify(nextVitals));
      await storage.set(STORAGE_KEYS.forecast, JSON.stringify(nextForecast));
      await storage.set(STORAGE_KEYS.sprints, JSON.stringify(nextSprints));
    } catch (e) {
      console.error("Failed to save", e);
    }
  };

  useEffect(() => {
    setSprintStatusDisplayMode(normalizeSprintStatusDisplayMode(forecast.sprintStatusDisplayMode));
  }, [forecast.sprintStatusDisplayMode]);

  const handleSprintStatusDisplayModeChange = (mode: "avg" | "today") => {
    const normalizedMode = normalizeSprintStatusDisplayMode(mode);
    setSprintStatusDisplayMode(normalizedMode);
    if (forecast.sprintStatusDisplayMode === normalizedMode) return;
    const nextForecast = { ...forecast, sprintStatusDisplayMode: normalizedMode };
    setForecast(nextForecast);
    void persistAll(entriesAsc, vitals, nextForecast, sprints);
  };

  const blockDemoWrite = (message = "Demo Mode: changes are not saved.") => {
    if (!isDemoMode) return false;
    setDemoNotice(message);
    setSettingsDataStatusMsg(message);
    return true;
  };

  const demoProfiles = useMemo(() => buildDemoProfiles(todayYmd), [todayYmd]);

  const snapshotRealProfile = () => {
    realProfileSnapshotRef.current = {
      entries: entriesAsc.map((entry) => ({ ...entry })),
      vitals: { ...vitals },
      forecast: { ...forecast },
      sprints: sprints.map((sprint) => ({ ...sprint })),
      roadmap: roadmap ? JSON.parse(JSON.stringify(roadmap)) : null,
      currentPage,
    };
  };

  const applyDemoProfile = (profileId: DemoProfileId) => {
    const profile = demoProfiles[profileId] ?? demoProfiles.cut_ahead;
    const normalizedEntries = [...profile.entries].sort((a, b) => (a.date < b.date ? -1 : 1));
    setVitals(normalizeVitals(profile.vitals));
    setEntries(normalizedEntries);
    setSprints(profile.sprints);
    setForecast({ ...DEFAULT_FORECAST, ...profile.forecast });
    setRoadmap(profile.roadmap ?? null);
    setCurrentPage("tracker");
    setDemoNotice("Demo Mode: changes are not saved.");
  };

  const loadRealProfileFromStorage = async () => {
    let loadedVitalsForEntries = normalizeVitals(DEFAULT_VITALS);
    try {
      const vit = await storage.get(STORAGE_KEYS.vitals);
      loadedVitalsForEntries = vit?.value ? normalizeVitals(JSON.parse(vit.value) || {}) : normalizeVitals(DEFAULT_VITALS);
      setVitals(loadedVitalsForEntries);
    } catch {
      loadedVitalsForEntries = normalizeVitals(DEFAULT_VITALS);
      setVitals(loadedVitalsForEntries);
    }

    try {
      const fc = await storage.get(STORAGE_KEYS.forecast);
      if (fc?.value) {
        const parsedForecast = { ...DEFAULT_FORECAST, ...(JSON.parse(fc.value) || {}) };
        const maxFuture = clampYmdToBounds(addDaysYMD(todayYmd, MAX_FUTURE_DAYS) ?? todayYmd, MIN_YMD, MAX_YMD);
        const sprintStartDate = isYmd(parsedForecast.sprintStartDate) ? clampYmdToBounds(parsedForecast.sprintStartDate, MIN_YMD, MAX_YMD) : null;
        const targetDate = isYmd(parsedForecast.targetDate) ? clampYmdToBounds(parsedForecast.targetDate, sprintStartDate ?? MIN_YMD, maxFuture) : null;
        const planHistory = normalizeForecastPlanHistory(parsedForecast.planHistory);
        const sprintStatusDisplayMode = normalizeSprintStatusDisplayMode(parsedForecast.sprintStatusDisplayMode);
        setForecast({ ...parsedForecast, sprintStartDate, targetDate, planHistory, sprintStatusDisplayMode });
      } else {
        setForecast(DEFAULT_FORECAST);
      }
    } catch {
      setForecast(DEFAULT_FORECAST);
    }

    try {
      const sp = await storage.get(STORAGE_KEYS.sprints);
      if (sp?.value) {
        const parsed = JSON.parse(sp.value);
        const cleanedSource = Array.isArray(parsed)
          ? parsed.filter(Boolean).map((s) => ({ ...s, name: sanitizeSprintName(s?.name), startDate: toYmdLoose(s?.startDate), endDate: s?.endDate ? toYmdLoose(s?.endDate) : null }))
          : [];
        const cleaned = cleanedSource.filter((s) => isYmd(s.startDate) && (s.endDate === null || (isYmd(s.endDate) && s.endDate >= s.startDate)));
        setSprints(cleaned);
      } else setSprints([]);
    } catch {
      setSprints([]);
    }

    try {
      const [rm, rmUpdated] = await Promise.all([
        storage.get(STORAGE_KEYS.roadmap),
        storage.get(STORAGE_KEYS.roadmapUpdatedAt),
      ]);
      setRoadmapUpdatedAt(rmUpdated?.value ? String(rmUpdated.value) : null);
      if (rm?.value) {
        const parsed = JSON.parse(rm.value);
        const normalized = normalizeRoadmapForProfile(parsed, new Set(), todayYmd);
        setRoadmap(normalized ?? null);
      } else {
        setRoadmap(null);
      }
    } catch {
      setRoadmap(null);
      setRoadmapUpdatedAt(null);
    }

    try {
      const ent = await storage.get(STORAGE_KEYS.entries);
      if (ent?.value) {
        const loaded = JSON.parse(ent.value) || [];
        const normalized = [...loaded].map((e) => ({ ...e, date: toYmdLoose(e?.date), caloriesIn: e.caloriesIn ?? null, totalBurned: e.totalBurned ?? null, activeCalories: e.activeCalories ?? null, netCalories: e.netCalories ?? null, bmr: e.bmr ?? null, theoreticalWeight: e.theoreticalWeight ?? e.weight }))
          .filter((e) => isYmd(e.date) && e?.tdeeSource !== "demo").sort((a, b) => (a.date < b.date ? -1 : 1));
        setEntries(reconcileEntriesDerivedMetrics(normalized, loadedVitalsForEntries));
      } else {
        setEntries([]);
      }
    } catch {
      setEntries([]);
    }

    try {
      const del = await storage.get(STORAGE_KEYS.deletedEntries);
      if (del?.value) {
        const parsed: Record<string, string> = JSON.parse(del.value) || {};
        // Prune tombstones older than 90 days
        const cutoff = new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString();
        const pruned = Object.fromEntries(Object.entries(parsed).filter(([, ts]) => ts > cutoff));
        setDeletedEntryTombstones(pruned);
      }
    } catch {
      setDeletedEntryTombstones({});
    }

    try {
      const hs = await storage.get(STORAGE_KEYS.hasSynced);
      setHasSynced(hs?.value === "1");
    } catch {
      setHasSynced(false);
    }

    try {
      const hkEnabled = await storage.get(STORAGE_KEYS.healthKitEnabled);
      const hkEnabledUpdatedAt = await storage.get(STORAGE_KEYS.healthKitEnabledUpdatedAt);
      const parsedLocalTs = hkEnabledUpdatedAt?.value ? Date.parse(String(hkEnabledUpdatedAt.value)) : NaN;
      healthKitLocalUpdatedAtRef.current = Number.isFinite(parsedLocalTs) ? parsedLocalTs : 0;
      if (hkEnabled?.value === "1" || hkEnabled?.value === "true") {
        setHealthKitEnabled(true);
        console.info("[healthkit] startup resolution: using local persisted value", { value: true, updatedAt: hkEnabledUpdatedAt?.value ?? null });
      } else if (hkEnabled?.value === "0" || hkEnabled?.value === "false") {
        setHealthKitEnabled(false);
        console.info("[healthkit] startup resolution: using local persisted value", { value: false, updatedAt: hkEnabledUpdatedAt?.value ?? null });
      } else {
        console.info("[healthkit] startup resolution: no local persisted value found");
      }
    } catch {
      console.info("[healthkit] startup resolution: failed to read local persisted value");
    }
  };

  // ---- Load ----
  useEffect(() => {
    (async () => {
      // Reset so sync pull waits for fresh data (prevents race with stale demo state).
      setInitialDataLoaded(false);
      let foundEntries = false;
      let foundSprints = false;
      try {
        const [ent, sp] = await Promise.all([storage.get(STORAGE_KEYS.entries), storage.get(STORAGE_KEYS.sprints)]);
        foundEntries = !!ent?.value;
        foundSprints = !!sp?.value;
      } catch {
        // ignore
      }
      hadLocalDataOnBootRef.current = foundEntries || foundSprints;

      await loadRealProfileFromStorage();

      if (typeof window !== "undefined") {
        const params = new URLSearchParams(window.location.search);
        const previewMode = params.get("preview");
        const requestedPage = params.get("page");
        const allowedPages = new Set(["tracker", "trends", "all", "sprints", "settings", "roadmap", "viewer"]);

        // Preview helper: show a populated main dashboard instead of onboarding when there is no local data.
        if (
          allowPreviewBootstrap
          && !userId
          && (previewMode === "dashboard" || previewMode === "main" || previewMode === "layout" || previewMode === "shell" || previewMode === "ui")
                    && !foundEntries
          && !foundSprints
        ) {
          setIsDemoMode(true);
          setDemoProfileId("cut_ahead");
          setDemoEntrySource("settings");
          applyDemoProfile("cut_ahead");
          setCurrentPage("tracker");
          setDemoNotice("Preview mode: loaded demo dashboard.");
        }

        if (requestedPage && allowedPages.has(requestedPage)) setCurrentPage(requestedPage);
      }

      setInitialDataLoaded(true);
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowPreviewBootstrap, userId]);

  const enterDemoMode = (profileId: DemoProfileId, source: DemoEntrySource = "settings") => {
    if (userId) {
      setSettingsDataStatusMsg("Demo Mode is disabled while signed in. Sign out first.");
      return;
    }
    if (!isDemoMode) snapshotRealProfile();
    setDemoEntrySource(source);
    setDemoProfileId(profileId);
    setIsDemoMode(true);
    applyDemoProfile(profileId);
    setSettingsDataStatusMsg("Demo Mode enabled. This sandbox never saves to your real profile.");
  };

  const exitDemoMode = async () => {
    const source = demoEntrySource;
    setIsDemoMode(false);
    setDemoNotice(null);
    setShowCsvImportModal(false);
    setRestoreProfileFile(null);
    setRestoreConfirmText("");

    if (source === "onboarding") {
      realProfileSnapshotRef.current = null;
      setDemoEntrySource("settings");
      setEntries([]);
      setSprints([]);
      setRoadmap(null);
      setVitals(normalizeVitals(DEFAULT_VITALS));
      setForecast(DEFAULT_FORECAST);
      setObPhase("bodyStats");
      setCurrentPage("tracker");
      setShowLanding(true);
      setSettingsDataStatusMsg("Exited Demo Mode.");
      return;
    }

    const snapshot = realProfileSnapshotRef.current;
    if (snapshot) {
      if (!userId && (isNativeIOS || (snapshot.entries.length === 0 && snapshot.sprints.length === 0))) {
        realProfileSnapshotRef.current = null;
        setEntries([]);
        setSprints([]);
        setVitals(normalizeVitals(DEFAULT_VITALS));
        setForecast(DEFAULT_FORECAST);
        setRoadmap(null);
        setShowLanding(true);
        setSettingsDataStatusMsg("Exited Demo Mode.");
        return;
      }
      setEntries(snapshot.entries);
      setVitals(snapshot.vitals);
      setForecast(snapshot.forecast);
      setSprints(snapshot.sprints);
      setRoadmap(snapshot.roadmap);
      setCurrentPage(snapshot.currentPage);
      realProfileSnapshotRef.current = null;
      setSettingsDataStatusMsg("Exited Demo Mode. Restored your previous profile in place.");
      return;
    }

    if (!userId) {
      setEntries([]);
      setSprints([]);
      setVitals(normalizeVitals(DEFAULT_VITALS));
      setForecast(DEFAULT_FORECAST);
      setRoadmap(null);
      setShowLanding(true);
      setSettingsDataStatusMsg("Exited Demo Mode.");
      return;
    }
    // Clear demo entries synchronously so pullAndMerge (triggered by isDemoMode→false)
    // doesn't merge demo entries with real Supabase entries before storage loads.
    setEntries([]);
    setSprints([]);
    setSettingsDataStatusMsg("Exited Demo Mode. Restored your saved profile.");
    await loadRealProfileFromStorage();
  };

  useEffect(() => {
    if (!userId || !isDemoMode) return;
    setIsDemoMode(false);
    setDemoNotice(null);
    setDemoEntrySource("settings");
    realProfileSnapshotRef.current = null;
    setEntries([]);
    setSprints([]);
    setRoadmap(null);
    setVitals(normalizeVitals(DEFAULT_VITALS));
    setForecast(DEFAULT_FORECAST);
    setSettingsDataStatusMsg("Signed in detected — exited Demo Mode and restored your saved profile.");
    void loadRealProfileFromStorage();
  }, [isDemoMode, userId]);


  useEffect(() => {
    if (!SPRINT_PALETTE.includes(newSprintColor)) {
      setNewSprintColor(pickNextSprintColor(sprints));
    }
  }, [sprints, newSprintColor]);

  // Auto-save roadmap whenever it changes
  useEffect(() => {
    (async () => {
      try {
        if (isDemoMode || !initialDataLoaded) return;
        if (roadmap !== null) {
          await storage.set(STORAGE_KEYS.roadmap, JSON.stringify(roadmap));
          const updatedAt = new Date().toISOString();
          await storage.set(STORAGE_KEYS.roadmapUpdatedAt, updatedAt);
          setRoadmapUpdatedAt(updatedAt);
        } else {
          await storage.delete(STORAGE_KEYS.roadmap);
          await storage.delete(STORAGE_KEYS.roadmapUpdatedAt);
          setRoadmapUpdatedAt(null);
        }
      } catch {
        // ignore
      }
    })();
  }, [roadmap, isDemoMode, initialDataLoaded]);

  // ---- Derived ----
  const entriesDesc = useMemo(() => [...entriesAsc].reverse(), [entriesAsc]);
  const firstEverDate = entriesAsc[0]?.date ?? null;
  const suggestedSprintStartDate = useMemo(() => {
    const latestEntryDate = entriesAsc[entriesAsc.length - 1]?.date ?? null;
    if (isYmd(latestEntryDate)) return latestEntryDate > todayYmd ? latestEntryDate : todayYmd;
    return todayYmd;
  }, [entriesAsc, todayYmd]);

  const newSprintGuardrails = useMemo(() => validateSprintGuardrails({
    startWeight: safeNumber(newSprintStartingWeight),
    targetWeight: safeNumber(newSprintTargetWeight),
    startDate: newSprintStartDate,
    targetDate: newSprintTargetDate,
    goalType: newSprintGoalType ?? undefined,
  }), [newSprintStartingWeight, newSprintTargetWeight, newSprintStartDate, newSprintTargetDate, newSprintGoalType]);

  const isPaceHardError = (message: string) => (
    message === "Weight loss pace cannot exceed 2.0 lb/week."
    || message === "Weight gain pace cannot exceed 1.0 lb/week."
  );

  const normalizedSprintNameKey = (value: string) => sanitizeSprintName(value, "").trim().toLowerCase();

  const findSprintNameDuplicate = (candidateName: string, excludeId: string | null = null) => {
    const key = normalizedSprintNameKey(candidateName);
    if (!key) return null;
    return sprints.find((s) => s.id !== excludeId && normalizedSprintNameKey(String(s.name ?? "")) === key) ?? null;
  };

  const newSprintNameDuplicate = useMemo(
    () => findSprintNameDuplicate(newSprintName),
    [newSprintName, sprints],
  );

  const createSprintBlockingErrors = useMemo(() => {
    const errors = newSprintGuardrails.hardErrors.filter((err) => !isPaceHardError(err));
    if (newSprintNameDuplicate) errors.push(`Sprint name already exists: "${newSprintNameDuplicate.name}".`);
    return errors;
  }, [newSprintGuardrails.hardErrors, newSprintNameDuplicate]);

  const newSprintPaceGuidance = useMemo(() => {
    const sw = safeNumber(newSprintStartingWeight);
    const tw = safeNumber(newSprintTargetWeight);
    const paceAbs = Number.isFinite(newSprintGuardrails.paceLbsPerWeek) ? Math.abs(Number(newSprintGuardrails.paceLbsPerWeek)).toFixed(1) : "—";
    if (sw !== null && tw !== null && tw > sw) {
      return {
        recommendedLine: "Recommended max gain pace is about 1 lb/week.",
        targetLine: `This plan targets ${paceAbs} lb/week.`,
        helperLine: "Consider extending the timeline or adjusting calories.",
      };
    }
    if (sw !== null && tw !== null && Math.abs(tw - sw) < 0.25) {
      return {
        recommendedLine: "Maintenance typically stays within ±0.25 lb/week.",
        targetLine: `This plan targets ${paceAbs} lb/week.`,
        helperLine: "Adjust calories or timeline to stay closer to maintenance.",
      };
    }
    return {
      recommendedLine: "Recommended max loss pace is about 2 lb/week.",
      targetLine: `This plan targets ${paceAbs} lb/week.`,
      helperLine: "Consider extending the timeline or adjusting calories.",
    };
  }, [newSprintStartingWeight, newSprintTargetWeight, newSprintGuardrails.paceLbsPerWeek]);

  const obSprintGuardrails = useMemo(() => validateSprintGuardrails({
    startWeight: safeNumber(obSprint.startWeight),
    targetWeight: safeNumber(obSprint.targetWeight),
    startDate: obSprint.startDate,
    targetDate: obSprint.targetDate,
    goalType: obSprint.goalType,
  }), [obSprint.startWeight, obSprint.targetWeight, obSprint.startDate, obSprint.targetDate, obSprint.goalType]);

  const getPaceWarningCopy = (goalType: SprintGoalType, paceLbsPerWeek: number | null) => {
    if (!Number.isFinite(paceLbsPerWeek)) return null;
    const pace = Number(paceLbsPerWeek);
    if (goalType === "cut" && pace < -SPRINT_PACE_LIMITS.cutMaxLossLbsPerWeek) {
      return {
        title: `Requested: ${formatSignedPace(pace, 1)}`,
        recommendedLine: "Max allowed: -2.0 lb/week",
        targetLine: "Adjust your timeline or target weight to continue.",
        helperLine: null,
      };
    }
    if (goalType === "bulk" && pace > SPRINT_PACE_LIMITS.bulkMaxGainLbsPerWeek) {
      return {
        title: `Requested: ${formatSignedPace(pace, 1)}`,
        recommendedLine: "Max allowed: +1.0 lb/week",
        targetLine: "Adjust your timeline or target weight to continue.",
        helperLine: null,
      };
    }
    if (goalType === "maintain" && Math.abs(pace) > SPRINT_PACE_LIMITS.maintainMaxAbsLbsPerWeek) {
      return {
        title: "This plan won’t maintain weight.",
        recommendedLine: "Maintenance typically stays within ±0.25 lb/week.",
        targetLine: `This plan implies ${formatSignedPace(pace, 1)}.`,
        helperLine: "Consider extending the timeline or choosing a less aggressive goal.",
      };
    }
    return null;
  };

  const obInferredPaceByDate = useMemo(() => {
    if (obSprint.planMethod !== "byDate") return null;
    const metrics = computeSprintPlanMetrics({
      startWeight: safeNumber(obSprint.startWeight),
      targetWeight: safeNumber(obSprint.targetWeight),
      startDate: obSprint.startDate,
      targetDate: obSprint.targetDate,
    });
    return Number.isFinite(metrics.paceLbsPerWeek) ? Number(metrics.paceLbsPerWeek) : null;
  }, [obSprint.planMethod, obSprint.startWeight, obSprint.targetWeight, obSprint.startDate, obSprint.targetDate]);

  const obInferredByDateWarning = useMemo(() => {
    if (obSprint.planMethod !== "byDate") return null;
    const goalType = (obSprint.goalType ?? deriveSprintGoalType(safeNumber(obSprint.startWeight), safeNumber(obSprint.targetWeight))) as SprintGoalType;
    return getPaceWarningCopy(goalType, obInferredPaceByDate);
  }, [obSprint.planMethod, obSprint.goalType, obSprint.startWeight, obSprint.targetWeight, obInferredPaceByDate]);

  const obShowPaceWarning = obInferredByDateWarning !== null;

  const formatMdy = (ymd?: string | null) => {
    if (!ymd) return "—";
    const dt = parseISODateYMD(ymd);
    if (!dt) return "—";
    const d = String(dt.getDate()).padStart(2, "0");
    const m = String(dt.getMonth() + 1).padStart(2, "0");
    const y = dt.getFullYear();
    return `${m}/${d}/${y}`;
  };

  const canCreateSprint = useMemo(() => {
    if (!newSprintName.trim()) return false;
    if (!isYmd(newSprintStartDate)) return false;
    if (newSprintType === "new") {
      const w = safeNumber(newSprintStartingWeight);
      return w !== null && w > 0 && isYmd(newSprintTargetDate) && createSprintBlockingErrors.length === 0;
    }
    if (!isYmd(newSprintEndDate)) return false;
    return newSprintEndDate >= newSprintStartDate;
  }, [newSprintName, newSprintStartDate, newSprintType, newSprintStartingWeight, newSprintEndDate, newSprintTargetDate, createSprintBlockingErrors.length]);

  const isActiveSprint = (sprint) => !!sprint && sprint.endDate === null;
  const getBestKnownWeightByDate = (dateYmd, options: { allowAfter?: boolean; floorDate?: string | null } = {}) => {
    if (!Array.isArray(entriesAsc) || !isYmd(dateYmd)) return null;
    const allowAfter = options.allowAfter === true;
    const floorDate = isYmd(options.floorDate) ? options.floorDate : null;

    const findWeight = (useTheoretical = false) => {
      for (let i = entriesAsc.length - 1; i >= 0; i -= 1) {
        const entry = entriesAsc[i];
        if (!entry || !isYmd(entry.date)) continue;
        if (floorDate && entry.date < floorDate) continue;
        if (!allowAfter && entry.date > dateYmd) continue;
        const cand = useTheoretical ? safeNumber(entry.theoreticalWeight) : safeNumber(entry.weight);
        if (cand !== null) return cand;
      }
      return null;
    };

    const logged = findWeight(false);
    if (logged !== null) return logged;
    const inferred = findWeight(true);
    if (inferred !== null) return inferred;
    if (!allowAfter) {
      return getBestKnownWeightByDate(dateYmd, { ...options, allowAfter: true });
    }
    return null;
  };

  const getActiveSprint = (allSprints) => {
    if (!Array.isArray(allSprints) || !allSprints.length) return null;
    if (forecast.activeSprintId) {
      const byPointer = allSprints.find((s) => s.id === forecast.activeSprintId);
      if (isActiveSprint(byPointer)) return byPointer;
    }
    return allSprints.find((s) => isActiveSprint(s)) ?? null;
  };
  const getSprintDisplayEndDate = (sprint, options: { treatAsActive?: boolean } = {}) => {
    if (!sprint) return null;
    if (isYmd(sprint.endDate)) return sprint.endDate;
    if (options.treatAsActive) return null;
    if (isYmd(sprint.forecastEndDate) && sprint.forecastEndDate >= sprint.startDate) return sprint.forecastEndDate;
    if (isYmd(sprint.startDate) && sprint.startDate > todayYmd) return sprint.startDate;
    return todayYmd;
  };
  const proposeArchiveEndDate = (sprint) => (isActiveSprint(sprint) ? todayYmd : (isYmd(sprint?.endDate) ? sprint.endDate : todayYmd));
  const buildArchiveStats = (sprint, proposedEndDate) => {
    const startWeightValue = safeNumber(sprint?.startWeight)
      ?? getLoggedWeightForDate(entriesAsc, sprint?.startDate)
      ?? getBestKnownWeightByDate(sprint?.startDate ?? proposedEndDate, { allowAfter: true });
    const explicitEndWeight = safeNumber(sprint?.endWeight);
    const fallbackEndWeight = getLoggedWeightForDate(entriesAsc, proposedEndDate)
      ?? getBestKnownWeightByDate(proposedEndDate, { floorDate: sprint?.startDate ?? null, allowAfter: true });
    const safeStartWeight = startWeightValue ?? fallbackEndWeight ?? 0;
    const endWeightValue = explicitEndWeight ?? fallbackEndWeight ?? safeStartWeight;
    const net = endWeightValue - safeStartWeight;
    const durationWeeks = Math.max(1, Math.abs(daysBetweenClamped(sprint?.startDate, proposedEndDate)) / 7);
    const pace = net / durationWeeks;
    return { startWeightValue: safeStartWeight, endWeightValue, net, pace };
  };

  const activeSprint = useMemo(() => {
    return getActiveSprint(sprints);
  }, [sprints, forecast.activeSprintId]);
  const activeSprintId = activeSprint?.id ?? null;
  const isTrulyActiveSprint = (sprint) => !!sprint && sprint.endDate === null && sprint.id === activeSprintId;


  useEffect(() => {
    if (activeModal !== "newSprint" || newSprintType !== "new") return;
    const loggedWeight = getLoggedWeightForDate(entriesAsc, newSprintStartDate);
    if (loggedWeight === null) {
      setNewSprintStartWeightSource(null);
      return;
    }
    setNewSprintStartingWeight(String(loggedWeight));
    setNewSprintStartWeightSource("fromLog");
  }, [activeModal, newSprintType, newSprintStartDate, entriesAsc]);

  const sprintDraftGuardrails = useMemo(() => {
    if (!sprintDraft) return { hardErrors: [], softWarnings: [] };
    return validateSprintGuardrails({
      startWeight: safeNumber(sprintDraft.startingWeight),
      targetWeight: safeNumber(sprintDraft.targetWeight),
      startDate: sprintDraft.startDate,
      targetDate: sprintDraft.targetDate,
      goalType: sprintDraft.goalType ?? deriveSprintGoalType(safeNumber(sprintDraft.startingWeight), safeNumber(sprintDraft.targetWeight)),
    });
  }, [sprintDraft]);

  const sprintDraftPaceGuidance = useMemo(() => {
    const sw = safeNumber(sprintDraft?.startingWeight);
    const tw = safeNumber(sprintDraft?.targetWeight);
    const paceAbs = Number.isFinite(sprintDraftGuardrails.paceLbsPerWeek) ? Math.abs(Number(sprintDraftGuardrails.paceLbsPerWeek)).toFixed(1) : "—";
    if (sw !== null && tw !== null && tw > sw) {
      return {
        recommendedLine: "Recommended max gain pace is about 1 lb/week.",
        targetLine: `This plan targets ${paceAbs} lb/week.`,
      };
    }
    if (sw !== null && tw !== null && Math.abs(tw - sw) < 0.25) {
      return {
        recommendedLine: "Maintenance typically stays within ±0.25 lb/week.",
        targetLine: `This plan targets ${paceAbs} lb/week.`,
      };
    }
    return {
      recommendedLine: "Recommended max loss pace is about 2 lb/week.",
      targetLine: `This plan targets ${paceAbs} lb/week.`,
    };
  }, [sprintDraft?.startingWeight, sprintDraft?.targetWeight, sprintDraftGuardrails.paceLbsPerWeek]);

  useEffect(() => {
    if (activeModal !== "sprint" || !sprintDraft) return;
    const loggedWeight = getLoggedWeightForDate(entriesAsc, sprintDraft.startDate);
    if (loggedWeight === null) {
      setSprintStartWeightSource(null);
      return;
    }
    setSprintDraft((prev) => (prev ? { ...prev, startingWeight: String(loggedWeight) } : prev));
    setSprintStartWeightSource("fromLog");
  }, [activeModal, sprintDraft?.startDate, entriesAsc]);

  // Show onboarding overlay when the user has no data at all and data loading is complete.
  // A separate cloud-sync interstitial (defined after useSupabaseSync) overlays this when needed.
  // obShouldShowForAuthUser covers the email-confirmation path (SIGNED_IN fires but
  // obSignedInWithAccount is never set) — it is set by an effect after the first cloud pull.
  const showOnboarding =
    (!userId || obSignedInWithAccount || obShouldShowForAuthUser) &&
    initialDataLoaded &&
    entries.length === 0 &&
    sprints.length === 0 &&
    !isShellPreview;

  // Live auto-calculated calorie plan for onboarding step 3.
  // Depends on vitals (step 1), starting weight defaults, and sprint targets (step 3).
  const obSprintAutoCalc = useMemo(() => {
    const ft = safeNumber(obVitals.ft);
    const ins = safeNumber(obVitals.ins) ?? 0;
    const heightIn = ft !== null ? ft * 12 + ins : null;
    const startWeight = safeNumber(obSprint.startWeight);
    const targetWeight = safeNumber(obSprint.targetWeight);
    if (
      !isYmd(obSprint.startDate) || !isYmd(obSprint.targetDate) ||
      startWeight === null || targetWeight === null ||
      heightIn === null || !isYmd(obVitals.birthday) || !obVitals.gender
    ) return null;
    const days = daysBetweenInclusiveStartExclusiveEnd(obSprint.startDate, obSprint.targetDate);
    if (!days || days <= 0) return null;
    const toLose = startWeight - targetWeight;
    const requiredNet = -((toLose * 3500) / days);
    const bmr = calcBMR({ weightLbs: startWeight, heightIn, gender: obVitals.gender, birthday: obVitals.birthday });
    let tdee = null;
    let suggestedIntake = null;
    if (bmr !== null) {
      tdee = calcTDEEFromFactor({ bmr, factor: obVitals.activityFactor });
      if (tdee !== null) suggestedIntake = Math.max(0, Math.round(requiredNet + tdee));
    }
    return {
      requiredNet: Math.round(requiredNet),
      suggestedIntake,
      bmr: bmr !== null ? Math.round(bmr) : null,
      tdee: tdee !== null ? Math.round(tdee) : null,
      days,
      weeklyRate: Math.abs((requiredNet / 3500) * 7),
    };
  }, [obVitals.ft, obVitals.ins, obVitals.birthday, obVitals.gender, obVitals.activityFactor, obSprint.startWeight, obSprint.startDate, obSprint.targetDate, obSprint.targetWeight]);

  // Auto-fill calorie drafts whenever the auto-calc result changes.
  // Keep the exact computed intake/net so onboarding review and created sprint stay consistent.
  useEffect(() => {
    if (!obSprintAutoCalc) return;
    setObSprint((s) => ({
      ...s,
      netDraft: String(obSprintAutoCalc.requiredNet),
      intakeDraft: obSprintAutoCalc.suggestedIntake !== null ? String(obSprintAutoCalc.suggestedIntake) : "",
      assumedNetCalories: obSprintAutoCalc.requiredNet,
    }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [obSprintAutoCalc?.requiredNet, obSprintAutoCalc?.suggestedIntake, obVitals.gender]);

  const newSprintAutoCalc = useMemo(() => {
    const startWeight = safeNumber(newSprintStartingWeight);
    const targetWeight = safeNumber(newSprintTargetWeight);
    if (!isYmd(newSprintStartDate) || !isYmd(newSprintTargetDate) || startWeight === null || targetWeight === null) return null;
    const days = daysBetweenInclusiveStartExclusiveEnd(newSprintStartDate, newSprintTargetDate);
    if (!days || days <= 0) return null;
    const requiredNet = -(((startWeight - targetWeight) * 3500) / days);
    const bmr = calcBMR({ weightLbs: startWeight, heightIn: vitals.height, gender: vitals.gender, birthday: vitals.birthday });
    const tdee = bmr !== null ? calcTDEEFromFactor({ bmr, factor: vitals.activityFactor ?? forecast.activityFactor }) : null;
    return { requiredNet: Math.round(requiredNet), suggestedIntake: tdee !== null ? Math.max(0, Math.round(requiredNet + tdee)) : null, tdee: tdee !== null ? Math.round(tdee) : null, weeklyRate: Math.abs((requiredNet / 3500) * 7) };
  }, [newSprintStartingWeight, newSprintTargetWeight, newSprintStartDate, newSprintTargetDate, vitals, forecast.activityFactor]);

  useEffect(() => {
    if (activeModal !== "newSprint" || newSprintType !== "new" || !newSprintAutoCalc) return;
    const rawIntake = newSprintAutoCalc.suggestedIntake;
    setNewSprintAssumedNetCalories(newSprintAutoCalc.requiredNet);
    setNewSprintNetDraft(String(newSprintAutoCalc.requiredNet));
    if (rawIntake !== null) setNewSprintIntakeDraft(String(rawIntake));
  }, [activeModal, newSprintType, newSprintAutoCalc?.requiredNet, newSprintAutoCalc?.suggestedIntake, vitals.gender]);

  useEffect(() => {
    setShowAllSprintHistory(false);
  }, [activeSprint?.id, currentPage]);

  useEffect(() => {
    setMobileSprintHistoryVisibleCount(3);
    setExpandedSprintHistoryRowDate(null);
  }, [activeSprint?.id, currentPage]);

  useEffect(() => {
    setMobileAllDataVisibleCount(3);
    setExpandedAllDataRowDate(null);
  }, [currentPage]);

  const selectedSprint = useMemo(() => {
    if (currentPage === "viewer") {
      if (!viewerSprintId) return null;
      return sprints.find((s) => s.id === viewerSprintId) ?? null;
    }
    return activeSprint;
  }, [currentPage, viewerSprintId, sprints, activeSprint]);

  const selectedSprintEndDate = useMemo(() => {
    if (!selectedSprint) return null;
    return getSprintDisplayEndDate(selectedSprint, { treatAsActive: isTrulyActiveSprint(selectedSprint) });
  }, [selectedSprint, activeSprintId, todayYmd]);

  useEffect(() => {
    if (!initialDataLoaded || didNormalizeLegacySprintEndDatesRef.current) return;
    const nextSprints = sprints.map((s) => {
      if (!s || s.endDate !== null) return s;
      if (s.id === activeSprintId) return s;
      const normalizedEndDate = getSprintDisplayEndDate(s, { treatAsActive: false }) ?? todayYmd;
      return { ...s, endDate: normalizedEndDate };
    });
    const changed = nextSprints.some((s, idx) => s !== sprints[idx]);
    didNormalizeLegacySprintEndDatesRef.current = true;
    if (!changed) return;
    setSprints(nextSprints);
    void persistAll(entriesAsc, vitals, forecast, nextSprints);
  }, [initialDataLoaded, sprints, activeSprintId, todayYmd, entriesAsc, vitals, forecast]);

  const isArchivedOrViewer = useMemo(() => {
    if (!selectedSprint) return false;
    return currentPage === "viewer" || !isTrulyActiveSprint(selectedSprint);
  }, [selectedSprint, currentPage, activeSprintId]);

  const isHistoricSprint = useMemo(() => {
    if (!selectedSprint) return false;
    return selectedSprint.status === "archived" || !isTrulyActiveSprint(selectedSprint);
  }, [selectedSprint, activeSprintId]);

  useEffect(() => {
    if (!initialDataLoaded || didRepairActiveSprintsRef.current) return;
    const activeCandidates = sprints.filter((s) => isActiveSprint(s));
    if (activeCandidates.length <= 1) {
      didRepairActiveSprintsRef.current = true;
      return;
    }

    let keepId = null;
    const pointed = forecast.activeSprintId ? activeCandidates.find((s) => s.id === forecast.activeSprintId) : null;
    if (pointed) {
      keepId = pointed.id;
    } else {
      const sorted = [...activeCandidates].sort((a, b) => {
        const aKey = a.createdAt ?? a.startDate ?? "";
        const bKey = b.createdAt ?? b.startDate ?? "";
        return aKey > bKey ? -1 : aKey < bKey ? 1 : 0;
      });
      keepId = sorted[0]?.id ?? null;
    }

    const nextSprints = sprints.map((s) => (isActiveSprint(s) && s.id !== keepId ? { ...s, endDate: todayYmd } : s));
    const keeper = nextSprints.find((s) => s.id === keepId);
    const nextForecast = {
      ...forecast,
      activeSprintId: keeper?.id ?? null,
      sprintStartDate: keeper?.startDate ?? null,
    };

    didRepairActiveSprintsRef.current = true;
    setSprints(nextSprints);
    setForecast(nextForecast);
    void persistAll(entriesAsc, vitals, nextForecast, nextSprints);
  }, [initialDataLoaded, sprints, forecast, todayYmd]);

  // Keep forecast pointer aligned if an active sprint exists; allow no active sprint.
  useEffect(() => {
    const active = sprints.find((s) => s && s.endDate === null) ?? null;

    if (!active) {
      if (forecast.activeSprintId !== null) {
        const nextForecast = { ...forecast, activeSprintId: null };
        setForecast(nextForecast);
        persistAll(entriesAsc, vitals, nextForecast, sprints);
      }
      return;
    }

    if (forecast.activeSprintId !== active.id || forecast.sprintStartDate !== active.startDate) {
      const nextForecast = { ...forecast, activeSprintId: active.id, sprintStartDate: active.startDate };
      setForecast(nextForecast);
      persistAll(entriesAsc, vitals, nextForecast, sprints);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sprints, forecast.activeSprintId, forecast.sprintStartDate]);

  // Sync archive UI with current active sprint when opening settings.
  useEffect(() => {
    if (activeModal !== "sprint") return;
    if (!activeSprint) return;
    setArchiveName(activeSprint.name || "Sprint");
  }, [activeModal, activeSprint?.id, activeSprint?.name]);

  // Migration: set the current active sprint's forecast end date to 2026-05-12 and keep it.
  // (Only applies to the sprint that starts 2026-02-12 and only if it is currently unset.)
  useEffect(() => {
    if (didMigrateActiveForecastEnd.current) return;
    if (!activeSprint) return;
    if (activeSprint.endDate !== null) return;
    if (activeSprint.startDate !== "2026-02-12") return;
    if (activeSprint.forecastEndDate) {
      didMigrateActiveForecastEnd.current = true;
      return;
    }
    didMigrateActiveForecastEnd.current = true;
    // Persist on the sprint record (controls progress chart x-axis horizon)
    // eslint-disable-next-line no-void
    void (async () => {
      const nextSprint = { ...activeSprint, forecastEndDate: "2026-05-12" };
      const nextSprints = sprints.map((s) => (s.id === activeSprint.id ? nextSprint : s));
      setSprints(nextSprints);
      await persistAll(entriesAsc, vitals, forecast, nextSprints);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSprint?.id]);

  const sprintStartEffective = useMemo(() => {
    if (!selectedSprint?.startDate) return null;
    if (!firstEverDate) return selectedSprint.startDate;
    return effectiveSprintStart(firstEverDate, selectedSprint.startDate);
  }, [firstEverDate, selectedSprint]);

  const sprintEndEffective = useMemo(() => selectedSprintEndDate, [selectedSprintEndDate]);

  const sprintEntriesAsc = useMemo(() => {
    if (!sprintStartEffective) return [];
    return entriesAsc.filter((e) => e.date >= sprintStartEffective && (!sprintEndEffective || e.date <= sprintEndEffective));
  }, [entriesAsc, sprintStartEffective, sprintEndEffective]);

  const sprintEntriesDesc = useMemo(() => [...sprintEntriesAsc].reverse(), [sprintEntriesAsc]);

  const sprintAnchor = useMemo(
    () => getSprintAnchorWeight(entriesAsc, selectedSprint?.startDate ?? null, selectedSprintEndDate),
    [entriesAsc, selectedSprint?.startDate, selectedSprintEndDate]
  );

  // Sprint Settings: recompute targetDate when rate preset or relevant fields change (after sprintAnchor is available)
  useEffect(() => {
    if (sprintPlanMethod !== "byPace" || sprintPresetRate === null) return;
    const sw = safeNumber(sprintDraft?.startingWeight ?? sprintAnchor.anchorWeight);
    const tw = safeNumber(sprintDraft?.targetWeight ?? forecast.targetWeight);
    const startDate = sprintDraft?.startDate ?? activeSprint?.startDate ?? sprintStartDraft;
    const result = computeTargetDateFromRate({ startWeight: sw, targetWeight: tw, startDateYmd: startDate, rateLbsPerWeekAbs: sprintPresetRate });
    if (!result || !result.targetDateYmd) return;
    setSprintPresetClamped(result.clamped);
    setSprintDraft((prev) => (prev ? { ...prev, targetDate: result.targetDateYmd } : prev));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sprintPlanMethod, sprintPresetRate, sprintAnchor.anchorWeight, forecast.targetWeight, activeSprint?.startDate, sprintStartDraft, sprintDraft?.startingWeight, sprintDraft?.targetWeight, sprintDraft?.startDate]);

  // Sprint-scoped theoretical series (resets at sprint start)
  const sprintTheoreticalByDate = useMemo(() => calcSprintTheoreticalByDate(sprintEntriesAsc), [sprintEntriesAsc]);

  const startWeight = sprintAnchor.anchorWeight;
  const endWeight = sprintEntriesAsc.length ? sprintEntriesAsc[sprintEntriesAsc.length - 1].weight : null;

  const daysTracked = sprintEntriesAsc.length;
  const lastLoggedDate = sprintEntriesAsc[sprintEntriesAsc.length - 1]?.date ?? null;
  const statsHorizonDate = selectedSprintEndDate ?? (lastLoggedDate && lastLoggedDate > todayYmd ? lastLoggedDate : todayYmd);
  const totalSprintDays = dayCountInclusive(sprintStartEffective, statsHorizonDate);
  const trackedPct = totalSprintDays ? (daysTracked / totalSprintDays) * 100 : null;
  const endingWeightSub = selectedSprintEndDate
    ? formatMDY(selectedSprintEndDate)
    : lastLoggedDate
      ? `Last logged ${formatMDY(lastLoggedDate)}`
      : null;

  const netLoss = useMemo(() => {
    if (!startWeight || !endWeight) return null;
    return startWeight - endWeight;
  }, [startWeight, endWeight]);

  const weeklyAvgLoss = useMemo(() => {
    if (netLoss === null || !daysTracked) return null;
    const weeks = daysTracked / 7;
    return weeks ? netLoss / weeks : null;
  }, [netLoss, daysTracked]);

  const avgNet = useMemo(() => {
    const vals = sprintEntriesAsc.map((e) => e.netCalories).filter((v) => v !== null && v !== undefined);
    if (!vals.length) return null;
    return vals.reduce((a, b) => a + b, 0) / vals.length;
  }, [sprintEntriesAsc]);

  const latestSprintNet = useMemo(() => {
    for (let i = sprintEntriesAsc.length - 1; i >= 0; i -= 1) {
      const v = sprintEntriesAsc[i].netCalories;
      if (v !== null && v !== undefined) return v;
    }
    return null;
  }, [sprintEntriesAsc]);

  const avgSprintIntake = useMemo(() => {
    const vals = sprintEntriesAsc.map((e) => e.caloriesIn).filter((v) => v !== null && v !== undefined);
    if (!vals.length) return null;
    return vals.reduce((a, b) => a + b, 0) / vals.length;
  }, [sprintEntriesAsc]);
  const hasCaloriesInLogsInSprint = useMemo(
    () => sprintEntriesAsc.some((e) => e.caloriesIn !== null && e.caloriesIn !== undefined),
    [sprintEntriesAsc]
  );


  const currentBmr = useMemo(() => {
    if (!endWeight) return null;
    return calcBMR({ weightLbs: endWeight, heightIn: vitals.height, gender: vitals.gender, birthday: vitals.birthday });
  }, [endWeight, vitals]);

  const effectiveNetCaloriesForForecast = useMemo(() => {
    if (sprintAnchor.anchorWeight === null) return null;
    if (forecast.inputMode === "net") return safeNumber(forecast.assumedNetCalories) ?? 0;
    const bmr = calcBMR({ weightLbs: sprintAnchor.anchorWeight, heightIn: vitals.height, gender: vitals.gender, birthday: vitals.birthday });
    if (bmr === null) return null;
    const tdee = calcTDEEFromFactor({ bmr, factor: vitals.activityFactor ?? forecast.activityFactor });
    const intake = safeNumber(forecast.assumedNetCalories) ?? 0;
    return intake - tdee;
  }, [forecast, sprintAnchor.anchorWeight, vitals]);

  const targetIntakeForForecast = useMemo(() => {
    if (forecast.inputMode === "intake") return safeNumber(forecast.assumedNetCalories) ?? 0;
    if (effectiveNetCaloriesForForecast === null || sprintAnchor.anchorWeight === null) return null;
    const bmr = calcBMR({ weightLbs: sprintAnchor.anchorWeight, heightIn: vitals.height, gender: vitals.gender, birthday: vitals.birthday });
    if (bmr === null) return null;
    const tdee = calcTDEEFromFactor({ bmr, factor: vitals.activityFactor ?? forecast.activityFactor });
    return tdee + effectiveNetCaloriesForForecast;
  }, [forecast, effectiveNetCaloriesForForecast, sprintAnchor.anchorWeight, vitals]);

  const trackerCalorieComparison = useMemo(() => {
    const targetNet = effectiveNetCaloriesForForecast;
    const actualNet = avgNet;
    const targetIntake = targetIntakeForForecast;
    const actualIntake = avgSprintIntake;
    const mode = trackerCalorieCompareMode;

    if (mode === "net") {
      if (targetNet === null || actualNet === null) return null;
      return {
        mode,
        target: targetNet,
        actual: actualNet,
        delta: actualNet - targetNet,
      };
    }

    if (targetIntake === null || actualIntake === null) return null;
    return {
      mode,
      target: targetIntake,
      actual: actualIntake,
      delta: actualIntake - targetIntake,
    };
  }, [trackerCalorieCompareMode, effectiveNetCaloriesForForecast, avgNet, targetIntakeForForecast, avgSprintIntake]);

  useEffect(() => {
    if (!forecast.activeSprintId || effectiveNetCaloriesForForecast === null) return;
    const sprintId = String(forecast.activeSprintId);
    if (!sprintId) return;
    const tomorrow = addDaysYMD(todayYmd, 1) ?? todayYmd;

    setForecast((prev) => {
      if (!prev.activeSprintId || String(prev.activeSprintId) !== sprintId) return prev;
      const normalizedHistory = normalizeForecastPlanHistory(prev.planHistory);
      const sprintHistory = normalizedHistory.filter((row) => row.sprintId === sprintId);
      const nextTargetNet = Math.round(effectiveNetCaloriesForForecast);

      if (!sprintHistory.length) {
        const baselineDate = isYmd(prev.sprintStartDate) ? prev.sprintStartDate : todayYmd;
        return {
          ...prev,
          planHistory: [...normalizedHistory, { sprintId, effectiveDate: baselineDate, targetNet: nextTargetNet, createdAt: new Date().toISOString() }],
        };
      }

      const latest = sprintHistory[sprintHistory.length - 1];
      if (Math.round(latest.targetNet) === nextTargetNet) return prev;

      const sameEffectiveIndex = normalizedHistory.findIndex((row) => row.sprintId === sprintId && row.effectiveDate === tomorrow);
      const nextHistory = [...normalizedHistory];
      if (sameEffectiveIndex >= 0) {
        nextHistory[sameEffectiveIndex] = { ...nextHistory[sameEffectiveIndex], targetNet: nextTargetNet, createdAt: new Date().toISOString() };
      } else {
        nextHistory.push({ sprintId, effectiveDate: tomorrow, targetNet: nextTargetNet, createdAt: new Date().toISOString() });
      }
      return { ...prev, planHistory: nextHistory };
    });
  }, [forecast.activeSprintId, effectiveNetCaloriesForForecast, todayYmd]);

  // ---- Tracker chart (sprint scoped) ----

  const getSprintTimelineEnd = (sprint, todayStr, fallbackEndStr) => {
    if (!sprint) return fallbackEndStr ?? todayStr;

    // "forecastEndDate" controls how far the progress graph renders out to.
    // Archived sprints default to endDate.
    const candidate = sprint.forecastEndDate ?? (sprint.endDate !== null ? sprint.endDate : null) ?? fallbackEndStr ?? todayStr;

    if (!candidate) return todayStr;
    return candidate;
  };

  const trackerTimelineData = useMemo(() => {
    if (!sprintEntriesAsc.length || !sprintStartEffective) return [];

    const isArchived = !!selectedSprintEndDate;

    // Timeline end is per-sprint. For the active sprint we store it on the sprint record as `forecastEndDate`.
    // If none is set, fall back to target date when available, otherwise today.
    const defaultActiveEnd = isYmd(forecast.targetDate) ? forecast.targetDate : todayYmd;
    const endForTimeline = getSprintTimelineEnd(selectedSprint ?? activeSprint, todayYmd, isArchived ? selectedSprintEndDate ?? todayYmd : defaultActiveEnd);

    // Safety: don't render a timeline that ends before the start.
    const safeEndForTimeline = sprintStartEffective && endForTimeline < sprintStartEffective ? sprintStartEffective : endForTimeline;

    const base = buildDailyTimelineWithInferredRange(sprintEntriesAsc, sprintStartEffective, safeEndForTimeline, todayYmd);

    const timelineDates = base.map((d) => d.date);

    // Gap bridge (inclusive endpoints): only draw when two actual weigh-ins have missing days between them.
    const actualIndexes = base
      .map((d, idx) => (Number.isFinite(d.actual) ? idx : -1))
      .filter((idx) => idx >= 0);
    const bridgeByDate = new Map();
    for (let i = 0; i < actualIndexes.length - 1; i += 1) {
      const startIdx = actualIndexes[i];
      const endIdx = actualIndexes[i + 1];
      if (endIdx - startIdx < 2) continue; // no missing-day gap
      const startActual = base[startIdx]?.actual;
      const endActual = base[endIdx]?.actual;
      if (!Number.isFinite(startActual) || !Number.isFinite(endActual)) continue;
      for (let idx = startIdx; idx <= endIdx; idx += 1) {
        const point = base[idx];
        if (!point) continue;
        if (idx === startIdx || idx === endIdx) {
          bridgeByDate.set(point.date, point.actual);
          continue;
        }
        const t = (idx - startIdx) / (endIdx - startIdx);
        bridgeByDate.set(point.date, startActual + (endActual - startActual) * t);
      }
    }

    const expectedByDate = isHistoricSprint
      ? new Map()
      : calcExpectedWeightByDate({
        timelineDates,
        sprintStartEffective,
        startWeight,
        sprintEntriesAsc,
        todayYmd,
        hasCaloriesInLogs: hasCaloriesInLogsInSprint,
      });

    const projectedByDate = new Map();
    if (!isHistoricSprint) {
      const todayActual = base.find((d) => d.date === todayYmd && Number.isFinite(d.actual))?.actual ?? null;
      const latestActualOnOrBeforeToday = [...base]
        .reverse()
        .find((d) => d.date <= todayYmd && Number.isFinite(d.actual))?.actual ?? null;
      const anchorExpected = expectedByDate.get(todayYmd);
      const projectionAnchor = todayActual ?? latestActualOnOrBeforeToday ?? (Number.isFinite(anchorExpected) ? anchorExpected : startWeight);

      let projected = Number.isFinite(projectionAnchor) ? projectionAnchor : null;
      for (let i = 0; i < timelineDates.length; i += 1) {
        const date = timelineDates[i];
        if (date < todayYmd || effectiveNetCaloriesForForecast === null || projected === null) {
          projectedByDate.set(date, null);
          continue;
        }
        if (date === todayYmd) {
          projectedByDate.set(date, projected);
          continue;
        }
        projected += effectiveNetCaloriesForForecast / 3500;
        projectedByDate.set(date, projected);
      }
    }

    const originalPlanEnd = isHistoricSprint ? selectedSprint?.targetDate ?? selectedSprintEndDate : selectedSprintEndDate ?? forecast.targetDate;
    const targetWeight = safeNumber(isHistoricSprint ? selectedSprint?.targetWeight : forecast.targetWeight);
    const planSpanDays = originalPlanEnd && sprintStartEffective ? daysBetweenInclusiveStartExclusiveEnd(sprintStartEffective, originalPlanEnd) : null;

    return base.map((d) => {
      let originalPlan = null;
      if (sprintStartEffective && originalPlanEnd && targetWeight !== null && startWeight !== null && d.date >= sprintStartEffective && d.date <= originalPlanEnd) {
        if (!planSpanDays || planSpanDays <= 0) {
          originalPlan = startWeight;
        } else {
          const daysFromStart = daysBetweenInclusiveStartExclusiveEnd(sprintStartEffective, d.date) ?? 0;
          originalPlan = startWeight + ((targetWeight - startWeight) * daysFromStart) / planSpanDays;
        }
      }

      return {
        date: d.date,
        actual: d.actual,
        inferred: d.actual === null ? d.inferred : null,
        bridge: bridgeByDate.get(d.date) ?? null,
        expected: isHistoricSprint ? null : expectedByDate.get(d.date) ?? null,
        projected: isHistoricSprint ? null : projectedByDate.get(d.date) ?? null,
        originalPlan,
      };
    });
  }, [
    sprintEntriesAsc,
    sprintStartEffective,
    selectedSprint,
    activeSprint,
    forecast.targetDate,
    forecast.targetWeight,
    effectiveNetCaloriesForForecast,
    todayYmd,
    startWeight,
    hasCaloriesInLogsInSprint,
    isHistoricSprint,
  ]);

  const trackerTimelineDays = useMemo(() => countTimelineDays(trackerTimelineData), [trackerTimelineData]);
  const trackerActualEntryDays = useMemo(() => countActualEntryDays(trackerTimelineData), [trackerTimelineData]);
  const trackerAutoMovingAverageWindow = useMemo(
    () => suggestedMovingAverageWindowFromData(trackerTimelineDays, trackerActualEntryDays),
    [trackerTimelineDays, trackerActualEntryDays]
  );
  const sprintStatusMovingAverageWindow = trackerAutoMovingAverageWindow;
  const trackerMovingAverageWindow = movingAverageMode === "auto" ? trackerAutoMovingAverageWindow : manualMovingAverageWindow;

  const trackerChartData = useMemo(
    () => addDailyMovingAverage(trackerTimelineData, trackerMovingAverageWindow, todayYmd),
    [trackerTimelineData, trackerMovingAverageWindow, todayYmd]
  );
  const trackerStatusChartData = useMemo(
    () => addDailyMovingAverage(trackerTimelineData, sprintStatusMovingAverageWindow, todayYmd),
    [trackerTimelineData, sprintStatusMovingAverageWindow, todayYmd]
  );

  const trackerTimelineEnd = trackerTimelineData.length ? trackerTimelineData[trackerTimelineData.length - 1].date : null;
  const trackerVisibleEnd = useMemo(() => {
    if (!trackerTimelineEnd) return null;
    if (trackerViewMode === "target") return trackerTimelineEnd;
    if (trackerViewMode === "toToday") {
      // Range: sprint start → min(today, sprint end)
      return todayYmd < trackerTimelineEnd ? todayYmd : trackerTimelineEnd;
    }
    const offsetDays = trackerViewMode === "6w" ? 42 : 21;
    const rangeEnd = addDaysYMD(todayYmd, offsetDays) ?? trackerTimelineEnd;
    return rangeEnd < trackerTimelineEnd ? rangeEnd : trackerTimelineEnd;
  }, [trackerTimelineEnd, trackerViewMode, todayYmd]);

  const trackerVisibleChartData = useMemo(() => {
    if (!trackerVisibleEnd || !sprintStartEffective) return trackerChartData;
    return filterSeriesByDate(trackerChartData, sprintStartEffective, trackerVisibleEnd);
  }, [trackerChartData, sprintStartEffective, trackerVisibleEnd]);

  const trackerCalorieChartData = useMemo(() => {
    if (!trackerVisibleChartData.length) return [];
    const byDate = new Map(sprintEntriesAsc.map((e) => [e.date, e]));
    const activeSprintId = selectedSprint?.id ? String(selectedSprint.id) : (forecast.activeSprintId ? String(forecast.activeSprintId) : null);
    const planHistory = normalizeForecastPlanHistory(forecast.planHistory).filter((row) => row.sprintId === activeSprintId);

    const targetForDate = (date) => {
      for (let i = planHistory.length - 1; i >= 0; i -= 1) {
        const row = planHistory[i];
        if (row.effectiveDate <= date) return row.targetNet;
      }
      return effectiveNetCaloriesForForecast;
    };

    return trackerVisibleChartData.map((d) => ({
      date: d.date,
      netCalories: byDate.get(d.date)?.netCalories ?? null,
      caloriesIn: byDate.get(d.date)?.caloriesIn ?? null,
      tdee: byDate.get(d.date)?.totalBurned ?? null,
      activeCalories: byDate.get(d.date)?.activeCalories ?? null,
      targetNet: targetForDate(d.date),
    }));
  }, [trackerVisibleChartData, sprintEntriesAsc, selectedSprint?.id, forecast.activeSprintId, forecast.planHistory, effectiveNetCaloriesForForecast]);

  const trackerYTicks = useMemo(() => {
    if (!trackerVisibleChartData.length) return [];
    const vals = trackerVisibleChartData
      .flatMap((d) => [d.actual, d.inferred, d.movingAvg, d.expected, d.projected, d.originalPlan])
      .filter((v) => v !== null && v !== undefined);
    if (!vals.length) return [];
    const min = Math.floor(Math.min(...vals) / 5) * 5;
    const max = Math.ceil(Math.max(...vals) / 5) * 5;
    const ticks = [];
    for (let x = min; x <= max; x += 5) ticks.push(x);
    return ticks;
  }, [trackerVisibleChartData]);

  const trackerCalorieDomain = useMemo(() => {
    const vals = trackerCalorieChartData
      .flatMap((d) => [d.netCalories, d.targetNet])
      .filter((v) => v !== null && v !== undefined);
    if (!vals.length) return [-500, 500];
    const absMax = Math.max(Math.abs(Math.min(...vals)), Math.abs(Math.max(...vals)));
    const rounded = Math.ceil(absMax / 500) * 500;
    return [-rounded, rounded];
  }, [trackerCalorieChartData]);

  const trackerCalorieMobileDomain = useMemo(() => {
    const values = trackerCalorieChartData
      .map((d) => d[trackerCaloriesMetric])
      .filter((v) => v !== null && v !== undefined);
    if (!values.length) return [-500, 500];
    const dataMin = Math.min(...values);
    const dataMax = Math.max(...values);
    const yMin = Math.min(0, dataMin);
    const yMax = Math.max(0, dataMax);
    const span = Math.max(1, yMax - yMin);
    const step = span <= 1200 ? 250 : span <= 2500 ? 500 : 1000;
    const pad = Math.max(step, span * 0.08);
    const rawMin = yMin - pad;
    const rawMax = yMax + pad;
    const minTick = Math.floor(rawMin / step) * step;
    const maxTick = Math.ceil(rawMax / step) * step;
    return [minTick, maxTick];
  }, [trackerCalorieChartData, trackerCaloriesMetric]);

  const trackerCalorieMobileTicks = useMemo(() => {
    if (trackerCaloriesMetric !== "netCalories") return [];
    const [minVal, maxVal] = trackerCalorieMobileDomain;
    const span = Math.max(1, maxVal - minVal);
    const step = span <= 1200 ? 250 : span <= 2500 ? 500 : 1000;
    const ticks: number[] = [];
    for (let v = minVal; v <= maxVal; v += step) ticks.push(v);
    if (!ticks.includes(0)) ticks.push(0);
    return ticks.sort((a, b) => a - b);
  }, [trackerCalorieMobileDomain, trackerCaloriesMetric]);

  const trackerChartDates = useMemo(() => trackerVisibleChartData.map((point) => point.date), [trackerVisibleChartData]);
  const trackerXAxisFormatter = useMemo(
    () => createAdaptiveDateTickFormatter(trackerChartDates.length),
    [trackerChartDates.length],
  );
  const trackerMobileXAxisTicks = useMemo(() => {
    return buildAdaptiveDateTicks(trackerChartDates, getTrackerDateTickCount(trackerChartDates.length, "mobile"));
  }, [trackerChartDates]);
  const trackerDesktopXAxisTicks = useMemo(() => {
    return buildAdaptiveDateTicks(trackerChartDates, getTrackerDateTickCount(trackerChartDates.length, "desktop"));
  }, [trackerChartDates]);

  const trackerCaloriesMetricLabel = TRACKER_CALORIE_METRIC_OPTIONS.find(({ key }) => key === trackerCaloriesMetric)?.label ?? "Net";

  const renderTrackerCaloriesTooltip = ({ active, payload, label }) => {
    if (!active || !label) return null;
    const row = payload?.find((entry) => entry?.payload)?.payload;
    if (!row) return null;

    const metricValue = row[trackerCaloriesMetric];
    const targetValue = row.targetNet;
    const showTarget = trackerCaloriesMetric === "netCalories";

    return (
      <div style={tooltipStyle}>
        <div style={{ fontWeight: 700, marginBottom: "0.3rem" }}>{formatMDY(label)}</div>
        <div style={{ display: "grid", gap: "0.18rem" }}>
          <div>
            <span style={{ color: "rgba(255,255,255,0.72)" }}>{trackerCaloriesMetricLabel}:</span>{" "}
            <span style={{ color: "rgba(255,255,255,0.95)", fontWeight: 700 }}>
              {typeof metricValue === "number" ? comma(metricValue) : "No log"}
            </span>
          </div>
          {showTarget ? (
            <div>
              <span style={{ color: "rgba(255,255,255,0.72)" }}>Target:</span>{" "}
              <span style={{ color: "rgba(255,255,255,0.95)", fontWeight: 700 }}>
                {typeof targetValue === "number" ? comma(targetValue) : "No target"}
              </span>
            </div>
          ) : null}
        </div>
      </div>
    );
  };

  const viewerChartData = useMemo(() => {
    if (!trackerChartData.length || !sprintStartEffective || !statsHorizonDate) return [];
    return filterSeriesByDate(trackerChartData, sprintStartEffective, statsHorizonDate);
  }, [trackerChartData, sprintStartEffective, statsHorizonDate]);

  const viewerMetricRowsBase = useMemo(() => {
    const byDate = new Map(sprintEntriesAsc.map((entry) => [entry.date, entry]));
    return viewerChartData.map((point) => ({
      date: point.date,
      weight: point.actual ?? point.inferred ?? null,
      weightLogged: point.actual,
      caloriesIn: byDate.get(point.date)?.caloriesIn ?? null,
      activeCalories: byDate.get(point.date)?.activeCalories ?? null,
      netCalories: byDate.get(point.date)?.netCalories ?? null,
      tdee: byDate.get(point.date)?.totalBurned ?? null,
      movingAvg: point.movingAvg ?? null,
    }));
  }, [viewerChartData, sprintEntriesAsc]);

  const viewerMetricObservedPoints = useMemo(() => {
    if (viewerMetric === "weight") {
      return viewerMetricRowsBase
        .filter((row) => Number.isFinite(row.weightLogged))
        .map((row) => ({ date: row.date, value: row.weightLogged as number }));
    }
    return viewerMetricRowsBase
      .filter((row) => Number.isFinite(row[viewerMetric]))
      .map((row) => ({ date: row.date, value: row[viewerMetric] as number }));
  }, [viewerMetric, viewerMetricRowsBase]);

  const viewerMetricAverage = useMemo(() => {
    if (!viewerMetricObservedPoints.length) return null;
    const values = viewerMetricObservedPoints.map((point) => point.value);
    return values.reduce((sum, value) => sum + value, 0) / values.length;
  }, [viewerMetricObservedPoints]);

  const viewerMetricMinMax = useMemo(() => {
    if (viewerMetricObservedPoints.length < 2) return { min: null, max: null };
    const values = viewerMetricObservedPoints.map((point) => point.value);
    return { min: Math.min(...values), max: Math.max(...values) };
  }, [viewerMetricObservedPoints]);

  const viewerMetricRegression = useMemo(() => calcRegressionSlopePerDay(viewerMetricObservedPoints), [viewerMetricObservedPoints]);

  const viewerMetricTrendPerDay = useMemo(() => {
    if (!viewerMetricRegression || viewerMetricObservedPoints.length < 4) return null;
    const slope = viewerMetricRegression.slopePerDay;
    return Math.abs(slope) < 0.0001 ? 0 : slope;
  }, [viewerMetricRegression, viewerMetricObservedPoints.length]);

  const viewerWeightStartPoint = useMemo(() => {
    if (!viewerMetricRowsBase.length) return null;
    const first = viewerMetricRowsBase[0];
    if (first.weight !== null) return { date: first.date, weight: first.weight };
    const fallback = viewerMetricRowsBase.find((point) => point.weight !== null);
    return fallback ? { date: first.date, weight: fallback.weight as number } : null;
  }, [viewerMetricRowsBase]);

  const viewerWeightEndPoint = useMemo(() => {
    if (!viewerMetricRowsBase.length) return null;
    const last = viewerMetricRowsBase[viewerMetricRowsBase.length - 1];
    if (last.weight !== null) return { date: last.date, weight: last.weight };
    for (let i = viewerMetricRowsBase.length - 1; i >= 0; i -= 1) {
      if (viewerMetricRowsBase[i].weight !== null) return { date: last.date, weight: viewerMetricRowsBase[i].weight as number };
    }
    return null;
  }, [viewerMetricRowsBase]);

  const viewerWeightDelta = useMemo(() => {
    if (!viewerWeightStartPoint || !viewerWeightEndPoint) return null;
    return viewerWeightEndPoint.weight - viewerWeightStartPoint.weight;
  }, [viewerWeightEndPoint, viewerWeightStartPoint]);

  const viewerWeightWeeklyDelta = useMemo(() => {
    if (viewerWeightDelta === null || !viewerWeightStartPoint || !viewerWeightEndPoint) return null;
    const days = daysBetweenInclusiveStartExclusiveEnd(viewerWeightStartPoint.date, viewerWeightEndPoint.date);
    if (days === null || days <= 0) return null;
    return viewerWeightDelta / (days / 7);
  }, [viewerWeightDelta, viewerWeightEndPoint, viewerWeightStartPoint]);

  const viewerMetricChips = useMemo(() => {
    return buildMetricStatChips({
      metric: viewerMetric,
      metricAverage: viewerMetricAverage,
      metricMinMax: viewerMetricMinMax,
      metricObservedPoints: viewerMetricObservedPoints,
      metricTrendPerDay: viewerMetricTrendPerDay,
      startPoint: viewerWeightStartPoint,
      endPoint: viewerWeightEndPoint,
      totalDelta: viewerWeightDelta,
      weeklyDelta: viewerWeightWeeklyDelta,
      startLabel: viewerChartData[0]?.date ?? null,
      endLabel: viewerChartData[viewerChartData.length - 1]?.date ?? null,
    });
  }, [buildMetricStatChips, viewerMetric, viewerMetricAverage, viewerMetricMinMax, viewerMetricObservedPoints, viewerMetricTrendPerDay, viewerWeightStartPoint, viewerWeightEndPoint, viewerWeightDelta, viewerWeightWeeklyDelta, viewerChartData]);

  const viewerDaysTrackedLabel = useMemo(() => {
    const logged = viewerMetricObservedPoints.length;
    const total = viewerMetricRowsBase.length;
    const pct = total > 0 ? Math.round((logged / total) * 100) : 0;
    return { logged, total, pct };
  }, [viewerMetricObservedPoints.length, viewerMetricRowsBase.length]);

  const viewerSummaryChips = useMemo(() => ([
    ...viewerMetricChips,
    <ViewerStatChip key="viewer-days-tracked" label="Days tracked" value={`${viewerDaysTrackedLabel.logged}/${viewerDaysTrackedLabel.total || 0}`} sub={`${viewerDaysTrackedLabel.pct}% of days`} tone="muted" />,
  ]), [viewerMetricChips, viewerDaysTrackedLabel.logged, viewerDaysTrackedLabel.pct, viewerDaysTrackedLabel.total]);

  const viewerWeightYTicks = useMemo(() => {
    if (!viewerChartData.length) return [];
    const values = viewerChartData
      .flatMap((point) => [point.actual, point.inferred, point.movingAvg])
      .filter((value) => value !== null && value !== undefined);
    if (!values.length) return [];
    const min = Math.floor(Math.min(...values) / 5) * 5;
    const max = Math.ceil(Math.max(...values) / 5) * 5;
    const ticks: number[] = [];
    for (let value = min; value <= max; value += 5) ticks.push(value);
    return ticks;
  }, [viewerChartData]);

  const viewerMetricDomain = useMemo(() => {
    if (viewerMetric !== "netCalories") return ["auto", "auto"] as const;
    const values = viewerMetricRowsBase.map((row) => row.netCalories).filter((value) => value !== null && value !== undefined);
    if (!values.length) return [-500, 500] as const;
    const absMax = Math.max(Math.abs(Math.min(...values)), Math.abs(Math.max(...values)));
    const rounded = Math.ceil(absMax / 500) * 500;
    return [-rounded, rounded] as const;
  }, [viewerMetric, viewerMetricRowsBase]);

  const viewerChartDates = useMemo(() => viewerChartData.map((point) => point.date), [viewerChartData]);
  const viewerXAxisFormatter = useMemo(() => createAdaptiveDateTickFormatter(viewerChartDates.length), [viewerChartDates.length]);
  const viewerMobileXAxisTicks = useMemo(
    () => buildAdaptiveDateTicks(viewerChartDates, getTrackerDateTickCount(viewerChartDates.length, "mobile")),
    [viewerChartDates]
  );
  const viewerDesktopXAxisTicks = useMemo(
    () => buildAdaptiveDateTicks(viewerChartDates, getTrackerDateTickCount(viewerChartDates.length, "desktop")),
    [viewerChartDates]
  );

  const renderViewerMetricTooltip = ({ active, payload, label }) => {
    if (!active || !label) return null;
    const row = payload?.find((entry) => entry?.payload)?.payload;
    if (!row) return null;
    return renderMetricTooltip(viewerMetric, row, label);
  };

  // ---- Trends page timeline ----
  const trendsMaxEnd = useMemo(() => clampYmdToBounds(addDaysYMD(todayYmd, MAX_FUTURE_DAYS) ?? todayYmd, MIN_YMD, MAX_YMD), [todayYmd]);

  const sanitizeChartRange = (startRaw, endRaw, editedField = "end") => {
    const fallbackStart = entriesAsc[0]?.date ?? todayYmd;
    const startCandidate = isYmd(startRaw) ? startRaw : fallbackStart;
    const endCandidateBase = isYmd(endRaw) ? endRaw : todayYmd;

    let start = clampYmdToBounds(startCandidate, MIN_YMD, MAX_YMD);
    let end = clampYmdToBounds(endCandidateBase, start, trendsMaxEnd);
    let message = null;

    if (end < start) {
      if (editedField === "start") end = start;
      else start = end;
      message = "Start date cannot be after end date; range adjusted.";
    }

    const spanDays = daysBetweenClamped(start, end, MAX_DAILY_POINTS);
    if (spanDays === null) {
      if (editedField === "start") {
        end = clampYmdToBounds(addDaysYMD(start, MAX_DAILY_POINTS - 1) ?? start, start, trendsMaxEnd);
      } else {
        start = clampYmdToBounds(addDaysYMD(end, -(MAX_DAILY_POINTS - 1)) ?? MIN_YMD, MIN_YMD, end);
      }
      message = `Date range too large; showing last ${MAX_DAILY_POINTS.toLocaleString()} days.`;
    }

    return { start, end, message };
  };

  const commitChartRange = (startRaw, endRaw, editedField = "end") => {
    const { start, end, message } = sanitizeChartRange(startRaw, endRaw, editedField);
    setChartsStart(start);
    setChartsEnd(end);
    setChartsStartDraft(start);
    setChartsEndDraft(end);
    setChartsRangeMessage(message);
    return { start, end, message };
  };

  useEffect(() => {
    if (!entriesAsc.length) return;
    const defaultStart = addDaysYMD(todayYmd, -30);
    const initialStart = defaultStart && defaultStart > entriesAsc[0].date ? defaultStart : entriesAsc[0].date;
    commitChartRange(trendsStart ?? initialStart, trendsEnd ?? todayYmd);
  }, [entriesAsc, todayYmd]);

  useEffect(() => {
    if (!trendsStart) return;
    setChartsStartDraft((draft) => (draft === trendsStart ? draft : trendsStart));
  }, [trendsStart]);

  useEffect(() => {
    if (!trendsEnd) return;
    setChartsEndDraft((draft) => (draft === trendsEnd ? draft : trendsEnd));
  }, [trendsEnd]);

  const trendsSelectedStart = trendsStart ?? entriesAsc[0]?.date ?? null;
  const trendsSelectedEnd = trendsEnd ?? todayYmd;

  const trendsTimelineData = useMemo(() => {
    if (!entriesAsc.length || !trendsSelectedStart || !trendsSelectedEnd || trendsSelectedStart > trendsSelectedEnd) return [];
    return buildDailyTimelineWithInferredRange(entriesAsc, trendsSelectedStart, trendsSelectedEnd);
  }, [entriesAsc, trendsSelectedStart, trendsSelectedEnd]);

  const trendsTimelineDays = useMemo(() => countTimelineDays(trendsTimelineData), [trendsTimelineData]);
  const trendsRangeDays = useMemo(() => {
    if (!trendsSelectedStart || !trendsSelectedEnd) return trendsTimelineData.length;
    const inclusiveDays = daysBetweenInclusiveStartExclusiveEnd(trendsSelectedStart, addDaysYMD(trendsSelectedEnd, 1));
    return inclusiveDays ?? trendsTimelineData.length;
  }, [trendsSelectedStart, trendsSelectedEnd, trendsTimelineData.length]);
  const trendsAutoMovingAverageWindow = useMemo(() => {
    if (trendsRangeDays <= 45) return 5;
    if (trendsRangeDays <= 120) return 7;
    if (trendsRangeDays <= 240) return 10;
    if (trendsRangeDays <= 420) return 14;
    if (trendsRangeDays <= 900) return 21;
    return 28;
  }, [trendsRangeDays]);
  const trendsMovingAverageWindow = movingAverageMode === "auto" ? trendsAutoMovingAverageWindow : manualMovingAverageWindow;

  const trendsChartData = useMemo(
    () => addDailyMovingAverage(trendsTimelineData, trendsMovingAverageWindow, todayYmd),
    [trendsTimelineData, trendsMovingAverageWindow, todayYmd]
  );

  const trackerMovingAverageLabel = movingAverageMode === "off" ? "Trend" : `Trend • ${trackerMovingAverageWindow}d`;
  const trendsMovingAverageLabel = movingAverageMode === "off" ? "Trend" : `Trend • ${trendsMovingAverageWindow}d`;
  const currentPageMovingAverageWindow = currentPage === "trends" ? trendsMovingAverageWindow : trackerMovingAverageWindow;
  const showMovingAverageLines = movingAverageMode !== "off";

  const movingAveragePresets = [3, 5, 7, 10, 14, 21, 30];

  const getRangePresetStart = (preset, end, firstEntryDate) => {
    if (!firstEntryDate) return null;
    const endDt = parseISODateYMD(end);
    if (!endDt) return null;

    let start = firstEntryDate;
    const dt = new Date(endDt);

    if (preset === "30d") dt.setDate(dt.getDate() - 30);
    else if (preset === "3m") dt.setMonth(dt.getMonth() - 3);
    else if (preset === "6m") dt.setMonth(dt.getMonth() - 6);
    else if (preset === "1y") dt.setFullYear(dt.getFullYear() - 1);
    else if (preset === "all") return firstEntryDate;

    if (preset !== "all") {
      start = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
    }

    return start < firstEntryDate ? firstEntryDate : start;
  };

  const getNextHigherMovingAveragePreset = (baseWindow) => {
    const higher = movingAveragePresets.find((v) => v > baseWindow);
    return higher ?? movingAveragePresets[0];
  };

  const cycleMovingAverageSetting = () => {
    if (movingAverageMode === "off") {
      setMovingAverageMode("auto");
      return;
    }

    if (movingAverageMode === "auto") {
      setMovingAverageMode("manual");
      setManualMovingAverageWindow(movingAveragePresets[0]);
      return;
    }

    const idx = movingAveragePresets.indexOf(manualMovingAverageWindow);
    if (idx === -1) {
      setManualMovingAverageWindow(movingAveragePresets[0]);
      return;
    }

    if (idx === movingAveragePresets.length - 1) {
      setMovingAverageMode("off");
      return;
    }

    setManualMovingAverageWindow(movingAveragePresets[idx + 1]);
  };

  const toggleTrackerSeries = (key) => {
    setTrackerSeriesVisible((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const trackerRealityItems = [
    { key: "actual", label: "Actual", color: TRACKER_PROGRESS_SERIES_STYLES.actual.color },
    { key: "movingAvg", label: "Trend", color: TRACKER_PROGRESS_SERIES_STYLES.movingAvg.color },
  ];

  const trackerOverlayItems = [
    { key: "expected", label: "Expected", color: TRACKER_PROGRESS_SERIES_STYLES.expected.color },
    { key: "projected", label: "Plan", color: TRACKER_PROGRESS_SERIES_STYLES.projected.color },
    { key: "originalPlan", label: "Original Plan", color: TRACKER_PROGRESS_SERIES_STYLES.originalPlan.color },
  ];

  const setChartsStartWithAutoMovingAverage = (nextStart) => {
    setMovingAverageMode("auto");
    setChartsStartDraft(nextStart);
  };

  const setChartsEndWithAutoMovingAverage = (nextEnd) => {
    setMovingAverageMode("auto");
    setChartsEndDraft(nextEnd);
  };

  const commitChartsStartDraft = () => {
    if (!entriesAsc.length) return;
    setMovingAverageMode("auto");
    commitChartRange(trendsStartDraft, trendsEndDraft || trendsEnd || todayYmd, "start");
  };

  const commitChartsEndDraft = () => {
    if (!entriesAsc.length) return;
    setMovingAverageMode("auto");
    commitChartRange(trendsStartDraft || trendsStart || entriesAsc[0]?.date || todayYmd, trendsEndDraft, "end");
  };

  const trendsCalorieChartData = useMemo(() => {
    if (!trendsChartData.length) return [];
    const byDate = new Map(entriesAsc.map((e) => [e.date, e]));
    return trendsChartData.map((d) => ({ date: d.date, netCalories: byDate.get(d.date)?.netCalories ?? null }));
  }, [trendsChartData, entriesAsc]);

  const trendsRangeWeights = useMemo(() => {
    if (!trendsChartData.length) return [];
    return trendsChartData.map((d) => ({ date: d.date, weight: d.actual ?? d.inferred ?? null }));
  }, [trendsChartData]);

  const trendsStartPoint = useMemo(() => {
    if (!trendsRangeWeights.length) return null;
    const first = trendsRangeWeights[0];
    if (first.weight !== null) return first;
    const fallback = trendsRangeWeights.find((p) => p.weight !== null);
    return fallback ? { date: first.date, weight: fallback.weight } : null;
  }, [trendsRangeWeights]);

  const trendsEndPoint = useMemo(() => {
    if (!trendsRangeWeights.length) return null;
    const last = trendsRangeWeights[trendsRangeWeights.length - 1];
    if (last.weight !== null) return last;
    for (let i = trendsRangeWeights.length - 1; i >= 0; i -= 1) {
      if (trendsRangeWeights[i].weight !== null) return { date: last.date, weight: trendsRangeWeights[i].weight };
    }
    return null;
  }, [trendsRangeWeights]);

  const trendsTotalDelta = useMemo(() => {
    if (!trendsStartPoint || !trendsEndPoint) return null;
    return trendsEndPoint.weight - trendsStartPoint.weight;
  }, [trendsStartPoint, trendsEndPoint]);

  const trendsWeeklyDelta = useMemo(() => {
    if (trendsTotalDelta === null || !trendsStartPoint || !trendsEndPoint) return null;
    const days = daysBetweenInclusiveStartExclusiveEnd(trendsStartPoint.date, trendsEndPoint.date);
    if (days === null || days <= 0) return null;
    return trendsTotalDelta / (days / 7);
  }, [trendsTotalDelta, trendsStartPoint, trendsEndPoint]);

  const trendsYTicks = useMemo(() => {
    if (!trendsChartData.length) return [];
    const vals = trendsChartData.map((d) => (d.actual ?? d.inferred ?? null)).filter((v) => v !== null);
    if (!vals.length) return [];
    const min = Math.floor(Math.min(...vals) / 5) * 5;
    const max = Math.ceil(Math.max(...vals) / 5) * 5;
    const ticks = [];
    for (let x = min; x <= max; x += 5) ticks.push(x);
    return ticks;
  }, [trendsChartData]);

  const trendsCalorieDomain = useMemo(() => {
    const vals = trendsCalorieChartData.map((d) => d.netCalories).filter((v) => v !== null);
    if (!vals.length) return [-500, 500];
    const absMax = Math.max(Math.abs(Math.min(...vals)), Math.abs(Math.max(...vals)));
    const rounded = Math.ceil(absMax / 500) * 500;
    return [-rounded, rounded];
  }, [trendsCalorieChartData]);

  const trendsMetricRowsBase = useMemo(() => {
    const byDate = new Map(entriesAsc.map((e) => [e.date, e]));
    return trendsChartData.map((d) => {
      const entry = byDate.get(d.date);
      const weightValue = d.actual ?? d.inferred ?? null;
      return {
        date: d.date,
        weight: weightValue,
        weightLogged: d.actual,
        caloriesIn: entry?.caloriesIn ?? null,
        activeCalories: entry?.activeCalories ?? null,
        netCalories: entry?.netCalories ?? null,
        tdee: entry?.totalBurned ?? null,
      };
    });
  }, [trendsChartData, entriesAsc]);

  const trendsMetricRows = useMemo(() => {
    if (trendsMetric === "weight") return trendsMetricRowsBase;

    const points = trendsMetricRowsBase.map((row) => {
      const v = row[trendsMetric];
      return {
        ...row,
        actual: Number.isFinite(v) ? v : null,
        inferred: null,
      };
    });
    const withAvg = addDailyMovingAverage(points, trendsMovingAverageWindow, todayYmd);
    return withAvg.map((row) => ({ ...row, movingAvgMetric: row.movingAvg }));
  }, [trendsMetric, trendsMetricRowsBase, trendsMovingAverageWindow, todayYmd]);

  const trendsMetricObservedPoints = useMemo(() => {
    if (trendsMetric === "weight") {
      return trendsMetricRowsBase
        .filter((r) => Number.isFinite(r.weightLogged))
        .map((r) => ({ date: r.date, value: r.weightLogged as number }));
    }
    return trendsMetricRowsBase
      .filter((r) => Number.isFinite(r[trendsMetric]))
      .map((r) => ({ date: r.date, value: r[trendsMetric] as number }));
  }, [trendsMetric, trendsMetricRowsBase]);

  const trendsMetricAverage = useMemo(() => {
    if (!trendsMetricObservedPoints.length) return null;
    const vals = trendsMetricObservedPoints.map((r) => r.value);
    return vals.reduce((sum, v) => sum + v, 0) / vals.length;
  }, [trendsMetricObservedPoints]);

  const trendsMetricMinMax = useMemo(() => {
    if (trendsMetricObservedPoints.length < 2) return { min: null, max: null };
    const vals = trendsMetricObservedPoints.map((r) => r.value);
    return { min: Math.min(...vals), max: Math.max(...vals) };
  }, [trendsMetricObservedPoints]);

  const trendsMetricRegression = useMemo(() => calcRegressionSlopePerDay(trendsMetricObservedPoints), [trendsMetricObservedPoints]);

  const trendsMetricTrendPerDay = useMemo(() => {
    if (!trendsMetricRegression || trendsMetricObservedPoints.length < 4) return null;
    const slope = trendsMetricRegression.slopePerDay;
    return Math.abs(slope) < 0.0001 ? 0 : slope;
  }, [trendsMetricRegression, trendsMetricObservedPoints.length]);

  const trendsMetricRowsWithTrendline = useMemo(() => {
    if (trendsMetric === "weight" || !trendsMetricRegression) return trendsMetricRows;
    return trendsMetricRows.map((row) => {
      const x = daysBetweenInclusiveStartExclusiveEnd(trendsMetricRegression.firstDate, row.date);
      if (x === null) return { ...row, trendlineMetric: null };
      return { ...row, trendlineMetric: trendsMetricRegression.intercept + trendsMetricRegression.slopePerDay * x };
    });
  }, [trendsMetric, trendsMetricRegression, trendsMetricRows]);


  const renderTrendsMetricTooltip = ({ active, payload, label }) => {
    if (!active || !label) return null;
    const row = payload?.find((entry) => entry?.payload)?.payload;
    if (!row) return null;

    return renderMetricTooltip(trendsMetric, row, label);
  };

  function renderMetricTooltip(metric: ChartMetric, row: any, label: string) {
    if (metric === "weight") {
      const value = row.weight;
      return (
        <div style={tooltipStyle}>
          <div style={{ fontWeight: 700, marginBottom: "0.3rem" }}>{formatMDY(label)}</div>
          <div>
            <span style={{ color: "rgba(255,255,255,0.72)" }}>Weight:</span>{" "}
            <span style={{ color: "rgba(255,255,255,0.95)", fontWeight: 700 }}>
              {typeof value === "number" ? `${value.toFixed(1)} lb` : "No log"}
            </span>
          </div>
        </div>
      );
    }

    const calorieRows: Array<{ key: TrackerCalorieMetric; label: string }> = [
      { key: "netCalories", label: "Net" },
      { key: "caloriesIn", label: "Calories In" },
      { key: "tdee", label: "TDEE" },
      { key: "activeCalories", label: "Active" },
    ];

    return (
      <div style={tooltipStyle}>
        <div style={{ fontWeight: 700, marginBottom: "0.3rem" }}>{formatMDY(label)}</div>
        <div style={{ display: "grid", gap: "0.18rem" }}>
          {calorieRows.map(({ key, label: rowLabel }) => {
            const value = row[key];
            const isSelected = metric === key;
            return (
              <div key={key}>
                <span style={{ color: isSelected ? "rgba(191,219,254,0.95)" : "rgba(255,255,255,0.72)", fontWeight: isSelected ? 700 : 500 }}>
                  {rowLabel}:
                </span>{" "}
                <span style={{ color: "rgba(255,255,255,0.95)", fontWeight: isSelected ? 800 : 700 }}>
                  {typeof value === "number" ? comma(value) : "No log"}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  function buildMetricStatChips({
    metric,
    metricAverage,
    metricMinMax,
    metricObservedPoints,
    metricTrendPerDay,
    startPoint,
    endPoint,
    totalDelta,
    weeklyDelta,
    startLabel,
    endLabel,
  }: {
    metric: ChartMetric;
    metricAverage: number | null;
    metricMinMax: { min: number | null; max: number | null };
    metricObservedPoints: Array<{ date: string; value: number }>;
    metricTrendPerDay: number | null;
    startPoint: { date: string; weight: number } | null;
    endPoint: { date: string; weight: number } | null;
    totalDelta: number | null;
    weeklyDelta: number | null;
    startLabel?: string | null;
    endLabel?: string | null;
  }) {
    const sign = (n: number, digits = 0) => `${n > 0 ? "+" : n < 0 ? "-" : ""}${Math.abs(n).toFixed(digits)}`;
    const avgNet = metricAverage !== null && Math.abs(metricAverage) < 0.0001 ? 0 : metricAverage;
    const totalNet = metric === "netCalories" ? metricObservedPoints.reduce((sum, p) => sum + p.value, 0) : null;
    const trendIcon = (slopePerDay: number | null) => {
      if (slopePerDay === null) return "→";
      const abs = Math.abs(slopePerDay);
      const flatThreshold = metric === "weight" ? 0.01 : metric === "netCalories" ? 2 : 3;
      if (abs < flatThreshold) return "→";
      return slopePerDay > 0 ? "↑" : "↓";
    };

    const trendChip = (label: string, text: string, slopePerDay: number | null) => (
      <ViewerStatChip key={`${metric}-${label}-trend`} label={label} value={`${trendIcon(slopePerDay)} ${text}`} tone="muted" />
    );

    const minMaxTitle = metric === "weight" ? "Weight range" : metric === "netCalories" ? "Net daily range" : metric === "caloriesIn" ? "Calories in range" : metric === "tdee" ? "TDEE range" : "Active calories range";
    const minMaxChip = metricMinMax.min !== null && metricMinMax.max !== null
      ? <MinMaxStatChip key={`${metric}-minmax`} title={minMaxTitle} min={metricMinMax.min} max={metricMinMax.max} unit={metric === "weight" ? "lbs" : "cal/day"} decimals={metric === "weight" ? 1 : 0} showSign={metric !== "weight"} />
      : null;

    if (metric === "weight") {
      const weightSlopePerDay = weeklyDelta !== null ? weeklyDelta / 7 : null;
      return [
        <ViewerStatChip key={`${metric}-start`} label="Start" value={startPoint ? `${startPoint.weight.toFixed(1)} lbs` : "—"} sub={startPoint ? formatMDY(startLabel ?? startPoint.date) : null} tone="muted" />,
        <ViewerStatChip key={`${metric}-end`} label="End" value={endPoint ? `${endPoint.weight.toFixed(1)} lbs` : "—"} sub={endPoint ? formatMDY(endLabel ?? endPoint.date) : null} tone="muted" />,
        <ViewerStatChip key={`${metric}-delta`} label="Delta" value={totalDelta === null ? "—" : `${sign(totalDelta, 1)} lbs`} tone="muted" />,
        weeklyDelta !== null ? trendChip("Trend", `${sign(weeklyDelta, 2)} lb/week`, weightSlopePerDay) : null,
        minMaxChip,
      ].filter(Boolean);
    }

    if (metric === "caloriesIn") {
      return [
        <ViewerStatChip key={`${metric}-avg`} label="Avg" value={metricAverage === null ? "—" : `${Math.round(metricAverage).toLocaleString()} cal/day`} tone="muted" />,
        metricTrendPerDay !== null ? trendChip("Trend", `${sign(metricTrendPerDay)} cal/day`, metricTrendPerDay) : null,
        minMaxChip,
      ].filter(Boolean);
    }

    if (metric === "netCalories") {
      return [
        <ViewerStatChip key={`${metric}-avg`} label="Avg net" value={avgNet === null ? "—" : `${Math.round(avgNet).toLocaleString()} cal/day`} tone="muted" />,
        metricTrendPerDay !== null ? trendChip("Trend", `${sign(metricTrendPerDay)} cal/day`, metricTrendPerDay) : null,
        minMaxChip,
        totalNet !== null && metricObservedPoints.length >= 2 ? <ViewerStatChip key={`${metric}-total`} label="Total net" value={`${Math.round(totalNet).toLocaleString()} cal`} sub={`${(totalNet / 3500).toFixed(1)} lb implied`} subSize="0.84rem" tone="muted" /> : null,
      ].filter(Boolean);
    }

    return [
      <ViewerStatChip key={`${metric}-avg`} label="Avg" value={metricAverage === null ? "—" : `${Math.round(metricAverage).toLocaleString()} cal/day`} tone="muted" />,
      metricTrendPerDay !== null ? trendChip("Trend", `${sign(metricTrendPerDay)} cal/day`, metricTrendPerDay) : null,
      minMaxChip,
    ].filter(Boolean);
  }

  const trendsDaysTrackedLabel = useMemo(() => {
    const logged = trendsMetricObservedPoints.length;
    const total = trendsMetricRowsBase.length;
    const pct = total > 0 ? Math.round((logged / total) * 100) : 0;
    return { logged, total, pct };
  }, [trendsMetricObservedPoints.length, trendsMetricRowsBase.length]);

  useEffect(() => {
    if (!trendsChartData.length) {
      setChartsBrushRange({ startIndex: 0, endIndex: 0 });
      return;
    }

    // Keep the chart visible by default whenever the source dataset changes.
    // (A collapsed 0..0 controlled brush can make the main lines appear missing.)
    const maxIndex = Math.max(0, trendsChartData.length - 1);
    setChartsBrushRange({ startIndex: 0, endIndex: maxIndex });
  }, [trendsChartData]);

  const trendsVisibleRange = useMemo(() => {
    if (!trendsChartData.length) return null;
    const maxIndex = Math.max(0, trendsChartData.length - 1);
    const startIndex = clamp(trendsBrushRange.startIndex ?? 0, 0, maxIndex);
    const endIndex = clamp(trendsBrushRange.endIndex ?? maxIndex, startIndex, maxIndex);
    return {
      startIndex,
      endIndex,
      startDate: trendsChartData[startIndex]?.date ?? trendsChartData[0].date,
      endDate: trendsChartData[endIndex]?.date ?? trendsChartData[maxIndex].date,
      totalDays: Math.max(1, endIndex - startIndex + 1),
    };
  }, [trendsChartData, trendsBrushRange]);

  const sprintColorById = useMemo(() => {
    const sorted = [...sprints].sort((a, b) => {
      const ak = `${a.createdAt ?? ""}|${a.startDate ?? ""}|${a.id ?? ""}`;
      const bk = `${b.createdAt ?? ""}|${b.startDate ?? ""}|${b.id ?? ""}`;
      return ak < bk ? -1 : ak > bk ? 1 : 0;
    });

    const usage = new Map(SPRINT_PALETTE.map((color) => [color, 0]));
    const map = new Map();

    sorted.forEach((s) => {
      if (s?.id && SPRINT_PALETTE.includes(s?.color)) {
        map.set(s.id, s.color);
        usage.set(s.color, (usage.get(s.color) ?? 0) + 1);
      }
    });

    sorted.forEach((s) => {
      if (!s?.id || map.has(s.id)) return;
      const minUsage = Math.min(...Array.from(usage.values()));
      const startIdx = hashString(s?.id ?? s?.name ?? "") % SPRINT_PALETTE.length;
      const rotated = [...SPRINT_PALETTE.slice(startIdx), ...SPRINT_PALETTE.slice(0, startIdx)];
      const picked = rotated.find((color) => (usage.get(color) ?? 0) === minUsage) ?? SPRINT_PALETTE[0];
      map.set(s.id, picked);
      usage.set(picked, (usage.get(picked) ?? 0) + 1);
    });

    return map;
  }, [sprints]);

  const getSprintColor = (sprint) => {
    if (!sprint?.id) return SPRINT_PALETTE[0];
    return sprintColorById.get(sprint.id) ?? SPRINT_PALETTE[0];
  };

  const sprintBandsForCharts = useMemo(() => {
    if (!trendsVisibleRange) return [];
    const todayStr = todayYmd;
    return sprints
      .map((s, idx) => {
        const rawStart = s.startDate;
        const rawEnd = s.endDate ?? todayStr;
        const startDate = rawStart > trendsVisibleRange.startDate ? rawStart : trendsVisibleRange.startDate;
        const endDate = rawEnd < trendsVisibleRange.endDate ? rawEnd : trendsVisibleRange.endDate;
        if (startDate > endDate) return null;
        const overlapDays = (daysBetweenInclusiveStartExclusiveEnd(startDate, addDaysYMD(endDate, 1)) ?? 0);
        const widthRatio = overlapDays / trendsVisibleRange.totalDays;
        return {
          sprint: s,
          x1: startDate,
          x2: endDate,
          color: getSprintColor(s),
          widthRatio,
          sortOrder: (s.priority ?? 0) * 100000 + idx,
        };
      })
      .filter(Boolean)
      .sort((a, b) => a.sortOrder - b.sortOrder);
  }, [sprints, trendsVisibleRange, todayYmd]);

  const getSprintsForDate = (dateYmd) => {
    if (!dateYmd) return [];
    return sprints
      .filter((s) => dateYmd >= s.startDate && dateYmd <= (s.endDate ?? todayYmd))
      .sort((a, b) => (b.priority ?? 0) - (a.priority ?? 0));
  };

  const trendsXAxisFormatter = useMemo(() => {
    const totalDays = trendsVisibleRange?.totalDays ?? trendsChartData.length;
    const mode = totalDays >= 730 ? "monthYear" : totalDays >= 365 ? "monthDayYearHints" : "monthDay";

    return (ymd) => {
      const dt = parseISODateYMD(ymd);
      if (!dt) return ymd;
      const m = dt.getMonth() + 1;
      const d = dt.getDate();
      const y2 = String(dt.getFullYear()).slice(-2);

      if (mode === "monthYear") return `${m}/${y2}`;
      if (mode === "monthDayYearHints") return m === 1 ? `${m}/${d}/${y2}` : `${m}/${d}`;
      return `${m}/${d}`;
    };
  }, [trendsVisibleRange?.totalDays, trendsChartData.length]);

  const trendsTotalDays = trendsVisibleRange?.totalDays ?? trendsChartData.length;
  const trendsMobileXAxisTicks = useMemo(
    () => buildAdaptiveDateTicks(trendsChartData.map((d) => d.date), getTrackerDateTickCount(trendsTotalDays, "mobile")),
    [trendsChartData, trendsTotalDays],
  );
  const trendsDesktopXAxisTicks = useMemo(
    () => buildAdaptiveDateTicks(trendsChartData.map((d) => d.date), getTrackerDateTickCount(trendsTotalDays, "desktop")),
    [trendsChartData, trendsTotalDays],
  );
  const trendsCalorieMobileXAxisTicks = useMemo(
    () => buildAdaptiveDateTicks(trendsMetricRows.map((d) => d.date), getTrackerDateTickCount(trendsTotalDays, "mobile")),
    [trendsMetricRows, trendsTotalDays],
  );
  const trendsCalorieDesktopXAxisTicks = useMemo(
    () => buildAdaptiveDateTicks(trendsMetricRows.map((d) => d.date), getTrackerDateTickCount(trendsTotalDays, "desktop")),
    [trendsMetricRows, trendsTotalDays],
  );

  const selectedChartSprint = useMemo(() => {
    if (!chartSprintActionId) return null;
    return sprints.find((s) => s.id === chartSprintActionId) ?? null;
  }, [chartSprintActionId, sprints]);

  // ---- Actions ----

  // -- Onboarding actions --

  const validateObBodyStats = () => {
    const errs = [];
    const ft = safeNumber(obVitals.ft);
    const ins = safeNumber(obVitals.ins) ?? 0;
    const h = ft !== null ? ft * 12 + ins : null;
    if (h === null || h < HEIGHT_MIN_IN || h > HEIGHT_MAX_IN) errs.push("Height must be between 3'0\" and 8'0\".");
    const age = calcAge(obVitals.birthday);
    if (age === null) errs.push("Enter a valid birthday.");
    else if (age < AGE_MIN) errs.push(`Age must be at least ${AGE_MIN}.`);
    else if (age > AGE_MAX) errs.push(`Age must be ${AGE_MAX} or under.`);
    return errs;
  };


  const validateObCurrentWeight = () => {
    const errs = [];
    const currentWeight = safeNumber(obVitals.currentWeightLbs);
    if (currentWeight === null || currentWeight <= 0 || currentWeight > 1400) errs.push("Current weight must be a valid positive number.");
    return errs;
  };
  const validateObImport = () => {
    return [];
  };

  const validateObGoal = () => {
    const errs = [];
    const sw = safeNumber(obSprint.startWeight);
    const tw = safeNumber(obSprint.targetWeight);
    if (sw === null || sw <= 0) errs.push("Starting weight is required.");
    if (tw === null || tw <= 0) errs.push("Target weight is required.");
    if (!isYmd(obSprint.startDate)) errs.push("Start date is required.");
    if (!isYmd(obSprint.targetDate)) errs.push("Target date is required.");
    else if (obSprint.targetDate <= obSprint.startDate) errs.push("Target date must be after the sprint start date.");
    if (sw !== null && tw !== null) {
      if (obSprint.goalType === "cut" && tw >= sw) errs.push("Cut mode requires target weight below starting weight.");
      if (obSprint.goalType === "maintain" && Math.abs(tw - sw) > 0.0001) errs.push("Maintain mode requires target weight equal to starting weight.");
      if (obSprint.goalType === "bulk" && tw <= sw) errs.push("Bulk mode requires target weight above starting weight.");
    }
    return errs;
  };

  const OB_PHASES: ObPhase[] = obSignedInWithAccount
    ? ["bodyStats", "currentWeight", "activity", "goal"]
    : ["bodyStats", "currentWeight", "activity", "import", "goal"];

  const obGoNext = () => {
    let errs: string[] = [];
    if (obPhase === "bodyStats") errs = validateObBodyStats();
    if (obPhase === "currentWeight") errs = validateObCurrentWeight();
    if (obPhase === "import") errs = validateObImport();
    if (obPhase === "goal") errs = validateObGoal();
    if (errs.length) { setObErrors(errs); return; }
    setObErrors([]);
    const idx = OB_PHASES.indexOf(obPhase);
    if (idx < OB_PHASES.length - 1) setObPhase(OB_PHASES[idx + 1]);
  };

  const obGoBack = () => {
    setObErrors([]);
    const idx = OB_PHASES.indexOf(obPhase);
    if (idx > 0) setObPhase(OB_PHASES[idx - 1]);
    else setShowLanding(true);
  };

  // Keep onboarding warnings dynamic: once shown, revalidate the active step as inputs change
  // so resolved errors disappear immediately (without requiring another Next click).
  useEffect(() => {
    if (obErrors.length === 0) return;
    let nextErrs: string[] = [];
    if (obPhase === "bodyStats") nextErrs = validateObBodyStats();
    if (obPhase === "currentWeight") nextErrs = validateObCurrentWeight();
    if (obPhase === "import") nextErrs = validateObImport();
    if (obPhase === "goal") nextErrs = validateObGoal();
    setObErrors(nextErrs);
  }, [
    obErrors.length,
    obPhase,
    obVitals.ft,
    obVitals.ins,
    obVitals.birthday,
    obVitals.currentWeightLbs,
    obSprint.startWeight,
    obSprint.targetWeight,
    obSprint.startDate,
    obSprint.targetDate,
    obSprint.goalType,
  ]);

  const commitObNetDraft = () => {
    const parsed = parseSignedDraftNumber(obSprint.netDraft);
    if (parsed === null) {
      setObSprint((s) => ({ ...s, netDraft: String(s.assumedNetCalories ?? "") }));
      return;
    }
    setObSprint((s) => ({ ...s, assumedNetCalories: parsed }));
  };

  const commitObIntakeDraft = () => {
    const parsed = parseSignedDraftNumber(obSprint.intakeDraft.replace(/-/g, ""));
    if (parsed === null) {
      setObSprint((s) => ({ ...s, intakeDraft: String(Math.max(0, s.assumedNetCalories ?? 0)) }));
      return;
    }
    setObSprint((s) => ({ ...s, assumedNetCalories: Math.max(0, parsed) }));
  };

  const handleObCsvUpload = async (file) => {
    setObCsvMsg(null);
    setObCsvRows(null);
    try {
      const text = await file.text();
      const parsed = importEntriesFromCsvText(text);
      if (!parsed.rows.length) {
        setObCsvMsg(parsed.errors.length ? parsed.errors[0] : "No valid rows found in the file.");
        return;
      }
      setObCsvRows(parsed.rows);
      const years = parsed.rows
        .map((r) => Number(String(r.date).slice(0, 4)))
        .filter((y) => Number.isFinite(y));
      const minYear = years.length ? Math.min(...years) : null;
      const maxYear = years.length ? Math.max(...years) : null;
      const yearRange = (minYear && maxYear) ? `${minYear}–${maxYear}` : "unknown range";
      const warnSuffix = parsed.errors.length ? ` (${parsed.errors.length} row${parsed.errors.length === 1 ? "" : "s"} skipped)` : "";
      setObCsvMsg(`Imported ${parsed.rows.length} entr${parsed.rows.length === 1 ? "y" : "ies"} (${yearRange}).${warnSuffix}`);
    } catch {
      setObCsvMsg("Failed to read file — please check the format and try again.");
    }
  };

  const applyImportedProfile = async (parsed, options = {}) => {
    const { nextVitals, nextEntries, nextSprints, nextForecast, nextRoadmap } = parsed;
    const reconciledEntries = reconcileEntriesDerivedMetrics(
      [...nextEntries].sort((a, b) => (a.date < b.date ? -1 : 1)),
      nextVitals,
    );
    setVitals(nextVitals);
    setEntries(reconciledEntries);
    setSprints(nextSprints);
    setForecast(nextForecast);
    setRoadmap(nextRoadmap ?? null);
    setObCsvRows(null);
    setObCsvMsg(null);
    if (options?.skipToTracker) setCurrentPage("tracker");
    await persistAll(reconciledEntries, nextVitals, nextForecast, nextSprints);
    return `Imported profile with ${reconciledEntries.length} entr${reconciledEntries.length === 1 ? "y" : "ies"}, ${nextSprints.length} sprint${nextSprints.length === 1 ? "" : "s"}, and ${nextRoadmap ? 1 : 0} roadmap${nextRoadmap ? "" : "s"}.`;
  };

  const handleObProfileUpload = async (file) => {
    setObErrors([]);
    setObProfileMsg(null);
    try {
      const text = await file.text();
      const parsedJson = JSON.parse(text);
      const parsed = importProfilePayload(parsedJson);
      if (parsed.error) {
        setObProfileMsg(parsed.error);
        return;
      }
      // Deterministic behavior: onboarding profile import REPLACES all local onboarding state.
      const success = await applyImportedProfile(parsed, { skipToTracker: true });
      setObProfileMsg(success);
    } catch {
      setObProfileMsg("Profile import failed: invalid JSON file.");
    }
  };

  const buildObEntryAndVitals = () => {
    const ft = safeNumber(obVitals.ft) ?? 0;
    const ins = safeNumber(obVitals.ins) ?? 0;
    const nextVitals = normalizeVitals({
      height: obVitals.ft !== "" ? ft * 12 + ins : DEFAULT_VITALS.height,
      gender: obVitals.gender,
      birthday: obVitals.birthday,
      activityLevel: getActivityOptionByFactor(obVitals.activityFactor).value,
      activityFactor: obVitals.activityFactor,
      currentWeightLbs: safeNumber(obVitals.currentWeightLbs),
      displayName: obName.trim(),
    });

    const todayWeight = safeNumber(obVitals.currentWeightLbs);
    if (todayWeight === null) return null;

    const map = new Map();
    if (obCsvRows && obCsvRows.length > 0) {
      for (const r of obCsvRows) {
        if (!r.weight || !isYmd(r.date)) continue;
        if (r.date === todayYmd) continue;
        const resolvedTdee = resolveTdeeForDate({
          entry: { totalBurned: r.totalBurned, activeCalories: r.activeCalories },
          date: r.date,
          bestKnownWeight: r.weight,
          activityLevelFactor: nextVitals.activityFactor,
          vitals: nextVitals,
        });
        const rowBmr = calcBMRAtDate({ weightLbs: r.weight, heightIn: nextVitals.height, gender: nextVitals.gender, birthday: nextVitals.birthday, onDateYmd: r.date });
        const rowNet = r.caloriesIn !== null ? (r.netCalories ?? (resolvedTdee.tdee !== null ? getNetCalories(r.caloriesIn, resolvedTdee.tdee) : null)) : null;
        map.set(r.date, { date: r.date, weight: r.weight, caloriesIn: r.caloriesIn, totalBurned: resolvedTdee.tdee, activeCalories: r.activeCalories, netCalories: rowNet, bmr: rowBmr, tdeeSource: resolvedTdee.source, theoreticalWeight: r.weight, updatedAt: new Date().toISOString() });
      }
    }

    const todayBmr = calcBMRAtDate({ weightLbs: todayWeight, heightIn: nextVitals.height, gender: nextVitals.gender, birthday: nextVitals.birthday, onDateYmd: todayYmd });
    map.set(todayYmd, {
      date: todayYmd,
      weight: todayWeight,
      caloriesIn: null,
      totalBurned: null,
      activeCalories: null,
      netCalories: null,
      bmr: todayBmr,
      theoreticalWeight: todayWeight,
      updatedAt: new Date().toISOString(),
    });

    const nextEntries = recalcTheoretical(Array.from(map.values()).sort((a, b) => (a.date < b.date ? -1 : 1)));
    return { nextVitals, nextEntries };
  };

  const completeOnboarding = async () => {
    const built = buildObEntryAndVitals();
    if (!built) return;
    const { nextVitals, nextEntries } = built;
    const resolvedNet = obSprint.inputMode === "net"
      ? parseSignedDraftNumber(obSprint.netDraft) ?? obSprint.assumedNetCalories ?? obSprintAutoCalc?.requiredNet ?? -500
      : Math.max(0, parseSignedDraftNumber(obSprint.intakeDraft.replace(/-/g, "")) ?? safeNumber(String(obSprint.assumedNetCalories ?? 0)) ?? 0);
    const sprintId = genId("sprint");
    const nextSprint = {
      id: sprintId,
      name: sanitizeSprintName(obSprint.name, "My first sprint"),
      startDate: obSprint.startDate,
      endDate: null,
      color: SPRINT_PALETTE.includes(obSprint.color) ? obSprint.color : pickNextSprintColor([]),
      forecastEndDate: isYmd(obSprint.targetDate) ? obSprint.targetDate : null,
      createdAt: todayYmd,
    };
    const nextForecast = {
      ...DEFAULT_FORECAST,
      activeSprintId: sprintId,
      sprintStartDate: obSprint.startDate,
      targetWeight: safeNumber(obSprint.targetWeight),
      targetDate: obSprint.targetDate,
      inputMode: obSprint.inputMode,
      assumedNetCalories: resolvedNet,
      activityFactor: safeNumber(String(obVitals.activityFactor)) ?? 1.3,
    };
    setVitals(nextVitals);
    setEntries(nextEntries);
    setSprints([nextSprint]);
    setForecast(nextForecast);
    setCurrentPage("tracker");
    await persistAll(nextEntries, nextVitals, nextForecast, [nextSprint]);
  };

  const handleFinishOnboarding = async () => {
    const errs = validateObGoal();
    const sprintErrs = obSprintGuardrails.hardErrors;
    const combined = [...errs, ...sprintErrs];
    if (combined.length) { setObErrors(combined); return; }
    setShowObPlanReveal(true);
  };

  // V2 onboarding completion. Maps the redesigned flow's collected data into
  // the existing vitals + first-entry + first-sprint + forecast shape and
  // persists it through the same path the legacy completion used.
  const completeOnboardingFromV2 = async (result: OnboardingResult, choice: AuthChoice) => {
    const { draft, dailyTargetCalories, sprintName, targetDateYmd } = result;
    const todayBmr = calcBMRAtDate({
      weightLbs: draft.currentWeightLbs,
      heightIn: draft.heightIn,
      gender: draft.sex ?? "X",
      birthday: draft.birthday,
      onDateYmd: todayYmd,
    });
    const nextVitals = normalizeVitals({
      ...DEFAULT_VITALS,
      height: draft.heightIn,
      gender: draft.sex ?? "X",
      birthday: draft.birthday,
      activityLevel: getActivityOptionByFactor(draft.activityFactor).value,
      activityFactor: draft.activityFactor,
      currentWeightLbs: draft.currentWeightLbs,
      displayName: "",
    });
    const nowIso = new Date().toISOString();
    const nextEntries = recalcTheoretical([
      {
        date: todayYmd,
        weight: draft.currentWeightLbs,
        caloriesIn: null,
        totalBurned: null,
        activeCalories: null,
        netCalories: null,
        bmr: todayBmr,
        theoreticalWeight: draft.currentWeightLbs,
        updatedAt: nowIso,
      },
    ]);
    const startDate = todayYmd;
    const sprintId = genId("sprint");
    const nextSprint = {
      id: sprintId,
      name: sanitizeSprintName(sprintName, "My first sprint"),
      startDate,
      endDate: null,
      color: pickNextSprintColor([]),
      forecastEndDate: isYmd(targetDateYmd) ? targetDateYmd : null,
      createdAt: startDate,
    };
    const intakeCalories = Math.max(0, Math.round(dailyTargetCalories));
    // `forecast.assumedNetCalories` is overloaded by `inputMode`:
    //   inputMode = "intake" → field holds the daily intake target (positive cal)
    //   inputMode = "net"    → field holds the net (intake − tdee, negative for cut)
    // The dashboard widget reads it via that same overload, so we have to
    // match the convention or the "Today's target" card explodes (intake gets
    // rendered as the deficit number, etc.). We pick "intake" because that's
    // what the user agreed to on the plan-reveal screen (the big kcal hero).
    const nextForecast = {
      ...DEFAULT_FORECAST,
      activeSprintId: sprintId,
      sprintStartDate: startDate,
      targetWeight: draft.targetWeightLbs,
      targetDate: isYmd(targetDateYmd) ? targetDateYmd : "",
      inputMode: "intake",
      assumedNetCalories: intakeCalories,
      activityFactor: draft.activityFactor,
    };
    // Always persist locally first — so the plan survives the OAuth redirect.
    await persistAll(nextEntries, nextVitals, nextForecast, [nextSprint]);

    // Stash a copy under a key that survives clearAnonymousLocalState, so the
    // plan crosses the SIGNED_IN boundary even when the account is empty in
    // the cloud. Hydration happens in the effect next to the post-sign-in
    // routing block. Skipped for "skip" (stays anonymous) and "already-authed"
    // (state is set immediately below and pushed via the normal path).
    if (choice.kind === "apple" || choice.kind === "google" || choice.kind === "magic") {
      await writeOnboardingHandoff({
        entries: nextEntries,
        vitals: nextVitals,
        sprints: [nextSprint],
        forecast: nextForecast,
        authMethod: choice.kind,
        email: choice.kind === "magic" ? choice.email : undefined,
        createdAt: Date.now(),
      });
    }

    // For OAuth (Apple/Google): do NOT mutate React state here. Setting entries
    // flips showOnboarding to false (entries.length === 0 gate) which would
    // flash the dashboard for a frame before the browser navigates to the
    // OAuth provider. Instead, leave the onboarding overlay rendering its own
    // "Signing you in…" interstitial. When the user returns authenticated, the
    // normal load path pulls the persisted plan from localStorage.
    if (choice.kind === "skip" || choice.kind === "magic" || choice.kind === "already-authed") {
      setVitals(nextVitals);
      setEntries(nextEntries);
      setSprints([nextSprint]);
      setForecast(nextForecast);
      setCurrentPage("tracker");
    }

    function populateStateForFallback(): void {
      setVitals(nextVitals);
      setEntries(nextEntries);
      setSprints([nextSprint]);
      setForecast(nextForecast);
      setCurrentPage("tracker");
    }

    try {
      if (choice.kind === "apple") {
        const result = await v2SignInWithApple();
        if (result?.error) {
          // OAuth init failed (e.g., Apple not configured in Supabase). Don't
          // strand the user on the auth screen — drop them into the dashboard.
          console.warn("[onboarding-v2] Apple sign-in error:", result.error.message);
          populateStateForFallback();
        }
        // On success the browser navigates away; SIGNED_IN handler will finish.
      } else if (choice.kind === "google") {
        const result = await v2SignInWithGoogle();
        if (result?.error) {
          console.warn("[onboarding-v2] Google sign-in error:", result.error.message);
          populateStateForFallback();
        }
      } else if (choice.kind === "magic") {
        const { error } = await v2SignInWithMagicLink(choice.email);
        if (error) {
          console.warn("[onboarding-v2] Magic link error:", error.message);
        }
      }
    } catch (err) {
      console.warn("[onboarding-v2] auth kickoff failed", err);
      populateStateForFallback();
    }
  };

  const handleOnboardingSkipSprint = async () => {
    // Save vitals + first entry only; user will create a sprint later via the normal flow.
    const built = buildObEntryAndVitals();
    if (!built) return;
    const { nextVitals, nextEntries } = built;
    setVitals(nextVitals);
    setEntries(nextEntries);
    setCurrentPage("tracker");
    await persistAll(nextEntries, nextVitals, forecast, sprints);
  };

  const upsertEntry = async ({ date, weight, caloriesIn, totalBurned, activeCalories, netCalories, bmr, tdeeSource, activeCaloriesSource = undefined, totalBurnedSource = undefined }) => {
    if (blockDemoWrite()) return;
    const nextMap = new Map(entriesAsc.map((e) => [e.date, e]));
    const prev = nextMap.get(date) ?? null;

    // Preserve existing weight when omitted (allows updating calories later)
    const nextWeight = weight ?? prev?.weight ?? null;

    // Source resolution: if caller provides explicit source, use it.
    // Otherwise, if user changed a value, mark it as 'manual'.
    // If value is unchanged, preserve existing source.
    const resolvedActiveSource: string | null =
      activeCaloriesSource !== undefined
        ? activeCaloriesSource
        : activeCalories !== null && activeCalories !== prev?.activeCalories
        ? "manual"
        : (prev?.activeCaloriesSource ?? null);

    const resolvedTotalSource: string | null =
      totalBurnedSource !== undefined
        ? totalBurnedSource
        : totalBurned !== null && totalBurned !== prev?.totalBurned
        ? "manual"
        : (prev?.totalBurnedSource ?? null);

    nextMap.set(date, {
      date,
      weight: nextWeight,
      caloriesIn,
      totalBurned,
      activeCalories,
      netCalories,
      bmr,
      tdeeSource: tdeeSource ?? prev?.tdeeSource ?? "unknown",
      theoreticalWeight: nextWeight,
      updatedAt: new Date().toISOString(),
      activeCaloriesSource: resolvedActiveSource,
      totalBurnedSource: resolvedTotalSource,
      // Always clear rejected reasons when user manually saves
      activeCaloriesRejectedReason: null,
      totalBurnedRejectedReason: null,
    });

    const reconciled = reconcileEntriesDerivedMetrics(
      Array.from(nextMap.values()).sort((a, b) => (a.date < b.date ? -1 : 1)),
      vitals,
    );
    const next = recalcTheoretical(reconciled);
    setEntries(next);
    await persistAll(next);
    // Write manual weight back to Apple Health (best-effort, lbs → kg)
    if (healthKitEnabled && typeof weight === "number") {
      void writeWeightToHealth(date, weight * 0.453592);
    }
  };

  const resetDailyEntryFields = (keepDate) => {
    setCurrentEntry((s) => ({ ...s, date: keepDate, weight: "", caloriesIn: "", totalBurned: "", activeCalories: "", totalBurnedSource: null, activeCaloriesSource: null }));
  };

  const loadCurrentEntryForDate = (date: string) => {
    const entry = entriesAsc.find((candidate) => candidate.date === date) ?? null;
    const nextInputMode = entry?.activeCalories != null ? "active" : "total";
    setTdeeInputMode(nextInputMode);
    setCurrentEntry({
      date,
      weight: "",
      caloriesIn: "",
      totalBurned: "",
      activeCalories: "",
      totalBurnedSource: null,
      activeCaloriesSource: null,
    });
    setDailyEntryFocusedField(null);
  };

  const openEntryEditor = (date: string) => {
    if (isArchivedOrViewer) return;
    const entry = entriesAsc.find((candidate) => candidate.date === date) ?? null;
    if (!entry) return;
    loadCurrentEntryForDate(entry.date);
    setActiveModal(null);
    setCurrentPage("tracker");
    setShowMobileLogModal(true);
    requestAnimationFrame(() => {
      if (dailyEntryCardRef.current && typeof dailyEntryCardRef.current.scrollIntoView === "function") {
        dailyEntryCardRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    });
  };

  const getPrevEntryBeforeDate = (dateYmd) => {
    for (let i = entriesAsc.length - 1; i >= 0; i -= 1) {
      const e = entriesAsc[i];
      if (e.date < dateYmd) return e;
    }
    return null;
  };

  const sanitizeEntryDate = (rawDate) => {
    const maxEntryDate = clampYmdToBounds(addDaysYMD(todayYmd, MAX_FUTURE_DAYS) ?? todayYmd, MIN_YMD, MAX_YMD);
    if (!isYmd(rawDate)) return { ok: false, date: currentEntry.date, message: "Entry date must be a valid date." };
    const nextDate = clampYmdToBounds(rawDate, MIN_YMD, maxEntryDate);
    if (nextDate !== rawDate) {
      return { ok: true, date: nextDate, message: "Entry date was clamped to safe limits." };
    }
    return { ok: true, date: nextDate, message: null };
  };

  const buildEntryWarnings = ({ date, weight }) => {
    const lines = [];
    if (typeof weight === "number") {
      if (weight < WEIGHT_MIN_WARN) lines.push(`Weight is very low (${weight}). Continue?`);
      if (weight > WEIGHT_MAX_WARN) lines.push(`Weight is very high (${weight}). Continue?`);
      const prev = getPrevEntryBeforeDate(date);
      if (prev && typeof prev.weight === "number") {
        const delta = Math.abs(weight - prev.weight);
        if (delta >= WEIGHT_DELTA_WARN_LBS) {
          lines.push(`Weight changed by ${delta.toFixed(1)} lb since last entry (from ${prev.weight.toFixed(1)} to ${weight.toFixed(1)}). Continue?`);
        }
      }
    }
    return lines;
  };

  const confirmIfNeeded = async (lines, action) => {
    if (!lines.length) {
      await action();
      return;
    }
    pendingConfirmActionRef.current = action;
    setConfirmLines(lines);
    setConfirmOpen(true);
  };

  const cancelConfirm = () => {
    setConfirmOpen(false);
    setConfirmLines([]);
    pendingConfirmActionRef.current = null;
  };

  const continueConfirm = async () => {
    const pendingAction = pendingConfirmActionRef.current;
    setConfirmOpen(false);
    setConfirmLines([]);
    pendingConfirmActionRef.current = null;
    await pendingAction?.();
  };

  const handleQuickLog = async () => {
    if (isArchivedOrViewer) return;
    const w = safeNumber(currentEntry.weight);
    if (w === null) return;
    const sanitizedDate = sanitizeEntryDate(currentEntry.date);
    if (!sanitizedDate.ok) {
      setCalcPlanError(sanitizedDate.message);
      return;
    }
    if (sanitizedDate.message) setCalcPlanNotice(sanitizedDate.message);
    const warnings = buildEntryWarnings({ date: sanitizedDate.date, weight: w });
    await confirmIfNeeded(warnings, async () => {
      await upsertEntry({ date: sanitizedDate.date, weight: w, caloriesIn: null, totalBurned: null, activeCalories: null, netCalories: null, bmr: null });
      resetDailyEntryFields(sanitizedDate.date);
      requestAnimationFrame(() => dailyEntryWeightRef.current?.focus());
    });
  };

  const handleAddOrUpdate = async () => {
    if (isArchivedOrViewer) return;

    const sanitizedDate = sanitizeEntryDate(currentEntry.date);
    if (!sanitizedDate.ok) {
      setCalcPlanError(sanitizedDate.message);
      return;
    }
    if (sanitizedDate.message) setCalcPlanNotice(sanitizedDate.message);
    const date = sanitizedDate.date;

    const existing = entriesAsc.find((e) => e.date === date) ?? null;
    const isUpdate = !!existing;

    // Allow "log weight now, add calories later": if weight isn't provided on update, keep existing weight.
    const w = safeNumber(currentEntry.weight) ?? existing?.weight ?? null;
    if (w === null) return;

    // For optional fields: empty input keeps existing value (so you can add calories later without wiping old ones).
    const calIn = currentEntry.caloriesIn === "" ? existing?.caloriesIn ?? null : safeNumber(currentEntry.caloriesIn);

    // BMR should be computed based on that entry's date (age-at-date)
    const bmr = calcBMRAtDate({ weightLbs: w, heightIn: vitals.height, gender: vitals.gender, birthday: vitals.birthday, onDateYmd: date });

    let active = null;
    if (tdeeInputMode === "active") {
      active = currentEntry.activeCalories === "" ? existing?.activeCalories ?? null : safeNumber(currentEntry.activeCalories);
      if (active !== null) active = Math.round(active);
    }

    const loggedTotalBurnedRaw = tdeeInputMode === "total"
      ? (currentEntry.totalBurned === "" ? existing?.totalBurned ?? null : safeNumber(currentEntry.totalBurned))
      : existing?.totalBurned ?? null;
    const loggedTotalBurned = loggedTotalBurnedRaw !== null ? Math.round(loggedTotalBurnedRaw) : null;

    const bestKnownWeight = getBestKnownWeightForDate({
      entriesAsc,
      date,
      sprintStartWeight: startWeight,
      vitalsCurrentWeight: vitals.currentWeightLbs,
    }) ?? w;

    const resolvedTdee = resolveTdeeForDate({
      entry: { totalBurned: loggedTotalBurned, activeCalories: active },
      date,
      bestKnownWeight,
      activityLevelFactor: vitals.activityFactor ?? forecast.activityFactor,
      vitals,
    });

    // Net calories only exists when calories-in was logged.
    const net = calIn !== null && resolvedTdee.tdee !== null ? getNetCalories(calIn, resolvedTdee.tdee) : null;

    const hasExistingBurnLog = (existing?.totalBurned != null || existing?.activeCalories != null);
    const hasTypedBurnInput = tdeeInputMode === "total"
      ? currentEntry.totalBurned.trim().length > 0
      : currentEntry.activeCalories.trim().length > 0;
    const shouldPersistBackupOnlyTdee = resolvedTdee.source === "fromActivityFactor" && !hasExistingBurnLog && !hasTypedBurnInput;
    const totalBurnedToSave = shouldPersistBackupOnlyTdee
      ? null
      : (resolvedTdee.tdee != null ? Math.round(resolvedTdee.tdee) : null);

    const warnings = buildEntryWarnings({ date, weight: w });
    await confirmIfNeeded(warnings, async () => {
      await upsertEntry({
        date,
        weight: w,
        caloriesIn: calIn,
        totalBurned: totalBurnedToSave,
        activeCalories: active,
        netCalories: net,
        bmr,
        tdeeSource: resolvedTdee.source,
        totalBurnedSource: totalBurnedToSave === null ? null : (currentEntry.totalBurnedSource ?? undefined),
        activeCaloriesSource: active === null ? null : (currentEntry.activeCaloriesSource ?? undefined),
      });
      resetDailyEntryFields(date);
      setSaveButtonFlash(true);
      setLogSuccessToast(isUpdate ? "Entry updated" : "Entry logged");
      setTimeout(() => {
        setShowMobileLogModal(false);
      }, 600);
      setTimeout(() => setSaveButtonFlash(false), 1200);
      setTimeout(() => setLogSuccessToast(null), 2400);
      requestAnimationFrame(() => dailyEntryWeightRef.current?.focus());
    });
  };

  const deleteEntry = async (date) => {
    const next = entriesAsc.filter((e) => e.date !== date);
    const recalced = recalcTheoretical(next.sort((a, b) => (a.date < b.date ? -1 : 1)));
    setEntries(recalced);
    // Record tombstone so offline deletion survives sync
    const deletedAt = new Date().toISOString();
    const nextTombstones = { ...deletedEntryTombstones, [date]: deletedAt };
    setDeletedEntryTombstones(nextTombstones);
    await Promise.all([
      persistAll(recalced),
      storage.set(STORAGE_KEYS.deletedEntries, JSON.stringify(nextTombstones)),
    ]);
  };

  const resetAll = async () => {
    if (blockDemoWrite("Disabled in Demo")) return;
    const ok = window.confirm("Factory reset? This removes all local data. This cannot be undone unless you have a backup.");
    if (!ok) return;
    try {
      await storage.delete(STORAGE_KEYS.entries);
      await storage.delete(STORAGE_KEYS.vitals);
      await storage.delete(STORAGE_KEYS.forecast);
      await storage.delete(STORAGE_KEYS.sprints);
      await storage.delete(STORAGE_KEYS.roadmap);
      await storage.delete(STORAGE_KEYS.roadmapUpdatedAt);
      await storage.delete(STORAGE_KEYS.deletedEntries);
      await storage.delete(STORAGE_KEYS.hasSynced);
    } catch {
      // ignore
    }
    setEntries([]);
    setVitals(DEFAULT_VITALS);
    setForecast(DEFAULT_FORECAST);
    setSprints([]);
    setRoadmap(null);
    setRoadmapUpdatedAt(null);
    setShowLanding(true);
  };

  const applySprintStartDate = async (newStart) => {
    if (blockDemoWrite()) return;
    if (!activeSprint) return;
    const nextSprint = { ...activeSprint, startDate: newStart };
    const nextSprints = sprints.map((s) => (s.id === activeSprint.id ? nextSprint : s));
    const nextForecast = { ...forecast, sprintStartDate: newStart, activeSprintId: activeSprint.id };
    setSprints(nextSprints);
    setForecast(nextForecast);
    await persistAll(entriesAsc, vitals, nextForecast, nextSprints);
  };

  const applySprintForecastEndDate = async (newEnd) => {
    if (blockDemoWrite()) return;
    if (!activeSprint) return;
    if (newEnd && !isYmd(newEnd)) return;

    const nextSprint = { ...activeSprint, forecastEndDate: newEnd || null };
    const nextSprints = sprints.map((s) => (s.id === activeSprint.id ? nextSprint : s));
    setSprints(nextSprints);
    await persistAll(entriesAsc, vitals, forecast, nextSprints);
  };

  const calcCaloriesToMeetGoal = () => {
    setCalcPlanError(null);

    if (calcPlanDisabledReason) {
      setCalcPlanNotice(calcPlanDisabledReason);
      return;
    }

    const currentWeight = weightForPlanCalc;
    if (currentWeight === null || currentWeight === undefined) {
      setCalcPlanError("Missing sprint anchor weight. Add a sprint-day weigh-in and try again.");
      return;
    }

    const anchorDate = sprintAnchor.anchorDate;
    const targetWeight = safeNumber(sprintDraft?.targetWeight ?? forecast.targetWeight);
    const targetDate = forecast.targetDate;
    if (!isYmd(anchorDate) || targetWeight === null || !isYmd(targetDate)) {
      setCalcPlanError("Set a valid anchor date, target weight, and target date to calculate.");
      return;
    }

    const days = daysBetweenInclusiveStartExclusiveEnd(anchorDate, targetDate);
    if (!days || days <= 0) {
      setCalcPlanError("Target date must be after the sprint anchor date.");
      return;
    }

    const toLose = currentWeight - targetWeight;
    const totalDef = toLose * 3500;
    const requiredNet = -(totalDef / days);

    if (forecast.inputMode === "intake") {
      const bmr = calcBMR({ weightLbs: currentWeight, heightIn: vitals.height, gender: vitals.gender, birthday: vitals.birthday });
      if (bmr === null) {
        setCalcPlanError("Unable to calculate intake. Check vitals (height, gender, birthday).");
        return;
      }
      const tdee = calcTDEEFromFactor({ bmr, factor: vitals.activityFactor ?? forecast.activityFactor });
      if (tdee === null) {
        setCalcPlanError("Unable to calculate intake. Check activity factor.");
        return;
      }
      const intake = Math.max(0, Math.round(requiredNet + tdee));
      setForecast((f) => ({ ...f, assumedNetCalories: intake }));
      return;
    }

    setForecast((f) => ({ ...f, assumedNetCalories: Math.round(requiredNet) }));
  };

  const performArchiveSprint = async (sprintId, proposedEndDate) => {
    if (blockDemoWrite()) return { ok: false };
    const target = sprints.find((s) => s.id === sprintId);
    if (!target) return { ok: false };
    const archiveEndDate = isYmd(proposedEndDate) ? proposedEndDate : todayYmd;
    const nextSprints = sprints.map((s) => (
      s.id === sprintId
        ? { ...s, endDate: archiveEndDate }
        : s
    ));
    const wasActive = forecast.activeSprintId === sprintId || isActiveSprint(target);
    const nextForecast = wasActive
      ? { ...forecast, activeSprintId: null, sprintStartDate: null }
      : forecast;
    setSprints(nextSprints);
    if (nextForecast !== forecast) setForecast(nextForecast);
    await persistAll(entriesAsc, vitals, nextForecast, nextSprints);
    return { ok: true, nextSprints, nextForecast };
  };

  const openArchiveConfirm = (sprintId, afterArchive = null) => {
    const sprint = sprints.find((s) => s.id === sprintId);
    if (!sprint) return;
    const proposedEndDate = proposeArchiveEndDate(sprint);
    const stats = buildArchiveStats(sprint, proposedEndDate);
    setArchiveConfirm({ sprintId, proposedEndDate, afterArchive, stats });
  };

  const requestArchiveFirst = (active, action) => {
    if (!active) return;
    setPendingSprintAction(action);
    setArchiveFirstPrompt({ activeSprintId: active.id, activeSprintName: active.name || "Sprint" });
  };

  const runPendingSprintAction = async (actionOverride = null, sprintsOverride = null, forecastOverride = null) => {
    const action = actionOverride ?? pendingSprintAction;
    if (!action) return;
    const sourceSprints = sprintsOverride ?? sprints;
    const sourceForecast = forecastOverride ?? forecast;
    if (action.type === "createActive") {
      // Keep sprint creator workflow intact after archiving active sprint.
      // Re-open the existing creator draft so the user can finish with preserved inputs.
      setPendingSprintAction(null);
      setActiveModal("newSprint");
      return;
    }
    if (action.type === "setActive") {
      const sprintId = action.payload?.targetSprintId;
      const target = sourceSprints.find((sp) => sp.id === sprintId);
      if (!target) {
        setPendingSprintAction(null);
        return;
      }
      const nextSprints = sourceSprints.map((sp) => (sp.id === sprintId ? { ...sp, endDate: null } : sp));
      const nextForecast = { ...sourceForecast, activeSprintId: sprintId, sprintStartDate: target.startDate };
      setSprints(nextSprints);
      setForecast(nextForecast);
      setPendingSprintAction(null);
      setCurrentPage("tracker");
      await persistAll(entriesAsc, vitals, nextForecast, nextSprints);
    }
  };

  const archiveActiveSprint = async () => {
    if (!activeSprint) return;
    openArchiveConfirm(activeSprint.id);
  };

  const confirmArchiveSprint = async () => {
    if (!archiveConfirm) return;
    const afterArchive = archiveConfirm.afterArchive; // capture before clearing
    const result = await performArchiveSprint(archiveConfirm.sprintId, archiveConfirm.proposedEndDate);
    setArchiveConfirm(null);
    if (!result?.ok) {
      cancelEditSprint();
      return;
    }
    if (afterArchive?.type === "resumePending") {
      // Only reset the edit-sprint-specific state; do NOT close activeModal so the new sprint
      // creator draft is preserved and the modal doesn't flash closed before reopening.
      setEditingSprintId(null);
      setEditingSprintDraft({ name: "", startDate: "", endDate: "", color: SPRINT_PALETTE[0] });
      await runPendingSprintAction(pendingSprintAction, result.nextSprints, result.nextForecast);
    } else {
      cancelEditSprint();
    }
  };

  const resolveForecastWithDraftCalories = () => {
    const netParsed = forecast.inputMode === "net" ? parseSignedDraftNumber(forecastNetDraft) : null;
    const intakeParsed = forecast.inputMode === "intake" ? parseSignedDraftNumber(forecastIntakeDraft.replace(/-/g, "")) : null;

    if (forecast.inputMode === "net" && netParsed !== null) {
      return { ...forecast, assumedNetCalories: netParsed };
    }

    if (forecast.inputMode === "intake" && intakeParsed !== null) {
      return { ...forecast, assumedNetCalories: Math.max(0, intakeParsed) };
    }

    return forecast;
  };

  const startSprintFromSettings = async () => {
    if (blockDemoWrite()) return;
    if (activeSprint) return;
    const startDate = sprintStartDraft.trim();
    if (!isYmd(startDate)) {
      window.alert("Sprint start date is required and must be in YYYY-MM-DD format.");
      return;
    }

    const nextForecastBase = resolveForecastWithDraftCalories();
    const nextName = sanitizeSprintName((archiveName || "").trim(), "Sprint");
    const duplicateSprint = findSprintNameDuplicate(nextName);
    if (duplicateSprint) {
      window.alert(`A sprint named "${duplicateSprint.name}" already exists. Please choose a different name.`);
      return;
    }
    const nextSprint = {
      id: genId("sprint"),
      name: nextName,
      startDate,
      endDate: null,
      color: pickNextSprintColor(sprints),
      forecastEndDate: null,
      createdAt: todayYmd,
    };
    const nextSprints = [...sprints, nextSprint];
    const nextForecast = {
      ...nextForecastBase,
      activeSprintId: nextSprint.id,
      sprintStartDate: startDate,
    };

    setSprints(nextSprints);
    setForecast(nextForecast);
    setCurrentPage("tracker");
    setActiveModal(null);
    await persistAll(entriesAsc, vitals, nextForecast, nextSprints);
  };

  const deleteSprint = async (id) => {
    if (blockDemoWrite()) return;
    const sprint = sprints.find((s) => s.id === id);
    if (!sprint) return;

    const next = sprints.filter((s) => s.id !== id);
    let nextForecast = forecast;

    if (forecast.activeSprintId === id) {
      // if we deleted active sprint, clear pointer so the ensure-effect recreates an active sprint
      nextForecast = { ...forecast, activeSprintId: null };
      setForecast(nextForecast);
    }

    setSprints(next);
    if (currentPage === "viewer" && viewerSprintId === id) {
      setViewerSprintId(null);
      setCurrentPage("sprints");
    }

    await persistAll(entriesAsc, vitals, nextForecast, next);
    if (userId) {
      await sbDeleteSprint(id);
    }
  };

  const openSprintDeleteConfirm = (sprintId) => {
    const sprint = sprints.find((item) => item.id === sprintId);
    if (!sprint) return;
    setSprintDeleteConfirm({
      sprintId: sprint.id,
      sprintName: sprint.name,
      isActive: forecast.activeSprintId === sprint.id,
    });
  };

  const confirmDeleteSprint = async () => {
    if (!sprintDeleteConfirm) return;
    const sprintId = sprintDeleteConfirm.sprintId;
    setSprintDeleteConfirm(null);
    await deleteSprint(sprintId);
  };

  const openSprintViewer = (sprintId) => {
    if (!sprintId) return;
    const sprint = sprints.find((item) => item.id === sprintId);
    if (sprint && isTrulyActiveSprint(sprint)) {
      setViewerSprintId(null);
      setCurrentPage("tracker");
      return;
    }
    setViewerSprintId(sprintId);
    setCurrentPage("viewer");
  };

  const openSprintChartActions = (sprintId) => {
    if (!sprintId) return;
    setChartSprintActionId(sprintId);
  };

  const zoomChartsToSprint = (sprintId) => {
    const sprint = sprints.find((s) => s.id === sprintId);
    if (!sprint) return;
    setMovingAverageMode("auto");
    commitChartRange(sprint.startDate, sprint.endDate ?? todayYmd);
    setChartSprintActionId(null);
  };

  const openSprintViewerFromModal = (sprintId) => {
    setChartSprintActionId(null);
    openSprintViewer(sprintId);
  };

  const startEditSprint = (sprint) => {
    if (!sprint) return;
    if (isTrulyActiveSprint(sprint)) {
      window.alert("Active sprint can't be edited here.");
      return;
    }
    setEditingSprintId(sprint.id);
    setEditingSprintDraft({
      name: sprint.name ?? "",
      startDate: sprint.startDate ?? todayYmd,
      endDate: getSprintDisplayEndDate(sprint, { treatAsActive: isTrulyActiveSprint(sprint) }) ?? "",
      color: getSprintColor(sprint),
    });
  };

  const cancelEditSprint = () => {
    setEditingSprintId(null);
    setEditingSprintDraft({ name: "", startDate: "", endDate: "", color: SPRINT_PALETTE[0] });
    setActiveModal(null);
  };

  const sanitizeSprintDates = ({ startDate, endDate = null, allowBlankEnd = true, maxEndDateYmd = null }) => {
    if (!isYmd(startDate)) return { ok: false, message: "Sprint start date must be a valid YYYY-MM-DD date." };

    const clampedStart = clampYmdToBounds(startDate, MIN_YMD, MAX_YMD);
    if (clampedStart !== startDate) {
      return { ok: false, message: `Sprint start date must be between ${MIN_YMD} and ${MAX_YMD}.` };
    }

    let nextEnd = endDate;
    if (nextEnd === "") nextEnd = null;

    if (nextEnd === null) {
      if (!allowBlankEnd) return { ok: false, message: "Sprint end date is required." };
      const maxAllowedEnd = clampYmdToBounds(addDaysYMD(todayYmd, MAX_SPRINT_SPAN_DAYS) ?? todayYmd, clampedStart, MAX_YMD);
      const maxSpanEnd = clampYmdToBounds(addDaysYMD(clampedStart, MAX_SPRINT_SPAN_DAYS) ?? clampedStart, clampedStart, maxAllowedEnd);
      return { ok: true, startDate: clampedStart, endDate: null, maxSpanEnd };
    }

    if (!isYmd(nextEnd)) return { ok: false, message: "Sprint end date must be blank or a valid YYYY-MM-DD date." };
    let clampedEnd = clampYmdToBounds(nextEnd, clampedStart, MAX_YMD);

    const computedMaxEnd = isYmd(maxEndDateYmd)
      ? clampYmdToBounds(maxEndDateYmd, clampedStart, MAX_YMD)
      : clampYmdToBounds(addDaysYMD(todayYmd, MAX_SPRINT_SPAN_DAYS) ?? todayYmd, clampedStart, MAX_YMD);
    if (clampedEnd > computedMaxEnd) {
      clampedEnd = computedMaxEnd;
      return {
        ok: true,
        startDate: clampedStart,
        endDate: clampedEnd,
        maxSpanEnd: clampYmdToBounds(addDaysYMD(clampedStart, MAX_SPRINT_SPAN_DAYS) ?? clampedStart, clampedStart, MAX_YMD),
        message: `Sprint end date cannot be later than ${computedMaxEnd}.`,
      };
    }

    const maxSpanEnd = clampYmdToBounds(addDaysYMD(clampedStart, MAX_SPRINT_SPAN_DAYS) ?? clampedStart, clampedStart, MAX_YMD);
    if (clampedEnd > maxSpanEnd) {
      clampedEnd = maxSpanEnd;
      return {
        ok: true,
        startDate: clampedStart,
        endDate: clampedEnd,
        maxSpanEnd,
        message: `Sprint duration capped to ${MAX_SPRINT_SPAN_DAYS} days.`,
      };
    }

    return { ok: true, startDate: clampedStart, endDate: clampedEnd, maxSpanEnd };
  };

  const saveEditedSprint = async (sprintId) => {
    if (blockDemoWrite()) return;
    const name = sanitizeSprintName(editingSprintDraft.name);
    const startDate = editingSprintDraft.startDate;
    const endDate = editingSprintDraft.endDate.trim() === "" ? null : editingSprintDraft.endDate.trim();
    const color = SPRINT_PALETTE.includes(editingSprintDraft.color) ? editingSprintDraft.color : pickNextSprintColor(sprints);

    if (!name) {
      window.alert("Sprint name is required.");
      return;
    }

    const duplicateSprint = findSprintNameDuplicate(name, sprintId);
    if (duplicateSprint) {
      window.alert(`A sprint named "${duplicateSprint.name}" already exists. Please choose a different name.`);
      return;
    }

    const sanitized = sanitizeSprintDates({ startDate, endDate, allowBlankEnd: false, maxEndDateYmd: todayYmd });
    if (!sanitized.ok) {
      window.alert(sanitized.message);
      return;
    }

    if (sanitized.message) setCalcPlanNotice(sanitized.message);

    // Check if this sprint is linked to a roadmap phase — if so, propagate edits back to phase
    const editingSprint = sprints.find((s) => s.id === sprintId);
    if (!editingSprint) return;
    if (isTrulyActiveSprint(editingSprint)) {
      window.alert("Active sprint can't be edited here.");
      cancelEditSprint();
      return;
    }
    if (editingSprint?.roadmapId && editingSprint?.phaseId && roadmap && roadmap.id === editingSprint.roadmapId) {
      // Build patch from changed dates
      const phasePatch: Partial<Pick<RoadmapPhase, "endDate" | "startDate">> = {};
      if (sanitized.startDate) phasePatch.startDate = sanitized.startDate;
      if (sanitized.endDate)   phasePatch.endDate   = sanitized.endDate;
      // applyPhaseEdit syncs linked sprint dates via setSprints; capture its result
      // so we can layer name/color onto the date-updated sprints (otherwise the
      // stale `sprints` closure here would clobber the date change).
      const phaseResult = Object.keys(phasePatch).length > 0
        ? await applyPhaseEdit(editingSprint.phaseId, phasePatch)
        : null;
      const baseSprints = phaseResult?.nextSprints ?? sprints;
      const nextSprints = baseSprints.map((s) =>
        s.id === sprintId ? { ...s, name, color, updatedAt: new Date().toISOString() } : s
      );
      setSprints(nextSprints);
      cancelEditSprint();
      await persistAll(entriesAsc, vitals, forecast, nextSprints);
      return;
    }

    // Regular (unlinked) sprint edit
    const nextSprints = sprints.map((s) => (
      s.id === sprintId ? { ...s, name, startDate: sanitized.startDate, endDate: sanitized.endDate, color } : s
    ));

    let nextForecast = forecast;
    if (forecast.activeSprintId === sprintId) {
      nextForecast = sanitized.endDate === null
        ? { ...forecast, sprintStartDate: sanitized.startDate }
        : { ...forecast, activeSprintId: null };
      setForecast(nextForecast);
    }

    setSprints(nextSprints);
    cancelEditSprint();
    await persistAll(entriesAsc, vitals, nextForecast, nextSprints);
  };


  const createSprint = async (allowRecommendedPaceOverride = false, bypassActiveGuard = false, bypassCalFloor = false, forcedNetOverride: number | null = null, forcedTargetDateOverride: string | null = null) => {
    if (blockDemoWrite()) return;
    const name = sanitizeSprintName(newSprintName, "Sprint");
    const startDate = newSprintStartDate;

    if (!name) {
      window.alert("Sprint name is required.");
      return;
    }

    const duplicateSprint = findSprintNameDuplicate(name);
    if (duplicateSprint) {
      window.alert(`A sprint named "${duplicateSprint.name}" already exists. Please choose a different name.`);
      return;
    }

    const isNewSprint = newSprintType === "new";
    let resolvedNewSprintNet = forcedNetOverride !== null
      ? forcedNetOverride
      : newSprintInputMode === "net"
      ? parseSignedDraftNumber(newSprintNetDraft) ?? safeNumber(newSprintAssumedNetCalories) ?? 0
      : Math.max(0, parseSignedDraftNumber(newSprintIntakeDraft.replace(/-/g, "")) ?? safeNumber(newSprintAssumedNetCalories) ?? 0);
    const startingWeight = safeNumber(newSprintStartingWeight);
    const paceHardErrors = newSprintGuardrails.hardErrors.filter((err) => isPaceHardError(err));
    if (isNewSprint && createSprintBlockingErrors.length > 0) {
      window.alert(createSprintBlockingErrors[0]);
      return;
    }
    if (isNewSprint && paceHardErrors.length > 0 && !allowRecommendedPaceOverride) {
      const reviewTargetDateYmd = isYmd(forcedTargetDateOverride) ? forcedTargetDateOverride : newSprintTargetDate;
      const reviewFloor = getCalorieWarningFloor(vitals.gender);
      const reviewIntake = newSprintAutoCalc?.tdee !== null && newSprintAutoCalc?.tdee !== undefined
        ? Math.round(resolvedNewSprintNet + newSprintAutoCalc.tdee)
        : null;
      const reviewBelowFloor = reviewIntake !== null && reviewIntake < reviewFloor;
      setCreateSprintWarningConfirm({
        summary: {
          startDate: formatMD(newSprintStartDate),
          startWeight: safeNumber(newSprintStartingWeight) !== null ? `${safeNumber(newSprintStartingWeight)} lbs` : "—",
          targetDate: formatMD(reviewTargetDateYmd),
          targetDateYmd: reviewTargetDateYmd,
          targetWeight: safeNumber(newSprintTargetWeight) !== null ? `${safeNumber(newSprintTargetWeight)} lbs` : "—",
          pace: `${((Math.abs(resolvedNewSprintNet) * 7) / 3500).toFixed(1)} lb/week`,
          net: resolvedNewSprintNet,
          intake: reviewIntake,
          belowFloor: reviewBelowFloor,
          floor: reviewFloor,
          overrideUsed: bypassCalFloor && reviewBelowFloor,
          adjustedUsed: bypassCalFloor,
        },
        bypassCalFloor,
      });
      return;
    }
    // Calorie floor check — show confirmation only when the raw plan is below the sex-specific
    // minimum AND faster than the pace cap. The pace cap is the hard limit; the calorie floor is
    // a soft "Low intake" warning (see 1383490). A plan at or under -2.0 lb/week is legitimate
    // even when it lands below 1500/1200 cal, so it must not be routed here — the modal would
    // otherwise offer "Recommended (floor)" as its only option and silently slow the plan.
    // This mirrors shouldRouteEditCalFloor in the sprint editor so create and edit agree.
    let floorSafeAdjustedNet: number | null = null;
    let floorLowestAllowableNet: number | null = null;
    if (isNewSprint && newSprintAutoCalc) {
      // Compute the "lowest allowable" plan — intake at the 2lb/wk (or 1lb/wk bulk) pace cap.
      // This mirrors recalibrate's "Use fastest safe plan" logic.
      const _goalType = deriveSprintGoalType(safeNumber(newSprintStartingWeight), safeNumber(newSprintTargetWeight));
      const _paceLimit = _goalType === "cut"
        ? SPRINT_PACE_LIMITS.cutMaxLossLbsPerWeek
        : _goalType === "bulk"
          ? SPRINT_PACE_LIMITS.bulkMaxGainLbsPerWeek
          : SPRINT_PACE_LIMITS.maintainMaxAbsLbsPerWeek;
      const _paceSign = _goalType === "cut" ? -1 : _goalType === "bulk" ? 1 : 0;
      const _lowestAllowableNet = _paceSign !== 0 ? Math.round(_paceSign * (_paceLimit * 3500 / 7)) : 0;
      const _lowestAllowableIntake = newSprintAutoCalc.tdee !== null
        ? Math.round(newSprintAutoCalc.tdee + _lowestAllowableNet)
        : null;
      const _boundedPlan = resolveBoundedPlanCalories({ requiredNet: newSprintAutoCalc.requiredNet, tdee: newSprintAutoCalc.tdee, gender: vitals.gender });
      const _rawIntake = newSprintAutoCalc.suggestedIntake;
      // Pace of the plan as actually stored (rounded net), matching the editor's draftPaceAbs.
      const _rawPaceAbs = Math.abs((newSprintAutoCalc.requiredNet * 7) / 3500);
      const _exceedsPaceCap = _goalType === "cut" && _rawPaceAbs > _paceLimit;
      if (_rawIntake !== null && _boundedPlan.belowFloor && _exceedsPaceCap && newSprintAutoCalc.tdee !== null) {
        // "Recommended floor" plan: intake at the gender safety floor.
        const _floorIntake = _boundedPlan.floor;
        const _floorNet = _floorIntake - newSprintAutoCalc.tdee;
        floorSafeAdjustedNet = _floorNet;
        floorLowestAllowableNet = _goalType === "cut" ? _lowestAllowableNet : null;
        // Offer the pace-cap plan whenever it is a genuinely different plan from the floor plan.
        // Do NOT gate it on the pace plan being the leaner of the two: which option has fewer
        // calories flips with TDEE. Below ~2500 TDEE the pace plan eats less and moves faster;
        // above it the pace plan eats MORE and moves slower, and hiding it there left the user
        // with only the harsher choice. Both are valid — let them pick.
        const _showOverride = _goalType === "cut"
          && _lowestAllowableIntake !== null
          && _rawIntake < _lowestAllowableIntake
          && _lowestAllowableIntake !== _floorIntake;
        if (!bypassCalFloor) {
          // Default to whichever option honours BOTH guardrails. The floor plan can itself
          // exceed the pace cap (high TDEE), in which case the pace plan is the safer default.
          const _floorPlanWithinPaceCap = Math.abs((_floorNet * 7) / 3500) <= _paceLimit;
          setCalFloorChoice(_floorPlanWithinPaceCap || !_showOverride ? "recommended" : "fastest");
          setCalFloorEditContext(null);
          setCalFloorConfirmData({
            rawIntake: Math.round(_rawIntake),
            floorIntake: _floorIntake,
            safeAdjustedNet: floorSafeAdjustedNet,
            lowestAllowableIntake: _showOverride ? _lowestAllowableIntake : null,
            lowestAllowableNet: _showOverride ? _lowestAllowableNet : null,
          });
          return;
        }
      }
    }
    if (floorLowestAllowableNet !== null) {
      resolvedNewSprintNet = Math.max(resolvedNewSprintNet, floorLowestAllowableNet);
    }
    if (isNewSprint && (startingWeight === null || startingWeight <= 0)) {
      window.alert("Starting weight is required for New Sprint.");
      return;
    }
    const sprintDateCheck = sanitizeSprintDates({
      startDate,
      endDate: isNewSprint ? null : newSprintEndDate.trim(),
      allowBlankEnd: isNewSprint,
    });
    if (!sprintDateCheck.ok) {
      window.alert(sprintDateCheck.message);
      return;
    }

    let sanitizedTargetDate = "";
    if (isNewSprint) {
      const requestedTargetDate = isYmd(forcedTargetDateOverride) ? forcedTargetDateOverride : newSprintTargetDate;
      if (!isYmd(requestedTargetDate)) {
        window.alert("Target date must be a valid date.");
        return;
      }
      const maxTarget = clampYmdToBounds(addDaysYMD(todayYmd, MAX_SPRINT_SPAN_DAYS) ?? todayYmd, sprintDateCheck.startDate, MAX_YMD);
      sanitizedTargetDate = clampYmdToBounds(requestedTargetDate, sprintDateCheck.startDate, maxTarget);
      if (sanitizedTargetDate <= sprintDateCheck.startDate) {
        window.alert("Target date must be after the sprint start date.");
        return;
      }
      if (sanitizedTargetDate !== requestedTargetDate) {
        setCalcPlanNotice("Target date was clamped to a safe future limit.");
      }
    }

    if (sprintDateCheck.message) setCalcPlanNotice(sprintDateCheck.message);

    const nextSprint = {
      id: genId("sprint"),
      name,
      startDate: sprintDateCheck.startDate,
      endDate: sprintDateCheck.endDate,
      color: SPRINT_PALETTE.includes(newSprintColor) ? newSprintColor : pickNextSprintColor(sprints),
      forecastEndDate: isNewSprint && isYmd(newSprintForecastEndDate)
        ? clampYmdToBounds(newSprintForecastEndDate, sprintDateCheck.startDate, sprintDateCheck.maxSpanEnd ?? MAX_YMD)
        : null,
      createdAt: todayYmd,
    };

    const nextSprints = [...sprints, nextSprint];
    let nextEntries = entriesAsc;
    let nextForecast = forecast;

    if (isNewSprint) {
      const existingActive = getActiveSprint(sprints);
      if (!bypassActiveGuard && existingActive && existingActive.id !== nextSprint.id) {
        requestArchiveFirst(existingActive, { type: "createActive", payload: { allowRecommendedPaceOverride } });
        return;
      }

      const hasStartDateEntry = entriesAsc.some((e) => e.date === sprintDateCheck.startDate);
      if (!hasStartDateEntry && startingWeight !== null) {
        const seeded = [
          ...entriesAsc,
          {
            date: sprintDateCheck.startDate,
            weight: startingWeight,
            caloriesIn: null,
            totalBurned: null,
            activeCalories: null,
            netCalories: null,
            bmr: calcBMRAtDate({
              weightLbs: startingWeight,
              heightIn: vitals.height,
              gender: vitals.gender,
              birthday: vitals.birthday,
              onDateYmd: sprintDateCheck.startDate,
            }),
            theoreticalWeight: startingWeight,
          },
        ].sort((a, b) => (a.date < b.date ? -1 : 1));
        nextEntries = recalcTheoretical(seeded);
      }

      nextForecast = {
        ...forecast,
        activeSprintId: nextSprint.id,
        sprintStartDate: sprintDateCheck.startDate,
        targetWeight: safeNumber(newSprintTargetWeight),
        targetDate: sanitizedTargetDate,
        inputMode: newSprintInputMode,
        assumedNetCalories: resolvedNewSprintNet,
        activityFactor: safeNumber(vitals.activityFactor) ?? forecast.activityFactor,
      };
    }

    setEntries(nextEntries);
    setSprints(nextSprints);
    if (isNewSprint) {
      setForecast(nextForecast);
      setCurrentPage("tracker");
    }

    await persistAll(nextEntries, vitals, nextForecast, nextSprints);

    setNewSprintType("new");
    setNewSprintName("");
    setNewSprintStartDate(suggestedSprintStartDate);
    setNewSprintEndDate("");
    setNewSprintForecastEndDate("");
    setNewSprintStartingWeight("");
    setNewSprintStartWeightSource(null);
    setNewSprintColor(pickNextSprintColor(nextSprints));
    setNewSprintTargetWeight(nextForecast.targetWeight ?? DEFAULT_FORECAST.targetWeight);
    setNewSprintTargetDate(nextForecast.targetDate || DEFAULT_FORECAST.targetDate || "");
    setNewSprintInputMode(nextForecast.inputMode);
    setNewSprintAssumedNetCalories(nextForecast.assumedNetCalories);
    setNewSprintNetDraft(String(safeNumber(nextForecast.assumedNetCalories) ?? 0));
    setNewSprintIntakeDraft(String(Math.max(0, safeNumber(nextForecast.assumedNetCalories) ?? 0)));
    setSprintSaveToast("Sprint created");
    setActiveModal(null);
  };

  // ---- Chart helpers ----

  const renderWeightTooltip = ({ active, payload, label }) => {
    if (!active) return null;
    const rows = (payload ?? []).filter((item) => item && item.value !== null && item.value !== undefined);
    const sprintsAtDate = getSprintsForDate(label);

    return (
      <div style={tooltipStyle}>
        <div style={{ fontWeight: 700, marginBottom: "0.35rem" }}>{formatMDY(label)}</div>
        {rows.map((item) => {
          const tooltipName = item.dataKey === "actual"
            ? "Actual (logged)"
            : item.dataKey === "bridge"
              ? "Inferred"
              : item.name;
          return (
            <div key={item.dataKey} style={{ color: item.color ?? "white", fontSize: "0.8rem" }}>
              {tooltipName}: {fmt2(item.value)}
            </div>
          );
        })}
        {sprintsAtDate.length ? (
          <div style={{ marginTop: "0.4rem", paddingTop: "0.35rem", borderTop: "1px solid rgba(255,255,255,0.14)", fontSize: "0.78rem" }}>
            {sprintsAtDate.slice(0, 2).map((s) => (
              <div key={s.id}>
                Sprint: <span style={{ fontWeight: 700 }}>{s.name}</span> ({formatMD(s.startDate)}–{formatMD(s.endDate ?? todayYmd)})
                {s.notes ? ` • ${s.notes}` : ""}
                {s.tag ? ` • ${s.tag}` : ""}
              </div>
            ))}
          </div>
        ) : null}
      </div>
    );
  };

  const chartTooltip = <Tooltip content={renderWeightTooltip} />;

  // ---- Forecast summary text (tracker) ----

  const forecastSummary = useMemo(() => {
    if (isHistoricSprint) return null;
    if (!endWeight) return null;
    const net = effectiveNetCaloriesForForecast;
    if (net === null) return null;

    const dailyDelta = net / 3500;
    const weekly = dailyDelta * 7;

    const modeLabel = forecast.inputMode === "net" ? `${Math.round(net)} net cal/day` : `${comma(forecast.assumedNetCalories)} intake`;
    const direction = net < 0 ? "deficit" : net > 0 ? "surplus" : "flat";
    const rate = `${Math.abs(weekly).toFixed(1)} lbs/wk`;

    return { modeLabel, direction, rate, net };
  }, [endWeight, effectiveNetCaloriesForForecast, forecast, isHistoricSprint]);

  const sprintProjectionPreview = useMemo(() => {
    if (sprintAnchor.anchorWeight === null || !isYmd(forecast.targetDate) || !isYmd(sprintAnchor.anchorDate)) return null;
    const net = effectiveNetCaloriesForForecast;
    if (net === null) return null;
    const days = daysBetweenInclusiveStartExclusiveEnd(sprintAnchor.anchorDate, forecast.targetDate);
    if (!days || days <= 0) return null;

    const projected = sprintAnchor.anchorWeight + (net / 3500) * days;
    const weekly = Math.abs((net / 3500) * 7);
    const modeText = `${Math.round(net)} net/day`;
    return {
      projected: projected.toFixed(1),
      date: formatMDY(forecast.targetDate),
      weekly: weekly.toFixed(1),
      modeText,
    };
  }, [forecast, effectiveNetCaloriesForForecast, sprintAnchor.anchorDate, sprintAnchor.anchorWeight]);

  const trackerProjectionDisplay = useMemo(() => {
    if (isHistoricSprint) return null;
    if (!isYmd(forecast.targetDate)) return null;
    const targetWeight = safeNumber(sprintDraft?.targetWeight ?? forecast.targetWeight);
    const currentWeight = sprintAnchor.anchorWeight;
    if (targetWeight === null || currentWeight === null || effectiveNetCaloriesForForecast === null || !sprintEntriesAsc.length) return null;

    const lastEntry = sprintEntriesAsc[sprintEntriesAsc.length - 1];
    const projectionSeries = calcForecastSeries({
      lastDate: lastEntry.date,
      currentWeight: lastEntry.weight,
      targetDate: forecast.targetDate,
      effectiveNetCalories: effectiveNetCaloriesForForecast,
    });

    const endpoint = projectionSeries[projectionSeries.length - 1];
    if (!endpoint || typeof endpoint.projected !== "number" || !Number.isFinite(endpoint.projected)) return null;

    const endingWeight = endpoint.projected;
    const totalChange = endingWeight - currentWeight;
    const deltaFromGoal = endingWeight - targetWeight;

    const isLoseGoal = targetWeight < currentWeight;
    const isGainGoal = targetWeight > currentWeight;

    const missRaw = isLoseGoal ? endingWeight - targetWeight : isGainGoal ? targetWeight - endingWeight : Math.abs(endingWeight - targetWeight);
    const isWorseThanGoal = isLoseGoal ? endingWeight > targetWeight : isGainGoal ? endingWeight < targetWeight : Math.abs(endingWeight - targetWeight) > 0;
    const miss = Math.max(0, missRaw);

    let statusIcon = "+";
    let statusColor = isWorseThanGoal ? "rgba(255,255,255,0.7)" : "rgba(187,247,208,0.95)";
    if (isWorseThanGoal && miss > 2) {
      statusIcon = "!";
      statusColor = "rgba(248,113,113,0.95)";
    } else if (isWorseThanGoal && miss > 0.5) {
      statusIcon = "~";
      statusColor = "rgba(251,191,36,0.95)";
    }

    const goalRelation = deltaFromGoal > 0 ? "over" : deltaFromGoal < 0 ? "under" : "on";
    const goalDeltaText = `${Math.abs(deltaFromGoal).toFixed(1)} lbs`;
    const secondaryText =
      goalRelation === "on"
        ? `${statusIcon} On goal`
        : `${statusIcon} ${goalDeltaText} ${goalRelation} goal`;

    return {
      dateText: formatMDY(forecast.targetDate),
      endingWeight,
      totalChange,
      secondaryText,
      statusColor,
    };
  }, [forecast.targetDate, forecast.targetWeight, effectiveNetCaloriesForForecast, sprintEntriesAsc, sprintAnchor.anchorWeight, isHistoricSprint]);

  const recalcCurrentWeight = useMemo(() => getCurrentWeightAnchor({
    chartData: trackerStatusChartData,
    movingAvgDays: sprintStatusMovingAverageWindow,
    todayKey: todayYmd,
    sprintStartWeight: sprintAnchor.anchorWeight,
    sprintStartDateKey: sprintStartEffective,
  }), [trackerStatusChartData, sprintStatusMovingAverageWindow, todayYmd, sprintAnchor.anchorWeight, sprintStartEffective]);

  const recalcData = useMemo(() => {
    if (isHistoricSprint || !activeSprint) return null;
    const targetWeight = safeNumber(sprintDraft?.targetWeight ?? forecast.targetWeight);

    // Infer targetDate from linked roadmap phase when forecast doesn't have one
    let effectiveTargetDate: string | null = forecast.targetDate ?? null;
    if (!isYmd(effectiveTargetDate) && roadmap) {
      const linkedPhaseId = Object.entries((roadmap.phaseSprintLinks ?? {}) as Record<string, string>)
        .find(([, sid]) => sid === activeSprint.id)?.[0] ?? null;
      const linkedPhase = linkedPhaseId
        ? ((roadmap.phases ?? []) as any[]).find((p) => p.id === linkedPhaseId)
        : null;
      if (linkedPhase && isYmd(linkedPhase.endDate)) {
        effectiveTargetDate = linkedPhase.endDate;
      }
    }

    if (!isYmd(effectiveTargetDate) || targetWeight === null || !recalcCurrentWeight) return null;

    const currentWeight = recalcCurrentWeight.weight;
    if (!Number.isFinite(currentWeight)) return null;

    const remainingDays = daysBetweenInclusiveStartExclusiveEnd(todayYmd, effectiveTargetDate);
    const remainingLbs = targetWeight - currentWeight;
    const requiredNetPerDay = remainingDays > 0 ? (remainingLbs * 3500) / remainingDays : null;
    const currentPlanNet = effectiveNetCaloriesForForecast;
    const deltaNet = requiredNetPerDay !== null && currentPlanNet !== null ? requiredNetPerDay - currentPlanNet : null;

    const bmr = calcBMR({ weightLbs: currentWeight, heightIn: vitals.height, gender: vitals.gender, birthday: vitals.birthday });
    const tdee = bmr !== null ? calcTDEEFromFactor({ bmr, factor: vitals.activityFactor ?? forecast.activityFactor }) : null;
    const requiredIntake = requiredNetPerDay !== null && tdee !== null ? tdee + requiredNetPerDay : null;

    const anchorDateKey = recalcCurrentWeight.dateKey;
    const plannedPoint = trackerChartData.find((d) => d.date === anchorDateKey && Number.isFinite(d.originalPlan))
      ?? [...trackerChartData].reverse().find((d) => d.date <= anchorDateKey && Number.isFinite(d.originalPlan));
    const plannedWeightToday = plannedPoint?.originalPlan ?? null;
    const deltaWeight = plannedWeightToday !== null ? currentWeight - plannedWeightToday : null;

    const goalDirection = startWeight !== null && targetWeight !== startWeight
      ? (targetWeight < startWeight ? "cut" : "gain")
      : (targetWeight < currentWeight ? "cut" : targetWeight > currentWeight ? "gain" : "maintain");

    let status = "on-track";
    if (deltaWeight !== null && goalDirection !== "maintain") {
      if (goalDirection === "cut") {
        if (deltaWeight < -REPLAN_STATUS_THRESHOLD_LBS) status = "ahead";
        else if (deltaWeight > REPLAN_STATUS_THRESHOLD_LBS) status = "behind";
      } else {
        if (deltaWeight > REPLAN_STATUS_THRESHOLD_LBS) status = "ahead";
        else if (deltaWeight < -REPLAN_STATUS_THRESHOLD_LBS) status = "behind";
      }
    }

    const lastWeighIn = [...sprintEntriesAsc].reverse().find((e) => e.date <= todayYmd && Number.isFinite(e.weight)) ?? null;
    const staleDays = lastWeighIn ? (daysBetweenInclusiveStartExclusiveEnd(lastWeighIn.date, todayYmd) ?? 0) : null;
    const isStale = staleDays !== null && staleDays >= STALE_WEIGHIN_DAYS;

    let projectedDateFromCurrentPlan = null;
    if (currentPlanNet !== null && Math.abs(currentPlanNet) > 0) {
      const rawDays = (remainingLbs * 3500) / currentPlanNet;
      if (Number.isFinite(rawDays) && rawDays > 0) {
        const roundedDays = Math.max(1, Math.round(rawDays));
        projectedDateFromCurrentPlan = addDaysYMD(todayYmd, roundedDays);
      }
    }

    const isAggressive = requiredNetPerDay !== null && Math.abs(requiredNetPerDay) > REPLAN_MAX_ABS_NET;
    const isUpToDate = deltaNet !== null && Math.abs(deltaNet) <= REPLAN_UP_TO_DATE_NET_TOLERANCE;

    return {
      currentWeight,
      currentAnchor: recalcCurrentWeight,
      targetWeight,
      targetDate: effectiveTargetDate,
      remainingDays,
      requiredNetPerDay,
      requiredIntake,
      currentPlanNet,
      deltaNet,
      status,
      deltaWeight,
      plannedWeightToday,
      isAggressive,
      projectedDateFromCurrentPlan,
      isStale,
      staleDays,
      tdee,
      goalDirection,
    };
  }, [
    isHistoricSprint,
    activeSprint,
    forecast.targetWeight,
    forecast.targetDate,
    forecast.activityFactor,
    recalcCurrentWeight,
    todayYmd,
    effectiveNetCaloriesForForecast,
    vitals.height,
    vitals.gender,
    vitals.birthday,
    vitals.activityFactor,
    trackerChartData,
    startWeight,
    sprintEntriesAsc,
    roadmap,
  ]);

  const applyRecalculatedCalories = async () => {
    if (blockDemoWrite()) return;
    if (!activeSprint || isArchivedOrViewer || recalcConfirm?.kind !== "calories") return;
    if (recalcPrimaryAlreadyAligned || recalcConfirm.nextAssumedValue === null || typeof recalcConfirm.nextAssumedValue !== "number" || !Number.isFinite(recalcConfirm.nextAssumedValue)) {
      setRecalcConfirm(null);
      return;
    }
    const nextAssumed = recalcConfirm.nextAssumedValue;
    const nextForecast = { ...forecast, assumedNetCalories: nextAssumed };
    const nextSprints = sprints.map((sp) => (activeSprint && sp.id === activeSprint.id ? { ...sp, assumedNetCalories: nextAssumed } : sp));
    setRecalcConfirm(null);
    setForecast(nextForecast);
    setSprints(nextSprints);
    await persistAll(entriesAsc, vitals, nextForecast, nextSprints);
  };

  const applyRecalculatedDate = async () => {
    if (blockDemoWrite()) return;
    if (!activeSprint || isArchivedOrViewer || recalcConfirm?.kind !== "date" || !isYmd(recalcConfirm.nextTargetDate)) return;
    const nextDate = recalcConfirm.nextTargetDate;
    if (nextDate === forecast.targetDate) {
      setRecalcConfirm(null);
      return;
    }
    const nextForecast = { ...forecast, targetDate: nextDate };
    const nextSprints = sprints.map((sp) => (activeSprint && sp.id === activeSprint.id ? { ...sp, targetDate: nextDate, forecastEndDate: nextDate } : sp));
    setRecalcConfirm(null);
    setForecast(nextForecast);
    setSprints(nextSprints);
    await persistAll(entriesAsc, vitals, nextForecast, nextSprints);
  };

  const recalcGoalWeightValue = safeNumber(forecast.targetWeight);
  const recalcGoalWeightText = recalcGoalWeightValue !== null ? `${recalcGoalWeightValue.toFixed(1)} lbs` : "your goal weight";
  const recalcGoalDateText = isYmd(recalcData?.targetDate) ? formatMDY(recalcData.targetDate) : "your goal date";
  const recalcProjectedDateText = isYmd(recalcData?.projectedDateFromCurrentPlan) ? formatMDY(recalcData.projectedDateFromCurrentPlan) : "—";
  const recalcDaysRelative = recalcData && isYmd(recalcData.projectedDateFromCurrentPlan) && isYmd(recalcData.targetDate)
    ? daysBetweenInclusiveStartExclusiveEnd(recalcData.projectedDateFromCurrentPlan, recalcData.targetDate)
    : null;
  const recalcDaysRelativeText = recalcDaysRelative === null
    ? "on time"
    : recalcDaysRelative === 0
      ? "on time"
      : `${Math.abs(recalcDaysRelative)} days ${recalcDaysRelative > 0 ? "early" : "late"}`;
  const recalcGoalDateShiftText = recalcDaysRelative === null
    ? "Timing unavailable"
    : recalcDaysRelative === 0
      ? "On time"
      : `${Math.abs(recalcDaysRelative)} days ${recalcDaysRelative > 0 ? "earlier" : "later"}`;
  const recalcRequiredNetRounded = recalcData?.requiredNetPerDay !== null && recalcData?.requiredNetPerDay !== undefined
    ? Math.round(recalcData.requiredNetPerDay)
    : null;
  const recalcRequiredIntake = recalcData?.requiredIntake ?? null;
  const recalcCurrentNetRounded = recalcData?.currentPlanNet !== null && recalcData?.currentPlanNet !== undefined
    ? Math.round(recalcData.currentPlanNet)
    : null;
  const recalcCurrentIntakeRounded = recalcData?.currentPlanNet !== null && recalcData?.currentPlanNet !== undefined && recalcData?.tdee !== null && recalcData?.tdee !== undefined
    ? Math.round(recalcData.tdee + recalcData.currentPlanNet)
    : null;
  const recalcWeeklyRateText = recalcData?.requiredNetPerDay !== null && recalcData?.requiredNetPerDay !== undefined
    ? Math.abs((recalcData.requiredNetPerDay / 3500) * 7).toFixed(1)
    : null;
  const recalcRecommendedRateMax = recalcData?.goalDirection === "cut"
    ? SPRINT_PACE_LIMITS.cutMaxLossLbsPerWeek
    : recalcData?.goalDirection === "bulk"
      ? SPRINT_PACE_LIMITS.bulkMaxGainLbsPerWeek
      : SPRINT_PACE_LIMITS.maintainMaxAbsLbsPerWeek;
  const recalcRequiresSafePlan = !!(
    recalcData
    && recalcData.goalDirection !== "maintain"
    && recalcData.requiredNetPerDay !== null
    && Math.abs((recalcData.requiredNetPerDay / 3500) * 7) > recalcRecommendedRateMax
  );
  const recalcSafeNetPerDay = recalcData?.requiredNetPerDay !== null && recalcData?.requiredNetPerDay !== undefined
    ? (recalcData.requiredNetPerDay < 0 ? -1 : 1) * ((recalcRecommendedRateMax * 3500) / 7)
    : null;
  const recalcSafeNetRounded = recalcSafeNetPerDay !== null ? Math.round(recalcSafeNetPerDay) : null;
  const recalcSafeIntake = recalcSafeNetPerDay !== null && recalcData?.tdee !== null && recalcData?.tdee !== undefined
    ? Math.round(recalcData.tdee + recalcSafeNetPerDay)
    : null;
  const recalcSafeDateResult = recalcData && Number.isFinite(recalcData.currentWeight) && Number.isFinite(recalcData.targetWeight)
    ? computeTargetDateFromRate({
        startWeight: Number(recalcData.currentWeight),
        targetWeight: Number(recalcData.targetWeight),
        startDateYmd: todayYmd,
        rateLbsPerWeekAbs: recalcRecommendedRateMax,
      })
    : null;
  const recalcSafeProjectedDate = recalcSafeDateResult?.targetDateYmd ?? null;
  const recalcSafeProjectedDateText = isYmd(recalcSafeProjectedDate) ? formatMDY(recalcSafeProjectedDate) : recalcGoalDateText;
  const recalcModalTitle = recalcConfirm?.kind === "date"
    ? "Adjust goal date"
    : recalcRequiresSafePlan
      ? "Use fastest safe plan"
      : "Update your plan";
  const recalcDateComparisonText = recalcDaysRelative === null
    ? "timing unavailable vs original date"
    : recalcDaysRelative === 0
      ? "same as original date"
      : `${Math.abs(recalcDaysRelative)} days ${recalcDaysRelative > 0 ? "earlier" : "later"} than original date`;
  const recalcPrimaryOptionTitle = recalcRequiresSafePlan ? "Fastest safe plan" : "Stay aligned";
  const recalcPrimaryCtaLabel = recalcRequiresSafePlan ? "Use fastest safe plan" : "Recalibrate";
  const recalcPrimarySignedRate = formatSignedPace(
    recalcRequiresSafePlan
      ? (recalcSafeNetPerDay !== null ? (recalcSafeNetPerDay / 3500) * 7 : null)
      : (recalcData?.requiredNetPerDay !== null && recalcData?.requiredNetPerDay !== undefined ? (recalcData.requiredNetPerDay / 3500) * 7 : null),
    1,
    "lb/week",
  );
  const recalcCurrentSignedRate = formatSignedPace(
    recalcData?.currentPlanNet !== null && recalcData?.currentPlanNet !== undefined
      ? (recalcData.currentPlanNet / 3500) * 7
      : null,
    1,
    "lb/week",
  );
  const recalcPrimaryCaloriesLine = `Calories: ${recalcRequiresSafePlan
    ? (recalcSafeIntake !== null ? `~${Math.round(recalcSafeIntake).toLocaleString()}` : "~—")
    : (recalcRequiredIntake !== null ? `~${Math.round(recalcRequiredIntake).toLocaleString()}` : "~—")} intake · ${recalcRequiresSafePlan
    ? (recalcSafeNetRounded !== null ? recalcSafeNetRounded.toLocaleString() : "—")
    : (recalcRequiredNetRounded !== null ? recalcRequiredNetRounded.toLocaleString() : "—")} net`;
  const recalcCurrentCaloriesLine = `Calories: ${recalcCurrentIntakeRounded !== null ? `~${recalcCurrentIntakeRounded.toLocaleString()}` : "~—"} intake · ${recalcCurrentNetRounded !== null ? recalcCurrentNetRounded.toLocaleString() : "—"} net`;
  const recalcPrimaryRecommendedNet = recalcRequiresSafePlan ? recalcSafeNetRounded : recalcRequiredNetRounded;
  const recalcPrimaryRecommendedIntake = recalcRequiresSafePlan ? recalcSafeIntake : (recalcRequiredIntake !== null ? Math.round(recalcRequiredIntake) : null);
  const recalcPrimaryRecommendedProjectedDate = recalcRequiresSafePlan ? recalcSafeProjectedDate : (isYmd(recalcData?.targetDate) ? recalcData.targetDate : null);
  const recalcCurrentRatePerWeek = recalcData?.currentPlanNet !== null && recalcData?.currentPlanNet !== undefined
    ? (recalcData.currentPlanNet / 3500) * 7
    : null;
  const recalcPrimaryRecommendedRatePerWeek = recalcRequiresSafePlan
    ? (recalcSafeNetPerDay !== null ? (recalcSafeNetPerDay / 3500) * 7 : null)
    : (recalcData?.requiredNetPerDay !== null && recalcData?.requiredNetPerDay !== undefined ? (recalcData.requiredNetPerDay / 3500) * 7 : null);
  const recalcPrimaryOutcomeText = recalcRequiresSafePlan
    ? `Hit ${recalcGoalWeightText} by ${recalcSafeProjectedDateText}`
    : `Hit ${recalcGoalWeightText} by ${recalcGoalDateText}`;
  const recalcPrimaryRateDelta = recalcCurrentRatePerWeek !== null && recalcPrimaryRecommendedRatePerWeek !== null
    ? Math.abs(recalcCurrentRatePerWeek - recalcPrimaryRecommendedRatePerWeek)
    : null;
  const recalcPrimaryProjectedDateMatches = isYmd(recalcData?.projectedDateFromCurrentPlan) && isYmd(recalcPrimaryRecommendedProjectedDate)
    ? recalcData.projectedDateFromCurrentPlan === recalcPrimaryRecommendedProjectedDate
    : null;
  const recalcPrimaryNetDiff = recalcCurrentNetRounded !== null && recalcPrimaryRecommendedNet !== null
    ? Math.abs(recalcCurrentNetRounded - recalcPrimaryRecommendedNet)
    : null;
  const recalcPrimaryIntakeDiff = recalcCurrentIntakeRounded !== null && recalcPrimaryRecommendedIntake !== null
    ? Math.abs(recalcCurrentIntakeRounded - recalcPrimaryRecommendedIntake)
    : null;
  const recalcPrimaryHasComparableFields = recalcPrimaryNetDiff !== null || recalcPrimaryIntakeDiff !== null || recalcPrimaryRateDelta !== null || recalcPrimaryProjectedDateMatches !== null;
  const recalcPrimaryAlreadyAligned = !!(
    recalcPrimaryHasComparableFields
    && (recalcPrimaryNetDiff === null || recalcPrimaryNetDiff <= 5)
    && (recalcPrimaryIntakeDiff === null || recalcPrimaryIntakeDiff <= 5)
    && (recalcPrimaryRateDelta === null || recalcPrimaryRateDelta <= 0.05)
    && (recalcPrimaryProjectedDateMatches === null || recalcPrimaryProjectedDateMatches)
  );
  const recalcPrimaryNextAssumedValue = forecast.inputMode === "net"
    ? recalcPrimaryRecommendedNet
    : (recalcPrimaryRecommendedIntake !== null ? Math.max(0, recalcPrimaryRecommendedIntake) : null);
  const recalcPrimaryCanApply = !isArchivedOrViewer
    && recalcData?.requiredNetPerDay !== null
    && recalcPrimaryNextAssumedValue !== null
    && !recalcPrimaryAlreadyAligned;
  const recalcUpdatedVsCurrentDateDelta = (() => {
    const updatedDate = recalcRequiresSafePlan ? recalcSafeProjectedDate : recalcData?.targetDate;
    return isYmd(updatedDate) && isYmd(recalcData?.projectedDateFromCurrentPlan)
      ? daysBetweenInclusiveStartExclusiveEnd(updatedDate, recalcData.projectedDateFromCurrentPlan)
      : null;
  })();
  const recalcModalShiftText = recalcUpdatedVsCurrentDateDelta === null
    ? "on a different timeline"
    : recalcUpdatedVsCurrentDateDelta === 0
      ? "on the same timeline"
      : `${Math.abs(recalcUpdatedVsCurrentDateDelta)} days ${recalcUpdatedVsCurrentDateDelta > 0 ? "earlier" : "later"}`;
  const recalcSupportiveNotice = "Hitting your target date would require going beyond the recommended pace range.";
  const mainContentWidthStyle: React.CSSProperties = isMobile
    ? {
        width: "100%",
        minWidth: "auto",
      }
    : {
        width: "100%",
        maxWidth: 1180,
        margin: "0 auto",
        padding: 24,
        boxSizing: "border-box",
        minWidth: 0,
      };

  const historicSummaryText = useMemo(() => {
    if (!isHistoricSprint) return null;
    if (netLoss === null || weeklyAvgLoss === null || !daysTracked) return "Not enough sprint data for a summary yet.";
    return `${Math.abs(netLoss).toFixed(1)} lbs ${netLoss >= 0 ? "lost" : "gained"} over ${daysTracked} days (~${Math.abs(weeklyAvgLoss).toFixed(2)} lbs/week).`;
  }, [isHistoricSprint, netLoss, weeklyAvgLoss, daysTracked]);

  const weightForPlanCalc = sprintAnchor.anchorWeight;

  const getSprintEditorTargetDate = (sprintOverride = null) => {
    const targetSprint = sprintOverride ?? activeSprint ?? (forecast.activeSprintId ? sprints.find((s) => s.id === forecast.activeSprintId) : null);
    if (isYmd(targetSprint?.forecastEndDate)) return targetSprint.forecastEndDate;
    if (isYmd(forecast.targetDate)) return forecast.targetDate;
    return "";
  };

  const calcPlanDisabledReason = useMemo(() => {
    if (sprintAnchor.anchorSource === "none") return "Add a weigh-in on the sprint start date (or your first sprint day) to calculate a plan.";
    if (safeNumber(sprintDraft?.targetWeight ?? forecast.targetWeight) === null || !isYmd(sprintDraft?.targetDate ?? getSprintEditorTargetDate())) return "Set a target weight and date to calculate.";
    return null;
  }, [sprintAnchor.anchorSource, sprintDraft?.targetWeight, sprintDraft?.targetDate, forecast.targetWeight, activeSprint?.forecastEndDate, forecast.targetDate]);
  const sprintSettingsGuardrails = useMemo(() => validateSprintGuardrails({
    startWeight: safeNumber(sprintDraft?.startingWeight),
    targetWeight: safeNumber(sprintDraft?.targetWeight),
    startDate: sprintDraft?.startDate ?? activeSprint?.startDate ?? sprintStartDraft,
    targetDate: sprintDraft?.targetDate ?? getSprintEditorTargetDate(),
  }), [sprintDraft, activeSprint?.startDate, sprintStartDraft]);

  const sprintDraftEffectiveNetCalories = useMemo(() => {
    if (sprintAnchor.anchorWeight === null) return null;
    const inputMode = sprintDraft?.inputMode ?? forecast.inputMode;
    const assumedCalories = safeNumber(sprintDraft?.assumedNetCalories ?? forecast.assumedNetCalories);
    if (assumedCalories === null) return null;
    if (inputMode === "net") return assumedCalories;

    const bmr = calcBMR({ weightLbs: sprintAnchor.anchorWeight, heightIn: vitals.height, gender: vitals.gender, birthday: vitals.birthday });
    if (bmr === null) return null;
    const tdee = calcTDEEFromFactor({ bmr, factor: vitals.activityFactor ?? forecast.activityFactor });
    return tdee === null ? null : assumedCalories - tdee;
  }, [sprintAnchor.anchorWeight, sprintDraft?.inputMode, sprintDraft?.assumedNetCalories, forecast.inputMode, forecast.assumedNetCalories, forecast.activityFactor, vitals]);

  const sprintDraftProjectionPreview = useMemo(() => {
    const targetDate = sprintDraft?.targetDate ?? getSprintEditorTargetDate();
    if (sprintAnchor.anchorWeight === null || !isYmd(targetDate) || !isYmd(sprintAnchor.anchorDate) || sprintDraftEffectiveNetCalories === null) return null;
    const days = daysBetweenInclusiveStartExclusiveEnd(sprintAnchor.anchorDate, targetDate);
    if (!days || days <= 0) return null;

    const projected = sprintAnchor.anchorWeight + (sprintDraftEffectiveNetCalories / 3500) * days;
    const weekly = Math.abs((sprintDraftEffectiveNetCalories / 3500) * 7);
    return {
      projected: projected.toFixed(1),
      date: formatMDY(targetDate),
      weekly: weekly.toFixed(1),
    };
  }, [sprintAnchor.anchorWeight, sprintAnchor.anchorDate, sprintDraft?.targetDate, sprintDraftEffectiveNetCalories]);

  useEffect(() => {
    if (activeModal !== "sprint" || isArchivedOrViewer || calcPlanDisabledReason || !sprintDraft) return;
    const currentWeight = safeNumber(sprintDraft.startingWeight);
    const targetWeight = safeNumber(sprintDraft.targetWeight);
    if (currentWeight === null || targetWeight === null || !isYmd(sprintDraft.startDate) || !isYmd(sprintDraft.targetDate)) return;
    const days = daysBetweenInclusiveStartExclusiveEnd(sprintDraft.startDate, sprintDraft.targetDate);
    if (!days || days <= 0) return;
    const requiredNet = Math.round(-(((currentWeight - targetWeight) * 3500) / days));
    if (sprintDraft.inputMode === "net") {
      setSprintDraft((prev) => (prev ? { ...prev, assumedNetCalories: requiredNet, netDraft: String(requiredNet) } : prev));
      return;
    }
    const bmr = calcBMR({ weightLbs: currentWeight, heightIn: vitals.height, gender: vitals.gender, birthday: vitals.birthday });
    if (bmr === null) return;
    const tdee = calcTDEEFromFactor({ bmr, factor: vitals.activityFactor ?? forecast.activityFactor });
    if (tdee === null) return;
    const intake = Math.max(0, Math.round(requiredNet + tdee));
    setSprintDraft((prev) => (prev ? { ...prev, assumedNetCalories: intake, intakeDraft: String(intake) } : prev));
  }, [activeModal, isArchivedOrViewer, calcPlanDisabledReason, sprintDraft?.startingWeight, sprintDraft?.targetWeight, sprintDraft?.startDate, sprintDraft?.targetDate, sprintDraft?.inputMode, vitals, forecast.activityFactor]);

  const jumpToSprintStartEntry = () => {
    const sprintStart = selectedSprint?.startDate ?? forecast.sprintStartDate;
    if (isYmd(sprintStart)) {
      loadCurrentEntryForDate(sprintStart);
    }

    setActiveModal(null);
    setCurrentPage("tracker");
    setShowMobileLogModal(true);

    setTimeout(() => {
      if (dailyEntryCardRef.current && typeof dailyEntryCardRef.current.scrollIntoView === "function") {
        dailyEntryCardRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 40);
  };

  useEffect(() => {
    if (!showMobileLogModal) {
      setSaveButtonFlash(false);
      setDailyEntryFocusedField(null);
      setIsDailyEntryDatePickerOpen(false);
      return;
    }
    loadCurrentEntryForDate(currentEntry.date);
  }, [showMobileLogModal]);

  const openDailyEntryDatePicker = useCallback(() => {
    if (isArchivedOrViewer) return;
    const inputEl = dailyEntryDateRef.current as (HTMLInputElement & { showPicker?: () => void }) | null;
    if (!inputEl) return;
    setIsDailyEntryDatePickerOpen(true);
    try {
      if (typeof inputEl.showPicker === "function") {
        inputEl.showPicker();
        return;
      }
    } catch {
      // Fall through to focus/click fallback for browsers with partial showPicker support.
    }
    inputEl.focus();
    inputEl.click();
  }, [isArchivedOrViewer]);

  const openVitalsEditor = () => {
    setVitalsDraft(normalizeVitals(vitals));
    setVitalsEditorError(null);
    setActiveModal("vitals");
    setShowActivityFactorHelp(false);
    setShowSprintNetHelp(false);
  };

  const openSprintEditor = () => {
    const targetSprint = activeSprint ?? (forecast.activeSprintId ? sprints.find((s) => s.id === forecast.activeSprintId) : null);
    const startDate = targetSprint?.startDate ?? sprintStartDraft;
    const loggedWeight = getLoggedWeightForDate(entriesAsc, startDate);
    const nextDraft = {
      name: archiveName,
      color: targetSprint?.color ?? SPRINT_PALETTE[0],
      startDate,
      startingWeight: loggedWeight !== null ? String(loggedWeight) : String(sprintAnchor.anchorWeight ?? ""),
      targetWeight: String(safeNumber(forecast.targetWeight) ?? ""),
      targetDate: getSprintEditorTargetDate(targetSprint),
      inputMode: forecast.inputMode,
      assumedNetCalories: safeNumber(forecast.assumedNetCalories) ?? 0,
      netDraft: String(safeNumber(forecast.assumedNetCalories) ?? 0),
      intakeDraft: String(Math.max(0, safeNumber(forecast.assumedNetCalories) ?? 0)),
      planMethod: "byDate",
      presetRate: null,
      presetClamped: false,
      goalType: deriveSprintGoalType(safeNumber(sprintAnchor.anchorWeight), safeNumber(forecast.targetWeight)),
    };
    setSprintDraft(nextDraft);
    setSprintDraftOriginal(nextDraft);
    setSprintStartWeightSource(loggedWeight !== null ? "fromLog" : null);
    setActiveModal("sprint");
    setShowActivityFactorHelp(false);
    setShowSprintNetHelp(false);
    setSprintPlanMethod("byDate");
    setSprintPresetRate(null);
    setSprintPresetClamped(false);
  };

  const requestRoadmapDelete = () => {
    if (!roadmap) return;
    setRoadmapHeaderMenuOpen(false);
    setRoadmapDeleteConfirmOpen(true);
  };

  const confirmRoadmapDelete = () => {
    if (!roadmap) {
      setRoadmapDeleteConfirmOpen(false);
      return;
    }
    setRoadmap(null);
    setRoadmapHeaderMenuOpen(false);
    setRoadmapDeleteConfirmOpen(false);
  };

  const closeModal = () => {
    setActiveModal(null);
    setShowMetabolismWeightConfirm(false);
    setPendingVitalsSave(null);
    setSprintDraft(null);
    setSprintDraftOriginal(null);
    setSprintUpdateConfirm(null);
    setCreateSprintWarningConfirm(null);
    setCalFloorConfirmData(null);
    setCalFloorChoice("recommended");
    setCalFloorEditContext(null);
    setShowActivityFactorHelp(false);
    setShowSprintNetHelp(false);
  };

  const applySprintDraftChanges = async (overrides?: { net: number; targetDate: string | null } | null) => {
    if (!sprintDraft || !activeSprint) return;
    const draftForSave = overrides
      ? {
        ...sprintDraft,
        assumedNetCalories: overrides.net,
        netDraft: String(overrides.net),
        targetDate: isYmd(overrides.targetDate) ? overrides.targetDate : sprintDraft.targetDate,
      }
      : sprintDraft;
    const nextTargetDate = isYmd(draftForSave.targetDate) ? draftForSave.targetDate : null;
    const nextName = sanitizeSprintName(archiveName || activeSprint.name || "Sprint", "Sprint");
    const duplicateSprint = findSprintNameDuplicate(nextName, activeSprint.id);
    if (duplicateSprint) {
      window.alert(`A sprint named "${duplicateSprint.name}" already exists. Please choose a different name.`);
      return;
    }
    const nextForecast = {
      ...forecast,
      sprintStartDate: draftForSave.startDate,
      targetWeight: safeNumber(draftForSave.targetWeight),
      targetDate: nextTargetDate,
      inputMode: draftForSave.inputMode,
      assumedNetCalories: safeNumber(draftForSave.assumedNetCalories) ?? 0,
    };
    const nextSprints = sprints.map((sp) => (
      sp.id === activeSprint.id
        ? { ...sp, name: nextName, color: draftForSave.color ?? sp.color, startDate: draftForSave.startDate, forecastEndDate: nextTargetDate }
        : sp
    ));

    // Mirror createSprint: if the (new) start date has no logged entry but we
    // have a starting weight, seed it so the sprint isn't left in the empty state.
    let nextEntries = entriesAsc;
    const startWeight = safeNumber(draftForSave.startingWeight);
    const hasStartDateEntry = entriesAsc.some((e) => e.date === draftForSave.startDate);
    if (!hasStartDateEntry && startWeight !== null) {
      const seeded = [
        ...entriesAsc,
        {
          date: draftForSave.startDate,
          weight: startWeight,
          caloriesIn: null,
          totalBurned: null,
          activeCalories: null,
          netCalories: null,
          bmr: calcBMRAtDate({
            weightLbs: startWeight,
            heightIn: vitals.height,
            gender: vitals.gender,
            birthday: vitals.birthday,
            onDateYmd: draftForSave.startDate,
          }),
          theoreticalWeight: startWeight,
        },
      ].sort((a, b) => (a.date < b.date ? -1 : 1));
      nextEntries = recalcTheoretical(seeded);
    }

    setEntries(nextEntries);
    setForecast(nextForecast);
    setSprints(nextSprints);
    await persistAll(nextEntries, vitals, nextForecast, nextSprints);
    setSprintSaveToast("Sprint updated");
    closeModal();
  };

  const saveSprintEditor = async () => {
    if (!sprintDraft || !sprintDraftOriginal) return;
    const oldTargetWeight = safeNumber(sprintDraftOriginal.targetWeight);
    const newTargetWeight = safeNumber(sprintDraft.targetWeight);
    const oldTargetDate = sprintDraftOriginal.targetDate ?? "";
    const newTargetDate = sprintDraft.targetDate ?? "";
    const oldStart = sprintDraftOriginal.startDate ?? "";
    const newStart = sprintDraft.startDate ?? "";
    const oldNet = safeNumber(sprintDraftOriginal.assumedNetCalories) ?? 0;
    const newNet = safeNumber(sprintDraft.assumedNetCalories) ?? 0;
    const oldMode = sprintDraftOriginal.inputMode;
    const newMode = sprintDraft.inputMode;

    const oldMetrics = computeSprintPlanMetrics({ startWeight: safeNumber(sprintDraftOriginal.startingWeight), targetWeight: oldTargetWeight, startDate: oldStart, targetDate: oldTargetDate });
    const newMetrics = computeSprintPlanMetrics({ startWeight: safeNumber(sprintDraft.startingWeight), targetWeight: newTargetWeight, startDate: newStart, targetDate: newTargetDate });

    const meaningful = Math.abs((newTargetWeight ?? 0) - (oldTargetWeight ?? 0)) > 0.001
      || newTargetDate !== oldTargetDate
      || newStart !== oldStart
      || Math.abs(newNet - oldNet) >= 100
      || newMode !== oldMode;

    const shouldConfirm = meaningful || sprintSettingsGuardrails.hardErrors.length > 0 || sprintSettingsGuardrails.softWarnings.length > 0;
    if (!shouldConfirm) {
      await applySprintDraftChanges();
      return;
    }

    const EPSILON = 0.0001;
    const CUT_MAX_LOSS_LB_PER_WEEK = 2.0;
    const BULK_MAX_GAIN_LB_PER_WEEK = 1.0;
    const MAINTAIN_MAX_ABS_LB_PER_WEEK = 0.25;
    const SHORT_SPRINT_DAYS_THRESHOLD = 21;
    const BIG_NET_DELTA_THRESHOLD = 400;

    const normalizeNearZero = (value: number) => (Math.abs(value) < EPSILON ? 0 : value);
    const normalizeNumeric = (value: number | null | undefined) => {
      if (!Number.isFinite(value)) return null;
      return normalizeNearZero(Number(value));
    };
    const formatSignedValue = (value: number, digits: number) => {
      const normalized = normalizeNearZero(value);
      const abs = Math.abs(normalized).toLocaleString(undefined, { minimumFractionDigits: digits, maximumFractionDigits: digits });
      if (normalized > 0) return `+${abs}`;
      if (normalized < 0) return `-${abs}`;
      return abs;
    };
    const formatCalories = (value: number) => normalizeNearZero(Math.round(value)).toLocaleString();

    const oldDeltaDisplay = `${formatSignedValue(oldMetrics.deltaLbs ?? 0, 1)} lb`;
    const newDeltaDisplay = `${formatSignedValue(newMetrics.deltaLbs ?? 0, 1)} lb`;
    const oldPaceDisplay = `${formatSignedValue(oldMetrics.paceLbsPerWeek ?? 0, 1)} lb/week`;
    const newPaceDisplay = `${formatSignedValue(newMetrics.paceLbsPerWeek ?? 0, 1)} lb/week`;
    const oldDateDisplay = isYmd(oldTargetDate) ? formatMDY(oldTargetDate) : "—";
    const newDateDisplay = isYmd(newTargetDate) ? formatMDY(newTargetDate) : "—";
    const oldNetDisplay = `${formatCalories(oldNet)} cal/day`;
    const newNetDisplay = `${formatCalories(newNet)} cal/day`;

    const changeRows = [
      { key: "delta", label: "Planned change", before: oldDeltaDisplay, after: newDeltaDisplay },
      { key: "pace", label: "Pace", before: oldPaceDisplay, after: newPaceDisplay },
      { key: "targetDate", label: "Target date", before: oldDateDisplay, after: newDateDisplay },
      { key: "net", label: "Daily net calories", before: oldNetDisplay, after: newNetDisplay },
    ].filter((row) => row.before !== row.after);

    const startWeight = safeNumber(sprintDraft.startingWeight);
    const targetWeight = safeNumber(sprintDraft.targetWeight);
    const sprintPhaseType: SprintGoalType = (sprintDraft.goalType as SprintGoalType) ?? deriveSprintGoalType(startWeight, targetWeight);
    const tdeeForDraft = (() => {
      const sw = safeNumber(sprintDraft.startingWeight);
      if (sw === null) return null;
      const bmr = calcBMR({ weightLbs: sw, heightIn: vitals.height, gender: vitals.gender, birthday: vitals.birthday });
      if (bmr === null) return null;
      return calcTDEEFromFactor({ bmr, factor: vitals.activityFactor ?? forecast.activityFactor });
    })();
    const intakeFloor = getCalorieWarningFloor(vitals.gender);
    const draftIntake = tdeeForDraft !== null ? Math.round(tdeeForDraft + newNet) : null;
    const draftPaceAbs = Math.abs((newNet * 7) / 3500);
    const shouldRouteEditCalFloor = sprintPhaseType === "cut" && draftIntake !== null && draftIntake < intakeFloor && draftPaceAbs > SPRINT_PACE_LIMITS.cutMaxLossLbsPerWeek;
    if (shouldRouteEditCalFloor && startWeight !== null && targetWeight !== null) {
      const recommendedNet = intakeFloor - tdeeForDraft!;
      const recommendedRateAbs = Math.abs((recommendedNet * 7) / 3500);
      const recommendedTargetDate = recommendedRateAbs > 0
        ? computeTargetDateFromRate({ startWeight, targetWeight, startDateYmd: sprintDraft.startDate, rateLbsPerWeekAbs: recommendedRateAbs })?.targetDateYmd ?? null
        : null;
      const fastestNet = -Math.round((SPRINT_PACE_LIMITS.cutMaxLossLbsPerWeek * 3500) / 7);
      const fastestTargetDate = computeTargetDateFromRate({ startWeight, targetWeight, startDateYmd: sprintDraft.startDate, rateLbsPerWeekAbs: SPRINT_PACE_LIMITS.cutMaxLossLbsPerWeek })?.targetDateYmd ?? null;
      setCalFloorEditContext({ recommendedNet, recommendedTargetDate, fastestNet, fastestTargetDate });
      // Same default rule as createSprint: prefer the option that honours both guardrails.
      // At high TDEE the floor plan itself exceeds the pace cap, so the pace plan is safer.
      setCalFloorChoice(recommendedRateAbs <= SPRINT_PACE_LIMITS.cutMaxLossLbsPerWeek ? "recommended" : "fastest");
      setCalFloorConfirmData({
        rawIntake: draftIntake,
        floorIntake: intakeFloor,
        safeAdjustedNet: recommendedNet,
        lowestAllowableIntake: tdeeForDraft !== null ? Math.round(tdeeForDraft + fastestNet) : null,
        lowestAllowableNet: fastestNet,
      });
      return;
    }

    const proposedPace = normalizeNumeric(newMetrics.paceLbsPerWeek);
    const currentPace = normalizeNumeric(oldMetrics.paceLbsPerWeek);
    let warningTitle = "";
    let warningBody = "";

    if (proposedPace !== null) {
      if (sprintPhaseType === "cut" && proposedPace < -CUT_MAX_LOSS_LB_PER_WEEK) {
        warningTitle = "This pace is faster than typically recommended.";
        warningBody = "Most sustainable weight loss plans target up to ~2 lb per week. Consider extending the timeline or reducing the calorie deficit.";
      } else if (sprintPhaseType === "bulk" && proposedPace > BULK_MAX_GAIN_LB_PER_WEEK) {
        warningTitle = "This pace is faster than typically recommended.";
        warningBody = "Most lean gain plans target up to ~1 lb per week. Consider extending the timeline or reducing the calorie surplus.";
      } else if (sprintPhaseType === "maintain" && Math.abs(proposedPace) > MAINTAIN_MAX_ABS_LB_PER_WEEK) {
        warningTitle = "This plan won’t maintain weight.";
        warningBody = "Maintenance typically stays within ±0.25 lb per week. Adjust calories or timeline to stay closer to maintenance.";
      }
    }

    const headsUpItems = new Set<string>();
    if (sprintPhaseType !== "maintain" && Math.abs(newNet - oldNet) > BIG_NET_DELTA_THRESHOLD) {
      headsUpItems.add("Daily deficit/surplus is unusually large.");
    }

    setSprintUpdateConfirm({
      changeRows,
      warningTitle,
      warningBody,
      isWarning: warningTitle.length > 0,
      headsUpItems: Array.from(headsUpItems),
      proposedPace,
      currentPace,
    });
  };

  const saveVitalsEditor = async () => {
    if (blockDemoWrite()) return;
    const normalizedVitals = normalizeVitals(vitalsDraft);

    // Age validation
    const age = calcAge(normalizedVitals.birthday);
    if (age === null) {
      setVitalsEditorError("Enter a valid birthday.");
      return;
    }
    if (age < AGE_MIN || age > AGE_MAX) {
      setVitalsEditorError(`Age must be between ${AGE_MIN} and ${AGE_MAX}.`);
      return;
    }

    // Height validation
    if (!normalizedVitals.height || normalizedVitals.height < HEIGHT_MIN_IN || normalizedVitals.height > HEIGHT_MAX_IN) {
      setVitalsEditorError(`Height must be between 3'0" and 8'0".`);
      return;
    }

    setVitalsEditorError(null);
    const previousWeight = safeNumber(vitals.currentWeightLbs);
    const nextWeight = safeNumber(normalizedVitals.currentWeightLbs);
    const weightChanged = (previousWeight ?? null) !== (nextWeight ?? null);

    if (weightChanged) {
      setPendingVitalsSave(normalizedVitals);
      setShowMetabolismWeightConfirm(true);
      return;
    }

    setVitals(normalizedVitals);
    setActiveModal(null);
    await persistAll(entriesAsc, normalizedVitals, forecast, sprints);
  };

  const applyVitalsSaveWithTodayWeight = async (normalizedVitals: ReturnType<typeof normalizeVitals>) => {
    const nextWeight = safeNumber(normalizedVitals.currentWeightLbs);
    if (nextWeight === null) {
      setVitals(normalizedVitals);
      await persistAll(entriesAsc, normalizedVitals, forecast, sprints);
      return;
    }

    const nextMap = new Map(entriesAsc.map((entry) => [entry.date, entry]));
    const existingToday = nextMap.get(todayYmd) ?? null;
    nextMap.set(todayYmd, {
      date: todayYmd,
      weight: nextWeight,
      caloriesIn: existingToday?.caloriesIn ?? null,
      totalBurned: existingToday?.totalBurned ?? null,
      activeCalories: existingToday?.activeCalories ?? null,
      netCalories: existingToday?.netCalories ?? null,
      bmr: existingToday?.bmr ?? null,
      tdeeSource: existingToday?.tdeeSource ?? "unknown",
      theoreticalWeight: nextWeight,
      updatedAt: new Date().toISOString(),
      activeCaloriesSource: existingToday?.activeCaloriesSource ?? null,
      totalBurnedSource: existingToday?.totalBurnedSource ?? null,
      activeCaloriesRejectedReason: existingToday?.activeCaloriesRejectedReason ?? null,
      totalBurnedRejectedReason: existingToday?.totalBurnedRejectedReason ?? null,
    });
    const reconciled = reconcileEntriesDerivedMetrics(
      Array.from(nextMap.values()).sort((a, b) => (a.date < b.date ? -1 : 1)),
      normalizedVitals,
    );
    const nextEntries = recalcTheoretical(reconciled);
    setVitals(normalizedVitals);
    setEntries(nextEntries);
    await persistAll(nextEntries, normalizedVitals, forecast, sprints);
    if (healthKitEnabled) {
      void writeWeightToHealth(todayYmd, nextWeight * 0.453592);
    }
  };

  const cancelMetabolismWeightConfirm = () => {
    setShowMetabolismWeightConfirm(false);
    setPendingVitalsSave(null);
  };

  const confirmMetabolismWeightConfirm = async () => {
    if (!pendingVitalsSave) {
      setShowMetabolismWeightConfirm(false);
      return;
    }
    const savePayload = pendingVitalsSave;
    setShowMetabolismWeightConfirm(false);
    setPendingVitalsSave(null);
    setActiveModal(null);
    await applyVitalsSaveWithTodayWeight(savePayload);
  };
  const openNewSprintModal = () => {
    setNewSprintType("new");
    setNewSprintName("");
    setNewSprintStartDate(suggestedSprintStartDate);
    setNewSprintEndDate("");
    setNewSprintForecastEndDate("");
    setNewSprintStartingWeight("");
    setNewSprintStartWeightSource(null);
    setNewSprintTargetWeight(safeNumber(forecast.targetWeight) ?? "");
    setNewSprintTargetDate(isYmd(forecast.targetDate) ? forecast.targetDate : "");
    setNewSprintInputMode(forecast.inputMode);
    setNewSprintAssumedNetCalories(safeNumber(forecast.assumedNetCalories) ?? 0);
    setNewSprintNetDraft(String(safeNumber(forecast.assumedNetCalories) ?? 0));
    setNewSprintIntakeDraft(String(Math.max(0, safeNumber(forecast.assumedNetCalories) ?? 0)));
    setNewSprintColor(pickNextSprintColor(sprints));
    setShowNewSprintStartDateHelp(false);
    setShowNewSprintStartWeightHelp(false);
    setNewSprintPlanMethod("byDate");
    setNewSprintPresetRate(null);
    setNewSprintPresetClamped(false);
    setCalFloorConfirmData(null);
    setCalFloorChoice("recommended");
    setCalFloorEditContext(null);
    setNewSprintGoalType(null);
    setActiveModal("newSprint");
  };

  useEffect(() => {
    if (!calcPlanNotice) return;
    const t = setTimeout(() => setCalcPlanNotice(null), 2800);
    return () => clearTimeout(t);
  }, [calcPlanNotice]);

  useEffect(() => {
    if (!calcPlanError) return;
    const t = setTimeout(() => setCalcPlanError(null), 3200);
    return () => clearTimeout(t);
  }, [calcPlanError]);

  useEffect(() => {
    if (!trendsRangeMessage) return;
    const t = setTimeout(() => setChartsRangeMessage(null), 3500);
    return () => clearTimeout(t);
  }, [trendsRangeMessage]);

  useEffect(() => {
    if (!sprintSaveToast) return;
    const t = setTimeout(() => setSprintSaveToast(null), 2600);
    return () => clearTimeout(t);
  }, [sprintSaveToast]);

  // ---- iOS Widget data sync ----

  // Clear widget data when user is not logged in (covers app-start with no session
  // and any sign-out that doesn't go through handleSupabaseSignOut).
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    if (userId) return;
    void SprintWidget.clearWidgetData();
  }, [userId]);

  useEffect(() => {
    if (!Capacitor.isNativePlatform() || !recalcData || !activeSprint || !userId) return;

    const {
      status,
      currentWeight,
      targetWeight,
      targetDate,
      remainingDays,
      plannedWeightToday,
      goalDirection,
      currentPlanNet,
    } = recalcData;

    const sw = Number.isFinite(startWeight) ? Number(startWeight) : null;
    const tw = Number.isFinite(targetWeight) ? targetWeight : null;
    const cw = Number.isFinite(currentWeight) ? currentWeight : null;

    // Determine mode — matches main app sprintMode detection (weight proximity OR near-maintenance calories)
    const netNearMaintenance = Number.isFinite(currentPlanNet) && Math.abs(currentPlanNet) < 30;
    const sprintMode: "cut" | "bulk" | "maintain" =
      (sw !== null && tw !== null && Math.abs(tw - sw) < 0.25) || netNearMaintenance
        ? "maintain"
        : goalDirection === "cut"
          ? "cut"
          : "bulk";

    // For maintain sprints, status is always on-track (recalcData.status can bleed "behind"
    // when goalDirection != "maintain" due to strict equality vs the 0.25 lb threshold).
    const widgetStatus = sprintMode === "maintain" ? "on-track" : status;

    // Align copy — alignColorHex must always be a 6-digit hex string (not rgba)
    let alignCopy = "Tracking closely";
    let alignColorHex = "#d4d4d8"; // near-white hex for "on-track"
    if (sprintMode === "maintain" && cw !== null && tw !== null) {
      // Maintain zone logic mirrors the main app (WeightTracker.tsx ~line 8383)
      const delta = cw - tw;
      const goodRange  = Math.max(1.5, Math.min(6,  tw * 0.02));
      const watchRange = Math.max(2.5, Math.min(10, tw * 0.035));
      if (Math.abs(delta) <= goodRange) {
        alignCopy = "Within range";             alignColorHex = "#86efac";
      } else if (delta > 0 && delta <= watchRange) {
        alignCopy = "Trending high";            alignColorHex = "#f59e0b";
      } else if (delta < 0 && Math.abs(delta) <= watchRange) {
        alignCopy = "Trending low";             alignColorHex = "#f59e0b";
      } else if (delta > 0) {
        alignCopy = "Above maintenance range";  alignColorHex = "#ef4444";
      } else {
        alignCopy = "Below maintenance range";  alignColorHex = "#ef4444";
      }
    } else if (sprintMode !== "maintain" && cw !== null && plannedWeightToday !== null) {
      const delta = cw - plannedWeightToday;
      const tol = Math.max(0.1, Math.abs(plannedWeightToday) * 0.003);
      const derived =
        goalDirection === "cut"
          ? delta < -tol ? "ahead" : delta > tol ? "behind" : "on-track"
          : delta > tol ? "ahead" : delta < -tol ? "behind" : "on-track";
      if (derived === "ahead") { alignCopy = `▲ ${Math.abs(delta).toFixed(1)} lb ahead`; alignColorHex = "#86efac"; }
      else if (derived === "behind") { alignCopy = `▼ ${Math.abs(delta).toFixed(1)} lb behind`; alignColorHex = "#ef4444"; }
    }

    // Chip values
    const lbsToGoLabel = tw !== null && cw !== null
      ? `${Math.abs(tw - cw).toFixed(1)} lb to go` : "— lb to go";
    const daysLeftLabel = Number.isFinite(remainingDays)
      ? `${Math.max(0, remainingDays)} days left` : "— days left";
    const netChangeLbRaw = sw !== null && cw !== null ? cw - sw : null;
    const netChangeLb = netChangeLbRaw !== null ? (Math.abs(netChangeLbRaw) < 0.05 ? 0 : netChangeLbRaw) : null;
    const netChangeLabel = netChangeLb !== null
      ? `${netChangeLb >= 0 ? "+" : "−"}${Math.abs(netChangeLb).toFixed(1)} lb net` : "— lb net";

    // Progress bar percentages
    const clampPct = (p: number) => Math.min(Math.max(p, 0), 100);
    // barRange hoisted so sprintLbsPerDay (below) can reference it in both modes
    const barRange = sw !== null && tw !== null ? tw - sw : 0;
    let plannedPct: number | null;
    let actualPct: number | null;
    let gapStartPct: number | null;
    let gapWidthPct: number;
    let mGoodStartPct: number | null = null;
    let mGoodEndPct: number | null = null;

    if (sprintMode === "maintain" && cw !== null && tw !== null) {
      // Symmetric bar: 50% = target weight, scaled by ±(watchRange × 1.5).
      // Mirrors the main app's maintainNowPct formula (lines 8325-8328).
      const watchRange = Math.max(2.5, Math.min(10, tw * 0.035));
      const barHalfRange = watchRange * 1.5;
      const delta = cw - tw;
      plannedPct  = 50;
      actualPct   = clampPct(50 + (delta / barHalfRange) * 50);
      gapStartPct = Math.min(plannedPct, actualPct);
      gapWidthPct = Math.abs(actualPct - plannedPct);
      // Good-range band percentages for the maintain bar (mirrors main app mGoodStartPct/mGoodEndPct)
      const goodRange = Math.max(1.5, Math.min(6, tw * 0.02));
      mGoodStartPct = clampPct(50 - (goodRange / barHalfRange) * 50);
      mGoodEndPct   = clampPct(50 + (goodRange / barHalfRange) * 50);
    } else {
      const pctFor = (w: number | null) =>
        w !== null && sw !== null && Math.abs(barRange) > 1e-6
          ? clampPct(((w - sw) / barRange) * 100) : null;
      plannedPct  = pctFor(plannedWeightToday);
      actualPct   = pctFor(cw);
      gapStartPct = plannedPct !== null && actualPct !== null ? Math.min(plannedPct, actualPct) : null;
      gapWidthPct = plannedPct !== null && actualPct !== null ? Math.abs(actualPct - plannedPct) : 0;
    }

    // Summary chip labels
    const startLabel = `Start ${isYmd(sprintStartEffective) ? formatMD(sprintStartEffective) : "—"} · ${sw !== null ? `${sw.toFixed(1)} lbs` : "—"}`;
    const goalLabel  = `Goal ${isYmd(targetDate) ? formatMD(targetDate) : "—"} · ${tw !== null ? `${tw.toFixed(1)} lbs` : "—"}`;
    const nowLabel   = `Now ${cw !== null ? `${cw.toFixed(1)} lbs` : "—"}`;
    const deltaLabel = netChangeLb !== null
      ? `Δ ${netChangeLb >= 0 ? "+" : "−"}${Math.abs(netChangeLb).toFixed(1)} lbs`
      : "Δ —";

    // Days early/late label ("10d early", "3d late", "" when on-track)
    const sprintDurationDays = isYmd(activeSprint.startDate) && isYmd(targetDate)
      ? Math.max(1, daysBetweenInclusiveStartExclusiveEnd(activeSprint.startDate, targetDate) ?? 1)
      : null;
    const sprintLbsPerDay = sprintDurationDays && Math.abs(barRange) > 0
      ? barRange / sprintDurationDays : null;
    const deltaFromPlan = cw !== null && plannedWeightToday !== null ? cw - plannedWeightToday : null;
    const rawDaysRel = sprintLbsPerDay && deltaFromPlan !== null && Math.abs(sprintLbsPerDay) > 0.001
      ? Math.round(deltaFromPlan / sprintLbsPerDay) : null;
    // positive rawDaysRel → finishing early; negative → finishing late
    const daysEarlyLateLabel = rawDaysRel !== null && Math.abs(rawDaysRel) >= 1 && status !== "on-track"
      ? (rawDaysRel > 0 ? `${rawDaysRel}d early` : `${Math.abs(rawDaysRel)}d late`)
      : "";

    // Progress bar endpoint fields for Sprint Progress widget
    const startDateShort = isYmd(sprintStartEffective) ? formatMD(sprintStartEffective) : "—";
    const goalDateShort  = isYmd(targetDate) ? formatMD(targetDate) : "—";
    const barStartWeight = sw;

    if (import.meta.env.DEV) console.log('[SprintWidget] sending update — sprint:', activeSprint?.id, 'status:', widgetStatus, 'mode:', sprintMode);
    SprintWidget.updateWidgetData({
      sprintName: (activeSprint.name || "Active Sprint").trim(),
      status: widgetStatus,
      mode: sprintMode,
      displayWeight: cw ?? undefined,
      goalWeight: tw ?? undefined,
      goalDateLabel: isYmd(targetDate) ? formatMD(targetDate) : "—",
      alignCopy,
      alignColorHex,
      netChangeLabel,
      lbsToGoLabel,
      daysLeftLabel,
      plannedPct,
      actualPct,
      gapStartPct,
      gapWidthPct,
      startLabel,
      goalLabel,
      nowLabel,
      deltaLabel,
      daysEarlyLateLabel,
      startDateShort,
      goalDateShort,
      barStartWeight: barStartWeight ?? undefined,
      mGoodStartPct: sprintMode === "maintain" ? mGoodStartPct : undefined,
      mGoodEndPct:   sprintMode === "maintain" ? mGoodEndPct   : undefined,
    }).then(() => {
      if (import.meta.env.DEV) console.log('[SprintWidget] updateWidgetData OK');
    }).catch((err: unknown) => {
      console.error('[SprintWidget] updateWidgetData FAILED:', err);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recalcData, activeSprint?.id, startWeight]);

  // ---- Trends page presets ----

  const setRangePreset = (preset) => {
    if (!entriesAsc.length) return;
    const end = todayYmd;
    const start = getRangePresetStart(preset, end, entriesAsc[0].date);
    if (!start) return;

    setMovingAverageMode("auto");
    commitChartRange(start, end);
  };

  // ---- Render helpers ----

  const latestLoggedWeightEntry = useMemo(() => getLatestLoggedWeight(entriesAsc), [entriesAsc]);
  const currentSprintWeight = Number.isFinite(recalcData?.currentWeight)
    ? Number(recalcData?.currentWeight)
    : (latestLoggedWeightEntry?.weight ?? null);
  const currentSprintNetChange = Number.isFinite(startWeight) && Number.isFinite(currentSprintWeight)
    ? currentSprintWeight - startWeight
    : null;
  const currentSprintStatus = recalcData?.status === "ahead"
    ? "Ahead"
    : recalcData?.status === "behind"
      ? "Behind"
      : "On Track";
  const currentSprintStatusTone = recalcData?.status === "ahead"
    ? { border: "rgba(34,197,94,0.36)", bg: "rgba(34,197,94,0.14)", text: "#bbf7d0" }
    : recalcData?.status === "behind"
      ? { border: "rgba(239,68,68,0.36)", bg: "rgba(239,68,68,0.14)", text: "#fecaca" }
      : { border: "rgba(59,130,246,0.36)", bg: "rgba(59,130,246,0.12)", text: "#dbeafe" };
  const currentSprintPhase = Number.isFinite(startWeight) && Number.isFinite(forecast.targetWeight)
    ? Math.abs(Number(forecast.targetWeight) - Number(startWeight)) < 0.25
      ? "Maintain"
      : Number(forecast.targetWeight) < Number(startWeight)
        ? "Cut"
        : "Bulk"
    : null;

  const jumpToSprintSection = () => {
    setCurrentPage("tracker");
    setTimeout(() => {
      if (sprintSectionRef.current && typeof sprintSectionRef.current.scrollIntoView === "function") {
        sprintSectionRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, 50);
  };

  const sprintSummaryChipStyle: React.CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    gap: "0.24rem",
    padding: "0.26rem 0.46rem",
    borderRadius: 11,
    border: "1px solid rgba(255,255,255,0.12)",
    background: "rgba(255,255,255,0.04)",
    fontSize: "0.72rem",
    lineHeight: 1.2,
    whiteSpace: "nowrap",
    flex: "0 0 auto",
    minWidth: 0,
  };

  const brandLogoStyle: React.CSSProperties = {
    width: 44,
    height: 44,
    borderRadius: 12,
    objectFit: "cover",
    boxShadow: "0 10px 24px rgba(0,0,0,0.35)",
    border: "1px solid rgba(255,255,255,0.14)",
    flexShrink: 0,
  };

  const header = (
    <div style={{ ...cardStyle, padding: "1.15rem 1.2rem", borderRadius: 18, display: "none" }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: "0.95rem", alignItems: "start" }}>
        <div style={{ minWidth: 0, display: "flex", gap: "0.75rem", alignItems: "center" }}>
          <img src="/logo.png" alt="WeightPlanner Pro" style={brandLogoStyle} />
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: "2rem", fontWeight: 900, color: COLORS.blue, lineHeight: 1.05 }}>{APP_NAME}</div>
            <div style={{ marginTop: "0.34rem", color: "rgba(255,255,255,0.68)", fontSize: "0.9rem", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{APP_TAGLINE}</div>
            <div style={{ marginTop: "0.14rem", color: "rgba(255,255,255,0.44)", fontSize: "0.75rem" }}>{APP_DOMAIN}</div>
          </div>
        </div>

        <div style={{ display: "grid", justifyContent: "flex-end", minWidth: 0, width: "100%", gap: "0.5rem" }}>
          {isDemoMode ? (
            <div style={{ display: "flex", gap: "0.45rem", justifyContent: "flex-end", flexWrap: "wrap" }}>
              <span style={{ ...sprintSummaryChipStyle, borderColor: "rgba(250,204,21,0.45)", background: "rgba(250,204,21,0.18)", color: "#fde68a", fontWeight: 800 }}>Demo — Not Saved</span>
              <select
                value={demoProfileId}
                onChange={(e) => enterDemoMode(e.target.value as DemoProfileId, demoEntrySource)}
                style={{ ...btnBase, background: "rgba(255,255,255,0.06)", color: "white", border: "1px solid rgba(255,255,255,0.16)" }}
              >
                <option value="cut_ahead">Cut (Ahead of schedule)</option>
                <option value="bulk_lean">Lean Bulk</option>
                <option value="maintain">Maintenance</option>
              </select>
              <button style={{ ...btnBase, background: "rgba(239,68,68,0.16)", borderColor: "rgba(239,68,68,0.34)", color: "#fecaca" }} onClick={() => { void exitDemoMode(); }}>Exit Demo</button>
            </div>
          ) : null}
          <button
            type="button"
            aria-label="View sprint details"
            onClick={jumpToSprintSection}
            style={{
              ...cardStyle,
              width: "fit-content",
              maxWidth: "100%",
            maxInlineSize: "100%",
              padding: "0.48rem 0.56rem",
              borderRadius: 12,
              display: "grid",
              gap: "0.38rem",
              textAlign: "left",
              cursor: "pointer",
              border: "1px solid rgba(255,255,255,0.08)",
              background: "rgba(255,255,255,0.02)",
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(255,255,255,0.05)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(255,255,255,0.02)"; }}
          >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "0.4rem", flexWrap: "wrap" }}>
              <div style={{ display: "inline-flex", alignItems: "center", gap: "0.28rem", flexWrap: "wrap" }}>
                <span style={{ fontWeight: 800, color: "rgba(255,255,255,0.9)", fontSize: "0.84rem", whiteSpace: "nowrap" }}>Current Sprint</span>
                <span style={{ ...sprintSummaryChipStyle, padding: "0.22rem 0.5rem", borderColor: currentSprintStatusTone.border, background: currentSprintStatusTone.bg, color: currentSprintStatusTone.text, fontWeight: 700 }}>{currentSprintStatus}</span>
                {currentSprintPhase ? (
                  <span style={{ ...sprintSummaryChipStyle, padding: "0.22rem 0.48rem", borderColor: "rgba(255,255,255,0.2)", background: "rgba(255,255,255,0.02)", color: "rgba(255,255,255,0.75)", fontWeight: 650 }}>{currentSprintPhase}</span>
                ) : null}
              </div>
              <span style={{ marginLeft: "auto", fontSize: "0.75rem", color: "rgba(255,255,255,0.58)", fontWeight: 700, whiteSpace: "nowrap" }}>View →</span>
            </div>

            <div style={{ display: "flex", flexWrap: "wrap", gap: "0.4rem", justifyContent: "flex-start", alignItems: "center", minWidth: 0 }}>
              <span style={sprintSummaryChipStyle}><span style={{ color: "rgba(255,255,255,0.58)" }}>Start</span><span style={{ color: "rgba(255,255,255,0.9)", fontWeight: 700 }}>{isYmd(sprintStartEffective) ? formatMD(sprintStartEffective) : "—"} · {Number.isFinite(startWeight) ? `${Number(startWeight).toFixed(1)} lbs` : "—"}</span></span>
              <span style={sprintSummaryChipStyle}><span style={{ color: "rgba(255,255,255,0.58)" }}>Goal</span><span style={{ color: "rgba(255,255,255,0.9)", fontWeight: 700 }}>{isYmd(forecast.targetDate) ? formatMD(forecast.targetDate) : "—"} · {Number.isFinite(forecast.targetWeight) ? `${Number(forecast.targetWeight).toFixed(1)} lbs` : "—"}</span></span>
              <span style={sprintSummaryChipStyle}><span style={{ color: "rgba(255,255,255,0.58)" }}>Now</span><span style={{ color: "rgba(255,255,255,0.9)", fontWeight: 700 }}>{Number.isFinite(currentSprintWeight) ? `${Number(currentSprintWeight).toFixed(1)} lbs` : "—"}</span></span>
              <span style={sprintSummaryChipStyle}><span style={{ color: "rgba(255,255,255,0.58)" }}>Δ</span><span style={{ color: "rgba(255,255,255,0.9)", fontWeight: 700 }}>{Number.isFinite(currentSprintNetChange) ? `${currentSprintNetChange > 0 ? "+" : currentSprintNetChange < 0 ? "−" : ""}${Math.abs(currentSprintNetChange).toFixed(1)} lbs` : "—"}</span></span>
            </div>
          </button>

          {activeModal === "vitals" ? (
            <ModalShell title="Edit vitals" ariaLabel="vitals editor" onClose={closeModal} panelStyle={{ maxWidth: 560, width: "min(560px, calc(100vw - 24px))", maxHeight: "90vh", overflowY: "auto" }}>
              <div style={{ paddingTop: "0.75rem", borderTop: "1px solid rgba(255,255,255,0.08)", display: "grid", gap: "1rem" }}>
                {/* Row 1: Birthday + Height */}
                <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: "0.75rem", alignItems: "start" }}>
                  <div style={{ display: "grid", gap: "0.45rem" }}>
                    <div style={{ color: "rgba(255,255,255,0.75)", fontSize: "0.85rem" }}>Birthday</div>
                    <MobileDateField
                      value={vitalsDraft.birthday}
                      onCommit={(v) => setVitalsDraft((s) => ({ ...s, birthday: v }))}
                      style={{
                        width: "100%",
                        boxSizing: "border-box" as const,
                        padding: "0.75rem 0.85rem",
                        borderRadius: 12,
                        border: "1px solid rgba(255,255,255,0.14)",
                        background: "rgba(255,255,255,0.04)",
                        color: "white",
                        outline: "none",
                        minHeight: 50,
                      }}
                    />
                    <div style={{ fontSize: "0.78rem", color: "rgba(255,255,255,0.45)" }}>
                      Age: {calcAge(vitalsDraft.birthday) ?? "—"}
                    </div>
                  </div>
                  {/* Height: ft + in */}
                  <div style={{ display: "grid", gap: "0.45rem" }}>
                    <div style={{ color: "rgba(255,255,255,0.75)", fontSize: "0.85rem" }}>Height</div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.5rem" }}>
                      <div style={{ position: "relative" }}>
                        <input
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          value={vitalsDraft.height !== null && vitalsDraft.height !== undefined ? String(Math.floor(Number(vitalsDraft.height) / 12)) : ""}
                          onChange={(e) => {
                            const ft = safeNumber(e.target.value) ?? 0;
                            const ins = Number(vitalsDraft.height ?? 0) % 12;
                            setVitalsDraft((s) => ({ ...s, height: ft * 12 + ins }));
                          }}
                          placeholder="0"
                          style={{ width: "100%", padding: "0.75rem 2rem 0.75rem 0.85rem", borderRadius: 12, border: "1px solid rgba(255,255,255,0.14)", background: "rgba(255,255,255,0.04)", color: "white", outline: "none", minHeight: 50, boxSizing: "border-box" }}
                        />
                        <span style={{ position: "absolute", right: "0.7rem", top: "50%", transform: "translateY(-50%)", color: "rgba(255,255,255,0.4)", fontSize: "0.82rem", pointerEvents: "none", userSelect: "none" }}>ft</span>
                      </div>
                      <div style={{ position: "relative" }}>
                        <input
                          type="text"
                          inputMode="decimal"
                          pattern="[0-9]*"
                          value={vitalsDraft.height !== null && vitalsDraft.height !== undefined ? String(Math.round((Number(vitalsDraft.height) % 12) * 10) / 10) : ""}
                          onChange={(e) => {
                            const ins = Math.min(safeNumber(e.target.value) ?? 0, 11.9);
                            const ft = Math.floor(Number(vitalsDraft.height ?? 0) / 12);
                            setVitalsDraft((s) => ({ ...s, height: ft * 12 + ins }));
                          }}
                          placeholder="0"
                          style={{ width: "100%", padding: "0.75rem 2rem 0.75rem 0.85rem", borderRadius: 12, border: "1px solid rgba(255,255,255,0.14)", background: "rgba(255,255,255,0.04)", color: "white", outline: "none", minHeight: 50, boxSizing: "border-box" }}
                        />
                        <span style={{ position: "absolute", right: "0.7rem", top: "50%", transform: "translateY(-50%)", color: "rgba(255,255,255,0.4)", fontSize: "0.82rem", pointerEvents: "none", userSelect: "none" }}>in</span>
                      </div>
                    </div>
                  </div>
                </div>
                <div style={{ display: "grid", gap: "0.45rem" }}>
                  <div style={{ color: "rgba(255,255,255,0.75)", fontSize: "0.85rem" }}>Weight</div>
                  <div style={{ position: "relative", maxWidth: 220 }}>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={vitalsDraft.currentWeightLbs ?? ""}
                      onChange={(e) => setVitalsDraft((s) => ({ ...s, currentWeightLbs: e.target.value }))}
                      placeholder="e.g. 185.4"
                      style={{ width: "100%", padding: "0.75rem 2.6rem 0.75rem 0.85rem", borderRadius: 12, border: "1px solid rgba(255,255,255,0.14)", background: "rgba(255,255,255,0.04)", color: "white", outline: "none", minHeight: 50, boxSizing: "border-box" }}
                    />
                    <span style={{ position: "absolute", right: "0.7rem", top: "50%", transform: "translateY(-50%)", color: "rgba(255,255,255,0.4)", fontSize: "0.82rem", pointerEvents: "none", userSelect: "none" }}>lbs</span>
                  </div>
                </div>
                {/* Row 2: Sex (inline, compact) */}
                <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
                  <div style={{ color: "rgba(255,255,255,0.75)", fontSize: "0.85rem", flexShrink: 0 }}>Sex</div>
                  <div style={{ display: "flex", gap: "0.5rem" }}>
                    {[{ v: "M", label: "Male" }, { v: "F", label: "Female" }].map(({ v, label }) => (
                      <button key={v} type="button" onClick={() => setVitalsDraft((s) => ({ ...s, gender: v }))}
                        style={{ ...btnBase, padding: "0.38rem 1rem", fontSize: "0.85rem", background: vitalsDraft.gender === v ? "rgba(59,130,246,0.22)" : "rgba(255,255,255,0.05)", border: `1px solid ${vitalsDraft.gender === v ? "rgba(59,130,246,0.5)" : "rgba(255,255,255,0.12)"}`, color: vitalsDraft.gender === v ? "#93c5fd" : "rgba(255,255,255,0.72)", fontWeight: vitalsDraft.gender === v ? 700 : 500 }}
                      >{label}</button>
                    ))}
                  </div>
                </div>

                {/* Row 2: Activity level — inline selector */}
                <div style={{ display: "grid", gap: "0.5rem" }}>
                  <div style={{ color: "rgba(255,255,255,0.75)", fontSize: "0.85rem" }}>Activity level</div>
                  <div style={{ display: "grid", gap: "0.3rem" }}>
                    {ACTIVITY_LEVELS.map((opt) => {
                      const sel = String(vitalsDraft.activityLevel ?? DEFAULT_VITALS.activityLevel) === opt.value;
                      return (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => {
                            const chosen = getActivityOptionByLevel(opt.value) ?? opt;
                            setVitalsDraft((prev) => ({ ...prev, activityLevel: chosen.value, activityFactor: chosen.factor }));
                          }}
                          style={{
                            border: sel ? "1px solid rgba(56,189,248,0.45)" : "1px solid rgba(255,255,255,0.08)",
                            borderRadius: 10,
                            background: sel ? "rgba(56,189,248,0.07)" : "rgba(255,255,255,0.03)",
                            color: sel ? "rgba(248,250,252,0.95)" : "rgba(229,231,235,0.65)",
                            padding: "0.55rem 0.85rem",
                            display: "grid",
                            gap: "0.2rem",
                            textAlign: "left",
                            width: "100%",
                            cursor: "pointer",
                          }}
                        >
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <span style={{ fontSize: "0.88rem", fontWeight: sel ? 650 : 500 }}>{opt.label}</span>
                            <span style={{ fontSize: "0.75rem", color: "rgba(255,255,255,0.35)", fontVariantNumeric: "tabular-nums" }}>{opt.factor}</span>
                          </div>
                          <div style={{ fontSize: "0.76rem", color: sel ? "rgba(255,255,255,0.52)" : "rgba(255,255,255,0.32)", fontWeight: 400, marginTop: 1 }}>
                            {ACTIVITY_DESCS[opt.value] ?? ""}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Buttons */}
                {vitalsEditorError ? (
                  <div style={{ color: "#f87171", fontSize: "0.83rem", marginBottom: "0.25rem" }}>
                    {vitalsEditorError}
                  </div>
                ) : null}
                <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem" }}>
                  <button style={{ ...btnBase, background: "rgba(255,255,255,0.06)", color: "white" }} onClick={saveVitalsEditor}>
                    Save
                  </button>
                  <button style={{ ...btnBase, background: "rgba(255,255,255,0.08)", color: "white" }} onClick={closeModal}>
                    Cancel
                  </button>
                </div>
              </div>
            </ModalShell>
          ) : null}

          {activeModal === "sprint" ? (
            <ModalShell title="Sprint Settings" ariaLabel="sprint settings editor" onClose={closeModal} panelStyle={{ width: "min(660px, calc(100vw - 32px))", maxHeight: "90vh", overflowY: "auto" }}>
              <div style={{ display: "grid", gap: isMobile ? "0.45rem" : "0.7rem", marginTop: "0", paddingTop: isMobile ? "0.35rem" : "0.55rem", borderTop: "1px solid rgba(255,255,255,0.08)" }}>
              <div style={{ display: "grid", gap: isMobile ? "0.3rem" : "0.4rem" }}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: "0.55rem", alignItems: "start" }}>
                  <div style={{ display: "flex", gap: "0.55rem", alignItems: "flex-start" }}>
                    <div style={{ flex: 1 }}>
                      <Input label="Sprint name" value={archiveName} onChange={setArchiveName} disabled={isArchivedOrViewer} controlHeight={SPRINT_DATE_FIELD_HEIGHT} />
                    </div>
                    {(activeSprint ?? (forecast.activeSprintId ? sprints.find((s) => s.id === forecast.activeSprintId) : null)) ? (
                      <SprintColorDots
                        label="Sprint color"
                        value={sprintDraft?.color ?? (activeSprint ?? sprints.find((s) => s.id === forecast.activeSprintId))?.color ?? SPRINT_PALETTE[0]}
                        onChange={(nextColor) => setSprintDraft((prev) => (prev ? { ...prev, color: nextColor } : prev))}
                      />
                    ) : null}
                  </div>
                  <div style={createSprintFieldWrapStyle}>
                    <div style={createSprintLabelRowStyle}>Sprint type</div>
                    <SprintGoalTypeToggle
                      value={(sprintDraft?.goalType ?? deriveSprintGoalType(safeNumber(sprintDraft?.startingWeight), safeNumber(sprintDraft?.targetWeight))) as SprintGoalType}
                      onChange={(nextType) => setSprintDraft((prev) => {
                        if (!prev) return prev;
                        const nextTarget = normalizeSprintWeightsForGoalType({
                          goalType: nextType,
                          startWeight: safeNumber(prev.startingWeight),
                          targetWeight: safeNumber(prev.targetWeight),
                          defaultDeltaLbs: 10,
                        });
                        return { ...prev, goalType: nextType, targetWeight: nextTarget !== null ? String(nextTarget) : prev.targetWeight };
                      })}
                    />
                  </div>
                  <TriFieldRow
                    mobile={isMobile}
                    leftLabel={<div style={{ ...createSprintLabelRowStyle, marginBottom: 0 }}>Start weight</div>}
                    rightLabel={<div style={{ ...createSprintLabelRowStyle, marginBottom: 0 }}>Target weight</div>}
                    leftField={<WeightInputWithUnit value={sprintDraft?.startingWeight ?? sprintAnchor.anchorWeight ?? ""} onChange={(e) => { setSprintDraft((prev) => (prev ? { ...prev, startingWeight: e.target.value } : prev)); setSprintStartWeightSource((prev) => (prev === "fromLog" ? "edited" : prev)); }} style={{ width: "100%", padding: "0.55rem 0.65rem", borderRadius: 12, border: "1px solid rgba(255,255,255,0.14)", background: "rgba(255,255,255,0.04)", color: "white", outline: "none", minHeight: SPRINT_DATE_FIELD_HEIGHT }} />}
                    rightField={<WeightInputWithUnit value={sprintDraft?.targetWeight ?? forecast.targetWeight ?? ""} onChange={(e) => setSprintDraft((prev) => {
                      if (!prev) return prev;
                      const nextTarget = safeNumber(e.target.value);
                      const nextGoalType = deriveSprintGoalType(safeNumber(prev.startingWeight), nextTarget);
                      return { ...prev, targetWeight: e.target.value, goalType: nextGoalType };
                    })} style={{ width: "100%", padding: "0.55rem 0.65rem", borderRadius: 12, border: "1px solid rgba(255,255,255,0.14)", background: "rgba(255,255,255,0.04)", color: "white", outline: "none", minHeight: SPRINT_DATE_FIELD_HEIGHT }} />}
                    chip={<DeltaChip startWeight={sprintDraft?.startingWeight ?? sprintAnchor.anchorWeight} endWeight={sprintDraft?.targetWeight ?? forecast.targetWeight} />}
                  />
                  {/* Planning method toggle */}
                  <div style={createSprintFieldWrapStyle}>
                    <div style={createSprintLabelRowStyle}>Planning method</div>
                    <PlanMethodToggle value={sprintPlanMethod} onChange={(v) => {
                      setSprintPlanMethod(v);
                      if (v === "byDate") { setSprintPresetRate(null); setSprintPresetClamped(false); }
                    }} />
                  </div>
                  {sprintPlanMethod === "byPace" && (() => {
                    const _sw = safeNumber(sprintDraft?.startingWeight ?? sprintAnchor.anchorWeight);
                    const _tw = safeNumber(sprintDraft?.targetWeight ?? forecast.targetWeight);
                    if (_sw === null || _tw === null) return null;
                    const _presets = getRatePresetOptions({ startWeight: _sw, targetWeight: _tw });
                    if (!_presets.length) return null;
                    const _dir = _tw < _sw ? "Cut" : _tw > _sw ? "Bulk" : "Pace";
                    return (
                      <div style={createSprintFieldWrapStyle}>
                        <div style={createSprintLabelRowStyle}>{_dir === "Pace" ? "Pace" : `${_dir} Pace`}</div>
                        <RatePresetChips presets={_presets} selectedRate={sprintPresetRate} onSelect={(rate) => setSprintPresetRate(rate)} detailed showNegative={_tw < _sw} compact={isMobile} />
                        {sprintPresetClamped && (
                          <div style={{ fontSize: "0.75rem", color: "rgba(251,191,36,0.9)" }}>
                            Target date adjusted to stay within allowed sprint duration.
                          </div>
                        )}
                      </div>
                    );
                  })()}
                  <div style={{ display: "grid", gap: "0.45rem" }}>
                    <TriFieldRow
                      mobile={isMobile}
                      leftLabel={<div style={{ ...createSprintLabelRowStyle, marginBottom: 0, whiteSpace: "nowrap" }}>Start date</div>}
                      rightLabel={<div style={{ ...createSprintLabelRowStyle, marginBottom: 0 }}>{sprintPlanMethod === "byPace" ? "End date" : "Target date"}</div>}
                      leftField={(
                        <MobileDateField
                          value={sprintDraft?.startDate ?? activeSprint?.startDate ?? sprintStartDraft}
                          onCommit={(v) => setSprintDraft((prev) => (prev ? { ...prev, startDate: v } : prev))}
                          style={{ width: "100%", padding: "0.55rem 0.65rem", borderRadius: 12, border: "1px solid rgba(255,255,255,0.14)", background: "rgba(255,255,255,0.04)", color: "white", outline: "none", minHeight: SPRINT_DATE_FIELD_HEIGHT }}
                        />
                      )}
                      rightField={(
                        <MobileDateField
                          value={sprintDraft?.targetDate ?? getSprintEditorTargetDate()}
                          onCommitStart={() => { if (sprintPlanMethod === "byPace") { setSprintPlanMethod("byDate"); setSprintPresetRate(null); setSprintPresetClamped(false); } }}
                          onCommit={(v) => setSprintDraft((prev) => (prev ? { ...prev, targetDate: v } : prev))}
                          style={{ width: "100%", padding: "0.55rem 0.65rem", borderRadius: 12, border: "1px solid rgba(255,255,255,0.14)", background: "rgba(255,255,255,0.04)", color: "white", outline: "none", minHeight: SPRINT_DATE_FIELD_HEIGHT, opacity: sprintPlanMethod === "byPace" ? 0.7 : 1 }}
                        />
                      )}
                      chip={<DurationChip startDate={sprintDraft?.startDate ?? activeSprint?.startDate ?? sprintStartDraft} endDate={sprintDraft?.targetDate ?? getSprintEditorTargetDate()} />}
                    />
                    {sprintPlanMethod === "byDate" ? (
                      !isMobile ? (
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 86px", gap: "0.28rem 0.55rem" }}>
                          <div />
                          <DurationPresetChips
                            startDate={(sprintDraft?.startDate ?? activeSprint?.startDate ?? sprintStartDraft) || todayYmd}
                            endDate={sprintDraft?.targetDate ?? getSprintEditorTargetDate()}
                            onSelect={(nextDate) => setSprintDraft((prev) => (prev ? { ...prev, targetDate: nextDate } : prev))}
                          />
                          <div />
                        </div>
                      ) : null
                    ) : null}
                  </div>
                </div>
              </div>

              <div style={{ display: "grid", gap: isMobile ? "0.3rem" : "0.4rem" }}>
                  <div style={{ display: "none" }}>
                  <div style={createSprintFieldWrapStyle}>
                    <div style={createSprintLabelRowStyle}>Calorie Plan Type</div>
                    <div style={{ display: "flex", gap: "0.5rem", minHeight: SPRINT_PLAN_CONTROL_HEIGHT }}>
                      <button
                        style={{
                          ...btnBase,
                          flex: 1,
                          minHeight: SPRINT_PLAN_CONTROL_HEIGHT,
                          fontSize: "0.85rem",
                          border: "none",
                          background: (sprintDraft?.inputMode ?? forecast.inputMode) === "net" ? `linear-gradient(135deg, ${COLORS.blue}, ${COLORS.lightBlue})` : "rgba(255,255,255,0.06)",
                          color: "white",
                        }}
                        onClick={() => {
                          if ((sprintDraft?.inputMode ?? forecast.inputMode) === "intake") commitSprintDraftIntake();
                          setSprintDraft((prev) => (prev ? { ...prev, inputMode: "net" } : prev));
                        }}
                      >
                        Net
                      </button>
                      <button
                        style={{
                          ...btnBase,
                          flex: 1,
                          minHeight: SPRINT_PLAN_CONTROL_HEIGHT,
                          fontSize: "0.85rem",
                          border: "none",
                          background: (sprintDraft?.inputMode ?? forecast.inputMode) === "intake" ? `linear-gradient(135deg, ${COLORS.blue}, ${COLORS.lightBlue})` : "rgba(255,255,255,0.06)",
                          color: "white",
                        }}
                        onClick={() => {
                          if ((sprintDraft?.inputMode ?? forecast.inputMode) === "net") commitSprintDraftNet();
                          setSprintDraft((prev) => (prev ? { ...prev, inputMode: "intake" } : prev));
                        }}
                      >
                        Intake
                      </button>
                    </div>
                  </div>

                  <div style={createSprintFieldWrapStyle}>
                    <div style={createSprintLabelRowStyle}>
                      <span>{(sprintDraft?.inputMode ?? forecast.inputMode) === "net" ? "Net calories" : "Target Intake / Day"}</span>
                      <span style={{
                          fontSize: "0.72rem",
                          fontWeight: 700,
                          lineHeight: 1.15,
                          padding: "0.16rem 0.48rem",
                          borderRadius: 999,
                          border: "1px solid rgba(255,255,255,0.2)",
                          background: safeNumber(sprintDraft?.assumedNetCalories) < 0
                            ? "rgba(34,197,94,0.2)"
                            : safeNumber(sprintDraft?.assumedNetCalories) > 0
                              ? "rgba(239,68,68,0.2)"
                              : "rgba(148,163,184,0.2)",
                          color: safeNumber(sprintDraft?.assumedNetCalories) < 0
                            ? "#86efac"
                            : safeNumber(sprintDraft?.assumedNetCalories) > 0
                              ? "#fca5a5"
                              : "#cbd5e1",
                          visibility: (sprintDraft?.inputMode ?? forecast.inputMode) === "net" ? "visible" : "hidden",
                        }}>
                          {safeNumber(sprintDraft?.assumedNetCalories) < 0 ? "Deficit" : safeNumber(sprintDraft?.assumedNetCalories) > 0 ? "Surplus" : "Neutral"}
                        </span>
                      <InfoTooltipIcon
                        ariaLabel="Net calorie help"
                        isOpen={showSprintNetHelp}
                        onToggle={() => setShowSprintNetHelp((v) => !v)}
                        onOpen={() => setShowSprintNetHelp(true)}
                        onClose={() => setShowSprintNetHelp(false)}
                      >
                        Net = intake − burn
                      </InfoTooltipIcon>
                    </div>
                    <TargetCalorieInputGroup
                      controlHeight={SPRINT_PLAN_CONTROL_HEIGHT}
                      mode={sprintDraft?.inputMode ?? forecast.inputMode}
                      currentSignedValue={sprintDraft?.assumedNetCalories ?? forecast.assumedNetCalories}
                      netDraft={sprintDraft?.netDraft ?? forecastNetDraft}
                      intakeDraft={sprintDraft?.intakeDraft ?? forecastIntakeDraft}
                      onNetDraftChange={(v) => setSprintDraft((prev) => (prev ? { ...prev, netDraft: v } : prev))}
                      onIntakeDraftChange={(v) => setSprintDraft((prev) => (prev ? { ...prev, intakeDraft: v } : prev))}
                      onCommitNet={commitSprintDraftNet}
                      onCommitIntake={commitSprintDraftIntake}
                      onSetNegative={() => {
                        const parsed = parseSignedDraftNumber(String(sprintDraft?.netDraft ?? ""));
                        const magnitude = Math.abs(parsed ?? safeNumber(sprintDraft?.assumedNetCalories) ?? 500);
                        const next = -magnitude;
                        setSprintDraft((prev) => (prev ? { ...prev, netDraft: String(next), assumedNetCalories: next } : prev));
                      }}
                      onSetPositive={() => {
                        const parsed = parseSignedDraftNumber(String(sprintDraft?.netDraft ?? ""));
                        const magnitude = Math.abs(parsed ?? safeNumber(sprintDraft?.assumedNetCalories) ?? 500);
                        const next = magnitude;
                        setSprintDraft((prev) => (prev ? { ...prev, netDraft: String(next), assumedNetCalories: next } : prev));
                      }}
                      showSignControlsInNetOnly
                    />
                    <div style={createSprintHelperRowStyle}>
                      {forecast.inputMode === "net" ? "Net = intake − burn" : "\u00A0"}
                    </div>
                    {(sprintDraft?.inputMode ?? forecast.inputMode) === "intake" && Number(sprintDraft?.assumedNetCalories) < 1200 ? (
                      <div style={{ fontSize: "0.75rem", color: "rgba(255,255,255,0.55)" }}>May be aggressive.</div>
                    ) : null}
                  </div>
                  </div>

                {sprintAnchor.anchorSource === "first-in-sprint" && sprintAnchor.anchorDate ? (
                  <div style={{ fontSize: "0.76rem", color: "rgba(255,255,255,0.62)" }}>
                    Using first weigh-in in sprint ({formatMDY(sprintAnchor.anchorDate)}) as start.
                  </div>
                ) : null}

                {sprintDraftProjectionPreview ? (
                  <SprintPlanSummary
                    startWeight={safeNumber(sprintAnchor.anchorWeight)}
                    targetWeight={safeNumber(sprintDraft?.targetWeight ?? forecast.targetWeight)}
                    startDate={sprintDraft?.startDate ?? activeSprint?.startDate ?? sprintStartDraft}
                    targetDate={sprintDraft?.targetDate ?? getSprintEditorTargetDate()}
                    weeklyRate={safeNumber(sprintDraftProjectionPreview.weekly)}
                    requiredNet={sprintDraftEffectiveNetCalories}
                    tdee={(function () {
                      const startWeightForTdee = safeNumber(sprintAnchor.anchorWeight);
                      if (startWeightForTdee === null) return null;
                      const bmrForSummary = calcBMR({ weightLbs: startWeightForTdee, heightIn: vitals.height, gender: vitals.gender, birthday: vitals.birthday });
                      if (bmrForSummary === null) return null;
                      return calcTDEEFromFactor({ bmr: bmrForSummary, factor: vitals.activityFactor ?? forecast.activityFactor });
                    })()}
                    gender={vitals.gender}
                    calorieView={sprintSummaryMode}
                    onCalorieViewChange={setSprintSummaryMode}
                    compact={isMobile}
                  />
                ) : null}

                <div style={{ fontSize: "0.78rem", color: "rgba(255,255,255,0.72)" }}>
                  Calories auto-update from your start weight, target weight, and target date.
                </div>

                {sprintAnchor.anchorSource === "none" ? (
                  <div style={{ marginTop: "0.2rem", fontSize: "0.78rem", color: "rgba(255,255,255,0.78)", display: "flex", gap: "0.6rem", flexWrap: "wrap", alignItems: "center" }}>
                    <span>Add a weigh-in on the sprint start date (or your first sprint day) to calculate a plan.</span>
                    <button
                      type="button"
                      onClick={jumpToSprintStartEntry}
                      style={{
                        border: "none",
                        background: "transparent",
                        color: COLORS.lightBlue,
                        textDecoration: "underline",
                        textUnderlineOffset: "2px",
                        padding: 0,
                        cursor: "pointer",
                        fontWeight: 700,
                      }}
                    >
                      Add sprint start weigh-in
                    </button>
                  </div>
                ) : calcPlanDisabledReason ? (
                  <div style={{ marginTop: "0.2rem", fontSize: "0.78rem", color: "rgba(255,255,255,0.62)" }}>
                    {calcPlanDisabledReason}
                  </div>
                ) : null}

                {calcPlanError ? <div style={{ fontSize: "0.76rem", color: "rgba(248,113,113,0.95)" }}>{calcPlanError}</div> : null}
                {sprintSettingsGuardrails.hardErrors.some((err) => isPaceHardError(err)) ? (
                  <div style={{ fontSize: "0.76rem", color: "rgba(248,113,113,0.98)", display: "grid", gap: "0.12rem" }}>
                    <div>{sprintDraftPaceGuidance.recommendedLine}</div>
                    <div>{sprintDraftPaceGuidance.targetLine}</div>
                  </div>
                ) : null}
                {sprintSettingsGuardrails.hardErrors.filter((err) => !isPaceHardError(err)).length > 0 ? <div style={{ fontSize: "0.76rem", color: "rgba(248,113,113,0.95)" }}>{sprintSettingsGuardrails.hardErrors.filter((err) => !isPaceHardError(err))[0]}</div> : null}
                {filterRedundantPaceWarnings(sprintSettingsGuardrails.softWarnings).length > 0 ? <div style={{ fontSize: "0.75rem", color: "rgba(255,255,255,0.65)" }}>{filterRedundantPaceWarnings(sprintSettingsGuardrails.softWarnings).slice(0, 2).join(" • ")}</div> : null}
                {calcPlanNotice ? <div style={{ fontSize: "0.75rem", color: "rgba(255,255,255,0.55)" }}>{calcPlanNotice}</div> : null}
              </div>

              {activeSprint ? (
                <div style={{ display: "grid", gap: isMobile ? "0.35rem" : "0.6rem" }}>

                  <div style={{ display: "flex", justifyContent: "space-between", gap: "0.75rem", flexWrap: "wrap", alignItems: "stretch" }}>
                    <button
                      style={{
                        ...btnBase,
                        background: "rgba(255,255,255,0.06)",
                        borderColor: "rgba(255,255,255,0.16)",
                        color: "rgba(255,255,255,0.9)",
                        opacity: isArchivedOrViewer ? 0.5 : 1,
                        minHeight: SPRINT_DATE_FIELD_HEIGHT,
                      }}
                      onClick={archiveActiveSprint}
                      disabled={isArchivedOrViewer}
                      title={isArchivedOrViewer ? "Archived sprints are read-only" : "Archive this sprint"}
                    >
                      Archive Today
                    </button>

                    <button
                      style={{
                        ...btnBase,
                        background: "rgba(255,255,255,0.06)",
                        color: "rgba(255,255,255,0.85)",
                        borderColor: "rgba(255,255,255,0.12)",
                        minHeight: SPRINT_DATE_FIELD_HEIGHT,
                      }}
                      onClick={saveSprintEditor}
                    >
                      Save Changes
                    </button>
                  </div>
                </div>
              ) : (
                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                  <button
                    style={{
                      ...btnBase,
                      background: "rgba(255,255,255,0.06)",
                      color: "rgba(255,255,255,0.85)",
                      borderColor: "rgba(255,255,255,0.12)",
                    }}
                    onClick={startSprintFromSettings}
                  >
                    Start Sprint
                  </button>
                </div>
              )}
                </div>
            </ModalShell>
          ) : null}

        </div>
      </div>

      <div style={{ marginTop: "1.25rem", borderTop: "1px solid rgba(255,255,255,0.06)", paddingTop: "1rem" }}>
        {isDemoMode ? (
          <div style={{ marginTop: "0.7rem", color: "#fde68a", fontSize: "0.82rem", borderTop: "1px solid rgba(250,204,21,0.2)", paddingTop: "0.65rem" }}>
            {demoNotice || "Demo sandbox is interactive, but changes are not saved."}
          </div>
        ) : null}

      </div>
    </div>
  );

  // ---- Sprint Status Panel v2 — unified control panel (no separate stats row) ----

  const sprintStatusPanel = (() => {
    if (!activeSprint) return null;
    if (!recalcData) {
      const displayEnd = getSprintDisplayEndDate(activeSprint, { treatAsActive: isTrulyActiveSprint(activeSprint) });
      return (
        <div style={{ ...cardStyle, borderRadius: 18, display: "grid", gap: "0.75rem" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "0.6rem", flexWrap: "wrap" }}>
            <div style={{ fontSize: "0.9rem", color: "rgba(255,255,255,0.66)", fontWeight: 700 }}>Sprint Status</div>
            <Pill label="Active" tone="blue" />
          </div>
          <div style={{ display: "grid", gap: "0.3rem" }}>
            <div style={{ fontSize: "1.05rem", fontWeight: 900, color: "white" }}>{activeSprint.name || "Sprint"}</div>
            <div style={{ color: "rgba(255,255,255,0.7)", fontSize: "0.86rem" }}>
              {formatMDY(activeSprint.startDate)} → {displayEnd ? formatMDY(displayEnd) : "(active)"}
            </div>
            <div style={{ color: "rgba(255,255,255,0.65)", fontSize: "0.8rem" }}>
              Add target weight/date in Sprint Settings to enable full sprint summary metrics.
            </div>
          </div>
          <div>
            <button
              type="button"
              style={{ ...btnBase, background: "rgba(59,130,246,0.18)", borderColor: "rgba(59,130,246,0.45)", color: "#bfdbfe", padding: "0.36rem 0.7rem" }}
              onClick={openSprintEditor}
            >
              Open Sprint Settings
            </button>
          </div>
        </div>
      );
    }

    const {
      status,
      currentWeight,
      targetWeight,
      targetDate,
      remainingDays,
      requiredNetPerDay,
      requiredIntake,
      deltaWeight,
      plannedWeightToday,
      isAggressive,
      projectedDateFromCurrentPlan,
      goalDirection,
      currentPlanNet,
      currentAnchor,
    } = recalcData;

    const lastLoggedAnchor = [...trackerChartData]
      .filter((d) => d.date <= todayYmd && Number.isFinite(d.actual))
      .sort((a, b) => (a.date < b.date ? -1 : 1))
      .pop();

    const movingAvgWeight = Number.isFinite(currentWeight) ? currentWeight : null;
    const latestLoggedWeight = lastLoggedAnchor && Number.isFinite(lastLoggedAnchor.actual) ? Number(lastLoggedAnchor.actual) : null;
    const displayWeight = sprintStatusDisplayMode === "avg"
      ? movingAvgWeight
      : (latestLoggedWeight ?? movingAvgWeight);
    const avgDateLabel = formatMD(currentAnchor?.dateKey ?? todayYmd);
    const latestLogDateLabel = isYmd(lastLoggedAnchor?.date) ? formatMD(lastLoggedAnchor.date) : "—";
    const hasLastLog = latestLoggedWeight !== null;
    const hasMovingAverage = movingAvgWeight !== null;
    const sourceSubtext = sprintStatusDisplayMode === "avg"
      ? (hasMovingAverage ? `Avg • ${avgDateLabel}` : "Avg unavailable")
      : (hasLastLog ? `Logged ${latestLogDateLabel}` : "No weigh-ins yet");
    const secondarySourceSubtext = sprintStatusDisplayMode === "avg"
      ? (hasLastLog ? `Last log ${latestLoggedWeight.toFixed(1)} • ${latestLogDateLabel}` : "Add your first weigh-in to populate trend")
      : (hasMovingAverage ? `Avg ${movingAvgWeight.toFixed(1)}` : "Avg unavailable");
    // Subtle helper text shown below the toggle buttons.
    const modeHelperText = sprintStatusDisplayMode === "avg"
      ? "Avg smooths daily fluctuations."
      : "Last reflects your most recent weigh-in.";

    // pillTone/pillLabel are initialized below after isMaintain and derivedStatus are computed.

    const derivedSprintStart = sprintEntriesAsc.find((e) => Number.isFinite(e.weight))?.weight ?? null;
    const sprintStart = Number.isFinite(startWeight) ? startWeight : (Number.isFinite(derivedSprintStart) ? derivedSprintStart : null);
    const sprintTarget = Number.isFinite(targetWeight) ? targetWeight : null;
    const hasCurrentWeight = Number.isFinite(displayWeight);
    const hasTargetWeight = Number.isFinite(sprintTarget);
    const hasStartWeight = Number.isFinite(sprintStart);
    const netNearMaintenance = Number.isFinite(currentPlanNet) && Math.abs(currentPlanNet) < 30;
    const sprintMode: "cut" | "bulk" | "maintain" = (hasStartWeight && hasTargetWeight && Math.abs((sprintTarget ?? 0) - (sprintStart ?? 0)) < 0.25) || (hasTargetWeight && netNearMaintenance)
      ? "maintain"
      : (hasStartWeight && hasTargetWeight && (sprintTarget ?? 0) < (sprintStart ?? 0))
        ? "cut"
        : "bulk";
    const clampPct = (pct: number) => Math.min(Math.max(pct, 0), 100);
    const isMaintain = sprintMode === "maintain";
    // Maintenance uses a symmetric target-centered axis with 3 zones: GOOD (±2%), WATCH (±3.5%), ALERT (beyond).
    const maintainGoodRangeLb  = hasTargetWeight ? clamp((sprintTarget ?? 0) * 0.02,  1.5, 6)  : 0;
    const maintainWatchRangeLb = hasTargetWeight ? clamp((sprintTarget ?? 0) * 0.035, 2.5, 10) : 0;
    // Bar renders ±(watchRange × 1.5) so ALERT zones are visible on the bar edges.
    const maintainBarHalfRange = maintainWatchRangeLb * 1.5;
    const maintainDeltaLb = isMaintain && hasCurrentWeight && hasTargetWeight ? (displayWeight ?? 0) - (sprintTarget ?? 0) : null;
    const maintainNowPctRaw = isMaintain && maintainDeltaLb !== null && maintainBarHalfRange > 0
      ? 50 + (maintainDeltaLb / maintainBarHalfRange) * 50
      : null;
    const maintainNowPct = maintainNowPctRaw !== null ? clampPct(maintainNowPctRaw) : null;
    // Zone boundary percentages on the bar
    const mGoodStartPct  = maintainBarHalfRange > 0 ? clampPct(50 - (maintainGoodRangeLb  / maintainBarHalfRange) * 50) : 50;
    const mGoodEndPct    = maintainBarHalfRange > 0 ? clampPct(50 + (maintainGoodRangeLb  / maintainBarHalfRange) * 50) : 50;
    const mWatchStartPct = maintainBarHalfRange > 0 ? clampPct(50 - (maintainWatchRangeLb / maintainBarHalfRange) * 50) : 0;
    const mWatchEndPct   = maintainBarHalfRange > 0 ? clampPct(50 + (maintainWatchRangeLb / maintainBarHalfRange) * 50) : 100;
    // Zone classification
    type MaintainZone = "good" | "watch-high" | "watch-low" | "alert-high" | "alert-low" | "no-data";
    const maintainZone: MaintainZone = !hasCurrentWeight || maintainDeltaLb === null
      ? "no-data"
      : Math.abs(maintainDeltaLb) <= maintainGoodRangeLb
        ? "good"
        : maintainDeltaLb > 0
          ? (maintainDeltaLb <= maintainWatchRangeLb ? "watch-high" : "alert-high")
          : (Math.abs(maintainDeltaLb) <= maintainWatchRangeLb ? "watch-low" : "alert-low");
    const maintainZoneTone = maintainZone === "good" ? "green"
      : (maintainZone === "watch-high" || maintainZone === "watch-low") ? "orange"
      : (maintainZone === "alert-high" || maintainZone === "alert-low") ? "red"
      : "muted";

    // ±0.3% of planned weight as blue on-track tolerance for cut/bulk.
    const onTrackToleranceLb = (!isMaintain && plannedWeightToday !== null)
      ? Math.max(0.1, Math.abs(plannedWeightToday) * 0.003)
      : REPLAN_STATUS_THRESHOLD_LBS;
    const displayDeltaFromPlan = !isMaintain && plannedWeightToday !== null && displayWeight !== null
      ? displayWeight - plannedWeightToday
      : null;
    // derivedStatus is always based on the currently selected source (Avg or Last).
    const derivedStatus: "ahead" | "behind" | "on-track" =
      displayDeltaFromPlan === null
        ? "on-track"
        : goalDirection === "cut"
          ? (displayDeltaFromPlan < -onTrackToleranceLb ? "ahead" : displayDeltaFromPlan > onTrackToleranceLb ? "behind" : "on-track")
          : goalDirection === "gain"
            ? (displayDeltaFromPlan > onTrackToleranceLb ? "ahead" : displayDeltaFromPlan < -onTrackToleranceLb ? "behind" : "on-track")
            : "on-track";
    // Initialize pill chip from derivedStatus; maintain mode overrides below.
    let pillTone = derivedStatus === "ahead" ? "green" : derivedStatus === "behind" ? "red" : "blue";
    let pillLabel = derivedStatus === "ahead" ? "Ahead" : derivedStatus === "behind" ? "Behind" : "On Track";

    const hasPlanEndpoints = hasStartWeight && hasTargetWeight;
    const barRange = hasPlanEndpoints && !isMaintain ? (sprintTarget ?? 0) - (sprintStart ?? 0) : 0;
    const pctForWeight = (weight: number | null) => {
      if (weight === null || !Number.isFinite(weight) || sprintStart === null || !Number.isFinite(barRange) || Math.abs(barRange) < 1e-6) return null;
      return clampPct(((weight - sprintStart) / barRange) * 100);
    };
    const actualPct = isMaintain ? maintainNowPct : pctForWeight(hasCurrentWeight ? displayWeight : null);
    const plannedPct = isMaintain ? 50 : pctForWeight(plannedWeightToday);
    const gapStartPct = plannedPct !== null && actualPct !== null ? Math.min(plannedPct, actualPct) : null;
    const gapEndPct = plannedPct !== null && actualPct !== null ? Math.max(plannedPct, actualPct) : null;
    const gapWidthPct = gapStartPct !== null && gapEndPct !== null ? gapEndPct - gapStartPct : 0;

    const requiredNetRounded = requiredNetPerDay !== null ? Math.round(requiredNetPerDay) : null;
    const requiredLbsPerWeek = requiredNetPerDay !== null ? Math.abs((requiredNetPerDay / 3500) * 7).toFixed(1) : null;

    // Days early/late derived from the currently selected source (Avg or Last).
    // Compute sprint lbs-per-day rate from plan endpoints; use it to convert weight delta → day offset.
    const sprintDurationDays = isYmd(sprintStartEffective) && isYmd(targetDate)
      ? Math.max(1, daysBetweenInclusiveStartExclusiveEnd(sprintStartEffective, targetDate) ?? 1)
      : null;
    const sprintLbsPerDay = sprintDurationDays !== null && Math.abs(barRange) > 0
      ? barRange / sprintDurationDays
      : null;
    let daysRelative: number | null = null;
    let projFinishDateText: string | null = null;
    if (!isMaintain && displayDeltaFromPlan !== null && sprintLbsPerDay !== null && Math.abs(sprintLbsPerDay) > 0.001) {
      // positive result = ahead (finishes early), negative = behind (late)
      daysRelative = Math.round(displayDeltaFromPlan / sprintLbsPerDay);
    } else if (isYmd(projectedDateFromCurrentPlan) && isYmd(targetDate)) {
      // Fallback for maintain mode or when plan data is insufficient
      daysRelative = daysBetweenInclusiveStartExclusiveEnd(projectedDateFromCurrentPlan, targetDate);
      projFinishDateText = formatMDY(projectedDateFromCurrentPlan);
    }

    let alignCopy = "Tracking closely";
    let alignSecondaryCopy: string | null = null;
    let alignColor = derivedStatus === "ahead" ? "#86efac" : derivedStatus === "behind" ? COLORS.red : "rgba(255,255,255,0.85)";
    if (isMaintain) {
      if (maintainZone === "no-data") {
        alignCopy = "Log today’s weight";
        alignColor = "rgba(255,255,255,0.82)";
      } else if (maintainZone === "good") {
        alignCopy = "Within range";
        alignColor = "#86efac";
      } else if (maintainZone === "watch-high") {
        alignCopy = "Trending high";
        alignColor = "#f59e0b";
      } else if (maintainZone === "watch-low") {
        alignCopy = "Trending low";
        alignColor = "#f59e0b";
      } else if (maintainZone === "alert-high") {
        alignCopy = "Above maintenance range";
        alignColor = COLORS.red;
      } else {
        alignCopy = "Below maintenance range";
        alignColor = COLORS.red;
      }
      if (maintainDeltaLb !== null) {
        alignSecondaryCopy = `${maintainDeltaLb >= 0 ? "+" : "−"}${Math.abs(maintainDeltaLb).toFixed(1)} lb ${maintainDeltaLb >= 0 ? "above" : "below"} target`;
      }
    } else {
      const deltaAbs = displayDeltaFromPlan !== null ? Math.abs(displayDeltaFromPlan).toFixed(1) : null;
      if (deltaAbs !== null && derivedStatus !== "on-track") {
        alignCopy = derivedStatus === "ahead" ? `▲ ${deltaAbs} lb ahead` : `▼ ${deltaAbs} lb behind`;
      }
    }

    const temporalStatusCompact = daysRelative === null
      ? null
      : daysRelative === 0
        ? "On time"
        : `${Math.abs(daysRelative)}d ${daysRelative > 0 ? "early" : "late"}`;
    const temporalStatusColor = daysRelative !== null
      ? (daysRelative > 0 ? COLORS.green : daysRelative < 0 ? COLORS.orange : "rgba(255,255,255,0.7)")
      : "rgba(255,255,255,0.7)";
    const showTemporalStatusInline = !isMaintain && temporalStatusCompact !== null;

    if (sprintMode === "maintain") {
      if (maintainZone === "no-data")        { pillTone = "muted";   pillLabel = "Need Weigh-in"; }
      else if (maintainZone === "good")      { pillTone = "green";   pillLabel = "Within Range"; }
      else if (maintainZone === "watch-high") { pillTone = "orange";  pillLabel = "Trending High"; }
      else if (maintainZone === "watch-low") { pillTone = "orange";  pillLabel = "Trending Low"; }
      else if (maintainZone === "alert-high") { pillTone = "red";     pillLabel = "Above Range"; }
      else                                   { pillTone = "red";     pillLabel = "Below Range"; }
    }

    const actualDotColor = isMaintain
      ? (maintainZone === "no-data"   ? "rgba(255,255,255,0.6)"
       : maintainZone === "good"      ? COLORS.lightBlue
       : (maintainZone === "watch-high" || maintainZone === "watch-low") ? "#f59e0b"
       : COLORS.red)
      : (derivedStatus === "ahead" ? "#4ade80" : derivedStatus === "behind" ? COLORS.red : COLORS.lightBlue);

    const dotTooltipStyle: React.CSSProperties = {
      position: "absolute",
      bottom: "calc(100% + 10px)",
      left: "50%",
      transform: "translateX(-50%)",
      background: "rgba(13,20,38,0.98)",
      border: "1px solid rgba(255,255,255,0.16)",
      borderRadius: 10,
      padding: "0.5rem 0.7rem",
      minWidth: 155,
      zIndex: 200,
      pointerEvents: "none",
      whiteSpace: "nowrap",
      boxShadow: "0 4px 16px rgba(0,0,0,0.5)",
    };

    const sectionLabel = (text: string) => (
      <div style={{ fontSize: "0.72rem", color: "rgba(255,255,255,0.48)", textTransform: "uppercase", letterSpacing: "0.1em", fontWeight: 700 }}>
        {text}
      </div>
    );

    const formatSigned = (value: number | null, digits = 1) => {
      if (value === null || !Number.isFinite(value)) return "—";
      const normalizedValue = normalizeNearZero(value) ?? 0;
      const sign = normalizedValue > 0 ? "+" : normalizedValue < 0 ? "−" : "";
      return `${sign}${Math.abs(normalizedValue).toFixed(digits)}`;
    };

    const startAnchorWeight = Number.isFinite(startWeight) ? startWeight : null;
    const startAnchorDateKey = isYmd(sprintStartEffective) ? sprintStartEffective : (currentAnchor?.dateKey ?? todayYmd);
    const displayDateKey = sprintStatusDisplayMode === "today"
      ? (isYmd(lastLoggedAnchor?.date) ? lastLoggedAnchor.date : (currentAnchor?.dateKey ?? todayYmd))
      : (currentAnchor?.dateKey ?? todayYmd);
    const netChangeRaw = startAnchorWeight !== null && displayWeight !== null ? displayWeight - startAnchorWeight : null;
    const netChange = normalizeNearZero(netChangeRaw);
    const rawElapsedDays = daysBetweenInclusiveStartExclusiveEnd(startAnchorDateKey, displayDateKey);
    const daysElapsed = Math.max(1, Number.isFinite(rawElapsedDays) ? rawElapsedDays : 1);
    const ratePerWeek = netChange !== null ? normalizeNearZero(netChange / (daysElapsed / 7)) : null;

    const towardGoalForNet = netChange !== null && ((goalDirection === "cut" && netChange < 0) || (goalDirection === "gain" && netChange > 0));
    const towardGoalForRate = ratePerWeek !== null && ((goalDirection === "cut" && ratePerWeek < 0) || (goalDirection === "gain" && ratePerWeek > 0));
    const nearZeroNet = netChange === null || netChange === 0;
    const nearZeroRate = ratePerWeek === null || ratePerWeek === 0;
    // Maintain mode uses zone tone instead of cut/bulk trend direction,
    // so KPI card colors remain consistent with the chip and bar.
    const netCardTone = isMaintain
      ? maintainZoneTone
      : nearZeroNet ? "muted" : towardGoalForNet ? "green" : "red";
    const rateCardTone = isMaintain
      ? maintainZoneTone
      : nearZeroRate ? "muted" : towardGoalForRate ? "green" : "red";
    const daysLeftValue = Number.isFinite(remainingDays) ? `${Math.max(0, remainingDays)}d` : "—";
    const netKpiValue = `${formatSigned(netChange, 1)} lb`;
    const rateKpiValue = `${formatSigned(ratePerWeek, 1)} lb/week`;
    const remainingWeight =
      hasTargetWeight && displayWeight !== null
        ? Math.max(0, parseFloat(Math.abs((sprintTarget ?? 0) - displayWeight).toFixed(1)))
        : null;
    const remainingKpiValue = remainingWeight !== null ? `${remainingWeight.toFixed(1)} lb` : "—";

    if (typeof window !== "undefined" && import.meta.env?.DEV && netChange !== null && Math.abs(netChange) >= 0.1) {
      if (`${formatSigned(netChange, 1)} lb` === "0.0 lb") {
        console.warn("[SprintStatus] Net display collapsed to 0.0 despite non-trivial change", { netChange, currentAnchor, startAnchorWeight });
      }
      if (`${formatSigned(ratePerWeek, 2)} lb/wk` === "0.00 lb/wk") {
        console.warn("[SprintStatus] Rate display collapsed to 0.00 despite non-trivial change", { netChange, ratePerWeek, daysElapsed, currentAnchor, startAnchorDateKey });
      }
    }

    const planLabel = plannedWeightToday !== null ? `Plan ${plannedWeightToday.toFixed(1)}` : "Plan —";
    const nowLabel = Number.isFinite(displayWeight) ? `Now ${displayWeight.toFixed(1)}` : "Now —";
    const sprintStartDateLabel = isYmd(startAnchorDateKey) ? formatMD(startAnchorDateKey) : "—";
    const sprintEndDateLabel = isYmd(targetDate) ? formatMD(targetDate) : "—";
    const sprintStartWeightLabel = Number.isFinite(sprintStart) ? `${sprintStart.toFixed(1)} lb` : "—";
    const sprintEndWeightLabel = Number.isFinite(sprintTarget) ? `${sprintTarget.toFixed(1)} lb` : "—";
    const estimateLabelWidth = (label: string | null) => (label ? Math.max(62, Math.round(label.length * 6.4 + 18)) : 0);
    const clampLabelLeft = (centerPct: number | null, widthPx: number) => {
      if (centerPct === null || !Number.isFinite(centerPct)) return "clamp(6px, 50%, calc(100% - 6px))";
      const raw = `calc(${centerPct}% - ${Math.round(widthPx / 2)}px)`;
      return `clamp(6px, ${raw}, calc(100% - ${widthPx + 6}px))`;
    };
    const planLabelWidth = estimateLabelWidth(planLabel);
    const nowLabelWidth = estimateLabelWidth(nowLabel);
    // Each label renders as [7px dot] + [0.25rem gap] + text inside an inline-flex,
    // so the full element occupies ~11px more than the text alone.
    const LABEL_LEADING_PX = 11;
    const LABEL_EDGE_PADDING_PX = 6;
    const LABEL_COLLISION_GAP_PX = 6;
    // Mirrors clampLabelLeft, but in pixel space so we can detect overlap of the
    // actually-rendered range rather than the raw marker positions (which differ
    // when a label is clamped against the bar edge).
    const computeLabelRange = (centerPx: number | null, textWidth: number, barWidthPx: number): { left: number; right: number } | null => {
      if (centerPx === null || !Number.isFinite(centerPx)) return null;
      const rawLeft = centerPx - textWidth / 2;
      const minLeft = LABEL_EDGE_PADDING_PX;
      const maxLeft = Math.max(minLeft, barWidthPx - textWidth - LABEL_EDGE_PADDING_PX);
      const left = Math.min(Math.max(rawLeft, minLeft), maxLeft);
      return { left, right: left + textWidth + LABEL_LEADING_PX };
    };
    const rangesOverlap = (a: { left: number; right: number } | null, b: { left: number; right: number } | null): boolean => {
      if (!a || !b) return false;
      return a.right + LABEL_COLLISION_GAP_PX > b.left && b.right + LABEL_COLLISION_GAP_PX > a.left;
    };

    const BAR_LAYOUT_PX = 640;
    const planXPx = plannedPct !== null ? (plannedPct / 100) * BAR_LAYOUT_PX : null;
    const nowXPx = actualPct !== null ? (actualPct / 100) * BAR_LAYOUT_PX : null;
    const labelOverlapRisk = rangesOverlap(
      computeLabelRange(planXPx, planLabelWidth, BAR_LAYOUT_PX),
      computeLabelRange(nowXPx, nowLabelWidth, BAR_LAYOUT_PX),
    );
    const labelRenderMode: "normal" | "stack" = labelOverlapRisk ? "stack" : "normal";
    const showPlanInlineLabel = plannedPct !== null;
    const planLabelTop = labelRenderMode === "stack" ? 0 : 8;
    const nowLabelTop = labelRenderMode === "stack" ? 16 : 8;

    const displaySprintName = (activeSprint.name || "").trim() || "Active Sprint";
    const truncatedSprintName = displaySprintName.length > 34 ? `${displaySprintName.slice(0, 33)}…` : displaySprintName;
    const modeChipLabel = sprintMode === "maintain" ? "Maintain" : sprintMode === "cut" ? "Cut" : "Bulk";
    const modeChipTone = sprintMode === "cut" ? "blue" : sprintMode === "bulk" ? "orange" : "purple";


    const planVsActualHeader = <div>{sectionLabel("Plan vs Actual")}</div>;
    const kpiCardStyle = (tone: string): React.CSSProperties => {
      const toneMap: Record<string, { border: string; bg: string; value: string }> = {
        green: { border: statusColors.positiveSoftBorder, bg: statusColors.positiveSoftBg, value: statusColors.positiveTextSoft },
        red: { border: "rgba(239,68,68,0.45)", bg: "rgba(239,68,68,0.12)", value: "#fee2e2" },
        orange: { border: statusColors.warningSoftBorder, bg: statusColors.warningSoftBg, value: statusColors.warningTextSoft },
        muted: { border: "rgba(255,255,255,0.16)", bg: "rgba(255,255,255,0.04)", value: "rgba(255,255,255,0.92)" },
      };
      return {
        border: `1px solid ${toneMap[tone]?.border ?? toneMap.muted.border}`,
        background: toneMap[tone]?.bg ?? toneMap.muted.bg,
        borderRadius: 12,
        padding: "0.42rem 0.58rem",
        minWidth: 112,
      };
    };

    if (isDashboardMobile) {
      const planNowLabel = plannedWeightToday !== null ? `${plannedWeightToday.toFixed(1)} lb` : "—";
      const MOBILE_BAR_LAYOUT_PX = typeof window !== "undefined" ? Math.max(280, window.innerWidth - 30) : 390;
      const planXPx = plannedPct !== null ? (plannedPct / 100) * MOBILE_BAR_LAYOUT_PX : null;
      const nowXPx = actualPct !== null ? (actualPct / 100) * MOBILE_BAR_LAYOUT_PX : null;
      const mobileLabelOverlap = rangesOverlap(
        computeLabelRange(planXPx, planLabelWidth, MOBILE_BAR_LAYOUT_PX),
        computeLabelRange(nowXPx, nowLabelWidth, MOBILE_BAR_LAYOUT_PX),
      );
      const mobilePlanLabelTop = mobileLabelOverlap ? 2 : 7;
      const mobileNowLabelTop = mobileLabelOverlap ? 18 : 7;
      const lbsToGoValue = hasTargetWeight && displayWeight !== null
        ? `${Math.abs((sprintTarget ?? 0) - displayWeight).toFixed(1)} lb to go`
        : "— lb to go";
      const daysLeftChipValue = Number.isFinite(remainingDays) ? `${Math.max(0, remainingDays)} days left` : "— days left";
      const netChangeLbRaw = hasStartWeight && hasCurrentWeight && sprintStart !== null && displayWeight !== null
        ? displayWeight - sprintStart
        : null;
      const netChangeLb = normalizeNearZero(netChangeLbRaw);
      const netChangeChipValue = netChangeLb !== null
        ? `${netChangeLb >= 0 ? "+" : "−"}${Math.abs(netChangeLb).toFixed(1)} lb net`
        : "— lb net";

      return (
          <div style={{ ...cardStyle, width: "100%", borderRadius: 18, padding: "0.8rem 0.85rem" }}>
            {!sprintStatusExpanded ? (
              <>
                <div style={{ display: "flex", alignItems: "baseline", gap: "0.4rem", minWidth: 0 }}>
                  <div style={{ fontSize: "1rem", fontWeight: 900, minWidth: 0, flex: 1 }} title={displaySprintName}>
                    Sprint Status — <span style={{ display: "inline-block", maxWidth: "min(220px, calc(100vw - 12rem))", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", verticalAlign: "bottom" }}>{truncatedSprintName}</span>
                  </div>
                </div>
                <div style={{ marginTop: "0.42rem", display: "grid", gridTemplateColumns: "minmax(0, 1fr) auto", alignItems: "stretch", gap: "0.5rem" }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "0.4rem", flexWrap: "wrap" }}>
                      <Pill label={pillLabel} tone={pillTone} />
                      <Pill label={modeChipLabel} tone={modeChipTone} />
                    </div>
                    <div style={{ marginTop: "0.62rem", display: "inline-flex", alignItems: "center", gap: "0.4rem", flexWrap: "nowrap", maxWidth: "100%" }}>
                      <div style={{ fontSize: "1.92rem", fontWeight: 860, color: "white", lineHeight: 1, whiteSpace: "nowrap" }}>{displayWeight !== null ? displayWeight.toFixed(1) : "—"} <span style={{ fontSize: "0.9rem", color: COLORS.lightBlue }}>lb</span></div>
                      <div style={{ display: "inline-flex", alignItems: "center", gap: "0.16rem", padding: "0.1rem", borderRadius: 999, border: "1px solid rgba(255,255,255,0.14)", background: "rgba(255,255,255,0.02)", flexShrink: 0 }}>
                        <button
                          type="button"
                          onClick={() => handleSprintStatusDisplayModeChange("avg")}
                          title="Uses moving average to smooth fluctuations."
                          style={{ ...btnBase, padding: "0.12rem 0.42rem", borderRadius: 999, fontSize: "0.62rem", lineHeight: 1.1, color: sprintStatusDisplayMode === "avg" ? "rgba(255,255,255,0.94)" : "rgba(255,255,255,0.58)", background: sprintStatusDisplayMode === "avg" ? "rgba(255,255,255,0.11)" : "transparent", borderColor: "transparent", boxShadow: "none" }}
                        >
                          Avg
                        </button>
                        <button
                          type="button"
                          onClick={() => handleSprintStatusDisplayModeChange("today")}
                          title="Uses latest logged weight."
                          style={{ ...btnBase, padding: "0.12rem 0.42rem", borderRadius: 999, fontSize: "0.62rem", lineHeight: 1.1, color: sprintStatusDisplayMode === "today" ? "rgba(255,255,255,0.94)" : "rgba(255,255,255,0.58)", background: sprintStatusDisplayMode === "today" ? "rgba(255,255,255,0.11)" : "transparent", borderColor: "transparent", boxShadow: "none" }}
                        >
                          Last
                        </button>
                      </div>
                    </div>
                    <div style={{ marginTop: "0.26rem", fontSize: "0.74rem", color: "rgba(255,255,255,0.74)" }}>
                      Goal {sprintEndWeightLabel} by {sprintEndDateLabel}
                    </div>
                    <div style={{ marginTop: "0.45rem", fontSize: "0.84rem", fontWeight: 700, color: alignColor, textAlign: "left", display: "flex", alignItems: "center", gap: "0.34rem", flexWrap: "wrap" }}>
                      <span>{alignCopy}</span>
                      {showTemporalStatusInline ? (
                        <>
                          <span style={{ color: "rgba(255,255,255,0.36)", fontWeight: 600 }}>•</span>
                          <span style={{ color: temporalStatusColor }}>{temporalStatusCompact}</span>
                        </>
                      ) : null}
                    </div>
                  </div>
                  <div style={{ display: "grid", alignContent: "center", gap: "0.35rem", minWidth: 106 }}>
                    <div style={{ ...kpiCardStyle("muted"), width: "100%", minWidth: 0, padding: "0.26rem 0.46rem", textAlign: "center" }}>
                      <div style={{ fontSize: "0.67rem", fontWeight: 700, color: "rgba(255,255,255,0.82)", whiteSpace: "nowrap" }}>{netChangeChipValue}</div>
                    </div>
                    <div style={{ ...kpiCardStyle("muted"), width: "100%", minWidth: 0, padding: "0.26rem 0.46rem", textAlign: "center" }}>
                      <div style={{ fontSize: "0.67rem", fontWeight: 700, color: "rgba(255,255,255,0.82)", whiteSpace: "nowrap" }}>{lbsToGoValue}</div>
                    </div>
                    <div style={{ ...kpiCardStyle("muted"), width: "100%", minWidth: 0, padding: "0.26rem 0.46rem", textAlign: "center" }}>
                      <div style={{ fontSize: "0.67rem", fontWeight: 700, color: "rgba(255,255,255,0.82)", whiteSpace: "nowrap" }}>{daysLeftChipValue}</div>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <>
                <div style={{ display: "flex", alignItems: "baseline", gap: "0.4rem", minWidth: 0 }}>
                  <div style={{ fontSize: "1rem", fontWeight: 900, minWidth: 0, flex: 1 }} title={displaySprintName}>
                    Sprint Status — <span style={{ display: "inline-block", maxWidth: "min(220px, calc(100vw - 12rem))", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", verticalAlign: "bottom" }}>{truncatedSprintName}</span>
                  </div>
                  <button
                    type="button"
                    style={{ ...btnBase, padding: "0.24rem 0.5rem", background: "transparent", borderColor: "rgba(255,255,255,0.2)", color: "rgba(255,255,255,0.82)", fontSize: "0.72rem" }}
                    onClick={openSprintEditor}
                  >
                    Edit
                  </button>
                </div>
                <div style={{ marginTop: "0.42rem", display: "flex", alignItems: "center", gap: "0.4rem", flexWrap: "wrap" }}>
                  <Pill label={pillLabel} tone={pillTone} />
                  <Pill label={modeChipLabel} tone={modeChipTone} />
                </div>
                <div style={{ marginTop: "0.62rem" }}>
                  <div style={{ display: "inline-flex", alignItems: "baseline", gap: "0.4rem", flexWrap: "nowrap", maxWidth: "100%" }}>
                    <div style={{ fontSize: "1.92rem", fontWeight: 860, color: "white", lineHeight: 1, whiteSpace: "nowrap" }}>{displayWeight !== null ? displayWeight.toFixed(1) : "—"} <span style={{ fontSize: "0.9rem", color: COLORS.lightBlue }}>lb</span></div>
                    <div style={{ display: "inline-flex", alignItems: "center", gap: "0.16rem", padding: "0.1rem", borderRadius: 999, border: "1px solid rgba(255,255,255,0.14)", background: "rgba(255,255,255,0.02)", flexShrink: 0 }}>
                      <button
                        type="button"
                        onClick={() => handleSprintStatusDisplayModeChange("avg")}
                        title="Uses moving average to smooth fluctuations."
                        style={{ ...btnBase, padding: "0.12rem 0.42rem", borderRadius: 999, fontSize: "0.62rem", lineHeight: 1.1, color: sprintStatusDisplayMode === "avg" ? "rgba(255,255,255,0.94)" : "rgba(255,255,255,0.58)", background: sprintStatusDisplayMode === "avg" ? "rgba(255,255,255,0.11)" : "transparent", borderColor: "transparent", boxShadow: "none" }}
                      >
                        Avg
                      </button>
                      <button
                        type="button"
                        onClick={() => handleSprintStatusDisplayModeChange("today")}
                        title="Uses latest logged weight."
                        style={{ ...btnBase, padding: "0.12rem 0.42rem", borderRadius: 999, fontSize: "0.62rem", lineHeight: 1.1, color: sprintStatusDisplayMode === "today" ? "rgba(255,255,255,0.94)" : "rgba(255,255,255,0.58)", background: sprintStatusDisplayMode === "today" ? "rgba(255,255,255,0.11)" : "transparent", borderColor: "transparent", boxShadow: "none" }}
                      >
                        Last
                      </button>
                    </div>
                  </div>
                </div>
                <div style={{ marginTop: "0.45rem", fontSize: "0.84rem", fontWeight: 700, color: alignColor, textAlign: "left", display: "flex", alignItems: "center", gap: "0.34rem", flexWrap: "wrap" }}>
                  <span>{isMaintain ? alignCopy : (alignSecondaryCopy ? `${alignCopy} of plan` : alignCopy)}</span>
                  {showTemporalStatusInline ? (
                    <>
                      <span style={{ color: "rgba(255,255,255,0.36)", fontWeight: 600 }}>•</span>
                      <span style={{ color: temporalStatusColor }}>{temporalStatusCompact}</span>
                    </>
                  ) : null}
                </div>
              </>
            )}

            {sprintStatusExpanded ? (
              <>
                <div style={{ marginTop: "0.66rem", borderTop: "1px solid rgba(255,255,255,0.08)", paddingTop: "0.62rem" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                    <div style={{ fontSize: "0.7rem", color: "rgba(255,255,255,0.55)", textTransform: "uppercase", letterSpacing: "0.09em", fontWeight: 700 }}>Plan vs Actual</div>
                  </div>
                  {isMaintain ? (
                    <div style={{ marginTop: "0.45rem", display: "grid", gap: "0.16rem" }}>
                      <div style={{ position: "relative" }}>
                      {hasTargetWeight && sprintTarget ? (
                        <div style={{ position: "absolute", bottom: "calc(100% + 4px)", left: "50%", transform: "translateX(-50%)", display: "inline-flex", flexDirection: "column", alignItems: "center", border: "1px solid rgba(255,255,255,0.14)", borderRadius: 6, padding: "0.08rem 0.3rem", background: "rgba(255,255,255,0.04)", lineHeight: 1.15, whiteSpace: "nowrap" }}>
                            <span style={{ fontSize: "0.48rem", color: "rgba(255,255,255,0.45)", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em" }}>Target</span>
                            <span style={{ fontSize: "0.6rem", color: "rgba(255,255,255,0.78)", fontWeight: 800 }}>{sprintTarget.toFixed(1)}</span>
                          </div>
                      ) : null}
                      <div style={{ position: "relative", height: 17, borderRadius: 999, overflow: "hidden", background: "linear-gradient(90deg, rgba(148,163,184,0.18), rgba(148,163,184,0.28))" }}>
                        <div style={{ position: "absolute", left: `${mGoodStartPct}%`, width: `${Math.max(0, mGoodEndPct - mGoodStartPct)}%`, top: 2, bottom: 2, borderRadius: 999, background: "rgba(59,130,246,0.38)" }} />
                        <div style={{ position: "absolute", left: "50%", top: 0, bottom: 0, width: 2, transform: "translateX(-50%)", background: "rgba(255,255,255,0.50)" }} />
                        {actualPct !== null ? <div style={{ position: "absolute", left: `${actualPct}%`, top: "50%", width: 16, height: 16, borderRadius: 999, background: actualDotColor, transform: "translate(-50%, -50%)", boxShadow: `0 0 7px ${actualDotColor}99` }} /> : null}
                      </div>
                      </div>
                      <div style={{ position: "relative", height: 20, marginTop: "0.02rem" }}>
                        {actualPct !== null ? (
                          <div style={{ position: "absolute", left: clampLabelLeft(actualPct, nowLabelWidth), top: 0, display: "inline-flex", alignItems: "center", gap: "0.25rem", fontSize: "0.62rem", color: "rgba(255,255,255,0.84)", whiteSpace: "nowrap" }}>
                            <span style={{ width: 7, height: 7, borderRadius: 999, background: actualDotColor, flexShrink: 0 }} />
                            <span>{nowLabel}</span>
                          </div>
                        ) : null}
                      </div>
                    </div>
                  ) : (
                    <>
                      {hasLastLog ? (
                        <>
                          <div style={{ marginTop: "0.5rem", display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.35rem" }}>
                            <div>
                              <div style={{ fontSize: "0.63rem", color: "rgba(255,255,255,0.55)", fontWeight: 700 }}>Start</div>
                              <div style={{ fontSize: "0.82rem", fontWeight: 800 }}>{sprintStartWeightLabel}</div>
                              <div style={{ fontSize: "0.62rem", color: "rgba(255,255,255,0.55)" }}>{sprintStartDateLabel}</div>
                            </div>
                            <div style={{ textAlign: "right" }}>
                              <div style={{ fontSize: "0.63rem", color: "rgba(255,255,255,0.55)", fontWeight: 700 }}>Goal</div>
                              <div style={{ fontSize: "0.82rem", fontWeight: 800 }}>{sprintEndWeightLabel}</div>
                              <div style={{ fontSize: "0.62rem", color: "rgba(255,255,255,0.55)" }}>{sprintEndDateLabel}</div>
                            </div>
                          </div>
                          <div style={{ marginTop: "0.5rem", position: "relative", height: 18, borderRadius: 999, background: "linear-gradient(90deg, rgba(148,163,184,0.2), rgba(148,163,184,0.28))" }}>
                            {gapStartPct !== null && gapWidthPct >= 1.5 ? <div style={{ position: "absolute", top: 2, bottom: 2, left: `${gapStartPct}%`, width: `${gapWidthPct}%`, borderRadius: 6, background: derivedStatus === "ahead" ? "linear-gradient(90deg, rgba(74,222,128,0.34), rgba(74,222,128,0.52))" : derivedStatus === "behind" ? "linear-gradient(90deg, rgba(248,113,113,0.32), rgba(248,113,113,0.48))" : "linear-gradient(90deg, rgba(59,130,246,0.22), rgba(59,130,246,0.46))" }} /> : null}
                            {plannedPct !== null ? <div style={{ position: "absolute", top: "50%", left: `${plannedPct}%`, transform: "translate(-50%, -50%)", width: 14, height: 14, borderRadius: 999, border: `2.2px solid ${COLORS.purple}`, background: "rgba(11,15,20,0.95)" }} /> : null}
                            {actualPct !== null ? <div style={{ position: "absolute", top: "50%", left: `${actualPct}%`, transform: "translate(-50%, -50%)", width: 16, height: 16, borderRadius: 999, background: actualDotColor, boxShadow: `0 0 7px ${actualDotColor}99` }} /> : null}
                          </div>
                          <div style={{ position: "relative", height: 38, marginTop: "0.08rem" }}>
                            {plannedPct !== null ? (
                              <div style={{ position: "absolute", left: clampLabelLeft(plannedPct, planLabelWidth), top: mobilePlanLabelTop, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: "0.25rem", fontSize: "0.6rem", color: "rgba(255,255,255,0.75)", whiteSpace: "nowrap", maxWidth: 124 }}><span style={{ width: 7, height: 7, borderRadius: 999, border: `1.4px solid ${COLORS.purple}`, background: "transparent", flexShrink: 0 }} /><span>{planLabel}</span></div>
                            ) : null}
                            {actualPct !== null ? (
                              <div style={{ position: "absolute", left: clampLabelLeft(actualPct, nowLabelWidth), top: mobileNowLabelTop, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: "0.25rem", fontSize: "0.6rem", color: "rgba(255,255,255,0.84)", whiteSpace: "nowrap", maxWidth: 124 }}><span style={{ width: 7, height: 7, borderRadius: 999, background: actualDotColor, flexShrink: 0 }} /><span>{nowLabel}</span></div>
                            ) : null}
                          </div>
                        </>
                      ) : (
                        <div style={{ marginTop: "0.5rem", border: "1px dashed rgba(255,255,255,0.22)", borderRadius: 10, padding: "0.55rem 0.6rem", fontSize: "0.72rem", color: "rgba(255,255,255,0.62)" }}>
                          No weigh-ins yet. Add your first log to see plan vs actual.
                        </div>
                      )}
                    </>
                  )}
                </div>

                <div style={{ marginTop: "0.66rem", borderTop: "1px solid rgba(255,255,255,0.08)", paddingTop: "0.62rem" }}>
                  <div style={{ fontSize: "0.7rem", color: "rgba(255,255,255,0.55)", textTransform: "uppercase", letterSpacing: "0.09em", fontWeight: 700 }}>Sprint Metrics</div>
                  <div style={{ marginTop: "0.45rem", display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.5rem" }}>
                    <div style={{ ...kpiCardStyle(netCardTone), minWidth: 0 }}>
                      <div style={{ fontSize: "0.6rem", letterSpacing: "0.08em", textTransform: "uppercase", color: "rgba(255,255,255,0.62)", fontWeight: 700 }}>NET CHANGE</div>
                      <div style={{ marginTop: "0.12rem", fontSize: "0.92rem", fontWeight: 900 }}>{netKpiValue}</div>
                    </div>
                    <div style={{ ...kpiCardStyle(rateCardTone), minWidth: 0 }}>
                      <div style={{ fontSize: "0.6rem", letterSpacing: "0.08em", textTransform: "uppercase", color: "rgba(255,255,255,0.62)", fontWeight: 700 }}>AVG RATE</div>
                      <div style={{ marginTop: "0.12rem", fontSize: "0.92rem", fontWeight: 900 }}>{rateKpiValue}</div>
                    </div>
                    <div style={{ ...kpiCardStyle("muted"), minWidth: 0 }}>
                      <div style={{ fontSize: "0.6rem", letterSpacing: "0.08em", textTransform: "uppercase", color: "rgba(255,255,255,0.62)", fontWeight: 700 }}>REMAINING</div>
                      <div style={{ marginTop: "0.12rem", fontSize: "0.92rem", fontWeight: 900 }}>{remainingKpiValue}</div>
                    </div>
                    <div style={{ ...kpiCardStyle("muted"), minWidth: 0 }}>
                      <div style={{ fontSize: "0.6rem", letterSpacing: "0.08em", textTransform: "uppercase", color: "rgba(255,255,255,0.62)", fontWeight: 700 }}>LEFT</div>
                      <div style={{ marginTop: "0.12rem", fontSize: "0.92rem", fontWeight: 900 }}>{daysLeftValue}</div>
                    </div>
                  </div>
                </div>
              </>
            ) : null}

            <button
              type="button"
              onClick={() => setSprintStatusExpanded((prev) => !prev)}
              style={{ ...btnBase, width: "100%", marginTop: "0.7rem", justifyContent: "center", background: "transparent", borderColor: "rgba(255,255,255,0.12)", borderLeft: "none", borderRight: "none", borderBottom: "none", borderRadius: 0, color: "rgba(255,255,255,0.78)" }}
              aria-label={sprintStatusExpanded ? "Collapse sprint status" : "Expand sprint status"}
            >
              <span style={{ display: "inline-flex", alignItems: "center", gap: "0.3rem" }}>
                <span>{sprintStatusExpanded ? "Collapse" : "Expand"}</span>
                <ChevronIcon direction={sprintStatusExpanded ? "up" : "down"} size={14} />
              </span>
            </button>
          </div>
      );
    }

    return (
      <div style={{ ...cardStyle, padding: isMobile ? "0.75rem 0.85rem" : "1.05rem 1.15rem", borderRadius: 18 }}>
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "120px minmax(360px,1fr)", gridTemplateRows: isMobile ? undefined : "auto auto", columnGap: isMobile ? undefined : "18px", rowGap: "0.55rem", alignItems: "start" }}>
          <div style={{ gridColumn: isMobile ? "1" : "1 / span 2", gridRow: "1", display: "flex", alignItems: "baseline", gap: "0.55rem", flexWrap: "wrap" }}>
            <div style={{ fontSize: isMobile ? "1.1rem" : "1.26rem", fontWeight: 900, minWidth: 0 }} title={displaySprintName}>
              Sprint Status — <span style={{ display: "inline-block", maxWidth: isMobile ? "min(220px, calc(100% - 1rem))" : 340, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", verticalAlign: "bottom" }}>{truncatedSprintName}</span>
            </div>
            <Pill label={pillLabel} tone={pillTone} />
            <Pill label={modeChipLabel} tone={modeChipTone} />
            <button
              type="button"
              style={{ ...btnBase, marginLeft: "auto", padding: "0.28rem 0.52rem", background: "transparent", borderColor: "rgba(255,255,255,0.2)", color: "rgba(255,255,255,0.82)", fontSize: "0.75rem" }}
              onClick={openSprintEditor}
            >
              Edit plan
            </button>
          </div>


          <div style={{ gridColumn: "1", gridRow: "2" }}>
            {sectionLabel("You are here")}
            <div style={{ display: "flex", alignItems: "baseline", gap: "0.35rem", marginTop: "0.42rem" }}>
              <span style={{ fontSize: "2.05rem", fontWeight: 860, lineHeight: 1, color: "white" }}>{displayWeight !== null ? displayWeight.toFixed(1) : "—"}</span>
              <span style={{ fontSize: "0.9rem", fontWeight: 700, color: COLORS.lightBlue }}>lbs</span>
            </div>
            <div style={{ marginTop: "0.32rem", display: "inline-flex", alignItems: "center", gap: "0.16rem", padding: "0.1rem", borderRadius: 999, border: "1px solid rgba(255,255,255,0.14)", background: "rgba(255,255,255,0.02)" }}>
              <button
                type="button"
                onClick={() => handleSprintStatusDisplayModeChange("avg")}
                title="Uses moving average to smooth fluctuations."
                style={{ ...btnBase, padding: "0.12rem 0.42rem", borderRadius: 999, fontSize: "0.62rem", lineHeight: 1.1, color: sprintStatusDisplayMode === "avg" ? "rgba(255,255,255,0.94)" : "rgba(255,255,255,0.58)", background: sprintStatusDisplayMode === "avg" ? "rgba(255,255,255,0.11)" : "transparent", borderColor: "transparent", boxShadow: "none" }}
              >
                Avg
              </button>
              <button
                type="button"
                onClick={() => handleSprintStatusDisplayModeChange("today")}
                title="Uses latest logged weight."
                style={{ ...btnBase, padding: "0.12rem 0.42rem", borderRadius: 999, fontSize: "0.62rem", lineHeight: 1.1, color: sprintStatusDisplayMode === "today" ? "rgba(255,255,255,0.94)" : "rgba(255,255,255,0.58)", background: sprintStatusDisplayMode === "today" ? "rgba(255,255,255,0.11)" : "transparent", borderColor: "transparent", boxShadow: "none" }}
              >
                Last
              </button>
            </div>
          </div>

          <div style={{ gridColumn: isMobile ? "1" : "2", gridRow: isMobile ? "3" : "2", borderLeft: isMobile ? "none" : "1px solid rgba(255,255,255,0.08)", paddingLeft: isMobile ? "0" : "1rem", borderTop: isMobile ? "1px solid rgba(255,255,255,0.08)" : "none", paddingTop: isMobile ? "0.75rem" : "0" }}>
            {!isMaintain ? null : (
              <>
                {planVsActualHeader}
                <div style={{ marginTop: "0.36rem", fontSize: "0.84rem", fontWeight: 700, color: alignColor, display: "flex", alignItems: "center", gap: "0.34rem", flexWrap: "wrap" }}>
                  <span>{alignCopy}</span>
                  {showTemporalStatusInline ? (
                    <>
                      <span style={{ color: "rgba(255,255,255,0.36)", fontWeight: 600 }}>•</span>
                      <span style={{ color: temporalStatusColor }}>{temporalStatusCompact}</span>
                    </>
                  ) : null}
                </div>
                {alignSecondaryCopy ? <div style={{ marginTop: "0.16rem", fontSize: "0.7rem", color: "rgba(255,255,255,0.68)", fontWeight: 600 }}>{alignSecondaryCopy}</div> : null}
              </>
            )}

            <div style={{ marginTop: isMaintain ? "0.4rem" : "0", width: "100%" }}>
              {!hasTargetWeight ? (
                <div style={{ border: "1px dashed rgba(255,255,255,0.22)", borderRadius: 10, padding: "0.55rem 0.6rem", fontSize: "0.72rem", color: "rgba(255,255,255,0.62)" }}>
                  Set a target weight to enable Sprint Status.
                </div>
              ) : isMaintain ? (
                <div style={{ display: "grid", gap: "0.16rem" }}>
                  <div style={{ position: "relative" }}>
                  {hasTargetWeight && sprintTarget ? (
                    <div style={{ position: "absolute", bottom: "calc(100% + 4px)", left: "50%", transform: "translateX(-50%)", display: "inline-flex", flexDirection: "column", alignItems: "center", border: "1px solid rgba(255,255,255,0.14)", borderRadius: 6, padding: "0.08rem 0.3rem", background: "rgba(255,255,255,0.04)", lineHeight: 1.15, whiteSpace: "nowrap" }}>
                      <span style={{ fontSize: "0.48rem", color: "rgba(255,255,255,0.45)", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em" }}>Target</span>
                      <span style={{ fontSize: "0.6rem", color: "rgba(255,255,255,0.78)", fontWeight: 800 }}>{sprintTarget.toFixed(1)}</span>
                    </div>
                  ) : null}
                  <div style={{ position: "relative", height: 17, borderRadius: 999, overflow: "hidden", background: "linear-gradient(90deg, rgba(148,163,184,0.18), rgba(148,163,184,0.28))" }}>
                    {/* Blue in-range band */}
                    <div style={{ position: "absolute", left: `${mGoodStartPct}%`, width: `${Math.max(0, mGoodEndPct - mGoodStartPct)}%`, top: 2, bottom: 2, borderRadius: 999, background: "rgba(59,130,246,0.38)" }} />
                    {/* Center target line */}
                    <div style={{ position: "absolute", left: "50%", top: 0, bottom: 0, width: 2, transform: "translateX(-50%)", background: "rgba(255,255,255,0.50)" }} />
                    {actualPct !== null ? <div style={{ position: "absolute", left: `${actualPct}%`, top: "50%", width: 16, height: 16, borderRadius: 999, background: actualDotColor, transform: "translate(-50%, -50%)", boxShadow: `0 0 7px ${actualDotColor}99` }} /> : null}
                  </div>
                  </div>
                  {!hasCurrentWeight ? <div style={{ fontSize: "0.66rem", color: "rgba(255,255,255,0.65)" }}>Log today’s weight to see status.</div> : null}
                  <div style={{ position: "relative", height: 20, marginTop: "0.02rem" }}>
                    {actualPct !== null ? (
                      <div style={{ position: "absolute", left: clampLabelLeft(actualPct, nowLabelWidth), top: 0, display: "inline-flex", alignItems: "center", gap: "0.25rem", fontSize: "0.62rem", color: "rgba(255,255,255,0.84)", whiteSpace: "nowrap" }}>
                        <span style={{ width: 7, height: 7, borderRadius: 999, background: actualDotColor, flexShrink: 0 }} />
                        <span>{nowLabel}</span>
                      </div>
                    ) : null}
                  </div>
                </div>
              ) : (
                <div style={{ display: "flex", alignItems: "center", gap: "0.62rem" }}>
                  <div style={{ display: "inline-flex", flexDirection: "column", width: "auto", flexShrink: 0, border: "1px solid rgba(255,255,255,0.1)", background: "rgba(255,255,255,0.04)", borderRadius: 6, padding: "0.1rem 0.28rem", lineHeight: 1.05 }}>
                    <div style={{ fontSize: "10px", lineHeight: "11px", color: "rgba(255,255,255,0.55)", fontWeight: 700 }}>Start</div>
                    <div style={{ fontSize: "11px", lineHeight: "12px", color: "rgba(255,255,255,0.75)", fontWeight: 600 }}>{sprintStartWeightLabel}</div>
                    <div style={{ fontSize: "10px", lineHeight: "11px", color: "rgba(255,255,255,0.55)", fontWeight: 600 }}>{sprintStartDateLabel}</div>
                  </div>

                  <div style={{ flex: 1, minWidth: 0, display: "grid", gap: "0.16rem" }}>
                    <div>
                      {planVsActualHeader}
                      <div style={{ marginTop: "0.2rem", fontSize: "0.84rem", fontWeight: 700, color: alignColor, display: "flex", alignItems: "center", gap: "0.34rem", flexWrap: "wrap" }}>
                        <span>{alignCopy}</span>
                        {showTemporalStatusInline ? (
                          <>
                            <span style={{ color: "rgba(255,255,255,0.36)", fontWeight: 600 }}>•</span>
                            <span style={{ color: temporalStatusColor }}>{temporalStatusCompact}</span>
                          </>
                        ) : null}
                      </div>
                      {alignSecondaryCopy ? <div style={{ marginTop: "0.1rem", fontSize: "0.7rem", color: "rgba(255,255,255,0.68)", fontWeight: 600 }}>{alignSecondaryCopy}</div> : null}
                    </div>
                    <div style={{ position: "relative", height: 17, borderRadius: 999, background: "linear-gradient(90deg, rgba(148,163,184,0.2), rgba(148,163,184,0.28))", overflow: "visible" }}>
                      {gapStartPct !== null && gapWidthPct >= 1.5 ? <div style={{ position: "absolute", top: 2, bottom: 2, left: `${gapStartPct}%`, width: `${gapWidthPct}%`, borderRadius: 6, background: derivedStatus === "ahead" ? "linear-gradient(90deg, rgba(74,222,128,0.34), rgba(74,222,128,0.52))" : derivedStatus === "behind" ? "linear-gradient(90deg, rgba(248,113,113,0.32), rgba(248,113,113,0.48))" : "linear-gradient(90deg, rgba(59,130,246,0.22), rgba(59,130,246,0.46))" }} /> : null}
                      {plannedPct !== null ? <div style={{ position: "absolute", top: "50%", left: `${plannedPct}%`, transform: "translate(-50%, -50%)", width: 14, height: 14, borderRadius: 999, border: `2.2px solid ${COLORS.purple}`, background: "rgba(11,15,20,0.95)" }} /> : null}
                      {actualPct !== null ? <div style={{ position: "absolute", top: "50%", left: `${actualPct}%`, transform: "translate(-50%, -50%)", width: 16, height: 16, borderRadius: 999, background: actualDotColor, boxShadow: `0 0 7px ${actualDotColor}99` }} /> : null}
                    </div>
                    <div style={{ position: "relative", height: 24, marginTop: "0.02rem" }}>
                      {showPlanInlineLabel ? <div style={{ position: "absolute", left: clampLabelLeft(plannedPct, planLabelWidth), top: planLabelTop, display: "inline-flex", alignItems: "center", gap: "0.25rem", fontSize: "0.62rem", color: "rgba(255,255,255,0.75)", whiteSpace: "nowrap" }}><span style={{ width: 7, height: 7, borderRadius: 999, border: `1.4px solid ${COLORS.purple}`, background: "transparent" }} /><span>{planLabel}</span></div> : null}
                      <div style={{ position: "absolute", left: clampLabelLeft(actualPct, nowLabelWidth), top: nowLabelTop, display: "inline-flex", alignItems: "center", gap: "0.25rem", fontSize: "0.62rem", color: "rgba(255,255,255,0.84)", whiteSpace: "nowrap" }}><span style={{ width: 7, height: 7, borderRadius: 999, background: actualDotColor }} /><span>{nowLabel}</span></div>
                    </div>
                  </div>

                  <div style={{ display: "inline-flex", flexDirection: "column", width: "auto", flexShrink: 0, border: "1px solid rgba(255,255,255,0.1)", background: "rgba(255,255,255,0.04)", borderRadius: 6, padding: "0.1rem 0.28rem", lineHeight: 1.05, textAlign: "right" }}>
                    <div style={{ fontSize: "10px", lineHeight: "11px", color: "rgba(255,255,255,0.55)", fontWeight: 700 }}>Goal</div>
                    <div style={{ fontSize: "11px", lineHeight: "12px", color: "rgba(255,255,255,0.75)", fontWeight: 600 }}>{sprintEndWeightLabel}</div>
                    <div style={{ fontSize: "10px", lineHeight: "11px", color: "rgba(255,255,255,0.55)", fontWeight: 600 }}>{sprintEndDateLabel}</div>
                  </div>
                </div>
              )}
            </div>

            <div
              style={{
                marginTop: "0.6rem",
                display: "grid",
                gridTemplateColumns: isDashboardHeroStacked ? "repeat(2, minmax(0, 1fr))" : "repeat(4, minmax(0, 1fr))",
                gap: "0.55rem",
                alignItems: "stretch",
                minWidth: 0,
              }}
            >
              <div style={{ ...kpiCardStyle(netCardTone), padding: "0.5rem 0.64rem", minWidth: 0 }}>
                <div style={{ fontSize: "0.6rem", letterSpacing: "0.09em", textTransform: "uppercase", color: "rgba(255,255,255,0.62)", fontWeight: 700 }}>NET CHANGE</div>
                <div style={{ marginTop: "0.15rem", fontSize: "0.96rem", fontWeight: 900 }}>{netKpiValue}</div>
              </div>
              <div style={{ ...kpiCardStyle(rateCardTone), padding: "0.5rem 0.64rem", minWidth: 0 }}>
                <div style={{ fontSize: "0.6rem", letterSpacing: "0.09em", textTransform: "uppercase", color: "rgba(255,255,255,0.62)", fontWeight: 700 }}>PACE</div>
                <div style={{ marginTop: "0.15rem", fontSize: "0.96rem", fontWeight: 900 }}>{rateKpiValue}</div>
              </div>
              <div style={{ ...kpiCardStyle("muted"), padding: "0.5rem 0.64rem", minWidth: 0 }}>
                <div style={{ fontSize: "0.6rem", letterSpacing: "0.09em", textTransform: "uppercase", color: "rgba(255,255,255,0.62)", fontWeight: 700 }}>REMAINING</div>
                <div style={{ marginTop: "0.15rem", fontSize: "0.96rem", fontWeight: 900 }}>{remainingKpiValue}</div>
              </div>
              <div style={{ ...kpiCardStyle("muted"), padding: "0.5rem 0.64rem", minWidth: 0 }}>
                <div style={{ fontSize: "0.6rem", letterSpacing: "0.09em", textTransform: "uppercase", color: "rgba(255,255,255,0.62)", fontWeight: 700 }}>LEFT</div>
                <div style={{ marginTop: "0.15rem", fontSize: "0.96rem", fontWeight: 900 }}>{daysLeftValue}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  })();

  const dailyTargetsMode: "cut" | "bulk" | "maintain" =
    recalcData?.goalDirection === "cut" ? "cut" :
    recalcData?.goalDirection === "gain" ? "bulk" : "maintain";
  const dtNet = effectiveNetCaloriesForForecast;
  const dtLbsPerWeek = dtNet !== null ? (dtNet * 7) / 3500 : null;
  const dtBand = dtLbsPerWeek !== null ? getRateBand(dailyTargetsMode, dtLbsPerWeek) : null;
  const dtModeLabel = dtBand ? dtBand.label.replace(/ cut$| bulk$/i, "") : null;
  const dtModeTone = dailyTargetsMode === "cut" ? "blue" : dailyTargetsMode === "bulk" ? "orange" : "purple";

  const dailyTargetsCard = activeSprint ? (
    <DailyTargetsCard
      targetIntake={targetIntakeForForecast}
      targetNet={effectiveNetCaloriesForForecast}
      recalcData={recalcData ? {
        requiredIntake: recalcData.requiredIntake,
        requiredNetPerDay: recalcData.requiredNetPerDay,
        tdee: recalcData.tdee,
      } : null}
      gender={vitals.gender ?? null}
      modeLabel={dtModeLabel}
      modeTone={dtModeTone}
    />
  ) : null;

  const trackerStats = (
    currentPage === "viewer" ? (
      <div style={{ ...cardStyle, padding: "1.5rem", borderRadius: 18 }}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: "0.75rem", alignItems: "center", flexWrap: "wrap" }}>
          <div style={{ fontSize: "1.25rem", fontWeight: 900 }}>Sprint Stats</div>
          <div style={{ fontSize: "0.92rem", fontWeight: 700, color: "rgba(255,255,255,0.72)" }}>{CHART_METRIC_META[viewerMetric].title}</div>
        </div>
        <div style={{ marginTop: "1rem", display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: "0.65rem" }}>
          {viewerSummaryChips}
        </div>
      </div>
    ) : (
      <div style={{ ...cardStyle, padding: "1.5rem", borderRadius: 18 }}>
        <div style={{ fontSize: "1.25rem", fontWeight: 900 }}>Stats</div>
        <div style={{ marginTop: "1rem", display: "grid", gridTemplateColumns: isMobile ? "repeat(auto-fit, minmax(110px, 1fr))" : "repeat(auto-fit, minmax(130px, 1fr))", gap: "0.65rem" }}>
          <StatCard label="Starting Weight" value={startWeight !== null ? startWeight.toFixed(1) : null} sub={sprintStartEffective ? `Sprint start: ${formatMDY(sprintStartEffective)}` : null} accent={COLORS.blue} />
          <StatCard label={isArchivedOrViewer ? "Ending Weight" : "Current weight"} value={endWeight !== null ? endWeight.toFixed(1) : null} sub={endingWeightSub} accent={COLORS.blue} />
          <StatCard label="Net Loss" value={netLoss !== null ? `${netLoss.toFixed(1)} lbs` : null} accent={COLORS.green} />
          <StatCard label="Weekly Avg Loss" value={weeklyAvgLoss !== null ? `${weeklyAvgLoss.toFixed(2)} lbs/wk` : null} accent={COLORS.green} />
          <button
            type="button"
            onClick={() => setTrackerCalorieCompareMode((m) => (m === "net" ? "intake" : "net"))}
            style={{ ...btnBase, background: "transparent", padding: 0, border: "none", textAlign: "left" }}
            title="Toggle net/intake comparison"
          >
            <StatCard
              label={trackerCalorieComparison?.mode === "intake" ? "Calorie Compare (Intake)" : "Calorie Compare (Net)"}
              value={trackerCalorieComparison ? `Δ ${trackerCalorieComparison.delta >= 0 ? "+" : ""}${Math.round(trackerCalorieComparison.delta).toLocaleString()} cal/day` : null}
              sub={
                trackerCalorieComparison
                  ? `Target ${Math.round(trackerCalorieComparison.target).toLocaleString()} • Actual ${Math.round(trackerCalorieComparison.actual).toLocaleString()}`
                  : "Need sprint calorie data"
              }
              accent={COLORS.pink}
            />
          </button>
          <StatCard
            label="Days Tracked"
            value={totalSprintDays ? `${daysTracked}/${totalSprintDays}` : daysTracked || null}
            sub={trackedPct !== null ? `${trackedPct.toFixed(0)}% of sprint days` : null}
            accent="rgba(255,255,255,0.92)"
          />
        </div>
      </div>
    )
  );

  const recalibrateCard = (() => {
    if (!activeSprint || !recalcData) return null;
    const { requiredNetPerDay, requiredIntake, projectedDateFromCurrentPlan, targetDate, currentPlanNet, remainingDays, isStale, staleDays, tdee } = recalcData;
    const goalWeightValue = safeNumber(forecast.targetWeight);
    const goalWeightText = goalWeightValue !== null ? `${goalWeightValue.toFixed(1)} lbs` : "your goal weight";
    const goalDateText = isYmd(targetDate) ? formatMDY(targetDate) : "your goal date";
    const currentNetRounded = currentPlanNet !== null ? Math.round(currentPlanNet) : null;
    const currentIntakeRounded = currentPlanNet !== null && tdee !== null ? Math.round(tdee + currentPlanNet) : null;
    const currentTargetHeader = currentIntakeRounded !== null ? `At your current target (~${currentIntakeRounded.toLocaleString()} cal/day)` : "At your current target";
    const requiredNetRounded = requiredNetPerDay !== null ? Math.round(requiredNetPerDay) : null;
    const projectedDateText = isYmd(projectedDateFromCurrentPlan) ? formatMDY(projectedDateFromCurrentPlan) : "—";
    const formatGoalDateLabel = (ymd: string | null | undefined, fallback: string) => {
      const dateObj = ymd ? parseISODateYMD(ymd) : null;
      return dateObj
        ? new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric" }).format(dateObj)
        : fallback;
    };
    const projectedGoalDateLabel = formatGoalDateLabel(projectedDateFromCurrentPlan, projectedDateText);
    const targetGoalDateLabel = formatGoalDateLabel(targetDate, goalDateText);
    const safeProjectedGoalDateLabel = formatGoalDateLabel(recalcSafeProjectedDate, recalcSafeProjectedDateText);
    const requiredLbsPerWeek = requiredNetPerDay !== null ? Math.abs((requiredNetPerDay / 3500) * 7).toFixed(1) : null;
    const currentWeeklyRateText = currentPlanNet !== null ? Math.abs((currentPlanNet / 3500) * 7).toFixed(1) : null;

    let daysRelative: number | null = null;
    if (isYmd(projectedDateFromCurrentPlan) && isYmd(targetDate)) {
      daysRelative = daysBetweenInclusiveStartExclusiveEnd(projectedDateFromCurrentPlan, targetDate);
    }

    const daysRelativeText = daysRelative === null
      ? "Timing unavailable"
      : daysRelative === 0
        ? "On time"
        : `${Math.abs(daysRelative)} days ${daysRelative > 0 ? "early" : "late"}`;

    const currentTargetContext = `Hit ${goalWeightText} on ${projectedDateText}`;
    const recommendedOutcome = `Hit ${goalWeightText} by ${goalDateText}`;
    const currentOutcome = `Hit ${goalWeightText} on ${projectedDateText}`;
    const primaryIntakeValue = recalcRequiresSafePlan ? recalcSafeIntake : requiredIntake;
    const primaryNetValue = recalcRequiresSafePlan ? recalcSafeNetRounded : requiredNetRounded;
    const primarySignedRate = formatSignedPace(recalcRequiresSafePlan ? (recalcSafeNetPerDay !== null ? (recalcSafeNetPerDay / 3500) * 7 : null) : (requiredNetPerDay !== null ? (requiredNetPerDay / 3500) * 7 : null), 1, "lb/week");
    const currentSignedRate = formatSignedPace(currentPlanNet !== null ? (currentPlanNet / 3500) * 7 : null, 1, "lb/week");
    const safeRateText = `${recalcRecommendedRateMax.toFixed(1)} lb/week`;
    const currentTargetSubline = currentIntakeRounded !== null ? `Current target ~${currentIntakeRounded.toLocaleString()} cal/day` : "Current target —";
    const primaryCaloriesLine = `Calories: ${primaryIntakeValue !== null ? `~${Math.round(primaryIntakeValue).toLocaleString()}` : "~—"} intake · ${primaryNetValue !== null ? `${primaryNetValue.toLocaleString()}` : "—"} net`;
    const currentCaloriesLine = `Calories: ${currentIntakeRounded !== null ? `~${currentIntakeRounded.toLocaleString()}` : "~—"} intake · ${currentNetRounded !== null ? `${currentNetRounded.toLocaleString()}` : "—"} net`;
    const primaryOutcomeLine = `Hit target weight (${goalWeightText}) by ${recalcRequiresSafePlan ? recalcSafeProjectedDateText : goalDateText}`;
    const currentOutcomeLine = `Hit target weight (${goalWeightText}) on ${projectedDateText}`;
    const goalDateShiftText = daysRelative === null ? "Timing unavailable" : daysRelative === 0 ? "On time" : `${Math.abs(daysRelative)} days ${daysRelative > 0 ? "earlier" : "later"}`;
    const updatedDateForComparison = recalcRequiresSafePlan ? recalcSafeProjectedDate : targetDate;
    const updatedVsCurrentDateDelta = isYmd(updatedDateForComparison) && isYmd(projectedDateFromCurrentPlan)
      ? daysBetweenInclusiveStartExclusiveEnd(updatedDateForComparison, projectedDateFromCurrentPlan)
      : null;
    const modalShiftText = updatedVsCurrentDateDelta === null ? "on a different timeline" : updatedVsCurrentDateDelta === 0 ? "on the same timeline" : `${Math.abs(updatedVsCurrentDateDelta)} days ${updatedVsCurrentDateDelta > 0 ? "earlier" : "later"}`;

    // Hide the "Adjust goal date" option when accepting it would be a no-op
    // (the date projected from the current pace already equals the target).
    const canAdjustGoalDate = !isArchivedOrViewer
      && isYmd(projectedDateFromCurrentPlan)
      && projectedDateFromCurrentPlan !== targetDate;

    const timingTone: React.CSSProperties = daysRelative === null
      ? { background: "rgba(255,255,255,0.08)", color: "rgba(255,255,255,0.72)", border: "1px solid rgba(255,255,255,0.08)" }
      : daysRelative > 0
        ? { background: "rgba(34,197,94,0.18)", color: "#86efac", border: "1px solid rgba(74,222,128,0.22)" }
        : daysRelative < 0
          ? { background: "rgba(245,158,11,0.18)", color: "#fcd34d", border: "1px solid rgba(245,158,11,0.22)" }
          : { background: "rgba(255,255,255,0.08)", color: "rgba(255,255,255,0.72)", border: "1px solid rgba(255,255,255,0.08)" };

    const optionCardBase: React.CSSProperties = {
      width: "100%",
      borderRadius: 14,
      padding: isDashboardMobile ? "0.78rem" : "0.9rem",
      display: "flex",
      flexDirection: "column",
      gap: "0.55rem",
      minWidth: 0,
      height: "100%",
      boxSizing: "border-box",
    };

    const optionTitleStyle: React.CSSProperties = {
      fontSize: "0.74rem",
      fontWeight: 800,
      letterSpacing: "0.06em",
      textTransform: "uppercase",
    };

    const metricListStyle: React.CSSProperties = {
      display: "grid",
      gap: "0.28rem",
      color: "rgba(255,255,255,0.82)",
      fontSize: isDashboardMobile ? "0.8rem" : "0.82rem",
      lineHeight: 1.35,
      minWidth: 0,
    };


    const outcomeBlock = (label: string, primary: string, secondary?: string, emphasize = false) => (
      <div style={{ borderTop: "1px solid rgba(255,255,255,0.08)", paddingTop: "0.55rem", display: "grid", gap: "0.18rem", minWidth: 0 }}>
        <div style={{ fontSize: "0.66rem", fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase", color: emphasize ? "rgba(255,255,255,0.7)" : "rgba(255,255,255,0.5)" }}>{label}</div>
        <div style={{ fontSize: emphasize ? (isDashboardMobile ? "0.92rem" : "0.98rem") : "0.9rem", fontWeight: emphasize ? 800 : 700, color: "white", lineHeight: 1.3, overflowWrap: "anywhere" }}>{primary}</div>
        {secondary ? <div style={{ fontSize: "0.78rem", color: emphasize ? "rgba(255,255,255,0.78)" : timingTone.color, lineHeight: 1.3 }}>{secondary}</div> : null}
      </div>
    );

    const openRecalibrateConfirm = () => {
      if (requiredNetPerDay === null || !recalcPrimaryCanApply || recalcPrimaryNextAssumedValue === null) return;
      const nextNet = recalcPrimaryRecommendedNet ?? Math.round(requiredNetPerDay);
      const isAheadOfSchedule = daysRelative !== null && daysRelative > 0;
      const isBehindSchedule = daysRelative !== null && daysRelative < 0;
      const isBehindFastestSafePlan = isBehindSchedule && recalcRequiresSafePlan;
      setRecalcConfirm({
        kind: "calories",
        title: "Update your plan",
        introLines: [
          isBehindFastestSafePlan
            ? "You're behind schedule. Updating your calories will get you as close as possible to your target date."
            : isAheadOfSchedule
              ? "You're ahead of schedule. Updating your calories will keep you aligned with your target date."
              : isBehindSchedule
                ? "You're behind schedule. Updating your calories will keep you on track for your target date."
                : "You're on track. Updating your calories will keep you aligned with your target date.",
        ],
        currentLines: [
          `Calories: ~${currentIntakeRounded !== null ? currentIntakeRounded.toLocaleString() : "—"} intake · ${currentNetRounded !== null ? currentNetRounded.toLocaleString() : "—"} net`,
          `Pace: ${currentSignedRate}`,
          `Goal: ${goalWeightText} by ${projectedGoalDateLabel}`,
        ],
        updatedLines: [
          `Calories: ~${primaryIntakeValue !== null ? Math.round(primaryIntakeValue).toLocaleString() : "—"} intake · ${primaryNetValue !== null ? primaryNetValue.toLocaleString() : "—"} net`,
          `Pace: ${primarySignedRate}`,
          `Goal: ${goalWeightText} by ${recalcRequiresSafePlan ? safeProjectedGoalDateLabel : targetGoalDateLabel}`,
        ],
        outcomeLine: null,
        updatedHeading: isBehindFastestSafePlan ? "Fastest safe plan" : "Updated",
        confirmLabel: recalcRequiresSafePlan ? "Use fastest safe plan" : "Use updated plan",
        nextAssumedValue: recalcPrimaryNextAssumedValue,
        nextNetValue: nextNet,
      });
    };

    const openKeepCurrentConfirm = () => {
      if (!isYmd(projectedDateFromCurrentPlan) || projectedDateFromCurrentPlan === targetDate) return;
      setRecalcConfirm({
        kind: "date",
        title: "Adjust goal date",
        introLines: [
          `Your current calorie target reaches your goal on ${projectedDateText}.`,
          "We'll update your goal date to match that pace.",
        ],
        updatedLines: [
          currentCaloriesLine,
          `Pace: ${currentSignedRate}`,
          `→ Hits ${goalWeightText} on ${projectedDateText}`,
        ],
        outcomeLine: `You’ll reach your goal ${goalDateShiftText.toLowerCase()}.`,
        confirmLabel: "Adjust goal date",
        nextTargetDate: projectedDateFromCurrentPlan,
      });
    };

    const primaryButtonStyle: React.CSSProperties = {
      ...btnBase,
      width: isDashboardMobile ? "100%" : "auto",
      justifyContent: "center",
      background: actionColors.primaryGradient,
      borderColor: "rgba(148,163,184,0.45)",
      color: actionColors.primaryText,
      padding: isDashboardMobile ? "0.62rem 0.85rem" : "0.68rem 0.95rem",
      fontSize: "0.84rem",
      lineHeight: 1.15,
      minHeight: 40,
      fontWeight: 800,
      flexShrink: 0,
      minWidth: isDashboardMobile ? undefined : 160,
      maxWidth: isDashboardMobile ? undefined : 240,
      alignSelf: isDashboardMobile ? "stretch" : "flex-start",
      marginTop: isDashboardMobile ? undefined : "auto",
    };

    const secondaryButtonStyle: React.CSSProperties = {
      ...btnBase,
      width: isDashboardMobile ? "100%" : "auto",
      justifyContent: "center",
      background: "rgba(255,255,255,0.03)",
      borderColor: "rgba(255,255,255,0.14)",
      color: "rgba(255,255,255,0.88)",
      padding: isDashboardMobile ? "0.62rem 0.85rem" : "0.68rem 0.95rem",
      fontSize: "0.82rem",
      lineHeight: 1.15,
      minHeight: 40,
      fontWeight: 700,
      flexShrink: 0,
      minWidth: isDashboardMobile ? undefined : 160,
      maxWidth: isDashboardMobile ? undefined : 240,
      alignSelf: isDashboardMobile ? "stretch" : "flex-start",
      marginTop: isDashboardMobile ? undefined : "auto",
    };

    const mobileExpandedContent = (
      <div style={{ marginTop: "0.8rem", borderTop: "1px solid rgba(255,255,255,0.08)", paddingTop: "0.78rem", display: "grid", gap: "0.75rem", minWidth: 0 }}>
        <div style={{ fontSize: "0.78rem", fontWeight: 700, color: "rgba(255,255,255,0.64)", lineHeight: 1.4 }}>
          To stay aligned with your target date, update your target:
        </div>

        <div style={{ display: "grid", gap: "0.65rem", minWidth: 0 }}>
          <div
            style={{
              ...optionCardBase,
              background: "linear-gradient(180deg, rgba(59,130,246,0.16), rgba(255,255,255,0.05))",
              border: "1px solid rgba(96,165,250,0.32)",
              boxShadow: "0 10px 28px rgba(15,23,42,0.18)",
            }}
          >
            <div style={{ ...optionTitleStyle, color: "#dbeafe" }}>{recalcPrimaryOptionTitle}</div>
            <div style={metricListStyle}>
              <div>{primaryCaloriesLine}</div>
              <div>Pace: {primarySignedRate}</div>
            </div>
            <div style={{ borderTop: "1px solid rgba(255,255,255,0.08)", paddingTop: "0.55rem", fontSize: "0.9rem", fontWeight: 700, color: "white", lineHeight: 1.35, overflowWrap: "anywhere" }}>
              {primaryOutcomeLine}
            </div>
            <button
              type="button"
              style={primaryButtonStyle}
              disabled={!recalcPrimaryCanApply || remainingDays <= 0}
              onClick={(event) => {
                event.stopPropagation();
                openRecalibrateConfirm();
              }}
            >
              {recalcPrimaryCtaLabel}
            </button>
          </div>

          {canAdjustGoalDate ? (
            <div
              style={{
                ...optionCardBase,
                background: "rgba(255,255,255,0.03)",
                border: "1px solid rgba(255,255,255,0.1)",
              }}
            >
              <div style={{ ...optionTitleStyle, color: "rgba(255,255,255,0.7)" }}>Adjust goal date</div>
              <div style={metricListStyle}>
                <div>{currentCaloriesLine}</div>
                <div>Pace: {currentSignedRate}</div>
              </div>
              <div style={{ borderTop: "1px solid rgba(255,255,255,0.08)", paddingTop: "0.55rem", display: "grid", gap: "0.18rem", minWidth: 0 }}>
                <div style={{ fontSize: "0.9rem", fontWeight: 700, color: "white", lineHeight: 1.35, overflowWrap: "anywhere" }}>New target date: {projectedDateText}</div>
                <div style={{ fontSize: "0.8rem", color: timingTone.color, lineHeight: 1.3 }}>{goalDateShiftText}</div>
              </div>
              <button
                type="button"
                style={secondaryButtonStyle}
                onClick={(event) => {
                  event.stopPropagation();
                  openKeepCurrentConfirm();
                }}
              >
                Adjust goal date
              </button>
            </div>
          ) : null}
        </div>
      </div>
    );

    if (recalcPrimaryAlreadyAligned) {
      return (
        <div
          style={{
            ...cardStyle,
            width: "100%",
            borderRadius: 18,
            padding: isDashboardMobile ? "0.8rem 0.85rem" : "0.9rem",
            minWidth: 0,
            overflow: "hidden",
            background: "linear-gradient(180deg, rgba(255,255,255,0.04), rgba(255,255,255,0.02))",
            border: "1px solid rgba(255,255,255,0.08)",
          }}
        >
          <div style={{ display: "grid", gap: "0.55rem", minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: "0.45rem", flexWrap: "wrap", minWidth: 0 }}>
              <div style={{ fontSize: isDashboardMobile ? "0.95rem" : "1rem", fontWeight: 800, color: "rgba(255,255,255,0.92)" }}>Recalibrate sprint</div>
              <div style={{ display: "inline-flex", alignItems: "center", borderRadius: 999, padding: "0.24rem 0.6rem", fontSize: "0.76rem", fontWeight: 700, lineHeight: 1.2, background: "rgba(34,197,94,0.14)", color: "#86efac", border: "1px solid rgba(74,222,128,0.18)" }}>
                Up to date
              </div>
            </div>
            <div style={{ fontSize: isDashboardMobile ? "0.84rem" : "0.88rem", color: "rgba(255,255,255,0.74)", lineHeight: 1.45 }}>
              Your current target already matches the recommended plan.
            </div>
          </div>
        </div>
      );
    }

    if (isDashboardMobile) {
      return (
        <div style={{ ...cardStyle, width: "100%", borderRadius: 18, padding: "0.8rem 0.85rem", minWidth: 0, overflow: "hidden" }}>
          <div style={{ display: "grid", gap: rebalanceExpanded ? "0.2rem" : "0.45rem", minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: "0.45rem", flexWrap: "wrap", minWidth: 0 }}>
              <div style={{ fontSize: "0.95rem", fontWeight: 800, color: "rgba(255,255,255,0.92)" }}>Recalibrate sprint</div>
              <div style={{ display: "inline-flex", alignItems: "center", borderRadius: 999, padding: "0.22rem 0.56rem", fontSize: "0.76rem", fontWeight: 700, lineHeight: 1.2, ...timingTone }}>{daysRelativeText}</div>
            </div>
            <div style={{ fontSize: rebalanceExpanded ? "0.84rem" : "0.96rem", fontWeight: 800, color: "white", lineHeight: 1.3, overflowWrap: "anywhere" }}>{currentTargetContext}</div>
          </div>

          {!rebalanceExpanded ? (
            <div style={{ marginTop: "0.72rem", display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: "0.5rem", minWidth: 0 }}>
              <button
                type="button"
                style={primaryButtonStyle}
                disabled={!recalcPrimaryCanApply || remainingDays <= 0}
                onClick={openRecalibrateConfirm}
              >
                {recalcPrimaryCtaLabel}
              </button>
              {canAdjustGoalDate ? (
                <button
                  type="button"
                  style={{ background: "transparent", border: "none", padding: 0, color: "rgba(255,255,255,0.68)", fontSize: "0.8rem", fontWeight: 700, justifySelf: "center" }}
                  onClick={openKeepCurrentConfirm}
                >
                  Adjust goal date
                </button>
              ) : null}
            </div>
          ) : mobileExpandedContent}

          <button
            type="button"
            onClick={() => setRebalanceExpanded((prev) => !prev)}
            style={{ ...btnBase, width: "100%", marginTop: "0.7rem", justifyContent: "center", background: "transparent", borderColor: "rgba(255,255,255,0.12)", borderLeft: "none", borderRight: "none", borderBottom: "none", borderRadius: 0, color: "rgba(255,255,255,0.78)" }}
            aria-label={rebalanceExpanded ? "Collapse recalibrate sprint options" : "Expand recalibrate sprint options"}
          >
            <span style={{ display: "inline-flex", alignItems: "center", gap: "0.3rem" }}>
              <span>{rebalanceExpanded ? "Collapse" : "More options"}</span>
              <ChevronIcon direction={rebalanceExpanded ? "up" : "down"} size={14} />
            </span>
          </button>
        </div>
      );
    }

    return (
      <div style={{ ...cardStyle, borderRadius: 18, alignSelf: "stretch", padding: isMobile ? "0.72rem" : "0.9rem", minHeight: "100%", minWidth: 0, overflow: "hidden" }}>
        <div style={{ display: "grid", gap: "0.85rem", minWidth: 0 }}>
          <div style={{ display: "grid", gap: "0.22rem", minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", flexWrap: "wrap", minWidth: 0 }}>
              <div style={{ fontSize: isMobile ? "0.92rem" : "1rem", fontWeight: 800, color: "rgba(255,255,255,0.92)" }}>Recalibrate sprint</div>
              <div style={{ display: "inline-flex", alignItems: "center", borderRadius: 999, padding: "0.24rem 0.6rem", fontSize: "0.77rem", fontWeight: 700, lineHeight: 1.2, ...timingTone }}>
                {daysRelativeText}
              </div>
            </div>
            <div style={{ fontSize: "0.82rem", fontWeight: 700, color: "rgba(255,255,255,0.62)", lineHeight: 1.35 }}>{currentTargetSubline}</div>
          </div>

          {remainingDays <= 0 ? (
            <div style={{ fontSize: "0.78rem", color: "rgba(254,202,202,0.9)", lineHeight: 1.45 }}>
              Target date has passed. Update your target date to continue planning.
            </div>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 260px), 1fr))", gap: "0.7rem", alignItems: "stretch", minWidth: 0 }}>
              <div
                style={{
                  ...optionCardBase,
                  background: "linear-gradient(180deg, rgba(59,130,246,0.16), rgba(255,255,255,0.05))",
                  border: "1px solid rgba(96,165,250,0.32)",
                  boxShadow: "0 10px 28px rgba(15,23,42,0.16)",
                }}
              >
                <div style={{ ...optionTitleStyle, color: "#dbeafe" }}>{recalcPrimaryOptionTitle}</div>
                <div style={metricListStyle}>
                  <div>{primaryCaloriesLine}</div>
                  <div>Pace: {primarySignedRate}</div>
                </div>
                <div style={{ borderTop: "1px solid rgba(255,255,255,0.08)", paddingTop: "0.55rem", fontSize: "0.92rem", fontWeight: 700, color: "white", lineHeight: 1.35, overflowWrap: "anywhere" }}>
                  {primaryOutcomeLine}
                </div>
                <button
                  type="button"
                  style={primaryButtonStyle}
                  disabled={!recalcPrimaryCanApply || remainingDays <= 0}
                  onClick={openRecalibrateConfirm}
                >
                  {recalcPrimaryCtaLabel}
                </button>
              </div>

              {canAdjustGoalDate ? (
                <div
                  style={{
                    ...optionCardBase,
                    background: "rgba(255,255,255,0.03)",
                    border: "1px solid rgba(255,255,255,0.1)",
                  }}
                >
                  <div style={{ ...optionTitleStyle, color: "rgba(255,255,255,0.7)" }}>Adjust goal date</div>
                  <div style={metricListStyle}>
                    <div>{currentCaloriesLine}</div>
                    <div>Pace: {currentSignedRate}</div>
                  </div>
                  <div style={{ borderTop: "1px solid rgba(255,255,255,0.08)", paddingTop: "0.55rem", display: "grid", gap: "0.18rem", minWidth: 0 }}>
                    <div style={{ fontSize: "0.92rem", fontWeight: 700, color: "white", lineHeight: 1.35, overflowWrap: "anywhere" }}>
                      New target date: {projectedDateText} · {recalcDateComparisonText}
                    </div>
                  </div>
                  <button
                    type="button"
                    style={secondaryButtonStyle}
                    onClick={openKeepCurrentConfirm}
                  >
                    Adjust goal date
                  </button>
                </div>
              ) : null}
            </div>
          )}

          {!remainingDays || remainingDays > 0 ? (
            <div style={{ fontSize: "0.74rem", color: "rgba(255,255,255,0.46)", lineHeight: 1.35 }}>Based on your calorie target</div>
          ) : null}
        </div>
      </div>
    );
  })();

  // ---- Oura Ring state (must be declared before dailyEntryCard which references these directly) ----
  const [ouraConnection, setOuraConnection] = useState<OuraConnection | null>(null);
  const [ouraStatus, setOuraStatus] = useState<"idle" | "syncing" | "error">("idle");
  const [ouraRedirectError, setOuraRedirectError] = useState<string | null>(null);
  const [showOuraUpdatingInfo, setShowOuraUpdatingInfo] = useState(false);

  // Load Oura connection when user signs in/out
  useEffect(() => {
    if (!userId || isDemoMode) { setOuraConnection(null); return; }
    getOuraConnection().then(setOuraConnection).catch(() => setOuraConnection(null));
  }, [userId, isDemoMode]);

  // ---- Apple Health / HealthKit state ----
  const [healthKitEnabled, setHealthKitEnabled] = useState(false);
  const [healthKitStatus, setHealthKitStatus] = useState<"idle" | "syncing" | "error">("idle");
  const [healthKitLastSyncAt, setHealthKitLastSyncAt] = useState<string | null>(null);
  const [activityBurnSource, setActivityBurnSource] = useState<ActivityBurnSource>("manual");

  const { requestPermission: requestHealthKitPermission, syncWeightsFromHealth, syncActiveCaloriesFromHealth, writeWeightToHealth, getAuthorizationStatus } = useHealthKit();

  // Auto-sync HealthKit on mount / when enabled (last 30 days, background, non-blocking)
  useEffect(() => {
    if (!healthKitEnabled || !Capacitor.isNativePlatform() || isDemoMode) return;
    const since = new Date();
    since.setDate(since.getDate() - 30);
    void (async () => {
      try {
        setHealthKitStatus("syncing");
        const [hkWeights, hkCalories] = await Promise.all([
          syncWeightsFromHealth(since),
          syncActiveCaloriesFromHealth(since),
        ]);
        setEntries((prev) => {
          let next = mergeHealthKitWeights(prev, hkWeights, activityBurnSource);
          next = mergeHealthKitActiveCalories(next, hkCalories, activityBurnSource);
          return reconcileEntriesDerivedMetrics(next, vitals);
        });
        setHealthKitLastSyncAt(new Date().toISOString());
        setHealthKitStatus("idle");
      } catch {
        setHealthKitStatus("error");
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [healthKitEnabled, isDemoMode]);

  useEffect(() => {
    if (!initialDataLoaded || !Capacitor.isNativePlatform() || isDemoMode) return;
    let cancelled = false;
    void (async () => {
      const auth = await getAuthorizationStatus();
      if (cancelled) return;
      console.info("[healthkit] startup authorization status", auth);
      const overall = auth?.overallStatus;
      if (overall === "denied" || overall === "restricted") {
        setHealthKitEnabled(false);
        setHealthKitStatus("error");
        const forcedIso = new Date().toISOString();
        healthKitLocalUpdatedAtRef.current = Date.parse(forcedIso);
        await storage.set(STORAGE_KEYS.healthKitEnabled, "0");
        await storage.set(STORAGE_KEYS.healthKitEnabledUpdatedAt, forcedIso);
        console.warn("[healthkit] startup resolution: authorization denied/restricted, forcing disconnected UI state");
      }
    })();
    return () => {
      cancelled = true;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialDataLoaded, isDemoMode]);

  const dailyEntryCard = (
    <div
      ref={dailyEntryCardRef}
      style={{
        ...cardStyle,
        position: "relative",
        borderRadius: 16,
        padding: isMobile ? "0.88rem" : "0.98rem",
        display: "grid",
        gap: "0.86rem",
        maxHeight: isMobile ? "min(78vh, 710px)" : undefined,
        overflowY: isMobile ? "auto" : undefined,
      }}
    >
      {(() => {
        const existingForDate = entriesAsc.find((e) => e.date === currentEntry.date) ?? null;
        const ouraConnected = !!ouraConnection?.enabled;
        const isToday = currentEntry.date === todayYmd;
        const isYesterday = currentEntry.date === addDaysYMD(todayYmd, -1);
        const dateObj = parseISODateYMD(currentEntry.date);
        const maxEntryDate = clampYmdToBounds(addDaysYMD(todayYmd, MAX_FUTURE_DAYS) ?? todayYmd, MIN_YMD, MAX_YMD);
        const weekday = dateObj
          ? new Intl.DateTimeFormat("en-US", { weekday: "short" }).format(dateObj)
          : "--";
        const monthDay = dateObj
          ? new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric" }).format(dateObj)
          : "--";
        const customDateLabel = dateObj
          ? new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric" }).format(dateObj)
          : "Pick date";
        const useDirectNativeDateTap = isAppleTouchDevice;

        const resolveDisplaySource = (
          userSrc: "oura" | "manual" | "inferred" | null,
          storedSrc: "oura" | "manual" | "inferred" | null | undefined,
          storedVal: number | null | undefined,
        ): "oura" | "manual" | "inferred" | "waiting" | null => {
          if (userSrc) return userSrc;
          if (storedSrc) return storedSrc;
          if (storedVal != null) return null;
          return ouraConnected ? "waiting" : null;
        };

        const totalBurnedDisplaySource = resolveDisplaySource(
          currentEntry.totalBurnedSource,
          existingForDate?.totalBurnedSource,
          existingForDate?.totalBurned,
        );
        const activeCaloriesDisplaySource = resolveDisplaySource(
          currentEntry.activeCaloriesSource,
          existingForDate?.activeCaloriesSource,
          existingForDate?.activeCalories,
        );

        const ghostValueColor = "rgba(226,232,240,0.58)";
        const activeValueColor = "white";
        const ghostWeightValue = existingForDate?.weight != null ? String(existingForDate.weight) : "";
        const ghostCaloriesInValue = existingForDate?.caloriesIn != null ? String(Math.round(existingForDate.caloriesIn)) : "";
        const ghostTotalBurnedValue = existingForDate?.totalBurned != null ? String(Math.round(existingForDate.totalBurned)) : "";
        const ghostActiveCaloriesValue = existingForDate?.activeCalories != null ? String(Math.round(existingForDate.activeCalories)) : "";

        // Three-state source tag: Oura / User / Estimated / null
        // For "waiting": only show "Oura" tag within 7-day Oura sync window
        const OURA_SYNC_DAYS = 7;
        const daysDiff = existingForDate
          ? Math.floor((Date.now() - Date.parse(`${currentEntry.date}T12:00:00`)) / 86400000)
          : 0;
        const withinSyncWindow = daysDiff <= OURA_SYNC_DAYS;

        const resolveBurnTag = (
          displaySrc: "oura" | "manual" | "inferred" | "waiting" | null,
          storedBurnedSource: string | null | undefined,
          tdeeSourceVal: string | null | undefined,
          hasStoredValue: boolean,
          rejectedReason: string | null | undefined,
        ): "Oura" | "User" | "Estimated" | "Updating" | null => {
          if (displaySrc === "oura") return "Oura";
          if (displaySrc === "manual") return "User";
          // Oura takes priority over any inferred/estimated state for today
          if (isToday && ouraConnected && displaySrc !== "manual") {
            return ouraConnection?.lastSyncAt ? "Updating" : "Oura";
          }
          if (rejectedReason) return "Estimated";
          if (displaySrc === "inferred") return "Estimated";
          if (displaySrc === "waiting" && withinSyncWindow) return ouraConnection?.lastSyncAt ? "Updating" : "Oura";
          // displaySrc === null
          if (hasStoredValue) {
            return tdeeSourceVal === "fromActivityFactor" ? "Estimated" : "User";
          }
          if (!isToday && !ouraConnected) return "Estimated"; // past, no oura, no value
          return null;
        };

        const totalBurnTag = resolveBurnTag(
          totalBurnedDisplaySource,
          existingForDate?.totalBurnedSource,
          existingForDate?.tdeeSource,
          existingForDate?.totalBurned != null,
          existingForDate?.totalBurnedRejectedReason,
        );
        const activeBurnTag = resolveBurnTag(
          activeCaloriesDisplaySource,
          existingForDate?.activeCaloriesSource,
          existingForDate?.tdeeSource,
          existingForDate?.activeCalories != null,
          existingForDate?.activeCaloriesRejectedReason,
        );
        const burnTag = tdeeInputMode === "total" ? totalBurnTag : activeBurnTag;

        const getBurnMicrocopy = (
          src: "oura" | "manual" | "inferred" | "waiting" | null,
          tag: "Oura" | "User" | "Estimated" | "Updating" | null,
          rejectedReason: string | null | undefined,
          hasValue: boolean,
          today: boolean,
        ): string | null => {
          if (src === "oura") return today ? "Synced from Oura • Updates throughout the day" : "Synced from Oura";
          // Oura takes priority for today's microcopy (non-manual)
          if (today && ouraConnected && src !== "manual") {
            return ouraConnection?.lastSyncAt ? "Oura syncs throughout the day" : "Waiting for Oura sync";
          }
          if (rejectedReason) return "Oura value out of range — using estimate";
          if (src === "waiting") return ouraConnection?.lastSyncAt ? "Oura syncs throughout the day" : "Waiting for Oura sync";
          if (src === "inferred" || tag === "Estimated") return "Estimated from your profile";
          if (src === null && hasValue) return "Imported entry";
          if (!hasValue && today) return "No calories burned logged yet";
          return null;
        };

        const totalBurnedMicrocopy = getBurnMicrocopy(
          totalBurnedDisplaySource, totalBurnTag,
          existingForDate?.totalBurnedRejectedReason,
          existingForDate?.totalBurned != null, isToday,
        );
        const activeCaloriesMicrocopy = getBurnMicrocopy(
          activeCaloriesDisplaySource, activeBurnTag,
          existingForDate?.activeCaloriesRejectedReason,
          existingForDate?.activeCalories != null, isToday,
        );

        const hasManualLock =
          existingForDate?.activeCaloriesSource === "manual" ||
          existingForDate?.totalBurnedSource === "manual";

        const labelRow = (label: string, showLogged: boolean) => (
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "0.6rem" }}>
            <span style={{ color: "rgba(255,255,255,0.9)", fontSize: "0.85rem", fontWeight: 800 }}>{label}</span>
            {showLogged ? (
              <span style={{ display: "inline-flex", alignItems: "center", gap: "0.34rem", fontSize: "0.72rem", fontWeight: 700, color: "#86efac" }}>
                <span style={{ width: 7, height: 7, borderRadius: 999, background: "#22c55e", boxShadow: "0 0 0 3px rgba(34,197,94,0.18)" }} />
                Logged
              </span>
            ) : null}
          </div>
        );

        const inputWithSuffix = ({
          id,
          value,
          onChange,
          onBlur,
          placeholder,
          suffix,
          inputRef,
          disabled,
          ghostValue,
          fieldKey,
        }: {
          id: string;
          value: string;
          onChange: (v: string) => void;
          onBlur?: () => void;
          placeholder: string;
          suffix: string;
          inputRef?: any;
          disabled?: boolean;
          ghostValue?: string;
          fieldKey: "weight" | "caloriesIn" | "burnTotal" | "burnActive";
        }) => {
          const inputMode = suffix === "lbs" ? "decimal" : "numeric";
          return (
            <div style={{ position: "relative" }}>
              {ghostValue && !value && dailyEntryFocusedField !== fieldKey ? (
                <span
                  style={{
                    position: "absolute",
                    left: 14,
                    right: 48,
                    top: "50%",
                    transform: "translateY(-50%)",
                    color: ghostValueColor,
                    pointerEvents: "none",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {ghostValue}
                </span>
              ) : null}
              <input
                id={id}
                ref={inputRef}
                type="text"
                inputMode={inputMode}
                pattern="[0-9]*"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                onFocus={() => setDailyEntryFocusedField(fieldKey)}
                onBlur={() => {
                  setDailyEntryFocusedField((prev) => (prev === fieldKey ? null : prev));
                  onBlur?.();
                }}
                placeholder={placeholder}
                disabled={disabled}
                style={{
                  width: "100%",
                  padding: "0.78rem 3rem 0.78rem 0.9rem",
                  borderRadius: 12,
                  border: "1px solid rgba(255,255,255,0.14)",
                  background: "rgba(255,255,255,0.04)",
                  color: activeValueColor,
                  WebkitTextFillColor: activeValueColor,
                  outline: "none",
                  minHeight: GOAL_FIELD_HEIGHT,
                  opacity: disabled ? 0.6 : 1,
                  caretColor: activeValueColor,
                }}
              />
              <span style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", fontSize: "0.8em", opacity: 0.55, pointerEvents: "none", color: "rgba(226,232,240,0.95)", fontWeight: 700 }}>
                {suffix}
              </span>
            </div>
          );
        };

        const dateChipStyle = (selected: boolean): React.CSSProperties => ({
          ...btnBase,
          width: "100%",
          justifyContent: "center",
          padding: "0.46rem 0.42rem",
          borderRadius: 999,
          fontSize: "0.75rem",
          fontWeight: 700,
          background: selected ? "rgba(59,130,246,0.18)" : "rgba(255,255,255,0.06)",
          border: selected ? "1px solid rgba(59,130,246,0.45)" : "1px solid rgba(255,255,255,0.14)",
          color: selected ? "#dbeafe" : "rgba(255,255,255,0.8)",
          display: "inline-flex",
          alignItems: "center",
          gap: "0.3rem",
        });

        return (
          <>
            {isDailyEntryDatePickerOpen ? (
              <div
                aria-hidden="true"
                style={{
                  position: "absolute",
                  inset: 0,
                  zIndex: 4,
                  borderRadius: 16,
                  background: isMobile
                    ? "rgba(2,6,23,0.52)"
                    : "rgba(2,6,23,0.4)",
                  backdropFilter: "blur(7px) saturate(0.9)",
                  WebkitBackdropFilter: "blur(7px) saturate(0.9)",
                  boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.08)",
                  pointerEvents: "auto",
                }}
              />
            ) : null}
            <div style={{ fontSize: "1.76rem", fontWeight: 950, lineHeight: 1.03, letterSpacing: "-0.02em" }}>
              {weekday}
              <span style={{ margin: "0 0.4rem", color: "rgba(255,255,255,0.3)", fontWeight: 500 }}>·</span>
              {monthDay}
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: "0.52rem" }}>
              <button
                type="button"
                style={dateChipStyle(isToday)}
                onClick={() => {
                  setIsDailyEntryDatePickerOpen(false);
                  loadCurrentEntryForDate(todayYmd);
                }}
                disabled={isArchivedOrViewer}
              >
                <svg aria-hidden viewBox="0 0 16 16" width="11" height="11" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" style={{verticalAlign:"middle",marginBottom:1}}><circle cx="8" cy="8" r="3"/><line x1="8" y1="1" x2="8" y2="3"/><line x1="8" y1="13" x2="8" y2="15"/><line x1="1" y1="8" x2="3" y2="8"/><line x1="13" y1="8" x2="15" y2="8"/><line x1="3.22" y1="3.22" x2="4.64" y2="4.64"/><line x1="11.36" y1="11.36" x2="12.78" y2="12.78"/><line x1="12.78" y1="3.22" x2="11.36" y2="4.64"/><line x1="4.64" y1="11.36" x2="3.22" y2="12.78"/></svg>{" "}Today
              </button>
              <button
                type="button"
                style={dateChipStyle(isYesterday)}
                onClick={() => {
                  setIsDailyEntryDatePickerOpen(false);
                  const ymd = addDaysYMD(todayYmd, -1);
                  if (!ymd) return;
                  loadCurrentEntryForDate(ymd);
                }}
                disabled={isArchivedOrViewer}
              >
                <svg aria-hidden viewBox="0 0 12 10" width="11" height="10" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{verticalAlign:"middle",marginBottom:1}}><polyline points="4,1 1,5 4,9"/><path d="M1 5h6a4 4 0 0 0 0-4"/></svg>{" "}Yesterday
              </button>
              <div
                style={{
                  ...dateChipStyle(!isToday && !isYesterday),
                  position: "relative",
                  cursor: isArchivedOrViewer ? "default" : "pointer",
                  opacity: isArchivedOrViewer ? 0.65 : 1,
                  boxShadow: isDailyEntryDatePickerOpen ? "0 0 0 1px rgba(96,165,250,0.5), 0 10px 28px rgba(30,41,59,0.34)" : "none",
                  zIndex: isDailyEntryDatePickerOpen ? 5 : 1,
                }}
                role="button"
                aria-label="Pick entry date"
                aria-haspopup="dialog"
                aria-expanded={isDailyEntryDatePickerOpen}
                tabIndex={isArchivedOrViewer ? -1 : 0}
                onClick={() => {
                  if (useDirectNativeDateTap || isArchivedOrViewer) return;
                  openDailyEntryDatePicker();
                }}
                onKeyDown={(e) => {
                  if (useDirectNativeDateTap || isArchivedOrViewer) return;
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    openDailyEntryDatePicker();
                  }
                }}
              >
                <svg aria-hidden viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{verticalAlign:"middle",marginBottom:1}}><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                <span>{!isToday && !isYesterday ? customDateLabel : "Pick date"}</span>
                <input
                  id="daily-entry-date"
                  ref={dailyEntryDateRef}
                  type="date"
                  value={currentEntry.date}
                  min={MIN_YMD}
                  max={maxEntryDate}
                  onChange={(e) => {
                    const v = e.target.value;
                    const nextDate = isYmd(v) ? clampYmdToBounds(v, MIN_YMD, maxEntryDate) : v;
                    if (!useDirectNativeDateTap) setIsDailyEntryDatePickerOpen(false);
                    loadCurrentEntryForDate(nextDate);
                    if (isYmd(v) && nextDate !== v) setCalcPlanNotice("Entry date was clamped to safe limits.");
                  }}
                  onFocus={() => setIsDailyEntryDatePickerOpen(true)}
                  onBlur={() => setIsDailyEntryDatePickerOpen(false)}
                  onClick={() => setIsDailyEntryDatePickerOpen(true)}
                  onKeyDown={(e) => {
                    if (e.key === "Escape" || e.key === "Tab") setIsDailyEntryDatePickerOpen(false);
                  }}
                  disabled={isArchivedOrViewer}
                  aria-label="Pick entry date"
                  style={{
                    position: "absolute",
                    inset: 0,
                    opacity: useDirectNativeDateTap ? 0.015 : 0.001,
                    cursor: isArchivedOrViewer ? "default" : "pointer",
                    pointerEvents: useDirectNativeDateTap ? "auto" : "none",
                    colorScheme: "dark",
                    zIndex: useDirectNativeDateTap ? 2 : 0,
                  }}
                />
              </div>
            </div>

            <div
              aria-hidden={isDailyEntryDatePickerOpen}
              style={{
                display: "grid",
                gap: "0.58rem",
                position: "relative",
                zIndex: 1,
                opacity: isDailyEntryDatePickerOpen ? 0.42 : 1,
                filter: isDailyEntryDatePickerOpen ? "blur(1.5px) saturate(0.78)" : "none",
                pointerEvents: isDailyEntryDatePickerOpen ? "none" : "auto",
                transition: "opacity 160ms ease, filter 160ms ease",
              }}
            >
              <div style={{ display: "grid", gap: "0.38rem" }}>
                {labelRow("Weight", existingForDate?.weight != null)}
                {inputWithSuffix({
                  id: "daily-entry-weight",
                  inputRef: dailyEntryWeightRef,
                  value: currentEntry.weight,
                  onChange: (v) => setCurrentEntry((s) => ({ ...s, weight: v })),
                  placeholder: ghostWeightValue ? "" : "",
                  suffix: "lbs",
                  disabled: isArchivedOrViewer,
                  ghostValue: ghostWeightValue,
                  fieldKey: "weight",
                })}
              </div>

              <div style={{ display: "grid", gap: "0.38rem" }}>
                {labelRow("Calories In", existingForDate?.caloriesIn != null)}
                {inputWithSuffix({
                  id: "daily-entry-calories-in",
                  value: currentEntry.caloriesIn,
                  onChange: (v) => setCurrentEntry((s) => ({ ...s, caloriesIn: v })),
                  placeholder: "",
                  suffix: "cal",
                  disabled: isArchivedOrViewer,
                  ghostValue: ghostCaloriesInValue,
                  fieldKey: "caloriesIn",
                })}
              </div>

              <div style={{ display: "grid", gap: "0rem" }}>
                {/* Row 1: section title only */}
                <div style={{ marginBottom: "0.625rem" }}>
                  <span style={{ color: "rgba(255,255,255,0.9)", fontSize: "0.85rem", fontWeight: 800 }}>Calories burned</span>
                </div>

                {/* Row 2: segmented control — compact inline pill */}
                <div style={{ display: "inline-flex", width: "fit-content", alignSelf: "start", borderRadius: 999, border: "1px solid rgba(255,255,255,0.12)", background: "rgba(255,255,255,0.05)", padding: 3, marginBottom: "0.75rem" }}>
                  <button
                    type="button"
                    onClick={() => setTdeeInputMode("total")}
                    style={{
                      border: "none",
                      outline: "none",
                      borderRadius: 999,
                      background: tdeeInputMode === "total" ? "rgba(255,255,255,0.16)" : "transparent",
                      color: tdeeInputMode === "total" ? "white" : "rgba(255,255,255,0.38)",
                      fontSize: "0.9rem",
                      fontWeight: 600,
                      padding: "0 1.1rem",
                      height: "2.1rem",
                      cursor: isArchivedOrViewer ? "default" : "pointer",
                      transition: "background 0.15s, color 0.15s",
                      boxShadow: tdeeInputMode === "total" ? "0 1px 4px rgba(0,0,0,0.32)" : "none",
                    }}
                    disabled={isArchivedOrViewer}
                  >
                    Total
                  </button>
                  <button
                    type="button"
                    onClick={() => setTdeeInputMode("active")}
                    style={{
                      border: "none",
                      outline: "none",
                      borderRadius: 999,
                      background: tdeeInputMode === "active" ? "rgba(255,255,255,0.16)" : "transparent",
                      color: tdeeInputMode === "active" ? "white" : "rgba(255,255,255,0.38)",
                      fontSize: "0.9rem",
                      fontWeight: 600,
                      padding: "0 1.1rem",
                      height: "2.1rem",
                      cursor: isArchivedOrViewer ? "default" : "pointer",
                      transition: "background 0.15s, color 0.15s",
                      boxShadow: tdeeInputMode === "active" ? "0 1px 4px rgba(0,0,0,0.32)" : "none",
                    }}
                    disabled={isArchivedOrViewer}
                  >
                    Active
                  </button>
                </div>

                {tdeeInputMode === "total" ? (
                  <>
                    {/* Row 3: field label + source tag */}
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.42rem" }}>
                      <div style={{ fontSize: "0.82rem", fontWeight: 800 }}>Total calories burned</div>
                      {burnTag === "Oura" && (
                        <span style={{ fontSize: "0.68rem", fontWeight: 800, color: "#c7d2fe", background: "rgba(99,102,241,0.22)", border: "1px solid rgba(199,210,254,0.25)", borderRadius: 999, padding: "0.12rem 0.52rem" }}>
                          Oura
                        </span>
                      )}
                      {burnTag === "User" && (
                        <span style={{ fontSize: "0.68rem", fontWeight: 800, color: "#86efac", background: "rgba(34,197,94,0.12)", border: "1px solid rgba(134,239,172,0.25)", borderRadius: 999, padding: "0.12rem 0.52rem" }}>
                          User
                        </span>
                      )}
                      {burnTag === "Estimated" && (
                        <span style={{ fontSize: "0.68rem", fontWeight: 800, color: "#fcd34d", background: "rgba(251,191,36,0.10)", border: "1px solid rgba(252,211,77,0.25)", borderRadius: 999, padding: "0.12rem 0.52rem" }}>
                          Estimated
                        </span>
                      )}
                      {burnTag === "Updating" && (
                        <button
                          type="button"
                          onClick={() => setShowOuraUpdatingInfo((v) => !v)}
                          style={{ fontSize: "0.68rem", fontWeight: 800, color: "rgba(199,210,254,0.55)", background: "rgba(99,102,241,0.10)", border: "1px solid rgba(199,210,254,0.15)", borderRadius: 999, padding: "0.12rem 0.52rem", cursor: "pointer" }}
                        >
                          Updating
                        </button>
                      )}
                    </div>
                    {burnTag === "Updating" && showOuraUpdatingInfo && (
                      <div style={{ fontSize: "0.73rem", color: "rgba(199,210,254,0.55)", background: "rgba(99,102,241,0.08)", border: "1px solid rgba(199,210,254,0.15)", borderRadius: 8, padding: "0.3rem 0.55rem", marginBottom: "0.42rem" }}>
                        Oura activity data is finalized at the end of the day. Your stats will appear here after your ring syncs tonight.
                      </div>
                    )}
                    {existingForDate?.totalBurnedSource === "oura" && currentEntry.totalBurnedSource === "manual" && (
                      <div style={{ fontSize: "0.73rem", color: "#fbbf24", background: "rgba(251,191,36,0.1)", border: "1px solid rgba(251,191,36,0.25)", borderRadius: 8, padding: "0.3rem 0.55rem", marginBottom: "0.42rem" }}>
                        Editing will override Oura's value for this entry.
                      </div>
                    )}
                    {/* Row 4: input */}
                    {inputWithSuffix({
                      id: "daily-burn-total",
                      value: currentEntry.totalBurned,
                      onChange: (v) => {
                        setCurrentEntry((s) => ({ ...s, totalBurned: v, totalBurnedSource: v.trim() ? "manual" : null }));
                      },
                      placeholder: "",
                      suffix: "cal",
                      disabled: isArchivedOrViewer,
                      ghostValue: tdeeInputMode === "total" ? ghostTotalBurnedValue : "",
                      fieldKey: "burnTotal",
                    })}
                    {/* Row 5: helper text */}
                    {totalBurnedMicrocopy && (
                      <div style={{ fontSize: "0.75rem", color: "rgba(255,255,255,0.45)", marginTop: "0.44rem" }}>
                        {totalBurnedMicrocopy}
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    {/* Row 3: field label + source tag */}
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.42rem" }}>
                      <div style={{ fontSize: "0.82rem", fontWeight: 800 }}>Active calories burned</div>
                      {burnTag === "Oura" && (
                        <span style={{ fontSize: "0.68rem", fontWeight: 800, color: "#c7d2fe", background: "rgba(99,102,241,0.22)", border: "1px solid rgba(199,210,254,0.25)", borderRadius: 999, padding: "0.12rem 0.52rem" }}>
                          Oura
                        </span>
                      )}
                      {burnTag === "User" && (
                        <span style={{ fontSize: "0.68rem", fontWeight: 800, color: "#86efac", background: "rgba(34,197,94,0.12)", border: "1px solid rgba(134,239,172,0.25)", borderRadius: 999, padding: "0.12rem 0.52rem" }}>
                          User
                        </span>
                      )}
                      {burnTag === "Estimated" && (
                        <span style={{ fontSize: "0.68rem", fontWeight: 800, color: "#fcd34d", background: "rgba(251,191,36,0.10)", border: "1px solid rgba(252,211,77,0.25)", borderRadius: 999, padding: "0.12rem 0.52rem" }}>
                          Estimated
                        </span>
                      )}
                      {burnTag === "Updating" && (
                        <button
                          type="button"
                          onClick={() => setShowOuraUpdatingInfo((v) => !v)}
                          style={{ fontSize: "0.68rem", fontWeight: 800, color: "rgba(199,210,254,0.55)", background: "rgba(99,102,241,0.10)", border: "1px solid rgba(199,210,254,0.15)", borderRadius: 999, padding: "0.12rem 0.52rem", cursor: "pointer" }}
                        >
                          Updating
                        </button>
                      )}
                    </div>
                    {burnTag === "Updating" && showOuraUpdatingInfo && (
                      <div style={{ fontSize: "0.73rem", color: "rgba(199,210,254,0.55)", background: "rgba(99,102,241,0.08)", border: "1px solid rgba(199,210,254,0.15)", borderRadius: 8, padding: "0.3rem 0.55rem", marginBottom: "0.42rem" }}>
                        Oura activity data is finalized at the end of the day. Your stats will appear here after your ring syncs tonight.
                      </div>
                    )}
                    {existingForDate?.activeCaloriesSource === "oura" && currentEntry.activeCaloriesSource === "manual" && (
                      <div style={{ fontSize: "0.73rem", color: "#fbbf24", background: "rgba(251,191,36,0.1)", border: "1px solid rgba(251,191,36,0.25)", borderRadius: 8, padding: "0.3rem 0.55rem", marginBottom: "0.42rem" }}>
                        Editing will override Oura's value for this entry.
                      </div>
                    )}
                    {/* Row 4: input */}
                    {inputWithSuffix({
                      id: "daily-burn-active",
                      value: currentEntry.activeCalories,
                      onChange: (v) => {
                        setCurrentEntry((s) => ({ ...s, activeCalories: v, activeCaloriesSource: v.trim() ? "manual" : null }));
                      },
                      placeholder: "",
                      suffix: "cal",
                      disabled: isArchivedOrViewer,
                      ghostValue: tdeeInputMode === "active" ? ghostActiveCaloriesValue : "",
                      fieldKey: "burnActive",
                    })}
                    {/* Row 5: helper text */}
                    {activeCaloriesMicrocopy && (
                      <div style={{ fontSize: "0.75rem", color: "rgba(255,255,255,0.45)", marginTop: "0.44rem" }}>
                        {activeCaloriesMicrocopy}
                      </div>
                    )}
                  </>
                )}

                {hasManualLock && ouraConnected && (
                  <button
                    style={{
                      ...btnBase,
                      fontSize: "0.75rem",
                      padding: "0.3rem 0.62rem",
                      color: "#c7d2fe",
                      background: "rgba(99,102,241,0.18)",
                      borderColor: "rgba(99,102,241,0.35)",
                      width: "fit-content",
                      marginTop: "0.5rem",
                    }}
                    onClick={async () => {
                      if (!existingForDate) return;
                      await upsertEntry({
                        date: existingForDate.date,
                        weight: existingForDate.weight,
                        caloriesIn: existingForDate.caloriesIn,
                        totalBurned: existingForDate.totalBurned,
                        activeCalories: existingForDate.activeCalories,
                        netCalories: existingForDate.netCalories,
                        bmr: existingForDate.bmr,
                        tdeeSource: existingForDate.tdeeSource,
                        activeCaloriesSource: null,
                        totalBurnedSource: null,
                      });
                      void handleOuraSyncNow();
                    }}
                    disabled={isArchivedOrViewer || ouraStatus === "syncing"}
                  >
                    {ouraStatus === "syncing" ? "Syncing\u2026" : <><svg aria-hidden viewBox="0 0 16 16" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{verticalAlign:"middle",marginBottom:1}}><path d="M14 8A6 6 0 0 1 3.5 13"/><path d="M2 8A6 6 0 0 1 12.5 3"/><polyline points="12,0 12.5,3 9.5,3"/></svg>{" Use Oura for this day"}</>}
                  </button>
                )}
              </div>
            </div>

            <button
              onClick={handleAddOrUpdate}
              disabled={isArchivedOrViewer}
              style={{
                ...btnBase,
                width: "100%",
                background: `linear-gradient(135deg, ${COLORS.blue}, ${COLORS.lightBlue})`,
                color: "white",
                padding: "0.72rem 0.9rem",
                marginTop: "0.1rem",
              }}
            >
              {saveButtonFlash ? "Saved ✓" : entriesAsc.some((e) => e.date === currentEntry.date) ? "Update entry" : "Save entry"}
            </button>
          </>
        );
      })()}
    </div>
  );

  const trackerCharts = currentPage === "viewer" ? (
    <div style={{ ...cardStyle, borderRadius: 18, minWidth: 0, overflow: "hidden", padding: isMobile ? "0.85rem" : "1rem", width: "100%" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "0.75rem", flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "0.55rem", flexWrap: "wrap", minWidth: 0 }}>
          <div style={{ fontSize: isMobile ? "1.05rem" : "1.2rem", fontWeight: 900 }}>Sprint Review</div>
          <div style={{ display: "flex", gap: "0.35rem", flexWrap: "wrap", minWidth: 0 }}>
            {CHART_METRIC_OPTIONS.map(({ key, label }) => (
              <button
                key={key}
                type="button"
                onClick={() => setViewerMetric(key)}
                style={{
                  ...btnBase,
                  minHeight: isMobile ? 26 : 28,
                  padding: isMobile ? "0.16rem 0.52rem" : "0.2rem 0.62rem",
                  fontSize: isMobile ? "0.74rem" : "0.78rem",
                  background: viewerMetric === key ? "rgba(59,130,246,0.22)" : "rgba(255,255,255,0.06)",
                  border: viewerMetric === key ? "1px solid rgba(99,155,255,0.5)" : "1px solid rgba(255,255,255,0.14)",
                  color: "rgba(255,255,255,0.92)",
                }}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div style={{ marginTop: "0.5rem", fontSize: "0.98rem", fontWeight: 600, color: "rgba(255,255,255,0.82)" }}>{CHART_METRIC_META[viewerMetric].title}</div>

      <div style={{ marginTop: "0.6rem", marginLeft: isMobile ? -6 : 0, marginRight: isMobile ? -6 : 0, height: isMobile ? 280 : 420, position: "relative", width: "auto" }}>
        {!viewerChartData.length ? (
          <div style={{ height: "100%", display: "grid", placeItems: "center", color: "rgba(255,255,255,0.45)" }}>No sprint data yet.</div>
        ) : viewerMetric === "weight" ? (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={viewerChartData} margin={isMobile ? { top: 8, right: 4, bottom: 4, left: 2 } : { top: 10, right: 12, bottom: 10, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
              <XAxis
                dataKey="date"
                type="category"
                ticks={isMobile ? viewerMobileXAxisTicks : viewerDesktopXAxisTicks}
                tickFormatter={viewerXAxisFormatter}
                tick={{ fill: "rgba(255,255,255,0.65)", fontSize: isMobile ? 10 : 12 }}
                tickMargin={isMobile ? 1 : 6}
                angle={isMobile ? -32 : -45}
                textAnchor="end"
                interval={0}
                height={isMobile ? 38 : 60}
              />
              <YAxis
                tick={{ fill: "rgba(255,255,255,0.65)", fontSize: isMobile ? 10 : 12 }}
                ticks={viewerWeightYTicks}
                domain={[viewerWeightYTicks[0] ?? "auto", viewerWeightYTicks[viewerWeightYTicks.length - 1] ?? "auto"]}
                width={isMobile ? 34 : 45}
                tickMargin={isMobile ? 4 : 8}
              />
              <Tooltip content={renderViewerMetricTooltip} />
              <Line type="monotone" dataKey="actual" name="Actual" stroke={COLORS.blue} strokeWidth={2} dot={{ r: 2, strokeWidth: 0 }} strokeOpacity={0.55} connectNulls={false} isAnimationActive={false} />
              <Line type="monotone" dataKey="inferred" name="Inferred (gaps)" stroke="rgba(255,255,255,0.35)" strokeWidth={2} dot={false} strokeDasharray="2 6" connectNulls={false} isAnimationActive={false} />
              {showMovingAverageLines ? <Line type="monotone" dataKey="movingAvg" name={trackerMovingAverageLabel} stroke={COLORS.lightBlue} strokeWidth={4} dot={false} connectNulls isAnimationActive={false} /> : null}
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={viewerMetricRowsBase} margin={isMobile ? { top: 8, right: 4, bottom: 4, left: 2 } : { top: 10, right: 12, bottom: 10, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
              <XAxis
                dataKey="date"
                type="category"
                ticks={isMobile ? viewerMobileXAxisTicks : viewerDesktopXAxisTicks}
                tickFormatter={viewerXAxisFormatter}
                tick={{ fill: "rgba(255,255,255,0.65)", fontSize: isMobile ? 10 : 12 }}
                tickMargin={isMobile ? 1 : 6}
                angle={isMobile ? -32 : -45}
                textAnchor="end"
                interval={0}
                height={isMobile ? 38 : 60}
              />
              <YAxis tick={{ fill: "rgba(255,255,255,0.65)", fontSize: isMobile ? 10 : 12 }} tickMargin={isMobile ? 4 : 8} domain={viewerMetric === "netCalories" ? viewerMetricDomain : ["auto", "auto"]} width={isMobile ? 34 : 55} />
              <Tooltip content={renderViewerMetricTooltip} />
              {viewerMetric === "netCalories" ? <ReferenceLine y={0} stroke="rgba(255,255,255,0.55)" /> : null}
              <Bar dataKey={viewerMetric} name={CHART_METRIC_META[viewerMetric].label} isAnimationActive={false}>
                {viewerMetricRowsBase.map((row, idx) => (
                  <Cell key={idx} fill={viewerMetric === "netCalories" ? netColor(row.netCalories) : "rgba(132,153,188,0.88)"} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

    </div>
  ) : (
    <div style={{ ...cardStyle, borderRadius: 18, minWidth: 0, overflow: "hidden", padding: isDashboardMobile ? "0.75rem" : "1rem", width: "100%" }}>
      <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", flexWrap: "wrap" }}>
        <div style={{ fontSize: "1.2rem", fontWeight: 900 }}>{isHistoricSprint ? "Sprint Review" : "Progress"}</div>
        {currentPage !== "viewer" ? (
          <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: "0.75rem", flexWrap: "wrap" }}>
            {[{ key: "toToday", label: "To today" }, { key: "3w", label: "3w" }, { key: "6w", label: "6w" }, { key: "target", label: "To target" }].map((mode) => (
              <button
                key={mode.key}
                type="button"
                onClick={() => setTrackerViewMode(mode.key)}
                style={{
                  ...btnBase,
                  padding: "0.2rem 0.5rem",
                  borderRadius: 999,
                  fontSize: "0.75rem",
                  background: trackerViewMode === mode.key ? "rgba(59,130,246,0.2)" : "rgba(255,255,255,0.06)",
                  borderColor: trackerViewMode === mode.key ? "rgba(59,130,246,0.45)" : "rgba(255,255,255,0.16)",
                  color: "rgba(255,255,255,0.9)",
                }}
              >
                {mode.label}
              </button>
            ))}
          </div>
        ) : null}
      </div>
      {!isDashboardMobile ? <div style={{ marginTop: "0.5rem", marginBottom: "0.45rem", fontSize: "0.98rem", fontWeight: 600, color: "rgba(255,255,255,0.82)" }}>Weight</div> : null}

      <div style={{ marginTop: isDashboardMobile ? "0.65rem" : "0.45rem", marginLeft: isDashboardMobile ? -6 : 0, marginRight: isDashboardMobile ? -6 : 0, height: isDashboardMobile ? 300 : (isMobile ? 300 : 560), position: "relative", width: "auto" }}>
        {!trackerVisibleChartData.length ? (
          <div style={{ height: "100%", display: "grid", placeItems: "center", color: "rgba(255,255,255,0.45)" }}>No sprint data yet.</div>
        ) : (
          <>
            {!isHistoricSprint && !isDashboardMobile ? (
              <div
                style={{
                  position: "absolute",
                  top: -44,
                  right: 10,
                  zIndex: 6,
                  display: "flex",
                  gap: "0.5rem",
                  alignItems: "flex-start",
                }}
              >
                <div
                  style={{
                    padding: "0.5rem",
                    borderRadius: 12,
                    background: "rgba(15,23,42,0.72)",
                    border: "1px solid rgba(255,255,255,0.14)",
                    backdropFilter: "blur(2px)",
                    display: "grid",
                    gap: "0.4rem",
                    alignContent: "start",
                    minWidth: 190,
                  }}
                >
                  <div style={{ fontSize: "0.76rem", letterSpacing: "0.08em", textTransform: "uppercase", color: "rgba(255,255,255,0.58)", fontWeight: 700 }}>Actual</div>
                  <div style={{ display: "flex", gap: "0.45rem", flexWrap: "wrap" }}>
                    <PinnedChip label={trackerRealityItems[0].label} color={trackerRealityItems[0].color} />
                    <button
                      type="button"
                      onClick={cycleMovingAverageSetting}
                      style={{
                        ...btnBase,
                        padding: "0.26rem 0.64rem",
                        borderRadius: 999,
                        fontSize: "0.8rem",
                        background: showMovingAverageLines ? "rgba(59,130,246,0.14)" : "rgba(255,255,255,0.06)",
                        border: showMovingAverageLines ? "1px solid rgba(59,130,246,0.4)" : "1px solid rgba(255,255,255,0.16)",
                        color: showMovingAverageLines ? "rgba(255,255,255,0.9)" : "rgba(255,255,255,0.55)",
                      }}
                      title="Click to cycle trend smoothing"
                    >
                      <span style={{ width: 8, height: 8, borderRadius: 999, background: TRACKER_PROGRESS_SERIES_STYLES.movingAvg.color }} />
                      {trackerMovingAverageLabel}
                      <svg viewBox="0 0 8 5" width="8" height="5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{opacity:0.85,flexShrink:0}} aria-hidden="true"><polyline points="1,1 4,4 7,1"/></svg>
                    </button>
                  </div>
                </div>
                <div
                  style={{
                    padding: "0.5rem",
                    borderRadius: 12,
                    background: "rgba(15,23,42,0.72)",
                    border: "1px solid rgba(255,255,255,0.14)",
                    backdropFilter: "blur(2px)",
                    display: "grid",
                    alignContent: "start",
                    minWidth: 214,
                  }}
                >
                  <OverlayLegendRow items={trackerOverlayItems} visibility={trackerSeriesVisible} onToggle={toggleTrackerSeries} title="Overlay toggles" compact />
                </div>
              </div>
            ) : null}
            <ResponsiveContainer width="100%" height="100%">
            <LineChart data={trackerVisibleChartData} margin={isDashboardMobile ? { top: 8, right: 4, bottom: 4, left: 2 } : { top: 10, right: 12, bottom: 10, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
              <XAxis
                dataKey="date"
                type="category"
                ticks={isDashboardMobile ? trackerMobileXAxisTicks : trackerDesktopXAxisTicks}
                tickFormatter={trackerXAxisFormatter}
                tick={{ fill: "rgba(255,255,255,0.65)", fontSize: isDashboardMobile ? 10 : 12 }}
                tickMargin={isDashboardMobile ? 1 : 6}
                angle={isDashboardMobile ? -32 : -45}
                textAnchor="end"
                interval={0}
                height={isDashboardMobile ? 38 : 60}
              />
              <YAxis
                tick={{ fill: "rgba(255,255,255,0.65)", fontSize: isDashboardMobile ? 10 : 12 }}
                ticks={trackerYTicks}
                domain={[trackerYTicks[0] ?? "auto", trackerYTicks[trackerYTicks.length - 1] ?? "auto"]}
                width={isDashboardMobile ? 34 : 45}
                tickMargin={isDashboardMobile ? 4 : 8}
                label={undefined}
              />
              {chartTooltip}
              {trackerVisibleEnd && todayYmd <= trackerVisibleEnd ? <ReferenceLine x={todayYmd} stroke="rgba(255,255,255,0.5)" strokeWidth={1} /> : null}
              {trackerVisibleEnd && todayYmd < trackerVisibleEnd ? <ReferenceArea x1={todayYmd} x2={trackerVisibleEnd} fill="rgba(255,255,255,0.05)" /> : null}

              <Line
                type="monotone"
                dataKey="actual"
                name="Actual Weight"
                stroke={TRACKER_PROGRESS_SERIES_STYLES.actual.color}
                strokeWidth={TRACKER_PROGRESS_SERIES_STYLES.actual.strokeWidth}
                dot={TRACKER_PROGRESS_SERIES_STYLES.actual.dot}
                strokeOpacity={TRACKER_PROGRESS_SERIES_STYLES.actual.strokeOpacity}
                connectNulls={TRACKER_PROGRESS_SERIES_STYLES.actual.connectNulls}
                isAnimationActive={false}
              />
              <Line
                type="monotone"
                dataKey="bridge"
                name="Inferred bridge"
                stroke="rgba(255,255,255,0.28)"
                strokeWidth={1.75}
                dot={false}
                strokeDasharray="2 5"
                connectNulls={false}
                legendType="none"
                isAnimationActive={false}
              />
              <Line
                type="monotone"
                dataKey="movingAvg"
                name={trackerMovingAverageLabel}
                stroke={TRACKER_PROGRESS_SERIES_STYLES.movingAvg.color}
                strokeWidth={TRACKER_PROGRESS_SERIES_STYLES.movingAvg.strokeWidth}
                dot={TRACKER_PROGRESS_SERIES_STYLES.movingAvg.dot}
                connectNulls={TRACKER_PROGRESS_SERIES_STYLES.movingAvg.connectNulls}
                isAnimationActive={false}
                hide={!showMovingAverageLines}
              />
              {!isHistoricSprint && hasCaloriesInLogsInSprint && trackerSeriesVisible.expected ? (
                <Line
                  type="monotone"
                  dataKey="expected"
                  name="Expected Weight"
                  stroke={TRACKER_PROGRESS_SERIES_STYLES.expected.color}
                  strokeWidth={TRACKER_PROGRESS_SERIES_STYLES.expected.strokeWidth}
                  dot={TRACKER_PROGRESS_SERIES_STYLES.expected.dot}
                  strokeDasharray={TRACKER_PROGRESS_SERIES_STYLES.expected.strokeDasharray}
                  strokeOpacity={TRACKER_PROGRESS_SERIES_STYLES.expected.strokeOpacity}
                  connectNulls={TRACKER_PROGRESS_SERIES_STYLES.expected.connectNulls}
                  isAnimationActive={false}
                />
              ) : null}
              {!isHistoricSprint && trackerSeriesVisible.projected ? (
                <Line
                  type="monotone"
                  dataKey="projected"
                  name="Projected Weight"
                  stroke={TRACKER_PROGRESS_SERIES_STYLES.projected.color}
                  strokeWidth={TRACKER_PROGRESS_SERIES_STYLES.projected.strokeWidth}
                  dot={TRACKER_PROGRESS_SERIES_STYLES.projected.dot}
                  strokeDasharray={TRACKER_PROGRESS_SERIES_STYLES.projected.strokeDasharray}
                  connectNulls={TRACKER_PROGRESS_SERIES_STYLES.projected.connectNulls}
                  isAnimationActive={false}
                />
              ) : null}
              {(isHistoricSprint || trackerSeriesVisible.originalPlan) ? (
                <Line
                  type="monotone"
                  dataKey="originalPlan"
                  name="Original Sprint Plan"
                  stroke={TRACKER_PROGRESS_SERIES_STYLES.originalPlan.color}
                  strokeWidth={TRACKER_PROGRESS_SERIES_STYLES.originalPlan.strokeWidth}
                  dot={TRACKER_PROGRESS_SERIES_STYLES.originalPlan.dot}
                  strokeDasharray={TRACKER_PROGRESS_SERIES_STYLES.originalPlan.strokeDasharray}
                  strokeOpacity={TRACKER_PROGRESS_SERIES_STYLES.originalPlan.strokeOpacity}
                  connectNulls={TRACKER_PROGRESS_SERIES_STYLES.originalPlan.connectNulls}
                  isAnimationActive={false}
                />
              ) : null}
            </LineChart>
          </ResponsiveContainer>
          </>
        )}
      </div>

      {isDashboardMobile && !isHistoricSprint ? (
        <>
          <div style={{ display: "flex", gap: "1rem", justifyContent: "center", padding: "0.02rem 0.5rem 0.4rem", flexWrap: "wrap" }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: "0.3rem", fontSize: "0.72rem", color: "rgba(255,255,255,0.55)" }}>
              <svg width="14" height="8" style={{ flexShrink: 0 }}><line x1="0" y1="4" x2="14" y2="4" stroke={TRACKER_PROGRESS_SERIES_STYLES.actual.color} strokeWidth="2" /></svg>
              Actual
            </span>
            <span style={{ display: "inline-flex", alignItems: "center", gap: "0.3rem", fontSize: "0.72rem", color: "rgba(255,255,255,0.55)" }}>
              <svg width="14" height="8" style={{ flexShrink: 0 }}><line x1="0" y1="4" x2="14" y2="4" stroke={TRACKER_PROGRESS_SERIES_STYLES.expected.color} strokeWidth="2" strokeDasharray="4 3" /></svg>
              Expected
            </span>
            <span style={{ display: "inline-flex", alignItems: "center", gap: "0.3rem", fontSize: "0.72rem", color: "rgba(255,255,255,0.55)" }}>
              <svg width="14" height="8" style={{ flexShrink: 0 }}><line x1="0" y1="4" x2="14" y2="4" stroke={TRACKER_PROGRESS_SERIES_STYLES.projected.color} strokeWidth="2" strokeDasharray="1 4" /></svg>
              Plan
            </span>
          </div>
          {progressControlsExpanded ? (
            <div style={{ marginTop: "0.3rem", display: "grid", gap: "0" }}>
              <div style={{ display: "grid", gap: "0.4rem", padding: "0.3rem 0.1rem" }}>
                <div style={{ fontSize: "0.72rem", letterSpacing: "0.08em", textTransform: "uppercase", color: "rgba(255,255,255,0.45)", fontWeight: 600 }}>Actual</div>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  <PinnedChip label={trackerRealityItems[0].label} color={trackerRealityItems[0].color} />
                  <button
                    type="button"
                    onClick={cycleMovingAverageSetting}
                    style={{
                      ...btnBase,
                      padding: "0.26rem 0.64rem",
                      borderRadius: 999,
                      fontSize: "0.8rem",
                      background: showMovingAverageLines ? "rgba(59,130,246,0.14)" : "rgba(255,255,255,0.06)",
                      border: showMovingAverageLines ? "1px solid rgba(59,130,246,0.4)" : "1px solid rgba(255,255,255,0.16)",
                      color: showMovingAverageLines ? "rgba(255,255,255,0.9)" : "rgba(255,255,255,0.55)",
                    }}
                    title="Click to cycle trend smoothing"
                  >
                    <span style={{ width: 8, height: 8, borderRadius: 999, background: TRACKER_PROGRESS_SERIES_STYLES.movingAvg.color }} />
                    {trackerMovingAverageLabel}
                    <svg viewBox="0 0 8 5" width="8" height="5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{opacity:0.85,flexShrink:0}} aria-hidden="true"><polyline points="1,1 4,4 7,1"/></svg>
                  </button>
                </div>
              </div>
              <div>
                <OverlayLegendRow items={trackerOverlayItems} visibility={trackerSeriesVisible} onToggle={toggleTrackerSeries} title="Overlays" />
              </div>
            </div>
          ) : null}
          <button
            type="button"
            onClick={() => setProgressControlsExpanded((prev) => !prev)}
            style={{ ...btnBase, width: "100%", marginTop: "0.7rem", justifyContent: "center", background: "transparent", borderColor: "rgba(255,255,255,0.12)", borderLeft: "none", borderRight: "none", borderBottom: "none", borderRadius: 0, color: "rgba(255,255,255,0.78)", paddingTop: "0.24rem", paddingBottom: "0.24rem" }}
            aria-label={progressControlsExpanded ? "Collapse progress controls" : "Expand progress controls"}
          >
            <span style={{ display: "inline-flex", alignItems: "center", gap: "0.3rem" }}>
              <span>{progressControlsExpanded ? "Hide chart controls" : "Show chart controls"}</span>
              <ChevronIcon direction={progressControlsExpanded ? "up" : "down"} size={14} />
            </span>
          </button>
        </>
      ) : null}

      {isHistoricSprint && currentPage !== "viewer" ? (
        <div
          style={{
            marginTop: "0.45rem",
            padding: "0.42rem 0.6rem",
            borderRadius: 12,
            border: "1px solid rgba(255,255,255,0.1)",
            background: "rgba(255,255,255,0.04)",
            display: "grid",
            gap: "0.12rem",
          }}
        >
          <div style={{ fontWeight: 800, fontSize: "0.88rem", color: "rgba(255,255,255,0.9)", lineHeight: 1.25 }}>Sprint Summary</div>
          <div style={{ fontSize: "0.77rem", color: "rgba(255,255,255,0.75)", lineHeight: 1.2 }}>{historicSummaryText}</div>
        </div>
      ) : null}

      {!isDashboardMobile ? <>
        <div style={{ marginTop: "1rem", marginBottom: "0.85rem", borderTop: "1px solid rgba(255,255,255,0.08)" }} />
        <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", flexWrap: "wrap" }}>
          <div style={{ fontSize: "0.98rem", fontWeight: 600, color: "rgba(255,255,255,0.82)" }}>Calories</div>
          <div style={{ display: "flex", gap: "0.35rem", flexWrap: "wrap" }}>
            {TRACKER_CALORIE_METRIC_OPTIONS.map(({ key, label }) => (
              <button
                key={key}
                type="button"
                onClick={() => setTrackerCaloriesMetric(key)}
                style={{
                  ...btnBase,
                  minHeight: 28,
                  padding: "0.2rem 0.62rem",
                  fontSize: "0.78rem",
                  background: trackerCaloriesMetric === key ? "rgba(59,130,246,0.22)" : "rgba(255,255,255,0.06)",
                  border: trackerCaloriesMetric === key ? "1px solid rgba(99,155,255,0.5)" : "1px solid rgba(255,255,255,0.14)",
                  color: "rgba(255,255,255,0.92)",
                }}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        <div style={{ marginTop: "0.5rem", height: 280, width: "100%" }}>
          {!trackerCalorieChartData.length ? (
            <div style={{ height: "100%", display: "grid", placeItems: "center", color: "rgba(255,255,255,0.45)" }}>No calorie data yet.</div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={trackerCalorieChartData} margin={{ top: 10, right: 12, bottom: 10, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                <XAxis dataKey="date" type="category" ticks={trackerDesktopXAxisTicks} tickFormatter={trackerXAxisFormatter} tick={{ fill: "rgba(255,255,255,0.65)", fontSize: 12 }} angle={-45} textAnchor="end" interval={0} height={60} />
                <YAxis tick={{ fill: "rgba(255,255,255,0.65)", fontSize: 12 }} domain={trackerCaloriesMetric === "netCalories" ? trackerCalorieDomain : ["auto", "auto"]} width={55} />
                {trackerVisibleEnd && todayYmd <= trackerVisibleEnd ? <ReferenceLine x={todayYmd} stroke="rgba(255,255,255,0.5)" strokeWidth={1} /> : null}
                {trackerVisibleEnd && todayYmd < trackerVisibleEnd ? <ReferenceArea x1={todayYmd} x2={trackerVisibleEnd} fill="rgba(255,255,255,0.05)" /> : null}
                {trackerCaloriesMetric === "netCalories" ? <ReferenceLine y={0} stroke="rgba(255,255,255,0.55)" /> : null}
                <Tooltip content={renderTrackerCaloriesTooltip} />
                <Bar dataKey={trackerCaloriesMetric} name={trackerCaloriesMetric === "netCalories" ? "Net" : trackerCaloriesMetric === "caloriesIn" ? "Calories In" : trackerCaloriesMetric === "tdee" ? "TDEE" : "Active"} isAnimationActive={false}>
                  {trackerCalorieChartData.map((d, idx) => (
                    <Cell key={idx} fill={trackerCaloriesMetric === "netCalories" ? netColor(d.netCalories) : "rgba(132,153,188,0.88)"} />
                  ))}
                </Bar>
                {trackerCaloriesMetric === "netCalories" ? <Line
                  type="monotone"
                  dataKey="targetNet"
                  name="Target"
                  stroke="rgba(255,255,255,0.85)"
                  strokeWidth={2}
                  strokeDasharray="6 4"
                  dot={false}
                  isAnimationActive={false}
                  connectNulls
                /> : null}
              </ComposedChart>
            </ResponsiveContainer>
          )}
        </div>
      </> : null}
    </div>
  );

  const trackerCaloriesMobileCard = (
      <div style={{ ...cardStyle, borderRadius: 18, width: "100%", padding: "0.75rem", minWidth: 0, overflow: "hidden" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "0.4rem", flexWrap: "wrap" }}>
          <div style={{ fontSize: "1.05rem", fontWeight: 900 }}>Calories</div>
          <div style={{ display: "flex", gap: "0.3rem", flexWrap: "wrap" }}>
            {TRACKER_CALORIE_METRIC_OPTIONS.map(({ key, label }) => (
              <button
                key={key}
                type="button"
                onClick={() => setTrackerCaloriesMetric(key)}
                style={{
                  ...btnBase,
                  minHeight: 26,
                  padding: "0.16rem 0.52rem",
                  fontSize: "0.74rem",
                  background: trackerCaloriesMetric === key ? "rgba(59,130,246,0.22)" : "rgba(255,255,255,0.06)",
                  border: trackerCaloriesMetric === key ? "1px solid rgba(99,155,255,0.5)" : "1px solid rgba(255,255,255,0.14)",
                  color: "rgba(255,255,255,0.92)",
                }}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        <div style={{ marginTop: "0.7rem", height: 240, width: "auto", marginLeft: -6, marginRight: -6 }}>
          {!trackerCalorieChartData.length ? (
            <div style={{ height: "100%", display: "grid", placeItems: "center", color: "rgba(255,255,255,0.45)" }}>No calorie data yet.</div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={trackerCalorieChartData} margin={{ top: 8, right: 4, bottom: 4, left: 2 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                <XAxis dataKey="date" type="category" ticks={trackerMobileXAxisTicks} tickFormatter={trackerXAxisFormatter} tick={{ fill: "rgba(255,255,255,0.65)", fontSize: 10 }} tickMargin={1} angle={-32} textAnchor="end" interval={0} height={38} />
                <YAxis tick={{ fill: "rgba(255,255,255,0.65)", fontSize: 10 }} tickMargin={4} domain={trackerCaloriesMetric === "netCalories" ? trackerCalorieMobileDomain : ["auto", "auto"]} ticks={trackerCaloriesMetric === "netCalories" ? trackerCalorieMobileTicks : undefined} width={34} />
                {trackerCaloriesMetric === "netCalories" ? <ReferenceLine y={0} stroke="rgba(255,255,255,0.55)" /> : null}
                <Tooltip content={renderTrackerCaloriesTooltip} />
                <Bar dataKey={trackerCaloriesMetric} name={trackerCaloriesMetric === "netCalories" ? "Net" : trackerCaloriesMetric === "caloriesIn" ? "Calories In" : trackerCaloriesMetric === "tdee" ? "TDEE" : "Active"} isAnimationActive={false}>
                  {trackerCalorieChartData.map((d, idx) => (
                    <Cell key={idx} fill={trackerCaloriesMetric === "netCalories" ? netColor(d.netCalories) : "rgba(132,153,188,0.88)"} />
                  ))}
                </Bar>
                {trackerCaloriesMetric === "netCalories" ? <Line
                  type="monotone"
                  dataKey="targetNet"
                  name="Target"
                  stroke="rgba(255,255,255,0.85)"
                  strokeWidth={2}
                  strokeDasharray="6 4"
                  dot={false}
                  isAnimationActive={false}
                  connectNulls
                /> : null}
              </ComposedChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
  );

  const displayedSprintEntries = showAllSprintHistory ? sprintEntriesDesc : sprintEntriesDesc.slice(0, 4);

  const getSprintLogSupportingPills = (entry) => {
    const expectedWeight = sprintTheoreticalByDate.get(entry.date);
    return [
      {
        key: "expected",
        label: "Expected",
        value: expectedWeight == null
          ? "—"
          : renderSprintLogInlineValueWithUnit(Number(expectedWeight).toFixed(1), "lbs"),
      },
      {
        key: "tdee",
        label: "TDEE",
        value: entry.totalBurned == null
          ? "—"
          : renderSprintLogInlineValueWithUnit(comma(entry.totalBurned), "kcal"),
      },
    ];
  };

  const renderSprintLogExpandedDetails = (entry, mode = "desktop") => {
    const pills = getSprintLogSupportingPills(entry);

    return (
      <div className={`sprint-log-expanded sprint-log-expanded--${mode}`}>
        <div className="sprint-log-support-pills" role="list" aria-label="Sprint log supporting details">
          {pills.map((pill) => (
            <div key={pill.key} className="sprint-log-support-pill" role="listitem">
              <span className="sprint-log-support-pill__label">{pill.label}</span>
              <span className="sprint-log-support-pill__value">{pill.value}</span>
            </div>
          ))}
        </div>
        {!isArchivedOrViewer ? (
          <div className="sprint-log-action-row">
            {mode === "mobile" ? (
              <button
                type="button"
                style={{ ...btnBase, background: "rgba(255,255,255,0.06)", color: "white", padding: "0.34rem 0.68rem" }}
                onClick={(event) => {
                  event.stopPropagation();
                  openEntryEditor(entry.date);
                }}
              >
                Edit
              </button>
            ) : null}
            <button
              type="button"
              style={{ ...btnBase, background: "rgba(239,68,68,0.12)", borderColor: "rgba(239,68,68,0.30)", color: COLORS.red, padding: "0.34rem 0.68rem", opacity: isArchivedOrViewer ? 0.5 : 1 }}
              onClick={(event) => {
                event.stopPropagation();
                deleteEntry(entry.date);
              }}
              disabled={isArchivedOrViewer}
            >
              Delete
            </button>
          </div>
        ) : null}
      </div>
    );
  };

  const sprintTable = (
    <div style={{ ...cardStyle, borderRadius: 18 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: "1rem" }}>
        <div style={{ fontSize: "1.25rem", fontWeight: 900 }}>Sprint log</div>
        {isArchivedOrViewer ? <Pill label="Read-only" tone="muted" /> : null}
      </div>
      {sprintEntriesDesc.length > 4 ? (
        <button
          type="button"
          onClick={() => setShowAllSprintHistory((v) => !v)}
          style={{ ...btnBase, marginTop: "0.7rem", background: "rgba(255,255,255,0.04)", color: "rgba(255,255,255,0.92)", width: "fit-content", padding: "0.42rem 0.75rem" }}
        >
          {showAllSprintHistory ? "Show last 4" : `Show all ${sprintEntriesDesc.length}`}
        </button>
      ) : null}

      <div style={{ marginTop: "1rem", overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0, minWidth: isMobile ? "auto" : 940 }}>
          <thead>
            <tr>
              <th style={tableHeaderStyle}>Date</th>
              <th style={tableHeaderStyle}>
                <span>Weight </span>
                <span style={{ textTransform: "none", fontSize: "0.72em", color: "rgba(255,255,255,0.55)" }}>(lbs)</span>
              </th>
              <th style={tableHeaderStyle}>
                <span>Intake </span>
                <span style={{ textTransform: "none", fontSize: "0.72em", color: "rgba(255,255,255,0.55)" }}>(cals)</span>
              </th>
              <th style={tableHeaderStyle}>
                <span>Net </span>
                <span style={{ textTransform: "none", fontSize: "0.72em", color: "rgba(255,255,255,0.55)" }}>(cals)</span>
              </th>
              <th style={tableHeaderStyle}>
                <span>Active </span>
                <span style={{ textTransform: "none", fontSize: "0.72em", color: "rgba(255,255,255,0.55)" }}>(cals)</span>
              </th>
              <th style={tableHeaderStyle}>
                <span>Expected </span>
                <span style={{ textTransform: "none", fontSize: "0.72em", color: "rgba(255,255,255,0.55)" }}>(lbs)</span>
              </th>
              <th style={tableHeaderStyle}>
                <span>TDEE </span>
                <span style={{ textTransform: "none", fontSize: "0.72em", color: "rgba(255,255,255,0.55)" }}>(cals)</span>
              </th>
              {!isArchivedOrViewer ? <th style={{ ...tableHeaderStyle, textAlign: "right" }} /> : null}
              {!isArchivedOrViewer ? <th style={{ ...tableHeaderStyle, textAlign: "right" }} /> : null}
            </tr>
          </thead>
          <tbody>
            {displayedSprintEntries.length ? (
              displayedSprintEntries.map((e) => {
                const activeIsOura = String(e.activeCaloriesSource ?? "").toLowerCase() === "oura";
                const expectedWeight = sprintTheoreticalByDate.get(e.date);
                const rowActivate = () => openEntryEditor(e.date);
                return (
                  <tr
                    key={e.date}
                    role="button"
                    tabIndex={0}
                    onClick={rowActivate}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        rowActivate();
                      }
                    }}
                    style={{ cursor: isArchivedOrViewer ? "default" : "pointer" }}
                  >
                    <td style={tableCellStyle}>{e.date}</td>
                    <td style={tableCellStyle}>{e.weight?.toFixed(1) ?? "—"}</td>
                    <td style={tableCellStyle}>{e.caloriesIn === null ? "—" : comma(e.caloriesIn)}</td>
                    <td style={{ ...tableCellStyle, color: e.netCalories === null ? "rgba(255,255,255,0.55)" : e.netCalories < 0 ? COLORS.green : COLORS.red }}>{e.netCalories === null ? "—" : comma(e.netCalories)}</td>
                    <td style={tableCellStyle}>
                      <span style={{ display: "inline-flex", alignItems: "center", gap: "0.32rem", flexWrap: "wrap" }}>
                        <span>{e.activeCalories === null ? "—" : comma(e.activeCalories)}</span>
                        {activeIsOura ? <span style={{ fontSize: "0.62rem", padding: "0.04rem 0.28rem", borderRadius: 999, border: "1px solid rgba(59,130,246,0.35)", color: "#bfdbfe", background: "rgba(59,130,246,0.10)", textTransform: "uppercase", letterSpacing: "0.03em", lineHeight: 1.2 }}>Oura</span> : null}
                      </span>
                    </td>
                    <td style={tableCellStyle}>{expectedWeight == null ? "—" : Number(expectedWeight).toFixed(1)}</td>
                    <td style={tableCellStyle}>{e.totalBurned == null ? "—" : comma(e.totalBurned)}</td>
                    {!isArchivedOrViewer ? (
                      <td style={{ ...tableCellStyle, textAlign: "right" }}>
                        <button
                          type="button"
                          style={{ ...btnBase, background: "rgba(255,255,255,0.06)", color: "white", padding: "0.32rem 0.62rem" }}
                          onClick={(event) => {
                            event.stopPropagation();
                            rowActivate();
                          }}
                        >
                          Edit
                        </button>
                      </td>
                    ) : null}
                    {!isArchivedOrViewer ? (
                      <td style={{ ...tableCellStyle, textAlign: "right" }}>
                        <button
                          type="button"
                          style={{ ...btnBase, background: "rgba(239,68,68,0.12)", borderColor: "rgba(239,68,68,0.30)", color: COLORS.red, padding: "0.32rem 0.62rem" }}
                          onClick={(event) => {
                            event.stopPropagation();
                            deleteEntry(e.date);
                          }}
                        >
                          Delete
                        </button>
                      </td>
                    ) : null}
                  </tr>
                );
              })
            ) : (
              <tr>
                <td style={{ ...tableCellStyle, color: "rgba(255,255,255,0.55)" }} colSpan={isArchivedOrViewer ? 7 : 9}>
                  No sprint entries.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  const displayedSprintEntriesMobile = sprintEntriesDesc.slice(0, mobileSprintHistoryVisibleCount);
  const hasMoreSprintEntriesMobile = mobileSprintHistoryVisibleCount < sprintEntriesDesc.length;
  const sprintHistoryMobileCard = (
      <div style={{ ...cardStyle, borderRadius: 18, width: "100%", padding: "0.75rem" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "0.7rem" }}>
          <div style={{ fontSize: "1.05rem", fontWeight: 900 }}>Sprint log</div>
          <div style={{ display: "flex", alignItems: "center", gap: "0.45rem" }}>
            <div style={{ fontSize: "0.72rem", color: "rgba(255,255,255,0.6)" }}>{Math.min(mobileSprintHistoryVisibleCount, sprintEntriesDesc.length)} / {sprintEntriesDesc.length}</div>
            {mobileSprintHistoryVisibleCount < sprintEntriesDesc.length ? (
              <button
                type="button"
                onClick={() => setMobileSprintHistoryVisibleCount(sprintEntriesDesc.length)}
                style={{ ...btnBase, padding: "0.24rem 0.52rem", fontSize: "0.7rem", background: "rgba(255,255,255,0.04)", color: "rgba(255,255,255,0.9)" }}
              >
                Show all {sprintEntriesDesc.length}
              </button>
            ) : null}
          </div>
        </div>

        <div style={{ marginTop: "0.65rem", display: "grid", gap: "0.45rem" }}>
          {displayedSprintEntriesMobile.length ? displayedSprintEntriesMobile.map((e) => {
            const expanded = expandedSprintHistoryRowDate === e.date;
            const activeIsOura = String(e.activeCaloriesSource ?? "").toLowerCase() === "oura";
            const rowActivate = () => openEntryEditor(e.date);
            return (
              <div key={e.date} style={{ border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, background: "rgba(255,255,255,0.03)", overflow: "hidden" }}>
                <div
                  role="button"
                  tabIndex={0}
                  onClick={rowActivate}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      rowActivate();
                    }
                  }}
                  style={{ display: "grid", gridTemplateColumns: "minmax(0, 0.9fr) repeat(4, auto) auto", alignItems: "center", gap: "0.32rem", padding: "0.48rem 0.56rem", cursor: isArchivedOrViewer ? "default" : "pointer" }}
                >
                  <span style={{ minWidth: 0, fontSize: "0.73rem", color: "rgba(255,255,255,0.92)", fontWeight: 700, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{formatMD(e.date)}</span>
                  <span style={{ fontSize: "0.65rem", color: "rgba(255,255,255,0.8)", whiteSpace: "nowrap" }}>Wt {e.weight?.toFixed(1) ?? "—"}</span>
                  <span style={{ fontSize: "0.65rem", color: "rgba(255,255,255,0.8)", whiteSpace: "nowrap" }}>Intake {e.caloriesIn === null ? "—" : comma(e.caloriesIn)}</span>
                  <span style={{ fontSize: "0.65rem", color: e.netCalories === null ? "rgba(255,255,255,0.6)" : e.netCalories < 0 ? "#86efac" : "#fca5a5", whiteSpace: "nowrap" }}>Net {e.netCalories === null ? "—" : comma(e.netCalories)}</span>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: "0.18rem", fontSize: "0.65rem", color: "rgba(255,255,255,0.8)", whiteSpace: "nowrap" }}>
                    <span>Act {e.activeCalories === null ? "—" : comma(e.activeCalories)}</span>
                    {activeIsOura ? <span style={{ fontSize: "0.54rem", padding: "0.03rem 0.22rem", borderRadius: 999, border: "1px solid rgba(59,130,246,0.35)", color: "#bfdbfe", background: "rgba(59,130,246,0.10)", textTransform: "uppercase", letterSpacing: "0.03em", lineHeight: 1.2 }}>Oura</span> : null}
                  </span>
                  <button
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation();
                      setExpandedSprintHistoryRowDate((prev) => prev === e.date ? null : e.date);
                    }}
                    style={{ ...btnBase, minWidth: 32, minHeight: 32, padding: 0, justifyContent: "center", background: expanded ? "rgba(255,255,255,0.10)" : "transparent", border: expanded ? "1px solid rgba(255,255,255,0.14)" : "1px solid transparent", color: "rgba(255,255,255,0.72)" }}
                    aria-label={expanded ? `Collapse ${e.date} sprint log details` : `Expand ${e.date} sprint log details`}
                    aria-expanded={expanded}
                  >
                    <ChevronIcon direction={expanded ? "down" : "right"} size={15} />
                  </button>
                </div>
                {expanded ? (
                  <div style={{ borderTop: "1px solid rgba(255,255,255,0.08)", padding: "0.38rem 0.5rem 0.48rem" }}>
                    {renderSprintLogExpandedDetails(e, "mobile")}
                  </div>
                ) : null}
              </div>
            );
          }) : <div style={{ color: "rgba(255,255,255,0.55)", fontSize: "0.8rem" }}>No sprint entries.</div>}
        </div>

        {sprintEntriesDesc.length > 3 ? (
          <div style={{ marginTop: "0.55rem", display: "flex", justifyContent: "center" }}>
            <button
              type="button"
              onClick={() => {
                if (hasMoreSprintEntriesMobile) {
                  setMobileSprintHistoryVisibleCount((prev) => Math.min(prev + 3, sprintEntriesDesc.length));
                } else {
                  setMobileSprintHistoryVisibleCount(3);
                  setExpandedSprintHistoryRowDate(null);
                }
              }}
              disabled={!hasMoreSprintEntriesMobile && mobileSprintHistoryVisibleCount <= 3}
              style={{ ...btnBase, padding: "0.3rem 0.68rem", fontSize: "0.74rem", background: "rgba(255,255,255,0.04)", color: "rgba(255,255,255,0.9)", opacity: !hasMoreSprintEntriesMobile && mobileSprintHistoryVisibleCount <= 3 ? 0.7 : 1 }}
            >
              <span style={{ display: "inline-flex", alignItems: "center", gap: "0.3rem" }}>
                <span>{hasMoreSprintEntriesMobile ? "Show next 3 entries" : "Collapse to 3"}</span>
                <ChevronIcon direction={hasMoreSprintEntriesMobile ? "down" : "up"} size={14} />
              </span>
            </button>
          </div>
        ) : null}
      </div>
  );

  const dashboardEmptyState = useMemo<"firstSprint" | "noActiveSprint" | "noEntries" | null>(() => {
    const hasAnySprints = sprints.length > 0;
    if (!hasAnySprints) return "firstSprint";
    if (!activeSprint) return "noActiveSprint";

    const activeStart = effectiveSprintStart(firstEverDate, activeSprint.startDate);
    if (!isYmd(activeStart)) return "noEntries";
    const activeEntriesCount = entriesAsc.filter((e) => e && isYmd(e.date) && e.date >= activeStart).length;
    return activeEntriesCount === 0 ? "noEntries" : null;
  }, [sprints.length, activeSprint?.id, activeSprint?.startDate, firstEverDate, entriesAsc]);

  const trackerOnboardingCard = (() => {
    const state = dashboardEmptyState ?? "firstSprint";
    const shouldShowMaintenanceTip = state === "firstSprint" || state === "noActiveSprint";

    const config = state === "firstSprint"
      ? {
        title: "Start your first sprint",
        body: "Your dashboard shows your active sprint — including your weight trend, calorie target, and forecast to your goal. Create a sprint to get started.",
        primaryLabel: "Start your first sprint",
        secondaryLabel: "Import past data",
        onPrimary: () => openNewSprintModal(),
        onSecondary: () => setShowCsvImportModal(true),
      }
      : state === "noActiveSprint"
        ? {
          title: "No active sprint",
          body: "Start a new sprint to track your progress and bring your dashboard back to life.",
          primaryLabel: "Start new sprint",
          secondaryLabel: "View past sprints",
          onPrimary: () => openNewSprintModal(),
          onSecondary: () => setCurrentPage("sprints"),
        }
        : {
          title: "Log your first entry",
          body: "Your dashboard will update once you start logging data for this sprint.",
          primaryLabel: "Log entry",
          secondaryLabel: "Edit sprint",
          onPrimary: () => jumpToSprintStartEntry(),
          onSecondary: () => openSprintEditor(),
        };

    return (
      <div style={{ ...cardStyle, borderRadius: 18, padding: "1.2rem" }}>
        <div style={{ fontSize: "1.25rem", fontWeight: 900 }}>{config.title}</div>
        <div style={{ marginTop: "0.75rem", color: "rgba(255,255,255,0.72)", fontSize: "0.95rem", lineHeight: 1.45 }}>
          {config.body}
        </div>
        <div style={{ marginTop: "0.95rem", display: "flex", gap: "0.65rem", flexWrap: "wrap" }}>
          <button
            style={{ ...btnBase, background: actionColors.primaryGradient, color: actionColors.primaryText, borderColor: "rgba(148,163,184,0.45)", width: isMobile ? "100%" : "auto" }}
            onClick={config.onPrimary}
          >
            {config.primaryLabel}
          </button>
          <button
            style={{ ...btnBase, background: "rgba(255,255,255,0.06)", color: "white", width: isMobile ? "100%" : "auto" }}
            onClick={config.onSecondary}
          >
            {config.secondaryLabel}
          </button>
        </div>
        {shouldShowMaintenanceTip ? (
          <div style={{ marginTop: "0.85rem", color: "rgba(255,255,255,0.58)", fontSize: "0.82rem", lineHeight: 1.4 }}>
            <div style={{ display: "flex", alignItems: "center", gap: "0.35rem" }}>
              <svg width={14} height={14} viewBox="0 0 24 24" fill="none"
                stroke="rgba(255,255,255,0.58)" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"
                style={{ flexShrink: 0, display: "block" }}>
                <path d="M9 21h6M12 3a6 6 0 0 1 6 6c0 2.5-1.5 4.5-3 6H9c-1.5-1.5-3-3.5-3-6a6 6 0 0 1 6-6z"/>
              </svg>
              <span>Not cutting or bulking?</span>
            </div>
            <div>Create a sprint with the same start and target weight to track maintenance.</div>
          </div>
        ) : null}
      </div>
    );
  })();

  const onboardingHeaderTitle = obPhase === "bodyStats"
    ? "Body stats"
    : obPhase === "currentWeight"
      ? "Current weight"
      : obPhase === "activity"
        ? "Daily activity"
        : obPhase === "import"
          ? "Import data"
          : "Set your goal";
  const onboardingHeaderSubtitle = obPhase === "bodyStats"
    ? "We use these to estimate your metabolism."
    : obPhase === "currentWeight"
      ? "We'll use this as your starting point."
      : obPhase === "activity"
        ? "This helps estimate your calorie needs."
        : obPhase === "import"
          ? "Bring in past entries or restore a backup."
          : "We'll build your calorie plan automatically.";

  const onboardingOverlay = showOnboarding ? (
    <OnboardingFlowV2
      isMobile={isMobile}
      isNativeIOS={isNativeIOS}
      isAuthenticated={!!userId && !isDemoMode}
      initialStep="goal"
      healthKitEnabled={healthKitEnabled}
      onComplete={completeOnboardingFromV2}
      onSignIn={() => {
        // From the welcome screen — open the legacy sign-in modal.
        setLoginModalMode("signin");
        setShowLoginModal(true);
      }}
      onExit={() => setShowLanding(true)}
      onImportFromHealth={async () => {
        if (!Capacitor.isNativePlatform()) return { kind: "error" };
        try {
          if (!healthKitEnabled) {
            // Mirror the working Settings flow exactly: requestPermission
            // triggers the iOS sheet (or no-ops if already decided), and we
            // trust its `granted` response. Do NOT pre-check
            // getAuthorizationStatus — iOS returns "denied" by default for
            // unrequested read permissions as a privacy measure, which
            // previously caused this button to silently disappear on fresh
            // installs.
            const { granted } = await requestHealthKitPermission();
            if (!granted) return { kind: "denied" };
            // Persist enabled flag the same way the Settings toggle does, so
            // background sync + cloud profile stay in sync.
            await handleHealthKitToggle(true);
          }
          const since = new Date(Date.now() - 1000 * 60 * 60 * 24 * 30);
          const samples = await syncWeightsFromHealth(since);
          if (!samples.length) return { kind: "noData" };
          const latest = samples
            .slice()
            .sort((a, b) => (a.date < b.date ? 1 : -1))[0];
          if (!latest) return { kind: "noData" };
          return { kind: "imported", weightLbs: Math.round(latest.kg * 2.2046226218 * 10) / 10 };
        } catch {
          return { kind: "error" };
        }
      }}
    />
  ) : null;

  // Legacy onboarding overlay — preserved temporarily so this huge IIFE keeps
  // type-checking against the existing state. The branch is never taken at
  // runtime; remove this whole block once we're confident in V2.
  const _legacyOnboardingOverlayUnused: React.ReactElement | null = false as boolean ? (() => {
    const obLabelStyle = {
      color: "rgba(255,255,255,0.62)",
      fontSize: isMobile ? "0.78rem" : "0.76rem",
      letterSpacing: isMobile ? "0.06em" : "normal",
      textTransform: isMobile ? "uppercase" as const : "none" as const,
      fontWeight: isMobile ? 700 : 500,
    };
    const obInputBaseStyle = {
      width: "100%",
      borderRadius: isMobile ? 12 : 10,
      border: "1px solid rgba(255,255,255,0.14)",
      background: "rgba(255,255,255,0.04)",
      color: "white",
      outline: "none",
      minHeight: isMobile ? 48 : 42,
      boxSizing: "border-box" as const,
      fontSize: isMobile ? "1rem" : "0.9rem",
    };

    const obVitalsStep = (
      <div style={{ display: "grid", gap: isMobile ? "0.9rem" : "0.65rem" }}>
        {obCloudNote && (
          <div style={{ background: "rgba(59,130,246,0.1)", border: "1px solid rgba(59,130,246,0.25)", borderRadius: 10, padding: "0.5rem 0.7rem", color: "#93c5fd", fontSize: "0.82rem" }}>
            {obCloudNote}
          </div>
        )}
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: "0.5rem", alignItems: "start", minWidth: 0 }}>
          <div style={{ display: "grid", gap: "0.3rem", minWidth: 0, overflow: "hidden" }}>
            <div style={obLabelStyle}>Display name (optional)</div>
            <input
              type="text"
              value={obName}
              onChange={(e) => setObName(e.target.value)}
              placeholder=""
              maxLength={40}
              style={{ ...obInputBaseStyle, padding: isMobile ? "0.78rem 0.85rem" : "0.6rem 0.7rem" }}
            />
          </div>
          <div style={{ display: "grid", gap: "0.3rem", minWidth: 0, overflow: "hidden" }}>
            <div style={obLabelStyle}>Birthday</div>
            <MobileDateField
              value={obVitals.birthday}
              onCommit={(v) => setObVitals((s) => ({ ...s, birthday: v }))}
              style={{ ...obInputBaseStyle, maxWidth: "100%", padding: isMobile ? "0.78rem 0.85rem" : "0.6rem 0.7rem", minWidth: 0 }}
            />
            {calcAge(obVitals.birthday) !== null ? <div style={{ color: "rgba(255,255,255,0.44)", fontSize: "0.78rem" }}>Age {calcAge(obVitals.birthday)}</div> : null}
          </div>
        </div>
        <div style={{ display: "grid", gap: "0.3rem" }}>
          <div style={obLabelStyle}>Sex</div>
          <select
            value={obVitals.gender}
            onChange={(e) => setObVitals((s) => ({ ...s, gender: e.target.value }))}
            style={{ ...obInputBaseStyle, padding: isMobile ? "0.78rem 0.85rem" : "0.6rem 0.7rem", background: "#1e293b", cursor: "pointer" }}
          >
            <option value="M" style={{ color: "white", background: "#1e293b" }}>Male</option>
            <option value="F" style={{ color: "white", background: "#1e293b" }}>Female</option>
          </select>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: "0.5rem", alignItems: "start" }}>
          <div style={{ display: "grid", gap: "0.3rem", minWidth: 0 }}>
            <div style={obLabelStyle}>Height</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.4rem" }}>
              <div style={{ position: "relative" }}>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={obVitals.ft}
                  onChange={(e) => setObVitals((s) => ({ ...s, ft: e.target.value }))}
                  placeholder=""
                  style={{ ...obInputBaseStyle, padding: isMobile ? "0.78rem 2rem 0.78rem 0.85rem" : "0.6rem 1.8rem 0.6rem 0.7rem" }}
                />
                <span style={{ position: "absolute", right: "0.55rem", top: "50%", transform: "translateY(-50%)", color: "rgba(255,255,255,0.38)", fontSize: "0.76rem", pointerEvents: "none" }}>ft</span>
              </div>
              <div style={{ position: "relative" }}>
                <input
                  type="text"
                  inputMode="decimal"
                  pattern="[0-9]*"
                  value={obVitals.ins}
                  onChange={(e) => setObVitals((s) => ({ ...s, ins: e.target.value }))}
                  placeholder=""
                  style={{ ...obInputBaseStyle, padding: isMobile ? "0.78rem 2rem 0.78rem 0.85rem" : "0.6rem 1.8rem 0.6rem 0.7rem" }}
                />
                <span style={{ position: "absolute", right: "0.55rem", top: "50%", transform: "translateY(-50%)", color: "rgba(255,255,255,0.38)", fontSize: "0.76rem", pointerEvents: "none" }}>in</span>
              </div>
            </div>
          </div>
        </div>
        <div style={{ borderTop: "1px solid rgba(255,255,255,0.08)", paddingTop: "0.75rem", marginTop: "0.15rem", display: "flex", alignItems: "center", gap: "0.5rem", flexWrap: "wrap" }}>
          <span style={{ color: "rgba(255,255,255,0.38)", fontSize: "0.76rem", flexShrink: 0 }}>Returning?</span>
          <button
            type="button"
            onClick={() => { setLoginModalMode("signin"); setShowLoginModal(true); }}
            style={{ background: "none", border: "1px solid rgba(255,255,255,0.14)", borderRadius: 8, color: "rgba(255,255,255,0.65)", cursor: "pointer", fontSize: isMobile ? "0.88rem" : "0.78rem", padding: "0.28rem 0.65rem", fontFamily: "inherit" }}
          >
            Sign in
          </button>
          <span style={{ color: "rgba(255,255,255,0.2)", fontSize: "0.72rem" }}>or</span>
          <label style={{ background: "none", border: "1px solid rgba(255,255,255,0.14)", borderRadius: 8, color: "rgba(255,255,255,0.65)", cursor: "pointer", fontSize: isMobile ? "0.88rem" : "0.78rem", padding: "0.28rem 0.65rem", display: "inline-flex", alignItems: "center", gap: "0.3rem" }}>
            Import profile
            <input type="file" accept=".json,application/json,text/json" style={{ display: "none" }}
              onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) handleObProfileUpload(f); }}
            />
          </label>
          {obProfileMsg && (
            <div style={{ width: "100%", borderRadius: 8, padding: "0.4rem 0.6rem", background: obProfileMsg.startsWith("Imported") ? "rgba(34,197,94,0.08)" : "rgba(239,68,68,0.08)", border: `1px solid ${obProfileMsg.startsWith("Imported") ? "rgba(34,197,94,0.3)" : "rgba(239,68,68,0.3)"}`, color: obProfileMsg.startsWith("Imported") ? "#86efac" : "#fca5a5", fontSize: "0.78rem" }}>
              {obProfileMsg}
            </div>
          )}
        </div>
      </div>
    );

    const obCurrentWeightStep = (
      <div style={{ display: "grid", gap: isMobile ? "0.85rem" : "0.65rem" }}>
        <div style={{ display: "grid", gap: "0.3rem", justifyItems: "center" }}>
          <div style={{ ...obLabelStyle, width: isMobile ? "min(100%, 280px)" : "min(100%, 340px)", justifySelf: "center" }}>Current weight</div>
          <div style={{ position: "relative", width: isMobile ? "min(100%, 270px)" : "min(100%, 340px)", margin: "0 auto" }}>
            <input
              type="text"
              inputMode="decimal"
              pattern="[0-9]*"
              value={obVitals.currentWeightLbs}
              onChange={(e) => setObVitals((s) => ({ ...s, currentWeightLbs: e.target.value }))}
              style={{ ...obInputBaseStyle, padding: isMobile ? "0.9rem 2.7rem 0.9rem 0.95rem" : "0.6rem 2.4rem 0.6rem 0.7rem", fontSize: isMobile ? "1.45rem" : "0.9rem", fontWeight: isMobile ? 600 : 500 }}
            />
            <span style={{ position: "absolute", right: "0.75rem", top: "50%", transform: "translateY(-50%)", color: "rgba(255,255,255,0.45)", fontSize: "0.95rem", pointerEvents: "none", fontWeight: 600 }}>lbs</span>
          </div>
        </div>
        <div style={{ color: "rgba(255,255,255,0.45)", fontSize: "0.9rem" }}>You can always update this later.</div>
      </div>
    );

    const obActivityStep = (
      <div style={{ display: "grid", gap: isMobile ? "0.55rem" : "0.4rem", paddingTop: isMobile ? "0.4rem" : 0 }}>
        {ONBOARDING_ACTIVITY_ROWS.map((opt) => {
          const selected = Number(obVitals.activityFactor) === Number(getActivityOptionByLevel(opt.value)?.factor ?? -1);
          return (
            <button key={opt.value} type="button"
              onClick={() => setObVitals((s) => ({ ...s, activityFactor: Number(getActivityOptionByLevel(opt.value)?.factor ?? s.activityFactor) }))}
              style={{
                border: selected ? "1px solid rgba(99,102,241,0.52)" : "1px solid rgba(255,255,255,0.10)",
                borderRadius: isMobile ? 12 : 10,
                background: selected ? "rgba(148,163,184,0.15)" : "rgba(255,255,255,0.03)",
                color: "rgba(229,231,235,0.88)",
                padding: isMobile ? "0.72rem 0.76rem" : "0.48rem 0.62rem",
                display: "grid",
                gridTemplateColumns: "16px 1fr",
                gap: "0.55rem",
                width: "100%",
                cursor: "pointer",
                textAlign: "left",
                fontFamily: "inherit",
              }}
            >
              <span style={{ fontSize: isMobile ? "0.92rem" : "0.8rem", opacity: selected ? 1 : 0.7, marginTop: 2 }}>{opt.icon}</span>
              <span>
                <span style={{ fontSize: isMobile ? "0.98rem" : "0.84rem", fontWeight: selected ? 700 : 600, display: "block" }}>{opt.label}</span>
                <span style={{ fontSize: isMobile ? "0.86rem" : "0.74rem", color: "rgba(255,255,255,0.54)", lineHeight: 1.25 }}>{opt.desc}</span>
              </span>
            </button>
          );
        })}
      </div>
    );

    const obImportStep = (
      <div style={{ display: "grid", gap: "0.9rem" }}>
        <div style={{ display: "grid", gap: "0.5rem", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 12, padding: "0.75rem 0.8rem", background: "rgba(255,255,255,0.02)" }}>
          <div style={{ color: "rgba(255,255,255,0.82)", fontSize: "0.86rem", fontWeight: 700 }}>Restore full profile</div>
          <div style={{ color: "rgba(255,255,255,0.55)", fontSize: "0.8rem" }}>
            Restores entries, sprints, and settings.
          </div>
          <label
            style={{
              ...btnBase,
              background: "rgba(99,102,241,0.30)",
              borderColor: "rgba(99,102,241,0.5)",
              color: "white",
              width: "fit-content",
              display: "inline-flex",
              alignItems: "center",
              gap: "0.5rem",
              cursor: "pointer",
              padding: "0.55rem 0.9rem",
              minHeight: 38,
            }}
          >
            Upload Backup (JSON)
            <input
              type="file"
              accept=".json,application/json,text/json"
              style={{ display: "none" }}
              onChange={(e) => {
                const f = e.target.files?.[0];
                e.target.value = "";
                if (f) handleObProfileUpload(f);
              }}
            />
          </label>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "0.45rem" }}>
          <div style={{ flex: 1, height: 1, background: "rgba(255,255,255,0.1)" }} />
          <span style={{ color: "rgba(255,255,255,0.34)", fontSize: "0.72rem", letterSpacing: "0.08em" }}>OR</span>
          <div style={{ flex: 1, height: 1, background: "rgba(255,255,255,0.1)" }} />
        </div>
        <div style={{ display: "grid", gap: "0.5rem", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 12, padding: "0.75rem 0.8rem", background: "rgba(255,255,255,0.02)" }}>
          <div style={{ color: "rgba(255,255,255,0.82)", fontSize: "0.86rem", fontWeight: 700 }}>Import history</div>
          <div style={{ color: "rgba(255,255,255,0.55)", fontSize: "0.8rem" }}>
            Date, weight, calories
          </div>
          <label
            style={{
              ...btnBase,
              background: "rgba(255,255,255,0.04)",
              borderColor: "rgba(255,255,255,0.2)",
              color: "rgba(255,255,255,0.9)",
              width: "fit-content",
              display: "inline-flex",
              alignItems: "center",
              gap: "0.5rem",
              cursor: "pointer",
              padding: "0.55rem 0.9rem",
              minHeight: 38,
            }}
          >
            Upload CSV
            <input
              type="file"
              accept=".csv,text/csv"
              style={{ display: "none" }}
              onChange={(e) => {
                const f = e.target.files?.[0];
                e.target.value = "";
                if (f) handleObCsvUpload(f);
              }}
            />
          </label>
          <button
            type="button"
            style={{ ...btnBase, background: "transparent", border: "none", color: "rgba(255,255,255,0.5)", padding: 0, justifySelf: "start", textDecoration: "underline", textUnderlineOffset: "3px", fontSize: "0.82rem" }}
            onClick={() => downloadTextFile("weight-tracker-example-import.csv", EXAMPLE_IMPORT_CSV)}
          >
            Download template
          </button>
        </div>
        {(obCsvMsg || obProfileMsg) && (
          <div style={{ borderRadius: 10, padding: "0.6rem 0.75rem", background: obCsvRows || (obProfileMsg ?? "").startsWith("Imported") ? "rgba(34,197,94,0.08)" : "rgba(239,68,68,0.08)", border: `1px solid ${obCsvRows || (obProfileMsg ?? "").startsWith("Imported") ? "rgba(34,197,94,0.3)" : "rgba(239,68,68,0.3)"}`, color: obCsvRows || (obProfileMsg ?? "").startsWith("Imported") ? "#86efac" : "#fca5a5", fontSize: "0.9rem" }}>
            {obCsvMsg || obProfileMsg}
          </div>
        )}
      </div>
    );

    const obErrorBanner = obErrors.length > 0 ? (
      <div style={{ background: "rgba(239,68,68,0.12)", border: "1px solid rgba(239,68,68,0.3)", borderRadius: 12, padding: "0.65rem", color: "#fca5a5", marginBottom: "0.75rem", display: "grid", gap: "0.25rem", fontSize: "0.85rem" }}>
        {obErrors.map((e, i) => <div key={i}>{e}</div>)}
      </div>
    ) : null;

    // ── Sprint step is defined inline below (too large to hoist cleanly) ──

    // ── Mobile bottom bar skip handlers ───────────────────────────────────
    const skipImport = () => { setObCsvRows(null); setObCsvMsg(null); setObErrors([]); setObPhase("goal"); };

    return (
      <div
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 20000,
          background: "linear-gradient(to bottom, #09090b 0%, #09090b 55%, #18181b 100%)",
          color: "white",
          fontFamily: 'Inter, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Arial',
          overflow: isMobile ? "hidden" : "auto",
          padding: isMobile ? 0 : "1.6rem 1.25rem 3.2rem",
          display: "flex",
          flexDirection: "column",
          alignItems: isMobile ? "stretch" : "center",
        }}
      >
        {isMobile ? (
          (() => {
            const MOBILE_FOOTER_HEIGHT = 84;
            const mobileFieldFocusCapture = (e: React.FocusEvent<HTMLDivElement>) => {
              const target = e.target as HTMLElement | null;
              if (!target || !(target instanceof HTMLInputElement || target instanceof HTMLSelectElement || target instanceof HTMLTextAreaElement)) return;
              window.setTimeout(() => target.scrollIntoView({ block: "center", behavior: "smooth" }), 120);
            };
            const mobileStepCardStyle: React.CSSProperties = {
              width: "100%",
              maxWidth: 420,
              borderRadius: 18,
              border: "1px solid rgba(255,255,255,0.14)",
              background: "linear-gradient(145deg, rgba(255,255,255,0.06), rgba(255,255,255,0.03))",
              padding: obPhase === "goal" ? "0.75rem" : "1rem",
              display: "grid",
              gap: "0.9rem",
              alignSelf: "center",
            };
            return (
              <div style={{ height: "100%", minHeight: "100dvh", background: COLORS.card, display: "grid", gridTemplateRows: "auto 1fr auto", paddingTop: "env(safe-area-inset-top)", paddingBottom: "env(safe-area-inset-bottom)" }}>
                <div style={{ padding: "0.75rem 1rem 0.45rem", display: "grid", gap: "0.4rem", justifyItems: "center", position: "relative", zIndex: 2, background: "#09090b" }}>
                  <div style={{ fontSize: "1.5rem", fontWeight: 800, textAlign: "center" }}>{onboardingHeaderTitle}</div>
                  <div style={{ color: "rgba(255,255,255,0.55)", fontSize: "0.88rem", textAlign: "center" }}>{onboardingHeaderSubtitle}</div>
                  {obPhase === "currentWeight" ? (
                    <div style={{ display: "flex", gap: "0.5rem", alignItems: "center", marginTop: "0.15rem" }}>{OB_PHASES.map((p) => <div key={`m-top-${p}`} style={{ width: obPhase === p ? 8 : 6, height: obPhase === p ? 8 : 6, borderRadius: 999, background: obPhase === p ? "#38bdf8" : "rgba(255,255,255,0.26)", transition: "all 0.2s" }} />)}</div>
                  ) : null}
                </div>
                <div onFocusCapture={mobileFieldFocusCapture} style={{ overflowY: "auto", padding: "0.6rem 1rem 1rem", display: "grid", alignContent: "center", justifyItems: "center", gap: "1rem", paddingBottom: MOBILE_FOOTER_HEIGHT + 16 }}>
                  <div style={mobileStepCardStyle}>
                    {obErrorBanner}
                    {obPhase === "bodyStats" && obVitalsStep}
                    {obPhase === "currentWeight" && obCurrentWeightStep}
                    {obPhase === "activity" && obActivityStep}
                    {obPhase === "import" && obImportStep}
                    {obPhase === "goal" && (
                      <div style={{ display: "grid", gap: "0.45rem" }}>
                        <div style={{ display: "grid", gap: "0.25rem" }}>
                          <div style={obLabelStyle}>Sprint name</div>
                          <input
                            type="text"
                            value={obSprint.name}
                            onChange={(e) => setObSprint((s) => ({ ...s, name: e.target.value }))}
                            placeholder="My first sprint"
                            maxLength={MAX_SPRINT_NAME_CHARS}
                            style={{ ...obInputBaseStyle, padding: "0.5rem 0.7rem", minHeight: 40, fontSize: "0.95rem" }}
                          />
                        </div>
                        <div style={{ display: "grid", gap: "0.22rem" }}><div style={obLabelStyle}>Sprint type</div><SprintGoalTypeToggle value={obSprint.goalType} onChange={(nextType) => setObSprint((prev) => { const nextTarget = normalizeSprintWeightsForGoalType({ goalType: nextType, startWeight: safeNumber(prev.startWeight), targetWeight: safeNumber(prev.targetWeight), defaultDeltaLbs: 10 }); return { ...prev, goalType: nextType, targetWeight: nextTarget !== null ? String(nextTarget) : prev.targetWeight }; })} /></div>

                        {/* Weights side-by-side with inline delta */}
                        <div style={{ display: "grid", gap: "0.22rem" }}>
                          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.5rem", minWidth: 0 }}>
                            <div style={{ display: "grid", gap: "0.2rem", minWidth: 0 }}>
                              <div style={obLabelStyle}>Current weight</div>
                              <div style={{ position: "relative", minWidth: 0 }}>
                                <input type="text" inputMode="decimal" pattern="[0-9]*" value={obSprint.startWeight} readOnly style={{ ...obInputBaseStyle, padding: "0.5rem 2rem 0.5rem 0.7rem", minHeight: 40, fontSize: "0.95rem" }} />
                                <span style={{ position: "absolute", right: "0.5rem", top: "50%", transform: "translateY(-50%)", color: "rgba(255,255,255,0.4)", fontSize: "0.78rem", pointerEvents: "none" }}>lbs</span>
                              </div>
                            </div>
                            <div style={{ display: "grid", gap: "0.2rem", minWidth: 0 }}>
                              <div style={obLabelStyle}>Target weight</div>
                              <div style={{ position: "relative", minWidth: 0 }}>
                                <input type="text" inputMode="decimal" pattern="[0-9]*" value={obSprint.targetWeight} onChange={(e) => setObSprint((s) => ({ ...s, targetWeight: e.target.value, goalType: deriveSprintGoalType(safeNumber(s.startWeight), safeNumber(e.target.value)) }))} placeholder="e.g. 175" style={{ ...obInputBaseStyle, padding: "0.5rem 2rem 0.5rem 0.7rem", minHeight: 40, fontSize: "0.95rem" }} />
                                <span style={{ position: "absolute", right: "0.5rem", top: "50%", transform: "translateY(-50%)", color: "rgba(255,255,255,0.4)", fontSize: "0.78rem", pointerEvents: "none" }}>lbs</span>
                              </div>
                            </div>
                          </div>
                          <DeltaChip startWeight={obSprint.startWeight} endWeight={obSprint.targetWeight} />
                        </div>

                        {/* Planning method */}
                        <PlanMethodToggle value={obSprint.planMethod} onChange={(v) => setObSprint((s) => ({ ...s, planMethod: v, ...(v === "byDate" ? { presetRate: null, presetClamped: false } : {}) }))} />

                        {/* Pace chips */}
                        {obSprint.planMethod === "byPace" && (() => {
                          const _sw = safeNumber(obSprint.startWeight);
                          const _tw = safeNumber(obSprint.targetWeight);
                          if (_sw === null || _tw === null) return null;
                          const _presets = getRatePresetOptions({ startWeight: _sw, targetWeight: _tw });
                          if (!_presets.length) return null;
                          return <RatePresetChips presets={_presets} selectedRate={obSprint.presetRate} onSelect={(rate) => setObSprint((s) => ({ ...s, presetRate: rate }))} detailed showNegative={_tw < _sw} compact />;
                        })()}

                        {/* Dates side-by-side */}
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.5rem", minWidth: 0 }}>
                          <div style={{ display: "grid", gap: "0.2rem", minWidth: 0, overflow: "hidden" }}>
                            <div style={obLabelStyle}>Start date</div>
                            {/* Display as text to avoid native date-input overflow on iOS */}
                            <div style={{ ...obInputBaseStyle, padding: "0.5rem 0.7rem", minHeight: 40, display: "flex", alignItems: "center", fontSize: "0.9rem", color: "rgba(255,255,255,0.86)" }}>{formatMdy(obSprint.startDate)}</div>
                          </div>
                          <div style={{ display: "grid", gap: "0.2rem", minWidth: 0, overflow: "hidden" }}>
                            <div style={obLabelStyle}>{obSprint.planMethod === "byPace" ? "Projected end" : "Target date"}</div>
                            <MobileDateField
                              value={obSprint.targetDate}
                              onCommit={(v) => setObSprint((s) => ({ ...s, targetDate: v, ...(s.planMethod === "byPace" ? { planMethod: "byDate", presetRate: null, presetClamped: false } : {}) }))}
                              style={{ ...obInputBaseStyle, padding: "0.5rem 0.7rem", minHeight: 40, fontSize: "0.9rem", width: "100%", maxWidth: "100%", minWidth: 0, opacity: obSprint.planMethod === "byPace" ? 0.88 : 1, borderColor: obSprint.planMethod === "byPace" ? "rgba(99,102,241,0.35)" : "rgba(255,255,255,0.14)" }}
                            />
                          </div>
                        </div>
                        {obSprint.planMethod === "byDate" && !isMobile && (
                          <DurationPresetChips startDate={obSprint.startDate || todayYmd} endDate={obSprint.targetDate || ""} onSelect={(nextDate) => setObSprint((s) => ({ ...s, targetDate: nextDate, ...(s.planMethod === "byPace" ? { planMethod: "byDate", presetRate: null, presetClamped: false } : {}) }))} />
                        )}
                        {obSprint.planMethod === "byDate" ? (
                          <div style={{ fontSize: "0.76rem", color: "rgba(255,255,255,0.65)" }}>
                            Inferred pace: <strong style={{ color: "rgba(255,255,255,0.92)" }}>{obInferredPaceByDate === 0 ? "Maintain" : formatSignedPace(obInferredPaceByDate, 1)}</strong>
                          </div>
                        ) : null}
                        {obSprint.presetClamped && <div style={{ fontSize: "0.75rem", color: "rgba(251,191,36,0.9)" }}>Target date adjusted to stay within allowed sprint duration.</div>}
                        {obShowPaceWarning ? (
                          <div style={{ borderRadius: 12, border: "1px solid rgba(248,113,113,0.42)", background: "linear-gradient(135deg, rgba(239,68,68,0.16), rgba(239,68,68,0.06))", padding: "0.62rem 0.72rem", display: "grid", gap: "0.2rem" }}>
                            {obInferredByDateWarning?.title ? <div style={{ fontWeight: 700, color: "rgba(254,226,226,0.98)", fontSize: "0.84rem" }}>{obInferredByDateWarning.title}</div> : null}
                            {obInferredByDateWarning?.recommendedLine ? <div style={{ fontSize: "0.79rem", color: "rgba(254,202,202,0.95)" }}>{obInferredByDateWarning.recommendedLine}</div> : null}
                            {obInferredByDateWarning?.targetLine ? <div style={{ fontSize: "0.77rem", color: "rgba(254,202,202,0.88)" }}>{obInferredByDateWarning.targetLine}</div> : null}
                            {obInferredByDateWarning?.helperLine ? <div style={{ fontSize: "0.75rem", color: "rgba(254,202,202,0.8)" }}>{obInferredByDateWarning.helperLine}</div> : null}
                          </div>
                        ) : null}
                      </div>
                    )}
                  </div>
                </div>
                <div style={{ height: MOBILE_FOOTER_HEIGHT, borderTop: "1px solid rgba(255,255,255,0.09)", backdropFilter: "blur(10px)", background: "rgba(13,16,19,0.97)", padding: "0 1rem", display: "grid", gridTemplateColumns: "1fr auto 1fr", alignItems: "center", gap: "0.5rem" }}>
                  <div style={{ justifySelf: "start" }}><button type="button" onClick={obGoBack} style={{ background: "none", border: "1px solid rgba(255,255,255,0.14)", borderRadius: 8, color: "rgba(255,255,255,0.72)", cursor: "pointer", fontSize: "0.9rem", padding: "0.38rem 0.72rem", fontFamily: "inherit" }}>← Back</button></div>
                  <div style={{ display: "grid", justifyItems: "center", gap: "0.35rem" }}>
                                        <div style={{ display: "flex", gap: "0.35rem", alignItems: "center" }}>{OB_PHASES.map((p) => <div key={p} style={{ width: 6, height: 6, borderRadius: 999, background: obPhase === p ? "#38bdf8" : "rgba(255,255,255,0.25)", transition: "background 0.2s" }} />)}</div>
                  </div>
                  <div style={{ justifySelf: "end", display: "flex", gap: "0.45rem", alignItems: "center" }}>{obPhase === "goal" || obPhase === "import" ? (<><button type="button" onClick={obPhase === "goal" ? () => setShowObSkipSprintInfo(true) : skipImport} style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.18)", borderRadius: 10, color: "rgba(255,255,255,0.8)", cursor: "pointer", fontSize: "0.85rem", fontWeight: 600, padding: "0.4rem 0.72rem", fontFamily: "inherit" }}>Skip</button><button type="button" onClick={obPhase === "goal" ? handleFinishOnboarding : obGoNext} style={{ background: "rgba(99,102,241,0.30)", border: "1px solid rgba(99,102,241,0.5)", borderRadius: 10, color: "white", cursor: "pointer", fontSize: "0.92rem", fontWeight: 700, padding: "0.44rem 0.95rem", fontFamily: "inherit" }}>Next →</button></>) : (<button type="button" onClick={obGoNext} style={{ background: "rgba(99,102,241,0.30)", border: "1px solid rgba(99,102,241,0.5)", borderRadius: 10, color: "white", cursor: "pointer", fontSize: "0.92rem", fontWeight: 700, padding: "0.44rem 0.95rem", fontFamily: "inherit" }}>Next →</button>)}</div>
                </div>
              </div>
            );
          })()
        ) : (
          /* Desktop: centered panel — logo, title, step dots, card with buttons inside */
          <div style={{ maxWidth: 560, width: "100%", marginTop: "auto", marginBottom: "auto", display: "grid", gap: "1rem", position: "relative" }}>
            <div style={{ position: "absolute", top: -96, left: "50%", transform: "translateX(-50%)", pointerEvents: "none" }}>
              <img src="/Logo-gt.png" alt="WeightPlanner Pro" style={{ width: 96, height: 96, objectFit: "contain", display: "block", filter: "drop-shadow(0 6px 14px rgba(16,185,129,0.25))" }} />
            </div>
            <div style={{ display: "grid", justifyItems: "center", textAlign: "center", gap: "0.22rem" }}>
              <div style={{ fontSize: "1.28rem", fontWeight: 800, letterSpacing: "0.01em", flexShrink: 0 }}>{onboardingHeaderTitle}</div>
              <div style={{ color: "rgba(255,255,255,0.52)", fontSize: "0.8rem", lineHeight: 1.3 }}>{onboardingHeaderSubtitle}</div>
            </div>
            <div style={{ ...cardStyle, borderRadius: 16, padding: "1.5rem 1.25rem" }}>
              {obErrorBanner}
              {obPhase === "bodyStats" && obVitalsStep}
              {obPhase === "currentWeight" && obCurrentWeightStep}
              {obPhase === "activity" && obActivityStep}
              {obPhase === "import" && obImportStep}
              {obPhase === "goal" && (
                <div style={{ display: "grid", gap: "1.05rem" }}>
                  <div style={{ color: "rgba(255,255,255,0.48)", fontSize: "0.78rem" }}>Adjust your goal or timeline anytime.</div>
                  <div style={{ display: "grid", gap: "0.32rem" }}>
                    <div style={{ color: "rgba(255,255,255,0.75)", fontSize: "0.85rem" }}>Sprint name</div>
                    <input
                      type="text"
                      value={obSprint.name}
                      onChange={(e) => setObSprint((s) => ({ ...s, name: e.target.value }))}
                      placeholder="My first sprint"
                      maxLength={MAX_SPRINT_NAME_CHARS}
                      style={{ width: "100%", padding: "0.75rem 0.85rem", borderRadius: 12, border: "1px solid rgba(255,255,255,0.14)", background: "rgba(255,255,255,0.04)", color: "white", outline: "none", minHeight: GOAL_FIELD_HEIGHT, boxSizing: "border-box" }}
                    />
                  </div>
                  <div style={{ display: "grid", gap: "0.32rem" }}><div style={{ color: "rgba(255,255,255,0.75)", fontSize: "0.85rem" }}>Sprint type</div><SprintGoalTypeToggle value={obSprint.goalType} onChange={(nextType) => setObSprint((prev) => { const nextTarget = normalizeSprintWeightsForGoalType({ goalType: nextType, startWeight: safeNumber(prev.startWeight), targetWeight: safeNumber(prev.targetWeight), defaultDeltaLbs: 10 }); return { ...prev, goalType: nextType, targetWeight: nextTarget !== null ? String(nextTarget) : prev.targetWeight }; })} /></div>
                  <TriFieldRow
                    mobile={isMobile}
                    leftLabel={
                      <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", whiteSpace: "nowrap", overflow: "hidden" }}>
                        <span style={{ color: "rgba(255,255,255,0.75)", fontSize: "0.85rem" }}>Current weight</span>
                        <span style={{ fontSize: "0.68rem", color: "rgba(255,255,255,0.55)", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 999, padding: "0.1rem 0.4rem", flexShrink: 0 }}>
                          {obSprint.startWeightSource === "custom" ? "Custom" : "Vitals"}
                        </span>
                      </div>
                    }
                    rightLabel={<span style={{ color: "rgba(255,255,255,0.75)", fontSize: "0.85rem", whiteSpace: "nowrap" }}>Target weight</span>}
                    leftField={
                      <div style={{ position: "relative" }}>
                        <input
                          type="text"
                          inputMode="decimal"
                          pattern="[0-9]*"
                          value={obSprint.startWeight}
                          readOnly
                      style={{ width: "100%", padding: "0.75rem 2.5rem 0.75rem 0.85rem", borderRadius: 12, border: "1px solid rgba(255,255,255,0.14)", background: "rgba(255,255,255,0.04)", color: "white", outline: "none", minHeight: GOAL_FIELD_HEIGHT, boxSizing: "border-box" }}
                        />
                        <span style={{ position: "absolute", right: "0.7rem", top: "50%", transform: "translateY(-50%)", color: "rgba(255,255,255,0.4)", fontSize: "0.82rem", pointerEvents: "none" }}>lbs</span>
                      </div>
                    }
                    rightField={
                      <div style={{ position: "relative" }}>
                        <input
                          type="text"
                          inputMode="decimal"
                          pattern="[0-9]*"
                          value={obSprint.targetWeight}
                          onChange={(e) => setObSprint((s) => ({ ...s, targetWeight: e.target.value, goalType: deriveSprintGoalType(safeNumber(s.startWeight), safeNumber(e.target.value)) }))}
                          placeholder="e.g. 175"
                          style={{ width: "100%", padding: "0.75rem 2.5rem 0.75rem 0.85rem", borderRadius: 12, border: "1px solid rgba(255,255,255,0.14)", background: "rgba(255,255,255,0.04)", color: "white", outline: "none", minHeight: GOAL_FIELD_HEIGHT, boxSizing: "border-box" }}
                        />
                        <span style={{ position: "absolute", right: "0.7rem", top: "50%", transform: "translateY(-50%)", color: "rgba(255,255,255,0.4)", fontSize: "0.82rem", pointerEvents: "none" }}>lbs</span>
                      </div>
                    }
                    chip={<DeltaChip startWeight={obSprint.startWeight} endWeight={obSprint.targetWeight} />}
                  />
                  <div style={{ display: "grid", gap: "0.45rem" }}>
                    <div style={{ color: "rgba(255,255,255,0.75)", fontSize: "0.85rem" }}>Planning method</div>
                    <PlanMethodToggle value={obSprint.planMethod} onChange={(v) => {
                      setObSprint((s) => ({ ...s, planMethod: v, ...(v === "byDate" ? { presetRate: null, presetClamped: false } : {}) }));
                    }} />
                  </div>
                  {obSprint.planMethod === "byPace" && (() => {
                    const _sw = safeNumber(obSprint.startWeight);
                    const _tw = safeNumber(obSprint.targetWeight);
                    if (_sw === null || _tw === null) return null;
                    const _presets = getRatePresetOptions({ startWeight: _sw, targetWeight: _tw });
                    if (!_presets.length) return null;
                    const _dir = _tw < _sw ? "Cut" : _tw > _sw ? "Bulk" : "Pace";
                    return (
                      <div style={{ display: "grid", gap: "0.45rem" }}>
                        <div style={{ color: "rgba(255,255,255,0.75)", fontSize: "0.85rem" }}>{_dir === "Pace" ? "Pace" : `${_dir} Pace`}</div>
                        <RatePresetChips presets={_presets} selectedRate={obSprint.presetRate} onSelect={(rate) => setObSprint((s) => ({ ...s, presetRate: rate }))} detailed showNegative={_tw < _sw} />
                      </div>
                    );
                  })()}
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", columnGap: "0.75rem", rowGap: "0.75rem" }}>
                    <div style={{ display: "grid", gap: "0.45rem", gridTemplateRows: `${CREATE_SPRINT_LABEL_ROW_MIN_HEIGHT}px ${SPRINT_DATE_FIELD_HEIGHT}px ${SPRINT_DATE_PRESET_ROW_HEIGHT}px 16px` }}>
                      <div style={{ display: "flex", alignItems: "center", minHeight: CREATE_SPRINT_LABEL_ROW_MIN_HEIGHT }}>
                        <span style={{ color: "rgba(255,255,255,0.75)", fontSize: "0.85rem" }}>Start date</span>
                      </div>
                      <MobileDateField
                        value={obSprint.startDate}
                        readOnly
                        onCommit={() => {}}
                        style={{ width: "100%", padding: "0.75rem 0.85rem", borderRadius: 12, border: "1px solid rgba(255,255,255,0.14)", background: "rgba(255,255,255,0.04)", color: "white", outline: "none", height: SPRINT_DATE_FIELD_HEIGHT, minHeight: SPRINT_DATE_FIELD_HEIGHT }}
                      />
                      <div style={{ minHeight: SPRINT_DATE_PRESET_ROW_HEIGHT }} />
                      <div style={{ fontSize: "0.75rem", color: "rgba(255,255,255,0.55)", minHeight: CREATE_SPRINT_HELPER_ROW_MIN_HEIGHT }}>{" "}</div>
                    </div>
                    <div style={{ display: "grid", gap: "0.45rem", gridTemplateRows: `${CREATE_SPRINT_LABEL_ROW_MIN_HEIGHT}px ${SPRINT_DATE_FIELD_HEIGHT}px ${SPRINT_DATE_PRESET_ROW_HEIGHT}px ${CREATE_SPRINT_HELPER_ROW_MIN_HEIGHT}px` }}>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "0.5rem", minHeight: CREATE_SPRINT_LABEL_ROW_MIN_HEIGHT }}>
                        <label style={{ color: "rgba(255,255,255,0.75)", fontSize: "0.85rem" }}>
                          {obSprint.planMethod === "byPace" ? "End date" : "Target date"}
                        </label>
                      </div>
                      <MobileDateField
                        value={obSprint.targetDate}
                        onCommit={(v) => setObSprint((s) => ({ ...s, targetDate: v, ...(s.planMethod === "byPace" ? { planMethod: "byDate", presetRate: null, presetClamped: false } : {}) }))}
                        style={{ width: "100%", padding: "0.75rem 0.85rem", borderRadius: 12, border: obSprint.planMethod === "byPace" ? "1px solid rgba(99,102,241,0.35)" : "1px solid rgba(255,255,255,0.14)", background: "rgba(255,255,255,0.04)", color: "white", outline: "none", height: SPRINT_DATE_FIELD_HEIGHT, minHeight: SPRINT_DATE_FIELD_HEIGHT, opacity: obSprint.planMethod === "byPace" ? 0.88 : 1 }}
                      />
                      {obSprint.planMethod === "byDate" ? (
                        <DurationPresetChips
                          startDate={obSprint.startDate || todayYmd}
                          endDate={obSprint.targetDate || ""}
                          onSelect={(nextDate) => setObSprint((s) => ({ ...s, targetDate: nextDate, ...(s.planMethod === "byPace" ? { planMethod: "byDate", presetRate: null, presetClamped: false } : {}) }))}
                        />
                      ) : <div style={{ minHeight: SPRINT_DATE_PRESET_ROW_HEIGHT }} />}
                      <div style={{ fontSize: "0.75rem", color: "rgba(255,255,255,0.55)", minHeight: 16, marginTop: -2 }} />
                      {obSprint.planMethod === "byDate" ? (
                        <div style={{ fontSize: "0.76rem", color: "rgba(255,255,255,0.65)" }}>
                          Inferred pace: <strong style={{ color: "rgba(255,255,255,0.92)" }}>{obInferredPaceByDate === 0 ? "Maintain" : formatSignedPace(obInferredPaceByDate, 1)}</strong>
                        </div>
                      ) : null}
                      {obSprint.presetClamped && (
                        <div style={{ fontSize: "0.75rem", color: "rgba(251,191,36,0.9)" }}>Target date adjusted to stay within allowed sprint duration.</div>
                      )}
                    </div>
                  </div>
                  {obShowPaceWarning ? (
                    <div style={{ borderRadius: 12, border: "1px solid rgba(248,113,113,0.42)", background: "linear-gradient(135deg, rgba(239,68,68,0.16), rgba(239,68,68,0.06))", padding: "0.62rem 0.72rem", display: "grid", gap: "0.2rem" }}>
                      {obInferredByDateWarning?.title ? <div style={{ fontWeight: 700, color: "rgba(254,226,226,0.98)", fontSize: "0.84rem" }}>{obInferredByDateWarning.title}</div> : null}
                      {obInferredByDateWarning?.recommendedLine ? <div style={{ fontSize: "0.79rem", color: "rgba(254,202,202,0.95)" }}>{obInferredByDateWarning.recommendedLine}</div> : null}
                      {obInferredByDateWarning?.targetLine ? <div style={{ fontSize: "0.77rem", color: "rgba(254,202,202,0.88)" }}>{obInferredByDateWarning.targetLine}</div> : null}
                      {obInferredByDateWarning?.helperLine ? <div style={{ fontSize: "0.75rem", color: "rgba(254,202,202,0.8)" }}>{obInferredByDateWarning.helperLine}</div> : null}
                    </div>
                  ) : null}
                </div>
              )}
              {/* Desktop footer row: back | dots | next */}
              <div style={{ marginTop: "1.25rem", display: "grid", gridTemplateColumns: "1fr auto 1fr", alignItems: "center", gap: "0.65rem" }}>
                <div style={{ display: "flex", gap: "0.65rem", alignItems: "center", justifySelf: "start" }}>
                  <button type="button" onClick={obGoBack} style={{ ...btnBase, background: "rgba(255,255,255,0.05)", color: "rgba(255,255,255,0.9)", border: "1px solid rgba(255,255,255,0.14)", minHeight: 38, padding: "0.52rem 0.9rem" }}>← Back</button>
                  {obPhase === "import" && (
                    <button type="button" onClick={skipImport} style={{ color: "rgba(255,255,255,0.45)", background: "none", border: "none", cursor: "pointer", fontSize: "0.86rem", padding: "0.4rem" }}>Skip for now</button>
                  )}
                  {obPhase === "goal" && (
                    <button type="button" onClick={() => setShowObSkipSprintInfo(true)} style={{ color: "rgba(255,255,255,0.45)", background: "none", border: "none", cursor: "pointer", fontSize: "0.86rem", padding: "0.4rem" }}>Skip for now</button>
                  )}
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", justifySelf: "center" }}>
                  {OB_PHASES.map((p) => {
                    const active = obPhase === p;
                    return <div key={`d-footer-${p}`} style={{ width: active ? 8 : 6, height: active ? 8 : 6, borderRadius: 999, background: active ? "#38bdf8" : "rgba(148,163,184,0.45)", transition: "all 0.2s" }} />;
                  })}
                </div>
                <div style={{ justifySelf: "end" }}>
                  {obPhase !== "goal" ? (
                    <button type="button" onClick={obGoNext} style={{ ...btnBase, background: "rgba(99,102,241,0.30)", color: "white", border: "1px solid rgba(99,102,241,0.5)", minHeight: 38, padding: "0.52rem 1rem" }}>Next →</button>
                  ) : (
                    <button type="button" onClick={handleFinishOnboarding} style={{ ...btnBase, background: "rgba(99,102,241,0.30)", color: "white", border: "1px solid rgba(99,102,241,0.5)", minHeight: 38, padding: "0.52rem 1rem" }}>Next →</button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  })() : null;

  // ---------------------------
  // Roadmap actions
  // ---------------------------

  const openRoadmapWizard = () => {
    const pattern: WizardPattern = "cut-maintain-bulk";
    // Auto-populate start weight from most recent weight entry at or before today
    const recentEntry = [...entriesAsc].reverse().find((e) => e.weight !== null && e.date <= todayYmd);
    const startWeight = recentEntry?.weight ?? 175;
    setRmWizardStep(0);
    setRmWizardPhaseIdx(0);
    setRmWizardPattern(pattern);
    setRmWizardPhaseRows(initWizardRows(pattern, todayYmd, startWeight));
    setRoadmapWizardOpen(true);
  };

  const closeRoadmapWizard = () => {
    setRoadmapWizardOpen(false);
    setRmWizardStep(0);
    setRmWizardPhaseIdx(0);
  };

  useEffect(() => {
    setRmWizardPhaseIdx((prev) => Math.min(prev, Math.max(0, rmWizardPhaseRows.length - 1)));
  }, [rmWizardPhaseRows.length]);

  const submitRoadmapWizard = () => {
    if (!rmWizardPhaseRows.length) return;
    const phases = wizardRowsToPhases(rmWizardPhaseRows);
    const newRoadmap: Roadmap = {
      id: genId("roadmap"),
      name: "My Physique Roadmap",
      phases,
      createdAt: new Date().toISOString(),
      phaseSprintLinks: {},
    };
    setRoadmap(newRoadmap);
    closeRoadmapWizard();
  };

  // Update a single wizard phase row field, propagating downstream
  const updateWizardRow = (rowIdx: number, field: keyof WizardPhaseRow | "planMethod", value: string) => {
    setRmWizardPhaseRows((prev) => {
      const rows = [...prev];
      const row = { ...rows[rowIdx] };
      const pm = row.planMethod ?? "byDate";

      if (field === "endDate") {
        // Manual end-date edit: if currently byPace, switch to byDate first
        row.endDate = value;
        row.lastEdited = "endDate";
        // Don't auto-recompute targetWeight; show implied pace inline instead
      } else if (field === "targetWeight") {
        row.targetWeight = value;
        row.lastEdited = "targetWeight";
        if (pm === "byPace") {
          // byPace: auto-recompute endDate from TW + current pace
          const sw = safeNumber(row.startWeight);
          const tw = safeNumber(value);
          if (sw !== null && tw !== null) {
            const ed = computeEndDateFromRate(row.startDate, sw, tw, row.type, row.aggressiveness);
            if (ed) row.endDate = ed;
          }
        }
        // byDate: don't auto-recompute endDate; show implied pace inline instead
      } else if (field === "aggressiveness") {
        row.aggressiveness = value as WizardAggressiveness;
        const sw = safeNumber(row.startWeight);
        if (sw !== null) {
          if (pm === "byDate") {
            // byDate: pace chip → recompute targetWeight (keep endDate fixed)
            if (row.endDate > row.startDate) {
              const tw = computeTargetWeightFromRate(sw, row.startDate, row.endDate, row.type, row.aggressiveness);
              if (tw !== null) { row.targetWeight = String(tw); row.lastEdited = "endDate"; }
            }
          } else {
            // byPace: pace chip → recompute endDate (keep targetWeight fixed)
            const tw = safeNumber(row.targetWeight);
            if (tw !== null) {
              const ed = computeEndDateFromRate(row.startDate, sw, tw, row.type, value as WizardAggressiveness);
              if (ed) { row.endDate = ed; row.lastEdited = "targetWeight"; }
            }
          }
        }
      } else if (field === "planMethod") {
        row.planMethod = value as "byDate" | "byPace";
        row.lastEdited = value === "byDate" ? "endDate" : "targetWeight";
        const sw = safeNumber(row.startWeight);
        if (sw !== null && value === "byPace") {
          // Switching to byPace: project endDate from current TW + aggressiveness
          const tw = safeNumber(row.targetWeight);
          if (tw !== null) {
            const ed = computeEndDateFromRate(row.startDate, sw, tw, row.type, row.aggressiveness);
            if (ed) row.endDate = ed;
          }
        }
      } else if (field === "type") {
        const newType = value as RoadmapPhaseType;
        row.type = newType;
        const paceOpts = getPaceOptionsForType(newType);
        const hasCurrent = paceOpts.some((o) => o.key === row.aggressiveness);
        if (!hasCurrent) row.aggressiveness = defaultPaceForType(newType);
        const sw = safeNumber(row.startWeight);
        if (sw !== null) {
          row.targetWeight = String(getDefaultTargetWeightForPhaseType(newType, sw));
          if (newType === "maintain") row.planMethod = "byDate";
          if (row.planMethod === "byPace") {
            const tw = safeNumber(row.targetWeight);
            if (tw !== null) {
              const ed = computeEndDateFromRate(row.startDate, sw, tw, newType, row.aggressiveness);
              if (ed) row.endDate = ed;
            }
          } else if (row.endDate > row.startDate) {
            const tw = computeTargetWeightFromRate(sw, row.startDate, row.endDate, newType, row.aggressiveness);
            if (tw !== null) row.targetWeight = String(tw);
          }
        }
      } else if (field === "startDate" && rowIdx === 0) {
        row.startDate = value;
        const sw = safeNumber(row.startWeight);
        if (sw !== null) {
          if (pm === "byPace") {
            const tw = safeNumber(row.targetWeight);
            if (tw !== null) {
              const ed = computeEndDateFromRate(value, sw, tw, row.type, row.aggressiveness);
              if (ed) row.endDate = ed;
            }
          } else if (row.endDate > value) {
            // byDate: recompute TW from fixed endDate + pace
            const tw = computeTargetWeightFromRate(sw, value, row.endDate, row.type, row.aggressiveness);
            if (tw !== null) row.targetWeight = String(tw);
          }
        }
      } else if (field === "startWeight" && rowIdx === 0) {
        row.startWeight = value;
        const sw = safeNumber(value);
        if (sw !== null && pm === "byDate" && row.endDate > row.startDate) {
          const tw = computeTargetWeightFromRate(sw, row.startDate, row.endDate, row.type, row.aggressiveness);
          if (tw !== null) row.targetWeight = String(tw);
        }
      } else {
        (row as Record<string, string>)[field as string] = value;
      }

      rows[rowIdx] = row;
      return propagateWizardRows(rows, rowIdx);
    });
  };

  const updateRoadmapPhaseBuilderDraft = useCallback((field: "type" | "startDate" | "startWeight" | "endDate" | "endWeight" | "aggressiveness" | "planMethod", value: string) => {
    setRoadmapPhaseBuilderDraft((prev) => {
      if (!prev) return prev;

      const next = { ...prev };
      const planMethod = next.planMethod ?? "byDate";

      if (field === "endDate") {
        if (planMethod === "byPace") next.planMethod = "byDate";
        next.endDate = value;
        return next;
      }

      if (field === "endWeight") {
        next.endWeight = value;
        if (planMethod === "byPace") {
          const sw = safeNumber(next.startWeight);
          const tw = safeNumber(value);
          if (sw !== null && tw !== null) {
            const computedEndDate = computeEndDateFromRate(next.startDate, sw, tw, next.type, next.aggressiveness);
            if (computedEndDate) next.endDate = computedEndDate;
          }
        }
        return next;
      }

      if (field === "aggressiveness") {
        next.aggressiveness = value as WizardAggressiveness;
        const sw = safeNumber(next.startWeight);
        if (sw !== null) {
          if (planMethod === "byDate") {
            if (next.endDate > next.startDate) {
              const computedTargetWeight = computeTargetWeightFromRate(sw, next.startDate, next.endDate, next.type, next.aggressiveness);
              if (computedTargetWeight !== null) next.endWeight = String(computedTargetWeight);
            }
          } else {
            const tw = safeNumber(next.endWeight);
            if (tw !== null) {
              const computedEndDate = computeEndDateFromRate(next.startDate, sw, tw, next.type, next.aggressiveness);
              if (computedEndDate) next.endDate = computedEndDate;
            }
          }
        }
        return next;
      }

      if (field === "planMethod") {
        next.planMethod = value as "byDate" | "byPace";
        const sw = safeNumber(next.startWeight);
        if (sw !== null && value === "byPace") {
          const tw = safeNumber(next.endWeight);
          if (tw !== null) {
            const computedEndDate = computeEndDateFromRate(next.startDate, sw, tw, next.type, next.aggressiveness);
            if (computedEndDate) next.endDate = computedEndDate;
          }
        }
        return next;
      }

      if (field === "type") {
        const newType = value as RoadmapPhaseType;
        next.type = newType;
        const paceOptions = getPaceOptionsForType(newType);
        if (!paceOptions.some((option) => option.key === next.aggressiveness)) {
          next.aggressiveness = defaultPaceForType(newType);
        }
        const sw = safeNumber(next.startWeight);
        if (sw !== null) {
          next.endWeight = String(getDefaultTargetWeightForPhaseType(newType, sw));
          if (newType === "maintain") next.planMethod = "byDate";
          if (next.planMethod === "byPace") {
            const tw = safeNumber(next.endWeight);
            if (tw !== null) {
              const computedEndDate = computeEndDateFromRate(next.startDate, sw, tw, newType, next.aggressiveness);
              if (computedEndDate) next.endDate = computedEndDate;
            }
          } else if (next.endDate > next.startDate) {
            const computedTargetWeight = computeTargetWeightFromRate(sw, next.startDate, next.endDate, newType, next.aggressiveness);
            if (computedTargetWeight !== null) next.endWeight = String(computedTargetWeight);
          }
        }
        return next;
      }

      if (field === "startDate" && !next.startValuesInherited) {
        next.startDate = value;
        const sw = safeNumber(next.startWeight);
        if (sw !== null) {
          if (planMethod === "byPace") {
            const tw = safeNumber(next.endWeight);
            if (tw !== null) {
              const computedEndDate = computeEndDateFromRate(value, sw, tw, next.type, next.aggressiveness);
              if (computedEndDate) next.endDate = computedEndDate;
            }
          } else if (next.endDate > value) {
            const computedTargetWeight = computeTargetWeightFromRate(sw, value, next.endDate, next.type, next.aggressiveness);
            if (computedTargetWeight !== null) next.endWeight = String(computedTargetWeight);
          }
        }
        return next;
      }

      if (field === "startWeight" && !next.startValuesInherited) {
        next.startWeight = value;
        const sw = safeNumber(value);
        if (sw !== null && planMethod === "byDate" && next.endDate > next.startDate) {
          const computedTargetWeight = computeTargetWeightFromRate(sw, next.startDate, next.endDate, next.type, next.aggressiveness);
          if (computedTargetWeight !== null) next.endWeight = String(computedTargetWeight);
        }
        return next;
      }

      return next;
    });
  }, []);

  // ---------------------------
  // applyPhaseEdit — canonical edit pipeline
  // Updates roadmap, propagates downstream, syncs linked sprints, persists.
  // ---------------------------
  const applyPhaseEdit = async (

    phaseId: string,
    patch: Partial<Pick<RoadmapPhase, "endDate" | "endWeight" | "startDate" | "startWeight" | "type">>
  ) => {
    if (!roadmap) return null;
    const nextRoadmap = applyPhaseEditPure(roadmap, phaseId, patch);

    // Sync all linked sprints to match their updated phases
    const nextSprints = sprints.map((sprint) => {
      if (!sprint.roadmapId || sprint.roadmapId !== roadmap.id || !sprint.phaseId) return sprint;
      const linkedPhase = nextRoadmap.phases.find((p) => p.id === sprint.phaseId);
      if (!linkedPhase) return sprint;
      const isActivatable = linkedPhase.startDate <= todayYmd && linkedPhase.endDate >= todayYmd;
      return {
        ...sprint,
        startDate: linkedPhase.startDate,
        endDate: isActivatable ? null : linkedPhase.endDate,
        updatedAt: new Date().toISOString(),
      };
    });

    setRoadmap(nextRoadmap);
    setSprints(nextSprints);
    await persistAll(entriesAsc, vitals, forecast, nextSprints);
    return { nextSprints, nextRoadmap };
  };

  const updatePhaseField = (phaseId: string, field: keyof RoadmapPhase, rawValue: string) => {
    if (!roadmap) return;
    const idx = roadmap.phases.findIndex((p) => p.id === phaseId);
    if (idx < 0) return;

    if (field === "endWeight" || field === "startWeight") {
      const num = safeNumber(rawValue);
      if (num === null) return;
      const phase = roadmap.phases[idx];
      const startWeight = field === "startWeight" ? num : phase.startWeight;
      const endWeight = field === "endWeight" ? num : phase.endWeight;
      const inferredType = inferRoadmapPhaseType(startWeight, endWeight);
      void applyPhaseEdit(phaseId, { [field]: num, type: inferredType });
    } else if (field === "endDate" || field === "startDate") {
      void applyPhaseEdit(phaseId, { [field]: rawValue });
    } else if (field === "type") {
      void applyPhaseEdit(phaseId, { type: rawValue as RoadmapPhaseType });
    } else {
      // Other fields don't need propagation
      const updatedPhase = { ...roadmap.phases[idx], [field]: rawValue };
      const updatedPhases = [...roadmap.phases];
      updatedPhases[idx] = updatedPhase;
      setRoadmap({ ...roadmap, phases: updatedPhases });
    }
  };

  const deletePhase = (phaseId: string) => {
    if (!roadmap) return;
    const idx = roadmap.phases.findIndex((p) => p.id === phaseId);
    if (idx < 0) return;
    const newPhases = roadmap.phases
      .filter((p) => p.id !== phaseId)
      .map((p, i) => ({ ...p, orderIndex: i }));
    if (newPhases.length === 0) {
      setEditingRoadmapPhaseId(null);
      setExpandedRoadmapPhaseById({});
      setRoadmap(null);
      return;
    }
    const propagated = propagatePhases(newPhases, Math.max(0, idx - 1));
    setRoadmap({ ...roadmap, phases: propagated });
  };

  const requestDeletePhase = (phaseId: string) => {
    if (!roadmap) return;
    const phase = roadmap.phases.find((p) => p.id === phaseId);
    if (!phase) return;
    const phaseIdx = roadmap.phases.findIndex((p) => p.id === phaseId);
    const phaseLabel = `Phase ${phaseIdx + 1}: ${ROADMAP_PHASE_LABELS[phase.type]}`;
    setMobilePhaseMenuOpenId(null);
    setRoadmapPhaseDeleteConfirm({ phaseId, phaseName: phaseLabel, isLastPhase: roadmap.phases.length === 1 });
  };

  const confirmDeletePhase = () => {
    if (!roadmapPhaseDeleteConfirm) return;
    deletePhase(roadmapPhaseDeleteConfirm.phaseId);
    setRoadmapPhaseDeleteConfirm(null);
  };

  const buildNextRoadmapPhaseDraft = useCallback((): RoadmapPhaseBuilderDraft | null => {
    if (!roadmap) return null;
    const last = roadmap.phases[roadmap.phases.length - 1];
    const latestLoggedWeight = safeNumber(String(entriesAsc[entriesAsc.length - 1]?.weight ?? 175)) ?? 175;
    const startDate = last ? last.endDate : todayYmd;
    const startWeight = last ? last.endWeight : latestLoggedWeight;
    const endDate = addDaysYMD(startDate, 56) ?? startDate; // default 8 weeks
    return {
      type: "maintain",
      startDate,
      endDate,
      startWeight: String(startWeight),
      endWeight: String(startWeight),
      aggressiveness: defaultPaceForType("maintain"),
      planMethod: "byDate",
      orderIndex: roadmap.phases.length,
      startValuesInherited: !!last,
      inheritedFromPhaseNumber: last ? roadmap.phases.length : null,
    };
  }, [entriesAsc, roadmap, todayYmd]);

  const getActiveSprintRoadmapSnapshot = useCallback((fallbackPhase: RoadmapPhase | null = null) => {
    if (!activeSprint) return null;

    const startDate = isYmd(forecast.sprintStartDate) ? forecast.sprintStartDate : activeSprint.startDate;
    const sprintAnchorForSync = getSprintAnchorWeight(entriesAsc, startDate);
    const latestLoggedWeight = [...entriesAsc].reverse().find((entry) => safeNumber(entry.weight) !== null)?.weight ?? null;
    const startWeight = sprintAnchorForSync.anchorWeight
      ?? latestLoggedWeight
      ?? safeNumber(vitals.currentWeightLbs)
      ?? fallbackPhase?.startWeight
      ?? 175;

    const plannedEndDate = isYmd(forecast.targetDate) && forecast.targetDate > startDate
      ? forecast.targetDate
      : null;
    const endDate = plannedEndDate
      ?? (isYmd(activeSprint.endDate) && activeSprint.endDate > startDate ? activeSprint.endDate : null)
      ?? fallbackPhase?.endDate
      ?? addDaysYMD(startDate, 56)
      ?? startDate;

    const endWeight = safeNumber(forecast.targetWeight)
      ?? fallbackPhase?.endWeight
      ?? startWeight;

    return {
      startDate,
      startWeight,
      endDate,
      endWeight,
      type: inferRoadmapPhaseType(startWeight, endWeight),
    };
  }, [activeSprint, entriesAsc, forecast.sprintStartDate, forecast.targetDate, forecast.targetWeight, vitals.currentWeightLbs]);

  const openAddPhaseBuilder = useCallback(() => {
    const draft = buildNextRoadmapPhaseDraft();
    if (!draft) return;
    setRoadmapPhaseBuilderDraft(draft);
  }, [buildNextRoadmapPhaseDraft]);

  const closeRoadmapPhaseBuilder = useCallback(() => {
    setRoadmapPhaseBuilderDraft(null);
  }, []);

  const commitRoadmapPhaseBuilder = useCallback(() => {
    if (!roadmap || !roadmapPhaseBuilderDraft) return;
    const startWeight = safeNumber(roadmapPhaseBuilderDraft.startWeight);
    const endWeight = safeNumber(roadmapPhaseBuilderDraft.endWeight);
    if (!isYmd(roadmapPhaseBuilderDraft.startDate) || !isYmd(roadmapPhaseBuilderDraft.endDate)) {
      window.alert("Phase dates must be valid YYYY-MM-DD values.");
      return;
    }
    if (roadmapPhaseBuilderDraft.endDate <= roadmapPhaseBuilderDraft.startDate) {
      window.alert("Phase end date must be after the start date.");
      return;
    }
    if (startWeight === null || endWeight === null) {
      window.alert("Phase weights are required.");
      return;
    }

    const newPhase: RoadmapPhase = {
      id: genId("phase"),
      orderIndex: roadmapPhaseBuilderDraft.orderIndex,
      type: roadmapPhaseBuilderDraft.type,
      startDate: roadmapPhaseBuilderDraft.startDate,
      endDate: roadmapPhaseBuilderDraft.endDate,
      startWeight,
      endWeight,
    };
    setRoadmap({ ...roadmap, phases: [...roadmap.phases, newPhase] });
    setExpandedRoadmapPhaseById((prev) => ({ ...prev, [newPhase.id]: true }));
    setEditingRoadmapPhaseId(newPhase.id);
    setRoadmapPhaseBuilderDraft(null);
  }, [roadmap, roadmapPhaseBuilderDraft]);

  const createSprintFromPhase = async (phase: RoadmapPhase, phaseIndex: number) => {
    if (!roadmap) return;

    // Don't create a duplicate if a linked sprint already exists
    const existingLinkedSprintId = roadmap.phaseSprintLinks?.[phase.id];
    if (existingLinkedSprintId && sprints.find((s) => s.id === existingLinkedSprintId)) return;

    const sprintId = genId("sprint");
    const sprintName = sanitizeSprintName(`${ROADMAP_PHASE_LABELS[phase.type]} – Phase ${phaseIndex + 1}`);
    const sprintColor = ROADMAP_PHASE_COLORS[phase.type];
    // Determine if this is the "active" phase (starts today or in past, ends in future or is latest)
    const isActivatable = phase.startDate <= todayYmd && phase.endDate >= todayYmd;
    const newSprint: GSprint = {
      id: sprintId,
      name: sprintName,
      startDate: phase.startDate,
      endDate: isActivatable ? null : phase.endDate, // null = active sprint
      color: sprintColor,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      // Roadmap linkage — bidirectional sync
      roadmapId: roadmap.id,
      phaseId: phase.id,
      phaseOrderIndex: phaseIndex,
    };
    const nextSprints = [...sprints, newSprint];
    setSprints(nextSprints);

    // If activatable, set this as the active sprint and update forecast targets
    if (isActivatable) {
      const nextForecast = {
        ...forecast,
        activeSprintId: sprintId,
        targetWeight: phase.endWeight,
        targetDate: phase.endDate,
        sprintStartDate: phase.startDate,
      };
      setForecast(nextForecast);
      await persistAll(entries, vitals, nextForecast, nextSprints);
    } else {
      await persistAll(entries, vitals, forecast, nextSprints);
    }

    // Link the sprint to this phase in the roadmap
    const updatedRoadmap: Roadmap = {
      ...roadmap,
      phaseSprintLinks: { ...(roadmap.phaseSprintLinks ?? {}), [phase.id]: sprintId },
    };
    setRoadmap(updatedRoadmap);
  };

  const createBlankRoadmap = () => {
    const startWeight = safeNumber(String(entries[entries.length - 1]?.weight ?? 175)) ?? 175;
    const endDate = addDaysYMD(todayYmd, 84) ?? todayYmd; // 12 weeks default
    const phase: RoadmapPhase = {
      id: genId("phase"),
      orderIndex: 0,
      type: "cut",
      startDate: todayYmd,
      endDate,
      startWeight,
      endWeight: Math.round((startWeight - 6) * 10) / 10,
    };
    setRoadmap({
      id: genId("roadmap"),
      name: "My Physique Roadmap",
      phases: [phase],
      createdAt: new Date().toISOString(),
    });
  };

  const createRoadmapFromSprint = async () => {
    if (!activeSprint) return;
    const activeSprintSnapshot = getActiveSprintRoadmapSnapshot();
    if (!activeSprintSnapshot) return;

    const phaseId = genId("phase");
    const phase: RoadmapPhase = {
      id: phaseId,
      orderIndex: 0,
      type: activeSprintSnapshot.type,
      startDate: activeSprintSnapshot.startDate,
      endDate: activeSprintSnapshot.endDate,
      startWeight: activeSprintSnapshot.startWeight,
      endWeight: activeSprintSnapshot.endWeight,
    };

    const roadmapId = genId("roadmap");
    const newRoadmap: Roadmap = {
      id: roadmapId,
      name: "My Physique Roadmap",
      phases: [phase],
      createdAt: new Date().toISOString(),
      phaseSprintLinks: { [phaseId]: activeSprint.id },
    };

    // Update the sprint with bidirectional roadmap linkage
    const updatedSprint: GSprint = {
      ...activeSprint,
      roadmapId,
      phaseId,
      phaseOrderIndex: 0,
      updatedAt: new Date().toISOString(),
    };
    const nextSprints = sprints.map((s) => s.id === activeSprint.id ? updatedSprint : s);

    setSprints(nextSprints);
    setRoadmap(newRoadmap);
    await persistAll(entries, vitals, forecast, nextSprints);
  };

  const syncActiveSprintToPhase1 = async () => {
    if (!activeSprint || !roadmap || roadmap.phases.length === 0) return;
    const phase1 = roadmap.phases[0];
    const activeSprintSnapshot = getActiveSprintRoadmapSnapshot(phase1);
    if (!activeSprintSnapshot) return;

    const patch = {
      startDate: activeSprintSnapshot.startDate,
      startWeight: activeSprintSnapshot.startWeight,
      endDate: activeSprintSnapshot.endDate,
      endWeight: activeSprintSnapshot.endWeight,
      type: activeSprintSnapshot.type,
    };

    // Compute proposed roadmap (pure — full cascade handled by applyPhaseEditPure)
    const proposedRoadmap = applyPhaseEditPure(roadmap, phase1.id, patch);

    // Build final roadmap with updated sprint link
    const finalRoadmap: Roadmap = {
      ...proposedRoadmap,
      phaseSprintLinks: { ...(proposedRoadmap.phaseSprintLinks ?? {}), [phase1.id]: activeSprint.id },
    };

    // Sync all linked sprints to their (possibly date-shifted) phases
    const nextSprints = sprints.map((sprint) => {
      // Update the active sprint's roadmap linkage fields
      if (sprint.id === activeSprint.id) {
        return {
          ...sprint,
          roadmapId: finalRoadmap.id,
          phaseId: phase1.id,
          phaseOrderIndex: 0,
          updatedAt: new Date().toISOString(),
        };
      }
      // Cascade date changes to other linked sprints (same logic as applyPhaseEdit)
      if (!sprint.roadmapId || sprint.roadmapId !== roadmap.id || !sprint.phaseId) return sprint;
      const linkedPhase = finalRoadmap.phases.find((p) => p.id === sprint.phaseId);
      if (!linkedPhase) return sprint;
      const isActivatable = linkedPhase.startDate <= todayYmd && linkedPhase.endDate >= todayYmd;
      return {
        ...sprint,
        startDate: linkedPhase.startDate,
        endDate: isActivatable ? null : linkedPhase.endDate,
        updatedAt: new Date().toISOString(),
      };
    });

    setRoadmap(finalRoadmap);
    setSprints(nextSprints);
    await persistAll(entries, vitals, forecast, nextSprints);
  };

  // Pre-compute conflicts and open the confirmation modal (does not apply changes)
  const openSyncSprintConfirm = () => {
    if (!activeSprint || !roadmap || roadmap.phases.length === 0) return;
    const phase1 = roadmap.phases[0];
    const activeSprintSnapshot = getActiveSprintRoadmapSnapshot(phase1);
    if (!activeSprintSnapshot) return;

    const proposed = applyPhaseEditPure(roadmap, phase1.id, {
      startDate: activeSprintSnapshot.startDate,
      startWeight: activeSprintSnapshot.startWeight,
      endDate: activeSprintSnapshot.endDate,
      endWeight: activeSprintSnapshot.endWeight,
      type: activeSprintSnapshot.type,
    });

    const conflicts: string[] = [];
    for (let i = 1; i < proposed.phases.length; i++) {
      const w = getPhaseTypeWarning(proposed.phases[i]);
      if (w) conflicts.push(`Phase ${i + 1} (${ROADMAP_PHASE_LABELS[proposed.phases[i].type]}): ${w}`);
    }
    setSyncSprintConflicts(conflicts);
    setSyncSprintConfirmOpen(true);
  };

  const renderRoadmapPhaseDetailEditor = ({
    row,
    rowIdx,
    onUpdate,
    startFieldsLocked = false,
    startFieldsHelperCopy = null,
  }: {
    row: Pick<WizardPhaseRow, "type" | "startDate" | "startWeight" | "endDate" | "targetWeight" | "aggressiveness" | "planMethod">;
    rowIdx: number;
    onUpdate: (field: "type" | "startDate" | "startWeight" | "endDate" | "targetWeight" | "aggressiveness" | "planMethod", value: string) => void;
    startFieldsLocked?: boolean;
    startFieldsHelperCopy?: string | null;
  }) => {
    const editorInputStyle: React.CSSProperties = {
      width: "100%",
      padding: "0.55rem 0.7rem",
      borderRadius: 10,
      border: "1px solid rgba(255,255,255,0.14)",
      background: "rgba(255,255,255,0.05)",
      color: "white",
      outline: "none",
      fontSize: "0.9rem",
    };
    const editorLockedInputStyle: React.CSSProperties = {
      ...editorInputStyle,
      background: "rgba(255,255,255,0.02)",
      color: "rgba(255,255,255,0.5)",
      cursor: "default",
    };
    const editorLabelStyle: React.CSSProperties = {
      fontSize: "0.75rem",
      fontWeight: 700,
      color: "rgba(255,255,255,0.6)",
      textTransform: "uppercase",
      letterSpacing: "0.04em",
      marginBottom: "0.35rem",
    };
    const phaseColor = ROADMAP_PHASE_COLORS[row.type];
    const planMethod = row.planMethod ?? "byDate";
    const startDt = safeParseYmd(row.startDate);
    const endDt = safeParseYmd(row.endDate);
    const impliedRate = (() => {
      const swNum = safeNumber(row.startWeight);
      const twNum = safeNumber(row.targetWeight);
      if (swNum === null || twNum === null) return null;
      return computeImpliedRate(row.startDate, swNum, row.endDate, twNum);
    })();
    const paceOpts = getPaceOptionsForType(row.type);
    const isCustomPace = impliedRate !== null && row.type !== "maintain" &&
      !paceOpts.some((o) => Math.abs(AGGRESSIVENESS_RATES[o.key][row.type] - impliedRate) < 0.05);
    const impliedBand = impliedRate !== null ? getRateBand(row.type, impliedRate) : null;
    const bandColorMap: Record<RateBandSeverity, string> = {
      conservative: "#22c55e",
      moderate: "#3b82f6",
      aggressive: "#f59e0b",
      extreme: "#ef4444",
    };
    const impliedBandColor = impliedBand ? bandColorMap[impliedBand.severity] : "rgba(255,255,255,0.55)";
    const hasDirectionMismatch = impliedRate !== null && (
      (row.type === "cut" && impliedRate > 0) ||
      (row.type === "bulk" && impliedRate < 0) ||
      (row.type === "maintain" && Math.abs(impliedRate) > 0.05)
    );
    const outOfRangeCopy = impliedRate !== null && impliedBand?.warn
      ? (row.type === "cut"
        ? "Warning: this cut pace is outside our recommended range and may be too aggressive to sustain."
        : row.type === "bulk"
          ? "Warning: this bulk pace is outside our recommended range and may drive faster-than-intended gain."
          : "Warning: this pace is outside a typical maintenance range.")
      : null;
    const directionCopy = hasDirectionMismatch
      ? (row.type === "cut"
        ? "Heads up: this timeline implies weight gain during a cut phase."
        : row.type === "bulk"
          ? "Heads up: this timeline implies weight loss during a bulk phase."
          : "Heads up: this timeline implies weight change during a maintenance phase.")
      : null;

    return (
      <div style={{ borderRadius: 12, border: `1px solid ${phaseColor}44`, background: `${phaseColor}0e`, padding: "0.9rem 1rem", minWidth: 0, width: "100%", boxSizing: "border-box" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "0.65rem", marginBottom: "0.75rem", minWidth: 0 }}>
          <div style={{ width: 22, height: 22, borderRadius: 999, background: phaseColor, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "0.7rem", fontWeight: 900, color: "white", flexShrink: 0 }}>{rowIdx + 1}</div>
          <div style={{ fontWeight: 800, fontSize: "0.92rem", color: "white", minWidth: 0 }}>Phase {rowIdx + 1}</div>
          <div style={{ display: "inline-flex", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 8, overflow: "hidden", marginLeft: "auto", maxWidth: "100%" }}>
            {(["cut", "maintain", "bulk"] as RoadmapPhaseType[]).map((t) => (
              <button
                key={t}
                onClick={() => onUpdate("type", t)}
                style={{
                  ...btnBase,
                  border: "none",
                  borderRadius: 0,
                  padding: "0.2rem 0.55rem",
                  fontSize: "0.72rem",
                  background: row.type === t ? `${ROADMAP_PHASE_COLORS[t]}33` : "rgba(255,255,255,0.03)",
                  color: row.type === t ? ROADMAP_PHASE_COLORS[t] : "rgba(255,255,255,0.45)",
                  fontWeight: row.type === t ? 800 : 500,
                }}
              >
                {ROADMAP_PHASE_LABELS[t]}
              </button>
            ))}
          </div>
        </div>

        <div style={{ display: "grid", gap: isMobile ? "0.38rem" : "0.55rem", minWidth: 0 }}>
          <TriFieldRow
            mobile={isMobile}
            leftLabel={<FieldLabel text="Start weight" showLock={startFieldsLocked} lockTitle="Inherited from the previous phase" mobile={isMobile} style={editorLabelStyle} />}
            rightLabel={<FieldLabel text="Target weight" style={editorLabelStyle} />}
            leftField={<WeightInputWithUnit value={row.startWeight} disabled={startFieldsLocked} readOnly={startFieldsLocked} step={0.1} onChange={(e) => onUpdate("startWeight", e.target.value)} style={{ ...(startFieldsLocked ? editorLockedInputStyle : editorInputStyle), padding: isMobile ? "0.48rem 0.55rem" : "0.55rem 0.65rem", fontSize: isMobile ? "0.9rem" : undefined, fontVariantNumeric: "tabular-nums" }} />}
            rightField={<WeightInputWithUnit value={row.targetWeight} step={0.1} placeholder="e.g. 175" onChange={(e) => onUpdate("targetWeight", e.target.value)} style={{ ...editorInputStyle, padding: isMobile ? "0.48rem 0.55rem" : "0.55rem 0.65rem", fontSize: isMobile ? "0.9rem" : undefined, fontVariantNumeric: "tabular-nums" }} />}
            chip={<DeltaChip startWeight={row.startWeight} endWeight={row.targetWeight} />}
          />

          {row.type !== "maintain" && (
            <div style={{ marginBottom: "0.2rem" }}>
              <PlanMethodToggle
                value={planMethod}
                onChange={(v) => onUpdate("planMethod", v)}
              />
            </div>
          )}

          {row.type !== "maintain" && planMethod === "byPace" && (
            <div style={{ marginBottom: "0.2rem" }}>
              <div style={editorLabelStyle}>{row.type === "cut" ? "Cut Pace" : "Bulk Pace"}</div>
              <div style={{ display: "grid", gridTemplateColumns: isMobile ? "repeat(2, 1fr)" : `repeat(${paceOpts.length}, 1fr)`, gap: "0.4rem" }}>
                {paceOpts.map((opt) => {
                  const rate = AGGRESSIVENESS_RATES[opt.key][row.type];
                  const sign = rate > 0 ? "+" : "";
                  const rateLabel = `${sign}${rate} lb/wk`;
                  const isSelected = row.aggressiveness === opt.key;
                  return (
                    <button key={opt.key} onClick={() => onUpdate("aggressiveness", opt.key)}
                      style={{ ...btnBase, display: "flex", flexDirection: "column", alignItems: "center", gap: "0.15rem",
                        padding: "0.45rem 0.4rem", textAlign: "center",
                        background: isSelected ? "rgba(59,130,246,0.22)" : "rgba(255,255,255,0.04)",
                        borderColor: isSelected ? "rgba(59,130,246,0.5)" : "rgba(255,255,255,0.1)",
                        color: "white",
                      }}>
                      <div style={{ fontSize: "0.75rem", fontWeight: 800 }}>{opt.label}</div>
                      <div style={{ fontSize: "0.65rem", color: "rgba(255,255,255,0.45)", fontWeight: 500 }}>{rateLabel}</div>
                    </button>
                  );
                })}
              </div>
              {isCustomPace && impliedRate !== null ? (
                <div style={{ marginTop: "0.4rem", fontSize: "0.72rem", color: "rgba(255,255,255,0.5)" }}>
                  Computed pace: <span style={{ color: "white", fontWeight: 700 }}>{impliedRate > 0 ? "+" : ""}{impliedRate.toFixed(2)} lb/wk</span>
                  {impliedBand ? <span style={{ marginLeft: "0.35rem", color: "rgba(255,255,255,0.4)" }}>({getPaceBandDisplayLabel(impliedBand.label)})</span> : null}
                </div>
              ) : null}
            </div>
          )}

          <TriFieldRow
            mobile={isMobile}
            leftLabel={<FieldLabel text="Start date" showLock={startFieldsLocked} lockTitle="Inherited from the previous phase" mobile={isMobile} style={editorLabelStyle} />}
            rightLabel={<FieldLabel text="End date" style={editorLabelStyle} />}
            leftField={(
              <MobileDateField
                value={row.startDate}
                disabled={startFieldsLocked}
                readOnly={startFieldsLocked}
                onCommit={(nextDate) => onUpdate("startDate", nextDate)}
                style={{ ...(startFieldsLocked ? editorLockedInputStyle : editorInputStyle), width: "100%", minWidth: 0, padding: "0.58rem 0.65rem", fontSize: "0.9rem", fontVariantNumeric: "tabular-nums" }}
              />
            )}
            rightField={(
              <MobileDateField
                value={row.endDate}
                min={row.startDate}
                onCommitStart={() => { if (planMethod === "byPace") onUpdate("planMethod", "byDate"); }}
                onCommit={(nextDate) => onUpdate("endDate", nextDate)}
                style={{ ...editorInputStyle, width: "100%", minWidth: 0, padding: "0.58rem 0.65rem", fontSize: "0.9rem", fontVariantNumeric: "tabular-nums", opacity: planMethod === "byPace" ? 0.72 : 1, borderColor: row.endDate <= row.startDate ? "rgba(239,68,68,0.55)" : "rgba(255,255,255,0.14)" }}
              />
            )}
            chip={<DurationChip startDate={row.startDate} endDate={row.endDate} />}
          />

          {startFieldsLocked && startFieldsHelperCopy ? (
            <div style={{ fontSize: "0.74rem", color: "rgba(255,255,255,0.62)", lineHeight: 1.4 }}>
              {startFieldsHelperCopy}
            </div>
          ) : null}

          {planMethod === "byDate" && (
            isMobile ? (
              <div style={{ display: "flex", justifyContent: "center" }}>
                <DurationPresetChips
                  startDate={row.startDate}
                  endDate={row.endDate}
                  onSelect={(nextEndDate) => onUpdate("endDate", nextEndDate)}
                />
              </div>
            ) : (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 86px", gap: "0.28rem 0.55rem" }}>
                <div />
                <DurationPresetChips
                  startDate={row.startDate}
                  endDate={row.endDate}
                  onSelect={(nextEndDate) => onUpdate("endDate", nextEndDate)}
                />
                <div />
              </div>
            )
          )}
          {planMethod === "byDate" && row.endDate <= row.startDate ? (
            <div style={{ fontSize: "0.72rem", color: "#fca5a5" }}>End date must be after start date.</div>
          ) : null}
        </div>

        {row.type === "maintain" ? (
          <div style={{ marginTop: "0.65rem", padding: "0.5rem 0.7rem", borderRadius: 8, background: "rgba(59,130,246,0.08)", border: "1px solid rgba(59,130,246,0.2)", fontSize: "0.75rem", color: "rgba(255,255,255,0.55)" }}>
            Maintain = net 0 — no calorie deficit or surplus
          </div>
        ) : planMethod === "byDate" ? (
          <div style={{ marginTop: isMobile ? "0.4rem" : "0.65rem" }}>
            {isMobile ? (
              <div style={{ borderRadius: 10, padding: "0.42rem 0.55rem", border: "1px solid rgba(255,255,255,0.14)", background: "rgba(255,255,255,0.04)", display: "grid", gap: "0.22rem" }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "0.45rem" }}>
                  <div style={{ fontSize: "0.68rem", color: "rgba(255,255,255,0.6)", fontWeight: 700, textTransform: "uppercase" }}>Computed pace</div>
                  {impliedBand ? <span style={{ fontSize: "0.62rem", fontWeight: 700, color: impliedBandColor, border: `1px solid ${impliedBandColor}66`, background: `${impliedBandColor}22`, borderRadius: 999, padding: "0.12rem 0.4rem" }}>{getPaceBandDisplayLabel(impliedBand.label)}</span> : null}
                </div>
                <div style={{ fontSize: "0.88rem", fontWeight: 800, color: "white" }}>
                  {impliedRate !== null ? formatSignedPace(impliedRate, 2, "lb/wk") : "—"}
                </div>
                {(directionCopy || outOfRangeCopy) ? (
                  <div style={{ fontSize: "0.68rem", color: outOfRangeCopy ? "#fca5a5" : "#fdba74", fontWeight: 600 }}>
                    {outOfRangeCopy ?? directionCopy}
                  </div>
                ) : null}
              </div>
            ) : (
              <>
                <div style={editorLabelStyle}>Computed pace</div>
                <div style={{ marginTop: "0.1rem", borderRadius: 10, padding: "0.65rem 0.75rem", border: "1px solid rgba(255,255,255,0.15)", background: "rgba(255,255,255,0.04)", display: "grid", gap: "0.35rem" }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "0.5rem" }}>
                    <div style={{ fontSize: "0.7rem", letterSpacing: "0.02em", textTransform: "uppercase", color: "rgba(255,255,255,0.5)", fontWeight: 700 }}>Computed pace</div>
                    {impliedBand ? <span style={{ fontSize: "0.66rem", fontWeight: 700, color: impliedBandColor, border: `1px solid ${impliedBandColor}66`, background: `${impliedBandColor}22`, borderRadius: 999, padding: "0.15rem 0.45rem" }}>{getPaceBandDisplayLabel(impliedBand.label)}</span> : null}
                  </div>
                  <div style={{ fontSize: "0.95rem", fontWeight: 800, color: "white" }}>
                    {impliedRate !== null ? formatSignedPace(impliedRate, 2, "lb/wk") : "—"}
                  </div>
                  <div style={{ fontSize: "0.7rem", color: "rgba(255,255,255,0.52)" }}>
                    Pace is calculated from your start weight, target weight, and date range.
                  </div>
                  {directionCopy ? <div style={{ fontSize: "0.72rem", color: "#fdba74", fontWeight: 600 }}>{directionCopy}</div> : null}
                  {outOfRangeCopy ? <div style={{ fontSize: "0.72rem", color: "#fca5a5", fontWeight: 600 }}>{outOfRangeCopy}</div> : null}
                </div>
              </>
            )}
          </div>
        ) : null}
      </div>
    );
  };

  const trackerPage = (
    <div style={{ ...mainContentWidthStyle, display: "grid", gap: "1.5rem" }}>
      {dashboardEmptyState ? (
        trackerOnboardingCard
      ) : activeSprint ? (
        <>
          {isDashboardMobile ? (
            <div style={{ display: "grid", gap: "1rem", width: "100%" }}>
              {sprintStatusPanel}
              {dailyTargetsCard}
              {trackerCharts}
              {trackerCaloriesMobileCard}
              {recalibrateCard}
              {sprintHistoryMobileCard}
            </div>
          ) : (
            <div style={{ display: "grid", gap: "1rem", width: "100%", minWidth: 0 }}>
              <div
                id="sprint-section"
                ref={setSprintSectionRefs}
                style={{
                  minWidth: 0,
                  display: "grid",
                  gridTemplateColumns: isDashboardHeroStacked ? "minmax(0, 1fr)" : "minmax(0, 3fr) minmax(0, 1fr)",
                  gap: "1rem",
                  alignItems: "stretch",
                }}
              >
                <div style={{ minWidth: 0 }}>
                  {sprintStatusPanel}
                </div>
                <div style={{ minWidth: 0 }}>
                  {dailyTargetsCard}
                </div>
              </div>
              <div style={{ minWidth: 0 }}>{trackerCharts}</div>
              <div style={{ minWidth: 0 }}>{recalibrateCard}</div>
              <div style={{ minWidth: 0 }}>{sprintTable}</div>
            </div>
          )}
        </>
      ) : null}
    </div>
  );

  const trendsPresetButtons = ([ ["30d", "30d"], ["3m", "3 mo"], ["6m", "6 mo"], ["1y", "1 yr"], ["all", "All"] ] as [string, string][]);
  const activeTrendsPreset = useMemo(() => {
    if (!entriesAsc.length || trendsSelectedEnd !== todayYmd || !trendsSelectedStart) return null;
    return trendsPresetButtons.find(([key]) => getRangePresetStart(key, todayYmd, entriesAsc[0].date) === trendsSelectedStart)?.[0] ?? null;
  }, [entriesAsc, getRangePresetStart, trendsPresetButtons, trendsSelectedEnd, trendsSelectedStart, todayYmd]);
  const trendsMetricMeta = CHART_METRIC_META;
  const isWeightTrendsView = trendsMetric === "weight";
  const showSprintsOnTrendsChart = showSprints && !isMobile;
  const renderTrendsBrushTraveller = (travellerProps) => {
    const x = Number.isFinite(travellerProps?.x) ? travellerProps.x : 0;
    const y = Number.isFinite(travellerProps?.y) ? travellerProps.y : 0;
    const width = Number.isFinite(travellerProps?.width) ? travellerProps.width : 14;
    const height = Number.isFinite(travellerProps?.height) ? travellerProps.height : 18;
    const centerX = x + width / 2;
    const centerY = y + height / 2;

    return (
      <g {...travellerProps} style={{ ...travellerProps?.style, cursor: "ew-resize" }}>
        <rect x={x} y={y} width={width} height={height} rx={5} ry={5} fill="rgba(147,197,253,0.9)" stroke="rgba(255,255,255,0.9)" strokeWidth={1.1} />
        <circle cx={centerX} cy={centerY} r={3} fill="rgba(11,15,20,0.95)" />
      </g>
    );
  };

  const trendsMetricChips = useMemo(() => {
    return buildMetricStatChips({
      metric: trendsMetric,
      metricAverage: trendsMetricAverage,
      metricMinMax: trendsMetricMinMax,
      metricObservedPoints: trendsMetricObservedPoints,
      metricTrendPerDay: trendsMetricTrendPerDay,
      startPoint: trendsStartPoint,
      endPoint: trendsEndPoint,
      totalDelta: trendsTotalDelta,
      weeklyDelta: trendsWeeklyDelta,
      startLabel: trendsSelectedStart,
      endLabel: trendsSelectedEnd,
    });
  }, [buildMetricStatChips, trendsMetric, trendsMetricAverage, trendsMetricMinMax, trendsMetricObservedPoints, trendsMetricTrendPerDay, trendsSelectedEnd, trendsSelectedStart, trendsStartPoint, trendsEndPoint, trendsTotalDelta, trendsWeeklyDelta]);

  const trendsPage = (
    <div style={{ ...mainContentWidthStyle, display: "grid", gap: "1rem" }}>
      <div style={{ ...cardStyle, borderRadius: 18, minWidth: isMobile ? "auto" : 0, overflow: isMobile ? "hidden" : "visible", padding: isMobile ? "0.9rem" : "1rem" }}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: "0.75rem", alignItems: "center", flexWrap: "wrap" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "0.55rem", flexWrap: "wrap" }}>
            <div style={{ fontSize: isMobile ? "1.2rem" : "1.4rem", fontWeight: 900 }}>Trends</div>
            <div style={{ display: "flex", gap: "0.35rem", flexWrap: "wrap" }}>
              {CHART_METRIC_OPTIONS.map(({ key, label }) => (
                <button key={key} type="button" onClick={() => setChartsMetric(key)} style={{ ...btnBase, minHeight: 28, padding: "0.2rem 0.62rem", fontSize: "0.78rem", background: trendsMetric === key ? "rgba(59,130,246,0.22)" : "rgba(255,255,255,0.06)", border: trendsMetric === key ? "1px solid rgba(99,155,255,0.5)" : "1px solid rgba(255,255,255,0.14)", color: "rgba(255,255,255,0.92)" }}>{label}</button>
              ))}
            </div>
          </div>
        </div>

        <div style={{ marginTop: "0.6rem", display: "grid", gridTemplateColumns: isMobile ? "repeat(2, minmax(0, 1fr))" : "repeat(2, minmax(0, 320px))", gap: isMobile ? "0.65rem" : "0.75rem", alignItems: "start", minWidth: 0 }}>
          <div style={{ width: "100%", display: "grid", gap: "0.5rem", minWidth: 0 }}>
            {isMobile ? (
              <div style={{ display: "grid", gap: "0.45rem" }}>
                <label style={{ color: "rgba(255,255,255,0.75)", fontSize: "0.85rem" }}>Start date</label>
                <MobileDateField
                  value={trendsStartDraft}
                  onCommit={(v) => {
                    setChartsStartWithAutoMovingAverage(v);
                    if (entriesAsc.length) commitChartRange(v, trendsEndDraft || trendsEnd || todayYmd, "start");
                  }}
                  style={{ width: "100%", padding: "0.58rem 0.65rem", borderRadius: 12, border: "1px solid rgba(255,255,255,0.14)", background: "rgba(255,255,255,0.04)", color: "white", outline: "none", minHeight: SPRINT_DATE_FIELD_HEIGHT, fontSize: "0.8rem", fontVariantNumeric: "tabular-nums" }}
                />
              </div>
            ) : (
              <Input label="Start date" type="date" value={trendsStartDraft} onChange={setChartsStartWithAutoMovingAverage} onBlur={commitChartsStartDraft} onKeyDown={(_, e) => { if (e.key === "Enter") commitChartsStartDraft(); }} controlHeight={GOAL_FIELD_HEIGHT} />
            )}
            {!isMobile ? <div style={{ minHeight: 30 }} /> : null}
          </div>
          <div style={{ width: "100%", display: "grid", gap: "0.5rem", minWidth: 0 }}>
            {isMobile ? (
              <div style={{ display: "grid", gap: "0.45rem" }}>
                <label style={{ color: "rgba(255,255,255,0.75)", fontSize: "0.85rem" }}>End date</label>
                <MobileDateField
                  value={trendsEndDraft}
                  onCommit={(v) => {
                    setChartsEndWithAutoMovingAverage(v);
                    if (entriesAsc.length) commitChartRange(trendsStartDraft || trendsStart || entriesAsc[0]?.date || todayYmd, v, "end");
                  }}
                  style={{ width: "100%", padding: "0.58rem 0.65rem", borderRadius: 12, border: "1px solid rgba(255,255,255,0.14)", background: "rgba(255,255,255,0.04)", color: "white", outline: "none", minHeight: SPRINT_DATE_FIELD_HEIGHT, fontSize: "0.8rem", fontVariantNumeric: "tabular-nums" }}
                />
              </div>
            ) : (
              <Input label="End date" type="date" value={trendsEndDraft} onChange={setChartsEndWithAutoMovingAverage} onBlur={commitChartsEndDraft} onKeyDown={(_, e) => { if (e.key === "Enter") commitChartsEndDraft(); }} controlHeight={GOAL_FIELD_HEIGHT} />
            )}
            {!isMobile ? <div style={{ display: "flex", gap: "0.35rem", flexWrap: "nowrap", overflowX: "visible", paddingTop: "0.05rem" }}>
              {trendsPresetButtons.map(([key, label]) => {
                const selected = activeTrendsPreset === key;
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setRangePreset(key)}
                    style={{
                      ...btnBase,
                      padding: "0.2rem 0.52rem",
                      borderRadius: 999,
                      fontSize: "0.74rem",
                      whiteSpace: "nowrap",
                      background: selected ? "rgba(59,130,246,0.2)" : "rgba(255,255,255,0.06)",
                      borderColor: selected ? "rgba(59,130,246,0.45)" : "rgba(255,255,255,0.16)",
                      color: "rgba(255,255,255,0.9)",
                    }}
                  >
                    {label}
                  </button>
                );
              })}
            </div> : null}
          </div>
        </div>
        {isMobile ? (
          <div style={{ marginTop: "0.45rem", display: "flex", gap: "0.35rem", flexWrap: "wrap", minWidth: 0, justifyContent: "flex-start" }}>
            {trendsPresetButtons.map(([key, label]) => {
              const selected = activeTrendsPreset === key;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => setRangePreset(key)}
                  style={{
                    ...btnBase,
                    padding: "0.2rem 0.52rem",
                    borderRadius: 999,
                    fontSize: "0.74rem",
                    whiteSpace: "nowrap",
                    background: selected ? "rgba(59,130,246,0.2)" : "rgba(255,255,255,0.06)",
                    borderColor: selected ? "rgba(59,130,246,0.45)" : "rgba(255,255,255,0.16)",
                    color: "rgba(255,255,255,0.9)",
                  }}
                >
                  {label}
                </button>
              );
            })}
          </div>
        ) : null}
        {trendsRangeMessage ? <div style={{ marginTop: "0.45rem", color: "#fcd34d", fontSize: "0.8rem" }}>{trendsRangeMessage}</div> : null}

        <div style={{ marginTop: "0.6rem", display: "flex", justifyContent: "space-between", alignItems: "center", gap: "0.6rem", flexWrap: "wrap" }}>
          <div style={{ fontSize: "1.04rem", fontWeight: 900 }}>{trendsMetricMeta[trendsMetric].title}</div>
          <div style={{ display: "inline-flex", gap: "0.35rem", alignItems: "center" }}>
            {(!isMobile ? [
              { key: "s", label: "Show sprints", on: showSprints, toggle: () => setShowSprints((v) => !v) },
            ] : []).map((t) => (
              <button key={t.key} type="button" onClick={t.toggle} disabled={t.disabled} title={t.title} style={{ ...btnBase, minHeight: 28, padding: "0.24rem 0.62rem", background: t.on ? "rgba(59,130,246,0.22)" : "rgba(255,255,255,0.06)", border: t.on ? "1px solid rgba(99,155,255,0.5)" : "1px solid rgba(255,255,255,0.14)", color: t.disabled ? "rgba(255,255,255,0.45)" : "rgba(255,255,255,0.88)", fontSize: "0.78rem", opacity: t.disabled ? 0.65 : 1, cursor: t.disabled ? "not-allowed" : "pointer" }}>{t.label}</button>
            ))}
            {isWeightTrendsView ? (
              <button type="button" onClick={cycleMovingAverageSetting} title="Toggle smoothing mode" style={{ ...btnBase, minHeight: 28, padding: "0.24rem 0.62rem", background: showMovingAverageLines ? "rgba(59,130,246,0.22)" : "rgba(255,255,255,0.06)", border: showMovingAverageLines ? "1px solid rgba(99,155,255,0.5)" : "1px solid rgba(255,255,255,0.14)", color: showMovingAverageLines ? "rgba(255,255,255,0.88)" : "rgba(255,255,255,0.55)", fontSize: "0.78rem" }}>
                {trendsMovingAverageLabel}
              </button>
            ) : null}
          </div>
        </div>

        <div style={{ marginTop: "0.6rem", height: isMobile ? 250 : 420, width: isMobile ? "auto" : "100%", marginLeft: isMobile ? -6 : 0, marginRight: isMobile ? -6 : 0, position: "relative" }}>
          {trendsMetric === "weight" ? (
            !trendsChartData.length ? <div style={{ height: "100%", display: "grid", placeItems: "center", color: "rgba(255,255,255,0.45)" }}>No data in selected range.</div> : (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={trendsChartData} margin={{ top: 10, right: 12, bottom: 10, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                  <XAxis dataKey="date" ticks={isMobile ? trendsMobileXAxisTicks : trendsDesktopXAxisTicks} tickFormatter={trendsXAxisFormatter} tick={{ fill: "rgba(255,255,255,0.65)", fontSize: isMobile ? 10 : 12 }} angle={isMobile ? -32 : -45} textAnchor="end" interval={0} height={isMobile ? 38 : 60} />
                  <YAxis tick={{ fill: "rgba(255,255,255,0.65)", fontSize: 12 }} ticks={trendsYTicks} domain={[trendsYTicks[0] ?? "auto", trendsYTicks[trendsYTicks.length - 1] ?? "auto"]} width={45} />
                  {chartTooltip}
                  {showSprintsOnTrendsChart ? sprintBandsForCharts.map((band, idx) => <ReferenceArea key={`${band.sprint.id}-${idx}`} x1={band.x1} x2={band.x2} y1={trendsYTicks[0] ?? "auto"} y2={trendsYTicks[trendsYTicks.length - 1] ?? "auto"} fill={hexToRgba(band.color, 0.14 + Math.min(idx * 0.012, 0.08))} stroke="none" ifOverflow="extendDomain" onClick={() => openSprintChartActions(band.sprint.id)} />) : null}
                  {showSprintsOnTrendsChart ? (
                    <Customized
                      component={({ xAxisMap, offset }) => {
                        const xAxis = Object.values(xAxisMap ?? {})[0];
                        const xScale = xAxis?.scale;
                        if (!xScale || !offset) return null;

                        const labelGap = 8;
                        const laneHeight = 24;
                        const laneEnds = [];
                        const plotLeft = offset.left;
                        const plotRight = offset.left + offset.width;

                        const baseLabels = sprintBandsForCharts
                          .map((band) => {
                            const xStart = xScale(band.x1);
                            const xEnd = xScale(band.x2);
                            if (!Number.isFinite(xStart) || !Number.isFinite(xEnd)) return null;
                            const regionLeft = Math.max(plotLeft, Math.min(xStart, xEnd));
                            const regionRight = Math.min(plotRight, Math.max(xStart, xEnd));
                            const rightAnchor = Math.min(plotRight, regionLeft + 2);
                            const leftAnchor = Math.max(plotLeft, regionRight - 2);
                            const rightMaxWidth = Math.min(SPRINT_CHIP_MAX_WIDTH_PX, Math.max(0, plotRight - rightAnchor - 2));
                            const leftMaxWidth = Math.min(SPRINT_CHIP_MAX_WIDTH_PX, Math.max(0, leftAnchor - plotLeft - 2));

                            const rightAvailTextPx = Math.max(0, rightMaxWidth - SPRINT_CHIP_HORIZONTAL_PADDING - SPRINT_CHIP_TEXT_MARGIN);
                            const leftAvailTextPx = Math.max(0, leftMaxWidth - SPRINT_CHIP_HORIZONTAL_PADDING - SPRINT_CHIP_TEXT_MARGIN);
                            const rightFit = fitLabelToWidth(band.sprint.name, rightAvailTextPx);
                            const leftFit = fitLabelToWidth(band.sprint.name, leftAvailTextPx);

                            let side = "right";
                            let maxLabelWidth = rightMaxWidth;
                            let resolved = rightFit;
                            if (leftFit.text.length > rightFit.text.length || (rightFit.hidden && !leftFit.hidden)) {
                              side = "left";
                              maxLabelWidth = leftMaxWidth;
                              resolved = leftFit;
                            }

                            if (resolved.hidden) {
                              const insideMaxWidth = Math.min(SPRINT_CHIP_MAX_WIDTH_PX, Math.max(0, regionRight - regionLeft));
                              const insideAvailTextPx = Math.max(0, insideMaxWidth - SPRINT_CHIP_HORIZONTAL_PADDING - SPRINT_CHIP_TEXT_MARGIN);
                              const insideFit = fitLabelToWidth(band.sprint.name, insideAvailTextPx);
                              if (!insideFit.hidden) {
                                side = "inside";
                                maxLabelWidth = insideMaxWidth;
                                resolved = insideFit;
                              } else {
                                resolved = { text: "…", truncated: true, hidden: false };
                                maxLabelWidth = Math.max(56, Math.max(rightMaxWidth, leftMaxWidth, insideMaxWidth));
                              }
                            }

                            if (maxLabelWidth < 22) return null;
                            const textWidth = Math.max(0, resolved.text.length * SPRINT_CHIP_AVG_CHAR_PX);
                            const rawPillWidth = Math.max(56, textWidth + SPRINT_CHIP_HORIZONTAL_PADDING);
                            const pillWidth = Math.min(rawPillWidth, maxLabelWidth);
                            let left = rightAnchor;
                            if (side === "left") left = Math.max(plotLeft, leftAnchor - pillWidth);
                            if (side === "inside") left = Math.max(plotLeft, Math.min(regionLeft + 2, plotRight - pillWidth));
                            const right = Math.min(plotRight, left + pillWidth);

                            return { band, labelText: resolved.text, fullName: sanitizeSprintName(band.sprint.name), left, right, pillWidth: Math.max(0, right - left) };
                          })
                          .filter(Boolean)
                          .sort((a, b) => a.left - b.left);

                        const labels = baseLabels.map((item) => {
                          let lane = 0;
                          while (laneEnds[lane] !== undefined && item.left < laneEnds[lane] + labelGap) lane += 1;
                          laneEnds[lane] = item.right;
                          return { ...item, lane };
                        });

                        return (
                          <g>
                            {labels.map((item) => {
                              const y = offset.top + 8 + item.lane * laneHeight;
                              return (
                                <g key={`label-${item.band.sprint.id}`} onClick={() => openSprintChartActions(item.band.sprint.id)} style={{ cursor: "pointer" }}>
                                  <title>{item.fullName}</title>
                                  <rect x={item.left} y={y} width={item.pillWidth} height={20} rx={10} fill={hexToRgba(item.band.color, 0.92)} stroke="rgba(255,255,255,0.35)" />
                                  <text x={item.left + SPRINT_CHIP_HORIZONTAL_PADDING / 2 - 1} y={y + 14} textAnchor="start" fill="rgba(255,255,255,0.96)" fontSize={11} fontWeight={800}>{item.labelText}</text>
                                </g>
                              );
                            })}
                          </g>
                        );
                      }}
                    />
                  ) : null}
                  <Line type="monotone" dataKey="actual" name="Actual" stroke={COLORS.blue} strokeWidth={2} dot={{ r: 2, strokeWidth: 0 }} strokeOpacity={0.55} connectNulls={false} isAnimationActive={false} />
                  <Line type="monotone" dataKey="inferred" name="Inferred (gaps)" stroke="rgba(255,255,255,0.35)" strokeWidth={2} dot={false} strokeDasharray="2 6" connectNulls={false} isAnimationActive={false} />
                  {showMovingAverageLines ? <Line type="monotone" dataKey="movingAvg" name={trendsMovingAverageLabel} stroke={COLORS.lightBlue} strokeWidth={4} dot={false} connectNulls isAnimationActive={false} /> : null}
                  {!isMobile && <Brush dataKey="date" height={18} travellerWidth={16} traveller={renderTrendsBrushTraveller} startIndex={trendsBrushRange.startIndex} endIndex={trendsBrushRange.endIndex} onChange={(range) => { if (!range) return; setChartsBrushRange((prev) => ({ startIndex: typeof range.startIndex === "number" ? range.startIndex : prev.startIndex, endIndex: typeof range.endIndex === "number" ? range.endIndex : prev.endIndex })); }} stroke="rgba(255,255,255,0.22)" fill="rgba(255,255,255,0.02)" tickFormatter={() => ""} />}
                </LineChart>
              </ResponsiveContainer>
            )
          ) : (
            !trendsMetricRows.length ? <div style={{ height: "100%", display: "grid", placeItems: "center", color: "rgba(255,255,255,0.45)" }}>No data in selected range.</div> : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={trendsMetricRowsWithTrendline} margin={{ top: 10, right: 12, bottom: 10, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                  <XAxis dataKey="date" ticks={isMobile ? trendsCalorieMobileXAxisTicks : trendsCalorieDesktopXAxisTicks} tickFormatter={trendsXAxisFormatter} tick={{ fill: "rgba(255,255,255,0.65)", fontSize: isMobile ? 10 : 12 }} angle={isMobile ? -32 : -45} textAnchor="end" interval={0} height={isMobile ? 38 : 60} />
                  <YAxis tick={{ fill: "rgba(255,255,255,0.65)", fontSize: 12 }} width={55} domain={trendsMetric === "netCalories" ? trendsCalorieDomain : ["auto", "auto"]} />
                  <Tooltip content={renderTrendsMetricTooltip} />
                  {showSprintsOnTrendsChart ? sprintBandsForCharts.map((band, idx) => <ReferenceArea key={`${band.sprint.id}-${idx}`} x1={band.x1} x2={band.x2} fill={hexToRgba(band.color, 0.12 + Math.min(idx * 0.012, 0.06))} stroke="none" ifOverflow="extendDomain" onClick={() => openSprintChartActions(band.sprint.id)} />) : null}
                  {showSprintsOnTrendsChart ? (
                    <Customized
                      component={({ xAxisMap, offset }) => {
                        const xAxis = Object.values(xAxisMap ?? {})[0];
                        const xScale = xAxis?.scale;
                        if (!xScale || !offset) return null;
                        const labelGap = 8;
                        const laneHeight = 24;
                        const laneEnds = [];
                        const plotLeft = offset.left;
                        const plotRight = offset.left + offset.width;
                        const baseLabels = sprintBandsForCharts.map((band) => {
                          const xStart = xScale(band.x1);
                          const xEnd = xScale(band.x2);
                          if (!Number.isFinite(xStart) || !Number.isFinite(xEnd)) return null;
                          const left = Math.max(plotLeft, Math.min(xStart, xEnd) + 2);
                          const right = Math.min(plotRight, Math.max(xStart, xEnd) - 2);
                          const maxLabelWidth = Math.min(SPRINT_CHIP_MAX_WIDTH_PX, Math.max(56, right - left));
                          const fit = fitLabelToWidth(band.sprint.name, Math.max(0, maxLabelWidth - SPRINT_CHIP_HORIZONTAL_PADDING - SPRINT_CHIP_TEXT_MARGIN));
                          if (fit.hidden) return null;
                          const textWidth = Math.max(0, fit.text.length * SPRINT_CHIP_AVG_CHAR_PX);
                          const pillWidth = Math.min(Math.max(56, textWidth + SPRINT_CHIP_HORIZONTAL_PADDING), maxLabelWidth);
                          return { band, labelText: fit.text, fullName: sanitizeSprintName(band.sprint.name), left, right: left + pillWidth, pillWidth };
                        }).filter(Boolean).sort((a, b) => a.left - b.left);
                        const labels = baseLabels.map((item) => {
                          let lane = 0;
                          while (laneEnds[lane] !== undefined && item.left < laneEnds[lane] + labelGap) lane += 1;
                          laneEnds[lane] = item.right;
                          return { ...item, lane };
                        });
                        return <g>{labels.map((item) => { const y = offset.top + 8 + item.lane * laneHeight; return <g key={`bar-label-${item.band.sprint.id}`} onClick={() => openSprintChartActions(item.band.sprint.id)} style={{ cursor: "pointer" }}><title>{item.fullName}</title><rect x={item.left} y={y} width={item.pillWidth} height={20} rx={10} fill={hexToRgba(item.band.color, 0.92)} stroke="rgba(255,255,255,0.35)" /><text x={item.left + SPRINT_CHIP_HORIZONTAL_PADDING / 2 - 1} y={y + 14} textAnchor="start" fill="rgba(255,255,255,0.96)" fontSize={11} fontWeight={800}>{item.labelText}</text></g>; })}</g>;
                      }}
                    />
                  ) : null}
                  {trendsMetric === "netCalories" ? <ReferenceLine y={0} stroke="rgba(255,255,255,0.55)" /> : null}
                  <Bar dataKey={trendsMetric} name={trendsMetricMeta[trendsMetric].label} isAnimationActive={false} fill="rgba(132,153,188,0.88)" />
                  {!isMobile && <Brush dataKey="date" height={18} travellerWidth={16} traveller={renderTrendsBrushTraveller} startIndex={trendsBrushRange.startIndex} endIndex={trendsBrushRange.endIndex} onChange={(range) => { if (!range) return; setChartsBrushRange((prev) => ({ startIndex: typeof range.startIndex === "number" ? range.startIndex : prev.startIndex, endIndex: typeof range.endIndex === "number" ? range.endIndex : prev.endIndex })); }} stroke="rgba(255,255,255,0.22)" fill="rgba(255,255,255,0.02)" tickFormatter={() => ""} />}
                </BarChart>
              </ResponsiveContainer>
            )
          )}
        </div>

        <div style={{ marginTop: "0.7rem", display: isMobile ? "grid" : "flex", gridTemplateColumns: isMobile ? "repeat(2, minmax(0, 1fr))" : undefined, gap: isMobile ? "0.65rem" : "0.45rem", flexWrap: isMobile ? undefined : "wrap", maxWidth: 900, minWidth: 0 }}>
          {trendsMetricChips}
          <ViewerStatChip label="Days tracked" value={`${trendsDaysTrackedLabel.logged}/${trendsDaysTrackedLabel.total || 0}`} sub={`${trendsDaysTrackedLabel.pct}% of days`} tone="muted" />
        </div>
        {selectedChartSprint ? (
          <div style={{ position: "fixed", inset: 0, background: "rgba(2,6,23,0.62)", zIndex: 80, display: "grid", placeItems: "center", padding: "1rem" }} onClick={() => setChartSprintActionId(null)}>
            <div onClick={(e) => e.stopPropagation()} style={{ width: "min(480px, 92vw)", borderRadius: 16, border: "1px solid rgba(255,255,255,0.18)", background: "linear-gradient(160deg, rgba(15,23,42,0.98), rgba(30,41,59,0.98))", padding: "1rem" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.65rem" }}><span style={{ width: 12, height: 12, borderRadius: 999, background: getSprintColor(selectedChartSprint), display: "inline-block" }} /><div style={{ fontWeight: 900 }}>{selectedChartSprint.name}</div></div>
              <div style={{ color: "rgba(255,255,255,0.72)", fontSize: "0.85rem" }}>{formatMDY(selectedChartSprint.startDate)} → {selectedChartSprint.endDate ? formatMDY(selectedChartSprint.endDate) : "(active)"}</div>
              <div style={{ marginTop: "0.9rem", display: "flex", gap: "0.6rem", flexWrap: "wrap" }}>
                <button style={{ ...btnBase, background: actionColors.primaryGradient, color: actionColors.primaryText, borderColor: "rgba(148,163,184,0.45)" }} onClick={() => zoomChartsToSprint(selectedChartSprint.id)}>Zoom chart to sprint range</button>
                <button style={{ ...btnBase, background: "rgba(255,255,255,0.08)", color: "white" }} onClick={() => openSprintViewerFromModal(selectedChartSprint.id)}>{isTrulyActiveSprint(selectedChartSprint) ? "Open dashboard" : "Open sprint viewer"}</button>
                <button style={{ ...btnBase, background: "rgba(255,255,255,0.06)", color: "white" }} onClick={() => setChartSprintActionId(null)}>Cancel</button>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );

  const importCsv = async (file) => {
    if (blockDemoWrite("Disabled in Demo")) return;
    if (!file) return;
    try {
      setCsvImportMsg(null);
      const text = await file.text();
      const parsed = importEntriesFromCsvText(text);
      if (!parsed.rows.length) {
        setCsvImportMsg(parsed.errors.length ? parsed.errors[0] : "No rows imported.");
        return;
      }

      // Overwrite-by-date. Keep existing fields unless CSV provides them.
      const map = new Map(entriesAsc.map((e) => [e.date, e]));

      for (const r of parsed.rows) {
        const prev = map.get(r.date) ?? null;

        const nextWeight = r.weight;

        const nextCaloriesIn = r.caloriesIn !== null ? r.caloriesIn : prev?.caloriesIn ?? null;
        const nextActiveCalories = r.activeCalories !== null ? r.activeCalories : prev?.activeCalories ?? null;
        const loggedTotalBurned = r.totalBurned !== null ? r.totalBurned : prev?.totalBurned ?? null;

        const bestKnownWeight = getBestKnownWeightForDate({
          entriesAsc,
          date: r.date,
          sprintStartWeight: startWeight,
          vitalsCurrentWeight: vitals.currentWeightLbs,
        }) ?? nextWeight;

        const resolvedTdee = resolveTdeeForDate({
          entry: { totalBurned: loggedTotalBurned, activeCalories: nextActiveCalories },
          date: r.date,
          bestKnownWeight,
          activityLevelFactor: vitals.activityFactor ?? forecast.activityFactor,
          vitals,
        });

        const nextBmr = Number.isFinite(bestKnownWeight)
          ? calcBMRAtDate({
            weightLbs: bestKnownWeight,
            heightIn: vitals.height,
            gender: vitals.gender,
            birthday: vitals.birthday,
            onDateYmd: r.date,
          })
          : null;

        const nextNet = nextCaloriesIn !== null
          ? (r.netCalories !== null ? r.netCalories : resolvedTdee.tdee !== null ? getNetCalories(nextCaloriesIn, resolvedTdee.tdee) : null)
          : null;

        map.set(r.date, {
          date: r.date,
          weight: nextWeight,
          caloriesIn: nextCaloriesIn,
          totalBurned: resolvedTdee.tdee,
          activeCalories: nextActiveCalories,
          netCalories: nextNet,
          bmr: nextBmr,
          tdeeSource: resolvedTdee.source,
          theoreticalWeight: nextWeight,
        });
      }

      const reconciled = reconcileEntriesDerivedMetrics(
        Array.from(map.values()).sort((a, b) => (a.date < b.date ? -1 : 1)),
        vitals,
      );
      const next = recalcTheoretical(reconciled);
      setEntries(next);
      await persistAll(next, vitals, forecast, sprints);

      const warningSuffix = parsed.errors.length ? ` (Skipped/Warnings: ${parsed.errors.length})` : "";
      const baseMsg = activeSprint
        ? `Imported ${parsed.rows.length} rows. Overwrote matching dates.`
        : `Imported ${parsed.rows.length} entries. Start a sprint to view trends.`;
      setCsvImportMsg(`${baseMsg}${warningSuffix}`);
      setShowCsvImportModal(false);
    } catch (e) {
      console.error(e);
      setCsvImportMsg("CSV import failed. Please check the file format.");
    }
  };

  const exportProfileJson = () => {
    if (isDemoMode) {
      setSettingsDataStatusMsg("Disabled in Demo");
      return;
    }
    try {
      const payload = buildProfileExportPayload({ vitals, entries: entriesAsc, sprints, forecast, roadmap });
      const text = JSON.stringify(payload, null, 2);
      downloadTextFile(`weight-tracker-profile-${todayYmd}.json`, text);
      setSettingsDataStatusMsg(`Exported profile JSON (v${PROFILE_SCHEMA_VERSION}).`);
    } catch {
      setSettingsDataStatusMsg("Profile export failed. Please try again.");
    }
  };

  const importProfileFromSettings = async (file) => {
    if (blockDemoWrite("Disabled in Demo")) return;
    if (!file) return;
    setSettingsDataStatusMsg(null);
    setRestoreProfileFile(file);
    setRestoreConfirmText("");
  };

  const closeRestoreProfileModal = () => {
    setRestoreProfileFile(null);
    setRestoreConfirmText("");
  };

  const confirmRestoreProfile = async () => {
    if (blockDemoWrite("Disabled in Demo")) return;
    if (!restoreProfileFile) return;
    try {
      const text = await restoreProfileFile.text();
      const parsedJson = JSON.parse(text);
      const parsed = importProfilePayload(parsedJson);
      if (parsed.error) {
        setSettingsDataStatusMsg(parsed.error);
        closeRestoreProfileModal();
        return;
      }
      const success = await applyImportedProfile(parsed, { skipToTracker: false });
      setSettingsDataStatusMsg(success);
      setCsvImportMsg(null);
      closeRestoreProfileModal();
    } catch {
      setSettingsDataStatusMsg("Profile restore failed: invalid JSON file.");
      closeRestoreProfileModal();
    }
  };


  const openCsvImportModal = () => {
    if (isDemoMode) {
      setCsvImportMsg("Disabled in Demo");
      setSettingsDataStatusMsg("Disabled in Demo");
      return;
    }
    setCsvImportMsg(null);
    setShowCsvImportModal(true);
  };

  const closeCsvImportModal = () => {
    setShowCsvImportModal(false);
  };

  const displayedAllDataEntriesMobile = entriesDesc.slice(0, mobileAllDataVisibleCount);
  const hasMoreAllDataEntriesMobile = mobileAllDataVisibleCount < entriesDesc.length;
  const useCompactAllDataLayout = isCompactMobile || isDashboardHeroStacked;
  const allDataMobileList = (
    <div style={{ marginTop: "1rem", display: "grid", gap: "0.45rem" }}>
      {displayedAllDataEntriesMobile.length ? displayedAllDataEntriesMobile.map((e) => {
        const expanded = expandedAllDataRowDate === e.date;
        const activeIsOura = String(e.activeCaloriesSource ?? "").toLowerCase() === "oura";
        return (
          <div key={e.date} style={{ border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, background: "rgba(255,255,255,0.03)", overflow: "hidden" }}>
            <div
              style={{ display: "grid", gridTemplateColumns: "minmax(0, 0.94fr) repeat(4, auto) auto", alignItems: "center", gap: "0.32rem", padding: "0.48rem 0.46rem" }}
            >
              <span style={{ minWidth: 0, fontSize: "0.73rem", color: "rgba(255,255,255,0.92)", fontWeight: 700, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{formatMD(e.date)}</span>
              <span style={{ fontSize: "0.65rem", color: "rgba(255,255,255,0.8)", whiteSpace: "nowrap" }}>Wt {e.weight?.toFixed(1) ?? "—"}</span>
              <span style={{ fontSize: "0.65rem", color: "rgba(255,255,255,0.8)", whiteSpace: "nowrap" }}>Intake {e.caloriesIn === null ? "—" : comma(e.caloriesIn)}</span>
              <span style={{ fontSize: "0.65rem", color: e.netCalories === null ? "rgba(255,255,255,0.6)" : e.netCalories < 0 ? "#86efac" : "#fca5a5", whiteSpace: "nowrap" }}>Net {e.netCalories === null ? "—" : comma(e.netCalories)}</span>
              <span style={{ display: "inline-flex", alignItems: "center", gap: "0.18rem", fontSize: "0.65rem", color: "rgba(255,255,255,0.8)", whiteSpace: "nowrap" }}>
                <span>Act {e.activeCalories === null ? "—" : comma(e.activeCalories)}</span>
                {activeIsOura ? <span style={{ fontSize: "0.54rem", padding: "0.03rem 0.22rem", borderRadius: 999, border: "1px solid rgba(59,130,246,0.35)", color: "#bfdbfe", background: "rgba(59,130,246,0.10)", textTransform: "uppercase", letterSpacing: "0.03em", lineHeight: 1.2 }}>Oura</span> : null}
              </span>
              <button
                type="button"
                onClick={() => setExpandedAllDataRowDate((prev) => prev === e.date ? null : e.date)}
                style={{ ...btnBase, minWidth: 30, minHeight: 30, padding: 0, marginRight: "-0.08rem", justifyContent: "center", background: expanded ? "rgba(255,255,255,0.10)" : "transparent", border: expanded ? "1px solid rgba(255,255,255,0.14)" : "1px solid transparent", color: "rgba(255,255,255,0.72)" }}
                aria-label={expanded ? `Collapse ${e.date} logbook details` : `Expand ${e.date} logbook details`}
                aria-expanded={expanded}
              >
                <ChevronIcon direction={expanded ? "down" : "right"} size={15} />
              </button>
            </div>
            {expanded ? (
              <div style={{ borderTop: "1px solid rgba(255,255,255,0.08)", padding: "0.38rem 0.5rem 0.48rem" }}>
                {renderSprintLogExpandedDetails(e, "mobile")}
              </div>
            ) : null}
          </div>
        );
      }) : <div style={{ color: "rgba(255,255,255,0.55)", fontSize: "0.8rem" }}>No entries.</div>}

      {entriesDesc.length > 3 ? (
        <div style={{ marginTop: "0.1rem", display: "flex", justifyContent: "center" }}>
          <button
            type="button"
            onClick={() => {
              if (hasMoreAllDataEntriesMobile) {
                setMobileAllDataVisibleCount((prev) => Math.min(prev + 3, entriesDesc.length));
              } else {
                setMobileAllDataVisibleCount(3);
                setExpandedAllDataRowDate(null);
              }
            }}
            disabled={!hasMoreAllDataEntriesMobile && mobileAllDataVisibleCount <= 3}
            style={{ ...btnBase, padding: "0.3rem 0.68rem", fontSize: "0.74rem", background: "rgba(255,255,255,0.04)", color: "rgba(255,255,255,0.9)", opacity: !hasMoreAllDataEntriesMobile && mobileAllDataVisibleCount <= 3 ? 0.7 : 1 }}
          >
            <span style={{ display: "inline-flex", alignItems: "center", gap: "0.3rem" }}>
              <span>{hasMoreAllDataEntriesMobile ? "Show next 3 entries" : "Collapse to 3"}</span>
              <ChevronIcon direction={hasMoreAllDataEntriesMobile ? "down" : "up"} size={14} />
            </span>
          </button>
        </div>
      ) : null}
    </div>
  );

  const allDataPage = (
    <div style={{ ...mainContentWidthStyle, display: "grid", gap: "1rem" }}>
      <div style={{ ...cardStyle, borderRadius: 18, ...(useCompactAllDataLayout ? { padding: "0.9rem 0.8rem" } : {}) }}>
        {logbookOpenedFromSettings ? (
          <div style={{ marginBottom: "0.45rem" }}>
            <button
              style={{
                border: "none",
                background: "transparent",
                color: "rgba(191,219,254,0.9)",
                fontSize: "0.86rem",
                fontWeight: 700,
                padding: "0.18rem 0",
                cursor: "pointer",
              }}
              onClick={() => {
                setSettingsInitialSection("data");
                setCurrentPage("settings");
                setLogbookOpenedFromSettings(false);
              }}
            >
              ← Settings
            </button>
          </div>
        ) : null}

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: "1rem", flexWrap: "wrap" }}>
          <div>
            <div style={{ fontSize: "1.6rem", fontWeight: 900 }}>All Data</div>
            <div style={{ marginTop: "0.35rem", color: "rgba(255,255,255,0.65)" }}>All logged entries.</div>
          </div>
          <div style={{ display: "flex", gap: "0.6rem", alignItems: "center", flexWrap: "wrap" }}>
            <div style={{ position: "relative" }}>
              <button
                style={{
                  ...btnBase,
                  background: "rgba(255,255,255,0.06)",
                  color: "white",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "0.5rem",
                }}
                title="Import a CSV with required columns: date,weight. Optional: caloriesIn,totalBurned,activeCalories,netCalories. Rows overwrite matching dates."
                onClick={openCsvImportModal}
                disabled={isDemoMode}
              >
                <UploadIcon size={15} />
                <span>Import CSV</span>
              </button>
            </div>

            <button
              style={{ ...btnBase, background: "rgba(255,255,255,0.06)", color: "white", opacity: isDemoMode ? 0.6 : 1, cursor: isDemoMode ? "not-allowed" : "pointer", display: "inline-flex", alignItems: "center", gap: "0.5rem" }}
              onClick={() => {
                if (isDemoMode) {
                  setSettingsDataStatusMsg("Disabled in Demo");
                  return;
                }
                downloadTextFile(`weight-tracker-all-data-${todayYmd}.csv`, entriesToCsv(entriesAsc));
              }}
              disabled={isDemoMode}
            >
              <DownloadIcon size={15} />
              <span>Export CSV</span>
            </button>
          </div>
        </div>

        {useCompactAllDataLayout ? (
          <div style={{ marginTop: "1rem", display: "grid", gap: "0.7rem" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "0.45rem" }}>
              <div style={{ fontSize: "0.72rem", color: "rgba(255,255,255,0.6)" }}>{Math.min(mobileAllDataVisibleCount, entriesDesc.length)} / {entriesDesc.length}</div>
              {mobileAllDataVisibleCount < entriesDesc.length ? (
                <button
                  type="button"
                  onClick={() => setMobileAllDataVisibleCount(entriesDesc.length)}
                  style={{ ...btnBase, padding: "0.24rem 0.52rem", fontSize: "0.7rem", background: "rgba(255,255,255,0.04)", color: "rgba(255,255,255,0.9)" }}
                >
                  Show all {entriesDesc.length}
                </button>
              ) : null}
            </div>
            {allDataMobileList}
          </div>
        ) : (
          <div style={{ marginTop: "1rem", overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
          <thead>
            <tr>
              <th style={tableHeaderStyle}>Date</th>
              <th style={tableHeaderStyle}>Weight</th>
              <th style={tableHeaderStyle}>Cal In</th>
              <th style={tableHeaderStyle}>Active</th>
              <th style={tableHeaderStyle}>TDEE</th>
              <th style={tableHeaderStyle}>Net Cal</th>
              <th style={tableHeaderStyle}>BMR</th>
              <th style={{ ...tableHeaderStyle, textAlign: "right", width: 120 }} />
            </tr>
          </thead>
          <tbody>
            {entriesDesc.length ? (
              entriesDesc.map((e) => (
                <tr key={e.date}>
                  <td style={tableCellStyle}>{e.date}</td>
                  <td style={tableCellStyle}>{e.weight?.toFixed(1)}</td>
                  <td style={tableCellStyle}>{e.caloriesIn === null ? "—" : comma(e.caloriesIn)}</td>
                  <td style={tableCellStyle}>{renderSourcedNumber(e.activeCalories, e.activeCaloriesSource)}</td>
                  <td style={tableCellStyle}>{renderSourcedNumber(e.totalBurned, e.totalBurnedSource)}</td>
                  <td style={{ ...tableCellStyle, color: e.netCalories === null ? "rgba(255,255,255,0.55)" : e.netCalories < 0 ? COLORS.green : COLORS.red }}>{e.netCalories === null ? "—" : comma(e.netCalories)}</td>
                  <td style={tableCellStyle}>{e.bmr === null ? "—" : comma(e.bmr)}</td>
                  <td style={{ ...tableCellStyle, textAlign: "right" }}>
                    <button
                      style={{
                        ...btnBase,
                        background: "rgba(239,68,68,0.12)",
                        borderColor: "rgba(239,68,68,0.30)",
                        color: COLORS.red,
                        padding: "0.5rem 0.85rem",
                      }}
                      onClick={() => deleteEntry(e.date)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td style={{ ...tableCellStyle, color: "rgba(255,255,255,0.55)" }} colSpan={8}>
                  No entries.
                </td>
              </tr>
            )}
          </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );

  const sprintsPage = (
    <div style={{ ...mainContentWidthStyle, display: "grid", gap: "1rem" }}>
      <div style={{
        ...cardStyle,
        borderRadius: 18,
        minWidth: 0,
        overflow: "hidden",
        width: "100%",
        ...(isCompactMobile ? { padding: "0.9rem" } : {}),
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "0.75rem", flexWrap: "wrap" }}>
        <div>
          <div style={{ fontSize: "1.6rem", fontWeight: 900 }}>Sprints</div>
          <div style={{ marginTop: "0.35rem", color: "rgba(255,255,255,0.65)" }}>Manage your sprints and track progress over time.</div>
        </div>
        <button
          style={{ ...btnBase, background: actionColors.primaryGradient, color: actionColors.primaryText, borderColor: "rgba(148,163,184,0.45)" }}
          onClick={() => {
            openNewSprintModal();
          }}
        >
          + New Sprint
        </button>
        </div>

        {(() => {
        const sprintWithStatus = sprints.map((s) => ({
          sprint: s,
          isActive: isTrulyActiveSprint(s),
        }));

        const sortByNewestStartDate = (a, b) => {
          if (a.startDate === b.startDate) {
            if (a.id === b.id) return 0;
            return a.id < b.id ? -1 : 1;
          }
          return a.startDate > b.startDate ? -1 : 1;
        };

        const activeSprints = sprintWithStatus
          .filter(({ isActive }) => isActive)
          .map(({ sprint }) => sprint)
          .sort(sortByNewestStartDate);

        const plannedSprints = sprintWithStatus
          .filter(({ isActive, sprint }) => !isActive && sprint.startDate > todayYmd)
          .map(({ sprint }) => sprint)
          .sort(sortByNewestStartDate);

        const historicalSprints = sprintWithStatus
          .filter(({ isActive, sprint }) => !isActive && sprint.startDate <= todayYmd)
          .map(({ sprint }) => sprint)
          .sort(sortByNewestStartDate);

        const renderSection = (title, sectionSprints) => (
          <div style={{ display: "grid", gap: "0.8rem" }}>
            <div style={{ fontSize: "0.84rem", letterSpacing: "0.01em", textTransform: "none", color: "rgba(255,255,255,0.74)", fontWeight: 800 }}>
              {title}
            </div>
            {sectionSprints.length ? sectionSprints.map((s) => {
            const isActive = isTrulyActiveSprint(s);
            const sprintActionButtonPadding = isCompactMobile ? "0.34rem 0.56rem" : "0.4rem 0.7rem";
            return (
              <div
                key={s.id}
                style={{
                  padding: "1rem 1rem",
                  borderRadius: 16,
                  background: "rgba(255,255,255,0.04)",
                  border: "1px solid rgba(255,255,255,0.10)",
                  display: "flex",
                  justifyContent: "space-between",
                  gap: "1rem",
                  flexWrap: "wrap",
                  alignItems: "center",
                  minWidth: 0,
            minInlineSize: 0,
                  overflow: "hidden",
                }}
              >
                <div style={{ display: "grid", gap: "0.55rem", minWidth: isCompactMobile ? 0 : 320, flex: "1 1 auto" }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: isCompactMobile && isActive ? "space-between" : "flex-start", gap: "0.5rem", flexWrap: "wrap", width: "100%" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", flexWrap: "wrap", minWidth: 0, flex: "1 1 auto" }}>
                      <button
                        type="button"
                        onClick={() => openSprintViewer(s.id)}
                        style={{
                          background: "transparent",
                          border: "none",
                          padding: 0,
                          color: "white",
                          fontWeight: 900,
                          fontSize: "1.05rem",
                          cursor: "pointer",
                          textDecoration: "none",
                          transition: "opacity 120ms ease, transform 120ms ease",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.opacity = "0.82";
                          e.currentTarget.style.transform = "translateX(1px)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.opacity = "1";
                          e.currentTarget.style.transform = "translateX(0px)";
                        }}
                        title={isActive ? "Open Dashboard" : "Open Sprint Viewer"}
                      >
                        {s.name}
                      </button>
                      <span
                        title="Sprint color"
                        style={{
                          width: 14,
                          height: 14,
                          borderRadius: 999,
                          background: getSprintColor(s),
                          border: "1px solid rgba(255,255,255,0.6)",
                          boxShadow: `0 0 0 4px ${hexToRgba(getSprintColor(s), 0.22)}`,
                          display: "inline-block",
                        }}
                      />
                    </div>
                    {isCompactMobile && isActive ? <Pill label="Active" tone="blue" /> : null}
                  </div>
                  <div style={{ color: "rgba(255,255,255,0.65)", fontSize: "0.85rem" }}>
                    {formatMDY(s.startDate)} → {isActive ? "(active)" : formatMDY(getSprintDisplayEndDate(s))}
                  </div>
                </div>

                <div style={{ display: "flex", gap: isCompactMobile ? "0.35rem" : "0.5rem", alignItems: "center", flexWrap: "nowrap", overflow: "hidden", minWidth: 0, maxWidth: "100%", width: isCompactMobile ? "100%" : "auto", transform: isCompactMobile ? "scale(0.95)" : "none", transformOrigin: "left center" }}>
                  {!isCompactMobile && isActive ? <Pill label="Active" tone="blue" /> : null}
                  {!isActive && s.startDate > todayYmd ? <Pill label="Planned" tone="muted" /> : null}

                  <button
                    style={{ ...btnBase, background: actionColors.primaryGradient, color: actionColors.primaryText, borderColor: "rgba(148,163,184,0.45)", padding: sprintActionButtonPadding, flexShrink: 0 }}
                    onClick={() => openSprintViewer(s.id)}
                  >
                    View
                  </button>

                  {!isActive ? (
                    <button style={{ ...btnBase, background: "transparent", borderColor: "transparent", color: "rgba(255,255,255,0.7)", padding: sprintActionButtonPadding, flexShrink: 0, boxShadow: "none" }} onClick={() => { startEditSprint(s); setActiveModal("editSprint"); }}>
                      Edit
                    </button>
                  ) : null}

                  {isActive ? (
                    <button
                      style={{ ...btnBase, background: "rgba(255,255,255,0.05)", borderColor: "rgba(255,255,255,0.16)", color: "rgba(255,255,255,0.86)", padding: sprintActionButtonPadding, flexShrink: 0 }}
                      onClick={() => openArchiveConfirm(s.id)}
                    >
                      Archive
                    </button>
                  ) : null}

                  <button
                    style={{
                      ...btnBase,
                      background: "rgba(239,68,68,0.12)",
                      borderColor: "rgba(239,68,68,0.30)",
                      color: COLORS.red,
                      padding: sprintActionButtonPadding,
                      flexShrink: 0,
                    }}
                    onClick={() => openSprintDeleteConfirm(s.id)}
                  >
                    Delete
                  </button>
                </div>
              </div>
            );
            }) : <div style={{ color: "rgba(255,255,255,0.5)", fontSize: "0.9rem" }}>No {title.toLowerCase()} yet.</div>}
          </div>
        );

          return (
            <div style={{ marginTop: "1.25rem", display: "grid", gap: "1rem" }}>
              {renderSection("Active sprint", activeSprints)}
              {plannedSprints.length > 0 && renderSection("Planned", plannedSprints)}
              {renderSection("Past sprints", historicalSprints)}
            </div>
          );
        })()}
      </div>
    </div>
  );

  const viewerPage = isMobile ? (
    /* Mobile viewer: compact cards, no wide table */
    <div style={{ ...mainContentWidthStyle, display: "grid", gap: "1rem", minWidth: 0 }}>
      {/* Compact header */}
      <div style={{ ...cardStyle, borderRadius: 18, padding: "0.85rem 1rem" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", flexWrap: "wrap" }}>
          <button
            type="button"
            onClick={() => { setCurrentPage("sprints"); setViewerSprintId(null); }}
            style={{ ...btnBase, display: "inline-flex", alignItems: "center", gap: "0.3rem", background: "rgba(255,255,255,0.06)", color: "rgba(255,255,255,0.78)", fontSize: "0.78rem", padding: "0.25rem 0.6rem", borderRadius: 999, flexShrink: 0 }}
          >
            ← Sprints
          </button>
          {selectedSprint ? (
            <div style={{ display: "inline-flex", alignItems: "center", gap: "0.4rem", minWidth: 0 }}>
              <span style={{ width: 8, height: 8, borderRadius: 999, background: getSprintColor(selectedSprint), border: "1px solid rgba(255,255,255,0.6)", display: "inline-block", flexShrink: 0 }} />
              <span style={{ fontSize: "0.95rem", fontWeight: 900, color: "white", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{selectedSprint.name}</span>
            </div>
          ) : null}
        </div>
        {selectedSprint ? (
          <div style={{ marginTop: "0.35rem", color: "rgba(255,255,255,0.6)", fontSize: "0.8rem" }}>
            {formatMDY(selectedSprint.startDate)} → {isTrulyActiveSprint(selectedSprint) ? "active" : formatMDY(getSprintDisplayEndDate(selectedSprint))}
          </div>
        ) : null}
      </div>

      {/* Stats grid */}
      {selectedSprint ? (
        <div style={{ ...cardStyle, borderRadius: 18, padding: "0.85rem" }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: "0.6rem", alignItems: "center", flexWrap: "wrap", marginBottom: "0.55rem" }}>
            <div style={{ fontSize: "0.9rem", fontWeight: 800 }}>Sprint Stats</div>
            <div style={{ fontSize: "0.76rem", fontWeight: 700, color: "rgba(255,255,255,0.68)" }}>{CHART_METRIC_META[viewerMetric].title}</div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "0.55rem" }}>
            {viewerSummaryChips}
          </div>
        </div>
      ) : null}

      {/* Chart */}
      <div style={{ minWidth: 0 }}>{trackerCharts}</div>
      {currentPage === "viewer" ? null : trackerCaloriesMobileCard}

      {/* Entry list — mobile card replaces wide table */}
      {sprintHistoryMobileCard}
    </div>
  ) : (
    /* Desktop viewer */
    <div style={{ ...mainContentWidthStyle, display: "grid", gap: "1.5rem", minWidth: 0, overflowX: "hidden" }}>
      <div style={{ ...cardStyle, borderRadius: 18 }}>
        <button
          type="button"
          onClick={() => { setCurrentPage("sprints"); setViewerSprintId(null); }}
          style={{ ...btnBase, display: "inline-flex", alignItems: "center", gap: "0.35rem", background: "rgba(255,255,255,0.06)", color: "rgba(255,255,255,0.78)", fontSize: "0.82rem", padding: "0.3rem 0.7rem", borderRadius: 999, marginBottom: "0.75rem" }}
        >
          ← Sprints
        </button>
        <div style={{ fontSize: "1.2rem", fontWeight: 900 }}>Sprint Viewer</div>
        {selectedSprint ? (
          <div style={{ marginTop: "0.45rem", display: "grid", gap: "0.25rem" }}>
            <div style={{ display: "inline-flex", alignItems: "center", gap: "0.55rem", width: "fit-content", padding: "0.26rem 0.56rem", borderRadius: 10, border: "1px solid rgba(255,255,255,0.2)", background: "rgba(255,255,255,0.06)" }}>
              <span style={{ width: 10, height: 10, borderRadius: 999, background: getSprintColor(selectedSprint), border: "1px solid rgba(255,255,255,0.6)", display: "inline-block" }} />
              <span style={{ fontSize: "1.08rem", fontWeight: 900, color: "white", lineHeight: 1.1 }}>{selectedSprint.name}</span>
            </div>
            <div style={{ color: "rgba(255,255,255,0.7)", fontSize: "0.95rem" }}>
              {formatMDY(selectedSprint.startDate)} → {isTrulyActiveSprint(selectedSprint) ? "active" : formatMDY(getSprintDisplayEndDate(selectedSprint))}
            </div>
          </div>
        ) : (
          <div style={{ marginTop: "0.35rem", color: "rgba(255,255,255,0.65)" }}>Viewing sprint: —</div>
        )}
      </div>

      {trackerStats}

      <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: "1.5rem" }}>{trackerCharts}</div>

      {sprintTable}
    </div>
  );

  const activitySummary = getActivityOptionByLevel(vitals.activityLevel) ?? getActivityOptionByFactor(vitals.activityFactor);

  const [showLoginModal, setShowLoginModal] = useState(false);
  const [loginModalMode, setLoginModalMode] = useState<"signin" | "signup" | "updatePassword">("signin");
  const [showLanding, setShowLanding] = useState(() => {
    if (typeof window === "undefined") return true;
    if (isNativeIOS && !userId) return true;
    if (!userId) {
      const hasEntries = !!window.localStorage.getItem("weight-tracker-entries");
      const hasSprints = !!window.localStorage.getItem("weight-tracker-sprints");
      if (!hasEntries && !hasSprints) return true;
    }
    return window.localStorage.getItem(LANDING_DISMISSED_KEY) !== "true";
  });

  const resolveHealthKitEnabledFromCloud = useCallback((enabled: boolean, cloudProfileUpdatedAt: string | null | undefined) => {
    const localTs = healthKitLocalUpdatedAtRef.current;
    const cloudTs = cloudProfileUpdatedAt ? Date.parse(String(cloudProfileUpdatedAt)) : NaN;
    const hasLocalTs = Number.isFinite(localTs) && localTs > 0;
    const hasCloudTs = Number.isFinite(cloudTs);

    if (hasLocalTs && hasCloudTs && localTs > cloudTs) {
      console.info("[healthkit] startup resolution: keeping newer local toggle over stale cloud value", {
        localUpdatedAt: new Date(localTs).toISOString(),
        cloudUpdatedAt: new Date(cloudTs).toISOString(),
        cloudValue: enabled,
      });
      return;
    }

    setHealthKitEnabled(enabled);
    const nextTs = hasCloudTs ? new Date(cloudTs).toISOString() : new Date().toISOString();
    healthKitLocalUpdatedAtRef.current = Date.parse(nextTs);
    void storage.set(STORAGE_KEYS.healthKitEnabled, enabled ? "1" : "0");
    void storage.set(STORAGE_KEYS.healthKitEnabledUpdatedAt, nextTs);
    console.info("[healthkit] startup resolution: applied cloud value", { enabled, cloudUpdatedAt: cloudProfileUpdatedAt ?? null });
  }, [storage]);

  const { status: sbStatus, lastSyncAt: sbLastSyncAt, error: sbError, syncNow: sbSyncNow, signOut: sbSignOut, firstPullDone: sbFirstPullDone, overwriteWarning: sbOverwriteWarning, confirmOverwriteWithCloud: confirmSupabaseOverwriteWithCloud, cancelOverwriteAndKeepLocal: cancelSupabaseOverwriteAndKeepLocal, deleteCloudSprint: sbDeleteSprint, cloudProfileVitalsStatus } = useSupabaseSync({
    userId,
    initialDataLoaded,
    entries: entriesAsc,
    sprints,
    vitals,
    forecast,
    roadmap,
    roadmapUpdatedAt,
    setEntries,
    setSprints,
    setVitals: (v) => setVitals(v != null ? v : normalizeVitals({})),
    setForecast,
    setRoadmap,
    setRoadmapUpdatedAt,
    persistAll,
    isYmd,
    toYmdLoose,
    enabled: !isDemoMode,
    isDemoMode,
    deletedEntryTombstones,
    hasSynced,
    onSyncSuccess: async () => {
      if (!hasSynced) {
        setHasSynced(true);
        await storage.set(STORAGE_KEYS.hasSynced, "1");
      }
    },
    setHealthKitEnabled: resolveHealthKitEnabledFromCloud,
  });

  useEffect(() => {
    const previousUserId = lastAuthRoutingUserIdRef.current;
    const didSwitchAccounts = previousUserId !== null && userId !== null && previousUserId !== userId;
    const didSignOut = previousUserId !== null && userId === null;

    if (didSwitchAccounts || didSignOut) {
      setObSignedInWithAccount(false);
      setObShouldShowForAuthUser(false);
      setObPhase("bodyStats");
      setObCloudNote(null);
    }

    lastAuthRoutingUserIdRef.current = userId;
  }, [userId]);

  const handleSupabaseSyncNow = async () => {
    if (isDemoMode) {
      setSettingsDataStatusMsg("Disabled in Demo");
      return;
    }
    await sbSyncNow();
  };

  const handleSupabaseSignOut = async () => {
    if (isDemoMode) {
      setSettingsDataStatusMsg("Disabled in Demo");
      return;
    }

    try {
      await SprintWidget.clearWidgetData();
      if (import.meta.env.DEV) console.log('[SprintWidget] clearWidgetData OK during sign-out');
    } catch (err) {
      console.error('[SprintWidget] clearWidgetData FAILED during sign-out:', err);
    }

    await sbSignOut();
    // Logout UX: keep local snapshot, return to landing, and make local-only state explicit.
    setShowLanding(true);
    setCurrentPage("tracker");
    setSettingsDataStatusMsg("You’re now in local mode (not synced).");
  };

  const handleSaveDisplayNameAndAvatar = async (name: string, key: string) => {
    const next = normalizeVitals({ ...vitals, displayName: name.trim(), avatarKey: key });
    setVitals(next);
    await persistAll(entries, next, forecast, sprints);
  };

  // Oura callbacks
  const handleOuraSyncNow = async () => {
    if (isDemoMode || !userId) return;
    setOuraStatus("syncing");
    try {
      await syncOuraNow();
      const updated = await getOuraConnection();
      setOuraConnection(updated);
      await sbSyncNow(); // pull updated entries into local state
      setOuraStatus("idle");
    } catch {
      setOuraStatus("error");
    }
  };

  const handleOuraDisconnect = async () => {
    if (isDemoMode || !userId) return;
    await disconnectOura();
    setOuraConnection(null);
  };

  const handleOuraToggleAuthoritative = async (authoritative: boolean) => {
    if (isDemoMode || !userId) return;
    await updateOuraSettings({ authoritative });
    setOuraConnection((prev) => (prev ? { ...prev, authoritative } : prev));
  };

  const handleOuraToggleAutoSync = async (enabled: boolean) => {
    if (isDemoMode || !userId) return;
    await updateOuraSettings({ enabled });
    if (userId) getOuraConnection().then(setOuraConnection).catch(() => {});
  };

  // HealthKit callbacks
  const handleHealthKitToggle = async (enabled: boolean) => {
    setHealthKitEnabled(enabled);
    const toggleIso = new Date().toISOString();
    healthKitLocalUpdatedAtRef.current = Date.parse(toggleIso);
    await storage.set(STORAGE_KEYS.healthKitEnabled, enabled ? "1" : "0");
    await storage.set(STORAGE_KEYS.healthKitEnabledUpdatedAt, toggleIso);
    console.info("[healthkit] toggle persisted locally", { enabled, updatedAt: toggleIso });
    if (userId && !isDemoMode) {
      console.info("[healthkit] syncing toggle to cloud profile", { enabled, userId });
      await pushProfile(userId, vitals, forecast, roadmap ? [roadmap] : [], enabled);
      pendingHealthKitCloudSyncRef.current = false;
    } else {
      pendingHealthKitCloudSyncRef.current = true;
      console.info("[healthkit] user not ready for cloud sync; queued pending profile write");
    }
  };

  const handleHealthKitSyncNow = async () => {
    if (!healthKitEnabled || isDemoMode) return;
    const since = new Date();
    since.setDate(since.getDate() - 30);
    setHealthKitStatus("syncing");
    try {
      const [hkWeights, hkCalories] = await Promise.all([
        syncWeightsFromHealth(since),
        syncActiveCaloriesFromHealth(since),
      ]);
      setEntries((prev) => {
        let next = mergeHealthKitWeights(prev, hkWeights, activityBurnSource);
        next = mergeHealthKitActiveCalories(next, hkCalories, activityBurnSource);
        return reconcileEntriesDerivedMetrics(next, vitals);
      });
      setHealthKitLastSyncAt(new Date().toISOString());
      setHealthKitStatus("idle");
    } catch {
      setHealthKitStatus("error");
    }
  };

  useEffect(() => {
    if (!userId || isDemoMode) return;
    if (!pendingHealthKitCloudSyncRef.current) return;
    let cancelled = false;
    void (async () => {
      try {
        await pushProfile(userId, vitals, forecast, roadmap ? [roadmap] : [], healthKitEnabled);
        if (cancelled) return;
        pendingHealthKitCloudSyncRef.current = false;
        console.info("[healthkit] flushed pending cloud profile write after auth/session ready", { enabled: healthKitEnabled, userId });
      } catch (error) {
        console.warn("[healthkit] failed to flush pending cloud profile write", error);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userId, isDemoMode, healthKitEnabled, vitals, forecast, roadmap]);

  const handleActivityBurnSourceChange = async (val: ActivityBurnSource) => {
    setActivityBurnSource(val);
    // Keep the legacy Oura authoritative flag in sync for backward-compat
    const ouraAuthoritative = val === "oura" || val === "best";
    if (ouraConnection) {
      await handleOuraToggleAuthoritative(ouraAuthoritative);
    }
  };

  // After a sign-in during onboarding, wait for the cloud pull to finish,
  // then guide the user forward if their account was empty.
  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(LANDING_DISMISSED_KEY, showLanding ? "false" : "true");
  }, [showLanding]);

  // Returning user fast-path: if we already know who they are AND they have
  // a sprint or entries, the landing page is just a speed bump that ends in
  // a confusing flash to the dashboard (Build my plan -> data exists ->
  // showOnboarding=false -> dashboard renders). Skip the landing entirely.
  useEffect(() => {
    if (!showLanding) return;
    if (!sbFirstPullDone) return;
    if (!userId) return;
    if (entries.length === 0 && sprints.length === 0) return;
    setShowLanding(false);
  }, [showLanding, sbFirstPullDone, userId, entries.length, sprints.length]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [showLanding]);

  // Defensive reset for global body locks when the landing page is shown.
  // Chrome can keep body-style locks from prior overlays longer than expected,
  // which can make trackpad scroll appear broken on the marketing page.
  useEffect(() => {
    if (typeof document === "undefined") return;
    if (!showLanding || showLoginModal) return;
    document.body.style.overflow = "";
    document.body.style.position = "";
    document.body.style.width = "";
    document.body.style.top = "";
  }, [showLanding, showLoginModal]);

  useEffect(() => {
    if (passwordRecoveryActive) {
      setShowLanding(true);
      setLoginModalMode("updatePassword");
      setShowLoginModal(true);
      return;
    }
    if (!userId) return;
    setShowLoginModal(false);
    setShowLanding(false);
    setCurrentPage("tracker");
  }, [passwordRecoveryActive, userId]);

  useEffect(() => {
    if (!authSuccessNotice || typeof window === "undefined") return undefined;
    const timeoutId = window.setTimeout(() => {
      onClearAuthSuccessNotice?.();
    }, 2200);
    return () => window.clearTimeout(timeoutId);
  }, [authSuccessNotice, onClearAuthSuccessNotice]);

  // Auto-open the auth modal when we arrive from an email link.
  // redirectAuthMode is set by App.tsx after parsing verification or recovery callbacks.
  useEffect(() => {
    if (passwordRecoveryActive) return;
    if (!verifiedEmailNotice || !redirectAuthMode) return;
    if (userId) return; // Already signed in — no modal needed
    setLoginModalMode(redirectAuthMode);
    setShowLoginModal(true);
  }, [passwordRecoveryActive, verifiedEmailNotice, redirectAuthMode, userId]);

  const openSignIn = () => {
    setLoginModalMode("signin");
    setShowLoginModal(true);
  };

  const openSignUp = () => {
    setLoginModalMode("signup");
    setShowLoginModal(true);
  };

  const continueLocally = () => {
    // V2 onboarding: drop both web AND iOS into the anonymous-friendly flow.
    // Sign-up is asked AFTER the projection screen, not as a gate.
    //
    // If signed-out with leftover local data (e.g. user wiped cloud, then
    // signed out — handleSignOut intentionally keeps the local snapshot for
    // the "I have an account" recovery path), "Build my plan" means start
    // fresh. Reset local state so the showOnboarding gate (entries.length ===
    // 0 && sprints.length === 0) actually trips and onboarding renders
    // instead of falling through to the dashboard. On iOS this also closes a
    // back-door past the hard auth wall.
    if (!userId && (entries.length > 0 || sprints.length > 0 || roadmap)) {
      const freshVitals = normalizeVitals(DEFAULT_VITALS);
      setEntries([]);
      setSprints([]);
      setRoadmap(null);
      setVitals(freshVitals);
      setForecast(DEFAULT_FORECAST);
      void persistAll([], freshVitals, DEFAULT_FORECAST, []);
      void storage.delete(STORAGE_KEYS.roadmap);
      void storage.delete(STORAGE_KEYS.roadmapUpdatedAt);
      void storage.delete(STORAGE_KEYS.deletedEntries);
      void clearOnboardingHandoff();
    }
    setObPhase("bodyStats");
    setShowLanding(false);
  };

  const tryDemoFromLanding = () => {
    setShowLoginModal(false);
    setObSignedInWithAccount(false);
    setObShouldShowForAuthUser(false);
    setObPhase("bodyStats");
    setObCloudNote(null);
    enterDemoMode("cut_ahead", "settings");
    setShowLanding(false);
  };

  // Onboarding handoff: hydrate the plan a signed-out user built right before
  // signing in, when the cloud account is empty. clearAnonymousLocalState()
  // wipes the standard storage keys on SIGNED_IN, so the plan is stashed under
  // a separate key by completeOnboardingFromV2 and pulled back here. The
  // existing debounced push in useSupabaseSync picks up the new state and
  // sends it to Supabase — no explicit push needed.
  useEffect(() => {
    if (!sbFirstPullDone || !userId || isDemoMode) return;
    let cancelled = false;
    void (async () => {
      const stash = await readOnboardingHandoff();
      if (cancelled) return;
      if (!stash) return;

      const cloudHasData =
        cloudProfileVitalsStatus === "present" || entries.length > 0 || sprints.length > 0;
      if (cloudHasData) {
        // Returning user with real data — cloud wins, drop the stash.
        await clearOnboardingHandoff();
        return;
      }
      if (cloudProfileVitalsStatus !== "absent") return;

      const stale = Date.now() - stash.createdAt > ONBOARDING_HANDOFF_TTL_MS;
      const emailMismatch =
        stash.authMethod === "magic" &&
        (!stash.email ||
          !userEmail ||
          stash.email.trim().toLowerCase() !== userEmail.trim().toLowerCase());
      if (stale || emailMismatch) {
        await clearOnboardingHandoff();
        return;
      }

      setEntries(stash.entries);
      setVitals(stash.vitals);
      setSprints(stash.sprints);
      setForecast(stash.forecast);
      await persistAll(stash.entries, stash.vitals, stash.forecast, stash.sprints);
      await clearOnboardingHandoff();
    })();
    return () => {
      cancelled = true;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sbFirstPullDone, userId, userEmail, cloudProfileVitalsStatus, isDemoMode, entries.length, sprints.length]);

  useEffect(() => {
    if (!sbFirstPullDone || isDemoMode) return;

    const hasTrackerData = entries.length > 0 || sprints.length > 0;
    if (hasTrackerData) {
      if (obShouldShowForAuthUser) setObShouldShowForAuthUser(false);
      if (cloudProfileVitalsStatus === "present") {
        if (obSignedInWithAccount) setObSignedInWithAccount(false);
        if (obCloudNote) setObCloudNote(null);
      }
      return;
    }

    if (cloudProfileVitalsStatus === "present") {
      if (obSignedInWithAccount) setObSignedInWithAccount(false);
      if (obShouldShowForAuthUser) setObShouldShowForAuthUser(false);
      if (obCloudNote) setObCloudNote(null);
      return;
    }

    if (cloudProfileVitalsStatus !== "absent") return;

    if (obSignedInWithAccount) {
      // Pull done, profile has no vitals, and the account has no entries/sprints — treat as genuinely new.
      setObCloudNote(
        sbError
          ? `Couldn't reach your account (${sbError}). Let's set you up — your data will sync when reconnected.`
          : "Your account has no saved data yet — let's get you set up."
      );
      setObPhase("bodyStats");
    } else if (userId) {
      // Email-confirmed path: SIGNED_IN fired but obSignedInWithAccount was never set.
      // Only route into onboarding when the cloud profile also lacks vitals, so returning
      // users whose entries/sprints are still syncing are not treated as new accounts.
      setObShouldShowForAuthUser(true);
      setObCloudNote(authSuccessNotice ?? "Account verified! Let's set up your plan.");
      setObPhase("bodyStats");
    }
    // If data was found, entries/sprints are now non-empty → showOnboarding becomes false automatically
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sbFirstPullDone, userId, obSignedInWithAccount, obShouldShowForAuthUser, cloudProfileVitalsStatus, isDemoMode, entries.length, sprints.length, sbError, authSuccessNotice, obCloudNote]);

  const settingsPage = (
    <SettingsPage
      vitals={vitals}
      activitySummary={activitySummary}
      settingsDataStatusMsg={settingsDataStatusMsg}
      todayYmd={todayYmd}
      entriesAsc={entriesAsc}
      openVitalsEditor={openVitalsEditor}
      openCsvImportModal={openCsvImportModal}
      downloadTextFile={downloadTextFile}
      entriesToCsv={entriesToCsv}
      exportProfileJson={exportProfileJson}
      importProfileFromSettings={importProfileFromSettings}
      resetAll={resetAll}
      fmtHeightFtIn={fmtHeightFtIn}
      calcAge={calcAge}
      supabaseUserId={userId}
      supabaseUserEmail={userEmail}
      supabaseStatus={isDemoMode ? "disabled" : sbStatus}
      supabaseLastSyncAt={sbLastSyncAt}
      supabaseError={sbError}
      onSupabaseSyncNow={handleSupabaseSyncNow}
      onSupabaseSignOut={handleSupabaseSignOut}
      onSupabaseSignIn={openSignIn}
      isDemoMode={isDemoMode}
      demoProfileId={demoProfileId}
      onStartDemo={(profileId) => enterDemoMode(profileId, "settings")}
      displayName={vitals.displayName ?? ""}
      avatarKey={vitals.avatarKey ?? ""}
      onSaveDisplayNameAndAvatar={handleSaveDisplayNameAndAvatar}
      ouraConnection={ouraConnection}
      ouraStatus={ouraStatus}
      ouraRedirectError={ouraRedirectError}
      onOuraConnect={async () => { setOuraRedirectError(null); await startOuraConnect(); }}
      onOuraDisconnect={handleOuraDisconnect}
      onOuraSyncNow={handleOuraSyncNow}
      onOuraToggleAuthoritative={handleOuraToggleAuthoritative}
      onOuraToggleAutoSync={handleOuraToggleAutoSync}
      activityBurnSource={activityBurnSource}
      onActivityBurnSourceChange={handleActivityBurnSourceChange}
      healthKitEnabled={healthKitEnabled}
      onHealthKitToggle={handleHealthKitToggle}
      healthKitStatus={healthKitStatus}
      healthKitLastSyncAt={healthKitLastSyncAt}
      onHealthKitSyncNow={handleHealthKitSyncNow}
      initialSection={settingsInitialSection}
      activeSection={settingsActiveSection}
      onActiveSectionChange={setSettingsActiveSection}
      onNavigateToLogbook={() => {
        setSettingsInitialSection("data");
        setSettingsActiveSection("data");
        setLogbookOpenedFromSettings(true);
        setCurrentPage("all");
      }}
    />
  );


  // ---------------------------
  // Cloud sync interstitial
  // Shown when a user has a Supabase session but no local data yet, while the initial pull runs.
  // Defined here (after useSupabaseSync) to access sbFirstPullDone.
  // ---------------------------
  const showCloudSyncing = !!userId && !hadLocalDataOnBootRef.current && !sbFirstPullDone;

  const cloudSyncInterstitial = showCloudSyncing ? (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 20001, // above onboarding overlay
        background: `linear-gradient(180deg, ${COLORS.bg1}, ${COLORS.bg2})`,
        color: "white",
        fontFamily: 'Inter, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Arial',
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: "1.25rem",
      }}
    >
      <div style={{ display: "grid", justifyItems: "center", gap: "0.6rem" }}>
        <img src="/Logo-gt.png" alt="WeightPlanner Pro" style={{ ...brandLogoStyle, width: 56, height: 56, borderRadius: 14, objectFit: "contain", border: "none" }} />
        <div style={{ textAlign: "center", fontWeight: 900, fontSize: "1.25rem", opacity: 0.85 }}>
          {APP_NAME}
        </div>
      </div>
      {/* Simple CSS spinner */}
      <div style={{
        width: 36,
        height: 36,
        borderRadius: "50%",
        border: `3px solid rgba(255,255,255,0.15)`,
        borderTopColor: COLORS.blue,
        animation: "wtp-spin 0.9s linear infinite",
      }} />
      <style>{`@keyframes wtp-spin { to { transform: rotate(360deg); } }`}</style>
      <div style={{ color: "rgba(255,255,255,0.72)", fontSize: "0.95rem" }}>
        Loading your account data…
      </div>
      {userEmail && (
        <div style={{ color: "rgba(148,163,184,0.6)", fontSize: "0.82rem" }}>{userEmail}</div>
      )}
    </div>
  ) : null;

  // ---------------------------
  // Roadmap page
  // ---------------------------

  const roadmapPage = (() => {
    const isRoadmapMobile = isDashboardMobile;
    const mobileRoadmapFrame = isRoadmapMobile ? { width: "100%", maxWidth: "100%", minWidth: 0, boxSizing: "border-box" as const } : undefined;
    // Phase 1 "currently active" gate: only allow sprint sync when phase 1 is in progress
    const phase1 = roadmap?.phases[0];
    const phase1IsCurrentlyActive = !!(
      phase1 && phase1.startDate <= todayYmd && phase1.endDate >= todayYmd
    );

    // Derive TDEE from vitals + current weight for calorie computations
    const currentWeightForBMR = entriesAsc[entriesAsc.length - 1]?.weight ?? 175;
    const bmrForRoadmap = calcBMR({ weightLbs: currentWeightForBMR, heightIn: vitals.height, gender: vitals.gender, birthday: vitals.birthday });
    const tdeeForRoadmap = bmrForRoadmap !== null ? calcTDEEFromFactor({ bmr: bmrForRoadmap, factor: vitals.activityFactor ?? 1.3 }) : null;

    // Build chart data for Recharts — plan lines per phase + actual weight overlay
    const phaseChartSeries = roadmap
      ? roadmap.phases.reduce<{ key: RoadmapPhaseType; color: string; label: string }[]>((acc, phase) => {
          if (acc.some((item) => item.key === phase.type)) return acc;
          acc.push({
            key: phase.type,
            color: ROADMAP_PHASE_COLORS[phase.type],
            label: ROADMAP_PHASE_LABELS[phase.type],
          });
          return acc;
        }, [])
      : [];

    // Roadmap date range
    const roadmapStartDate = roadmap?.phases[0]?.startDate ?? todayYmd;
    const roadmapEndDate = roadmap?.phases[roadmap.phases.length - 1]?.endDate ?? todayYmd;

    // Actual weight entries within roadmap range (+ a bit before for context)
    const actualEntriesForChart = entriesAsc
      .filter((e) => e.weight !== null && e.date >= roadmapStartDate && e.date <= roadmapEndDate);

    // Live phases: push-propagate during drag so chart and summary update in real time
    const liveRoadmapPhases: RoadmapPhase[] = (() => {
      if (!roadmap || !dragState) return roadmap?.phases ?? [];
      const idx = roadmap.phases.findIndex((p) => p.id === dragState.phaseId);
      if (idx < 0) return roadmap.phases;
      const newEndDate = tsToYmd(dragState.currentEndDateTs);
      const oldEndTs = safeParseYmd(roadmap.phases[idx].endDate, "0001-01-01", "9999-12-31")?.getTime() ?? 0;
      const deltaDayMs = dragState.currentEndDateTs - oldEndTs;
      const updated = roadmap.phases.map((ph, i) =>
        i === idx ? { ...ph, endDate: newEndDate, endWeight: dragState.currentEndWeight } : ph
      );
      if (deltaDayMs !== 0 && idx < updated.length - 1) {
        return pushPhasesAfterIndex(updated, idx, deltaDayMs);
      }
      return propagatePhases(updated, idx);
    })();

    // Build time-proportional chart data with interpolated plan lines
    // Only use phase boundary dates + actual entry dates; plan lines are interpolated for each date
    const allChartDates = roadmap
      ? Array.from(new Set([
          ...liveRoadmapPhases.flatMap((p) => [p.startDate, p.endDate]),
          ...actualEntriesForChart.map((e) => e.date),
        ])).sort()
      : [];

    const actualByDate = new Map(actualEntriesForChart.map((e) => [e.date, e.weight as number]));

    // Helper: linearly interpolate plan weight for a date within a phase
    const interpolatePlan = (phase: RoadmapPhase, date: string): number | null => {
      if (date < phase.startDate || date > phase.endDate) return null;
      const startDt = safeParseYmd(phase.startDate, "0001-01-01", "9999-12-31");
      const endDt = safeParseYmd(phase.endDate, "0001-01-01", "9999-12-31");
      const dateDt = safeParseYmd(date, "0001-01-01", "9999-12-31");
      if (!startDt || !endDt || !dateDt) return null;
      const totalMs = endDt.getTime() - startDt.getTime();
      const pct = totalMs > 0 ? (dateDt.getTime() - startDt.getTime()) / totalMs : 0;
      return Math.round((phase.startWeight + pct * (phase.endWeight - phase.startWeight)) * 10) / 10;
    };

    // Build chart rows: each date gets a timestamp (for time-axis), interpolated plan values, and actual weight
    const mergedChartData = allChartDates.map((date) => {
      const dtObj = safeParseYmd(date, "0001-01-01", "9999-12-31");
      const row: Record<string, string | number | null> = { date, ts: dtObj ? dtObj.getTime() : 0 };
      // Plan lines: interpolated within each live phase's range, null outside
      liveRoadmapPhases.forEach((phase, i) => {
        row[`phase_${i}`] = interpolatePlan(phase, date);
      });
      // Actual weight
      row.actual = actualByDate.get(date) ?? null;
      return row;
    });

    // Monthly tick timestamps for uniform x-axis spacing
    const roadmapStartTs = safeParseYmd(roadmapStartDate, "0001-01-01", "9999-12-31")?.getTime() ?? 0;
    const roadmapEndTs = safeParseYmd(roadmapEndDate, "0001-01-01", "9999-12-31")?.getTime() ?? 0;
    const roadmapTodayTs = safeParseYmd(todayYmd, "0001-01-01", "9999-12-31")?.getTime() ?? null;
    const todayInRoadmapDomain = roadmapTodayTs !== null && roadmapTodayTs >= roadmapStartTs && roadmapTodayTs <= roadmapEndTs;

    const formatRoadmapPace = (rateLbsPerWeek: number): string => {
      if (Math.abs(rateLbsPerWeek) < 0.05) return "0.0";
      if (Number.isInteger(rateLbsPerWeek * 2)) return rateLbsPerWeek.toFixed(1);
      return rateLbsPerWeek.toFixed(2);
    };

    // Edit-mode x-domain: tight fit (14 day pad) + auto-extend + pan/extend offsets
    const _editDayMs = 86400000;
    // Use live end date (accounts for pushed phases during drag)
    const _liveEndTs = (() => {
      const last = liveRoadmapPhases[liveRoadmapPhases.length - 1];
      return last ? (safeParseYmd(last.endDate, "0001-01-01", "9999-12-31")?.getTime() ?? roadmapEndTs) : roadmapEndTs;
    })();
    const _baseEditStart = roadmapStartTs - 7 * _editDayMs;
    const _baseEditEnd = _liveEndTs + 14 * _editDayMs + chartXExtendMs;
    const _draggedTs = dragState?.currentEndDateTs ?? 0;
    // Auto-extend right when dragging near the right viewport edge
    const _autoExtendEnd = _draggedTs > _baseEditEnd - chartPanOffsetMs - 14 * _editDayMs
      ? Math.max(_baseEditEnd, _draggedTs + 30 * _editDayMs)
      : _baseEditEnd;
    // Pan shifts both edges by the same amount
    const editXDomainStart = _baseEditStart + chartPanOffsetMs;
    const editXDomainEnd = _autoExtendEnd + chartPanOffsetMs;

    // Monthly ticks: span full edit viewport (pan-aware)
    const monthlyTickTs: number[] = [];
    const _tickStart = chartEditMode ? editXDomainStart : roadmapStartTs;
    const _tickEnd = chartEditMode ? editXDomainEnd : roadmapEndTs;
    if (_tickEnd) {
      const startDtObj = new Date(chartEditMode ? editXDomainStart : roadmapStartTs);
      const cur = new Date(startDtObj.getFullYear(), startDtObj.getMonth(), 1);
      while (cur.getTime() <= _tickEnd) {
        if (cur.getTime() >= _tickStart) monthlyTickTs.push(cur.getTime());
        cur.setMonth(cur.getMonth() + 1);
      }
    }

    // Dynamic tick density: fit as many ticks as possible without crowding
    // Mobile: ~9 max (~310px plot, ~34px/label footprint); Desktop: ~18 max (~800px+ plot)
    const _roadmapMaxTicks = isRoadmapMobile ? 9 : 18;
    const roadmapViewerTickStride = Math.max(1, Math.ceil(monthlyTickTs.length / _roadmapMaxTicks));
    const roadmapViewerTicks = monthlyTickTs.filter((_, i) => i % roadmapViewerTickStride === 0);
    // Smart format: single-year plan → month number only ("3","4"); multi-year → "M/YY" ("3/26","4/27")
    const _roadmapTickYears = new Set(monthlyTickTs.map((ts) => new Date(ts).getFullYear()));
    const roadmapXAxisFormatter = (ts: number) => {
      const d = new Date(ts);
      if (_roadmapTickYears.size <= 1) return `${d.getMonth() + 1}`;
      return `${d.getMonth() + 1}/${String(d.getFullYear()).slice(2)}`;
    };

    // Expanded y-domain for edit-mode overlay — generous padding so drags aren't clipped
    const allPhaseWeights = roadmap ? roadmap.phases.flatMap((p) => [p.startWeight, p.endWeight]) : [];
    const minPhaseWeight = allPhaseWeights.length > 0 ? Math.min(...allPhaseWeights) : 150;
    const maxPhaseWeight = allPhaseWeights.length > 0 ? Math.max(...allPhaseWeights) : 200;
    const currentWeightForDomain = (entriesAsc[entriesAsc.length - 1]?.weight ?? ((minPhaseWeight + maxPhaseWeight) / 2)) as number;
    const editWeightPad = Math.max(10, Math.round(0.06 * currentWeightForDomain));
    const editYDomainMin = Math.floor(minPhaseWeight - editWeightPad);
    const editYDomainMax = Math.ceil(maxPhaseWeight + editWeightPad);

    const inputStyle = {
      width: "100%",
      padding: "0.55rem 0.7rem",
      borderRadius: 10,
      border: "1px solid rgba(255,255,255,0.14)",
      background: "rgba(255,255,255,0.05)",
      color: "white",
      outline: "none",
      fontSize: "0.9rem",
    };
    const lockedInputStyle = { ...inputStyle, background: "rgba(255,255,255,0.02)", color: "rgba(255,255,255,0.5)", cursor: "default" };
    const readOnlyInputStyle = { ...lockedInputStyle, pointerEvents: "none" as const };
    const labelStyle = { fontSize: "0.75rem", fontWeight: 700, color: "rgba(255,255,255,0.6)", textTransform: "uppercase" as const, letterSpacing: "0.04em", marginBottom: "0.35rem" };
    const roadmapGhostEditButtonStyle = {
      ...btnBase,
      padding: "0.25rem 0.55rem",
      minHeight: 24,
      background: "transparent",
      borderColor: "rgba(255,255,255,0.16)",
      color: "rgba(255,255,255,0.62)",
      fontSize: "0.74rem",
      fontWeight: 700,
      opacity: 0.82,
      transition: "opacity 120ms ease, border-color 120ms ease, color 120ms ease",
    };

    const viewerPhasePills = (
      <Customized
        component={(props: Record<string, unknown>) => {
          const xAxisMap = props.xAxisMap as Record<string, { scale: (v: number) => number }> | undefined;
          const yAxisMap = props.yAxisMap as Record<string, { scale: (v: number) => number }> | undefined;
          const offset = props.offset as { left?: number; top?: number; width?: number; height?: number } | undefined;
          if (!xAxisMap || !yAxisMap) return null;
          const xScale = Object.values(xAxisMap)[0]?.scale;
          const yScale = Object.values(yAxisMap)[0]?.scale;
          if (!xScale || !yScale) return null;
          const plotLeft = offset?.left ?? 0;
          const plotTop = offset?.top ?? 0;
          const plotWidth = offset?.width ?? 600;
          const plotHeight = offset?.height ?? 260;

          return (
            <g>
              {roadmap.phases.map((ph, phaseIndex) => {
                const startTs = safeParseYmd(ph.startDate, "0001-01-01", "9999-12-31")?.getTime();
                const endTs = safeParseYmd(ph.endDate, "0001-01-01", "9999-12-31")?.getTime();
                if (!startTs || !endTs) return null;
                const midTs = startTs + (endTs - startTs) / 2;
                const midWeight = ph.startWeight + (ph.endWeight - ph.startWeight) / 2;
                const rawX = xScale(midTs);
                const rawY = yScale(midWeight);
                if (!Number.isFinite(rawX) || !Number.isFinite(rawY)) return null;

                const metrics = calcPhaseMetrics(ph, tdeeForRoadmap);
                const rate = metrics?.rateLbsPerWeek ?? 0;
                const signedRate = rate > 0 ? `+${formatRoadmapPace(rate)}` : formatRoadmapPace(rate);
                const text = `${ROADMAP_PHASE_LABELS[ph.type]} ${signedRate} lb/wk`;
                const linkedSprintForPill = roadmap.phaseSprintLinks?.[ph.id]
                  ? sprints.find((s) => s.id === roadmap.phaseSprintLinks![ph.id]) ?? null
                  : null;
                const sprintLabel = linkedSprintForPill ? truncateSprintName(linkedSprintForPill.name, 14) : null;
                const pillW = Math.max(88, Math.min(156, Math.max(text.length, sprintLabel?.length ?? 0) * 6.6 + 20));
                const pillH = sprintLabel ? 30 : 18;
                const clampedX = Math.max(plotLeft + 6 + pillW / 2, Math.min(plotLeft + plotWidth - 6 - pillW / 2, rawX));
                const staggerOffset = (phaseIndex % 3) * 11;
                const pillY = Math.max(plotTop + 4, Math.min(plotTop + plotHeight - pillH - 4, rawY - 22 - staggerOffset));
                const phaseColor = ROADMAP_PHASE_COLORS[ph.type];

                return (
                  <g key={`viewer-pill-${ph.id}`} style={{ pointerEvents: "none" }}>
                    <rect x={clampedX - pillW / 2} y={pillY} width={pillW} height={pillH} rx={9} fill="rgba(15,23,42,0.82)" stroke={`${phaseColor}99`} strokeWidth={1} />
                    {sprintLabel ? (
                      <text textAnchor="middle" fill={phaseColor} fontSize={10} fontWeight={700}>
                        <tspan x={clampedX} y={pillY + 10.5}>{sprintLabel}</tspan>
                        <tspan x={clampedX} dy={11}>{text}</tspan>
                      </text>
                    ) : (
                      <text x={clampedX} y={pillY + 12.25} textAnchor="middle" fill={phaseColor} fontSize={10} fontWeight={700}>{text}</text>
                    )}
                  </g>
                );
              })}
            </g>
          );
        }}
      />
    );

    return (
      <div style={{ ...mainContentWidthStyle, display: "grid", gap: isRoadmapMobile ? "0.75rem" : "1.5rem", width: "100%", minWidth: 0, overflowX: "hidden" }}>

        {/* Header */}
        <div style={{ ...cardStyle, ...mobileRoadmapFrame, borderRadius: 18, width: isRoadmapMobile ? "100%" : undefined, padding: isRoadmapMobile ? "0.9rem 1rem" : "1.15rem 1.25rem", position: "relative", overflow: "visible", zIndex: roadmapHeaderMenuOpen ? 200 : 1, minWidth: 0, boxSizing: "border-box" }}>
          {isRoadmapMobile ? (
            <div style={{ display: "grid", gap: "0.55rem" }}>
              {/* Row 1: Title + ellipsis */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ fontSize: "1.6rem", fontWeight: 900 }}>Roadmap</div>
                <div ref={roadmapHeaderMenuRef} style={{ position: "relative" }}>
                  <button
                    type="button"
                    aria-label="More roadmap actions"
                    onClick={() => setRoadmapHeaderMenuOpen((v) => !v)}
                    style={{ ...btnBase, width: 34, height: 34, borderRadius: 10, padding: 0, display: "inline-flex", alignItems: "center", justifyContent: "center", background: "rgba(255,255,255,0.04)", color: "rgba(255,255,255,0.86)", fontSize: "1.1rem", lineHeight: 1 }}
                  >
                    <svg viewBox="0 0 20 5" width="16" height="5" fill="currentColor" aria-hidden="true"><circle cx="2.5" cy="2.5" r="2"/><circle cx="10" cy="2.5" r="2"/><circle cx="17.5" cy="2.5" r="2"/></svg>
                  </button>
                  {roadmapHeaderMenuOpen && (
                    <div style={{ position: "absolute", top: "calc(100% + 8px)", right: 0, minWidth: 200, maxWidth: "min(80vw, 260px)", borderRadius: 12, border: "1px solid rgba(255,255,255,0.14)", background: "rgba(2,6,23,0.98)", boxShadow: "0 18px 40px rgba(0,0,0,0.5)", padding: "0.35rem", zIndex: 5000, display: "grid", gap: "0.2rem" }}>
                      {roadmap ? (
                        <>
                          <button style={{ ...btnBase, justifyContent: "flex-start", background: "transparent", border: "none", color: "rgba(255,255,255,0.88)", padding: "0.45rem 0.55rem", textAlign: "left" }} onClick={() => { openAddPhaseBuilder(); setRoadmapHeaderMenuOpen(false); }}>Add Phase</button>
                          {activeSprint && phase1IsCurrentlyActive && (
                            <button style={{ ...btnBase, justifyContent: "flex-start", background: "transparent", border: "none", color: "rgba(255,255,255,0.88)", padding: "0.45rem 0.55rem", textAlign: "left" }} onClick={() => { openSyncSprintConfirm(); setRoadmapHeaderMenuOpen(false); }}>Import Active Sprint</button>
                          )}
                          <div style={{ height: 1, background: "rgba(255,255,255,0.1)", margin: "0.2rem 0" }} />
                          <button style={{ ...btnBase, justifyContent: "flex-start", background: "transparent", border: "none", color: "rgba(255,255,255,0.88)", padding: "0.45rem 0.55rem", textAlign: "left" }} onClick={() => { openRoadmapWizard(); setRoadmapHeaderMenuOpen(false); }}>Edit / Recalculate Roadmap</button>
                          <div style={{ height: 1, background: "rgba(255,255,255,0.1)", margin: "0.2rem 0" }} />
                          <button style={{ ...btnBase, justifyContent: "flex-start", background: "transparent", border: "none", color: "#fca5a5", padding: "0.45rem 0.55rem", textAlign: "left" }} onClick={requestRoadmapDelete}>Delete Roadmap</button>
                        </>
                      ) : (
                        <>
                          {activeSprint && (
                            <button style={{ ...btnBase, justifyContent: "flex-start", background: "transparent", border: "none", color: "rgba(255,255,255,0.88)", padding: "0.45rem 0.55rem", textAlign: "left" }} onClick={() => { createRoadmapFromSprint(); setRoadmapHeaderMenuOpen(false); }}>Import Active Sprint</button>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
              {/* Row 2: Description */}
              <div style={{ color: "rgba(255,255,255,0.65)", fontSize: "0.9rem" }}>
                Plan your cut, bulk, and maintenance phases.
              </div>
              {/* Row 3: Primary action */}
              <button
                style={{ ...btnBase, background: actionColors.primaryGradient, color: actionColors.primaryText, borderColor: "rgba(148,163,184,0.45)", padding: "0.6rem 0.95rem", fontSize: "0.84rem", fontWeight: 800, alignSelf: "flex-start", boxShadow: "0 10px 26px rgba(59,130,246,0.18)" }}
                onClick={roadmap ? openAddPhaseBuilder : openRoadmapWizard}
              >
                {roadmap ? "Add Phase" : "Build Roadmap"}
              </button>
            </div>
          ) : (
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "0.75rem", flexWrap: "wrap" }}>
              <div>
                <div style={{ fontSize: "1.6rem", fontWeight: 900 }}>Roadmap</div>
                <div style={{ marginTop: "0.35rem", color: "rgba(255,255,255,0.65)" }}>
                  Plan your cut, bulk, and maintenance phases.
                </div>
              </div>
              <div style={{ display: "flex", gap: "0.6rem", flexWrap: "nowrap", overflowX: "auto" }}>
                {roadmap && (
                  <button
                    style={{ ...btnBase, background: actionColors.primaryGradient, color: actionColors.primaryText, borderColor: "rgba(148,163,184,0.45)", fontWeight: 800, boxShadow: "0 10px 26px rgba(59,130,246,0.18)", flexShrink: 0 }}
                    onClick={openAddPhaseBuilder}
                  >
                    Add Phase
                  </button>
                )}
                {roadmap && (
                  <button
                    style={{ ...btnBase, background: "rgba(239,68,68,0.15)", borderColor: "rgba(239,68,68,0.3)", color: "#fca5a5", flexShrink: 0 }}
                    onClick={requestRoadmapDelete}
                  >
                    Delete Roadmap
                  </button>
                )}
                <button
                  style={{ ...btnBase, background: roadmap ? "rgba(255,255,255,0.06)" : actionColors.primaryGradient, color: roadmap ? "white" : actionColors.primaryText, borderColor: "rgba(148,163,184,0.45)", flexShrink: 0 }}
                  onClick={openRoadmapWizard}
                >
                  {roadmap ? "Recalculate Roadmap" : "Build Roadmap"}
                </button>
                {roadmap && activeSprint && phase1IsCurrentlyActive && (
                  <button
                    style={{ ...btnBase, background: `linear-gradient(135deg, ${COLORS.orange}, #f97316)`, color: "white", flexShrink: 0 }}
                    onClick={openSyncSprintConfirm}
                  >
                    Import Active Sprint
                  </button>
                )}
                {!roadmap && activeSprint && (
                  <button
                    style={{ ...btnBase, background: `linear-gradient(135deg, ${COLORS.orange}, #f97316)`, color: "white", flexShrink: 0 }}
                    onClick={createRoadmapFromSprint}
                  >
                    Use Active Sprint
                  </button>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Timeline chart — card always shows; edit mode opens a full-screen overlay */}
        {roadmap && roadmap.phases.length > 0 && (() => {
          // Drag-handle + phase-pill Customized layer for the edit overlay chart
          const editDragHandles = (
            <Customized
              component={(props: Record<string, unknown>) => {
                const xAxisMap = props.xAxisMap as Record<string, { scale: ((v: number) => number) & { invert?: (px: number) => number } }> | undefined;
                const yAxisMap = props.yAxisMap as Record<string, { scale: ((v: number) => number) & { invert?: (px: number) => number } }> | undefined;
                const offset = props.offset as { left?: number; top?: number; width?: number; height?: number } | undefined;
                if (!xAxisMap || !yAxisMap) return null;
                const xAxisEntry = Object.values(xAxisMap)[0];
                const yAxisEntry = Object.values(yAxisMap)[0];
                if (!xAxisEntry?.scale || !yAxisEntry?.scale) return null;
                const xScale = xAxisEntry.scale;
                const yScale = yAxisEntry.scale;
                const plotLeft = offset?.left ?? 0;
                const plotTop = offset?.top ?? 0;
                const plotWidth = offset?.width ?? 600;
                const plotHeight = offset?.height ?? 300;
                // Capture the overlay SVG for mouse-coordinate inversion
                const svgEl = (props.svgRef as React.RefObject<SVGSVGElement> | undefined)?.current
                  ?? (typeof document !== "undefined" ? document.querySelector(".roadmap-edit-chart svg") as SVGSVGElement | null : null);
                if (svgEl && roadmapChartContainerRef.current !== svgEl) {
                  (roadmapChartContainerRef as React.MutableRefObject<SVGSVGElement | null>).current = svgEl;
                }

                // Drag guideline crosshairs
                const guideX = dragState ? xScale(dragState.currentEndDateTs) : null;
                const guideY = dragState ? yScale(dragState.currentEndWeight) : null;

                return (
                  <g>
                    {/* Drag guide lines — desktop only */}
                    {!isMobile && dragState && guideX !== null && Number.isFinite(guideX) && (
                      <line x1={guideX} y1={plotTop} x2={guideX} y2={plotTop + plotHeight} stroke="rgba(255,255,255,0.25)" strokeWidth={1} strokeDasharray="4 3" />
                    )}
                    {!isMobile && dragState && guideY !== null && Number.isFinite(guideY) && (
                      <line x1={plotLeft} y1={guideY} x2={plotLeft + plotWidth} y2={guideY} stroke="rgba(255,255,255,0.25)" strokeWidth={1} strokeDasharray="4 3" />
                    )}

                    {/* Phase mid-segment pill labels */}
                    {roadmap.phases.map((ph, pi) => {
                      const startTs = safeParseYmd(ph.startDate, "0001-01-01", "9999-12-31")?.getTime();
                      const isDraggingThis = dragState?.phaseId === ph.id;
                      const endTs = isDraggingThis ? dragState!.currentEndDateTs : (safeParseYmd(ph.endDate, "0001-01-01", "9999-12-31")?.getTime() ?? 0);
                      if (!startTs || !endTs) return null;
                      const midTs = (startTs + endTs) / 2;
                      const midX = xScale(midTs);
                      if (!Number.isFinite(midX)) return null;
                      // Clamp label within plot area
                      const labelW = 90;
                      const clampedX = Math.max(plotLeft + labelW / 2 + 4, Math.min(plotLeft + plotWidth - labelW / 2 - 4, midX));
                      const labelY = plotTop + 14;
                      const phColor = ROADMAP_PHASE_COLORS[ph.type];
                      const editLinkedSprintId = roadmap.phaseSprintLinks?.[ph.id];
                      const editSprintLabel = editLinkedSprintId
                        ? truncateSprintName(sprints.find((s) => s.id === editLinkedSprintId)?.name ?? "", 12) || null
                        : null;
                      const pillHeight = editSprintLabel ? 30 : 18;
                      return (
                        <g key={`pill_${ph.id}`}>
                          <rect x={clampedX - labelW / 2} y={labelY - 9} width={labelW} height={pillHeight} rx={9} fill={`${phColor}33`} stroke={`${phColor}88`} strokeWidth={1} />
                          {editSprintLabel ? (
                            <text textAnchor="middle" fill={phColor} fontSize={10} fontWeight={700} style={{ pointerEvents: "none" }}>
                              <tspan x={clampedX} y={labelY + 4}>{`P${pi + 1} · ${ROADMAP_PHASE_LABELS[ph.type]}`}</tspan>
                              <tspan x={clampedX} dy={11}>{editSprintLabel}</tspan>
                            </text>
                          ) : (
                            <text x={clampedX} y={labelY + 4} textAnchor="middle" fill={phColor} fontSize={10} fontWeight={700} style={{ pointerEvents: "none" }}>
                              {`P${pi + 1} · ${ROADMAP_PHASE_LABELS[ph.type]}`}
                            </text>
                          )}
                        </g>
                      );
                    })}

                    {/* Endpoint drag handles + persistent labels */}
                    {roadmap.phases.map((ph, pi) => {
                      const isDraggingThis = dragState?.phaseId === ph.id;
                      const displayTs = isDraggingThis ? dragState!.currentEndDateTs : (safeParseYmd(ph.endDate, "0001-01-01", "9999-12-31")?.getTime() ?? 0);
                      const displayWeight = isDraggingThis ? dragState!.currentEndWeight : ph.endWeight;
                      if (!displayTs) return null;
                      const cx = xScale(displayTs);
                      const cy = yScale(displayWeight);
                      if (!Number.isFinite(cx) || !Number.isFinite(cy)) return null;
                      const phaseColor = ROADMAP_PHASE_COLORS[ph.type];

                      // Persistent label: date line + weight line
                      const labelD = new Date(displayTs);
                      const labelDate = labelD.toLocaleString("default", { month: "short", day: "numeric" });
                      const labelWt = `${displayWeight.toFixed(1)} lb`;
                      const lblW = 62, lblH = 30, lblR = 6;
                      // Alternate above/below by index to reduce overlap; clamp within plot
                      const lblAbove = pi % 2 === 0 || cy - 50 < plotTop;
                      const lblRawX = cx + 10;
                      const lblRawY = lblAbove ? cy - 44 : cy + 14;
                      const lblX = Math.max(plotLeft + 2, Math.min(plotLeft + plotWidth - lblW - 2, lblRawX));
                      const lblY = Math.max(plotTop + 2, Math.min(plotTop + plotHeight - lblH - 2, lblRawY));

                      return (
                        <g key={`handle_${ph.id}`}>
                          {/* Glow ring */}
                          <circle cx={cx} cy={cy} r={isDraggingThis ? 13 : 10} fill={phaseColor} fillOpacity={isDraggingThis ? 0.28 : 0.13} stroke="none" style={{ pointerEvents: "none" }} />
                          {/* Visible handle dot */}
                          <circle cx={cx} cy={cy} r={isDraggingThis ? 7 : 5.5}
                            fill={isDraggingThis ? "white" : phaseColor}
                            stroke={isDraggingThis ? phaseColor : "rgba(255,255,255,0.9)"}
                            strokeWidth={2}
                            style={{ pointerEvents: "none" }}
                          />
                          {/* Persistent endpoint label — always visible, updates live */}
                          <g style={{ pointerEvents: "none" }}>
                            <rect x={lblX} y={lblY} width={lblW} height={lblH} rx={lblR}
                              fill="rgba(15,23,42,0.82)" stroke={`${phaseColor}88`} strokeWidth={1} />
                            <text x={lblX + lblW / 2} y={lblY + 11} textAnchor="middle"
                              fill={phaseColor} fontSize={10} fontWeight={700}>{labelDate}</text>
                            <text x={lblX + lblW / 2} y={lblY + 23} textAnchor="middle"
                              fill="rgba(255,255,255,0.75)" fontSize={10}>{labelWt}</text>
                          </g>
                          {/* Hit target — desktop only; pointer capture keeps drag alive */}
                          {!isMobile && <circle cx={cx} cy={cy} r={18} fill="transparent"
                            data-drag-handle="true"
                            style={{ cursor: isDraggingThis ? "grabbing" : "grab" }}
                            onPointerDown={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              (e.currentTarget as SVGCircleElement).setPointerCapture(e.pointerId);
                              const svg = roadmapChartContainerRef.current ?? (e.currentTarget.closest("svg") as SVGSVGElement | null);
                              if (svg) (roadmapChartContainerRef as React.MutableRefObject<SVGSVGElement | null>).current = svg;
                              const svgRect = svg?.getBoundingClientRect();
                              const originX = svgRect ? e.clientX - svgRect.left : cx;
                              const originY = svgRect ? e.clientY - svgRect.top : cy;
                              const endTs = safeParseYmd(ph.endDate, "0001-01-01", "9999-12-31")?.getTime() ?? 0;
                              setDragState({
                                phaseId: ph.id, phaseIndex: pi,
                                originPx: { x: originX, y: originY },
                                originEndDateTs: endTs, originEndWeight: ph.endWeight,
                                currentEndDateTs: endTs, currentEndWeight: ph.endWeight,
                                xScale: (v) => xScale(v), yScale: (v) => yScale(v),
                                xScaleInvert: (px) => (xScale.invert ? xScale.invert(px) : px),
                                yScaleInvert: (px) => (yScale.invert ? yScale.invert(px) : px),
                                shiftLockAxis: "none",
                              });
                            }}
                          />}
                        </g>
                      );
                    })}
                  </g>
                );
              }}
            />
          );

          return (
            <>
              {/* Read-only chart card */}
              <div style={{ ...cardStyle, ...mobileRoadmapFrame, borderRadius: 18, position: "relative", width: isRoadmapMobile ? "100%" : undefined, overflow: "hidden", padding: isRoadmapMobile ? "0.9rem" : "1rem", minWidth: 0, boxSizing: "border-box" }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "0.8rem", flexWrap: "wrap", gap: "0.5rem" }}>
                  <div style={{ fontWeight: 700, fontSize: "1.05rem" }}>Weight Projection</div>
                </div>
                {/* Normal read-only chart */}
                {!chartEditMode && (
                  <div style={{ overflow: "hidden", width: isRoadmapMobile ? "auto" : "100%", marginLeft: isRoadmapMobile ? -6 : 0, marginRight: isRoadmapMobile ? -6 : 0 }}>
                  <ResponsiveContainer width="100%" height={isRoadmapMobile ? 240 : 260}>
                    <LineChart data={mergedChartData} margin={isRoadmapMobile ? { top: 6, right: 4, left: 4, bottom: 0 } : { top: 8, right: 12, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                      <XAxis dataKey="ts" type="number" scale="time" domain={[roadmapStartTs, roadmapEndTs]} ticks={roadmapViewerTicks} tick={{ fill: "rgba(255,255,255,0.65)", fontSize: isRoadmapMobile ? 10 : 11 }} tickFormatter={roadmapXAxisFormatter} angle={isRoadmapMobile ? -32 : -45} textAnchor="end" height={isRoadmapMobile ? 38 : 50} stroke="rgba(255,255,255,0.12)" />
                      <YAxis domain={["auto", "auto"]} tick={{ fill: "rgba(255,255,255,0.65)", fontSize: isRoadmapMobile ? 10 : 11 }} tickFormatter={(v) => `${v}lb`} tickMargin={isRoadmapMobile ? 2 : 4} label={undefined} stroke="rgba(255,255,255,0.12)" width={isRoadmapMobile ? 44 : 46} />
                      <Tooltip contentStyle={tooltipStyle} labelFormatter={(ts: number) => { const d = new Date(ts); return `${d.getMonth()+1}/${d.getDate()}/${d.getFullYear()}`; }} formatter={(v, name) => { if (name === "actual") return [`${Number(v).toFixed(1)} lb`, "Actual"]; if (String(name).startsWith("phase_")) { const idx = parseInt(String(name).replace("phase_", ""), 10); const ph = roadmap?.phases[idx]; return [`${Number(v).toFixed(1)} lb`, ph ? `Plan: ${ROADMAP_PHASE_LABELS[ph.type]}` : String(name)]; } return [String(v), String(name)]; }} />
                      {todayInRoadmapDomain ? <ReferenceLine x={roadmapTodayTs as number} stroke="rgba(255,255,255,0.55)" strokeWidth={1} strokeOpacity={0.6} label={{ value: "Today", position: "top", fill: "rgba(255,255,255,0.65)", fontSize: 10, offset: 8 }} /> : null}
                      {todayInRoadmapDomain ? <ReferenceArea x1={roadmapTodayTs as number} x2={roadmapEndTs} fill="rgba(255,255,255,0.06)" /> : null}
                      {roadmap.phases.map((ph) => { const x1 = safeParseYmd(ph.startDate, "0001-01-01", "9999-12-31")?.getTime(); const x2 = safeParseYmd(ph.endDate, "0001-01-01", "9999-12-31")?.getTime(); if (!x1 || !x2) return null; return <ReferenceArea key={`band_${ph.id}`} x1={x1} x2={x2} fill={ROADMAP_PHASE_COLORS[ph.type]} fillOpacity={0.04} stroke="none" />; })}
                      {roadmap.phases.map((ph, i) => <Line key={ph.id} dataKey={`phase_${i}`} stroke={ROADMAP_PHASE_COLORS[ph.type]} strokeWidth={2.5} strokeDasharray="6 3" dot={false} connectNulls={false} isAnimationActive={false} name={`phase_${i}`} />)}
                      {actualEntriesForChart.length > 0 && <Line dataKey="actual" stroke="#e2e8f0" strokeWidth={1.6} dot={{ r: 2, fill: "#e2e8f0", stroke: "rgba(15,23,42,0.7)", strokeWidth: 0.8 }} connectNulls={true} isAnimationActive={false} name="actual" />}
                      {!isRoadmapMobile ? viewerPhasePills : null}
                    </LineChart>
                  </ResponsiveContainer>
                  </div>
                )}
                <div style={{ display: "flex", alignItems: "center", gap: isRoadmapMobile ? "0.35rem" : "0.5rem", marginTop: "0.7rem", minWidth: 0, whiteSpace: "nowrap", overflow: "hidden" }}>
                  {phaseChartSeries.map((s) => (
                    <div key={s.key} style={{ display: "inline-flex", alignItems: "center", gap: "0.28rem", fontSize: isRoadmapMobile ? "0.68rem" : "0.72rem", color: "rgba(255,255,255,0.72)", minWidth: 0, flex: "1 1 0" }}>
                      <div style={{ width: isRoadmapMobile ? 12 : 14, height: 3, borderRadius: 999, background: s.color, flexShrink: 0 }} />
                      <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{s.label}</span>
                    </div>
                  ))}
                  {actualEntriesForChart.length > 0 && (
                    <div style={{ display: "inline-flex", alignItems: "center", gap: "0.28rem", fontSize: isRoadmapMobile ? "0.68rem" : "0.72rem", color: "rgba(255,255,255,0.72)", minWidth: 0, flex: "1 1 0" }}>
                      <div style={{ width: isRoadmapMobile ? 12 : 14, height: 3, borderRadius: 999, background: "#e2e8f0", flexShrink: 0 }} />
                      <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>Actual</span>
                    </div>
                  )}
                </div>
                {/* Placeholder while overlay is open */}
                {chartEditMode && (
                  <div style={{ height: 80, display: "flex", alignItems: "center", justifyContent: "center", color: "rgba(255,255,255,0.3)", fontSize: "0.82rem" }}>
                    Editing in full-screen — press Done or Escape to close
                  </div>
                )}
              </div>

              {/* ── 75vh centered modal ── */}
              {chartEditMode && createPortal(
                <>
                  {/* Backdrop */}
                  <div
                    onClick={() => { setChartEditMode(false); setDragState(null); setDragOverlayPos(null); }}
                    style={{ position: "fixed", inset: 0, zIndex: 9100, background: "rgba(5,10,22,0.82)", backdropFilter: "blur(3px)" }}
                  />
                  {/* Modal sheet */}
                  <div style={{
                    position: "fixed",
                    top: "50%", left: "50%",
                    transform: "translate(-50%, -50%)",
                    zIndex: 9101,
                    width: "min(1200px, 92vw)",
                    height: "75vh",
                    maxHeight: "80vh",
                    background: brand.background.card,
                    border: "1px solid rgba(255,255,255,0.12)",
                    borderRadius: 20,
                    boxShadow: "0 24px 80px rgba(0,0,0,0.7)",
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                  }}>
                    {/* ── Sticky header ── */}
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0.9rem 1.25rem 0.7rem", borderBottom: "1px solid rgba(255,255,255,0.08)", flexShrink: 0, gap: "0.75rem" }}>
                      <div>
                        <div style={{ fontWeight: 800, fontSize: "1.05rem", color: "white" }}>Edit Weight Plan</div>
                        <div style={{ fontSize: "0.73rem", color: "rgba(255,255,255,0.4)", marginTop: "0.15rem" }}>
                          {isMobile ? "Edit fields below · tap Done to close" : "Drag endpoint handles · drag chart to pan · Escape or Done to close"}
                        </div>
                      </div>
                      {/* Extend timeline buttons */}
                      <div style={{ display: "flex", gap: "0.35rem", marginLeft: "auto" }}>
                        {[30, 60, 90].map((days) => (
                          <button
                            key={days}
                            onClick={() => setChartXExtendMs((m) => m + days * 86400000)}
                            style={{ ...btnBase, padding: "0.3rem 0.65rem", fontSize: "0.78rem", fontWeight: 600, background: "rgba(255,255,255,0.06)", borderColor: "rgba(255,255,255,0.14)", color: "rgba(255,255,255,0.55)" }}
                          >
                            +{days}d
                          </button>
                        ))}
                      </div>
                      <button
                        onClick={() => { setChartEditMode(false); setDragState(null); setDragOverlayPos(null); }}
                        style={{ ...btnBase, padding: "0.4rem 1.05rem", fontSize: "0.85rem", fontWeight: 700, background: "rgba(59,130,246,0.25)", borderColor: "rgba(59,130,246,0.5)", color: "#93c5fd" }}
                      >
                        Done
                      </button>
                    </div>

                    {/* ── Chart area (~65% of modal height) ── */}
                    <div
                      className="roadmap-edit-chart"
                      style={{ flex: "0 0 62%", minHeight: 0, padding: "0.5rem 1rem 0", touchAction: isMobile ? "auto" : "none", cursor: isMobile ? "default" : (dragState ? "default" : "grab") }}
                      onPointerDown={isMobile ? undefined : (e) => {
                        if (dragState) return;
                        // Skip if click originated on a drag handle
                        if ((e.target as Element).closest("[data-drag-handle]")) return;
                        const rect = (e.currentTarget as HTMLDivElement).getBoundingClientRect();
                        const chartWidth = Math.max(rect.width - 72, 100); // ~50px YAxis + ~22px right margin
                        const domainMs = editXDomainEnd - editXDomainStart;
                        const msPerPx = domainMs / chartWidth;
                        panStartRef.current = { clientX: e.clientX, baseOffset: chartPanOffsetMs, msPerPx };
                        (e.currentTarget as HTMLDivElement).setPointerCapture(e.pointerId);
                        e.currentTarget.style.cursor = "grabbing";
                      }}
                      onPointerMove={isMobile ? undefined : (e) => {
                        if (!panStartRef.current) return;
                        const dx = e.clientX - panStartRef.current.clientX;
                        // Drag right → pan left (view moves to earlier dates), drag left → pan right
                        const newOffset = panStartRef.current.baseOffset - dx * panStartRef.current.msPerPx;
                        setChartPanOffsetMs(newOffset);
                      }}
                      onPointerUp={isMobile ? undefined : (e) => {
                        panStartRef.current = null;
                        (e.currentTarget as HTMLDivElement).style.cursor = "grab";
                      }}
                    >
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={mergedChartData} margin={{ top: 12, right: 20, left: 0, bottom: 4 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                          <XAxis dataKey="ts" type="number" scale="time" domain={[editXDomainStart, editXDomainEnd]} ticks={monthlyTickTs} tick={{ fill: "rgba(255,255,255,0.45)", fontSize: 10 }} tickFormatter={roadmapXAxisFormatter} stroke="rgba(255,255,255,0.1)" />
                          <YAxis domain={[editYDomainMin, editYDomainMax]} tick={{ fill: "rgba(255,255,255,0.45)", fontSize: 10 }} tickFormatter={(v) => `${v}lb`} tickMargin={4} label={undefined} stroke="rgba(255,255,255,0.1)" width={46} />
                          {/* Phase shading — uses live phases so shading moves during drag */}
                          {liveRoadmapPhases.map((ph) => { const x1 = safeParseYmd(ph.startDate, "0001-01-01", "9999-12-31")?.getTime(); const x2 = safeParseYmd(ph.endDate, "0001-01-01", "9999-12-31")?.getTime(); if (!x1 || !x2) return null; return <ReferenceArea key={`eband_${ph.id}`} x1={x1} x2={x2} fill={ROADMAP_PHASE_COLORS[ph.type]} fillOpacity={0.07} stroke="none" />; })}
                          {/* Plan lines — data keys match liveRoadmapPhases indices */}
                          {liveRoadmapPhases.map((ph, i) => <Line key={ph.id} dataKey={`phase_${i}`} stroke={ROADMAP_PHASE_COLORS[ph.type]} strokeWidth={2.5} strokeDasharray="6 3" dot={{ r: 4, fill: ROADMAP_PHASE_COLORS[ph.type], stroke: "rgba(15,23,42,0.9)", strokeWidth: 2 }} connectNulls={false} isAnimationActive={false} name={`phase_${i}`} />)}
                          {/* Actual weight */}
                          {actualEntriesForChart.length > 0 && <Line dataKey="actual" stroke="#e2e8f0" strokeWidth={1.5} dot={{ r: 2.5, fill: "#e2e8f0", stroke: "rgba(15,23,42,0.9)", strokeWidth: 1 }} connectNulls={true} isAnimationActive={false} name="actual" />}
                          {/* Phase pills + drag handles via Customized */}
                          {editDragHandles}
                        </LineChart>
                      </ResponsiveContainer>
                    </div>

                    {/* ── Summary panel (~35% of modal height, scrollable) ── */}
                    <div style={{ flex: 1, minHeight: 0, overflowY: "auto", overflowX: "auto", borderTop: "1px solid rgba(255,255,255,0.08)", padding: "0.75rem 1.25rem 1rem" }}>
                      {(() => {
                        const bandColorMap: Record<RateBandSeverity, string> = { conservative: "#22c55e", moderate: "#3b82f6", aggressive: "#f59e0b", extreme: "#ef4444" };
                        // Use push-propagated live phases (same data used by the chart)
                        const summaryPhases = liveRoadmapPhases;

                        const totalDays = summaryPhases.reduce((acc, ph) => {
                          const s = safeParseYmd(ph.startDate, "0001-01-01", "9999-12-31");
                          const e = safeParseYmd(ph.endDate, "0001-01-01", "9999-12-31");
                          return acc + (s && e ? Math.round((e.getTime() - s.getTime()) / 86400000) : 0);
                        }, 0);
                        const firstPhase = summaryPhases[0];
                        const lastPhase = summaryPhases[summaryPhases.length - 1];
                        const totalDelta = lastPhase && firstPhase ? lastPhase.endWeight - firstPhase.startWeight : 0;

                        const rowStyle: React.CSSProperties = { display: "grid", gridTemplateColumns: "160px 140px 140px 52px 70px 110px 130px", gap: "0 0.5rem", alignItems: "center", padding: "0.45rem 0.5rem", borderRadius: 8, fontSize: "0.88rem" };
                        const headerStyle: React.CSSProperties = { ...rowStyle, fontSize: "0.72rem", fontWeight: 700, color: "rgba(255,255,255,0.4)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "0.1rem" };

                        return (
                          <>
                            <div style={headerStyle}>
                              <span>Phase</span><span>Start</span><span>End</span><span>Dur</span><span>Δ lb</span><span>Rate</span><span>Band</span>
                            </div>
                            {summaryPhases.map((ph, i) => {
                              const s = safeParseYmd(ph.startDate, "0001-01-01", "9999-12-31");
                              const e = safeParseYmd(ph.endDate, "0001-01-01", "9999-12-31");
                              const days = s && e ? Math.round((e.getTime() - s.getTime()) / 86400000) : 0;
                              const weeks = days / 7;
                              const delta = ph.endWeight - ph.startWeight;
                              const rate = weeks > 0 ? delta / weeks : 0;
                              const band = getRateBand(ph.type, rate);
                              const bc = bandColorMap[band.severity];
                              const phColor = ROADMAP_PHASE_COLORS[ph.type];
                              const isLive = dragState?.phaseId === ph.id || (dragState && i > (roadmap.phases.findIndex((p) => p.id === dragState.phaseId)));
                              return (
                                <div key={ph.id} style={{ ...rowStyle, background: isLive ? "rgba(255,255,255,0.05)" : "transparent", transition: "background 0.1s" }}>
                                  {/* Phase pill */}
                                  <div style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
                                    <span style={{ background: `${phColor}22`, border: `1px solid ${phColor}55`, color: phColor, borderRadius: 6, padding: "0.15rem 0.5rem", fontWeight: 700, fontSize: "0.78rem", whiteSpace: "nowrap" }}>
                                      P{i + 1} · {ROADMAP_PHASE_LABELS[ph.type]}
                                    </span>
                                  </div>
                                  {/* Start */}
                                  <span style={{ color: "rgba(255,255,255,0.75)", fontVariantNumeric: "tabular-nums" }}>
                                    {ph.startDate}<br />
                                    <span style={{ fontSize: "0.8rem", color: "rgba(255,255,255,0.5)" }}>{ph.startWeight.toFixed(1)} lb</span>
                                  </span>
                                  {/* End */}
                                  <span style={{ color: isLive ? "white" : "rgba(255,255,255,0.75)", fontWeight: isLive ? 700 : 400, fontVariantNumeric: "tabular-nums" }}>
                                    {ph.endDate}<br />
                                    <span style={{ fontSize: "0.8rem", color: isLive ? "#93c5fd" : "rgba(255,255,255,0.5)" }}>{ph.endWeight.toFixed(1)} lb</span>
                                  </span>
                                  {/* Duration */}
                                  <span style={{ color: "rgba(255,255,255,0.55)", fontVariantNumeric: "tabular-nums", fontSize: "0.82rem" }}>
                                    {days}d
                                  </span>
                                  {/* Delta */}
                                  <span style={{ color: delta < 0 ? ROADMAP_PHASE_COLORS.cut : delta > 0 ? ROADMAP_PHASE_COLORS.bulk : "rgba(255,255,255,0.5)", fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>
                                    {delta >= 0 ? "+" : ""}{delta.toFixed(1)}
                                  </span>
                                  {/* Rate */}
                                  <span style={{ color: "rgba(255,255,255,0.8)", fontVariantNumeric: "tabular-nums" }}>
                                    {formatSignedPace(rate, 2, "lb/wk")}
                                  </span>
                                  {/* Band */}
                                  <span style={{ background: `${bc}20`, border: `1px solid ${bc}50`, color: bc, borderRadius: 5, padding: "0.1rem 0.4rem", fontWeight: 700, fontSize: "0.75rem", display: "inline-block" }}>
                                    {getPaceBandDisplayLabel(band.label)}
                                  </span>
                                </div>
                              );
                            })}
                            {/* Total row */}
                            <div style={{ ...rowStyle, borderTop: "1px solid rgba(255,255,255,0.08)", marginTop: "0.35rem", paddingTop: "0.55rem", color: "rgba(255,255,255,0.5)", fontWeight: 700, fontSize: "0.8rem" }}>
                              <span>Total</span>
                              <span style={{ color: "rgba(255,255,255,0.65)" }}>{firstPhase?.startDate}</span>
                              <span style={{ color: "rgba(255,255,255,0.65)" }}>{lastPhase?.endDate}</span>
                              <span style={{ color: "rgba(255,255,255,0.65)", fontVariantNumeric: "tabular-nums" }}>{totalDays}d</span>
                              <span style={{ color: "rgba(255,255,255,0.8)", fontVariantNumeric: "tabular-nums" }}>
                                {totalDelta >= 0 ? "+" : ""}{totalDelta.toFixed(1)} lb
                              </span>
                              <span />
                              <span />
                            </div>
                          </>
                        );
                      })()}
                    </div>
                  </div>
                </>,
                document.body
              )}
            </>
          );
        })()}

        {/* Drag feedback overlay — desktop only */}
        {!isMobile && dragState && dragOverlayPos && (() => {
          const phase = roadmap?.phases.find((p) => p.id === dragState.phaseId);
          if (!phase) return null;
          const endDate = tsToYmd(dragState.currentEndDateTs);
          const startDt = safeParseYmd(phase.startDate, "0001-01-01", "9999-12-31");
          const endDt = safeParseYmd(endDate, "0001-01-01", "9999-12-31");
          const durationDays = startDt && endDt ? Math.round((endDt.getTime() - startDt.getTime()) / 86400000) : null;
          const durationWeeks = durationDays !== null ? durationDays / 7 : null;
          const lbsPerWeek = (durationWeeks && durationWeeks > 0)
            ? (dragState.currentEndWeight - phase.startWeight) / durationWeeks
            : null;
          const band = lbsPerWeek !== null ? getRateBand(phase.type, lbsPerWeek) : null;

          const bandColorMap: Record<RateBandSeverity, string> = {
            conservative: "#22c55e",
            moderate: "#3b82f6",
            aggressive: "#f59e0b",
            extreme: "#ef4444",
          };
          const bandColor = band ? bandColorMap[band.severity] : "rgba(255,255,255,0.7)";

          const d = new Date(dragState.currentEndDateTs);
          const dateLabel = `${d.toLocaleString("default", { month: "short" })} ${d.getDate()}, ${d.getFullYear()}`;

          return createPortal(
            <div
              style={{
                position: "fixed",
                left: dragOverlayPos.x,
                top: dragOverlayPos.y,
                zIndex: 9999,
                background: "rgba(15,23,42,0.97)",
                border: "1px solid rgba(255,255,255,0.15)",
                borderRadius: 10,
                padding: "0.6rem 0.85rem",
                fontSize: "0.8rem",
                color: "white",
                pointerEvents: "none",
                boxShadow: "0 4px 24px rgba(0,0,0,0.5)",
                minWidth: 180,
              }}
            >
              <div style={{ fontWeight: 700, marginBottom: "0.3rem", color: ROADMAP_PHASE_COLORS[phase.type] }}>
                Phase {dragState.phaseIndex + 1} · {ROADMAP_PHASE_LABELS[phase.type]}
              </div>
              <div style={{ color: "rgba(255,255,255,0.85)" }}>End: <strong>{dateLabel}</strong></div>
              <div style={{ color: "rgba(255,255,255,0.85)" }}>Weight: <strong>{dragState.currentEndWeight} lb</strong></div>
              {durationDays !== null && (
                <div style={{ color: "rgba(255,255,255,0.65)" }}>Duration: {durationDays}d</div>
              )}
              {lbsPerWeek !== null && (
                <div style={{ color: "rgba(255,255,255,0.85)" }}>
                  Rate: <strong>{formatSignedPace(lbsPerWeek, 2)}</strong>
                </div>
              )}
              {band && (
                <div style={{ marginTop: "0.35rem", display: "flex", alignItems: "center", gap: "0.4rem" }}>
                  <span style={{
                    background: `${bandColor}22`,
                    border: `1px solid ${bandColor}55`,
                    color: bandColor,
                    borderRadius: 5,
                    padding: "0.15rem 0.45rem",
                    fontWeight: 700,
                    fontSize: "0.74rem",
                  }}>{getPaceBandDisplayLabel(band.label)}</span>
                  {band.warn && (
                    <span style={{ color: "#fca5a5", fontSize: "0.74rem" }}>
                      ⚠ {phase.type === "cut" ? "Extreme cut: > 2.0 lb/wk" : phase.type === "bulk" ? "Extreme bulk: > 1.0 lb/wk" : "Not maintenance: > 0.25 lb/wk"}
                    </span>
                  )}
                </div>
              )}
            </div>,
            document.body
          );
        })()}

        {/* Phase cards */}
        {roadmap && roadmap.phases.length > 0 ? (
          roadmap.phases.map((phase, i) => {
            // Use per-phase start weight for TDEE calorie targets
            const phaseBmr = calcBMR({ weightLbs: phase.startWeight, heightIn: vitals.height, gender: vitals.gender, birthday: vitals.birthday });
            const phaseTdee = phaseBmr !== null ? calcTDEEFromFactor({ bmr: phaseBmr, factor: vitals.activityFactor ?? 1.3 }) : null;
            const metrics = calcPhaseMetrics(phase, phaseTdee);
            const warnings = getPhaseGuardrails(phase, metrics);
            const rateBand = metrics ? getRateBand(phase.type, metrics.rateLbsPerWeek) : null;
            const phaseTypeWarning = getPhaseTypeWarning(phase);
            const isFirst = i === 0;
            const phaseColor = ROADMAP_PHASE_COLORS[phase.type];
            const isPhaseEditing = editingRoadmapPhaseId === phase.id;
            const canEditPhase = isPhaseEditing && !chartEditMode;
            const linkedSprintId = roadmap?.phaseSprintLinks?.[phase.id];
            const linkedSprint = linkedSprintId ? sprints.find((s) => s.id === linkedSprintId) ?? null : null;
            const isSyncedPhase = !!linkedSprint;
            const canEditPhaseFields = canEditPhase && !isSyncedPhase;
            const isMobilePhaseExpanded = !isRoadmapMobile || !!expandedRoadmapPhaseById[phase.id];
            const phaseBandLabel = rateBand ? getPaceBandDisplayLabel(rateBand.label) : null;
            const showPhaseBandPill = !!rateBand && (rateBand.warn || rateBand.severity === "aggressive");
            const phaseSecondaryLabel = linkedSprint
              ? truncateSprintName(linkedSprint.name, isRoadmapMobile ? 30 : 40)
              : `${phase.startWeight.toFixed(1)} → ${phase.endWeight.toFixed(1)} lb`;
            const phaseDurationLabel = metrics ? formatSprintDurationLabel(metrics.days) : null;
            const phasePaceLabel = metrics ? formatSignedPace(metrics.rateLbsPerWeek, 2, "lb/wk") : null;
            const beginPhaseEditing = () => {
              if (isSyncedPhase) {
                setSyncedPhaseEditInfoOpen(true);
                return;
              }
              setExpandedRoadmapPhaseById((prev) => ({ ...prev, [phase.id]: true }));
              setEditingRoadmapPhaseId(phase.id);
            };

            return (
              <div
                key={phase.id}
                style={{
                  ...cardStyle,
                  ...mobileRoadmapFrame,
                  borderRadius: 18,
                  padding: isRoadmapMobile ? "0.85rem 0.9rem" : "0.85rem 0.95rem",
                  width: isRoadmapMobile ? "100%" : undefined,
                  maxWidth: "100%",
                  minWidth: 0,
                  boxSizing: "border-box",
                  position: "relative",
                  overflow: "visible",
                  zIndex: mobilePhaseMenuOpenId === phase.id ? 120 : 1,
                  borderLeft: `4px solid ${phaseColor}`,
                  boxShadow: isPhaseEditing ? `0 0 0 1px ${phaseColor}66, 0 0 0 5px ${phaseColor}22` : undefined,
                }}
              >
                {/* Phase card header */}
                {isRoadmapMobile ? (
                  <div style={{ display: "grid", gap: "0.45rem" }}>
                    <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "0.85rem" }}>
                      <button
                        type="button"
                        onClick={() => setExpandedRoadmapPhaseById((prev) => ({ ...prev, [phase.id]: !prev[phase.id] }))}
                        style={{ ...btnBase, border: "none", background: "transparent", padding: 0, color: "white", display: "flex", alignItems: "flex-start", justifyContent: "space-between", width: "100%", minWidth: 0, gap: "0.7rem" }}
                      >
                        <span style={{ display: "inline-flex", alignItems: "flex-start", gap: "0.55rem", minWidth: 0, flex: 1 }}>
                          <span style={{ width: 22, height: 22, borderRadius: 999, background: `${phaseColor}26`, border: `1px solid ${phaseColor}55`, display: "inline-flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: "0.68rem", color: phaseColor, flexShrink: 0, marginTop: "0.05rem" }}>{i + 1}</span>
                          <span style={{ display: "grid", gap: "0.2rem", minWidth: 0, textAlign: "left" }}>
                            <span style={{ fontWeight: 800, fontSize: "0.94rem", color: "rgba(255,255,255,0.94)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>Phase {i + 1} · {ROADMAP_PHASE_LABELS[phase.type]}</span>
                            <span style={{ fontSize: "0.8rem", color: linkedSprint ? phaseColor : "rgba(255,255,255,0.9)", fontWeight: linkedSprint ? 650 : 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={linkedSprint?.name}>{phaseSecondaryLabel}</span>
                            {phaseDurationLabel && phasePaceLabel ? (
                              <span style={{ fontSize: "0.76rem", color: "rgba(255,255,255,0.62)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                {phaseDurationLabel} · {phasePaceLabel}
                              </span>
                            ) : null}
                          </span>
                        </span>
                        <span style={{ paddingTop: "0.28rem", color: "rgba(255,255,255,0.72)", flexShrink: 0 }}>
                          <ChevronIcon direction={isMobilePhaseExpanded ? "up" : "down"} size={15} />
                        </span>
                      </button>
                      <div ref={mobilePhaseMenuRef} data-phase-menu-root="true" style={{ position: "relative", flexShrink: 0, marginLeft: "0.2rem" }}>
                        <button
                          type="button"
                          aria-label={`Phase ${i + 1} actions`}
                          onClick={() => setMobilePhaseMenuOpenId((prev) => (prev === phase.id ? null : phase.id))}
                          style={{ ...btnBase, width: 36, height: 36, borderRadius: 9, padding: 0, display: "inline-flex", alignItems: "center", justifyContent: "center", background: "rgba(255,255,255,0.07)", color: "rgba(255,255,255,0.86)", border: "1px solid rgba(255,255,255,0.14)" }}
                        >
                          <svg viewBox="0 0 20 20" width={16} height={16} aria-hidden="true"
                            style={{ display: "block", color: "rgba(255,255,255,0.86)" }}>
                            <circle cx="4" cy="10" r="1.5" fill="currentColor" />
                            <circle cx="10" cy="10" r="1.5" fill="currentColor" />
                            <circle cx="16" cy="10" r="1.5" fill="currentColor" />
                          </svg>
                        </button>
                        {mobilePhaseMenuOpenId === phase.id && (
                          <div style={{ position: "absolute", top: "calc(100% + 8px)", right: 0, minWidth: 150, borderRadius: 12, border: "1px solid rgba(255,255,255,0.14)", background: "rgba(2,6,23,0.98)", boxShadow: "0 18px 40px rgba(0,0,0,0.5)", padding: "0.35rem", zIndex: 5000, display: "grid", gap: "0.2rem" }}>
                            <button style={{ ...btnBase, justifyContent: "flex-start", background: "transparent", border: "none", color: "rgba(255,255,255,0.88)", padding: "0.45rem 0.55rem", textAlign: "left" }} onClick={() => { if (isPhaseEditing) setEditingRoadmapPhaseId(null); else beginPhaseEditing(); setMobilePhaseMenuOpenId(null); }}>{isPhaseEditing ? "Done editing" : "Edit phase"}</button>
                            <button style={{ ...btnBase, justifyContent: "flex-start", background: "transparent", border: "none", color: "#fca5a5", padding: "0.45rem 0.55rem", textAlign: "left" }} onClick={() => requestDeletePhase(phase.id)}>Delete phase</button>
                          </div>
                        )}
                      </div>
                    </div>
                    {showPhaseBandPill && phaseBandLabel ? (
                      <div>
                        <span style={{ background: `${phaseColor}20`, border: `1px solid ${phaseColor}44`, color: phaseColor, borderRadius: 999, padding: "0.14rem 0.52rem", fontWeight: 700, fontSize: "0.72rem", display: "inline-flex", alignItems: "center", whiteSpace: "nowrap" }}>{phaseBandLabel}</span>
                      </div>
                    ) : null}
                  </div>
                ) : (
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1rem", flexWrap: "wrap", gap: "0.5rem" }}>
                    <div style={{ display: "flex", alignItems: "flex-start", gap: "0.75rem", minWidth: 0 }}>
                      <div style={{ width: 24, height: 24, borderRadius: 999, background: `${phaseColor}22`, border: `1px solid ${phaseColor}55`, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: "0.72rem", color: phaseColor, flexShrink: 0, marginTop: "0.05rem" }}>
                        {i + 1}
                      </div>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontWeight: 800, fontSize: "1.02rem", color: "rgba(255,255,255,0.95)" }}>
                          Phase {i + 1} · {ROADMAP_PHASE_LABELS[phase.type]}
                        </div>
                        <div style={{ fontSize: "0.84rem", color: linkedSprint ? phaseColor : "rgba(255,255,255,0.88)", marginTop: "0.12rem", fontWeight: linkedSprint ? 650 : 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={linkedSprint?.name}>
                          {phaseSecondaryLabel}
                        </div>
                        {phaseDurationLabel && phasePaceLabel && (
                          <div style={{ fontSize: "0.78rem", color: "rgba(255,255,255,0.55)", marginTop: "0.15rem", display: "flex", alignItems: "center", gap: "0.45rem", flexWrap: "wrap" }}>
                            <span>{phaseDurationLabel} · {phasePaceLabel}</span>
                            {showPhaseBandPill && rateBand && phaseBandLabel && (() => {
                              const bandColorMap: Record<RateBandSeverity, string> = { conservative: "#22c55e", moderate: "#3b82f6", aggressive: "#f59e0b", extreme: "#ef4444" };
                              const bc = bandColorMap[rateBand.severity];
                              return (
                                <span style={{ background: `${bc}22`, border: `1px solid ${bc}44`, color: bc, borderRadius: 999, padding: "0.12rem 0.48rem", fontWeight: 700, fontSize: "0.68rem" }}>
                                  {phaseBandLabel}
                                </span>
                              );
                            })()}
                          </div>
                        )}
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: "0.5rem", alignItems: "center", flexWrap: "wrap" }}>
                    {isPhaseEditing ? <Pill label="Editing" tone="blue" size="sm" /> : null}
                    <button
                      type="button"
                      onClick={() => {
                        if (isPhaseEditing) {
                          setEditingRoadmapPhaseId(null);
                        } else {
                          beginPhaseEditing();
                        }
                      }}
                      style={roadmapGhostEditButtonStyle}
                      title={isPhaseEditing ? "Done editing this phase" : "Edit this phase"}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.opacity = "1";
                        e.currentTarget.style.borderColor = "rgba(148,163,184,0.45)";
                        e.currentTarget.style.color = "rgba(255,255,255,0.86)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.opacity = "0.82";
                        e.currentTarget.style.borderColor = "rgba(255,255,255,0.16)";
                        e.currentTarget.style.color = "rgba(255,255,255,0.62)";
                      }}
                    >
                      {isPhaseEditing ? "Done" : "✎ Edit"}
                    </button>
                    {/* Sprint link badge or activate button */}
                    {(() => {
                      if (linkedSprint) {
                        const isPlanned = linkedSprint.startDate > todayYmd;
                        return (
                          <div style={{ display: "flex", alignItems: "center", gap: "0.35rem", background: `${phaseColor}22`, border: `1px solid ${phaseColor}44`, borderRadius: 8, padding: "0.3rem 0.6rem", fontSize: "0.75rem", color: phaseColor, fontWeight: 700 }} title={linkedSprint.name}>
                            <span>{isPlanned ? "🗓" : "🏁"} {truncateSprintName(linkedSprint.name)}</span>
                          </div>
                        );
                      }
                      return (
                        <button
                          onClick={() => createSprintFromPhase(phase, i)}
                          style={{ ...btnBase, padding: "0.35rem 0.65rem", background: `${phaseColor}18`, borderColor: `${phaseColor}44`, color: phaseColor, fontSize: "0.78rem", fontWeight: 700 }}
                          title="Create a sprint from this phase"
                        >
                          + Sprint
                        </button>
                      );
                    })()}
                    {isFirst && activeSprint && phase1IsCurrentlyActive && (
                      <button
                        onClick={openSyncSprintConfirm}
                        style={{ ...btnBase, padding: "0.35rem 0.65rem", background: `linear-gradient(135deg, ${COLORS.orange}, #f97316)`, color: "white", fontSize: "0.78rem", fontWeight: 700 }}
                        title="Replace this phase with your active sprint"
                      >
                        Sync Active Sprint
                      </button>
                    )}
                    <button
                      onClick={() => requestDeletePhase(phase.id)}
                      style={{ ...btnBase, padding: "0.35rem 0.65rem", background: "rgba(239,68,68,0.1)", borderColor: "rgba(239,68,68,0.25)", color: "#fca5a5", fontSize: "0.82rem" }}
                      title="Delete phase"
                    >
                      <svg viewBox="0 0 10 10" width="10" height="10" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true"><line x1="1" y1="1" x2="9" y2="9"/><line x1="9" y1="1" x2="1" y2="9"/></svg>
                    </button>
                  </div>
                  </div>
                )}

                {isMobilePhaseExpanded && (
                  <>
                {isRoadmapMobile && canEditPhase && !isSyncedPhase && (
                  <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "0.15rem" }}>
                    <button
                      type="button"
                      onClick={() => setEditingRoadmapPhaseId(null)}
                      style={{ ...btnBase, padding: "0.22rem 0.6rem", fontSize: "0.74rem", background: "rgba(59,130,246,0.2)", borderColor: "rgba(59,130,246,0.45)", color: "#bfdbfe" }}
                    >
                      Done
                    </button>
                  </div>
                )}
                {/* Phase fields grid — Row 1: weights, Row 2: dates */}
                <div style={{ display: "grid", gap: "0.65rem", marginTop: isRoadmapMobile ? "0.65rem" : 0 }}>
                  <TriFieldRow
                    mobile={isRoadmapMobile}
                    leftLabel={<FieldLabel text="Start weight" showLock={!isFirst} mobile={isRoadmapMobile} style={labelStyle} />}
                    rightLabel={<FieldLabel text="Target weight" style={labelStyle} />}
                    leftField={<WeightInputWithUnit value={phase.startWeight} disabled={!canEditPhaseFields || !isFirst} readOnly={!canEditPhaseFields || !isFirst} onChange={(e) => canEditPhaseFields && isFirst && updatePhaseField(phase.id, "startWeight", e.target.value)} style={{ ...(isFirst ? (canEditPhaseFields ? inputStyle : readOnlyInputStyle) : lockedInputStyle), padding: "0.55rem 0.65rem", fontVariantNumeric: "tabular-nums" }} step={0.1} />}
                    rightField={<WeightInputWithUnit value={phase.endWeight} disabled={!canEditPhaseFields} readOnly={!canEditPhaseFields} onChange={(e) => canEditPhaseFields && updatePhaseField(phase.id, "endWeight", e.target.value)} style={{ ...(canEditPhaseFields ? inputStyle : readOnlyInputStyle), padding: "0.55rem 0.65rem", fontVariantNumeric: "tabular-nums" }} step={0.1} />}
                    chip={<DeltaChip startWeight={phase.startWeight} endWeight={phase.endWeight} />}
                  />
                  <TriFieldRow
                    mobile={isRoadmapMobile}
                    leftLabel={<FieldLabel text="Start date" showLock={!isFirst} mobile={isRoadmapMobile} style={labelStyle} />}
                    rightLabel={<FieldLabel text="End date" style={labelStyle} />}
                    leftField={<MobileDateField value={phase.startDate} disabled={!canEditPhaseFields || !isFirst} readOnly={!canEditPhaseFields || !isFirst} onCommit={(v) => canEditPhaseFields && isFirst && updatePhaseField(phase.id, "startDate", v)} style={{ ...(isFirst ? (canEditPhaseFields ? inputStyle : readOnlyInputStyle) : lockedInputStyle), padding: "0.55rem 0.5rem", width: "100%", minWidth: 0, maxWidth: "100%", boxSizing: "border-box", display: "block", WebkitAppearance: "none" as any }} />}
                    rightField={<MobileDateField value={phase.endDate} min={phase.startDate} disabled={!canEditPhaseFields} readOnly={!canEditPhaseFields} onCommit={(v) => canEditPhaseFields && updatePhaseField(phase.id, "endDate", v)} style={{ ...(canEditPhaseFields ? inputStyle : readOnlyInputStyle), padding: "0.55rem 0.5rem", borderColor: phase.endDate <= phase.startDate ? "rgba(239,68,68,0.5)" : "rgba(255,255,255,0.14)", width: "100%", minWidth: 0, maxWidth: "100%", boxSizing: "border-box", display: "block", WebkitAppearance: "none" as any }} />}
                    chip={<DurationChip startDate={phase.startDate} endDate={phase.endDate} />}
                  />
                  <div style={{ display: "grid", gap: "0.25rem" }}>
                    {isRoadmapMobile ? (
                      <div style={{ display: "flex", justifyContent: "flex-start", flexWrap: "wrap" }}>
                        <DurationPresetChips
                          startDate={phase.startDate}
                          endDate={phase.endDate}
                          disabled={!canEditPhaseFields}
                          onSelect={(nextEndDate) => canEditPhaseFields && updatePhaseField(phase.id, "endDate", nextEndDate)}
                        />
                      </div>
                    ) : (
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 86px", gap: "0.28rem 0.55rem" }}>
                        <div />
                        <DurationPresetChips
                          startDate={phase.startDate}
                          endDate={phase.endDate}
                          disabled={!canEditPhaseFields}
                          onSelect={(nextEndDate) => canEditPhaseFields && updatePhaseField(phase.id, "endDate", nextEndDate)}
                        />
                        <div />
                      </div>
                    )}
                    {phase.endDate <= phase.startDate && (
                      <div style={{ fontSize: "0.72rem", color: "#fca5a5" }}>End date must be after start date.</div>
                    )}
                  </div>
                </div>

                {/* Calorie targets */}
                {metrics && (
                  <div style={{ marginTop: "1rem" }}>
                    {phase.type === "maintain" ? (
                      /* Maintain: net = 0, eat at TDEE */
                      <div style={{ display: "grid", gridTemplateColumns: isRoadmapMobile ? "repeat(2, minmax(0, 1fr))" : "repeat(auto-fill, minmax(130px, 1fr))", gap: "0.55rem" }}>
                        <div style={{ gridColumn: "1 / -1", fontSize: "0.82rem", fontWeight: 700, color: "rgba(255,255,255,0.65)" }}>Phase Targets</div>
                        <div style={{ background: `${phaseColor}18`, border: `1px solid ${phaseColor}44`, borderRadius: 10, padding: "0.6rem 0.75rem" }}>
                          <div style={{ fontSize: "0.68rem", fontWeight: 700, color: phaseColor, textTransform: "uppercase", letterSpacing: "0.04em" }}>Target Intake</div>
                          <div style={{ fontSize: "1.1rem", fontWeight: 900, marginTop: "0.2rem", color: "white" }}>
                            {phaseTdee !== null ? `${Math.round(phaseTdee).toLocaleString()} cal` : "—"}
                          </div>
                          <div style={{ fontSize: "0.68rem", color: "rgba(255,255,255,0.5)", marginTop: "0.15rem" }}>per day · eat at TDEE</div>
                        </div>
                        <div style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 10, padding: "0.6rem 0.75rem" }}>
                          <div style={{ fontSize: "0.68rem", fontWeight: 700, color: "rgba(255,255,255,0.5)", textTransform: "uppercase", letterSpacing: "0.04em" }}>Net Calories</div>
                          <div style={{ fontSize: "1.1rem", fontWeight: 800, marginTop: "0.2rem", color: "rgba(255,255,255,0.85)" }}>+0 cal</div>
                          <div style={{ fontSize: "0.68rem", color: "rgba(255,255,255,0.5)", marginTop: "0.15rem" }}>per day</div>
                        </div>
                        <div style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 10, padding: "0.6rem 0.75rem" }}>
                          <div style={{ fontSize: "0.68rem", fontWeight: 700, color: "rgba(255,255,255,0.5)", textTransform: "uppercase", letterSpacing: "0.04em" }}>TDEE (at phase start)</div>
                          <div style={{ fontSize: "1.1rem", fontWeight: 800, marginTop: "0.2rem", color: "rgba(255,255,255,0.85)" }}>
                            {phaseTdee !== null ? `${Math.round(phaseTdee).toLocaleString()} cal` : "—"}
                          </div>
                          <div style={{ fontSize: "0.68rem", color: "rgba(255,255,255,0.5)", marginTop: "0.15rem" }}>{phase.startWeight.toFixed(1)} lb · {ACTIVITY_LEVELS.find((a) => a.factor === vitals.activityFactor)?.label ?? "custom"}</div>
                        </div>
                      </div>
                    ) : (
                      /* Cut / Bulk: four metric cards, no toggle */
                      <div style={{ display: "grid", gridTemplateColumns: isRoadmapMobile ? "repeat(2, minmax(0, 1fr))" : "repeat(auto-fill, minmax(130px, 1fr))", gap: "0.55rem" }}>
                        <div style={{ gridColumn: "1 / -1", fontSize: "0.82rem", fontWeight: 700, color: "rgba(255,255,255,0.65)" }}>Phase Targets</div>
                        {/* Pace */}
                        <div style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 10, padding: "0.6rem 0.75rem" }}>
                          <div style={{ fontSize: "0.68rem", fontWeight: 700, color: "rgba(255,255,255,0.5)", textTransform: "uppercase", letterSpacing: "0.04em" }}>Pace</div>
                          <div style={{ fontSize: "1.1rem", fontWeight: 800, marginTop: "0.2rem", color: "rgba(255,255,255,0.85)", display: "flex", alignItems: "center", gap: "0.4rem", flexWrap: "wrap" }}>
                            <span>{formatSignedPace(metrics.rateLbsPerWeek, 1)}</span>
                            {rateBand && (() => {
                              const bandColorMap: Record<RateBandSeverity, string> = { conservative: "#22c55e", moderate: "#3b82f6", aggressive: "#f59e0b", extreme: "#ef4444" };
                              const bc = bandColorMap[rateBand.severity];
                              return <span style={{ background: `${bc}22`, border: `1px solid ${bc}44`, color: bc, borderRadius: 5, padding: "0.1rem 0.4rem", fontWeight: 700, fontSize: "0.68rem" }}>{getPaceBandDisplayLabel(rateBand.label)}</span>;
                            })()}
                          </div>
                        </div>
                        {/* Target Intake */}
                        <div style={{ background: `${phaseColor}18`, border: `1px solid ${phaseColor}44`, borderRadius: 10, padding: "0.6rem 0.75rem" }}>
                          <div style={{ fontSize: "0.68rem", fontWeight: 700, color: phaseColor, textTransform: "uppercase", letterSpacing: "0.04em" }}>Target Intake</div>
                          <div style={{ fontSize: "1.1rem", fontWeight: 900, marginTop: "0.2rem", color: "white" }}>
                            {metrics.targetIntake !== null ? `${Math.round(metrics.targetIntake).toLocaleString()} cal` : "—"}
                          </div>
                          <div style={{ fontSize: "0.68rem", color: "rgba(255,255,255,0.5)", marginTop: "0.15rem" }}>per day</div>
                        </div>
                        {/* Net Calories */}
                        <div style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 10, padding: "0.6rem 0.75rem" }}>
                          <div style={{ fontSize: "0.68rem", fontWeight: 700, color: "rgba(255,255,255,0.5)", textTransform: "uppercase", letterSpacing: "0.04em" }}>Net Calories</div>
                          <div style={{ fontSize: "1.1rem", fontWeight: 800, marginTop: "0.2rem", color: "rgba(255,255,255,0.85)" }}>
                            {`${metrics.targetNet >= 0 ? "+" : ""}${Math.round(metrics.targetNet).toLocaleString()} cal`}
                          </div>
                          <div style={{ fontSize: "0.68rem", color: "rgba(255,255,255,0.5)", marginTop: "0.15rem" }}>per day</div>
                        </div>
                        {/* TDEE */}
                        <div style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 10, padding: "0.6rem 0.75rem" }}>
                          <div style={{ fontSize: "0.68rem", fontWeight: 700, color: "rgba(255,255,255,0.5)", textTransform: "uppercase", letterSpacing: "0.04em" }}>TDEE (at phase start)</div>
                          <div style={{ fontSize: "1.1rem", fontWeight: 800, marginTop: "0.2rem", color: "rgba(255,255,255,0.85)" }}>
                            {phaseTdee !== null ? `${Math.round(phaseTdee).toLocaleString()} cal` : "—"}
                          </div>
                          <div style={{ fontSize: "0.68rem", color: "rgba(255,255,255,0.5)", marginTop: "0.15rem" }}>{phase.startWeight.toFixed(1)} lb · {ACTIVITY_LEVELS.find((a) => a.factor === vitals.activityFactor)?.label ?? "custom"}</div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Guardrail warnings + rate band extreme + phase type mismatch */}
                {(warnings.length > 0 || phaseTypeWarning) && (
                  <div style={{ marginTop: "0.85rem", display: "grid", gap: "0.4rem" }}>
                    {warnings.map((w) => (
                      <div key={w} style={{ display: "flex", alignItems: "center", gap: "0.5rem", background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.25)", borderRadius: 8, padding: "0.5rem 0.75rem", fontSize: "0.82rem", color: "#fca5a5" }}>
                        ⚠ {w}
                      </div>
                    ))}
                    {phaseTypeWarning && (
                      <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", background: "rgba(59,130,246,0.1)", border: "1px solid rgba(59,130,246,0.25)", borderRadius: 8, padding: "0.5rem 0.75rem", fontSize: "0.82rem", color: "#93c5fd" }}>
                        ℹ {phaseTypeWarning}
                      </div>
                    )}
                  </div>
                )}
                  </>
                )}
              </div>
            );
          })
        ) : null}
        {!roadmap ? (
          <div style={{ ...cardStyle, borderRadius: 18, textAlign: "center", padding: "3rem" }}>
            <div style={{ marginBottom: "1rem", display: "flex", justifyContent: "center" }}>
              <svg viewBox="0 0 24 24" width="52" height="52" fill="none" stroke="rgba(255,255,255,0.35)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="m3 6 6-2 6 2 6-2v14l-6 2-6-2-6 2z" />
                <path d="M9 4v14" />
                <path d="M15 6v14" />
              </svg>
            </div>
            <div style={{ fontSize: "1.25rem", fontWeight: 800, marginBottom: "0.6rem" }}>No Roadmap Yet</div>
            <div style={{ color: "rgba(255,255,255,0.55)", maxWidth: 400, margin: "0 auto 1.5rem" }}>
              Create a multi-phase plan that connects cut, bulk, and maintenance cycles.
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem", alignItems: "center", maxWidth: 320, margin: "0 auto" }}>
              {activeSprint && (
                <button style={{ ...btnBase, background: `linear-gradient(135deg, ${COLORS.orange}, #f97316)`, color: "white", padding: "0.75rem 1.5rem", width: "100%" }} onClick={createRoadmapFromSprint}>
                  Use Active Sprint
                </button>
              )}
              <button style={{ ...btnBase, background: actionColors.primaryGradient, color: actionColors.primaryText, borderColor: "rgba(148,163,184,0.45)", padding: "0.75rem 1.5rem", width: "100%" }} onClick={openRoadmapWizard}>
                Build Roadmap
              </button>
            </div>
          </div>
        ) : null}

        {roadmapPhaseBuilderDraft && (
          <ModalShell
            title={`Add Phase ${roadmapPhaseBuilderDraft.orderIndex + 1}`}
            ariaLabel="Add roadmap phase"
            onClose={closeRoadmapPhaseBuilder}
            panelStyle={{ width: "min(640px, calc(100vw - 32px))" }}
          >
            <div style={{ marginTop: "0.8rem", display: "grid", gap: "0.85rem", minWidth: 0 }}>
              {roadmapPhaseBuilderDraft.startValuesInherited ? (
                <div style={{ fontSize: "0.74rem", color: "rgba(255,255,255,0.62)", lineHeight: 1.4 }}>
                  New phases start where the previous phase ends. To change the start values, edit Phase {roadmapPhaseBuilderDraft.inheritedFromPhaseNumber ?? 1}.
                </div>
              ) : null}
              {renderRoadmapPhaseDetailEditor({
                row: {
                  type: roadmapPhaseBuilderDraft.type,
                  startDate: roadmapPhaseBuilderDraft.startDate,
                  startWeight: roadmapPhaseBuilderDraft.startWeight,
                  endDate: roadmapPhaseBuilderDraft.endDate,
                  targetWeight: roadmapPhaseBuilderDraft.endWeight,
                  aggressiveness: roadmapPhaseBuilderDraft.aggressiveness,
                  planMethod: roadmapPhaseBuilderDraft.planMethod,
                },
                rowIdx: roadmapPhaseBuilderDraft.orderIndex,
                onUpdate: (field, value) => {
                  const normalizedField = field === "targetWeight" ? "endWeight" : field;
                  updateRoadmapPhaseBuilderDraft(normalizedField as Parameters<typeof updateRoadmapPhaseBuilderDraft>[0], value);
                },
                startFieldsLocked: roadmapPhaseBuilderDraft.startValuesInherited,
                startFieldsHelperCopy: null,
              })}

              <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.65rem", marginTop: "0.2rem" }}>
                <button
                  type="button"
                  onClick={closeRoadmapPhaseBuilder}
                  style={{ ...btnBase, background: "rgba(255,255,255,0.06)", color: "white", padding: "0.5rem 0.95rem" }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={commitRoadmapPhaseBuilder}
                  style={{ ...btnBase, background: actionColors.primaryGradient, color: actionColors.primaryText, borderColor: "rgba(148,163,184,0.45)", padding: "0.5rem 0.95rem" }}
                >
                  Add phase
                </button>
              </div>
            </div>
          </ModalShell>
        )}

        {/* Sync Active Sprint confirmation modal */}
        {syncSprintConfirmOpen && (
          <ModalShell
            title="Replace Phase 1?"
            ariaLabel="Sync sprint confirmation"
            onClose={() => setSyncSprintConfirmOpen(false)}
            panelStyle={{ width: "min(480px, calc(100vw - 32px))" }}
          >
            <div style={{ marginTop: "0.75rem", color: "rgba(226,232,240,0.95)", display: "grid", gap: "0.75rem" }}>
              <div>
                Phase 1's dates, weights, and type will be replaced with data from your active
                sprint. Later phases will be pushed forward automatically.
              </div>
              {syncSprintConflicts.length > 0 && (
                <div>
                  <div style={{ color: "#fca5a5", fontWeight: 700, marginBottom: "0.4rem" }}>
                    This will also cause issues with later phases:
                  </div>
                  <ul style={{ paddingLeft: "1.2rem", display: "grid", gap: "0.35rem", color: "#fca5a5" }}>
                    {syncSprintConflicts.map((c) => <li key={c}>{c}</li>)}
                  </ul>
                </div>
              )}
              <div style={{ display: "flex", gap: "0.75rem", justifyContent: "flex-end", marginTop: "0.25rem" }}>
                <button
                  style={{ ...btnBase, background: "rgba(255,255,255,0.06)", color: "white", padding: "0.5rem 1rem" }}
                  onClick={() => setSyncSprintConfirmOpen(false)}
                >
                  Cancel
                </button>
                <button
                  style={{ ...btnBase, background: `linear-gradient(135deg, ${COLORS.orange}, #f97316)`, color: "white", padding: "0.5rem 1rem" }}
                  onClick={() => { void syncActiveSprintToPhase1(); setSyncSprintConfirmOpen(false); }}
                >
                  Replace Phase 1
                </button>
              </div>
            </div>
          </ModalShell>
        )}

        {syncedPhaseEditInfoOpen && (
          <ModalShell
            title="Edit linked sprint"
            ariaLabel="Synced phase edit guidance"
            onClose={() => setSyncedPhaseEditInfoOpen(false)}
            panelStyle={{ width: "min(480px, calc(100vw - 32px))" }}
          >
            <div style={{ marginTop: "0.75rem", color: "rgba(226,232,240,0.95)", display: "grid", gap: "0.75rem" }}>
              <div>
                This phase is synced to a sprint, so Roadmap editing is locked for it.
              </div>
              <div style={{ color: "rgba(255,255,255,0.72)" }}>
                To edit it, open <strong>Dashboard</strong> and use <strong>Edit Sprint</strong> on the linked sprint.
              </div>
              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <button style={{ ...btnBase, background: "rgba(255,255,255,0.06)", color: "white", padding: "0.5rem 1rem" }} onClick={() => setSyncedPhaseEditInfoOpen(false)}>
                  Got it
                </button>
              </div>
            </div>
          </ModalShell>
        )}

        {/* Cycle Builder Wizard Modal */}
        {roadmapWizardOpen && (
          <ModalShell
            title="Build Cycle"
            ariaLabel="Cycle Builder Wizard"
            onClose={closeRoadmapWizard}
            panelStyle={{ width: "min(640px, calc(100vw - 32px))", maxHeight: "90vh" }}
          >
            {/* Step indicator */}
            <div style={{ display: "flex", gap: "0.5rem", marginTop: isMobile ? "0.2rem" : "0.5rem", marginBottom: isMobile ? "0.7rem" : "1.25rem", alignItems: "center" }}>
              {["Pattern", "Phase Details", "Preview"].map((label, si) => (
                <React.Fragment key={label}>
                  <div style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
                    <div style={{
                      width: 26, height: 26, borderRadius: 999, flexShrink: 0,
                      background: rmWizardStep >= si ? `linear-gradient(135deg, ${COLORS.blue}, ${COLORS.lightBlue})` : "rgba(255,255,255,0.08)",
                      color: "white", display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: "0.72rem", fontWeight: 800,
                    }}>{si + 1}</div>
                    <div style={{ fontSize: "0.78rem", color: rmWizardStep >= si ? "rgba(255,255,255,0.9)" : "rgba(255,255,255,0.3)", fontWeight: rmWizardStep === si ? 700 : 500, whiteSpace: "nowrap" }}>{label}</div>
                  </div>
                  {si < 2 && <div style={{ flex: 1, height: 1, background: "rgba(255,255,255,0.1)" }} />}
                </React.Fragment>
              ))}
            </div>

            {/* Step 0: Choose pattern */}
            {rmWizardStep === 0 && (
              <div style={{ display: "grid", gap: "0.6rem" }}>
                <div style={{ fontSize: "0.82rem", color: "rgba(255,255,255,0.55)", marginBottom: "0.25rem" }}>Choose your training cycle pattern:</div>
                {WIZARD_PATTERN_OPTIONS.map((opt) => (
                  <button
                    key={opt.key}
                    onClick={() => {
                      const recentEntry = [...entriesAsc].reverse().find((e) => e.weight !== null && e.date <= todayYmd);
                      const sw = recentEntry?.weight ?? safeNumber(rmWizardPhaseRows[0]?.startWeight) ?? 175;
                      setRmWizardPhaseIdx(0);
                      setRmWizardPattern(opt.key);
                      setRmWizardPhaseRows(initWizardRows(opt.key, todayYmd, sw));
                    }}
                    style={{
                      ...btnBase,
                      display: "flex", alignItems: "center", gap: "0.85rem",
                      padding: "0.75rem 1rem", textAlign: "left",
                      background: rmWizardPattern === opt.key ? "rgba(59,130,246,0.2)" : "rgba(255,255,255,0.04)",
                      borderColor: rmWizardPattern === opt.key ? "rgba(59,130,246,0.5)" : "rgba(255,255,255,0.1)",
                      color: "white",
                    }}
                  >
                    <div style={{ fontSize: "1.3rem", lineHeight: 1, flexShrink: 0 }}>{opt.icon}</div>
                    <div>
                      <div style={{ fontWeight: 800, fontSize: "0.9rem" }}>{opt.label}</div>
                      <div style={{ fontSize: "0.75rem", color: "rgba(255,255,255,0.5)", fontWeight: 500, marginTop: "0.1rem" }}>{opt.desc}</div>
                    </div>
                  </button>
                ))}
              </div>
            )}

            {/* Step 1: Per-phase details */}
            {rmWizardStep === 1 && (
              <div style={{ display: "grid", gap: "1rem" }}>
                {rmWizardPhaseRows.filter((_, idx) => idx === rmWizardPhaseIdx).map((row, _renderIdx) => {
                  const rowIdx = rmWizardPhaseIdx;
                  return (
                    <React.Fragment key={rowIdx}>
                      {renderRoadmapPhaseDetailEditor({
                        row,
                        rowIdx,
                        onUpdate: (field, value) => updateWizardRow(rowIdx, field as keyof WizardPhaseRow | "planMethod", value),
                        startFieldsLocked: rowIdx !== 0,
                      })}
                    </React.Fragment>
                  );
                })}
                {rmWizardPhaseRows.length > 1 && (
                  <div style={{ display: "grid", gap: "0.6rem", justifyItems: "center" }}>
                    <div style={{ display: "flex", gap: "0.32rem", alignItems: "center" }}>
                      {rmWizardPhaseRows.map((phase, idx) => (
                        <span
                          key={phase.id}
                          aria-hidden="true"
                          style={{
                            width: 7,
                            height: 7,
                            borderRadius: 999,
                            background: idx === rmWizardPhaseIdx ? "rgba(59,130,246,0.95)" : "rgba(255,255,255,0.24)",
                          }}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Step 2: Preview */}
            {rmWizardStep === 2 && (() => {
              if (!rmWizardPhaseRows.length) return <div style={{ color: "rgba(255,255,255,0.55)" }}>No phases defined. Go back to configure details.</div>;
              const previewPhases = wizardRowsToPhases(rmWizardPhaseRows);
              return (
                <div style={{ display: "grid", gap: "0.7rem" }}>
                  <div style={{ fontSize: "0.82rem", color: "rgba(255,255,255,0.55)" }}>
                    {previewPhases.length} phase{previewPhases.length !== 1 ? "s" : ""} · {formatMDY(previewPhases[0]?.startDate ?? todayYmd)} → {formatMDY(previewPhases[previewPhases.length - 1]?.endDate ?? todayYmd)}
                  </div>
                  {previewPhases.map((p, j) => {
                    // Use per-phase start weight for BMR/TDEE accuracy
                    const phaseBmr = calcBMR({ weightLbs: p.startWeight, heightIn: vitals.height, gender: vitals.gender, birthday: vitals.birthday });
                    const phaseTdee = phaseBmr !== null ? calcTDEEFromFactor({ bmr: phaseBmr, factor: vitals.activityFactor ?? 1.3 }) : null;
                    const m = calcPhaseMetrics(p, phaseTdee);
                    const phaseColor = ROADMAP_PHASE_COLORS[p.type];
                    const startDt = safeParseYmd(p.startDate);
                    const endDt = safeParseYmd(p.endDate);
                    const days = (startDt && endDt) ? Math.round((endDt.getTime() - startDt.getTime()) / 86400000) : null;
                    const weeks = days !== null ? Math.round((days / 7) * 10) / 10 : null;
                    return (
                      <div key={p.id} style={{ borderRadius: 12, border: `1px solid ${phaseColor}44`, background: `${phaseColor}0e`, padding: "0.9rem 1rem" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", marginBottom: "0.6rem" }}>
                          <div style={{ width: 10, height: 10, borderRadius: 999, background: phaseColor, flexShrink: 0 }} />
                          <div style={{ fontWeight: 800, fontSize: "0.9rem" }}>Phase {j + 1}: {ROADMAP_PHASE_LABELS[p.type]}</div>
                          {weeks !== null && <div style={{ marginLeft: "auto", fontSize: "0.72rem", color: "rgba(255,255,255,0.5)" }}>{weeks}w</div>}
                        </div>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.5rem" }}>
                          <div style={{ background: "rgba(255,255,255,0.04)", borderRadius: 8, padding: "0.5rem 0.65rem" }}>
                            <div style={{ fontSize: "0.65rem", color: "rgba(255,255,255,0.45)", fontWeight: 700, textTransform: "uppercase" }}>Start</div>
                            <div style={{ fontSize: "0.88rem", fontWeight: 700, marginTop: "0.15rem" }}>{formatMDY(p.startDate)}</div>
                            <div style={{ fontSize: "0.82rem", color: "rgba(255,255,255,0.75)" }}>{p.startWeight.toFixed(1)} lb</div>
                          </div>
                          <div style={{ background: "rgba(255,255,255,0.04)", borderRadius: 8, padding: "0.5rem 0.65rem" }}>
                            <div style={{ fontSize: "0.65rem", color: "rgba(255,255,255,0.45)", fontWeight: 700, textTransform: "uppercase" }}>End</div>
                            <div style={{ fontSize: "0.88rem", fontWeight: 700, marginTop: "0.15rem" }}>{formatMDY(p.endDate)}</div>
                            <div style={{ fontSize: "0.82rem", color: "rgba(255,255,255,0.75)" }}>{p.endWeight.toFixed(1)} lb</div>
                          </div>
                        </div>
                        {m && (
                          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "0.4rem", marginTop: "0.5rem" }}>
                            <div style={{ background: `${phaseColor}18`, borderRadius: 8, padding: "0.45rem 0.6rem" }}>
                              <div style={{ fontSize: "0.62rem", color: phaseColor, fontWeight: 700, textTransform: "uppercase" }}>Net cal/day</div>
                              <div style={{ fontSize: "0.88rem", fontWeight: 800, marginTop: "0.1rem" }}>{m.targetNet >= 0 ? "+" : ""}{Math.round(m.targetNet).toLocaleString()}</div>
                            </div>
                            <div style={{ background: "rgba(255,255,255,0.04)", borderRadius: 8, padding: "0.45rem 0.6rem" }}>
                              <div style={{ fontSize: "0.62rem", color: "rgba(255,255,255,0.45)", fontWeight: 700, textTransform: "uppercase" }}>Intake/day</div>
                              <div style={{ fontSize: "0.88rem", fontWeight: 800, marginTop: "0.1rem" }}>{m.targetIntake !== null ? Math.round(m.targetIntake).toLocaleString() : "—"}</div>
                            </div>
                            <div style={{ background: "rgba(255,255,255,0.04)", borderRadius: 8, padding: "0.45rem 0.6rem" }}>
                              <div style={{ fontSize: "0.62rem", color: "rgba(255,255,255,0.45)", fontWeight: 700, textTransform: "uppercase" }}>TDEE</div>
                              <div style={{ fontSize: "0.88rem", fontWeight: 800, marginTop: "0.1rem" }}>{phaseTdee !== null ? Math.round(phaseTdee).toLocaleString() : "—"}</div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })()}

            {/* Footer buttons */}
            <div style={{ marginTop: "1rem", paddingTop: "0.85rem", borderTop: "1px solid rgba(255,255,255,0.09)", display: "flex", justifyContent: "space-between", gap: "0.6rem" }}>
              <button
                style={{ ...btnBase, background: "rgba(255,255,255,0.08)", color: "white" }}
                onClick={() => {
                  if (rmWizardStep === 0) closeRoadmapWizard();
                  else if (rmWizardStep === 1 && rmWizardPhaseIdx > 0) setRmWizardPhaseIdx((idx) => Math.max(0, idx - 1));
                  else setRmWizardStep((s) => Math.max(0, s - 1));
                }}
              >
                {rmWizardStep === 0 ? "Cancel" : rmWizardStep === 1 && rmWizardPhaseIdx > 0 ? "← Previous Phase" : "← Back"}
              </button>
              {rmWizardStep < 2 ? (
                <button
                  style={{ ...btnBase, background: actionColors.primaryGradient, color: actionColors.primaryText, borderColor: "rgba(148,163,184,0.45)" }}
                  onClick={() => {
                    if (rmWizardStep === 1 && rmWizardPhaseIdx < rmWizardPhaseRows.length - 1) {
                      setRmWizardPhaseIdx((idx) => Math.min(rmWizardPhaseRows.length - 1, idx + 1));
                      return;
                    }
                    setRmWizardStep((s) => s + 1);
                  }}
                >
                  {rmWizardStep === 1
                    ? (rmWizardPhaseIdx < rmWizardPhaseRows.length - 1 ? "Next Phase" : "Preview Plan")
                    : "Next →"}
                </button>
              ) : (
                <button
                  style={{ ...btnBase, background: actionColors.primaryGradient, color: actionColors.primaryText, borderColor: "rgba(148,163,184,0.45)" }}
                  onClick={submitRoadmapWizard}
                >
                  Create Roadmap
                </button>
              )}
            </div>
          </ModalShell>
        )}
      </div>
    );
  })();

  let body = trackerPage;
  switch (currentPage) {
    case "trends":
      body = trendsPage;
      break;
    case "all":
      body = allDataPage;
      break;
    case "sprints":
      body = sprintsPage;
      break;
    case "settings":
      body = settingsPage;
      break;
    case "viewer":
      body = viewerPage;
      break;
    case "roadmap":
      body = roadmapPage;
      break;
    case "tracker":
    default:
      body = trackerPage;
      break;
  }

  const primaryNavItems = [
    { key: "tracker", label: "Dashboard", icon: <HomeIcon /> },
    { key: "sprints", label: "Sprints", icon: <FlagIcon /> },
    { key: "roadmap", label: "Roadmap", icon: <MapIcon /> },
    { key: "trends", label: "Trends", icon: <ChartIcon /> },
    { key: "all", label: "Logbook", icon: <HistoryIcon /> },
  ];
  const mobileNavItems = [
    { key: "tracker", label: "Dashboard", icon: <HomeIcon /> },
    { key: "sprints", label: "Sprints", icon: <FlagIcon /> },
    { key: "roadmap", label: "Roadmap", icon: <MapIcon /> },
    { key: "trends", label: "Trends", icon: <ChartIcon /> },
  ];
  const isAuthenticated = !!userId;
  const profileName = (vitals.displayName?.trim() || userEmail?.split("@")[0] || (isAuthenticated ? "Account" : "Local")).trim() || (isAuthenticated ? "Account" : "Local");
  const syncStatusLabel = !isAuthenticated
    ? "Local mode (not synced)"
    : sbStatus === "syncing"
      ? "Syncing"
      : sbError || sbStatus === "error"
        ? "Offline"
        : "Synced";
  const syncStatusTone: "good" | "warn" | "neutral" = !isAuthenticated
    ? "neutral"
    : sbError || sbStatus === "error"
      ? "warn"
      : "good";
  const syncStatus: "local" | "synced" | "syncing" | "offline" = !isAuthenticated
    ? "local"
    : sbStatus === "syncing"
      ? "syncing"
      : sbError || sbStatus === "error"
        ? "offline"
        : "synced";

  const demoHeaderTop = isDemoMode ? (
    <div style={{ display: "flex", alignItems: "center", width: "100%", minWidth: 0, overflow: "hidden" }}>
      {/* Left: "Demo" pill — fixed, never grows */}
      <span style={{ flexShrink: 0, fontSize: 11, fontWeight: 800, color: "#fde68a", background: "rgba(250,204,21,0.18)", border: "1px solid rgba(250,204,21,0.4)", borderRadius: 999, padding: "3px 10px", letterSpacing: "0.04em", whiteSpace: "nowrap" }}>
        Demo Mode
      </span>
      {/* Center: fixed-width selector — label changes never cause layout shift */}
      <div style={{ flex: 1, display: "flex", justifyContent: "center", minWidth: 0, padding: "0 8px" }}>
        <div style={{ position: "relative", width: 110, flexShrink: 0 }}>
          <select
            value={demoProfileId}
            onChange={(e) => enterDemoMode(e.target.value as DemoProfileId, demoEntrySource)}
            style={{ appearance: "none", WebkitAppearance: "none", width: "100%", background: "rgba(255,255,255,0.07)", color: "white", border: "1px solid rgba(255,255,255,0.2)", borderRadius: 8, padding: "0.3rem 1.8rem 0.3rem 0.65rem", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "var(--font-sans)" }}
          >
            <option value="cut_ahead">Cut</option>
            <option value="bulk_lean">Bulk</option>
            <option value="maintain">Maintain</option>
          </select>
          <svg viewBox="0 0 10 6" style={{ position: "absolute", right: 7, top: "50%", transform: "translateY(-50%)", width: 10, height: 6, fill: "rgba(255,255,255,0.5)", pointerEvents: "none" }} aria-hidden="true"><path d="M0 0l5 6 5-6z" /></svg>
        </div>
      </div>
      {/* Right: Exit — plain action, always reserved space */}
      <button
        onClick={() => { void exitDemoMode(); }}
        style={{ flexShrink: 0, background: "transparent", border: "1px solid rgba(255,255,255,0.18)", color: "rgba(229,231,235,0.9)", borderRadius: 8, padding: "0.3rem 0.75rem", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "var(--font-sans)", whiteSpace: "nowrap" }}
      >
        Exit Demo
      </button>
    </div>
  ) : undefined;

  const headerSprintSummaryCard = {
    statusLabel: currentSprintStatus,
    statusTone: currentSprintStatusTone,
    phaseLabel: currentSprintPhase,
    startLabel: `Start ${isYmd(sprintStartEffective) ? formatMD(sprintStartEffective) : "—"} • ${Number.isFinite(startWeight) ? `${Number(startWeight).toFixed(1)} lbs` : "—"}`,
    goalLabel: `Goal ${isYmd(forecast.targetDate) ? formatMD(forecast.targetDate) : "—"} • ${Number.isFinite(forecast.targetWeight) ? `${Number(forecast.targetWeight).toFixed(1)} lbs` : "—"}`,
    nowLabel: `Now ${Number.isFinite(currentSprintWeight) ? `${Number(currentSprintWeight).toFixed(1)} lbs` : "—"}`,
    deltaLabel: `Δ ${Number.isFinite(currentSprintNetChange) ? `${currentSprintNetChange > 0 ? "+" : currentSprintNetChange < 0 ? "−" : ""}${Math.abs(currentSprintNetChange).toFixed(1)} lbs` : "—"}`,
    onClick: jumpToSprintSection,
  };

  // Derive a banner from the verifiedEmailNotice (success or error) to show inside the LoginModal.
  const loginModalInitialBanner = obWelcomeNotice
    ? (redirectAuthMode === "updatePassword"
        ? { variant: "info" as const, title: "Reset your password", message: obWelcomeNotice }
        : /expired|invalid|already been used/i.test(obWelcomeNotice)
          ? { variant: "error" as const, title: "Link expired", message: obWelcomeNotice }
          : { variant: "info" as const, title: "Email verified", message: obWelcomeNotice })
    : null;

  if (showLanding) {
    return (
      <>
        <LandingPage
          onCreateAccount={openSignUp}
          onSignIn={openSignIn}
          onTryDemo={tryDemoFromLanding}
          onContinueLocally={continueLocally}
        />
        {showLoginModal ? <LoginModal initialMode={loginModalMode} initialBanner={loginModalInitialBanner} initialEmail={verifiedEmailPrefill} onClose={() => { setShowLoginModal(false); setObWelcomeNotice(null); onClearVerifiedEmailNotice?.(); }} onPasswordUpdateSuccess={onPasswordRecoveryComplete} /> : null}
      </>
    );
  }

  return (
    <>
      <AppShell
        navItems={primaryNavItems}
        activeNavKey={currentPage === "viewer" ? "sprints" : currentPage}
        onSelectNav={(key) => {
          setCurrentPage(key);
          setLogbookOpenedFromSettings(false);
          if (key === "settings") {
            setSettingsInitialSection("profile");
            setSettingsActiveSection("profile");
          }
          if (key !== "viewer") setViewerSprintId(null);
        }}
        mobileBottomNavItems={showOnboarding ? undefined : mobileNavItems}
        activeMobileNavKey={currentPage === "viewer" ? "sprints" : currentPage}
        mobileLogButton={{ onPress: () => setShowMobileLogModal(true) }}
        sidebarPrimaryAction={{
          label: "Log Daily Entry",
          onPress: () => setShowMobileLogModal(true),
          icon: (
            <svg viewBox="0 0 24 24" style={{ width: 18, height: 18, stroke: "currentColor", strokeWidth: 2.2, fill: "none", strokeLinecap: "round", strokeLinejoin: "round" }}>
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
          ),
        }}
        headerSprintLabel={activeSprint?.name || "Sprint" /* TODO: wire to dedicated sprint selector store if introduced */}
        headerSprintModeTag={currentSprintPhase || null}
        profileName={profileName}
        profileEmail={userEmail}
        isAuthenticated={isAuthenticated}
        avatarKey={vitals.avatarKey ?? ""}
        syncStatus={syncStatus}
        lastSyncAt={sbLastSyncAt}
        syncError={sbError ?? null}
        onSettings={() => setCurrentPage("settings")}
        hideSettingsButton={currentPage === "settings"}
        headerTopOverride={demoHeaderTop}
        headerLeftOverride={currentPage === "settings" ? (
          <div style={{ fontSize: "1.4rem", fontWeight: 900, letterSpacing: "-0.02em", color: "rgba(255,255,255,0.97)" }}>
            Settings
          </div>
        ) : undefined}
        headerBottomContent={currentPage === "settings" && isCompactMobile ? (
          <SettingsHeaderTabs activeSection={settingsActiveSection} onSelect={setSettingsActiveSection} />
        ) : undefined}
        sprintSummaryCard={headerSprintSummaryCard}
        appVersion="0.0.0"
        termsUrl="https://weightplanner.fit/terms"
        privacyUrl="https://weightplanner.fit/privacy"
        supportUrl="https://weightplanner.fit/support"
        profileActions={isAuthenticated
          ? [
            { key: "settings", label: "Settings", onSelect: () => setCurrentPage("settings") },
            { key: "logout", label: "Sign out", onSelect: () => { void handleSupabaseSignOut(); } },
          ]
          : [
            { key: "settings", label: "Settings", onSelect: () => setCurrentPage("settings") },
            { key: "login", label: "Log in", onSelect: openSignIn },
          ]}
      >
        <div style={{ maxWidth: 1600, margin: "0 auto", display: "grid", gap: "1.85rem", minWidth: 0, width: "100%" }}>
          {header}
          <div style={{ minWidth: 0, overflow: currentPage === "settings" ? "visible" : "hidden" }}>{body}</div>
        </div>
      </AppShell>

      {/* Mobile log entry modal */}
      {showMobileLogModal ? (
        <ModalShell
          title="Daily Log"
          ariaLabel="daily log"
          onClose={() => {
            setIsDailyEntryDatePickerOpen(false);
            setShowMobileLogModal(false);
          }}
          panelStyle={{
            maxWidth: "min(480px, calc(100vw - 24px))",
            padding: 18,
            transition: "box-shadow 160ms ease, border-color 160ms ease, transform 160ms ease",
            boxShadow: isDailyEntryDatePickerOpen
              ? "0 28px 80px rgba(2,6,23,0.74), 0 0 0 1px rgba(96,165,250,0.18)"
              : modalPanelStyle.boxShadow,
            borderColor: isDailyEntryDatePickerOpen ? "rgba(96,165,250,0.2)" : modalPanelStyle.borderColor,
          }}
          backdropStyle={isDailyEntryDatePickerOpen
            ? {
                background: "rgba(2,6,23,0.76)",
                backdropFilter: "blur(14px) saturate(0.9)",
                WebkitBackdropFilter: "blur(14px) saturate(0.9)",
              }
            : undefined}
        >
          {dailyEntryCard}
        </ModalShell>
      ) : null}

      {/* Success toasts */}
      {authSuccessNotice ? (
        <div style={{ position: "fixed", bottom: isMobile ? 140 : 84, left: "50%", transform: "translateX(-50%)", zIndex: 10000, background: "rgba(59,130,246,0.2)", border: "1px solid rgba(59,130,246,0.42)", color: "#dbeafe", borderRadius: 12, padding: "0.65rem 1.2rem", fontWeight: 700, fontSize: "0.9rem", backdropFilter: "blur(8px)", display: "flex", alignItems: "center", gap: "0.5rem", pointerEvents: "none", whiteSpace: "nowrap" }}>
          <span style={{ fontSize: "1.1em" }}>✓</span> {authSuccessNotice}
        </div>
      ) : null}
      {logSuccessToast ? (
        <div style={{ position: "fixed", bottom: isMobile ? 90 : 24, left: "50%", transform: "translateX(-50%)", zIndex: 9999, background: "rgba(34,197,94,0.18)", border: "1px solid rgba(34,197,94,0.45)", color: "#86efac", borderRadius: 12, padding: "0.65rem 1.2rem", fontWeight: 700, fontSize: "0.9rem", backdropFilter: "blur(8px)", display: "flex", alignItems: "center", gap: "0.5rem", pointerEvents: "none", whiteSpace: "nowrap" }}>
          <span style={{ fontSize: "1.1em" }}>✓</span> {logSuccessToast}
        </div>
       ) : null}

      {sprintSaveToast ? <div style={{ position: "fixed", bottom: isMobile ? 124 : 68, left: "50%", transform: "translateX(-50%)", zIndex: 9999, background: "rgba(59,130,246,0.2)", border: "1px solid rgba(59,130,246,0.42)", color: "#dbeafe", borderRadius: 12, padding: "0.55rem 0.95rem", fontWeight: 700, fontSize: "0.82rem", pointerEvents: "none" }}>{sprintSaveToast}</div> : null}

      {confirmOpen ? (
        <ModalShell title="This entry looks unusual" ariaLabel="Unusual entry confirmation" onClose={cancelConfirm} panelStyle={{ width: "min(560px, calc(100vw - 32px))", maxHeight: "90vh", overflowY: "auto" }}>
          <div style={{ marginTop: "0.75rem", color: "rgba(226,232,240,0.95)" }}>
            Please review these values before saving:
          </div>
          <ul style={{ marginTop: "0.75rem", marginBottom: "1rem", paddingLeft: "1.2rem", display: "grid", gap: "0.45rem", color: "rgba(226,232,240,0.95)" }}>
            {confirmLines.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.6rem" }}>
            <button type="button" style={{ ...btnBase, background: "rgba(255,255,255,0.08)", color: "white" }} onClick={cancelConfirm}>
              Cancel
            </button>
            <button
              type="button"
              style={{ ...btnBase, background: brand.state.danger, color: "white", borderColor: "rgba(239,68,68,0.6)" }}
              onClick={continueConfirm}
            >
              Continue anyway
            </button>
          </div>
        </ModalShell>
      ) : null}
      {showMetabolismWeightConfirm ? (
        <ModalShell
          title="Confirm weight update"
          ariaLabel="confirm metabolism weight update"
          onClose={cancelMetabolismWeightConfirm}
          panelStyle={{ width: "min(460px, calc(100vw - 32px))" }}
        >
          <div style={{ marginTop: "0.65rem", color: "rgba(226,232,240,0.95)", lineHeight: 1.45 }}>
            This will update today’s weight log.
          </div>
          <div style={{ marginTop: "1rem", display: "flex", justifyContent: "flex-end", gap: "0.6rem" }}>
            <button type="button" style={{ ...btnBase, background: "rgba(255,255,255,0.08)", color: "white" }} onClick={cancelMetabolismWeightConfirm}>
              Cancel
            </button>
            <button
              type="button"
              style={{ ...btnBase, background: brand.accent.primary, color: "white", borderColor: "rgba(59,130,246,0.6)" }}
              onClick={confirmMetabolismWeightConfirm}
            >
              Confirm
            </button>
          </div>
        </ModalShell>
      ) : null}
      {sprintUpdateConfirm ? (
        <ModalShell title="Review sprint updates" ariaLabel="review sprint updates" onClose={() => setSprintUpdateConfirm(null)} panelStyle={{ width: "min(560px, calc(100vw - 32px))" }}>
          <div style={{ marginTop: "0.4rem", color: "rgba(255,255,255,0.58)", fontSize: "0.83rem" }}>Confirm these changes to update your plan.</div>

          <div
            style={{
              marginTop: "0.9rem",
              borderRadius: 12,
              border: sprintUpdateConfirm.isWarning ? "1px solid rgba(251,191,36,0.32)" : "1px solid rgba(148,163,184,0.28)",
              background: sprintUpdateConfirm.isWarning ? "linear-gradient(135deg, rgba(251,191,36,0.15), rgba(249,115,22,0.06))" : "linear-gradient(135deg, rgba(148,163,184,0.14), rgba(148,163,184,0.05))",
              padding: "0.75rem 0.85rem",
              display: "grid",
              gap: "0.28rem",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "0.45rem", fontWeight: 700, color: "rgba(248,250,252,0.96)", fontSize: "0.88rem" }}>
              <span aria-hidden>{sprintUpdateConfirm.isWarning ? "⚠" : "✦"}</span>
              <span>{sprintUpdateConfirm.isWarning ? sprintUpdateConfirm.warningTitle : "Looks good."}</span>
            </div>
            <div style={{ fontSize: "0.8rem", color: sprintUpdateConfirm.isWarning ? "rgba(255,237,213,0.85)" : "rgba(226,232,240,0.72)" }}>
              {sprintUpdateConfirm.isWarning ? sprintUpdateConfirm.warningBody : "This update stays within recommended pace ranges."}
            </div>
          </div>

          {sprintUpdateConfirm.changeRows?.length ? (
            <div style={{ marginTop: "0.95rem" }}>
              <div style={{ color: "rgba(255,255,255,0.65)", fontSize: "0.74rem", textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 700 }}>What will change</div>
              <div style={{ marginTop: "0.45rem", display: "grid", gap: "0.45rem" }}>
                {sprintUpdateConfirm.changeRows.map((row: any) => (
                  <div key={row.key} style={{ border: "1px solid rgba(148,163,184,0.22)", background: "rgba(15,23,42,0.55)", borderRadius: 10, padding: "0.62rem 0.72rem", display: "flex", alignItems: "center", justifyContent: "space-between", gap: "0.75rem" }}>
                    <div style={{ color: "rgba(226,232,240,0.84)", fontSize: "0.83rem", fontWeight: 600 }}>{row.label}</div>
                    <div style={{ display: "flex", alignItems: "baseline", gap: "0.36rem", fontSize: "0.83rem" }}>
                      <span style={{ color: "rgba(148,163,184,0.95)" }}>{row.before}</span>
                      <span style={{ color: "rgba(148,163,184,0.7)" }}>→</span>
                      <span style={{ color: "rgba(248,250,252,0.96)", fontWeight: 700 }}>{row.after}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          {sprintUpdateConfirm.headsUpItems?.length ? (
            <div style={{ marginTop: "0.95rem", display: "grid", gap: "0.34rem" }}>
              <div style={{ color: "rgba(255,255,255,0.65)", fontSize: "0.74rem", textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 700 }}>Heads up</div>
              <ul style={{ margin: 0, paddingLeft: "1.1rem", color: "rgba(226,232,240,0.82)", fontSize: "0.81rem", display: "grid", gap: "0.3rem" }}>
                {sprintUpdateConfirm.headsUpItems.map((item: string) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
          ) : null}

          <div style={{ marginTop: "1rem", display: "grid", gap: "0.35rem" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "0.6rem" }}>
              <button type="button" style={{ ...btnBase, background: "rgba(255,255,255,0.08)", color: "white" }} onClick={() => setSprintUpdateConfirm(null)}>
                Cancel
              </button>
              <button
                type="button"
                style={{ ...btnBase, background: brand.accent.primary, color: "white", borderColor: "rgba(59,130,246,0.6)" }}
                onClick={async () => {
                  setSprintUpdateConfirm(null);
                  await applySprintDraftChanges();
                }}
              >
                Confirm update
              </button>
            </div>
            <div style={{ color: "rgba(255,255,255,0.48)", fontSize: "0.72rem", textAlign: "right" }}>Applies to this sprint and forecast.</div>
          </div>
        </ModalShell>
      ) : null}
      {recalcConfirm && !(recalcConfirm.kind === "calories" && recalcPrimaryAlreadyAligned) ? (
        <ModalShell
          title={recalcConfirm.title ?? recalcModalTitle}
          ariaLabel="recalculate confirmation"
          onClose={() => setRecalcConfirm(null)}
          panelStyle={{ width: "min(560px, calc(100vw - 32px))" }}
        >
          <div style={{ marginTop: "0.85rem", display: "grid", gap: "1rem", color: brand.text.primary }}>
            {Array.isArray(recalcConfirm.introLines) ? recalcConfirm.introLines.map((line: string) => (
              <div key={line} style={{ lineHeight: 1.4, fontSize: "0.95rem", fontWeight: 700 }}>
                {line}
              </div>
            )) : null}
            <div style={{ display: "grid", gap: "0.8rem", padding: "0.95rem 1rem", borderRadius: 16, background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }}>
              {recalcConfirm.kind === "calories" ? (
                <div style={{ display: "grid", gap: "0.8rem" }}>
                  <div style={{ minWidth: 0, color: brand.text.secondary }}>
                    <div style={{ fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase", color: brand.text.secondary }}>Current</div>
                    <div style={{ marginTop: "0.5rem", display: "grid", gap: "0.55rem" }}>
                      {recalcConfirm.currentLines?.map((line: string) => (
                        <div key={line} style={{ fontSize: "0.92rem", lineHeight: 1.35, color: brand.text.secondary }}>
                          {line}
                        </div>
                      ))}
                    </div>
                  </div>
                  <div style={{ borderTop: "1px solid rgba(255,255,255,0.08)", paddingTop: "0.8rem", minWidth: 0, color: brand.text.primary }}>
                    <div style={{ fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase", color: brand.text.primary }}>{recalcConfirm.updatedHeading ?? "Updated"}</div>
                    <div style={{ marginTop: "0.5rem", display: "grid", gap: "0.55rem" }}>
                      {recalcConfirm.updatedLines?.map((line: string) => (
                        <div key={line} style={{ fontSize: "0.95rem", lineHeight: 1.35, fontWeight: 700, color: brand.text.primary }}>
                          {line}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <div style={{ display: "grid", gap: "0.4rem" }}>
                  <div style={{ fontSize: "0.7rem", fontWeight: 800, letterSpacing: "0.05em", textTransform: "uppercase", color: "rgba(255,255,255,0.55)" }}>Updated</div>
                  <ul style={{ margin: 0, paddingLeft: "1.1rem", display: "grid", gap: "0.34rem", color: "rgba(226,232,240,0.95)" }}>
                    {recalcConfirm.updatedLines?.map((line: string) => <li key={line}>{line}</li>)}
                  </ul>
                </div>
              )}
            </div>
          </div>
          {recalcConfirm.outcomeLine ? (
            <div style={{ marginTop: "1rem", color: "rgba(226,232,240,0.95)", lineHeight: 1.55 }}>
              {recalcConfirm.outcomeLine}
            </div>
          ) : null}
          <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.7rem", marginTop: "1.15rem" }}>
            <button type="button" style={{ ...btnBase, background: "rgba(255,255,255,0.03)", color: "rgba(255,255,255,0.82)", borderColor: "rgba(255,255,255,0.12)" }} onClick={() => setRecalcConfirm(null)}>
              Cancel
            </button>
            <button
              type="button"
              style={{ ...btnBase, background: brand.accent.primary, color: "white", borderColor: "rgba(59,130,246,0.6)" }}
              onClick={recalcConfirm.kind === "calories" ? applyRecalculatedCalories : applyRecalculatedDate}
            >
              {recalcConfirm.confirmLabel ?? (recalcConfirm.kind === "calories" ? "Use updated plan" : "Adjust goal date")}
            </button>
          </div>
        </ModalShell>
      ) : null}
      {showCsvImportModal ? (
        <ModalShell title="Import CSV" ariaLabel="import CSV modal" onClose={closeCsvImportModal} panelStyle={{ width: "min(560px, calc(100vw - 32px))" }}>
          <div style={{ marginTop: "0.4rem", color: "rgba(255,255,255,0.72)", fontSize: "0.92rem", lineHeight: 1.45 }}>
            Accepted columns:
            <div style={{ marginTop: "0.25rem", color: "rgba(255,255,255,0.9)", fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}>date, weight, caloriesIn, totalBurned, activeCalories, netCalories</div>
          </div>

          <div style={{ marginTop: "0.8rem", display: "grid", gap: "0.75rem" }}>
            <label
              style={{
                ...btnBase,
                background: "rgba(59,130,246,0.20)",
                borderColor: "rgba(59,130,246,0.45)",
                color: "#dbeafe",
                width: "fit-content",
                display: "inline-flex",
                alignItems: "center",
                gap: "0.55rem",
                cursor: "pointer",
              }}
            >
              <FileIcon size={16} />
              <span>Choose File</span>
              <input
                type="file"
                accept=".csv,text/csv"
                style={{ display: "none" }}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  e.target.value = "";
                  if (f) importCsv(f);
                }}
              />
            </label>

            <button
              type="button"
              style={{
                ...btnBase,
                background: "transparent",
                border: "none",
                color: COLORS.lightBlue,
                padding: 0,
                justifySelf: "start",
                textDecoration: "underline",
                textUnderlineOffset: "3px",
              }}
              onClick={() => downloadTextFile("weight-tracker-example-import.csv", EXAMPLE_IMPORT_CSV)}
            >
              Don&apos;t have a CSV? Download template
            </button>

            {csvImportMsg ? (
              <div
                style={{
                  borderRadius: 10,
                  padding: "0.6rem 0.75rem",
                  background: "rgba(255,255,255,0.06)",
                  border: "1px solid rgba(255,255,255,0.16)",
                  color: "rgba(255,255,255,0.9)",
                  fontSize: "0.9rem",
                }}
              >
                {csvImportMsg}
              </div>
            ) : null}
          </div>
        </ModalShell>
      ) : null}
      {restoreProfileFile ? (
        <ModalShell title="Restore profile backup?" ariaLabel="restore profile confirmation" onClose={closeRestoreProfileModal} panelStyle={{ width: "min(560px, calc(100vw - 32px))" }}>
          <div style={{ marginTop: "0.75rem", color: "rgba(255,255,255,0.88)", lineHeight: 1.4 }}>
            This will replace your current profile (entries, sprints, settings). Consider downloading a backup first.
          </div>
          <div style={{ marginTop: "0.7rem" }}>
            <Input label="Type RESTORE to confirm" value={restoreConfirmText} onChange={setRestoreConfirmText} placeholder="RESTORE" />
          </div>
          <div style={{ marginTop: "0.9rem", display: "flex", justifyContent: "flex-end", gap: "0.55rem" }}>
            <button type="button" style={{ ...settingsButtonStyle, background: "rgba(255,255,255,0.08)" }} onClick={closeRestoreProfileModal}>Cancel</button>
            <button type="button" style={{ ...settingsButtonStyle, background: "rgba(239,68,68,0.24)", borderColor: "rgba(239,68,68,0.4)", color: "#fecaca" }} onClick={confirmRestoreProfile} disabled={restoreConfirmText.trim().toUpperCase() !== "RESTORE"}>Confirm restore</button>
          </div>
        </ModalShell>
      ) : null}
      {sbOverwriteWarning ? (
        <ModalShell title="Local data will be replaced" ariaLabel="local data overwrite warning" onClose={() => { void cancelSupabaseOverwriteAndKeepLocal(); }} panelStyle={{ width: "min(560px, calc(100vw - 32px))" }}>
          <div style={{ marginTop: "0.75rem", color: "rgba(255,255,255,0.88)", lineHeight: 1.45 }}>
            You signed into an account with existing cloud data. Continuing will replace this device&apos;s local data with cloud data.
          </div>
          <div style={{ marginTop: "0.65rem", color: "rgba(255,255,255,0.72)", fontSize: "0.9rem", lineHeight: 1.45 }}>
            If you want to keep local data, export it first in <strong>Settings → Data</strong>.
          </div>
          <div style={{ marginTop: "0.75rem", borderRadius: 10, border: "1px solid rgba(255,255,255,0.14)", background: "rgba(255,255,255,0.04)", padding: "0.65rem 0.75rem", fontSize: "0.84rem", color: "rgba(255,255,255,0.78)", display: "grid", gap: "0.2rem" }}>
            <div>Local: {sbOverwriteWarning.localEntries} entries • {sbOverwriteWarning.localSprints} sprints</div>
            <div>Cloud: {sbOverwriteWarning.cloudEntries} entries • {sbOverwriteWarning.cloudSprints} sprints</div>
          </div>
          <div style={{ marginTop: "0.95rem", display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: "0.55rem" }}>
            <button type="button" style={{ ...btnBase, width: "100%", minHeight: 42, justifyContent: "center", background: "rgba(255,255,255,0.08)", color: "white", order: isMobile ? 2 : 1 }} onClick={() => { void cancelSupabaseOverwriteAndKeepLocal(); }}>
              Cancel
            </button>
            <button type="button" style={{ ...btnBase, width: "100%", minHeight: 42, justifyContent: "center", background: "rgba(59,130,246,0.22)", borderColor: "rgba(59,130,246,0.48)", color: "#dbeafe", order: isMobile ? 1 : 2 }} onClick={() => { void confirmSupabaseOverwriteWithCloud(); }}>
              Continue with cloud data
            </button>
          </div>
        </ModalShell>
      ) : null}
      {archiveFirstPrompt ? (
        <ModalShell title="Archive current sprint first" ariaLabel="archive current sprint first" onClose={() => { setArchiveFirstPrompt(null); setPendingSprintAction(null); }} panelStyle={{ width: "min(560px, calc(100vw - 32px))" }}>
          <div style={{ marginTop: "0.75rem", color: "rgba(255,255,255,0.86)", lineHeight: 1.45 }}>
            You already have an active sprint: <strong>{archiveFirstPrompt.activeSprintName}</strong>. Archive it before creating a new one.
          </div>
          <div style={{ marginTop: "0.95rem", display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: "0.55rem" }}>
            <button type="button" style={{ ...btnBase, width: "100%", minHeight: 42, justifyContent: "center", background: "rgba(255,255,255,0.08)", color: "white", order: isMobile ? 2 : 1 }} onClick={() => { setArchiveFirstPrompt(null); setPendingSprintAction(null); }}>
              Cancel
            </button>
            <button
              type="button"
              style={{ ...btnBase, width: "100%", minHeight: 42, justifyContent: "center", background: "rgba(251,191,36,0.16)", borderColor: "rgba(251,191,36,0.36)", color: "#fde68a", order: isMobile ? 1 : 2 }}
              onClick={() => {
                const sprintId = archiveFirstPrompt.activeSprintId;
                setArchiveFirstPrompt(null);
                openArchiveConfirm(sprintId, { type: "resumePending" });
              }}
            >
              Archive & create sprint
            </button>
          </div>
        </ModalShell>
      ) : null}
      {archiveConfirm ? (
        <ModalShell title="Archive sprint?" ariaLabel="archive sprint confirmation" onClose={() => setArchiveConfirm(null)} panelStyle={{ width: "min(560px, calc(100vw - 32px))" }}>
          {(() => {
            const sprint = sprints.find((s) => s.id === archiveConfirm.sprintId);
            if (!sprint) return null;
            const stats = archiveConfirm.stats;
            const minDate = isYmd(sprint.startDate) ? sprint.startDate : undefined;
            const maxDate = todayYmd;
            const endDateValue = archiveConfirm.proposedEndDate;
            const endDateValid = isYmd(endDateValue)
              && (!minDate || endDateValue >= minDate)
              && endDateValue <= maxDate;
            const endDateError = !isYmd(endDateValue)
              ? "Pick a valid end date."
              : (minDate && endDateValue < minDate)
                ? `End date can't be before start (${minDate}).`
                : (endDateValue > maxDate)
                  ? `End date can't be after today (${maxDate}).`
                  : null;
            const commitEndDate = (next: string) => {
              if (!isYmd(next)) return;
              const nextStats = buildArchiveStats(sprint, next);
              setArchiveConfirm((prev) => prev ? { ...prev, proposedEndDate: next, stats: nextStats } : prev);
            };
            const dateInputStyle: React.CSSProperties = { width: "100%", padding: "0.55rem 0.65rem", borderRadius: 12, border: "1px solid rgba(255,255,255,0.14)", background: "rgba(255,255,255,0.04)", color: "white", outline: "none", minHeight: SPRINT_DATE_FIELD_HEIGHT };
            return (
              <>
                <div style={{ marginTop: "0.7rem", color: "rgba(255,255,255,0.84)", fontSize: "0.9rem" }}>
                  You're about to archive "{sprint.name}". Choose the date the sprint ended.
                </div>
                <div style={{ marginTop: "0.75rem", display: "grid", gap: "0.35rem" }}>
                  <div style={{ fontSize: "0.82rem", color: "rgba(255,255,255,0.78)" }}>End date</div>
                  <MobileDateField
                    value={isYmd(endDateValue) ? endDateValue : ""}
                    min={minDate}
                    max={maxDate}
                    onCommit={commitEndDate}
                    style={dateInputStyle}
                  />
                  {endDateError ? (
                    <div style={{ fontSize: "0.78rem", color: "rgba(252,165,165,0.95)" }}>{endDateError}</div>
                  ) : null}
                </div>
                <div style={{ marginTop: "0.75rem", border: "1px solid rgba(255,255,255,0.14)", borderRadius: 12, background: "rgba(255,255,255,0.03)", padding: "0.75rem" }}>
                  <div style={{ fontWeight: 800, marginBottom: "0.55rem", color: "rgba(255,255,255,0.93)" }}>Sprint summary</div>
                  <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: "0.45rem 0.75rem", fontSize: "0.82rem" }}>
                    <div>Start date: <strong>{sprint.startDate}</strong></div>
                    <div>End date: <strong>{archiveConfirm.proposedEndDate}</strong></div>
                    <div>Start weight: <strong>{stats.startWeightValue !== null ? fmt2(stats.startWeightValue) : "—"}</strong></div>
                    <div>Target weight: <strong>{stats.endWeightValue !== null ? fmt2(stats.endWeightValue) : "—"}</strong></div>
                    <div>Net change: <strong>{stats.net !== null ? fmt2(stats.net) : "—"}</strong></div>
                    <div>Pace: <strong>{stats.pace !== null ? `${fmt2(stats.pace)} lbs/wk` : "—"}</strong></div>
                  </div>
                </div>
                <div style={{ marginTop: "0.95rem", display: "flex", justifyContent: "flex-end", gap: "0.55rem" }}>
                  <button type="button" style={{ ...btnBase, background: "rgba(255,255,255,0.08)", color: "white" }} onClick={() => setArchiveConfirm(null)}>Cancel</button>
                  <button type="button" disabled={!endDateValid} style={{ ...btnBase, background: "rgba(251,191,36,0.16)", borderColor: "rgba(251,191,36,0.36)", color: "#fde68a", opacity: endDateValid ? 1 : 0.5, cursor: endDateValid ? "pointer" : "not-allowed" }} onClick={confirmArchiveSprint}>Archive Sprint</button>
                </div>
              </>
            );
          })()}
        </ModalShell>
      ) : null}
      {calFloorConfirmData ? (() => {
        // Each option honours one guardrail: "Recommended intake" holds the calorie floor,
        // "Recommended pace" holds the 2 lb/week cap. Which one has fewer calories flips with
        // TDEE, so both options show BOTH numbers and are tagged with the guardrail they miss.
        const capAbs = SPRINT_PACE_LIMITS.cutMaxLossLbsPerWeek;
        const floorPaceAbs = Math.abs((calFloorConfirmData.safeAdjustedNet * 7) / 3500);
        const floorPaceOverCap = floorPaceAbs > capAbs;
        const pacePlanLowIntake = calFloorConfirmData.lowestAllowableIntake !== null
          && calFloorConfirmData.lowestAllowableIntake < calFloorConfirmData.floorIntake;
        const chipStyle = { fontSize: "0.64rem", color: "rgba(226,232,240,0.9)", border: "1px solid rgba(148,163,184,0.34)", borderRadius: 999, padding: "0.08rem 0.34rem" } as const;
        const subRowStyle = { display: "flex", alignItems: "center", gap: "0.38rem", flexWrap: "wrap" } as const;
        return (
        <ModalShell title="Adjust your plan" ariaLabel="calorie floor confirmation" onClose={() => setCalFloorConfirmData(null)} panelStyle={{ width: "min(520px, calc(100vw - 32px))" }}>
          <div style={{ display: "grid", gap: "0.75rem", paddingTop: "0.4rem" }}>
            <div style={{ color: "rgba(226,232,240,0.92)", fontSize: "0.88rem", lineHeight: 1.5 }}>
              Your timeline requires a more aggressive plan. Pick how you'd like to ease it back.
            </div>
            <div style={{ display: "grid", gap: "0.5rem" }}>
              <button
                type="button"
                onClick={() => setCalFloorChoice("recommended")}
                style={{ ...btnBase, width: "100%", textAlign: "left", justifyContent: "flex-start", background: calFloorChoice === "recommended" ? "rgba(59,130,246,0.2)" : "rgba(255,255,255,0.04)", borderColor: calFloorChoice === "recommended" ? "rgba(99,155,255,0.52)" : "rgba(255,255,255,0.12)", minHeight: 0, padding: "0.62rem 0.72rem", display: "grid", gap: "0.2rem" }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "0.4rem" }}>
                  <span style={{ fontSize: "0.82rem", fontWeight: 700, color: "rgba(255,255,255,0.92)" }}>Recommended intake</span>
                  <span style={{ fontSize: "1rem", fontWeight: 800, color: "rgba(248,250,252,0.98)" }}>~{calFloorConfirmData.floorIntake.toLocaleString()} cal/day</span>
                </div>
                <div style={subRowStyle}>
                  <span style={{ fontSize: "0.74rem", color: "rgba(255,255,255,0.56)" }}>≈{floorPaceAbs.toFixed(1)} lb/week</span>
                  {floorPaceOverCap ? <span style={chipStyle}>Above {capAbs.toFixed(1)} lb/wk</span> : null}
                </div>
              </button>
              {calFloorConfirmData.lowestAllowableIntake !== null ? (
                <button
                  type="button"
                  onClick={() => setCalFloorChoice("fastest")}
                  style={{ ...btnBase, width: "100%", textAlign: "left", justifyContent: "flex-start", background: calFloorChoice === "fastest" ? "rgba(255,255,255,0.12)" : "rgba(255,255,255,0.04)", borderColor: calFloorChoice === "fastest" ? "rgba(148,163,184,0.45)" : "rgba(255,255,255,0.12)", minHeight: 0, padding: "0.62rem 0.72rem", display: "grid", gap: "0.2rem" }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "0.4rem" }}>
                    <span style={{ fontSize: "0.82rem", fontWeight: 700, color: "rgba(255,255,255,0.86)" }}>Recommended pace</span>
                    <span style={{ fontSize: "0.96rem", fontWeight: 800, color: "rgba(226,232,240,0.92)" }}>
                      {calFloorConfirmData.lowestAllowableIntake.toLocaleString()} cal/day
                    </span>
                  </div>
                  <div style={subRowStyle}>
                    <span style={{ fontSize: "0.74rem", color: "rgba(255,255,255,0.52)" }}>{capAbs.toFixed(1)} lb/week</span>
                    {pacePlanLowIntake ? <span style={chipStyle}>Low intake</span> : null}
                  </div>
                </button>
              ) : null}
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "0.55rem", paddingTop: "0.3rem", borderTop: "1px solid rgba(255,255,255,0.08)" }}>
              <button
                type="button"
                style={{ ...btnBase, background: "rgba(255,255,255,0.06)", color: "rgba(255,255,255,0.72)", fontSize: "0.82rem" }}
                onClick={() => { setCalFloorConfirmData(null); setCalFloorChoice("recommended"); setCalFloorEditContext(null); }}
              >
                Back
              </button>
              <button
                type="button"
                style={{ ...btnBase, background: `linear-gradient(135deg, ${COLORS.blue}, ${COLORS.lightBlue})`, color: "white", fontWeight: 700 }}
                onClick={() => {
                  const choice = calFloorChoice;
                  if (calFloorEditContext && activeModal === "sprint") {
                    const next = choice === "fastest" && calFloorEditContext.fastestNet !== null
                      ? { net: calFloorEditContext.fastestNet, targetDate: calFloorEditContext.fastestTargetDate }
                      : { net: calFloorEditContext.recommendedNet, targetDate: calFloorEditContext.recommendedTargetDate };
                    setCalFloorConfirmData(null);
                    setCalFloorEditContext(null);
                    void applySprintDraftChanges(next);
                    return;
                  }
                  if (choice === "fastest" && calFloorConfirmData.lowestAllowableIntake !== null && calFloorConfirmData.lowestAllowableNet !== null) {
                    const { lowestAllowableIntake, lowestAllowableNet } = calFloorConfirmData;
                    const sw = safeNumber(newSprintStartingWeight);
                    const tw = safeNumber(newSprintTargetWeight);
                    const fastestTargetDate = sw !== null && tw !== null
                      ? computeTargetDateFromRate({
                        startWeight: sw,
                        targetWeight: tw,
                        startDateYmd: newSprintStartDate,
                        rateLbsPerWeekAbs: SPRINT_PACE_LIMITS.cutMaxLossLbsPerWeek,
                      })?.targetDateYmd ?? null
                      : null;
                    setCalFloorConfirmData(null);
                    setNewSprintAssumedNetCalories(lowestAllowableNet);
                    setNewSprintNetDraft(String(lowestAllowableNet));
                    setNewSprintIntakeDraft(String(lowestAllowableIntake));
                    if (isYmd(fastestTargetDate)) setNewSprintTargetDate(fastestTargetDate);
                    void createSprint(false, false, true, lowestAllowableNet, fastestTargetDate);
                    return;
                  }
                  const { floorIntake, safeAdjustedNet } = calFloorConfirmData;
                  const sw = safeNumber(newSprintStartingWeight);
                  const tw = safeNumber(newSprintTargetWeight);
                  const recommendedRateAbs = (Math.abs(safeAdjustedNet) * 7) / 3500;
                  const recommendedTargetDate = sw !== null && tw !== null && recommendedRateAbs > 0
                    ? computeTargetDateFromRate({
                      startWeight: sw,
                      targetWeight: tw,
                      startDateYmd: newSprintStartDate,
                      rateLbsPerWeekAbs: recommendedRateAbs,
                    })?.targetDateYmd ?? null
                    : null;
                  setCalFloorConfirmData(null);
                  setNewSprintAssumedNetCalories(safeAdjustedNet);
                  setNewSprintNetDraft(String(safeAdjustedNet));
                  setNewSprintIntakeDraft(String(floorIntake));
                  if (isYmd(recommendedTargetDate)) setNewSprintTargetDate(recommendedTargetDate);
                  void createSprint(false, false, true, safeAdjustedNet, recommendedTargetDate);
                }}
              >
                Continue
              </button>
            </div>
          </div>
        </ModalShell>
        );
      })() : null}
      {createSprintWarningConfirm ? (
        <ModalShell title="Review this sprint" ariaLabel="create sprint pace review" onClose={() => setCreateSprintWarningConfirm(null)} panelStyle={{ width: "min(560px, calc(100vw - 32px))" }}>
          {createSprintWarningConfirm.summary?.belowFloor ? (
            <div style={{ marginTop: "0.75rem", borderRadius: 12, border: "1px solid rgba(251,191,36,0.3)", background: "linear-gradient(135deg, rgba(251,191,36,0.12), rgba(251,191,36,0.03))", padding: "0.65rem 0.8rem", display: "grid", gap: "0.18rem" }}>
              <div style={{ fontWeight: 700, color: "rgba(248,250,252,0.96)", fontSize: "0.84rem" }}>⚠ Below recommended intake</div>
              <div style={{ fontSize: "0.78rem", color: "rgba(255,237,213,0.86)" }}>This plan uses a lower calorie target than typically recommended.</div>
            </div>
          ) : null}

          <div style={{ marginTop: "0.8rem", fontSize: "0.72rem", letterSpacing: "0.08em", textTransform: "uppercase", color: "rgba(255,255,255,0.52)", fontWeight: 700 }}>Your plan</div>
          <div style={{ marginTop: "0.45rem", display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(2, minmax(0, 1fr))", gap: "0.5rem" }}>
            {[
              { key: "start", label: "Start", value: createSprintWarningConfirm.summary ? `${createSprintWarningConfirm.summary.startDate}\n${createSprintWarningConfirm.summary.startWeight}` : "—" },
              { key: "target", label: "Target", value: createSprintWarningConfirm.summary ? `${createSprintWarningConfirm.summary.targetDate}\n${createSprintWarningConfirm.summary.targetWeight}` : "—" },
              { key: "pace", label: "Pace", value: createSprintWarningConfirm.summary?.pace ?? "—" },
              { key: "calories", label: "Daily calories", value: createSprintWarningConfirm.summary ? `${createSprintWarningConfirm.summary.intake !== null ? `~${Number(createSprintWarningConfirm.summary.intake).toLocaleString()}` : "—"} intake · ${createSprintWarningConfirm.summary.net !== null ? `${createSprintWarningConfirm.summary.net > 0 ? "+" : ""}${Math.round(createSprintWarningConfirm.summary.net).toLocaleString()} net` : "—"}` : "—" },
            ].map((row) => (
              <div key={row.key} style={{ borderRadius: 12, border: "1px solid rgba(255,255,255,0.12)", background: "rgba(255,255,255,0.04)", padding: "0.58rem 0.65rem", display: "grid", gap: "0.16rem" }}>
                <div style={{ fontSize: "0.66rem", letterSpacing: "0.06em", textTransform: "uppercase", color: "rgba(255,255,255,0.56)", fontWeight: 700 }}>{row.label}</div>
                <div style={{ fontSize: "0.82rem", color: "rgba(248,250,252,0.95)", fontWeight: 700, whiteSpace: "pre-line" }}>{row.value}</div>
              </div>
            ))}
          </div>
          {createSprintWarningConfirm.summary?.overrideUsed ? (
            <div style={{ marginTop: "0.45rem", fontSize: "0.76rem", color: "rgba(255,255,255,0.62)" }}>Earliest date updated to meet goal weight using lowest allowed intake.</div>
          ) : createSprintWarningConfirm.summary?.adjustedUsed ? (
            <div style={{ marginTop: "0.45rem", fontSize: "0.76rem", color: "rgba(255,255,255,0.62)" }}>Target date updated to match selected daily intake.</div>
          ) : null}

          <div style={{ marginTop: "0.95rem", display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "0.6rem", flexWrap: isMobile ? "wrap" : "nowrap" }}>
            <button type="button" style={{ ...btnBase, background: "rgba(255,255,255,0.08)", color: "white", minWidth: 132, alignSelf: "flex-start" }} onClick={() => setCreateSprintWarningConfirm(null)}>
              Adjust plan
            </button>
            <div style={{ display: "grid", justifyItems: "end", alignContent: "start", gap: "0.25rem", marginLeft: "auto" }}>
              <button
                type="button"
                style={{ ...btnBase, background: brand.accent.primary, color: "white", borderColor: "rgba(59,130,246,0.6)", minWidth: 132 }}
                onClick={async () => {
                  setCreateSprintWarningConfirm(null);
                  await createSprint(true, false, Boolean(createSprintWarningConfirm?.bypassCalFloor), createSprintWarningConfirm?.summary?.net ?? null, createSprintWarningConfirm?.summary?.targetDateYmd ?? null);
                }}
              >
                Create sprint
              </button>
              <div style={{ color: "rgba(255,255,255,0.48)", fontSize: "0.72rem" }}>You can adjust or edit the sprint later.</div>
            </div>
          </div>
        </ModalShell>
      ) : null}

      {/* Create Sprint modal — rendered at top level so it works from dashboard and sprints page */}
      {activeModal === "newSprint" ? (
      <ModalShell
        title="Create Sprint"
        ariaLabel="create sprint modal"
        onClose={closeModal}
        panelStyle={{ width: "min(660px, calc(100vw - 32px))", maxHeight: "90vh", overflowY: "auto" }}
        titleAddon={isMobile ? (
          <div style={{ display: "inline-flex", border: "1px solid rgba(255,255,255,0.14)", borderRadius: 8, overflow: "hidden", flexShrink: 0 }}>
            <button style={{ ...btnBase, border: "none", borderRadius: 0, background: newSprintType === "new" ? "rgba(59,130,246,0.32)" : "rgba(255,255,255,0.06)", color: "white", padding: "0.28rem 0.55rem", fontSize: "0.75rem" }} onClick={() => setNewSprintType("new")}>New</button>
            <button style={{ ...btnBase, border: "none", borderRadius: 0, background: newSprintType === "historical" ? "rgba(59,130,246,0.32)" : "rgba(255,255,255,0.06)", color: "white", padding: "0.28rem 0.55rem", fontSize: "0.75rem" }} onClick={() => setNewSprintType("historical")}>Historic</button>
          </div>
        ) : null}
      >
        {!isMobile ? (
          <div style={{ marginTop: "0.12rem", marginBottom: "0.42rem", color: "rgba(255,255,255,0.72)", fontSize: "0.82rem" }}>
            {newSprintType === "historical" ? "Add a historical sprint to analyze." : "Define your goal and timeline."}
          </div>
        ) : null}

        <>
            {!isMobile ? (
              <div style={{ marginTop: "0.5rem", display: "inline-flex", border: "1px solid rgba(255,255,255,0.14)", borderRadius: 12, overflow: "hidden" }}>
                <button style={{ ...btnBase, border: "none", borderRadius: 0, background: newSprintType === "new" ? "rgba(59,130,246,0.32)" : "rgba(255,255,255,0.06)", color: "white", padding: "0.5rem 0.85rem" }} onClick={() => setNewSprintType("new")}>New Sprint</button>
                <button style={{ ...btnBase, border: "none", borderRadius: 0, background: newSprintType === "historical" ? "rgba(59,130,246,0.32)" : "rgba(255,255,255,0.06)", color: "white", padding: "0.5rem 0.85rem" }} onClick={() => setNewSprintType("historical")}>Historical</button>
              </div>
            ) : null}

            <div style={{ marginTop: isMobile ? "0.35rem" : "0.55rem", display: "grid", gap: isMobile ? "0.4rem" : "0.55rem", alignItems: "start" }}>
              {newSprintType === "new" ? (
                <div style={{ ...createSprintFieldWrapStyle }}>
                  <div style={createSprintLabelRowStyle}>Sprint type</div>
                  <SprintGoalTypeToggle
                    value={newSprintGoalType}
                    onChange={(nextType) => {
                      setNewSprintGoalType(nextType);
                      const nextTarget = normalizeSprintWeightsForGoalType({
                        goalType: nextType,
                        startWeight: safeNumber(newSprintStartingWeight),
                        targetWeight: safeNumber(newSprintTargetWeight),
                        defaultDeltaLbs: 10,
                      });
                      if (nextTarget !== null) setNewSprintTargetWeight(nextTarget);
                    }}
                  />
                </div>
              ) : null}
              <div style={{ display: "flex", gap: "0.75rem", alignItems: "flex-start" }}>
                <div style={{ ...createSprintFieldWrapStyle, flex: 1 }}>
                  <div style={createSprintLabelRowStyle}>Name</div>
                  <input
                    type="text"
                    value={newSprintName}
                    onChange={(e) => setNewSprintName(e.target.value.slice(0, MAX_SPRINT_NAME_CHARS))}
                    placeholder="e.g., Cut Phase"
                    maxLength={MAX_SPRINT_NAME_CHARS}
                    style={{
                      width: "100%",
                      padding: "0.75rem 0.85rem",
                      borderRadius: 12,
                      border: "1px solid rgba(255,255,255,0.14)",
                      background: "rgba(255,255,255,0.04)",
                      color: "white",
                      outline: "none",
                      minHeight: GOAL_FIELD_HEIGHT,
                    }}
                  />
                  {!newSprintName.trim() && (
                    <div style={{ fontSize: "0.78rem", color: "#f87171", marginTop: "0.25rem" }}>
                      Sprint name required
                    </div>
                  )}
                </div>
                <SprintColorDots label="Color" value={newSprintColor} onChange={setNewSprintColor} />
              </div>

              {newSprintType === "new" ? (
                <>
                  <div style={{ ...createSprintFieldWrapStyle, position: "relative", gap: "0.45rem" }}>
                    <TriFieldRow
                      mobile={isMobile}
                      leftLabel={<div style={{ ...createSprintLabelRowStyle, marginBottom: 0, justifyContent: "flex-start", gap: "0.35rem", whiteSpace: "nowrap" }}>Start weight{!isMobile && newSprintStartWeightSource ? <span style={{ fontSize: "0.68rem", color: "rgba(255,255,255,0.55)" }}>{newSprintStartWeightSource === "fromLog" ? " · from log" : " · edited"}</span> : null}</div>}
                      rightLabel={<div style={{ ...createSprintLabelRowStyle, marginBottom: 0 }}>Target weight</div>}
                      leftField={<div style={{ position: "relative" }}><input type="text" inputMode="decimal" pattern="[0-9]*" value={newSprintStartingWeight} onChange={(e) => { const nextStart = e.target.value; setNewSprintStartingWeight(nextStart); setNewSprintStartWeightSource((prev) => (prev === "fromLog" ? "edited" : prev)); const inferred = deriveSprintGoalType(safeNumber(nextStart), safeNumber(newSprintTargetWeight)); if (safeNumber(nextStart) !== null && safeNumber(newSprintTargetWeight) !== null) setNewSprintGoalType(inferred); }} style={{ width: "100%", padding: "0.55rem 2.4rem 0.55rem 0.65rem", borderRadius: 12, border: "1px solid rgba(255,255,255,0.14)", background: "rgba(255,255,255,0.04)", color: "white", outline: "none", minHeight: GOAL_FIELD_HEIGHT, fontVariantNumeric: "tabular-nums", boxSizing: "border-box" }} /><span style={{ position: "absolute", right: "0.6rem", top: "50%", transform: "translateY(-50%)", color: "rgba(255,255,255,0.38)", fontSize: "0.78rem", pointerEvents: "none" }}>lbs</span></div>}
                      rightField={<div style={{ position: "relative" }}><input type="text" inputMode="decimal" pattern="[0-9]*" value={newSprintTargetWeight} onChange={(e) => { const next = e.target.value === "" ? "" : safeNumber(e.target.value) ?? ""; setNewSprintTargetWeight(next); if (safeNumber(newSprintStartingWeight) !== null && safeNumber(next) !== null) { setNewSprintGoalType(deriveSprintGoalType(safeNumber(newSprintStartingWeight), safeNumber(next))); } }} style={{ width: "100%", padding: "0.55rem 2.4rem 0.55rem 0.65rem", borderRadius: 12, border: "1px solid rgba(255,255,255,0.14)", background: "rgba(255,255,255,0.04)", color: "white", outline: "none", minHeight: GOAL_FIELD_HEIGHT, fontVariantNumeric: "tabular-nums", boxSizing: "border-box" }} /><span style={{ position: "absolute", right: "0.6rem", top: "50%", transform: "translateY(-50%)", color: "rgba(255,255,255,0.38)", fontSize: "0.78rem", pointerEvents: "none" }}>lbs</span></div>}
                      chip={<DeltaChip startWeight={newSprintStartingWeight} endWeight={newSprintTargetWeight} />}
                    />
                  </div>
                  {/* Planning method toggle */}
                  <div style={createSprintFieldWrapStyle}>
                    {!isMobile && <div style={createSprintLabelRowStyle}>Planning method</div>}
                    <PlanMethodToggle value={newSprintPlanMethod} onChange={(v) => {
                      setNewSprintPlanMethod(v);
                      if (v === "byDate") { setNewSprintPresetRate(null); setNewSprintPresetClamped(false); }
                    }} />
                  </div>
                  {newSprintPlanMethod === "byPace" && (() => {
                    const _sw = safeNumber(newSprintStartingWeight);
                    const _tw = safeNumber(newSprintTargetWeight);
                    if (_sw === null || _tw === null) return null;
                    const _presets = getRatePresetOptions({ startWeight: _sw, targetWeight: _tw });
                    if (!_presets.length) return null;
                    const _dir = _tw < _sw ? "Cut" : _tw > _sw ? "Bulk" : "Pace";
                    return (
                      <div style={createSprintFieldWrapStyle}>
                        <div style={createSprintLabelRowStyle}>{_dir === "Pace" ? "Pace" : `${_dir} Pace`}</div>
                        <RatePresetChips presets={_presets} selectedRate={newSprintPresetRate} onSelect={(rate) => setNewSprintPresetRate(rate)} detailed showNegative={_tw < _sw} compact={isMobile} />
                        {newSprintPresetClamped && (
                          <div style={{ fontSize: "0.75rem", color: "rgba(251,191,36,0.9)" }}>
                            Target date adjusted to stay within allowed sprint duration.
                          </div>
                        )}
                      </div>
                    );
                  })()}
                  <div style={{ display: "grid", gap: "0.45rem" }}>
                    <TriFieldRow
                      mobile={isMobile}
                      leftLabel={<div style={{ ...createSprintLabelRowStyle, marginBottom: 0, whiteSpace: "nowrap" }}>Start date</div>}
                      rightLabel={<div style={{ ...createSprintLabelRowStyle, marginBottom: 0 }}><span>{newSprintPlanMethod === "byPace" ? "End date" : "Target date"}</span></div>}
                      leftField={(
                        <MobileDateField
                          value={newSprintStartDate}
                          onCommit={(v) => setNewSprintStartDate(v)}
                          style={{ width: "100%", padding: "0.55rem 0.65rem", borderRadius: 12, border: "1px solid rgba(255,255,255,0.14)", background: "rgba(255,255,255,0.04)", color: "white", outline: "none", minHeight: SPRINT_DATE_FIELD_HEIGHT }}
                        />
                      )}
                      rightField={(
                        <MobileDateField
                          value={newSprintTargetDate}
                          onCommitStart={() => { if (newSprintPlanMethod === "byPace") { setNewSprintPlanMethod("byDate"); setNewSprintPresetRate(null); setNewSprintPresetClamped(false); } }}
                          onCommit={(v) => setNewSprintTargetDate(v)}
                          style={{ width: "100%", padding: "0.55rem 0.65rem", borderRadius: 12, border: "1px solid rgba(255,255,255,0.14)", background: "rgba(255,255,255,0.04)", color: "white", outline: "none", minHeight: SPRINT_DATE_FIELD_HEIGHT, opacity: newSprintPlanMethod === "byPace" ? 0.7 : 1 }}
                        />
                      )}
                      chip={<DurationChip startDate={newSprintStartDate} endDate={newSprintTargetDate} />}
                    />
                    {newSprintPlanMethod === "byDate" ? (
                      !isMobile ? (
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 86px", gap: "0.28rem 0.55rem" }}>
                          <div />
                          <DurationPresetChips startDate={newSprintStartDate} endDate={newSprintTargetDate} onSelect={setNewSprintTargetDate} />
                          <div />
                        </div>
                      ) : null
                    ) : null}
                    <div style={createSprintHelperRowStyle}>{""}</div>
                  </div>
                </>
              ) : null}

              {newSprintType === "historical" ? (
                <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: "0.55rem" }}>
                  <Input label="Start date" type="date" value={newSprintStartDate} onChange={setNewSprintStartDate} helper=" " inputStyle={isMobile ? { WebkitAppearance: "none", fontSize: "0.88rem", padding: "0.55rem 0.65rem" } : null} />
                  <Input label="End date" type="date" value={newSprintEndDate} min={newSprintStartDate} onChange={setNewSprintEndDate} helper={newSprintEndDate && newSprintEndDate <= newSprintStartDate ? "End date must be after start date." : newSprintEndDate && newSprintEndDate > todayYmd ? "Heads up: historical sprints should end on or before today." : " "} inputStyle={isMobile ? { WebkitAppearance: "none", fontSize: "0.88rem", padding: "0.55rem 0.65rem" } : null} />
                </div>
              ) : null}

            </div>

            {newSprintType === "new" ? (
              <div style={{ marginTop: isMobile ? "0.2rem" : "0.5rem", paddingTop: isMobile ? "0.3rem" : "0.55rem", borderTop: "1px solid rgba(255,255,255,0.08)", display: "grid", gap: isMobile ? "0.35rem" : "0.55rem" }}>
                {newSprintAutoCalc ? (() => {
                  return (
                    <>
                      <SprintPlanSummary
                        startWeight={safeNumber(newSprintStartingWeight)}
                        targetWeight={safeNumber(newSprintTargetWeight)}
                        startDate={newSprintStartDate}
                        targetDate={newSprintTargetDate}
                        weeklyRate={newSprintAutoCalc.weeklyRate}
                        requiredNet={newSprintAutoCalc.requiredNet}
                        tdee={newSprintAutoCalc.tdee}
                        gender={vitals.gender}
                        calorieView={newSprintSummaryMode}
                        onCalorieViewChange={setNewSprintSummaryMode}
                        compact={isMobile}
                      />
                    </>
                  );
                })() : null}
                {(newSprintGuardrails.hardErrors.length > 0 || newSprintGuardrails.softWarnings.length > 0) ? (
                  <div style={{ display: "grid", gap: "0.4rem" }}>
                    {newSprintGuardrails.hardErrors.some((err) => isPaceHardError(err)) ? (
                      <div style={{ borderRadius: 12, border: "1px solid rgba(251,191,36,0.32)", background: "linear-gradient(135deg, rgba(251,191,36,0.15), rgba(249,115,22,0.06))", padding: "0.75rem 0.85rem", display: "grid", gap: "0.3rem" }}>
                        <div style={{ fontWeight: 700, color: "rgba(248,250,252,0.96)", fontSize: "0.88rem" }}>Above recommended pace</div>
                        <div style={{ fontSize: "0.82rem", color: "rgba(255,237,213,0.9)" }}>Max ~2.0 lb/week · Plan: {Math.abs(newSprintAutoCalc?.weeklyRate ?? 0).toFixed(1)} lb/week</div>
                      </div>
                    ) : null}
                    {createSprintBlockingErrors.length > 0 ? <div style={{ fontSize: "0.78rem", color: "rgba(248,113,113,0.95)" }}>{createSprintBlockingErrors[0]}</div> : null}
                    {!newSprintGuardrails.hardErrors.some((err) => isPaceHardError(err)) && filterRedundantPaceWarnings(newSprintGuardrails.softWarnings).length > 0 ? <div style={{ fontSize: "0.78rem", color: "rgba(255,255,255,0.78)" }}>{filterRedundantPaceWarnings(newSprintGuardrails.softWarnings).slice(0, 2).join(" • ")}</div> : null}
                  </div>
                ) : null}
              </div>
            ) : null}
            <div style={{ marginTop: isMobile ? "0.2rem" : "0.5rem", paddingTop: isMobile ? "0.3rem" : "0.55rem", borderTop: "1px solid rgba(255,255,255,0.08)", display: "flex", justifyContent: "flex-end" }}>
              <button
                style={{ ...btnBase, background: `linear-gradient(135deg, ${COLORS.blue}, ${COLORS.lightBlue})`, color: "white", padding: isMobile ? "0.6rem 1rem" : "0.82rem 1.25rem", fontWeight: 900, fontSize: "0.94rem", opacity: canCreateSprint ? 1 : 0.55 }}
                onClick={createSprint}
                disabled={!canCreateSprint}
              >
                Create Sprint
              </button>
            </div>
          </>
      </ModalShell>
      ) : null}

      {roadmapDeleteConfirmOpen ? (
        <ModalShell title="Delete roadmap?" ariaLabel="delete roadmap confirmation" onClose={() => setRoadmapDeleteConfirmOpen(false)} panelStyle={{ width: "min(520px, calc(100vw - 32px))" }}>
          <div style={{ display: "grid", gap: "0.85rem", paddingTop: "0.45rem" }}>
            <div style={{ color: "rgba(255,255,255,0.78)", lineHeight: 1.5 }}>
              This will remove your current roadmap and its phase plan from view. Linked sprint history will stay intact.
            </div>
            <div style={{ borderRadius: 12, border: "1px solid rgba(239,68,68,0.24)", background: "linear-gradient(135deg, rgba(239,68,68,0.16), rgba(127,29,29,0.08))", padding: "0.8rem 0.9rem", color: "rgba(254,226,226,0.94)", fontSize: "0.84rem" }}>
              You can build a new roadmap afterward, but this roadmap layout itself can’t be undone once deleted.
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.55rem", paddingTop: "0.2rem", borderTop: "1px solid rgba(255,255,255,0.08)" }}>
              <button style={{ ...btnBase, background: "rgba(255,255,255,0.06)", color: "white" }} onClick={() => setRoadmapDeleteConfirmOpen(false)}>Cancel</button>
              <button style={{ ...btnBase, background: "rgba(239,68,68,0.16)", borderColor: "rgba(239,68,68,0.32)", color: "#fecaca" }} onClick={confirmRoadmapDelete}>Delete roadmap</button>
            </div>
          </div>
        </ModalShell>
      ) : null}

      {roadmapPhaseDeleteConfirm ? (
        <ModalShell title={`Delete ${roadmapPhaseDeleteConfirm.phaseName}?`} ariaLabel="delete roadmap phase confirmation" onClose={() => setRoadmapPhaseDeleteConfirm(null)} panelStyle={{ width: "min(520px, calc(100vw - 32px))" }}>
          <div style={{ display: "grid", gap: "0.85rem", paddingTop: "0.45rem" }}>
            <div style={{ color: "rgba(255,255,255,0.78)", lineHeight: 1.5 }}>
              {roadmapPhaseDeleteConfirm.isLastPhase
                ? `Deleting ${roadmapPhaseDeleteConfirm.phaseName} will remove the last phase and delete the roadmap view.`
                : `${roadmapPhaseDeleteConfirm.phaseName} will be removed and the remaining roadmap phases will shift forward automatically.`}
            </div>
            <div style={{ borderRadius: 12, border: "1px solid rgba(239,68,68,0.24)", background: "linear-gradient(135deg, rgba(239,68,68,0.16), rgba(127,29,29,0.08))", padding: "0.8rem 0.9rem", color: "rgba(254,226,226,0.94)", fontSize: "0.84rem" }}>
              Linked sprint history stays intact. This phase deletion can’t be undone once confirmed.
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.55rem", paddingTop: "0.2rem", borderTop: "1px solid rgba(255,255,255,0.08)" }}>
              <button style={{ ...btnBase, background: "rgba(255,255,255,0.06)", color: "white" }} onClick={() => setRoadmapPhaseDeleteConfirm(null)}>Cancel</button>
              <button style={{ ...btnBase, background: "rgba(239,68,68,0.16)", borderColor: "rgba(239,68,68,0.32)", color: "#fecaca" }} onClick={confirmDeletePhase}>Delete phase</button>
            </div>
          </div>
        </ModalShell>
      ) : null}

      {sprintDeleteConfirm ? (
        <ModalShell title={`Delete ${sprintDeleteConfirm.sprintName}?`} ariaLabel="delete sprint confirmation" onClose={() => setSprintDeleteConfirm(null)} panelStyle={{ width: "min(520px, calc(100vw - 32px))" }}>
          <div style={{ display: "grid", gap: "0.85rem", paddingTop: "0.45rem" }}>
            <div style={{ color: "rgba(255,255,255,0.78)", lineHeight: 1.5 }}>
              This removes the sprint record only. Logged entries will stay intact.
            </div>
            <div style={{ borderRadius: 12, border: "1px solid rgba(239,68,68,0.24)", background: "linear-gradient(135deg, rgba(239,68,68,0.16), rgba(127,29,29,0.08))", padding: "0.8rem 0.9rem", color: "rgba(254,226,226,0.94)", fontSize: "0.84rem" }}>
              <div>This can’t be undone once confirmed.</div>
              {sprintDeleteConfirm.isActive ? (
                <div style={{ marginTop: "0.45rem" }}>
                  This is your active sprint. Deleting it will clear your active sprint.
                </div>
              ) : null}
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.55rem", paddingTop: "0.2rem", borderTop: "1px solid rgba(255,255,255,0.08)" }}>
              <button style={{ ...btnBase, background: "rgba(255,255,255,0.06)", color: "white" }} onClick={() => setSprintDeleteConfirm(null)}>Cancel</button>
              <button style={{ ...btnBase, background: "rgba(239,68,68,0.16)", borderColor: "rgba(239,68,68,0.32)", color: "#fecaca" }} onClick={() => { void confirmDeleteSprint(); }}>Delete sprint</button>
            </div>
          </div>
        </ModalShell>
      ) : null}

      {activeModal === "editSprint" && editingSprintId ? (
        <ModalShell
          title="Edit Sprint"
          ariaLabel="edit sprint modal"
          onClose={cancelEditSprint}
          panelStyle={{ width: "min(480px, calc(100vw - 32px))", maxHeight: "90vh", overflowY: "auto" }}
        >
          <div style={{ display: "grid", gap: "0.85rem", paddingTop: "0.5rem" }}>
            <div style={{ display: "flex", gap: "0.65rem", alignItems: "flex-start" }}>
              <div style={{ flex: 1 }}>
                <Input
                  label="Sprint name"
                  value={editingSprintDraft.name}
                  onChange={(v) => setEditingSprintDraft((prev) => ({ ...prev, name: v }))}
                  maxLength={MAX_SPRINT_NAME_CHARS}
                />
              </div>
              <SprintColorDots
                label="Color"
                value={editingSprintDraft.color}
                onChange={(v) => setEditingSprintDraft((prev) => ({ ...prev, color: v }))}
              />
            </div>
            <div style={{ display: "grid", gap: "0.45rem" }}>
              <TriFieldRow
                mobile={isMobile}
                leftLabel={<div style={{ ...createSprintLabelRowStyle, marginBottom: 0, whiteSpace: "nowrap" }}>Start date</div>}
                rightLabel={<div style={{ ...createSprintLabelRowStyle, marginBottom: 0 }}>End date</div>}
                leftField={(
                  <MobileDateField
                    value={editingSprintDraft.startDate}
                    onCommit={(v) => setEditingSprintDraft((prev) => ({ ...prev, startDate: v }))}
                    style={{ width: "100%", padding: "0.55rem 0.65rem", borderRadius: 12, border: "1px solid rgba(255,255,255,0.14)", background: "rgba(255,255,255,0.04)", color: "white", outline: "none", minHeight: SPRINT_DATE_FIELD_HEIGHT }}
                  />
                )}
                rightField={(
                  <MobileDateField
                    value={editingSprintDraft.endDate}
                    min={editingSprintDraft.startDate}
                    max={todayYmd}
                    onCommit={(v) => setEditingSprintDraft((prev) => ({ ...prev, endDate: v }))}
                    style={{ width: "100%", padding: "0.55rem 0.65rem", borderRadius: 12, border: "1px solid rgba(255,255,255,0.14)", background: "rgba(255,255,255,0.04)", color: "white", outline: "none", minHeight: SPRINT_DATE_FIELD_HEIGHT }}
                  />
                )}
                chip={<DurationChip startDate={editingSprintDraft.startDate} endDate={editingSprintDraft.endDate} />}
              />
              {!isMobile ? (
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 86px", gap: "0.28rem 0.55rem" }}>
                  <div />
                  <DurationPresetChips startDate={editingSprintDraft.startDate} endDate={editingSprintDraft.endDate} onSelect={(v) => setEditingSprintDraft((prev) => ({ ...prev, endDate: v }))} />
                  <div />
                </div>
              ) : null}
              {editingSprintDraft.endDate && editingSprintDraft.endDate <= editingSprintDraft.startDate ? (
                <div style={createSprintHelperRowStyle}>End date must be after start date.</div>
              ) : !editingSprintDraft.endDate ? (
                <div style={createSprintHelperRowStyle}>End date is required.</div>
              ) : editingSprintDraft.endDate > todayYmd ? (
                <div style={createSprintHelperRowStyle}>End date cannot be later than today.</div>
              ) : null}
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.55rem", paddingTop: "0.35rem", borderTop: "1px solid rgba(255,255,255,0.08)" }}>
              <button
                style={{ ...btnBase, background: "rgba(255,255,255,0.06)", color: "white" }}
                onClick={cancelEditSprint}
              >Cancel</button>
              <button
                style={{ ...btnBase, background: actionColors.primaryGradient, color: actionColors.primaryText, borderColor: "rgba(148,163,184,0.45)" }}
                onClick={() => saveEditedSprint(editingSprintId)}
              >Save</button>
            </div>
          </div>
        </ModalShell>
      ) : null}

      {onboardingOverlay}
      {showObSkipSprintInfo ? (
        <div style={{ ...modalBackdropStyle, zIndex: 21045 }} onClick={() => setShowObSkipSprintInfo(false)}>
          <div role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()} style={{ ...modalPanelStyle, maxWidth: 430, width: "calc(100% - 1.5rem)", background: "#12151a", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 16 }}>
            <div style={{ fontSize: "1.02rem", fontWeight: 800, marginBottom: "0.55rem" }}>Set up a sprint to unlock tracking</div>
            <div style={{ fontSize: "0.86rem", color: "rgba(255,255,255,0.72)", lineHeight: 1.45 }}>
              Sprints power progress tracking, calorie targets, and forecasts.
              <br />A sprint is your active plan.
              <br />You can skip this and create one later.
            </div>
            <div style={{ marginTop: "0.9rem", display: "grid", gap: "0.55rem" }}>
              <button type="button" onClick={() => setShowObSkipSprintInfo(false)} style={{ ...btnBase, width: "100%", minHeight: 40, justifyContent: "center", background: "rgba(99,102,241,0.30)", border: "1px solid rgba(99,102,241,0.5)", color: "white" }}>Create sprint</button>
              <button type="button" onClick={async () => { setShowObSkipSprintInfo(false); await handleOnboardingSkipSprint(); }} style={{ ...btnBase, width: "100%", minHeight: 40, justifyContent: "center", background: "rgba(255,255,255,0.06)", color: "white" }}>Skip for now</button>
            </div>
          </div>
        </div>
      ) : null}
      {showObPlanReveal ? (
        <div style={{ ...modalBackdropStyle, zIndex: 21050 }} onClick={() => setShowObPlanReveal(false)}>
          <div role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()} style={{ ...modalPanelStyle, maxWidth: 420, width: "calc(100% - 1.5rem)", background: "#12151a", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 16, overflow: "visible" }}>
            <div style={{ fontSize: "1.08rem", fontWeight: 800, marginBottom: "0.7rem" }}>Here’s your plan</div>
            <div style={{ display: "grid", gap: "0.4rem", fontSize: "0.84rem" }}>
              <div style={{ borderRadius: 10, border: "1px solid rgba(255,255,255,0.12)", background: "rgba(255,255,255,0.03)", padding: "0.5rem 0.6rem", display: "flex", justifyContent: "space-between", gap: "0.6rem" }}><span style={{ color: "rgba(255,255,255,0.58)" }}>Goal</span><span style={{ color: "rgba(255,255,255,0.9)", fontWeight: 600 }}>{`${safeNumber(obSprint.startWeight) ?? "—"} → ${safeNumber(obSprint.targetWeight) ?? "—"} lb`}</span></div>
              <div style={{ borderRadius: 10, border: "1px solid rgba(255,255,255,0.12)", background: "rgba(255,255,255,0.03)", padding: "0.5rem 0.6rem", display: "flex", justifyContent: "space-between", gap: "0.6rem" }}><span style={{ color: "rgba(255,255,255,0.58)" }}>Estimated pace</span><span style={{ color: "rgba(255,255,255,0.9)", fontWeight: 600 }}>{obSprintAutoCalc ? (() => { const sw = safeNumber(obSprint.startWeight); const tw = safeNumber(obSprint.targetWeight); const base = Math.abs(Number(obSprintAutoCalc.weeklyRate)); const signed = sw !== null && tw !== null ? (tw < sw ? -base : tw > sw ? base : 0) : Number(obSprintAutoCalc.weeklyRate); return `${signed > 0 ? "+" : ""}${signed.toFixed(2)} lb/week`; })() : "—"}</span></div>
              <div style={{ borderRadius: 10, border: "1px solid rgba(255,255,255,0.12)", background: "rgba(255,255,255,0.03)", padding: "0.5rem 0.6rem", display: "flex", justifyContent: "space-between", gap: "0.6rem" }}><span style={{ color: "rgba(255,255,255,0.58)" }}>End date</span><span style={{ color: "rgba(255,255,255,0.9)", fontWeight: 600 }}>{formatMdy(obSprint.targetDate)}</span></div>
              <div style={{ borderRadius: 10, border: "1px solid rgba(255,255,255,0.12)", background: "rgba(255,255,255,0.03)", padding: "0.5rem 0.6rem", display: "flex", justifyContent: "space-between", gap: "0.6rem" }}><span style={{ color: "rgba(255,255,255,0.58)" }}>Daily intake</span><span style={{ color: "rgba(255,255,255,0.9)", fontWeight: 600 }}>{obSprintAutoCalc?.suggestedIntake !== null && obSprintAutoCalc?.suggestedIntake !== undefined ? `${Math.round(obSprintAutoCalc.suggestedIntake)} calories` : "—"}</span></div>
              <div style={{ borderRadius: 10, border: "1px solid rgba(255,255,255,0.12)", background: "rgba(255,255,255,0.03)", padding: "0.5rem 0.6rem", display: "flex", justifyContent: "space-between", gap: "0.6rem" }}><span style={{ color: "rgba(255,255,255,0.58)" }}>Daily net</span><span style={{ color: "rgba(255,255,255,0.9)", fontWeight: 600 }}>{obSprintAutoCalc ? `${Math.round(obSprintAutoCalc.requiredNet)} calories` : "—"}</span></div>
              <div style={{ borderRadius: 10, border: "1px solid rgba(255,255,255,0.12)", background: "rgba(255,255,255,0.03)", padding: "0.5rem 0.6rem", display: "flex", justifyContent: "space-between", gap: "0.6rem" }}><span style={{ color: "rgba(255,255,255,0.58)", display: "inline-flex", alignItems: "center", gap: "0.3rem" }}>Assumed TDEE <InfoTooltipIcon ariaLabel="What is TDEE" isOpen={obTdeeHelp} onToggle={() => setObTdeeHelp((v) => !v)} onOpen={() => setObTdeeHelp(true)} onClose={() => setObTdeeHelp(false)}><div style={{ display: "grid", gap: "0.28rem" }}><div style={{ fontWeight: 700, color: "rgba(241,245,249,0.98)" }}>Estimated daily energy burn</div><div>TDEE (Total Daily Energy Expenditure) estimates how many calories you burn in a typical day.</div><div>Calculated from your age, sex, height, weight, and activity level.</div></div></InfoTooltipIcon></span><span style={{ color: "rgba(255,255,255,0.9)", fontWeight: 600 }}>{obSprintAutoCalc?.tdee !== null && obSprintAutoCalc?.tdee !== undefined ? `${Math.round(obSprintAutoCalc.tdee)} calories` : "—"}</span></div>
            </div>
            {obShowPaceWarning ? (
              <div style={{ marginTop: "0.7rem", borderRadius: 12, border: "1px solid rgba(248,113,113,0.45)", background: "linear-gradient(135deg, rgba(239,68,68,0.16), rgba(239,68,68,0.06))", padding: "0.75rem 0.85rem", display: "grid", gap: "0.32rem" }}>
                {obInferredByDateWarning?.title ? <div style={{ fontWeight: 700, color: "rgba(254,226,226,0.96)", fontSize: "0.88rem" }}>{obInferredByDateWarning.title}</div> : null}
                {obInferredByDateWarning?.recommendedLine ? <div style={{ fontSize: "0.8rem", color: "rgba(254,202,202,0.95)", lineHeight: 1.4 }}>{obInferredByDateWarning.recommendedLine}</div> : null}
                {obInferredByDateWarning?.targetLine ? <div style={{ fontSize: "0.8rem", color: "rgba(254,202,202,0.95)", lineHeight: 1.4 }}>{obInferredByDateWarning.targetLine}</div> : null}
                {obInferredByDateWarning?.helperLine ? <div style={{ fontSize: "0.78rem", color: "rgba(254,202,202,0.88)", lineHeight: 1.4 }}>{obInferredByDateWarning.helperLine}</div> : null}
              </div>
            ) : null}
            <div style={{ color: "rgba(255,255,255,0.45)", fontSize: "0.76rem", marginTop: "0.6rem" }}>This plan updates automatically as you log weight.</div>
            <div style={{ marginTop: "0.9rem", display: "flex", justifyContent: "space-between", gap: "0.6rem", flexWrap: "wrap" }}>
              <button type="button" onClick={() => { setShowObPlanReveal(false); setObPhase("goal"); }} style={{ ...btnBase, background: "rgba(255,255,255,0.06)", color: "white" }}>Edit goal</button>
              <button type="button" onClick={async () => { setShowObPlanReveal(false); await completeOnboarding(); }} style={{ ...btnBase, background: "rgba(99,102,241,0.30)", border: "1px solid rgba(99,102,241,0.5)", color: "white" }}>Start tracking →</button>
            </div>
          </div>
        </div>
      ) : null}
      {cloudSyncInterstitial}
      {showLoginModal ? <LoginModal initialMode={loginModalMode} initialBanner={loginModalInitialBanner} initialEmail={verifiedEmailPrefill} onClose={() => { setShowLoginModal(false); setObWelcomeNotice(null); onClearVerifiedEmailNotice?.(); }} onPasswordUpdateSuccess={onPasswordRecoveryComplete} /> : null}
    </>
  );
}
